import L from "leaflet";
import { crsPickerField } from "../../core/ui/crs-picker.js";
import * as toGeoJSON from "@tmcw/togeojson";
import JSZip from "jszip";
import shp from "shpjs";
import { createSlipInventoryRuntime } from "../slip-delivery/index.js";
import { createStreetView360Runtime } from "../streetview-360/index.js";
import { createPointCloudWorkspaceRuntime } from "../point-cloud-workspace/index.js";
import { reportTaskStatus } from "../../core/task-status.js";
import { deliverableFilename, deliverableKinds } from "../../core/deliverable-naming.js";

// Human labels for the standard deliverable kinds, used by the "Send to Deliverables"
// modal's type selector and the completeness checklist in the DAM's Deliverables panel.
const DELIVERABLE_KIND_LABELS = {
  slip_plot: "Slip Plot (PDF)",
  slip_inventory_geojson: "Slip Inventory (GeoJSON)",
  slip_inventory_csv: "Slip Inventory (CSV)",
  slam_tif: "SLAM (GeoTIFF)",
  slam_las: "SLAM (LAS)",
};

// State plane zones with approximate geographic bounds (lat/lon in decimal degrees).
// Each entry covers the area where that zone is the appropriate choice.
// Zones with overlapping bounds are intentional — users on a boundary should see both options.
const STATE_PLANE_ZONES = [
  // Washington
  { code: "2285", label: "NAD83 / Washington North (ftUS)", latMin: 47.0, latMax: 49.1, lonMin: -124.8, lonMax: -116.9 },
  { code: "2286", label: "NAD83 / Washington South (ftUS)", latMin: 45.5, latMax: 47.9, lonMin: -124.8, lonMax: -116.9 },
  // California
  { code: "2225", label: "NAD83 / California zone 1 (ftUS)", latMin: 39.3, latMax: 42.1, lonMin: -124.5, lonMax: -119.5 },
  { code: "2226", label: "NAD83 / California zone 2 (ftUS)", latMin: 37.4, latMax: 40.3, lonMin: -124.2, lonMax: -118.9 },
  { code: "6420", label: "NAD83(2011) / California zone 3 (ftUS)", latMin: 35.5, latMax: 38.8, lonMin: -123.5, lonMax: -117.0 },
  { code: "2227", label: "NAD83 / California zone 3 (ftUS)", latMin: 35.5, latMax: 38.8, lonMin: -123.5, lonMax: -117.0 },
  { code: "2228", label: "NAD83 / California zone 4 (ftUS)", latMin: 33.9, latMax: 37.1, lonMin: -121.3, lonMax: -115.6 },
  { code: "2229", label: "NAD83 / California zone 5 (ftUS)", latMin: 32.7, latMax: 35.9, lonMin: -120.1, lonMax: -115.5 },
  { code: "2230", label: "NAD83 / California zone 6 (ftUS)", latMin: 32.5, latMax: 34.7, lonMin: -118.1, lonMax: -114.5 },
  // Texas
  { code: "2276", label: "NAD83 / Texas North (ftUS)", latMin: 33.5, latMax: 37.1, lonMin: -103.1, lonMax: -94.0 },
  { code: "2277", label: "NAD83 / Texas North Central (ftUS)", latMin: 31.4, latMax: 34.6, lonMin: -105.1, lonMax: -94.0 },
  { code: "2278", label: "NAD83 / Texas Central (ftUS)", latMin: 28.8, latMax: 32.6, lonMin: -107.6, lonMax: -93.5 },
  { code: "2279", label: "NAD83 / Texas South Central (ftUS)", latMin: 26.4, latMax: 30.6, lonMin: -107.6, lonMax: -93.5 },
  { code: "2280", label: "NAD83 / Texas South (ftUS)", latMin: 25.5, latMax: 28.6, lonMin: -100.5, lonMax: -93.5 },
  // Louisiana
  { code: "3452", label: "NAD83 / Louisiana North (ftUS)", latMin: 30.4, latMax: 33.5, lonMin: -94.5, lonMax: -88.5 },
  { code: "3454", label: "NAD83 / Louisiana South (ftUS)", latMin: 28.4, latMax: 31.5, lonMin: -94.5, lonMax: -88.5 },
  // Mississippi
  { code: "2255", label: "NAD83 / Mississippi East (ftUS)", latMin: 30.1, latMax: 35.1, lonMin: -89.5, lonMax: -87.7 },
  { code: "2256", label: "NAD83 / Mississippi West (ftUS)", latMin: 30.1, latMax: 35.1, lonMin: -91.9, lonMax: -89.3 },
  // Alabama
  { code: "2253", label: "NAD83 / Alabama East (ftUS)", latMin: 30.0, latMax: 35.1, lonMin: -86.5, lonMax: -84.9 },
  { code: "2254", label: "NAD83 / Alabama West (ftUS)", latMin: 30.0, latMax: 35.1, lonMin: -88.5, lonMax: -86.3 },
  // Florida
  { code: "2236", label: "NAD83 / Florida East (ftUS)", latMin: 24.0, latMax: 31.1, lonMin: -82.2, lonMax: -79.8 },
  { code: "2237", label: "NAD83 / Florida West (ftUS)", latMin: 24.0, latMax: 31.1, lonMin: -87.7, lonMax: -81.9 },
  { code: "2238", label: "NAD83 / Florida North (ftUS)", latMin: 29.0, latMax: 31.5, lonMin: -87.7, lonMax: -82.3 },
  // Georgia
  { code: "2239", label: "NAD83 / Georgia East (ftUS)", latMin: 30.3, latMax: 35.1, lonMin: -83.5, lonMax: -80.5 },
  { code: "2240", label: "NAD83 / Georgia West (ftUS)", latMin: 30.3, latMax: 35.1, lonMin: -86.1, lonMax: -83.3 },
  // South Carolina
  { code: "2273", label: "NAD83 / South Carolina (ftUS)", latMin: 31.9, latMax: 35.4, lonMin: -83.5, lonMax: -78.4 },
  // North Carolina
  { code: "2264", label: "NAD83 / North Carolina (ftUS)", latMin: 33.7, latMax: 36.7, lonMin: -84.5, lonMax: -75.4 },
  // Virginia
  { code: "2283", label: "NAD83 / Virginia North (ftUS)", latMin: 37.4, latMax: 39.5, lonMin: -80.7, lonMax: -76.4 },
  { code: "2284", label: "NAD83 / Virginia South (ftUS)", latMin: 36.2, latMax: 38.4, lonMin: -83.9, lonMax: -76.4 },
  // Maryland / Delaware
  { code: "2248", label: "NAD83 / Maryland (ftUS)", latMin: 37.8, latMax: 39.8, lonMin: -79.5, lonMax: -74.8 },
  { code: "2235", label: "NAD83 / Delaware (ftUS)", latMin: 38.4, latMax: 39.9, lonMin: -75.8, lonMax: -74.9 },
  // New Jersey
  { code: "3424", label: "NAD83 / New Jersey (ftUS)", latMin: 38.8, latMax: 41.4, lonMin: -75.9, lonMax: -73.8 },
  // New York
  { code: "2260", label: "NAD83 / New York East (ftUS)", latMin: 40.4, latMax: 45.1, lonMin: -75.1, lonMax: -72.0 },
  { code: "2261", label: "NAD83 / New York Central (ftUS)", latMin: 41.8, latMax: 45.1, lonMin: -78.5, lonMax: -74.2 },
  { code: "2262", label: "NAD83 / New York West (ftUS)", latMin: 41.8, latMax: 44.7, lonMin: -80.0, lonMax: -76.2 },
  { code: "2263", label: "NAD83 / New York Long Island (ftUS)", latMin: 40.4, latMax: 41.6, lonMin: -74.4, lonMax: -71.8 },
];

const IMAGE_GENERATOR_STATE_VERSION = 4;
const PORTFOLIO_MODES = ["production", "routing", "project-management"];
const DAILY_TOTAL_HOURS = 10;
const DEFAULT_DATASET_URL = "/data/shm-marinas.kml";
const DEFAULT_DATASET_NAME = "SHM Marinas.kml";
const DEFAULT_PORTFOLIO_URL = "/data/shm-portfolio.xlsx";
const DEFAULT_PORTFOLIO_NAME = "SHM Portfolio.xlsx";
const GLOBAL_AIRPORTS_URL = "/data/global-airports.json";
const GSA_CONUS_RATES_URL = "/data/gsa-fy2026-conus.json";
const DOD_OCONUS_NONFOREIGN_URL = "/data/dod-oconus-nonforeign.json";
const STATE_FOREIGN_RATES_URL = "/data/state-foreign-march-2026.json";
const LOCAL_STATE_KEY = `hydrotools:image-generator:v${IMAGE_GENERATOR_STATE_VERSION}`;
const SHARED_PROJECT_TASKS_API = "/api/slip-inventory/v1/project-tasks/safe-harbor-main";
const TRAVEL_PLANNING_STATE_KEY = "hydrotools:travel-planning:v1";
const ENABLE_REPORT_POPUPS = false;
const DEFAULT_CONUS_LODGING = 110;
const DEFAULT_CONUS_MIE = 68;
const FEDERAL_MILEAGE_RATE_2026 = 0.725;
const DEFAULT_CREW_SIZE = 2;
const FIELD_STAY_LOOKAHEAD_MINUTES = 90;
const FIELD_STAY_LOOKAHEAD_MILES = 75;
const AIRPORT_TRANSFER_SPLIT_MINUTES = 60;
const AIR_TRIGGER_REGIONS = [
  "PUERTO RICO",
  "US VIRGIN ISLANDS",
  "U.S. VIRGIN ISLANDS",
  "VIRGIN ISLANDS",
  "ST. KITTS",
  "SAINT KITTS",
  "ST. KITTS AND NEVIS",
  "SAINT KITTS AND NEVIS",
  "ST KITTS",
  "ST KITTS AND NEVIS",
];
const DEFAULT_OFFICES = [
  { id: "office-default-san-rafael-ca",     name: "San Rafael, CA",    groupName: "San Rafael",     lat: 37.9747795, lng: -122.5316686, airportCode: "OAK" },
  { id: "office-default-thousand-oaks-ca",  name: "Thousand Oaks, CA", groupName: "Thousand Oaks",  lat: 34.1705609, lng: -118.8375937, airportCode: "BUR" },
  { id: "office-default-la-porte-tx",       name: "La Porte, TX",      groupName: "La Porte",       lat: 29.6657838, lng:  -95.0193729, airportCode: "HOU" },
  { id: "office-default-charleston-sc",     name: "Charleston, SC",    groupName: "Charleston",     lat: 32.7884363, lng:  -79.9399309, airportCode: "CHS" },
  { id: "office-default-boston-ma",         name: "Boston, MA",        groupName: "Boston",         lat: 42.3588336, lng:  -71.0578303, airportCode: "BOS" },
  { id: "office-default-virginia-beach-va", name: "Virginia Beach, VA",groupName: "Virginia Beach", lat: 36.8529841, lng:  -75.9774183, airportCode: "ORF" },
  { id: "office-default-dayton-oh",         name: "Dayton, OH",        groupName: "Dayton",         lat: 39.7589478, lng:  -84.1916069, airportCode: "DAY" },
  { id: "office-default-miami-fl",          name: "Miami, FL",         groupName: "Miami",          lat: 25.7741566, lng:  -80.1935973, airportCode: "MIA" },
];
const OFFICE_SELECTION_COLORS = [
  { fill: "#f59e0b", stroke: "#fef3c7", text: "#fef3c7" },
  { fill: "#a855f7", stroke: "#f3e8ff", text: "#f3e8ff" },
  { fill: "#14b8a6", stroke: "#ccfbf1", text: "#ccfbf1" },
  { fill: "#eab308", stroke: "#fef9c3", text: "#fef9c3" },
  { fill: "#ec4899", stroke: "#fce7f3", text: "#fce7f3" },
  { fill: "#06b6d4", stroke: "#cffafe", text: "#cffafe" },
  { fill: "#84cc16", stroke: "#ecfccb", text: "#ecfccb" },
  { fill: "#f97316", stroke: "#ffedd5", text: "#ffedd5" },
];
const REPORT_CAPTURE_RIGHT_PANEL_WIDTH = "680px";
const DOCK_TRAINING_CLASSES = [
  { id: "dock", label: "Dock", color: "#22c55e" },
  { id: "boat", label: "Boat", color: "#f97316" },
  { id: "water", label: "Water", color: "#38bdf8" },
  { id: "pile", label: "Pile / Structure", color: "#a78bfa" },
  { id: "ignore", label: "Ignore", color: "#94a3b8" },
];
const PROJECT_TASK_STATUS_OPTIONS = ["Not Started", "In Progress", "Blocked", "Done"];
const PROJECT_MGMT_BOTTOM_PANEL_ID = "safe-harbor-project-management";
const PROJECT_GANTT_BOTTOM_PANEL_ID = "safe-harbor-project-gantt";

function fmtProjectDate(value) {
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return "";
  return dt.toISOString().slice(0, 10);
}

function parseProjectDate(value) {
  const text = String(value || "").trim();
  if (!text) return null;
  const dt = new Date(`${text}T00:00:00Z`);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

function addProjectDays(date, days) {
  const dt = new Date(date.getTime());
  dt.setUTCDate(dt.getUTCDate() + days);
  return dt;
}

function daysBetweenProjectDates(a, b) {
  const ms = 24 * 60 * 60 * 1000;
  return Math.round((b.getTime() - a.getTime()) / ms);
}

function ganttMonthColors(monthZeroBased) {
  const m = Number(monthZeroBased);
  if (m === 5) {
    return {
      tint: "rgba(59,130,246,0.06)",
      dayBg: "rgba(59,130,246,0.08)",
      monthBg: "rgba(59,130,246,0.14)",
    };
  }
  if (m === 6) {
    return {
      tint: "rgba(16,185,129,0.06)",
      dayBg: "rgba(16,185,129,0.08)",
      monthBg: "rgba(16,185,129,0.14)",
    };
  }
  if (m === 7) {
    return {
      tint: "rgba(245,158,11,0.06)",
      dayBg: "rgba(245,158,11,0.08)",
      monthBg: "rgba(245,158,11,0.14)",
    };
  }
  return {
    tint: "transparent",
    dayBg: "transparent",
    monthBg: "rgba(148,163,184,0.12)",
  };
}

function defaultProjectTasks() {
  const rows = [
    ["Loch Lomond", "Equipment Prep", "Equipment Preparation", "Woolpert", "2026-05-11", "2026-05-11", "Not Started", 1],
    ["Loch Lomond", "Survey Control", "Geodetic Control & RTK QC", "Woolpert", "2026-05-12", "2026-05-12", "Not Started", 1],
    ["Loch Lomond", "Flight Ops", "UAS LiDAR Data Acquisition (Flight Ops)", "Woolpert", "2026-05-12", "2026-05-12", "Not Started", 1],
    ["Loch Lomond", "Data Processing", "Control & LiDAR Data Processing & Draft Outputs", "Woolpert", "2026-05-13", "2026-05-13", "Not Started", 1],
    ["Loch Lomond", "Product QC", "Feature Extraction, Product Generation, QC", "Woolpert", "2026-05-14", "2026-05-15", "Not Started", 2],
    ["Loch Lomond", "Data Delivery", "Linear Footage Report", "Woolpert", "2026-05-16", "2026-05-16", "Not Started", 1],
    ["Loch Lomond", "Data Delivery", "GIS Map Files", "Woolpert", "2026-05-16", "2026-05-16", "Not Started", 1],
    ["Loch Lomond", "QA/QC", "QA/QC Review (GM + SHM)", "Ops + PMO", "2026-05-18", "2026-05-20", "Not Started", 3],
    ["Loch Lomond", "Final Delivery", "Final Deliverables", "Woolpert", "2026-05-28", "2026-05-28", "Not Started", 1],
    ["Harbortown", "Equipment Prep", "Equipment Preparation/Shipping", "Woolpert", "2026-05-13", "2026-05-17", "Not Started", 5],
    ["Harbortown", "Flight Ops", "UAS LiDAR Data Acquisition (Flight Ops)", "Woolpert", "2026-05-18", "2026-05-18", "Not Started", 1],
    ["Harbortown", "Data Processing", "Control & LiDAR Data Processing & Draft Outputs", "Woolpert", "2026-05-19", "2026-05-19", "Not Started", 1],
    ["Harbortown", "Product QC", "Feature Extraction, Product Generation, QC", "Woolpert", "2026-05-20", "2026-05-21", "Not Started", 2],
    ["Harbortown", "Data Delivery", "Linear Footage Report", "Woolpert", "2026-05-22", "2026-05-22", "Not Started", 1],
    ["Harbortown", "QA/QC", "QA/QC Review (GM + SHM)", "Ops + PMO", "2026-05-26", "2026-05-28", "Not Started", 3],
    ["Harbortown", "Final Delivery", "Final Deliverables", "Woolpert", "2026-06-05", "2026-06-05", "Not Started", 1],
    ["Rybovich", "Equipment Prep", "Equipment Preparation/Shipping", "Woolpert", "2026-05-20", "2026-05-24", "Not Started", 5],
    ["Rybovich", "Flight Ops", "UAS LiDAR Data Acquisition (Flight Ops)", "Woolpert", "2026-05-19", "2026-05-19", "Not Started", 1],
    ["Rybovich", "Data Processing", "Control & LiDAR Data Processing & Draft Outputs", "Woolpert", "2026-05-20", "2026-05-20", "Not Started", 1],
    ["Rybovich", "Product QC", "Feature Extraction, Product Generation, QC", "Woolpert", "2026-05-21", "2026-05-22", "Not Started", 2],
    ["Rybovich", "Data Delivery", "Linear Footage Report", "Woolpert", "2026-05-23", "2026-05-23", "Not Started", 1],
    ["Rybovich", "QA/QC", "QA/QC Review (GM + SHM)", "Ops + PMO", "2026-05-26", "2026-05-28", "Not Started", 3],
    ["Rybovich", "Final Delivery", "Final Deliverables", "Woolpert", "2026-06-05", "2026-06-05", "Not Started", 1],
  ];
  return rows.map((r, i) => ({
    id: `pm-${i + 1}`,
    marina: r[0],
    category: r[1],
    task: r[2],
    owner: r[3],
    startDate: r[4],
    endDate: r[5],
    status: r[6],
    durationDays: r[7],
    notes: "",
  }));
}

export const imageGeneratorTool = {
  id: "image-generator",
  name: "Project Workspace",
  stateVersion: IMAGE_GENERATOR_STATE_VERSION,
  init(app) {
    this._rt = this._rt ?? createRuntime(app);
  },
  destroy() {
    this._rt?.destroy?.();
    this._rt = null;
  },
  renderPanel(app, containerEl) {
    if (!this._rt) this._rt = createRuntime(app);
    this._rt.mount(containerEl);
  },
  serialize() {
    return this._rt?.snapshot?.() ?? null;
  },
  restore(app, data) {
    if (!this._rt) this._rt = createRuntime(app);
    this._rt.restore?.(data);
  },
};

function createRuntime(app) {
  const map = app.map.instance;
  let containerEl = null;
  let fileInputEl = null;
  // Project-change/context-sync listeners (onContextMarinaChanged reloads DAM assets, etc.
  // for whichever project is now active) used to be wired up only inside mount(), which only
  // the full "Project Workspace" tool ever calls. A pinned sub-panel like Digital Asset
  // Manager only calls mountSection(), so if the full workspace tool was never opened this
  // session, nothing was ever listening for project switches -- the standalone panel just
  // sat on whatever project was active when it first mounted. ensureContextListeners() is
  // called from both mount() and mountSection() so either entry point wires this up exactly
  // once per runtime instance.
  let contextListenersAttached = false;
  // Ticket screen-recording — live MediaStream/MediaRecorder/Blob objects can't live in
  // `state` (it's re-rendered to innerHTML and gets snapshotted/persisted elsewhere), so
  // they're tracked here like containerEl/fileInputEl and reset in destroy(). Only the
  // finished recording's blob URL (a plain string) ever reaches state, via
  // ticketPendingRecording below, which drives the Attach/Discard preview in the composer.
  let ticketRecorderStream = null;
  let ticketRecorder = null;
  let ticketRecordedChunks = [];
  let ticketRecordingTimer = null;
  let ticketPendingRecording = null; // { blob, blobUrl, filename, contentType, kind } once stopped
  let captureLayer = null;
  let legendControlEl = null;
  let exporting = false;
  let cancelRequested = false;
  let defaultPortfolioAttempted = false;
  let portfolioWorkbook = null;
  let routeMatrixKey = "";
  let routeMatrixDurations = null;
  let routeMatrixDistances = null;
  let routeMatrixStatus = "idle";
  let routeMatrixError = "";
  let routeMatrixRequestId = 0;
  let routeGeometryKey = "";
  let routeGeometryStatus = "idle";
  let routeGeometryError = "";
  let routeGeometryFeatures = [];
  let routeGeometryRequestId = 0;
  let suppressUiRouteSync = false;
  let slipInventoryRuntime = null;
  let streetViewRuntime = null;
  let pointCloudRuntime = null;
  let globalAirports = null;
  let globalAirportsPromise = null;
  let gsaConusRates = null;
  let gsaConusRatesPromise = null;
  let dodOconusRates = null;
  let dodOconusRatesPromise = null;
  let stateForeignRates = null;
  let stateForeignRatesPromise = null;
  let enrichedTripPlanKey = "";
  let enrichedTripPlan = null;
  let enrichedTripPlanRequestId = 0;
  let rawTripPlanCacheKey = "";
  let rawTripPlanCache = null;
  let projectGeoTiffFile = null;
  let projectLasFile = null;
  let projectGeoJsonFile = null;
  let autoSaveViewTimer = 0;
  let suppressAutoSaveUntil = 0;
  let hydratedFromLocalState = false;
  let suppressAutoSaveView = false;
  let sharedProjectTasksLoaded = false;
  let sharedProjectTasksSaveTimer = 0;
  let sharedProjectTasksPollTimer = 0;
  let sharedProjectTasksUpdatedAt = "";
  let sharedDockPhotosLoadInFlight = false;
  let sharedDockPhotosLoadedForMarina = "";
  let sharedDockPhotosPendingMarina = "";
  let sharedDockPhotosLoadSeq = 0;
  let projectLocationPickHandler = null;
  let projectLocationPickMarker = null;
  const marinaRasterLayerCache = new Map();
  const dockTrainingRoot = L.featureGroup().addTo(map);
  const dockTrainingSavedLayer = L.featureGroup().addTo(dockTrainingRoot);
  const dockTrainingSketchLayer = L.featureGroup().addTo(dockTrainingRoot);
  let dockTrainingDrawSession = null;
  let status = "Load a KMZ/KML/GeoJSON point dataset.";
  let captureProgressCurrent = 0;
  let captureProgressTotal = 0;
  let state = {
    datasetName: "",
    items: [],
    activeIndex: -1,
    defaultZoom: 18,
    format: "jpg",
    scale: 2,
    marker: true,
    prefix: "marina",
    batchScope: "selected",
    offices: defaultOffices(),
    selectedOfficeIds: [],
    visibleOfficeMarinaIds: [],
    previewCodeFilter: "",
    officeDraft: { name: "", lat: "", lng: "" },
    projectCreate: {
      code: "",
      name: "",
      type: "marina",
      lat: "",
      lng: "",
      picking: false,
    },
    projectSettings: {
      selectedProjectId: "",
      epsgCode: "2227",
      nativeZUnit: "ft",
      marinaEpsgCodes: {},  // per-project EPSG overrides, keyed by project ID
    },
    catalog: {
      clients: [],
      selectedClientId: "safeharbor",
      marinas: [],
      loading: false,
      error: "",
    },
    digitalAsset: {
      selectedMarinaCode: "",
      showAllCategories: false,
      assetsByCategory: {},
      uploadingByCategory: {},
      uploadingFilesByCategory: {}, // catKey -> [{ key, name, size, uploadedBytes, status }], one entry per file in the current batch
      collapsedCategories: {},
      collapsedFolders: {},
      foldersByCategory: {},
      selectedFiles: {},
      depthLayers: {},
      deliveryTokens: {},
      deliveryManifest: {},
      clientDeliveryTokens: {},
      fieldTrackingTokens: {},
      invoiceWizardTokenLoaded: false,
      invoiceWizardToken: null,
      milestoneSchemas: {},
      sendToDeliverables: null,
      spatialLayersOnMap: {},
    },
    notes: {
      byProject: {},     // code -> note[] | null (null = not loaded yet)
      loading: {},       // code -> bool
      posting: false,
      draftText: "",
    },
    tickets: {
      byProject: {},        // code -> ticket[] | null
      loading: {},          // code -> bool
      posting: false,
      blockingSummary: null, // { [code]: { [milestoneKey]: openCount } }, portfolio-wide
      filterStatus: "",
      filterMilestoneKey: "",
      composerOpen: false,
      composerDraft: { title: "", description: "", milestoneKey: "", blocksMilestone: false, attachments: [] },
      attachmentUploading: false,
      noteDrafts: {},    // ticketId -> text
      notePosting: {},   // ticketId -> bool
    },
    planning: {
      averageSpeedMph: 45,
      roadFactor: 1.2,
      clusterSpacingMiles: 70,
      maxMarinasPerCluster: 5,
      defaultSurveyHours: 6,
      maxWorkHoursPerDay: 7,
      maxDriveHoursPerDay: 3,
      maxMarinasPerDay: 2,
      pricePerMarina: 6150,
      weatherContingencyPct: 10,
      weatherDayRate: 0,
      mileageRate: 0.725,
      crewSize: 2,
      gsaApiKey: "",
      travelMode: "auto",
      airOverheadHours: 4,
      airCruiseMph: 450,
      tripOfficeId: DEFAULT_OFFICES[0].id,
      tripGroupFilter: "",
      homeAirportOverride: "",
      returnToOffice: true,
    },
    showLegend: false,
    showMarinaCodes: false,
    showMarinaPoints: false,
    portfolioMapPoints: { active: false, potential: false, archived: false },
    portfolioColorBy: "status",   // "status" | "state" | "milestone" | "group"
    portfolioColorStep: "",        // active milestone key when colorBy=milestone
    portfolioShowMapLegend: false, // show floating legend on the map
    portfolioPoints: [],           // fetched from /api/project/catalog/map-points
    showOfficeMarkers: false,
    showTripRoute: false,
    tripPlaybackDayIndex: -1,
    tripPlaybackZoomToDay: false,
    editMarinaViews: false,
    portfolioSort: { col: "", dir: "asc" },
    portfolioBusyCodes: new Set(), // codes with an in-flight archive/restore request -- both move
                                    // every file in the project synchronously with the request, so
                                    // a large marina can take real time with zero feedback otherwise
    portfolioBusyKind: {},          // code -> "archive" | "restore", for the button label
    portfolioGroups: { active: true, archived: false },
    portfolioCustomGroups: {},         // { clientId: ["Wave 1", "Wave 2"] }
    portfolioGroupMeta: {},            // { clientId: { groupName: { color, visible, officeId, flyIn, flyInAirport } } }
    portfolioCustomGroupCollapsed: {}, // { "clientId::Wave 1": true }
    portfolioAddGroupShow: false,
    portfolioAddGroupName: "",
    portfolioNewClientShow: false,
    portfolioNewClientName: "",
    portfolioJustCreatedClient: null,   // { id, name } — survives catalog overrides until server confirms
    portfolioNewProjectShow: false,
    portfolioNewProjectCode: "",
    portfolioNewProjectName: "",
    // Independently togglable trays (keys match the old single-select portfolioMode values) —
    // each controls a slice of what shows per project row: production = milestone columns,
    // routing = trip/PDF/office tools on group headers, project-management = zoom/activate/
    // archive/delete actions and the group-assign dropdown. All off = just code + name.
    portfolioActiveTrays: { production: true, routing: false, "project-management": false },
    milestoneSchemaEditorOpen: false,
    pendingAlertConfirm: null, // {ruleId, code, marinaName, recipients, subject, preview} — set when a milestone save surfaces an alert rule match; renders a confirm popup
    pendingClientDelivRegenConfirm: null, // {clientSlug} — set when Regenerate is clicked on an existing client portal link; renders a confirm popup, only fetches on explicit confirm
    pendingFieldTrackRegenConfirm: null, // {clientSlug} — same as above, for the field-tracking link
    alertRulesEditorOpen: false,
    alertRulesDraft: null,     // array of {id, triggerKey, toValue, recipients, subject, body} — loaded from /api/alerts/rules, edited locally, PUT on Save
    alertRulesLoading: false,
    alertRulesLoadError: null,
    alertRulesSaveStatus: null, // {ok:true} | {ok:false, error} — transient save-result banner
    alertTestStatus: {},        // { [idx]: "sending" | {ok:true} | {ok:false, error} }
    panels: {
      planningGroup: false,
      productionGroup: false,
      projectMetadata: true,
      marinaPortfolio: true,
      projectPortfolio: true,
      projectSettings: false,
      digitalAssetManager: false,
      notes: false,
      tickets: false,
      officeAccess: false,
      tripPlanning: false,
      dockView360: false,
      pointCloud3d: false,
      lasSoundings: false,
      slipInventory: false,
      dockExtraction: false,
      projectManagement: false,
      previewItems: false,
    },
    projectTasks: defaultProjectTasks(),
    projectUi: {
      expandedMarinas: {},
      activeView: "table",
    },
    lasDock: {
      filePath: "",
      resolution: 1,
      minAboveWater: 0.5,
      maxAboveWater: 6,
      toolStatus: null,
      inspection: null,
      products: null,
      statusText: "No LAS inspected yet.",
      training: {
        activeClass: "dock",
        labels: [],
        selectedLabelId: "",
      },
    },
  };

  function defaultOffices() {
    return DEFAULT_OFFICES.map((office) => ({ ...office }));
  }

  function normalizeOffice(office) {
    const source = office && typeof office === "object" ? office : {};
    const defaultMatch = DEFAULT_OFFICES.find((entry) => entry.id === source.id || entry.name === source.name) || null;
    return {
      ...(defaultMatch || {}),
      ...source,
      airportCode: String(source.airportCode || defaultMatch?.airportCode || "").trim().toUpperCase(),
    };
  }

  function normalizeDailyPlanning(changedKey = "") {
    const totalCap = DAILY_TOTAL_HOURS;
    let work = Math.max(1, Math.min(totalCap, Number(state.planning.maxWorkHoursPerDay) || 7));
    let drive = Math.max(0, Math.min(totalCap, Number(state.planning.maxDriveHoursPerDay) || 3));
    if ((work + drive) > totalCap) {
      if (changedKey === "drive") {
        work = Math.max(1, totalCap - drive);
      } else {
        drive = Math.max(0, totalCap - work);
      }
    }
    state.planning.maxWorkHoursPerDay = Number(work.toFixed(2));
    state.planning.maxDriveHoursPerDay = Number(drive.toFixed(2));
  }

  function isEditableTarget(target) {
    if (!target) return false;
    const tag = String(target.tagName || "").toLowerCase();
    if (target.isContentEditable) return true;
    return ["input", "textarea", "select", "button"].includes(tag);
  }

  function esc(value) {
    return String(value ?? "").replace(/[&<>"']/g, (ch) => ({
      "&": "&amp;",
      "<": "&lt;",
      ">": "&gt;",
      '"': "&quot;",
      "'": "&#39;",
    }[ch]));
  }

  function dockTrainingClassById(classId) {
    return DOCK_TRAINING_CLASSES.find((entry) => entry.id === classId) || DOCK_TRAINING_CLASSES[0];
  }

  function dockTrainingClassOptions(selectedId) {
    return DOCK_TRAINING_CLASSES.map((entry) => `<option value="${esc(entry.id)}"${entry.id === selectedId ? " selected" : ""}>${esc(entry.label)}</option>`).join("");
  }

  function makeDockTrainingId() {
    return `dock-label-${Math.random().toString(36).slice(2, 10)}`;
  }

  function countDockTrainingByClass(classId) {
    return (state.lasDock.training?.labels || []).filter((entry) => entry?.classId === classId).length;
  }

  function dockTrainingExportPayload() {
    return {
      schema: "hydrotools-dock-training-v1",
      createdAt: new Date().toISOString(),
      source: {
        lasFilePath: String(state.lasDock.filePath || ""),
        products: state.lasDock.products?.products || null,
        outputDir: state.lasDock.products?.outputDir || "",
      },
      labels: (state.lasDock.training?.labels || []).map((label) => ({
        id: String(label.id || ""),
        classId: String(label.classId || ""),
        classLabel: dockTrainingClassById(label.classId).label,
        createdAt: String(label.createdAt || ""),
        geometry: label.geometry || null,
      })),
    };
  }

  function downloadDockTrainingLabels() {
    const payload = dockTrainingExportPayload();
    const stem = sanitizeFilePart(String(state.lasDock.filePath || "").split(/[\\/]/).pop()?.replace(/\.[^.]+$/, "") || "dock-training", "dock-training");
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${stem}_training_labels.json`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 2000);
  }

  function renderDockTrainingLayers() {
    dockTrainingSavedLayer.clearLayers();
    const labels = state.lasDock.training?.labels || [];
    labels.forEach((label) => {
      const ring = Array.isArray(label?.geometry?.coordinates?.[0]) ? label.geometry.coordinates[0] : [];
      if (ring.length < 4) return;
      const style = dockTrainingClassById(label.classId);
      const latlngs = ring.map((coord) => [Number(coord[1]), Number(coord[0])]);
      const isSelected = String(label.id || "") === String(state.lasDock.training?.selectedLabelId || "");
      const layer = L.polygon(latlngs, {
        color: style.color,
        weight: isSelected ? 3 : 2,
        fillColor: style.color,
        fillOpacity: isSelected ? 0.28 : 0.18,
        dashArray: isSelected ? null : "6 4",
      }).addTo(dockTrainingSavedLayer);
      layer.on("click", () => {
        state.lasDock.training.selectedLabelId = String(label.id || "");
        persistLocalState();
        renderDockTrainingLayers();
        render();
      });
      layer.bindTooltip(`${style.label}`, { sticky: true, direction: "top", opacity: 0.9 });
    });
  }

  function stopDockTrainingDraw(reason = "stopped") {
    if (!dockTrainingDrawSession) return;
    const { tempLine, tempFill, off } = dockTrainingDrawSession;
    off();
    try { dockTrainingSketchLayer.removeLayer(tempLine); } catch {}
    try { dockTrainingSketchLayer.removeLayer(tempFill); } catch {}
    dockTrainingDrawSession = null;
    const mapEl = map?.getContainer?.() || null;
    if (mapEl) mapEl.style.cursor = "";
    if (reason === "cancelled") app.ui.toast("Dock training draw cancelled.");
    render();
  }

  function startDockTrainingDraw() {
    stopDockTrainingDraw("replaced");
    const mapEl = map?.getContainer?.() || null;
    const activeClass = dockTrainingClassById(state.lasDock.training?.activeClass || "dock");
    if (mapEl) mapEl.style.cursor = "crosshair";
    const points = [];
    const tempLine = L.polyline([], { color: activeClass.color, weight: 2, dashArray: "6 6" }).addTo(dockTrainingSketchLayer);
    const tempFill = L.polygon([], { color: activeClass.color, weight: 2, fillColor: activeClass.color, fillOpacity: 0.14 }).addTo(dockTrainingSketchLayer);
    try { tempLine.bringToFront(); } catch {}
    try { tempFill.bringToFront(); } catch {}

    const updatePreview = (cursor = null) => {
      const path = cursor ? [...points, cursor] : [...points];
      tempLine.setLatLngs(path);
      if (points.length >= 2) tempFill.setLatLngs(path);
    };

    const finishDraw = () => {
      if (points.length < 3) {
        app.ui.toast("Need at least 3 vertices for a training polygon.");
        return;
      }
      const coords = points.map((p) => [Number(p.lng), Number(p.lat)]);
      const geometry = { type: "Polygon", coordinates: [[...coords, coords[0]]] };
      const label = {
        id: makeDockTrainingId(),
        classId: activeClass.id,
        geometry,
        createdAt: new Date().toISOString(),
      };
      state.lasDock.training.labels = [...(state.lasDock.training.labels || []), label];
      state.lasDock.training.selectedLabelId = label.id;
      persistLocalState();
      stopDockTrainingDraw("completed");
      renderDockTrainingLayers();
      render();
      app.ui.toast(`${activeClass.label} label captured.`);
    };

    const onClick = (e) => {
      points.push(e.latlng);
      updatePreview();
    };
    const onMove = (e) => updatePreview(e.latlng);
    const onDblClick = (e) => {
      L.DomEvent.stop(e);
      finishDraw();
    };
    const onContextMenu = (e) => {
      L.DomEvent.stop(e);
      finishDraw();
    };
    const onKey = (e) => {
      if (e.key === "Escape") stopDockTrainingDraw("cancelled");
    };

    const dblWas = map.doubleClickZoom?.enabled?.() ?? false;
    map.doubleClickZoom.disable();
    map.on("click", onClick);
    map.on("mousemove", onMove);
    map.on("dblclick", onDblClick);
    map.on("contextmenu", onContextMenu);
    document.addEventListener("keydown", onKey);

    dockTrainingDrawSession = {
      tempLine,
      tempFill,
      off() {
        map.off("click", onClick);
        map.off("mousemove", onMove);
        map.off("dblclick", onDblClick);
        map.off("contextmenu", onContextMenu);
        document.removeEventListener("keydown", onKey);
        if (dblWas) window.setTimeout(() => map.doubleClickZoom.enable(), 0);
      },
    };
    render();
    app.ui.toast(`Drawing ${activeClass.label} label. Click vertices, then double-click or right-click to finish.`);
  }

  function removeSelectedDockTrainingLabel() {
    const selectedId = String(state.lasDock.training?.selectedLabelId || "");
    if (!selectedId) {
      app.ui.toast("Select a training label first.");
      return;
    }
    state.lasDock.training.labels = (state.lasDock.training.labels || []).filter((label) => String(label?.id || "") !== selectedId);
    state.lasDock.training.selectedLabelId = "";
    persistLocalState();
    renderDockTrainingLayers();
    render();
  }

  function setStatus(message) {
    status = String(message || "").trim() || "Ready.";
    const el = containerEl?.querySelector?.("#imggen-status");
    if (el) el.textContent = status;
  }

  async function checkLasToolchain() {
    state.lasDock.statusText = "Checking LAS toolchain...";
    render();
    try {
      const response = await fetch("/api/image-generator/las-status");
      const payload = await response.json();
      if (!response.ok || payload?.ok === false) throw new Error(payload?.error || "LAS toolchain check failed.");
      state.lasDock.toolStatus = payload;
      state.lasDock.statusText = "LAS toolchain verified on host.";
      render();
    } catch (error) {
      state.lasDock.statusText = `LAS toolchain check failed: ${error?.message || "unknown error"}`;
      render();
      app.ui.toast("LAS toolchain check failed.");
    }
  }

  async function browseLasSource() {
    try {
      const response = await fetch("/api/qimera/pick-file", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title: "Select marina LAS/LAZ file",
          selectedPath: state.lasDock.filePath || "C:\\HydroShare",
          filter: "Point Cloud (*.las;*.laz)|*.las;*.laz|LAS (*.las)|*.las|LAZ (*.laz)|*.laz",
        }),
      });
      const payload = await response.json();
      if (!response.ok || payload?.ok === false) throw new Error(payload?.error || "File picker failed.");
      if (payload?.canceled || !payload?.path) return;
      const nextPath = String(payload.path || "");
      const changedSource = nextPath.trim().toLowerCase() !== String(state.lasDock.filePath || "").trim().toLowerCase();
      state.lasDock.filePath = nextPath;
      state.lasDock.inspection = null;
      state.lasDock.products = null;
      if (changedSource) {
        state.lasDock.training = { activeClass: state.lasDock.training?.activeClass || "dock", labels: [], selectedLabelId: "" };
        renderDockTrainingLayers();
      }
      state.lasDock.statusText = "LAS selected. Inspect it to frame the dock extraction workflow.";
      render();
    } catch (error) {
      state.lasDock.statusText = `Could not browse LAS source: ${error?.message || "unknown error"}`;
      render();
      app.ui.toast("LAS browse failed.");
    }
  }

  async function inspectLasSource() {
    const filePath = String(state.lasDock.filePath || "").trim();
    if (!filePath) {
      app.ui.toast("Choose a LAS file first.");
      return;
    }
    state.lasDock.statusText = "Inspecting LAS with PDAL...";
    render();
    try {
      const response = await fetch("/api/image-generator/las-inspect", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ filePath }),
      });
      const payload = await response.json();
      if (!response.ok || payload?.ok === false) throw new Error(payload?.error || "LAS inspection failed.");
      state.lasDock.inspection = payload;
      state.lasDock.statusText = "LAS inspection complete. Ready to frame the extraction pipeline.";
      render();
    } catch (error) {
      state.lasDock.statusText = `LAS inspection failed: ${error?.message || "unknown error"}`;
      render();
      app.ui.toast("LAS inspection failed.");
    }
  }

  async function generateLasProducts() {
    const filePath = String(state.lasDock.filePath || "").trim();
    if (!filePath) {
      app.ui.toast("Choose a LAS file first.");
      return;
    }
    state.lasDock.statusText = "Generating elevation and intensity rasters with PDAL...";
    render();
    try {
      const response = await fetch("/api/image-generator/las-products", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          filePath,
          resolution: Number(state.lasDock.resolution) || 1,
          minAboveWater: Number(state.lasDock.minAboveWater) || 0.5,
          maxAboveWater: Number(state.lasDock.maxAboveWater) || 6,
        }),
      });
      const payload = await response.json();
      if (!response.ok || payload?.ok === false) throw new Error(payload?.error || "LAS product generation failed.");
      state.lasDock.products = payload;
      state.lasDock.statusText = "LAS products generated. Elevation and intensity rasters are ready.";
      render();
      app.ui.toast("LAS products generated.");
    } catch (error) {
      state.lasDock.statusText = `LAS product generation failed: ${error?.message || "unknown error"}`;
      render();
      app.ui.toast("LAS product generation failed.");
    }
  }

  function buildHostedTiffUrl(filePath) {
    return `/api/qimera/local-file?path=${encodeURIComponent(String(filePath || ""))}&v=${Date.now()}`;
  }

  async function uploadProjectFileToHost(file, { kind = "misc", projectId = "" } = {}) {
    if (!(file instanceof File)) throw new Error("Missing local file to upload.");
    const project = String(projectId || state.projectSettings.selectedProjectId || "shared").trim() || "shared";
    const response = await fetch(`/api/qimera/upload?kind=${encodeURIComponent(String(kind || "misc"))}&marina=${encodeURIComponent(project)}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/octet-stream",
        "x-filename": String(file.name || "upload.bin"),
        "x-kind": String(kind || "misc"),
        "x-marina": project,
      },
      body: file,
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok || payload?.ok === false) {
      throw new Error(String(payload?.error || `Upload failed (${response.status})`));
    }
    return payload;
  }

  function loadLasProductsIntoMap() {
    const layersService = app?.services?.layers;
    const products = state.lasDock.products?.products || null;
    if (!layersService) {
      app.ui.toast("Layer services are not available.");
      return;
    }
    if (!products?.elevation && !products?.intensity) {
      app.ui.toast("Generate products first.");
      return;
    }
    const existingLayers = layersService.listLayers?.() || [];
    const addProduct = (kind, filePath, nameSuffix) => {
      if (!filePath) return;
      const existing = existingLayers.find((layer) => String(layer?.meta?.lasProductPath || "").trim().toLowerCase() === String(filePath).toLowerCase());
      const def = {
        id: existing?.id || undefined,
        type: "geotiff-url",
        kind: "overlay",
        name: `LAS ${nameSuffix}`,
        url: buildHostedTiffUrl(filePath),
        opacity: existing?.opacity ?? 0.92,
        visible: true,
        meta: {
          sourceType: "las-products",
          lasProductKind: kind,
          lasProductPath: filePath,
        },
      };
      if (existing?.id) {
        layersService.removeLayer(existing.id);
      }
      layersService.addLayer("image-generator", def);
    };
    addProduct("elevation", products.elevation, "Elevation");
    addProduct("intensity", products.intensity, "Intensity");
    addProduct("dock-band", products.dockBand, "Dock Band");
    state.lasDock.statusText = "Loaded LAS products into Hydrotools.";
    render();
    app.ui.toast("LAS products loaded.");
  }

  function updateCaptureProgressDom() {
    const wrapEl = containerEl?.querySelector?.("#imggen-progress-wrap");
    const fillEl = containerEl?.querySelector?.("#imggen-progress-fill");
    if (!wrapEl || !fillEl) return;
    const total = Math.max(0, Number(captureProgressTotal) || 0);
    const current = Math.max(0, Math.min(total, Number(captureProgressCurrent) || 0));
    const percent = total > 0 ? Math.max(0, Math.min(100, (current / total) * 100)) : 0;
    wrapEl.style.display = total > 0 ? "block" : "none";
    fillEl.style.width = `${percent}%`;
    fillEl.setAttribute("aria-valuenow", String(current));
    fillEl.setAttribute("aria-valuemax", String(total || 1));
  }

  function setCaptureProgress(current, total) {
    captureProgressCurrent = Math.max(0, Number(current) || 0);
    captureProgressTotal = Math.max(0, Number(total) || 0);
    updateCaptureProgressDom();
  }

  function clearCaptureProgress() {
    setCaptureProgress(0, 0);
  }

  function suppressAutoSaveFor(ms = 1200) {
    suppressAutoSaveUntil = Math.max(suppressAutoSaveUntil, Date.now() + Math.max(0, Number(ms) || 0));
  }

  function persistLocalState() {
    try {
      localStorage.setItem(LOCAL_STATE_KEY, JSON.stringify(snapshot()));
    } catch {}
  }

  function readLocalState() {
    try {
      const raw = localStorage.getItem(LOCAL_STATE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  function sharedProjectToPortfolioRow(project) {
    const code = normalizeProjectCode(project?.code || project?.marinaId || project?.id || project?.slug, "");
    const lat = Number(project?.lat ?? project?.location?.lat);
    const lng = Number(project?.lng ?? project?.location?.lng);
    const archivedAt = String(project?.archivedAt || "");
    return {
      ...project,
      code,
      name: String(project?.name || code),
      active: archivedAt ? false : project?.active !== false,
      archivedAt,
      activatedAt: String(project?.activatedAt || ""),
      projectId: String(project?.projectId || project?.id || code),
      projectType: String(project?.projectType || project?.type || "marina"),
      clientId: String(project?.clientId || project?.clientSlug || "safeharbor"),
      clientSlug: String(project?.clientSlug || project?.clientId || "safeharbor"),
      clientName: String(project?.clientName || ""),
      s3Prefix: String(project?.s3Prefix || ""),
      lat: Number.isFinite(lat) ? lat : null,
      lng: Number.isFinite(lng) ? lng : null,
      statusColumns: project?.statusColumns && typeof project.statusColumns === "object" ? project.statusColumns : defaultProjectStatusColumns(),
    };
  }

  // Counts how many saveCatalog() calls are currently in-flight.
  // loadCatalog() skips its state update while this is non-zero to avoid
  // overwriting locally-changed group/statusColumns data with stale server data.
  let _catalogSaveInFlight = 0;

  async function loadCatalog() {
    // If a save is in-flight our GET may race with an unfinished PUT and return stale data.
    // The saveCatalog() finally-block will trigger a fresh loadCatalog() after the save lands.
    if (_catalogSaveInFlight > 0) return;
    state.catalog.loading = true;
    state.catalog.error = "";
    try {
      const resp = await fetch("/api/projects/catalog?scope=portfolio");
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      // Re-check: a save may have started while this fetch was in-flight.
      if (_catalogSaveInFlight > 0) {
        state.catalog.loading = false;
        render();
        return;
      }
      state.catalog.clients = Array.isArray(data.catalog?.clients) ? data.catalog.clients : [];
      state.catalog.marinas = Array.isArray(data.catalog?.projects)
        ? data.catalog.projects.map(sharedProjectToPortfolioRow).filter((row) => row.code)
        : [];
      if (!state.catalog.selectedClientId || !state.catalog.clients.some((client) => client.id === state.catalog.selectedClientId)) {
        state.catalog.selectedClientId = state.catalog.clients.find((client) => client.id === "safeharbor")?.id || state.catalog.clients[0]?.id || "";
      }
    } catch (err) {
      state.catalog.error = String(err?.message || err);
    }
    state.catalog.loading = false;
    syncItemsFromCatalog();
    // The Portfolio grid's HTML is memoized (_htmlCache.portfolio) and only rebuilds when
    // explicitly invalidated -- without this, freshly (re)loaded catalog data (a new project,
    // an archive/restore, a group reassignment, ...) never actually shows up in the grid until
    // something else happens to bust the cache, or the page is reloaded outright. Several call
    // sites already work around this individually; doing it here covers every caller for free.
    delete _htmlCache.portfolio;
    render();
  }

  async function loadPortfolioPoints() {
    try {
      const resp = await fetch("/api/project/catalog/map-points");
      if (!resp.ok) return;
      const data = await resp.json();
      if (data.ok && Array.isArray(data.points)) {
        state.portfolioPoints = data.points;
        render();
      }
    } catch {}
  }

  function syncItemsFromCatalog() {
    const catalogMarinas = Array.isArray(state.catalog.marinas) ? state.catalog.marinas : [];
    const selectedClientId = String(state.catalog.selectedClientId || "");
    const geocoded = catalogMarinas.filter(
      (m) => Number.isFinite(Number(m.lat)) && Number.isFinite(Number(m.lng)) && !m.archivedAt
        && (!selectedClientId || String(m.clientId || m.clientSlug || "safeharbor") === selectedClientId)
    );
    if (!geocoded.length) return false;

    const existingByCode = new Map();
    const existingByName = new Map();
    (state.items || []).forEach((item) => {
      const code = normalizeProjectCode(projectSettingsMarinaCode(item), "");
      if (code) existingByCode.set(code, item);
      const name = normalizeMarinaKey(item.name);
      if (name) existingByName.set(name, item);
    });

    const activeId = currentItem()?.id || "";
    const newItems = geocoded.map((marina) => {
      const code = normalizeProjectCode(marina.code, "");
      const existing = existingByCode.get(code) || existingByName.get(normalizeMarinaKey(marina.name)) || null;
      return normalizeItem(buildItemFromCatalogEntry(marina, existing));
    });

    state.datasetName = "Project Portfolio";
    state.prefix = "marina";
    state.items = newItems;
    const nextActiveIndex = activeId ? newItems.findIndex((item) => item.id === activeId) : -1;
    state.activeIndex = nextActiveIndex >= 0 ? nextActiveIndex : (newItems.length ? 0 : -1);
    recomputePlanning();
    if (portfolioWorkbook) {
      syncPortfolioToItems(portfolioWorkbook);
      recomputePlanning();
    }
    persistLocalState();
    return true;
  }

  async function importKmlToCatalog() {
    try {
      setStatus("Loading KML for catalog import...");
      const parsed = await parseDatasetUrl(DEFAULT_DATASET_URL, DEFAULT_DATASET_NAME);
      const normalized = app.services.io.normalizeImportFeatureCollection(parsed.featureCollection, {
        sourceCRS: null,
        targetCRS: "EPSG:4326",
      });
      const fromGeoJson = flattenItems(normalized.featureCollection);
      const fromKmlDom = Array.isArray(parsed.kmlItems) ? parsed.kmlItems : [];
      const kmlItems = dedupeItems(fromKmlDom.length ? [...fromKmlDom, ...fromGeoJson] : fromGeoJson);

      const catalogMarinas = Array.isArray(state.catalog.marinas) ? [...state.catalog.marinas] : [];
      const byCode = new Map(catalogMarinas.map((m) => [normalizeProjectCode(m.code, ""), m]));
      const byName = new Map(catalogMarinas.map((m) => [normalizeMarinaKey(m.name), m]));

      let updatedCoords = 0;
      const toAdd = [];
      kmlItems.forEach((item) => {
        const lat = Number(item.lat);
        const lng = Number(item.lng);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
        const nameKey = normalizeMarinaKey(item.name);
        const existing = byName.get(nameKey) || null;
        if (existing) {
          if (!Number.isFinite(Number(existing.lat)) || !Number.isFinite(Number(existing.lng))) {
            existing.lat = lat;
            existing.lng = lng;
            updatedCoords += 1;
          }
        } else {
          const code = normalizeProjectCode(item.name, "");
          if (!byCode.has(code)) {
            const entry = {
              code,
              name: String(item.name || "").trim(),
              lat,
              lng,
              active: true,
              projectType: "marina",
              statusColumns: defaultProjectStatusColumns(),
            };
            toAdd.push(entry);
            byCode.set(code, entry);
            byName.set(nameKey, entry);
          }
        }
      });

      if (updatedCoords + toAdd.length === 0) {
        app.ui.toast("KML import: all marinas already matched with coordinates.");
        setStatus("KML import complete — no changes needed.");
        return;
      }

      state.catalog.marinas = [...catalogMarinas, ...toAdd];
      await saveCatalog();
      await loadCatalog();
      app.ui.toast(`KML import: updated coords for ${updatedCoords}, added ${toAdd.length} new.`);
      setStatus(`KML import complete: ${updatedCoords} coords updated, ${toAdd.length} added.`);
    } catch (err) {
      console.error("[ImageGenerator] KML catalog import failed", err);
      app.ui.toast(`KML import failed: ${err?.message || "unknown error"}`);
      setStatus(`KML import failed: ${err?.message || "unknown error"}`);
    }
  }

  async function saveCatalog() {
    _catalogSaveInFlight++;
    let savedOk = false;
    try {
      const resp = await fetch("/api/project/catalog", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ marinas: state.catalog.marinas }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        app.ui.toast(`Catalog save failed (${resp.status}): ${err.error || "unknown error"}`);
        throw new Error(`catalog save failed: ${resp.status}`);
      }
      savedOk = true;
    } finally {
      _catalogSaveInFlight--;
      // When the last in-flight save lands successfully, reload from the server to confirm
      // the canonical state and pick up any fields other code paths may have written.
      if (savedOk && _catalogSaveInFlight === 0) {
        loadCatalog().catch(() => {});
      }
    }
  }

  async function loadMilestoneChanges(kind) {
    const mc = state.milestoneChanges || (state.milestoneChanges = {});
    let since;
    if (kind === "today") { const d = new Date(); d.setHours(0, 0, 0, 0); since = d.toISOString(); }
    else if (kind === "30") since = new Date(Date.now() - 30 * 86400000).toISOString();
    else { kind = "7"; since = new Date(Date.now() - 7 * 86400000).toISOString(); }
    mc.kind = kind; mc.since = since; mc.loading = true;
    render();
    try {
      const clientSlug = String(state.catalog.selectedClientId || "");
      const resp = await fetch(`/api/project/milestone-changes?since=${encodeURIComponent(since)}${clientSlug ? `&clientSlug=${encodeURIComponent(clientSlug)}` : ""}`);
      const d = await resp.json();
      mc.rows = d.ok ? d.changes : [];
    } catch {
      mc.rows = [];
    }
    mc.loading = false;
    render();
  }

  function printMilestoneChanges() {
    const mc = state.milestoneChanges || {};
    if (!Array.isArray(mc.rows)) return;
    const kindLbl = mc.kind === "today" ? "Today" : `Past ${mc.kind} days`;
    const escP = (s) => String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    const rows = mc.rows.map((r) =>
      `<tr><td>${escP(new Date(r.ts).toLocaleString())}</td><td>${escP(r.projectName)} (${escP(r.code)})</td><td>${escP(r.milestoneLabel)}</td><td>${escP(r.from)} → ${escP(r.to)}</td><td>${escP(r.source === "portal" ? "Client Portal" : "AOS")}</td></tr>`
    ).join("");
    const w = window.open("", "_blank");
    w.document.write(`<html><head><title>Milestone Changes — ${escP(kindLbl)}</title><style>body{font-family:system-ui,sans-serif;padding:24px;color:#111}h2{margin:0 0 2px;font-size:18px}p{margin:0 0 16px;color:#555;font-size:12px}table{border-collapse:collapse;width:100%;font-size:12px}th,td{border:1px solid #ccc;padding:5px 8px;text-align:left}th{background:#f1f5f9}</style></head><body><h2>Milestone Changes — ${escP(kindLbl)}</h2><p>Generated ${escP(new Date().toLocaleString())} · ${mc.rows.length} change${mc.rows.length !== 1 ? "s" : ""}</p><table><thead><tr><th>When</th><th>Project</th><th>Milestone</th><th>Change</th><th>By</th></tr></thead><tbody>${rows || '<tr><td colspan="5">No changes in this period.</td></tr>'}</tbody></table></body></html>`);
    w.document.close(); w.focus(); setTimeout(() => w.print(), 250);
  }

  function defaultProjectStatusColumns() {
    return {
      planned: "Not Started",
      acquisition: "Not Started",
      processing: "Not Started",
      reporting: "Not Started",
      prelimDelivery: "Not Started",
      shmQaQc: "Not Started",
      finalDelivery: "Not Started",
    };
  }

  function normalizeProjectCode(value, fallback = "PROJECT") {
    const cleaned = String(value || "")
      .trim()
      .toUpperCase()
      .replace(/[^A-Z0-9_-]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 48);
    return cleaned || fallback;
  }

  function ensureProjectCreateState() {
    if (!state.projectCreate || typeof state.projectCreate !== "object") {
      state.projectCreate = { code: "", name: "", type: "marina", lat: "", lng: "", picking: false };
    }
    state.projectCreate.type = String(state.projectCreate.type || "marina").trim() || "marina";
    return state.projectCreate;
  }

  function stopProjectLocationPick() {
    if (projectLocationPickHandler) {
      try { map.off("click", projectLocationPickHandler); } catch {}
      projectLocationPickHandler = null;
    }
    try { map.getContainer().style.cursor = ""; } catch {}
    ensureProjectCreateState().picking = false;
  }

  function startProjectLocationPick() {
    const draft = ensureProjectCreateState();
    if (draft.picking) {
      stopProjectLocationPick();
      renderAllSections();
      return;
    }
    stopProjectLocationPick();
    draft.picking = true;
    try { map.getContainer().style.cursor = "crosshair"; } catch {}
    app.ui.toast("Click the map to set the project location.");
    projectLocationPickHandler = (event) => {
      const lat = Number(event?.latlng?.lat);
      const lng = Number(event?.latlng?.lng);
      if (Number.isFinite(lat) && Number.isFinite(lng)) {
        draft.lat = lat.toFixed(6);
        draft.lng = lng.toFixed(6);
        if (projectLocationPickMarker) {
          try { map.removeLayer(projectLocationPickMarker); } catch {}
        }
        projectLocationPickMarker = L.circleMarker([lat, lng], {
          radius: 7,
          color: "#38bdf8",
          weight: 2,
          fillColor: "#22c55e",
          fillOpacity: 0.85,
        }).addTo(map);
        app.ui.toast("Project location set.");
      }
      stopProjectLocationPick();
      persistLocalState();
      renderAllSections();
    };
    map.on("click", projectLocationPickHandler);
    renderAllSections();
  }

  function zoomToCatalogProject(code) {
    const normalized = normalizeProjectCode(code, "");
    const entry = (state.catalog.marinas || []).find((m) => normalizeProjectCode(m?.code || "") === normalized);
    const idx = state.items.findIndex((item) => projectSettingsMarinaCode(item) === normalized);
    if (idx >= 0) {
      focusItem(idx, { saveCurrent: true });
      return true;
    }
    const lat = Number(entry?.lat);
    const lng = Number(entry?.lng);
    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      suppressAutoSaveFor();
      map.setView([lat, lng], Math.max(12, Number(state.defaultZoom) || 18), { animate: true });
      if (projectLocationPickMarker) {
        try { map.removeLayer(projectLocationPickMarker); } catch {}
      }
      projectLocationPickMarker = L.circleMarker([lat, lng], {
        radius: 7,
        color: "#38bdf8",
        weight: 2,
        fillColor: "#22c55e",
        fillOpacity: 0.85,
      }).addTo(map);
      return true;
    }
    return false;
  }

  async function createPortfolioProjectFromDraft() {
    const draft = ensureProjectCreateState();
    const code = normalizeProjectCode(draft.code || draft.name, "");
    const name = String(draft.name || "").trim();
    const type = String(draft.type || "marina").trim().toLowerCase().replace(/[^a-z0-9_-]+/g, "-") || "marina";
    const lat = Number(draft.lat);
    const lng = Number(draft.lng);
    const selectedClient = (state.catalog.clients || []).find((client) => client.id === state.catalog.selectedClientId) || null;
    if (!name) { app.ui.toast("Enter a project name."); return; }
    if (!code) { app.ui.toast("Enter a project code."); return; }
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) { app.ui.toast("Click the map to set the project location."); return; }
    const exists = (state.catalog.marinas || []).some((m) => normalizeProjectCode(m?.code || "") === code);
    if (exists) { app.ui.toast(`Project ${code} already exists.`); return; }
    try {
      const resp = await fetch("/api/projects", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ code, name, type, lat, lng, clientSlug: selectedClient?.slug || selectedClient?.id || "safeharbor", clientName: selectedClient?.name || "" }),
      });
      const payload = await resp.json().catch(() => ({}));
      if (!resp.ok || !payload?.ok) throw new Error(payload?.error || `HTTP ${resp.status}`);
      await loadCatalog();
      state.projectSettings.selectedProjectId = code;
      draft.code = "";
      draft.name = "";
      draft.type = "marina";
      draft.lat = "";
      draft.lng = "";
      draft.picking = false;
      loadDamAssetsForProject(code);
      dispatchProjectChanged(code);
      app.ui.toast(`Created project ${code} and initialized DAM folders.`);
      zoomToCatalogProject(code);
      persistLocalState();
      renderAllSections();
      // Keep context.js (the landing page's source of truth for "is a project active")
      // in sync -- it doesn't share this catalog, so it needs its own refresh before it can
      // resolve and activate the project we just created.
      try {
        await app.services?.context?.refreshFromServer?.();
        const ctxProjects = app.services?.context?.getAllProjects?.() || [];
        const ctxProject = ctxProjects.find((p) =>
          [p.marinaId, p.code, p.id].some((v) => String(v || "").toUpperCase() === code.toUpperCase())
        );
        if (ctxProject) app.services.context.setActiveProject(ctxProject.id);
      } catch {}
    } catch (err) {
      app.ui.toast(`Create project failed: ${String(err?.message || err)}`);
    }
  }

  async function activateMarina(code) {
    state.portfolioBusyCodes.add(code);
    state.portfolioBusyKind[code] = "restore";
    delete _htmlCache.portfolio; // memoized grid HTML -- must invalidate or the busy state never renders
    render();
    try {
      const resp = await fetch("/api/project/activate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code, clientId: state.catalog.selectedClientId || "" }),
      });
      if (!resp.ok) {
        const d = await resp.json().catch(() => ({}));
        throw new Error(d.error || `HTTP ${resp.status}`);
      }
      await loadCatalog();
    } catch (err) {
      app.ui.toast(`Activate failed: ${String(err?.message || err)}`);
    } finally {
      state.portfolioBusyCodes.delete(code);
      delete state.portfolioBusyKind[code];
      delete _htmlCache.portfolio;
      render();
    }
  }

  async function archiveMarina(code) {
    if (!isAdminUser()) { app.ui.toast("Archiving a project is admin-only."); return; }
    if (!window.confirm(`Archive "${code}"? It will move to Archived and can be restored anytime.`)) return;
    state.portfolioBusyCodes.add(code);
    state.portfolioBusyKind[code] = "archive";
    delete _htmlCache.portfolio;
    render();
    try {
      const resp = await fetch("/api/project/deactivate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code }),
      });
      if (!resp.ok) {
        const d = await resp.json().catch(() => ({}));
        throw new Error(d.error || `HTTP ${resp.status}`);
      }
      await loadCatalog();
    } catch (err) {
      app.ui.toast(`Archive failed: ${String(err?.message || err)}`);
    } finally {
      state.portfolioBusyCodes.delete(code);
      delete state.portfolioBusyKind[code];
      delete _htmlCache.portfolio;
      render();
    }
  }

  async function createProjectFromPortfolioForm() {
    const code = normalizeProjectCode(state.portfolioNewProjectCode || state.portfolioNewProjectName || "", "");
    const name = String(state.portfolioNewProjectName || "").trim() || code;
    const effectiveClientId = String(state.catalog.selectedClientId || "");
    const selectedClient = (state.catalog.clients || []).find((c) => c.id === effectiveClientId) || null;
    const reqBody = {
      code, name, type: "marina",
      clientSlug: selectedClient?.slug || selectedClient?.id || effectiveClientId || "safeharbor",
      clientName: selectedClient?.name || "",
    };
    console.log("[portfolio:createProject] state:", { code, name, effectiveClientId, clientFound: !!selectedClient, reqBody });
    if (!code) { app.ui.toast("Enter a project code or name."); return; }
    const exists = (state.catalog.marinas || []).some((m) => normalizeProjectCode(m?.code || "") === code);
    if (exists) { app.ui.toast(`Project ${code} already exists. Choose a different code.`); return; }
    try {
      app.ui.toast(`Creating project ${code}…`);
      console.log("[portfolio:createProject] POSTing /api/project/project-create", reqBody);
      const resp = await fetch("/api/project/project-create", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(reqBody),
      });
      const payload = await resp.json().catch((e) => ({ _parseErr: String(e) }));
      console.log("[portfolio:createProject] response:", resp.status, payload);
      if (!resp.ok || !payload?.ok) throw new Error(payload?.error || `HTTP ${resp.status}`);
      state.portfolioNewProjectShow = false;
      state.portfolioNewProjectCode = "";
      state.portfolioNewProjectName = "";
      await loadCatalog();
      // Sync the context service so Activate works immediately without a page refresh.
      // Fire-and-forget — context:catalog:changed will arrive before the user clicks Activate.
      app.services?.context?.refreshFromServer?.({ emit: true }).catch(() => {});
      app.ui.toast(`Created project ${code}. Use the geocode button (⊙) to set its location.`);
      persistLocalState();
      renderAllSections();
    } catch (err) {
      console.error("[portfolio:createProject] error:", err);
      app.ui.toast(`Create failed: ${String(err?.message || err)}`);
    }
  }

  async function permanentlyDeleteMarina(code) {
    if (!window.confirm(`Permanently delete "${code}" from the catalog? This cannot be undone.`)) return;
    state.portfolioBusyCodes.add(code);
    state.portfolioBusyKind[code] = "delete";
    delete _htmlCache.portfolio;
    render();
    try {
      const resp = await fetch("/api/project/remove", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code }),
      });
      if (!resp.ok) {
        const d = await resp.json().catch(() => ({}));
        throw new Error(d.error || `HTTP ${resp.status}`);
      }
      await loadCatalog();
    } catch (err) {
      app.ui.toast(`Delete failed: ${String(err?.message || err)}`);
    } finally {
      state.portfolioBusyCodes.delete(code);
      delete state.portfolioBusyKind[code];
      delete _htmlCache.portfolio;
      render();
    }
  }

  const DAM_ASSET_CATEGORIES = ["360-dock-photos", "las-files", "geotiff-files", "vector-files", "surfaces", "source-data", "gnss-raw", "project-files", "tilesets", "uploaded-from-client"];
  const TICKET_STATUS_ORDER = ["open", "in_progress", "resolved", "closed"];

  const damLoadInFlight = new Set();
  // Guards against out-of-order responses clobbering fresher state: Generate Deliverables can
  // register several files into the same category in quick succession (each firing its own
  // loadDigitalAssets call), and nothing stops an earlier request's response from resolving
  // after a later one's -- when that happened, the earlier (shorter) file list would silently
  // overwrite the already-correct one, dropping a file from the UI until a full page reload.
  const damAssetRequestSeq = new Map();

  function loadDamAssetsForProject(code) {
    if (!code) return;
    damLoadInFlight.add(code);
    state.digitalAsset.assetsByCategory[code] = {};
    state.digitalAsset.selectedFiles = {};
    const loads = [
      ...DAM_ASSET_CATEGORIES.map((cat) => loadDigitalAssets(code, cat).catch(() => {})),
      loadDeliveryData(code).catch(() => {}),
    ];
    Promise.all(loads).finally(() => damLoadInFlight.delete(code));
  }

  async function loadDeliveryData(code) {
    if (!code) return;
    try {
      const [manifestResp, tokenResp] = await Promise.all([
        fetch(`/api/project/deliverables/manifest?code=${encodeURIComponent(code)}`),
        fetch(`/api/project/delivery-token?code=${encodeURIComponent(code)}`),
      ]);
      if (manifestResp.ok) {
        const d = await manifestResp.json();
        if (!state.digitalAsset.deliveryManifest) state.digitalAsset.deliveryManifest = {};
        state.digitalAsset.deliveryManifest[code] = Array.isArray(d.entries) ? d.entries : [];
      }
      if (tokenResp.ok) {
        const d = await tokenResp.json();
        if (!state.digitalAsset.deliveryTokens) state.digitalAsset.deliveryTokens = {};
        state.digitalAsset.deliveryTokens[code] = d.tokenData || null;
      }
      render();
    } catch (err) {
      app.ui.toast(`Delivery data load failed: ${String(err?.message || err)}`);
    }
  }

  // ── Project Notes ─────────────────────────────────────────────────────────
  async function loadProjectNotes(code) {
    if (!code) return;
    state.notes.loading[code] = true;
    try {
      const resp = await fetch(`/api/project/notes?code=${encodeURIComponent(code)}`);
      const d = await resp.json();
      state.notes.byProject[code] = Array.isArray(d.notes) ? d.notes : [];
    } catch (err) {
      app.ui.toast(`Notes load failed: ${String(err?.message || err)}`);
      state.notes.byProject[code] = state.notes.byProject[code] || [];
    }
    state.notes.loading[code] = false;
    render();
  }

  async function postProjectNote(code) {
    const text = String(state.notes.draftText || "").trim();
    if (!code || !text) return;
    state.notes.posting = true;
    render();
    try {
      const resp = await fetch("/api/project/notes", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code, text, authorEmail: app.auth?.email || null }),
      });
      const d = await resp.json();
      if (!resp.ok || !d.ok) throw new Error(d.error || `HTTP ${resp.status}`);
      state.notes.byProject[code] = [d.note, ...(state.notes.byProject[code] || [])];
      state.notes.draftText = "";
    } catch (err) {
      app.ui.toast(`Note failed: ${String(err?.message || err)}`);
    }
    state.notes.posting = false;
    render();
  }

  // ── Tickets ────────────────────────────────────────────────────────────────
  async function loadProjectTickets(code) {
    if (!code) return;
    state.tickets.loading[code] = true;
    try {
      const resp = await fetch(`/api/project/tickets?code=${encodeURIComponent(code)}`);
      const d = await resp.json();
      state.tickets.byProject[code] = Array.isArray(d.tickets) ? d.tickets : [];
    } catch (err) {
      app.ui.toast(`Tickets load failed: ${String(err?.message || err)}`);
      state.tickets.byProject[code] = state.tickets.byProject[code] || [];
    }
    state.tickets.loading[code] = false;
    render();
  }

  async function loadTicketBlockingSummary() {
    try {
      const resp = await fetch("/api/project/tickets/blocking-summary");
      const d = await resp.json();
      state.tickets.blockingSummary = d.ok ? (d.summary || {}) : {};
    } catch {
      state.tickets.blockingSummary = state.tickets.blockingSummary || {};
    }
    // The Portfolio grid's HTML is memoized (_htmlCache.portfolio) and only rebuilds when
    // explicitly invalidated -- without this, a blocking badge that just loaded would never
    // actually appear until something else happened to bust the cache.
    delete _htmlCache.portfolio;
    render();
  }

  function openTicketComposer() {
    state.tickets.composerOpen = true;
    // Generated up front: attachments (file/paste/recording) upload into
    // `tickets/${id}/...` while the form is still being filled out, before the ticket
    // itself exists — the create call below reuses this same id (see server.js
    // createTicketForProject's `clientId` handling) instead of minting a second one.
    state.tickets.composerDraft = {
      id: `ticket-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      title: "", description: "", milestoneKey: "", blocksMilestone: false, attachments: [],
    };
    const code = String(state.projectSettings.selectedProjectId || "");
    const clientSlug = (state.catalog.marinas || []).find((m) => m.code === code)?.clientSlug;
    if (clientSlug && !(clientSlug in (state.digitalAsset.milestoneSchemas || {}))) {
      loadClientMilestoneSchema(clientSlug).catch(() => {});
    }
    render();
  }

  function closeTicketComposer() {
    if (ticketPendingRecording?.blobUrl) URL.revokeObjectURL(ticketPendingRecording.blobUrl);
    ticketPendingRecording = null;
    state.tickets.composerOpen = false;
    render();
  }

  async function submitTicketComposer() {
    const code = String(state.projectSettings.selectedProjectId || "");
    const draft = state.tickets.composerDraft;
    const title = String(draft.title || "").trim();
    if (!code || !title) { app.ui.toast("Ticket needs a title."); return; }
    const clientSlug = String((state.catalog.marinas || []).find((m) => m.code === code)?.clientSlug || "");
    state.tickets.posting = true;
    render();
    try {
      const resp = await fetch("/api/project/tickets", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: draft.id, code, clientSlug,
          title, description: draft.description || "",
          milestoneKey: draft.milestoneKey || "",
          blocksMilestone: !!draft.blocksMilestone,
          attachments: draft.attachments || [],
          reportedByEmail: app.auth?.email || null,
        }),
      });
      const d = await resp.json();
      if (!resp.ok || !d.ok) throw new Error(d.error || `HTTP ${resp.status}`);
      state.tickets.byProject[code] = [d.ticket, ...(state.tickets.byProject[code] || [])];
      state.tickets.composerOpen = false;
      if (draft.blocksMilestone) loadTicketBlockingSummary().catch(() => {});
      app.ui.toast(`Ticket logged: ${d.ticket.title}`);
    } catch (err) {
      app.ui.toast(`Ticket failed: ${String(err?.message || err)}`);
    }
    state.tickets.posting = false;
    render();
  }

  async function patchProjectTicket(code, id, patch) {
    try {
      const resp = await fetch(`/api/project/tickets/${encodeURIComponent(id)}`, {
        method: "PATCH", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code, updatedBy: app.auth?.email || "internal", ...patch }),
      });
      const d = await resp.json();
      if (!resp.ok || !d.ok) throw new Error(d.error || `HTTP ${resp.status}`);
      state.tickets.byProject[code] = (state.tickets.byProject[code] || []).map((t) => (t.id === id ? d.ticket : t));
      if ("blocksMilestone" in patch || "status" in patch) loadTicketBlockingSummary().catch(() => {});
    } catch (err) {
      app.ui.toast(`Update failed: ${String(err?.message || err)}`);
    }
    render();
  }

  // Notes are an unbounded, append-only log on the ticket (mirrors project Notes) --
  // there's no separate status/PATCH gate on them, staff and (via the portal route)
  // clients can both add one.
  async function postTicketNote(code, ticketId) {
    const text = String(state.tickets.noteDrafts[ticketId] || "").trim();
    if (!code || !ticketId || !text) return;
    state.tickets.notePosting[ticketId] = true;
    render();
    try {
      const resp = await fetch(`/api/project/tickets/${encodeURIComponent(ticketId)}/notes`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code, text, authorEmail: app.auth?.email || null }),
      });
      const d = await resp.json();
      if (!resp.ok || !d.ok) throw new Error(d.error || `HTTP ${resp.status}`);
      state.tickets.byProject[code] = (state.tickets.byProject[code] || []).map((t) => (t.id === ticketId ? d.ticket : t));
      state.tickets.noteDrafts[ticketId] = "";
    } catch (err) {
      app.ui.toast(`Note failed: ${String(err?.message || err)}`);
    }
    state.tickets.notePosting[ticketId] = false;
    render();
  }

  const TICKET_ATTACHMENT_ACCEPT = ".png,.jpg,.jpeg,.webp,.gif,.mp4,.webm,.mov";

  // Shared by file-input, drag-drop, and clipboard-paste — presigns, uploads via the
  // existing putToS3 XHR helper (works for any Blob, not just File objects, so the
  // screen-recording path below reuses it too), then appends to the composer draft.
  async function attachFileToTicketDraft(code, file, filenameOverride) {
    const filename = filenameOverride || file.name || `paste-${Date.now()}`;
    state.tickets.attachmentUploading = true;
    render();
    try {
      const presignResp = await fetch("/api/project/tickets/attachment/presign", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          code, ticketId: state.tickets.composerDraft.id,
          filename, contentType: file.type || "application/octet-stream", size: file.size,
        }),
      });
      const presign = await presignResp.json();
      if (!presignResp.ok || !presign.ok) throw new Error(presign.error || `HTTP ${presignResp.status}`);
      await putToS3(presign.url, file);
      state.tickets.composerDraft.attachments.push({
        key: presign.key, contentType: presign.contentType, sizeBytes: presign.size,
        kind: presign.kind, uploadedAt: new Date().toISOString(), filename: presign.filename,
      });
    } catch (err) {
      app.ui.toast(`Attachment failed: ${String(err?.message || err)}`);
    }
    state.tickets.attachmentUploading = false;
    render();
  }

  function removeTicketDraftAttachment(index) {
    state.tickets.composerDraft.attachments.splice(index, 1);
    render();
  }

  // ── Screen recording (ticket attachments) ────────────────────────────────
  // Live MediaStream/MediaRecorder objects live outside `state` (see the module-level
  // ticketRecorder* vars) since a full render() replaces the composer's innerHTML on every
  // state change — putting a live stream there would tear it down mid-recording. The
  // recording timer updates a DOM node directly for the same reason (no render() while
  // recording). Only the finished blob's object URL (a plain string) ever reaches state,
  // via ticketPendingRecording, once the user has something to preview/attach/discard.
  const TICKET_RECORDING_MAX_MS = 3 * 60 * 1000;

  async function startTicketScreenRecording() {
    if (ticketRecorder) return;
    let stream;
    try {
      // preferCurrentTab (Chrome/Edge only, silently ignored elsewhere) pre-selects this tab
      // in the browser's own share picker instead of defaulting to "Entire Screen" -- there's
      // no way to skip that picker outright in any browser (by design: a page can never start
      // capturing without an explicit, user-confirmed source choice), this just cuts the
      // "find the right tab in a list" step down to one click of "Share".
      stream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: false, preferCurrentTab: true });
    } catch (err) {
      app.ui.toast(`Screen recording not available: ${String(err?.message || err)}`);
      return;
    }
    ticketRecorderStream = stream;
    ticketRecordedChunks = [];
    const mimeType = ["video/webm;codecs=vp9", "video/webm"].find((t) => MediaRecorder.isTypeSupported?.(t)) || "video/webm";
    ticketRecorder = new MediaRecorder(stream, { mimeType });
    ticketRecorder.ondataavailable = (e) => { if (e.data && e.data.size > 0) ticketRecordedChunks.push(e.data); };
    ticketRecorder.onstop = () => {
      try { ticketRecorderStream?.getTracks?.().forEach((t) => t.stop()); } catch {}
      const blob = new Blob(ticketRecordedChunks, { type: mimeType });
      ticketPendingRecording = {
        blob, blobUrl: URL.createObjectURL(blob),
        filename: `screen-recording-${Date.now()}.webm`, contentType: mimeType.split(";")[0], kind: "video",
      };
      ticketRecorderStream = null;
      ticketRecorder = null;
      if (ticketRecordingTimer) { clearInterval(ticketRecordingTimer); ticketRecordingTimer = null; }
      render();
    };
    // Screen-share revocation via the browser's own "Stop sharing" control fires `stop` on
    // the track, not on the recorder — without this the recorder (and stale stream) would
    // just hang open with no way for the UI to know recording had actually ended.
    stream.getVideoTracks()[0]?.addEventListener("ended", () => { if (ticketRecorder?.state !== "inactive") ticketRecorder?.stop(); });
    ticketRecorder.start();
    const startedAt = Date.now();
    ticketRecordingTimer = setInterval(() => {
      const elapsedMs = Date.now() - startedAt;
      const el = containerEl?.querySelector?.("[data-ticket-recording-elapsed]");
      if (el) el.textContent = fmtElapsedMMSS(elapsedMs);
      if (elapsedMs >= TICKET_RECORDING_MAX_MS && ticketRecorder?.state !== "inactive") ticketRecorder?.stop();
    }, 500);
    render();
  }

  function stopTicketScreenRecording() {
    if (ticketRecorder && ticketRecorder.state !== "inactive") ticketRecorder.stop();
  }

  // One still frame, not a recording: grab a single frame via getDisplayMedia + canvas (the
  // ImageCapture API would be more direct but isn't universally supported), then immediately
  // stop the share -- unlike startTicketScreenRecording this never keeps a stream open or
  // touches the module-level recorder state.
  async function takeTicketScreenshot(code) {
    let stream;
    try {
      stream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: false, preferCurrentTab: true });
    } catch (err) {
      app.ui.toast(`Screenshot not available: ${String(err?.message || err)}`);
      return;
    }
    try {
      const video = document.createElement("video");
      video.srcObject = stream;
      video.muted = true;
      await video.play();
      await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
      const canvas = document.createElement("canvas");
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      canvas.getContext("2d").drawImage(video, 0, 0);
      stream.getTracks().forEach((t) => t.stop());
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
      if (!blob) throw new Error("Could not capture frame.");
      const file = new File([blob], `screenshot-${Date.now()}.png`, { type: "image/png" });
      await attachFileToTicketDraft(code, file, file.name);
    } catch (err) {
      stream.getTracks().forEach((t) => t.stop());
      app.ui.toast(`Screenshot failed: ${String(err?.message || err)}`);
    }
  }

  function fmtElapsedMMSS(ms) {
    const totalSec = Math.floor(ms / 1000);
    const m = Math.floor(totalSec / 60);
    const s = totalSec % 60;
    return `${m}:${String(s).padStart(2, "0")}`;
  }

  async function attachTicketRecording(code) {
    if (!ticketPendingRecording) return;
    const { blob, blobUrl, filename, contentType } = ticketPendingRecording;
    ticketPendingRecording = null;
    // Blob.type is a getter-only accessor (throws under strict mode if you try to mutate it
    // in place) — wrap in a File via its constructor instead, which sets name/type properly.
    const file = new File([blob], filename, { type: contentType });
    await attachFileToTicketDraft(code, file, filename);
    URL.revokeObjectURL(blobUrl);
  }

  function discardTicketRecording() {
    if (ticketPendingRecording?.blobUrl) URL.revokeObjectURL(ticketPendingRecording.blobUrl);
    ticketPendingRecording = null;
    render();
  }

  async function loadClientDeliveryToken(clientSlug) {
    if (!clientSlug) return;
    if (!state.digitalAsset.clientDeliveryTokens) state.digitalAsset.clientDeliveryTokens = {};
    try {
      const resp = await fetch(`/api/clients/delivery-token?clientSlug=${encodeURIComponent(clientSlug)}`);
      const d = await resp.json().catch(() => ({}));
      state.digitalAsset.clientDeliveryTokens[clientSlug] = d.tokenData || null;
      render();
    } catch (err) {
      state.digitalAsset.clientDeliveryTokens[clientSlug] = null;
      render();
    }
  }

  // Mirrors loadClientDeliveryToken above, against the separate field-tracking token
  // namespace (see resolveFieldTrackingToken in server.js) -- an internal, no-login link for
  // field crews, never handed to a client.
  async function loadFieldTrackingToken(clientSlug) {
    if (!clientSlug) return;
    if (!state.digitalAsset.fieldTrackingTokens) state.digitalAsset.fieldTrackingTokens = {};
    try {
      const resp = await fetch(`/api/field-tracking-token?clientSlug=${encodeURIComponent(clientSlug)}`);
      const d = await resp.json().catch(() => ({}));
      state.digitalAsset.fieldTrackingTokens[clientSlug] = d.tokenData || null;
      render();
    } catch (err) {
      state.digitalAsset.fieldTrackingTokens[clientSlug] = null;
      render();
    }
  }

  // Mirrors loadFieldTrackingToken above, but there's only ever one Invoice Wizard link
  // site-wide (it isn't scoped to a client) -- contract staff log their own hours and export
  // their own PDF/Excel invoice, nothing about it is persisted server-side beyond this token.
  async function loadInvoiceWizardToken() {
    state.digitalAsset.invoiceWizardTokenLoaded = true;
    try {
      const resp = await fetch("/api/invoice-wizard-token");
      const d = await resp.json().catch(() => ({}));
      state.digitalAsset.invoiceWizardToken = d.tokenData || null;
      render();
    } catch (err) {
      state.digitalAsset.invoiceWizardToken = null;
      render();
    }
  }

  const DEFAULT_MILESTONE_SCHEMA = [
    { key: "planned",        label: "Planned" },
    { key: "acquisition",    label: "Acquisition" },
    { key: "processing",     label: "Processing" },
    { key: "reporting",      label: "Reporting" },
    { key: "prelimDelivery", label: "Prelim Delivery" },
    { key: "shmQaQc",        label: "SHM QA/QC" },
    { key: "finalDelivery",  label: "Final Delivery" },
  ];

  function labelToKey(label) {
    return label.trim()
      .replace(/[^a-zA-Z0-9 ]/g, " ")
      .trim()
      .split(/\s+/)
      .map((w, i) => i === 0 ? w.toLowerCase() : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join("") || "step";
  }

  function getClientMilestoneSchema(clientSlug) {
    const loaded = (state.digitalAsset.milestoneSchemas || {})[clientSlug];
    return Array.isArray(loaded) ? loaded : DEFAULT_MILESTONE_SCHEMA;
  }

  // Most-complete-first sort score. A marina actively In Progress or paused On Hold on its
  // next milestone has genuinely made more progress than one where that same milestone hasn't
  // been touched at all -- those get half credit here (Complete = full credit), mirroring
  // services/ais-api/src/server.js's projectSortScore, so two marinas tied on Complete-count
  // don't just fall back to alphabetical order regardless of how much further one of them has
  // actually moved.
  function marinaSortScore(marina, statusCols) {
    const sc = marina?.statusColumns || {};
    let appl = 0, score = 0;
    for (const col of statusCols) {
      const v = sc[col.key] || "Not Started";
      if (v === "N/A") continue;
      appl++;
      if (v === "Complete") score += 1;
      else if (v === "In Progress" || v === "On Hold") score += 0.5;
    }
    return appl ? score / appl : 0;
  }

  async function loadClientMilestoneSchema(clientSlug) {
    if (!clientSlug) return;
    if (!state.digitalAsset.milestoneSchemas) state.digitalAsset.milestoneSchemas = {};
    try {
      const resp = await fetch(`/api/clients/${encodeURIComponent(clientSlug)}/milestone-schema`);
      const d = await resp.json().catch(() => ({}));
      state.digitalAsset.milestoneSchemas[clientSlug] = Array.isArray(d.schema) ? d.schema : DEFAULT_MILESTONE_SCHEMA;
    } catch {
      state.digitalAsset.milestoneSchemas[clientSlug] = DEFAULT_MILESTONE_SCHEMA;
    }
    render();
  }

  async function saveClientMilestoneSchema(clientSlug, schema) {
    const resp = await fetch(`/api/clients/${encodeURIComponent(clientSlug)}/milestone-schema`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ schema }),
    });
    const d = await resp.json().catch(() => ({}));
    if (!resp.ok) throw new Error(d.error || `HTTP ${resp.status}`);
    if (!state.digitalAsset.milestoneSchemas) state.digitalAsset.milestoneSchemas = {};
    state.digitalAsset.milestoneSchemas[clientSlug] = d.schema;
    render();
  }

  function isAdminUser() {
    const groups = Array.isArray(app.auth?.groups) ? app.auth.groups : [];
    return groups.some((g) => String(g || "").trim().toLowerCase() === "admin");
  }

  // Schedule History: lets an admin see and undo edits to a portfolio group's planned
  // Acquisition/Processing date windows (client.json's portfolioGroups.meta) -- born from a
  // 2026-08-27 incident where a client-portal Gantt-bar drag fired 9 rapid saves in under two
  // minutes with no way to see or undo it short of an S3 console. Backed entirely by S3 object
  // versioning on clients/<slug>/client.json (server collapses bursts into checkpoints and
  // diffs each against the previous one -- see buildScheduleCheckpoints in server.js); restoring
  // only ever touches the four planned-date fields, never group color/visibility/officeId/etc.
  async function loadScheduleHistory(clientSlug) {
    if (!clientSlug) return;
    try {
      const resp = await fetch(`/api/clients/${encodeURIComponent(clientSlug)}/schedule-history`);
      const d = await resp.json().catch(() => ({}));
      if (!d.ok) return;
      if (!state.scheduleHistory) state.scheduleHistory = {};
      state.scheduleHistory[clientSlug] = Array.isArray(d.checkpoints) ? d.checkpoints : [];
    } catch {}
    render();
  }

  async function restoreScheduleCheckpoint(clientSlug, versionId) {
    try {
      const resp = await fetch(`/api/clients/${encodeURIComponent(clientSlug)}/schedule-history/${encodeURIComponent(versionId)}/restore`, {
        method: "POST",
      });
      const d = await resp.json().catch(() => ({}));
      if (!resp.ok || !d.ok) throw new Error(d.error || `HTTP ${resp.status}`);
      const restored = d.restoredGroups?.length || 0;
      const skipped = d.skippedGroups?.length || 0;
      app.ui.toast(`Schedule restored (${restored} group${restored === 1 ? "" : "s"})${skipped ? `, ${skipped} skipped (group no longer exists)` : ""}.`);
      await loadClientPortfolioGroups(clientSlug);
      await loadScheduleHistory(clientSlug);
    } catch (err) {
      app.ui.toast(`Restore failed: ${String(err?.message || err)}`);
    }
  }

  // The Schedule History popover is rendered into a dedicated document.body-appended node
  // (a "portal") instead of inline next to its toggle button. The button lives inside a
  // <details class="toolset-group"> panel, and that class has `overflow:hidden` plus
  // `will-change:transform` (dashboard.css) -- the latter makes the panel a containing block
  // for `position:fixed` descendants too, so a naive fixed-position popover nested inside it
  // would still get clipped at the panel's edge instead of floating above the next section.
  // Escaping to document.body sidesteps both.
  let _scheduleHistoryPortalEl = null;
  let _scheduleHistoryPortalHandlers = null;

  function closeScheduleHistoryPortal() {
    if (_scheduleHistoryPortalEl) {
      _scheduleHistoryPortalEl.remove();
      _scheduleHistoryPortalEl = null;
    }
    if (_scheduleHistoryPortalHandlers) {
      window.removeEventListener("resize", _scheduleHistoryPortalHandlers.reposition);
      document.removeEventListener("scroll", _scheduleHistoryPortalHandlers.reposition, true);
      _scheduleHistoryPortalHandlers = null;
    }
  }

  // Moves the (already-built) portal to sit under the given button, without touching its
  // innerHTML. Kept separate from the content rebuild below because this also runs on every
  // "scroll" event anywhere in the document (needed so the popover follows its anchor button
  // if a scroll happens elsewhere on the page) -- including scrolls of the popover's OWN
  // checkpoint list. Rebuilding innerHTML on every one of those would destroy and recreate the
  // list's child nodes, which resets ITS OWN scrollTop to 0 -- i.e. the list would snap back to
  // the top the instant you tried to scroll it. Repositioning alone has no such side effect.
  function positionScheduleHistoryPortal(btn) {
    if (!_scheduleHistoryPortalEl) return;
    const rect = btn.getBoundingClientRect();
    const panelEl = _scheduleHistoryPortalEl.firstElementChild;
    const panelWidth = panelEl?.offsetWidth || 320;
    const left = Math.max(4, Math.min(window.innerWidth - panelWidth - 4, rect.right - panelWidth));
    const top = Math.min(window.innerHeight - 20, rect.bottom + 4);
    if (panelEl) { panelEl.style.left = `${left}px`; panelEl.style.top = `${top}px`; }
  }

  // Called after every render (both the bundled Project Workspace layout and the standalone
  // Project Portfolio tool) -- no-ops immediately unless a panel is actually open, and tears
  // itself down once the toggle button it's anchored to no longer exists (client switched,
  // panel closed, tool unmounted). Rebuilds the portal's content, unlike
  // positionScheduleHistoryPortal above -- only call this for actual state changes.
  function syncScheduleHistoryPortal() {
    const clientSlug = state.scheduleHistoryPanelOpen;
    if (!clientSlug) { closeScheduleHistoryPortal(); return; }
    const btn = document.querySelector(`[data-schedule-history-toggle="${CSS.escape(clientSlug)}"]`);
    if (!btn) { closeScheduleHistoryPortal(); return; }
    if (!_scheduleHistoryPortalEl) {
      _scheduleHistoryPortalEl = document.createElement("div");
      _scheduleHistoryPortalEl.addEventListener("click", onClick);
      document.body.appendChild(_scheduleHistoryPortalEl);
    }
    _scheduleHistoryPortalEl.innerHTML = renderScheduleHistoryPanel(clientSlug);
    positionScheduleHistoryPortal(btn);
    if (!_scheduleHistoryPortalHandlers) {
      const reposition = () => {
        const anchor = document.querySelector(`[data-schedule-history-toggle="${CSS.escape(state.scheduleHistoryPanelOpen || "")}"]`);
        if (anchor) positionScheduleHistoryPortal(anchor);
      };
      window.addEventListener("resize", reposition);
      document.addEventListener("scroll", reposition, true);
      _scheduleHistoryPortalHandlers = { reposition };
    }
  }

  function getGroupMeta(clientId, groupName) {
    const defaults = { color: null, visible: true, officeId: null, flyIn: false, flyInAirport: "", includeInCombined: false };
    return Object.assign({}, defaults, state.portfolioGroupMeta?.[clientId]?.[groupName] || {});
  }

  function setGroupMetaField(clientId, groupName, patch) {
    if (!state.portfolioGroupMeta) state.portfolioGroupMeta = {};
    if (!state.portfolioGroupMeta[clientId]) state.portfolioGroupMeta[clientId] = {};
    state.portfolioGroupMeta[clientId][groupName] = Object.assign(
      { color: null, visible: true, officeId: null, flyIn: false, flyInAirport: "", includeInCombined: false },
      state.portfolioGroupMeta[clientId][groupName] || {},
      patch,
    );
  }

  async function loadClientPortfolioGroups(clientSlug) {
    if (!clientSlug) return;
    try {
      const resp = await fetch(`/api/clients/${encodeURIComponent(clientSlug)}/portfolio-groups`);
      const d = await resp.json().catch(() => ({}));
      if (!d.ok) return;
      if (Array.isArray(d.names) && d.names.length) {
        if (!state.portfolioCustomGroups) state.portfolioCustomGroups = {};
        state.portfolioCustomGroups[clientSlug] = d.names;
      }
      if (d.meta && typeof d.meta === "object") {
        if (!state.portfolioGroupMeta) state.portfolioGroupMeta = {};
        const existing = state.portfolioGroupMeta[clientSlug];
        // Merge server base with in-memory data so in-flight user changes
        // (e.g. an includeInCombined toggle before the PUT completes) are preserved.
        state.portfolioGroupMeta[clientSlug] = existing
          ? Object.assign({}, d.meta, existing)
          : d.meta;
      }
    } catch {}
    render();
  }

  async function saveClientPortfolioGroups(clientSlug) {
    if (!clientSlug) return;
    const names = state.portfolioCustomGroups?.[clientSlug] || [];
    const meta = state.portfolioGroupMeta?.[clientSlug] || {};
    await fetch(`/api/clients/${encodeURIComponent(clientSlug)}/portfolio-groups`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ names, meta }),
    });
  }

  function catalogActiveMarinasSorted() {
    return (Array.isArray(state.catalog.marinas) ? state.catalog.marinas : [])
      .filter((m) => m.active)
      .sort((a, b) => String(a.code || "").localeCompare(String(b.code || ""), undefined, { sensitivity: "base" }));
  }

  async function loadDigitalAssets(code, category) {
    const seqKey = `${code}::${category}`;
    const mySeq = (damAssetRequestSeq.get(seqKey) || 0) + 1;
    damAssetRequestSeq.set(seqKey, mySeq);
    try {
      const resp = await fetch(`/api/project/assets?code=${encodeURIComponent(code)}&category=${encodeURIComponent(category)}`);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      if (damAssetRequestSeq.get(seqKey) !== mySeq) return; // a newer request for this code/category has since been issued -- this response is stale, discard it
      if (!state.digitalAsset.assetsByCategory[code]) state.digitalAsset.assetsByCategory[code] = {};
      state.digitalAsset.assetsByCategory[code][category] = Array.isArray(data.files) ? data.files : [];
      if (!state.digitalAsset.foldersByCategory) state.digitalAsset.foldersByCategory = {};
      state.digitalAsset.foldersByCategory[`${code}::${category}`] = Array.isArray(data.folders) ? data.folders : [];
      render();
      if (category === "las-files" && code === state.projectSettings.selectedProjectId) {
        syncPointCloudRuntimeFromDam(code, { allowEmpty: true });
        loadDepthLayer(code);
        loadLasCrsStatus(code);
      }
    } catch (err) {
      app.ui.toast(`Asset load failed: ${String(err?.message || err)}`);
    }
  }

  // Each LAS/LAZ's standing against the project CRS (GET /api/project/las/crs-status): the
  // server checks anything new or changed and reuses stored results otherwise, so this is
  // cheap after the first pass. Rendered as a badge on each file row.
  const lasCrsStatusSeq = new Map();
  async function loadLasCrsStatus(code) {
    const mySeq = (lasCrsStatusSeq.get(code) || 0) + 1;
    lasCrsStatusSeq.set(code, mySeq);
    if (!state.digitalAsset.lasCrsStatus) state.digitalAsset.lasCrsStatus = {};
    state.digitalAsset.lasCrsStatus[code] = { ...(state.digitalAsset.lasCrsStatus[code] || {}), loading: true };
    try {
      const resp = await fetch(`/api/project/las/crs-status?marina=${encodeURIComponent(code)}`, { cache: "no-store" });
      const data = await resp.json();
      if (lasCrsStatusSeq.get(code) !== mySeq) return;
      if (!resp.ok || data?.ok === false) throw new Error(data?.error || `HTTP ${resp.status}`);
      const byKey = {};
      for (const f of data.files || []) byKey[f.key] = f;
      state.digitalAsset.lasCrsStatus[code] = { loading: data.pending > 0, byKey, projectCrs: data.projectCrs || null };
      // Files still being checked come back as pending; ask again until they've all landed.
      if (data.pending > 0 && code === state.projectSettings.selectedProjectId) setTimeout(() => loadLasCrsStatus(code), 6000);
    } catch (err) {
      if (lasCrsStatusSeq.get(code) !== mySeq) return;
      state.digitalAsset.lasCrsStatus[code] = { loading: false, byKey: {}, error: String(err?.message || err) };
    }
    render();
  }
  const LAS_CRS_BADGE = {
    ok: ["CRS ✓", "ok"], "label-missing": ["no CRS label", "warn"], "label-wrong": ["CRS label wrong", "bad"],
    "datum-differs": ["CRS datum?", "warn"], "data-other-crs": ["not in project CRS", "bad"], "no-fit": ["off site", "bad"],
    unverified: ["CRS unverified", "warn"], "no-project-crs": ["project CRS unset", "warn"],
  };
  const LAS_CRS_BADGE_STYLE = {
    ok: "background:rgba(34,197,94,0.12);color:#4ade80;border:1px solid rgba(34,197,94,0.3);",
    warn: "background:rgba(251,191,36,0.15);color:#fbbf24;border:1px solid rgba(251,191,36,0.3);",
    bad: "background:rgba(248,113,113,0.15);color:#f87171;border:1px solid rgba(248,113,113,0.3);",
    info: "background:rgba(148,163,184,0.12);color:var(--muted);border:1px solid rgba(148,163,184,0.25);",
  };
  function lasCrsBadgeHtml(code, key) {
    const st = state.digitalAsset.lasCrsStatus?.[code];
    const base = "font-size:9px;padding:1px 4px;border-radius:3px;flex-shrink:0;white-space:nowrap;";
    const fixJob = lasCrsFixJobsByKey.get(key);
    if (fixJob && (fixJob.status === "queued" || fixJob.status === "running")) {
      return `<span style="${base}${LAS_CRS_BADGE_STYLE.info}" title="${esc(fixJob.message || "")}">fixing ${Number(fixJob.progressPct) || 0}%</span>`;
    }
    if (!st) return "";
    const e = st.byKey?.[key];
    if (!e || e.pending) return st.loading ? `<span style="${base}${LAS_CRS_BADGE_STYLE.info}" title="Checking this file against the project CRS">CRS…</span>` : "";
    if (e.error) return `<span style="${base}${LAS_CRS_BADGE_STYLE.info}" title="${esc(e.error)}">CRS ?</span>`;
    const pc = e.projectCheck || {};
    let [label, level] = LAS_CRS_BADGE[pc.status] || ["CRS ?", "info"];
    if (pc.status === "ok" && pc.filename?.status === "wrong") [label, level] = ["filename CRS", "warn"];
    const style = `${base}${LAS_CRS_BADGE_STYLE[pc.level] || LAS_CRS_BADGE_STYLE[level]}`;
    const fixedNote = e.fixedBy ? ` Fixed copy: ${String(e.fixedBy).split("/").pop()}.` : "";
    if (Array.isArray(pc.fixes) && pc.fixes.length && !e.fixedBy) {
      return `<button data-las-crs-fix="${esc(key)}" data-las-crs-fix-code="${esc(code)}" style="${style}cursor:pointer;" title="${esc(`${pc.message || ""} Click to fix.`)}">${esc(label)} · fix</button>`;
    }
    return `<span style="${style}" title="${esc((pc.message || "") + fixedNote)}">${esc(e.fixedBy ? `${label} · fixed` : label)}</span>`;
  }

  // Phase 2: write a corrected copy of a LAS (relabel / reproject / rename, per the server's
  // plan). The server re-derives the plan itself; this just shows it, asks, and follows the job.
  const lasCrsFixJobsByKey = new Map();
  async function startLasCrsFix(code, key) {
    if (lasCrsFixJobsByKey.get(key)?.status === "running") return;
    let plan;
    try {
      const resp = await fetch(`/api/project/las/crs-fix/plan?marina=${encodeURIComponent(code)}&key=${encodeURIComponent(key)}`, { cache: "no-store" });
      const data = await resp.json();
      if (!resp.ok || !data?.ok) throw new Error(data?.error || `HTTP ${resp.status}`);
      plan = data.plan;
    } catch (err) {
      app.ui.toast(`Can't plan a fix: ${String(err?.message || err)}`);
      return;
    }
    const lines = [
      `${key.split("/").pop()}`, "", plan.message, "", "This will:", ...plan.steps.map((t) => `• ${t}`),
    ];
    if (plan.movesPoints) lines.push("", "This changes the coordinates of every point.");
    if (plan.needsConfirm) lines.push("", "Both CRSs place the data at the site, so this is a judgment call: only continue if you know the data was processed in the project's CRS.");
    if (!window.confirm(lines.join("\n"))) return;
    try {
      const resp = await fetch("/api/project/las/crs-fix", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ marina: code, key, confirm: true, requestedBy: app.auth?.email || null }),
      });
      const data = await resp.json();
      if (!resp.ok || !data?.ok) throw new Error(data?.error || `HTTP ${resp.status}`);
      lasCrsFixJobsByKey.set(key, { status: "queued", progressPct: 0, message: "Queued..." });
      render();
      pollLasCrsFix(code, key, data.jobId);
    } catch (err) {
      app.ui.toast(`Fix failed to start: ${String(err?.message || err)}`);
    }
  }
  async function pollLasCrsFix(code, key, jobId) {
    let job = null;
    try {
      const resp = await fetch(`/api/project/las/crs-fix/jobs/${encodeURIComponent(jobId)}`, { cache: "no-store" });
      const data = await resp.json();
      if (!resp.ok || !data?.ok) throw new Error(data?.error || `HTTP ${resp.status}`);
      job = data.job;
    } catch (err) {
      lasCrsFixJobsByKey.delete(key);
      app.ui.toast(`Lost track of the fix: ${String(err?.message || err)}`);
      render();
      return;
    }
    lasCrsFixJobsByKey.set(key, job);
    render();
    if (job.status === "queued" || job.status === "running") { setTimeout(() => pollLasCrsFix(code, key, jobId), 3000); return; }
    lasCrsFixJobsByKey.delete(key);
    app.ui.toast(job.status === "done" ? job.message : `Fix failed: ${job.error || job.message}`);
    await loadDigitalAssets(code, "las-files");
  }

  async function moveAssetToFolder(fromKey, folderName, catKey) {
    const filename = fromKey.split("/").pop();
    // Resolve against the category root so moving a file that's already inside a
    // folder lands in the target folder, not nested under its current one.
    const cat = catKey.split("::")[1] || "";
    const catIdx = cat ? fromKey.indexOf(`/${cat}/`) : -1;
    const catRoot = catIdx >= 0 ? fromKey.slice(0, catIdx + cat.length + 1) : fromKey.split("/").slice(0, -1).join("/");
    const toKey = `${catRoot}/${folderName}/${filename}`;
    if (toKey === fromKey) return;
    try {
      const resp = await fetch("/api/project/asset/move", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ fromKey, toKey }),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const [code, cat] = catKey.split("::");
      if (code && cat) await loadDigitalAssets(code, cat);
    } catch (err) {
      app.ui.toast(`Move failed: ${String(err?.message || err)}`);
    }
  }

  async function deleteAsset(key) {
    if (!window.confirm(`Delete ${key.split("/").pop()}?`)) return;
    try {
      const resp = await fetch(`/api/project/asset?key=${encodeURIComponent(key)}`, { method: "DELETE" });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const parts = key.split("/");
      const code = parts[1];
      const category = parts[2];
      app.log("dam.asset_deleted", { filename: key.split("/").pop(), marina: code, category });
      if (code && category) await loadDigitalAssets(code, category);
    } catch (err) {
      app.ui.toast(`Delete failed: ${String(err?.message || err)}`);
    }
  }

  async function loadDepthLayer(code) {
    try {
      const r = await fetch(`/api/depth/layer?marina=${encodeURIComponent(code)}`);
      if (!r.ok) return;
      const data = await r.json();
      if (!state.digitalAsset.depthLayers) state.digitalAsset.depthLayers = {};
      state.digitalAsset.depthLayers[code] = { layer: data.layer, rasterReady: data.rasterReady, buildJob: data.buildJob };
      render();
    } catch {}
  }

  function startDepthBuildPoll(code) {
    const poll = async () => {
      if (!containerEl) return;
      await loadDepthLayer(code);
      if (!containerEl) return;
      const dl = state.digitalAsset.depthLayers?.[code];
      const job = dl?.buildJob;
      if (dl?.rasterReady) { app.ui.toast(`Depth raster ready for ${code}`); return; }
      if (job?.status === "error") { app.ui.toast(`Depth build failed: ${job.message || "unknown error"}`); return; }
      // Keep polling until rasterReady — don't stop just because job status is
      // "done" or null (server restarts wipe the in-memory job map mid-build).
      setTimeout(poll, 3000);
    };
    setTimeout(poll, 2000);
  }

  function startCombineLasPoll(jobId, code, cat) {
    const poll = async () => {
      if (!containerEl) return;
      try {
        const r = await fetch(`/api/project/las/combine/${encodeURIComponent(jobId)}`);
        if (!containerEl) return;
        if (!r.ok) {
          // Job map is in-memory only (server restart wipes it mid-combine) -- the merge
          // itself may well have already landed in S3, so refresh the list rather than
          // just reporting an error into the void.
          app.ui.toast(`Combine status unknown (job not found) — refreshing file list…`);
          await loadDigitalAssets(code, cat);
          render();
          return;
        }
        const d = await r.json();
        if (d.status === "completed") {
          app.ui.toast(`Combine complete: ${d.targetKey?.split("/").pop() || "new file"}`);
          await loadDigitalAssets(code, cat);
          render();
          return;
        }
        if (d.status === "failed") {
          app.ui.toast(`Combine failed: ${d.error || "unknown error"}`);
          return;
        }
      } catch {}
      setTimeout(poll, 3000);
    };
    setTimeout(poll, 1500);
  }

  function startMinsasMosaicPoll(jobId, code, cat) {
    const poll = async () => {
      if (!containerEl) return;
      try {
        const r = await fetch(`/api/project/minsas/mosaic/${encodeURIComponent(jobId)}`);
        if (!containerEl) return;
        if (!r.ok) {
          app.ui.toast(`Mosaic status unknown (job not found) — refreshing file list…`);
          await loadDigitalAssets(code, cat);
          render();
          return;
        }
        const d = await r.json();
        if (d.status === "completed") {
          app.ui.toast(`Mosaic complete: ${d.targetKey?.split("/").pop() || "new file"}`);
          await loadDigitalAssets(code, cat);
          render();
          return;
        }
        if (d.status === "failed") {
          app.ui.toast(`Mosaic failed: ${d.error || "unknown error"}`);
          return;
        }
      } catch {}
      setTimeout(poll, 3000);
    };
    setTimeout(poll, 1500);
  }

  async function setDepthLayer(code, s3Key, sourceEpsg, nativeUnit) {
    // Optimistic state — show "queued" immediately before fetch resolves
    if (!state.digitalAsset.depthLayers) state.digitalAsset.depthLayers = {};
    state.digitalAsset.depthLayers[code] = {
      ...(state.digitalAsset.depthLayers[code] || {}),
      layer: { s3Key, sourceEpsg, nativeUnit },
      rasterReady: false,
      buildJob: { status: "running", progressPct: 0, message: "Queued…" },
    };
    render();
    try {
      const r = await fetch("/api/depth/set-layer", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ marina: code, s3Key, sourceEpsg, nativeUnit }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        // Clear the optimistic state so the badge doesn't stay stuck on "Queued…"
        state.digitalAsset.depthLayers[code] = { ...(state.digitalAsset.depthLayers[code] || {}), buildJob: null };
        render();
        app.ui.toast(`Set depth layer failed: ${d.error || r.status}`);
        return;
      }
    } catch (err) {
      state.digitalAsset.depthLayers[code] = { ...(state.digitalAsset.depthLayers[code] || {}), buildJob: null };
      render();
      app.ui.toast(`Set depth layer failed: ${err?.message || err}`);
      return;
    }
    startDepthBuildPoll(code);
  }

  async function downloadAsset(key) {
    try {
      const resp = await fetch(`/api/s3/presign/download?key=${encodeURIComponent(key)}`);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      if (!data.url) throw new Error("No download URL returned.");
      const a = document.createElement("a");
      a.href = data.url;
      a.download = key.split("/").pop() || "download";
      a.target = "_blank";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (err) {
      app.ui.toast(`Download failed: ${String(err?.message || err)}`);
    }
  }

  async function addDamSpatialToMap(key) {
    if (!state.digitalAsset.spatialLayersOnMap) state.digitalAsset.spatialLayersOnMap = {};
    const existingId = state.digitalAsset.spatialLayersOnMap[key];
    if (existingId) {
      try { app.services.layers.removeLayer(existingId); } catch {}
      delete state.digitalAsset.spatialLayersOnMap[key];
      render();
      return;
    }
    const name = key.split("/").pop() || key;
    const ext = name.split(".").pop().toLowerCase();
    try {
      app.ui.toast(`Loading ${name}…`);
      const presignResp = await fetch(`/api/s3/presign/download?key=${encodeURIComponent(key)}`);
      if (!presignResp.ok) throw new Error(`HTTP ${presignResp.status}`);
      const { url } = await presignResp.json();
      const resp = await fetch(url);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const buf = await resp.arrayBuffer();
      let geojson;
      if (ext === "geojson" || ext === "json") {
        geojson = JSON.parse(new TextDecoder().decode(buf));
      } else if (ext === "kml") {
        const doc = new DOMParser().parseFromString(new TextDecoder().decode(buf), "text/xml");
        geojson = toGeoJSON.kml(doc);
      } else if (ext === "kmz") {
        const zip = await JSZip.loadAsync(buf);
        const kmlEntry = Object.values(zip.files).find(f => f.name.endsWith(".kml"));
        if (!kmlEntry) throw new Error("No KML found in KMZ");
        const doc = new DOMParser().parseFromString(await kmlEntry.async("string"), "text/xml");
        geojson = toGeoJSON.kml(doc);
      } else if (ext === "zip") {
        geojson = await shp(buf);
      } else {
        throw new Error(`Unsupported format: .${ext}`);
      }
      if (!geojson) throw new Error("File produced no GeoJSON");
      const leafletLayer = L.geoJSON(geojson, {
        style: { color: "#38bdf8", weight: 2, fillOpacity: 0.1 },
        pointToLayer: (_feat, latlng) => L.circleMarker(latlng, { radius: 5, color: "#38bdf8", fillOpacity: 0.6 }),
        onEachFeature: (feature, layer) => {
          const props = feature.properties;
          if (!props) return;
          const rows = Object.entries(props)
            .filter(([, v]) => v != null && v !== "")
            .map(([k, v]) => `<tr><td style="padding:1px 6px 1px 0;color:#94a3b8;font-size:11px;white-space:nowrap;">${k}</td><td style="font-size:11px;">${v}</td></tr>`)
            .join("");
          if (rows) layer.bindPopup(`<table>${rows}</table>`, { maxWidth: 300 });
        },
      });
      const layerId = app.services.layers.addLayer("image-generator", {
        name,
        kind: "overlay",
        type: "leaflet-group",
        leaflet: leafletLayer,
        volatile: true,
        meta: { source: "dam-spatial-layer", damKey: key },
      });
      state.digitalAsset.spatialLayersOnMap[key] = layerId;
      try { const b = leafletLayer.getBounds(); if (b?.isValid()) map.fitBounds(b.pad(0.1)); } catch {}
      app.ui.toast(`Loaded: ${name}`);
      render();
    } catch (err) {
      app.ui.toast(`Failed to load: ${String(err?.message || err)}`);
    }
  }

  // Mirrors addDamSpatialToMap's toggle-by-key pattern (shares spatialLayersOnMap so a
  // key can only ever be on the map once, regardless of which DAM category loaded it),
  // but the raster path can't be parsed client-side -- it goes through the same
  // /api/qimera/cogify -> XYZ-tile flow the Project Settings background-GeoTIFF importer
  // already uses (image-generator's marinaGeoTiffById flow), just scoped to one DAM file
  // instead of one per-marina background layer, and with no sourceEpsg sent: cogify now
  // reads the file's own embedded CRS via gdalinfo when it isn't told one.
  async function toggleDamGeotiffOnMap(key, code) {
    if (!state.digitalAsset.spatialLayersOnMap) state.digitalAsset.spatialLayersOnMap = {};
    const existingId = state.digitalAsset.spatialLayersOnMap[key];
    if (existingId) {
      try { app.services.layers.removeLayer(existingId); } catch {}
      delete state.digitalAsset.spatialLayersOnMap[key];
      render();
      return;
    }
    const name = key.split("/").pop() || key;
    if (!state.digitalAsset.geotiffLoadingKeys) state.digitalAsset.geotiffLoadingKeys = {};
    if (!state.digitalAsset.geotiffLoadingProgress) state.digitalAsset.geotiffLoadingProgress = {};
    state.digitalAsset.geotiffLoadingKeys[key] = true;
    delete state.digitalAsset.geotiffLoadingProgress[key];
    render();
    // cogify is one long synchronous request with no progress of its own to return -- poll a
    // side-channel status endpoint (keyed by this same s3Key) that reports GDAL's own real
    // progress meter server-side, so the button can show "Warping 40%" instead of sitting on
    // static dots for however many minutes a large source takes.
    let progressPollTimer = setTimeout(async function pollCogifyProgress() {
      try {
        const r = await fetch(`/api/qimera/cogify/progress?s3Key=${encodeURIComponent(key)}`);
        const d = await r.json().catch(() => null);
        if (d?.ok && d.step) { state.digitalAsset.geotiffLoadingProgress[key] = { step: d.step, pct: d.pct }; render(); }
      } catch {}
      progressPollTimer = setTimeout(pollCogifyProgress, 1500);
    }, 1500);
    try {
      app.ui.toast(`Converting ${name} to map tiles…`);
      const cogifyResp = await fetch("/api/qimera/cogify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ s3Key: key, marina: code }),
      });
      const payload = await cogifyResp.json().catch(() => ({}));
      if (!cogifyResp.ok || payload?.ok === false) throw new Error(String(payload?.error || `COG conversion failed (${cogifyResp.status})`));
      const tileUrl = String(payload?.tileUrl || "").trim();
      if (!tileUrl) throw new Error("COG conversion completed but no tile URL was returned.");
      let bounds = null;
      let minNativeZoom = null;
      const tileJsonUrl = String(payload?.tileJsonUrl || "").trim();
      if (tileJsonUrl) {
        try {
          const tj = await (await fetch(tileJsonUrl)).json();
          const b = Array.isArray(tj?.bounds) ? tj.bounds : null;
          if (b && b.length === 4 && b.every((v) => Number.isFinite(Number(v)))) bounds = [[b[1], b[0]], [b[3], b[2]]];
          // TiTiler's tilejson minzoom is the lowest zoom it'll actually render (COGs built
          // from a small-extent source can start well above 0) -- without passing it through,
          // zooming out past that floor requests tiles TiTiler 404s on instead of scaling
          // down from the nearest native zoom the way maxNativeZoom already does at the top end.
          if (Number.isFinite(Number(tj?.minzoom))) minNativeZoom = Number(tj.minzoom);
        } catch {}
      }
      const layerId = app.services.layers.addLayer("image-generator", {
        name,
        type: "xyz",
        url: tileUrl,
        opacity: 0.92,
        options: {
          crossOrigin: true, maxNativeZoom: 22, maxZoom: 24,
          ...(minNativeZoom != null ? { minNativeZoom } : {}),
          ...(bounds ? { bounds } : {}),
        },
        meta: { source: "dam-geotiff-layer", damKey: key, cogPath: String(payload?.cogPath || "") },
      });
      state.digitalAsset.spatialLayersOnMap[key] = layerId;
      if (bounds) { try { map.fitBounds(bounds); } catch {} }
      app.ui.toast(`Loaded: ${name}`);
    } catch (err) {
      app.ui.toast(`Failed to load: ${String(err?.message || err)}`);
    } finally {
      clearTimeout(progressPollTimer);
      delete state.digitalAsset.geotiffLoadingKeys[key];
      delete state.digitalAsset.geotiffLoadingProgress[key];
      render();
    }
  }

  // Same toggle-by-key shape as addDamSpatialToMap/toggleDamGeotiffOnMap above, for a
  // LAS/LAZ file's bounding box -- can't be computed client-side (needs the file's actual
  // point bounds + the project's CRS), so this hits a small backend route instead of
  // parsing anything here.
  async function toggleDamLasFootprintOnMap(key, code) {
    if (!state.digitalAsset.spatialLayersOnMap) state.digitalAsset.spatialLayersOnMap = {};
    const existingId = state.digitalAsset.spatialLayersOnMap[key];
    if (existingId) {
      try { app.services.layers.removeLayer(existingId); } catch {}
      delete state.digitalAsset.spatialLayersOnMap[key];
      render();
      return;
    }
    const name = key.split("/").pop() || key;
    if (!state.digitalAsset.lasFootprintLoadingKeys) state.digitalAsset.lasFootprintLoadingKeys = {};
    state.digitalAsset.lasFootprintLoadingKeys[key] = true;
    render();
    try {
      app.ui.toast(`Reading footprint for ${name}…`);
      const r = await fetch(`/api/project/las/footprint?key=${encodeURIComponent(key)}&marina=${encodeURIComponent(code)}`);
      const payload = await r.json().catch(() => ({}));
      if (!r.ok || payload?.ok === false) throw new Error(String(payload?.error || `Footprint failed (${r.status})`));
      const leafletLayer = L.geoJSON(payload.geojson, { style: { color: "#38bdf8", weight: 2, fillOpacity: 0.08 } });
      const layerId = app.services.layers.addLayer("image-generator", {
        name, kind: "overlay", type: "leaflet-group", leaflet: leafletLayer, volatile: true,
        meta: { source: "dam-las-footprint", damKey: key },
      });
      state.digitalAsset.spatialLayersOnMap[key] = layerId;
      try { const b = leafletLayer.getBounds(); if (b?.isValid()) map.fitBounds(b.pad(0.1)); } catch {}
      app.ui.toast(`Footprint loaded: ${name}`);
    } catch (err) {
      app.ui.toast(`Failed to load footprint: ${String(err?.message || err)}`);
    } finally {
      delete state.digitalAsset.lasFootprintLoadingKeys[key];
      render();
    }
  }

  function damFormatBytes(b) {
    if (b < 1024) return `${b} B`;
    if (b < 1048576) return `${(b / 1024).toFixed(1)} KB`;
    if (b < 1073741824) return `${(b / 1048576).toFixed(1)} MB`;
    return `${(b / 1073741824).toFixed(2)} GB`;
  }

  // One progress bar/label per file (keyed "<catKey>::<fileKey>"), updated directly in the DOM
  // on every byte-progress tick instead of a full render() -- a multi-file batch would otherwise
  // re-render its whole row list on every XHR progress event.
  function damFileEntry(catKey, fileKey) {
    const list = state.digitalAsset.uploadingFilesByCategory[catKey];
    return Array.isArray(list) ? list.find((f) => f.key === fileKey) : null;
  }
  function damUpdateFileProgressDom(catKey, fileKey, uploadedBytes, totalBytes) {
    if (!containerEl) return;
    const pct = totalBytes > 0 ? Math.round((uploadedBytes / totalBytes) * 100) : 0;
    const esc2 = (s) => String(s || "").replace(/"/g, "&quot;");
    const ufKey = `${catKey}::${fileKey}`;
    const bar = containerEl.querySelector(`[data-progress-bar="${esc2(ufKey)}"]`);
    const label = containerEl.querySelector(`[data-progress-label="${esc2(ufKey)}"]`);
    if (bar) bar.style.width = `${pct}%`;
    if (label) label.textContent = `${damFormatBytes(uploadedBytes)} / ${damFormatBytes(totalBytes)}`;
  }

  const isXyzFile = (f) => /\.(xyz|txt|csv|pts|bag|e57|asc)$/i.test(String(f?.name || ""));

  // Upload XYZ to a temp S3 key via XHR (with byte progress), then trigger async PDAL conversion
  async function uploadXyzToDam(code, category, file, onProgress) {
    const safeName = String(file.name || "source.xyz").replace(/[^a-zA-Z0-9._-]/g, "_");
    const tempKey = `point-clouds/xyz-tmp/${Date.now()}-${safeName}`;
    const lazName = safeName.replace(/\.(xyz|txt|csv|pts|bag|e57|asc)$/i, ".laz");

    // 1. Get presigned PUT for temp key
    const presignResp = await fetch("/api/s3/presign/upload", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ key: tempKey, contentType: "application/octet-stream" }),
    });
    if (!presignResp.ok) throw new Error(`Presign failed (${presignResp.status})`);
    const { url } = await presignResp.json();
    if (!url) throw new Error("No presign URL returned");

    // 2. Upload to S3 via XHR for byte-level progress
    await new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("PUT", url);
      xhr.setRequestHeader("Content-Type", "application/octet-stream");
      if (onProgress) xhr.upload.onprogress = (e) => { if (e.lengthComputable) onProgress(e.loaded); };
      xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`S3 PUT ${xhr.status}`)));
      xhr.onerror = () => reject(new Error("Network error"));
      xhr.send(file);
    });

    // 3. Trigger async server-side PDAL conversion (returns immediately). The server
    // computes the destination key from (code, category, filename) via its own
    // projectRoot() -- it used to be built here as `marinas/${code}/${category}/...`,
    // a stale S3 convention nothing else in the DAM reads from, so a "successful"
    // conversion's output never showed up anywhere in the file list.
    const convResp = await fetch("/api/point-cloud/convert-xyz-s3", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ sourceKey: tempKey, code, category, filename: lazName }),
    });
    if (!convResp.ok) throw new Error(`Conversion start failed (${convResp.status})`);
    const { jobId } = await convResp.json();
    return jobId;
  }

  async function uploadAssetFiles(code, category, files) {
    const catKey = `${code}::${category}`;
    // Resume support: re-picking a folder after a partial failure should only send
    // what's missing. Skip files whose relative path already exists in S3 at the
    // same byte size (fresh listing — the cached state can be stale mid-session).
    // A same-NAME file at a DIFFERENT size is a real duplicate-filename collision, not a
    // resume -- confirm before letting it silently overwrite what's already on the DAM.
    try {
      const resp = await fetch(`/api/project/assets?code=${encodeURIComponent(code)}&category=${encodeURIComponent(category)}`);
      const data = await resp.json();
      const existing = new Map((Array.isArray(data.files) ? data.files : []).map((f) => [String(f.name), Number(f.size) || 0]));
      const before = files.length;
      files = files.filter((f) => {
        const rel = String(f._damRelPath || f.name);
        return !((f.size || 0) > 0 && existing.get(rel) === f.size);
      });
      const skipped = before - files.length;
      if (skipped > 0) app.ui.toast(`Skipping ${skipped} already-uploaded file(s); sending ${files.length}.`);
      const conflicts = files.filter((f) => existing.has(String(f._damRelPath || f.name)));
      if (conflicts.length) {
        const shown = conflicts.slice(0, 10).map((f) => String(f._damRelPath || f.name)).join("\n");
        const more = conflicts.length > 10 ? `\n…and ${conflicts.length - 10} more` : "";
        const ok = window.confirm(
          `${conflicts.length} file${conflicts.length === 1 ? "" : "s"} already on the DAM in "${category}" ${conflicts.length === 1 ? "has" : "have"} the same name but different content:\n\n${shown}${more}\n\nUpload anyway? This replaces the existing file(s).`
        );
        if (!ok) {
          const conflictSet = new Set(conflicts);
          files = files.filter((f) => !conflictSet.has(f));
          app.ui.toast(files.length ? `Skipping ${conflicts.length} name conflict(s); sending ${files.length}.` : "Upload cancelled — every file name-conflicts with one already on the DAM.");
        }
      }
      if (!files.length) return;
    } catch {}
    const totalBytes = files.reduce((s, f) => s + (f.size || 0), 0);
    let doneFiles = 0;
    const failedFiles = [];
    const totalFiles = files.length;
    state.digitalAsset.uploadingByCategory[catKey] = { done: 0, total: totalFiles, uploadedBytes: 0, totalBytes };
    // Every picked file gets its own placeholder row the instant the batch starts -- "queued" --
    // so a multi-file folder upload shows every filename right away, each with its own progress
    // bar, instead of one spinner/bar for the whole batch.
    state.digitalAsset.uploadingFilesByCategory[catKey] = files.map((f) => ({
      key: String(f._damRelPath || f.name), name: f.name, size: f.size || 0, uploadedBytes: 0, status: "queued", label: "",
    }));
    render();

    // Split las-files uploads: XYZ → two-step (upload + async convert); LAS/LAZ → direct presigned PUT
    const xyzFiles = category === "las-files" ? files.filter(isXyzFile) : [];
    const directFiles = category === "las-files" ? files.filter((f) => !isXyzFile(f)) : files;

    // Process XYZ files sequentially (PDAL is CPU-bound on the server)
    for (const file of xyzFiles) {
      const fileBytes = file.size || 0;
      const fileKey = String(file._damRelPath || file.name);
      const entry = damFileEntry(catKey, fileKey);
      if (entry) { entry.status = "uploading"; entry.uploadedBytes = 0; entry.label = ""; }
      render();
      let jobId = null;
      try {
        jobId = await uploadXyzToDam(code, category, file, (loaded) => {
          if (entry) entry.uploadedBytes = loaded;
          damUpdateFileProgressDom(catKey, fileKey, loaded, fileBytes);
        });
      } catch (err) {
        app.ui.toast(`Upload failed: ${file.name} — ${err.message}`);
        failedFiles.push(file.name);
        if (entry) { entry.status = "failed"; entry.label = ""; }
        doneFiles++;
        state.digitalAsset.uploadingByCategory[catKey] = { done: doneFiles, total: totalFiles, uploadedBytes: 0, totalBytes };
        render();
        continue;
      }

      // Poll for conversion completion (up to 20 min / 5s intervals)
      const startMs = Date.now();
      let converted = false;
      let conversionErrored = false;
      for (let i = 0; i < 240; i++) {
        await new Promise((r) => setTimeout(r, 5000));
        const elapsedMin = Math.round((Date.now() - startMs) / 60000);
        const statusLabel = `Converting… (${elapsedMin > 0 ? elapsedMin + " min" : "just started"})`;
        if (entry) { entry.uploadedBytes = fileBytes; entry.label = statusLabel; }
        const lbl = containerEl?.querySelector?.(`[data-progress-label="${catKey}::${fileKey}"]`);
        if (lbl) lbl.textContent = statusLabel;
        try {
          const sr = await fetch(`/api/point-cloud/convert-xyz-s3/${jobId}`);
          const sd = await sr.json();
          if (sd.status === "completed") { converted = true; break; }
          if (sd.status === "failed") { app.ui.toast(`Conversion failed: ${file.name} — ${sd.error || "unknown error"}`); conversionErrored = true; break; }
        } catch { break; }
      }
      if (!converted && !conversionErrored) { app.ui.toast(`Conversion timed out: ${file.name}`); failedFiles.push(file.name); }
      if (conversionErrored) failedFiles.push(file.name);
      doneFiles++;
      if (entry) { entry.status = converted ? "done" : "failed"; entry.label = ""; }
      state.digitalAsset.uploadingByCategory[catKey] = doneFiles < totalFiles
        ? { done: doneFiles, total: totalFiles, uploadedBytes: fileBytes, totalBytes }
        : false;
      render();
    }

    // Direct LAS/LAZ files (and all other categories): parallel presigned PUT with XHR progress
    const queue = [...directFiles];
    const CONCURRENCY = 6;
    async function worker() {
      while (queue.length) {
        const file = queue.shift();
        if (!file) return;
        // Folder uploads can repeat bare filenames across subfolders (ppk.cmd,
        // service.log per session) — key progress (and the placeholder row) by relative path,
        // not name.
        const fileKey = String(file._damRelPath || file.name);
        const entry = damFileEntry(catKey, fileKey);
        if (entry) entry.status = "uploading";
        render();
        try {
          await uploadAssetFile(code, category, file, (loaded) => {
            if (entry) entry.uploadedBytes = loaded;
            damUpdateFileProgressDom(catKey, fileKey, loaded, file.size || 0);
          });
          app.log("dam.asset_uploaded", { filename: fileKey, marina: code, category });
          if (entry) { entry.status = "done"; entry.uploadedBytes = file.size || 0; }
        } catch (err) {
          failedFiles.push(fileKey);
          app.log("dam.asset_upload_failed", { filename: fileKey, marina: code, category, error: err.message });
          app.ui.toast(`Upload failed: ${file.name}`);
          if (entry) entry.status = "failed";
        }
        doneFiles++;
        const totalUploaded = (state.digitalAsset.uploadingFilesByCategory[catKey] || []).reduce((s, f) => s + (f.uploadedBytes || 0), 0);
        state.digitalAsset.uploadingByCategory[catKey] = doneFiles < totalFiles
          ? { done: doneFiles, total: totalFiles, uploadedBytes: totalUploaded, totalBytes }
          : false;
        render();
      }
    }
    if (directFiles.length) {
      await Promise.all(Array.from({ length: Math.min(CONCURRENCY, directFiles.length) }, worker));
    }
    state.digitalAsset.uploadingByCategory[catKey] = false;
    delete state.digitalAsset.uploadingFilesByCategory[catKey];
    await loadDigitalAssets(code, category);
    render();
    if (failedFiles.length > 0) {
      const shown = failedFiles.slice(0, 20).join("\n");
      const more = failedFiles.length > 20 ? `\n…and ${failedFiles.length - 20} more` : "";
      app.ui.alertModal(
        `${failedFiles.length} of ${totalFiles} file(s) failed to upload to "${category}" and were NOT saved:\n\n${shown}${more}\n\nRe-select the same folder to retry — files that already uploaded successfully will be skipped.`,
        { title: "Some Uploads Failed" }
      );
    }
    // Point Cloud Workspace caches its own LAS file list per project and won't see new
    // uploads until told — reuse the existing project-changed signal (with the same
    // project id) to make it refresh without disturbing anything else about its state.
    if (category === "las-files") {
      dispatchProjectChanged(code);
    }
  }

  async function uploadAssetFile(code, category, file, onProgress) {
    const relativePath = String(file._damRelPath || "");
    const resp = await fetch("/api/project/asset/presign-upload", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ code, category, filename: file.name, contentType: file.type || "application/octet-stream", ...(relativePath ? { relativePath } : {}) }),
    });
    if (!resp.ok) throw new Error(`Presign failed: HTTP ${resp.status}`);
    const data = await resp.json();
    if (!data.url) throw new Error("No upload URL returned.");

    // Field uploads run over flaky connectivity — a dropped connection or a transient
    // S3 5xx shouldn't permanently drop the file. Retry a few times with backoff before
    // giving up; only the caller's final failure counts toward the failed-files summary.
    const MAX_ATTEMPTS = 4;
    for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
      try {
        await putToS3(data.url, file, onProgress);
        return;
      } catch (err) {
        if (!err.retryable || attempt === MAX_ATTEMPTS) throw err;
        await new Promise((r) => setTimeout(r, 500 * 2 ** (attempt - 1) + Math.random() * 250));
      }
    }
  }

  function putToS3(url, file, onProgress) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("PUT", url);
      xhr.setRequestHeader("Content-Type", file.type || "application/octet-stream");
      if (onProgress) {
        xhr.upload.onprogress = (e) => { if (e.lengthComputable) onProgress(e.loaded); };
      }
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) return resolve();
        const err = new Error(`S3 PUT failed: HTTP ${xhr.status}`);
        err.retryable = xhr.status >= 500 || xhr.status === 0;
        reject(err);
      };
      xhr.onerror = () => {
        const err = new Error("Network error during upload");
        err.retryable = true;
        reject(err);
      };
      xhr.send(file);
    });
  }

  // Drag-dropped folders arrive as directory entries, not files. Walk them (readEntries
  // must be called repeatedly until it returns an empty batch) and stamp each File with
  // its path relative to the drop root so uploads preserve the tree. Entries must be
  // grabbed synchronously during the drop event — they go stale after the first await.
  function collectDroppedFiles(dataTransfer) {
    const items = dataTransfer?.items ? Array.from(dataTransfer.items) : [];
    const entries = items.map((it) => it.webkitGetAsEntry?.()).filter(Boolean);
    if (!entries.some((e) => e.isDirectory)) {
      return Promise.resolve(dataTransfer?.files ? Array.from(dataTransfer.files) : []);
    }
    const out = [];
    async function walk(entry, pathPrefix) {
      if (entry.isFile) {
        const file = await new Promise((resolve, reject) => entry.file(resolve, reject)).catch(() => null);
        if (file) {
          if (pathPrefix) file._damRelPath = `${pathPrefix}${file.name}`;
          out.push(file);
        }
      } else if (entry.isDirectory) {
        const reader = entry.createReader();
        for (;;) {
          const batch = await new Promise((resolve, reject) => reader.readEntries(resolve, reject)).catch(() => []);
          if (!batch.length) break;
          for (const child of batch) await walk(child, `${pathPrefix}${entry.name}/`);
        }
      }
    }
    return (async () => {
      for (const entry of entries) await walk(entry, "");
      return out;
    })();
  }

  function normalizeProjectTaskRows(tasks) {
    if (!Array.isArray(tasks)) return [];
    return tasks.map((task, index) => ({
      id: String(task?.id || `pm-${index + 1}`),
      marina: String(task?.marina || ""),
      category: String(task?.category || ""),
      task: String(task?.task || ""),
      owner: String(task?.owner || ""),
      startDate: String(task?.startDate || ""),
      endDate: String(task?.endDate || ""),
      status: PROJECT_TASK_STATUS_OPTIONS.includes(String(task?.status || "")) ? String(task.status) : "Not Started",
      durationDays: Number(task?.durationDays) || 0,
      notes: String(task?.notes || ""),
    }));
  }

  async function loadSharedProjectTasks() {
    try {
      const response = await fetch(SHARED_PROJECT_TASKS_API, { headers: { Accept: "application/json" } });
      if (!response.ok) return;
      const payload = await response.json();
      if (!payload?.ok) return;
      const remoteUpdatedAt = String(payload.updated_at || "");
      if (
        sharedProjectTasksLoaded
        && !sharedProjectTasksSaveTimer
        && remoteUpdatedAt
        && sharedProjectTasksUpdatedAt
        && remoteUpdatedAt === sharedProjectTasksUpdatedAt
      ) {
        return;
      }
      const incoming = normalizeProjectTaskRows(payload.tasks);
      if (incoming.length) {
        state.projectTasks = incoming;
        ensureProjectUiState(state.projectTasks);
      } else if (!sharedProjectTasksLoaded) {
        state.projectTasks = defaultProjectTasks();
      }
      sharedProjectTasksUpdatedAt = remoteUpdatedAt;
      sharedProjectTasksLoaded = true;
    } catch {
      // Local-only mode remains available when API is unavailable.
    }
  }

  async function saveSharedProjectTasksNow() {
    try {
      const response = await fetch(SHARED_PROJECT_TASKS_API, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ tasks: normalizeProjectTaskRows(state.projectTasks) }),
      });
      if (response.ok) {
        const payload = await response.json();
        if (payload?.ok) sharedProjectTasksUpdatedAt = String(payload.updated_at || sharedProjectTasksUpdatedAt || "");
      }
    } catch {
      // Preserve local edits even if shared save fails.
    }
  }

  function queueSharedProjectTasksSave() {
    if (!sharedProjectTasksLoaded) return;
    if (sharedProjectTasksSaveTimer) {
      clearTimeout(sharedProjectTasksSaveTimer);
      sharedProjectTasksSaveTimer = 0;
    }
    sharedProjectTasksSaveTimer = setTimeout(() => {
      sharedProjectTasksSaveTimer = 0;
      saveSharedProjectTasksNow();
    }, 700);
  }

  function startSharedProjectTasksPolling() {
    if (sharedProjectTasksPollTimer) return;
    sharedProjectTasksPollTimer = setInterval(async () => {
      if (!containerEl || !sharedProjectTasksLoaded || sharedProjectTasksSaveTimer) return;
      const before = JSON.stringify(normalizeProjectTaskRows(state.projectTasks));
      await loadSharedProjectTasks();
      const after = JSON.stringify(normalizeProjectTaskRows(state.projectTasks));
      if (after !== before) {
        persistLocalState();
        renderProjectManagementBottomPanel();
      }
    }, 5000);
  }

  function taskMarinaGroups(tasks) {
    const groups = new Map();
    (tasks || []).forEach((task, index) => {
      const marina = String(task?.marina || "Unassigned");
      const row = { ...task, _index: index };
      if (!groups.has(marina)) groups.set(marina, []);
      groups.get(marina).push(row);
    });
    return groups;
  }

  function projectTimeline(tasks) {
    let minDate = null;
    let maxDate = null;
    (tasks || []).forEach((task) => {
      const s = parseProjectDate(task?.startDate);
      const e = parseProjectDate(task?.endDate);
      if (s && (!minDate || s < minDate)) minDate = s;
      if (e && (!maxDate || e > maxDate)) maxDate = e;
      if (s && !e && (!maxDate || s > maxDate)) maxDate = s;
    });
    if (!minDate) minDate = parseProjectDate(fmtProjectDate(new Date()));
    if (!maxDate) maxDate = minDate;
    if (minDate && maxDate && minDate > maxDate) maxDate = minDate;
    if (minDate) {
      const year = Number(minDate.getUTCFullYear());
      const summerStart = new Date(Date.UTC(year, 5, 1));
      const summerEnd = new Date(Date.UTC(year, 7, 31));
      if (summerStart < minDate) minDate = summerStart;
      if (summerEnd > maxDate) maxDate = summerEnd;
    }
    return { minDate, maxDate };
  }

  function ensureProjectUiState(tasks) {
    if (!state.projectUi || typeof state.projectUi !== "object") {
      state.projectUi = { expandedMarinas: {}, activeView: "table" };
    }
    if (!state.projectUi.expandedMarinas || typeof state.projectUi.expandedMarinas !== "object") {
      state.projectUi.expandedMarinas = {};
    }
    if (String(state.projectUi.activeView || "") !== "gantt" && String(state.projectUi.activeView || "") !== "table") {
      state.projectUi.activeView = "table";
    }
    if (!Number.isFinite(Number(state.projectUi.tableScrollTop))) state.projectUi.tableScrollTop = 0;
    if (!Number.isFinite(Number(state.projectUi.tableScrollLeft))) state.projectUi.tableScrollLeft = 0;
    if (!Number.isFinite(Number(state.projectUi.ganttScrollTop))) state.projectUi.ganttScrollTop = 0;
    if (!Number.isFinite(Number(state.projectUi.ganttScrollLeft))) state.projectUi.ganttScrollLeft = 0;
    taskMarinaGroups(tasks).forEach((_rows, marina) => {
      if (typeof state.projectUi.expandedMarinas[marina] !== "boolean") {
        state.projectUi.expandedMarinas[marina] = true;
      }
    });
  }

  function renderProjectManagementBottomPanelVariant(forcedView, ownerId, panelTitle) {
    const panelNonce = ownerId;
    const tasks = Array.isArray(state.projectTasks) ? state.projectTasks : [];
    ensureProjectUiState(tasks);
    const groups = taskMarinaGroups(tasks);
    const { minDate, maxDate } = projectTimeline(tasks);
    const activeView = forcedView === "gantt" ? "gantt" : "table";
    const days = [];
    if (minDate && maxDate) {
      let cursor = new Date(minDate.getTime());
      const guard = 370;
      let i = 0;
      while (cursor <= maxDate && i < guard) {
        days.push(new Date(cursor.getTime()));
        cursor = addProjectDays(cursor, 1);
        i += 1;
      }
    }
    const pmInputStyle = "height:22px; min-width:112px; padding:1px 7px; border-radius:8px; border:1px solid rgba(56,189,248,0.32); background:rgba(15,23,42,0.9); color:#e2e8f0; font-size:11px; line-height:1.1; outline:none; box-shadow:inset 0 0 0 1px rgba(2,6,23,0.12);";
    const pmSelectStyle = "height:22px; min-width:96px; padding:1px 7px; border-radius:8px; border:1px solid rgba(56,189,248,0.32); background:rgba(15,23,42,0.9); color:#d1fae5; font-size:11px; font-weight:600; line-height:1.1; outline:none; box-shadow:inset 0 0 0 1px rgba(2,6,23,0.12);";
    const pmNotesStyle = "height:22px; min-width:162px; padding:1px 7px; border-radius:8px; border:1px solid rgba(148,163,184,0.35); background:rgba(15,23,42,0.9); color:#e2e8f0; font-size:11px; line-height:1.1; outline:none;";
    const ganttBarHtml = (segment, color) => {
      if (!segment) return "";
      const radius = segment === "single"
        ? "999px"
        : segment === "start"
          ? "999px 0 0 999px"
          : segment === "end"
            ? "0 999px 999px 0"
            : "0";
      return `<div data-pm-gantt-bar="1" style="height:12px; margin:4px 0; background:${color}; border-radius:${radius}; box-shadow:0 0 10px rgba(56,189,248,0.28), 0 1px 5px rgba(2,6,23,0.42);"></div>`;
    };

    const compactTableRows = Array.from(groups.entries()).map(([marina, rows]) => {
      const expanded = state.projectUi.expandedMarinas[marina] !== false;
      const marinaRow = `
        <tr data-pm-marina-row="${esc(marina)}" style="background:rgba(148,163,184,0.12); font-weight:700;">
          <td style="padding:3px 6px; font-size:12px;">
            <button type="button" class="btn" data-pm-toggle-marina="${esc(marina)}" style="padding:2px 8px; font-size:11px;">${expanded ? "−" : "+"}</button>
            <span style="margin-left:8px;">${esc(marina)}</span>
          </td>
          <td style="padding:3px 6px; font-size:11px;" colspan="8">${rows.length} task${rows.length === 1 ? "" : "s"}</td>
        </tr>
      `;
      const taskRows = expanded ? rows.map((row) => `
        <tr>
          <td style="padding:2px 6px; font-size:11px;">${esc(row.category || "")}</td>
          <td style="padding:2px 6px; font-size:11px;">${esc(row.task || "")}</td>
          <td style="padding:2px 6px; font-size:11px;">${esc(row.owner || "")}</td>
          <td style="padding:2px 6px; font-size:11px;"><input type="date" data-pm-task-index="${row._index}" data-pm-field="startDate" value="${esc(fmtProjectDate(row.startDate))}" style="${pmInputStyle}" /></td>
          <td style="padding:2px 6px; font-size:11px;"><input type="date" data-pm-task-index="${row._index}" data-pm-field="endDate" value="${esc(fmtProjectDate(row.endDate))}" style="${pmInputStyle}" /></td>
          <td style="padding:2px 6px; min-width:48px; font-size:11px;">${esc(String(row.durationDays ?? ""))}</td>
          <td style="padding:2px 6px;">
            <select data-pm-task-index="${row._index}" data-pm-field="status" style="${pmSelectStyle}">
              ${PROJECT_TASK_STATUS_OPTIONS.map((opt) => `<option value="${esc(opt)}"${row.status === opt ? " selected" : ""}>${esc(opt)}</option>`).join("")}
            </select>
          </td>
          <td style="padding:2px 6px;"><input type="text" data-pm-task-index="${row._index}" data-pm-field="notes" value="${esc(row.notes || "")}" placeholder="Notes" style="${pmNotesStyle}" /></td>
        </tr>
      `).join("") : "";
      return marinaRow + taskRows;
    }).join("");

    const ganttRows = Array.from(groups.entries()).map(([marina, rows]) => {
      const expanded = state.projectUi.expandedMarinas[marina] !== false;
      const marinaHeaderCells = days.map(() => `<div style="height:24px; border-right:1px solid rgba(148,163,184,0.12); background:rgba(148,163,184,0.06);"></div>`).join("");
      const marinaHeader = `
        <div style="display:grid; grid-template-columns: 320px 140px repeat(${Math.max(1, days.length)}, 28px); align-items:stretch; border-bottom:1px solid rgba(148,163,184,0.14); background:rgba(148,163,184,0.10);">
          <div style="padding:4px 8px; font-size:12px; font-weight:700; white-space:nowrap; overflow:hidden; text-overflow:ellipsis;">
            <button type="button" class="btn" data-pm-toggle-marina="${esc(marina)}" style="padding:1px 7px; font-size:11px; margin-right:6px;">${expanded ? "−" : "+"}</button>${esc(marina)}
          </div>
          <div style="padding:4px 8px; font-size:11px; color:#cbd5e1;">${rows.length} task${rows.length === 1 ? "" : "s"}</div>
          ${marinaHeaderCells}
        </div>
      `;
      const taskRows = expanded ? rows.map((task) => {
        const s = parseProjectDate(task.startDate);
        const e = parseProjectDate(task.endDate);
        const status = String(task.status || "Not Started");
        const color = status === "Done" ? "#22c55e" : status === "In Progress" ? "#f59e0b" : status === "Blocked" ? "#ef4444" : "#64748b";
        const cells = days.map((day) => {
          const inRange = s && e && day >= s && day <= e;
          const prevDay = addProjectDays(day, -1);
          const nextDay = addProjectDays(day, 1);
          const prevInRange = s && e && prevDay >= s && prevDay <= e;
          const nextInRange = s && e && nextDay >= s && nextDay <= e;
          const segment = inRange
            ? (!prevInRange && !nextInRange ? "single" : !prevInRange ? "start" : !nextInRange ? "end" : "mid")
            : "";
          const month = Number(day.getUTCMonth());
          const monthTint = ganttMonthColors(month).tint;
          return `<div data-pm-gantt-cell="1" data-pm-task-index="${task._index}" data-pm-date="${esc(fmtProjectDate(day))}" style="height:20px; border-right:1px solid rgba(148,163,184,0.08); background:${monthTint}; cursor:default;">${ganttBarHtml(segment, color)}</div>`;
        }).join("");
        return `
          <div style="display:grid; grid-template-columns: 320px 140px repeat(${Math.max(1, days.length)}, 28px); align-items:stretch; border-bottom:1px solid rgba(148,163,184,0.1);">
            <div style="padding:4px 8px 4px 24px; font-size:11px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis;">${esc(task.task)}</div>
          <div style="padding:4px 8px; font-size:11px;" class="small">${esc(status)}</div>
          ${cells}
        </div>
      `;
      }).join("") : "";
      return marinaHeader + taskRows;
    }).join("");

    const monthSpans = [];
    days.forEach((d) => {
      const key = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
      const label = d.toLocaleString("en-US", { month: "short", year: "numeric", timeZone: "UTC" });
      const prev = monthSpans[monthSpans.length - 1];
      if (prev && prev.key === key) prev.count += 1;
      else monthSpans.push({ key, label, count: 1, month: d.getUTCMonth() });
    });
    const monthHeader = monthSpans.map((m) => {
      const monthBg = ganttMonthColors(Number(m.month)).monthBg;
      return `<div style="height:20px; border-right:1px solid rgba(148,163,184,0.18); text-align:center; font-size:10px; color:#cbd5e1; background:${monthBg}; grid-column: span ${m.count};">${esc(m.label)}</div>`;
    }).join("");
    const dayHeader = days.map((d) => {
      const month = Number(d.getUTCMonth());
      const dayBg = ganttMonthColors(month).dayBg;
      return `<div style="height:20px; border-right:1px solid rgba(148,163,184,0.18); text-align:center; font-size:10px; color:#cbd5e1; background:${dayBg};">${esc(String(d.getUTCDate()))}</div>`;
    }).join("");

    const html = `
      <div data-safe-harbor-pm-root="${panelNonce}" style="height:100%; min-height:0; display:flex; flex-direction:column; gap:10px;">
        <div class="small">${activeView === "gantt" ? "Gantt workspace" : "Project Management workspace"}</div>
        <div style="min-height:0; flex:1; display:${activeView === "table" ? "block" : "none"};">
          <div data-pm-table-scroll="1" style="height:100%; overflow:auto; border:1px solid rgba(148,163,184,0.2); border-radius:10px; background:rgba(15,23,42,0.35);">
            <table style="width:100%; border-collapse:collapse; min-width:1080px; font-size:12px;">
              <thead>
                <tr style="text-align:left; border-bottom:1px solid rgba(148,163,184,0.2); position:sticky; top:0; background:rgba(15,23,42,0.95); z-index:1;">
                  <th style="padding:3px 6px; font-size:11px;">Category</th>
                  <th style="padding:3px 6px; font-size:11px;">Task</th>
                  <th style="padding:3px 6px; font-size:11px;">Owner</th>
                  <th style="padding:3px 6px; font-size:11px;">Start</th>
                  <th style="padding:3px 6px; font-size:11px;">End</th>
                  <th style="padding:3px 6px; font-size:11px;">Days</th>
                  <th style="padding:3px 6px; font-size:11px;">Status</th>
                  <th style="padding:3px 6px; font-size:11px;">Notes</th>
                </tr>
              </thead>
              <tbody>
                ${compactTableRows || `<tr><td colspan="8" class="small" style="padding:10px;">No tasks loaded.</td></tr>`}
              </tbody>
            </table>
          </div>
        </div>
        <div style="min-height:0; flex:1; display:${activeView === "gantt" ? "block" : "none"};">
          <div style="height:100%; border:1px solid rgba(148,163,184,0.2); border-radius:10px; background:rgba(15,23,42,0.35); display:flex; flex-direction:column;">
            <div style="padding:8px 10px; font-weight:700; border-bottom:1px solid rgba(148,163,184,0.2); font-size:12px;">Gantt View</div>
            <div data-pm-gantt-scroll="1" style="overflow:auto; min-height:0;">
              <div style="position:sticky; top:0; background:rgba(15,23,42,0.95); border-bottom:1px solid rgba(148,163,184,0.2); z-index:2;">
                <div style="display:grid; grid-template-columns: 320px 140px repeat(${Math.max(1, days.length)}, 28px);">
                  <div style="padding:4px 8px; font-weight:700; font-size:11px;"></div>
                  <div style="padding:4px 8px; font-weight:700; font-size:11px;"></div>
                  ${monthHeader}
                </div>
                <div style="display:grid; grid-template-columns: 320px 140px repeat(${Math.max(1, days.length)}, 28px); border-top:1px solid rgba(148,163,184,0.12);">
                  <div style="padding:6px 8px; font-weight:700; font-size:12px;">Task</div>
                  <div style="padding:6px 8px; font-weight:700; font-size:12px;">Status</div>
                  ${dayHeader}
                </div>
              </div>
              ${ganttRows || `<div class="small" style="padding:10px;">No tasks to chart.</div>`}
            </div>
          </div>
        </div>
      </div>
    `;

    const sectionKey = forcedView === "gantt" ? "gantt" : "projectManagement";
    if (sectionEls[sectionKey]) {
      sectionEls[sectionKey].innerHTML = html;
    }

    const root = document.querySelector(`[data-safe-harbor-pm-root="${panelNonce}"]`);
    if (!root) return;
    root.querySelectorAll("[data-pm-toggle-marina]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const marina = String(btn.getAttribute("data-pm-toggle-marina") || "");
        if (!marina) return;
        state.projectUi.expandedMarinas[marina] = !(state.projectUi.expandedMarinas[marina] !== false);
        persistLocalState();
        queueSharedProjectTasksSave();
        renderProjectManagementBottomPanel();
      });
    });
    root.querySelectorAll("[data-pm-task-index][data-pm-field]").forEach((el) => {
      const field = String(el.getAttribute("data-pm-field") || "");
      const evt = field === "notes" ? "input" : "change";
      el.addEventListener(evt, () => {
        const tableScroller = root.querySelector('[data-pm-table-scroll="1"]');
        const ganttScroller = root.querySelector('[data-pm-gantt-scroll="1"]');
        if (tableScroller) {
          state.projectUi.tableScrollTop = Number(tableScroller.scrollTop) || 0;
          state.projectUi.tableScrollLeft = Number(tableScroller.scrollLeft) || 0;
        }
        if (ganttScroller) {
          state.projectUi.ganttScrollTop = Number(ganttScroller.scrollTop) || 0;
          state.projectUi.ganttScrollLeft = Number(ganttScroller.scrollLeft) || 0;
        }
        const idx = Number(el.getAttribute("data-pm-task-index"));
        if (!Number.isFinite(idx) || !state.projectTasks?.[idx]) return;
        const row = state.projectTasks[idx];
        if (field === "status") {
          const next = String(el.value || "Not Started");
          row.status = PROJECT_TASK_STATUS_OPTIONS.includes(next) ? next : "Not Started";
        } else if (field === "notes") {
          row.notes = String(el.value || "");
        } else if (field === "startDate") {
          row.startDate = String(el.value || "");
        } else if (field === "endDate") {
          row.endDate = String(el.value || "");
        }
        const s = parseProjectDate(row.startDate);
        const e = parseProjectDate(row.endDate);
        if (s && e) row.durationDays = Math.max(0, daysBetweenProjectDates(s, e) + 1);
        state.projectUi.lastEdit = { idx, field };
        persistLocalState();
        queueSharedProjectTasksSave();
        renderProjectManagementBottomPanel();
      });
    });
    const tableScroller = root.querySelector('[data-pm-table-scroll="1"]');
    const ganttScroller = root.querySelector('[data-pm-gantt-scroll="1"]');
    if (tableScroller) {
      tableScroller.scrollTop = Number(state.projectUi.tableScrollTop) || 0;
      tableScroller.scrollLeft = Number(state.projectUi.tableScrollLeft) || 0;
      tableScroller.addEventListener("scroll", () => {
        state.projectUi.tableScrollTop = Number(tableScroller.scrollTop) || 0;
        state.projectUi.tableScrollLeft = Number(tableScroller.scrollLeft) || 0;
      });
    }
    if (ganttScroller) {
      ganttScroller.scrollTop = Number(state.projectUi.ganttScrollTop) || 0;
      ganttScroller.scrollLeft = Number(state.projectUi.ganttScrollLeft) || 0;
      ganttScroller.addEventListener("scroll", () => {
        state.projectUi.ganttScrollTop = Number(ganttScroller.scrollTop) || 0;
        state.projectUi.ganttScrollLeft = Number(ganttScroller.scrollLeft) || 0;
      });
    }
    const dragState = { active: false, taskIdx: null, start: null, current: null };
    const clearDragHighlight = () => {
      root.querySelectorAll('[data-pm-gantt-cell="1"]').forEach((el) => {
        el.style.outline = "";
        el.style.outlineOffset = "";
      });
    };
    const paintDragHighlight = () => {
      clearDragHighlight();
      if (!dragState.active || !dragState.start || !dragState.current || !Number.isFinite(dragState.taskIdx)) return;
      const start = dragState.start < dragState.current ? dragState.start : dragState.current;
      const end = dragState.start < dragState.current ? dragState.current : dragState.start;
      root.querySelectorAll(`[data-pm-gantt-cell="1"][data-pm-task-index="${dragState.taskIdx}"]`).forEach((el) => {
        const ds = String(el.getAttribute("data-pm-date") || "");
        if (ds >= start && ds <= end) {
          el.style.outline = "2px solid rgba(255,255,255,0.85)";
          el.style.outlineOffset = "-2px";
        }
      });
    };
    const applyDragDates = () => {
      if (!dragState.active || !Number.isFinite(dragState.taskIdx) || !dragState.start || !dragState.current) return;
      const tableScroller = root.querySelector('[data-pm-table-scroll="1"]');
      const ganttScroller = root.querySelector('[data-pm-gantt-scroll="1"]');
      if (tableScroller) {
        state.projectUi.tableScrollTop = Number(tableScroller.scrollTop) || 0;
        state.projectUi.tableScrollLeft = Number(tableScroller.scrollLeft) || 0;
      }
      if (ganttScroller) {
        state.projectUi.ganttScrollTop = Number(ganttScroller.scrollTop) || 0;
        state.projectUi.ganttScrollLeft = Number(ganttScroller.scrollLeft) || 0;
      }
      const idx = Number(dragState.taskIdx);
      if (!state.projectTasks?.[idx]) return;
      const row = state.projectTasks[idx];
      const start = dragState.start < dragState.current ? dragState.start : dragState.current;
      const end = dragState.start < dragState.current ? dragState.current : dragState.start;
      row.startDate = start;
      row.endDate = end;
      const s = parseProjectDate(start);
      const e = parseProjectDate(end);
      if (s && e) row.durationDays = Math.max(0, daysBetweenProjectDates(s, e) + 1);
      state.projectUi.lastEdit = null;
      persistLocalState();
      queueSharedProjectTasksSave();
      if (activeView === "gantt") {
        const status = String(row.status || "Not Started");
        const barColor = status === "Done" ? "#22c55e" : status === "In Progress" ? "#f59e0b" : status === "Blocked" ? "#ef4444" : "#64748b";
        root.querySelectorAll(`[data-pm-gantt-cell="1"][data-pm-task-index="${idx}"]`).forEach((el) => {
          const bar = el.querySelector('[data-pm-gantt-bar="1"]');
          const ds = String(el.getAttribute("data-pm-date") || "");
          const d = parseProjectDate(ds);
          const month = d ? Number(d.getUTCMonth()) : -1;
          const monthTint = ganttMonthColors(month).tint;
          const inRange = ds >= start && ds <= end;
          el.style.background = monthTint;
          if (!inRange) {
            if (bar?.parentElement) bar.parentElement.removeChild(bar);
            return;
          }
          const dayDate = parseProjectDate(ds);
          if (!dayDate) return;
          const prevIso = fmtProjectDate(addProjectDays(dayDate, -1));
          const nextIso = fmtProjectDate(addProjectDays(dayDate, 1));
          const prevInRange = prevIso >= start && prevIso <= end;
          const nextInRange = nextIso >= start && nextIso <= end;
          const segment = !prevInRange && !nextInRange ? "single" : !prevInRange ? "start" : !nextInRange ? "end" : "mid";
          const radius = segment === "single"
            ? "999px"
            : segment === "start"
              ? "999px 0 0 999px"
              : segment === "end"
                ? "0 999px 999px 0"
                : "0";
          if (bar) {
            bar.style.background = barColor;
            bar.style.borderRadius = radius;
          } else {
            el.innerHTML = `<div data-pm-gantt-bar="1" style="height:12px; margin:4px 0; background:${barColor}; border-radius:${radius}; box-shadow:0 0 10px rgba(56,189,248,0.28), 0 1px 5px rgba(2,6,23,0.42);"></div>`;
          }
        });
      } else {
        renderProjectManagementBottomPanel();
      }
    };
    const stopDrag = () => {
      dragState.active = false;
      dragState.taskIdx = null;
      dragState.start = null;
      dragState.current = null;
      clearDragHighlight();
    };
    const onMouseUp = () => {
      if (!dragState.active) return;
      applyDragDates();
      stopDrag();
      window.removeEventListener("mouseup", onMouseUp);
      window.removeEventListener("blur", onBlurCancel);
      window.removeEventListener("keydown", onKeyDownCancel);
    };
    const onBlurCancel = () => {
      if (!dragState.active) return;
      stopDrag();
      window.removeEventListener("mouseup", onMouseUp);
      window.removeEventListener("blur", onBlurCancel);
      window.removeEventListener("keydown", onKeyDownCancel);
    };
    const onKeyDownCancel = (ev) => {
      if (!dragState.active) return;
      if (String(ev.key || "") === "Escape") onBlurCancel();
    };
    root.querySelectorAll('[data-pm-gantt-cell="1"]').forEach((cell) => {
      cell.addEventListener("mousedown", (ev) => {
        const idx = Number(cell.getAttribute("data-pm-task-index"));
        const ds = String(cell.getAttribute("data-pm-date") || "");
        if (!Number.isFinite(idx) || !ds) return;
        ev.preventDefault();
        dragState.active = true;
        dragState.taskIdx = idx;
        dragState.start = ds;
        dragState.current = ds;
        window.addEventListener("mouseup", onMouseUp);
        window.addEventListener("blur", onBlurCancel);
        window.addEventListener("keydown", onKeyDownCancel);
        paintDragHighlight();
      });
      cell.addEventListener("mouseenter", (ev) => {
        if (!dragState.active) return;
        if (ev && typeof ev.buttons === "number" && ev.buttons === 0) {
          stopDrag();
          return;
        }
        const idx = Number(cell.getAttribute("data-pm-task-index"));
        if (!Number.isFinite(idx) || idx !== dragState.taskIdx) return;
        const ds = String(cell.getAttribute("data-pm-date") || "");
        if (!ds) return;
        dragState.current = ds;
        paintDragHighlight();
      });
    });
    const lastEdit = state.projectUi.lastEdit;
    if (activeView === "table" && lastEdit && Number.isFinite(lastEdit.idx) && lastEdit.field) {
      const selector = `[data-pm-task-index="${Number(lastEdit.idx)}"][data-pm-field="${String(lastEdit.field)}"]`;
      const target = root.querySelector(selector);
      if (target && typeof target.focus === "function") {
        target.focus();
      }
    }
  }

  function renderProjectManagementBottomPanel() {
    renderProjectManagementBottomPanelVariant("table", PROJECT_MGMT_BOTTOM_PANEL_ID, "Project Management");
    renderProjectManagementBottomPanelVariant("gantt", PROJECT_GANTT_BOTTOM_PANEL_ID, "Gantt");
  }

  function sanitizeFilePart(value, fallback = "item") {
    const cleaned = String(value || "")
      .trim()
      .replace(/[<>:"/\\|?*\x00-\x1F]+/g, "-")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "");
    return cleaned || fallback;
  }

  function stripKnownSuffix(name) {
    return String(name || "").replace(/\.(kmz|kml|geojson|json)$/i, "");
  }

  function stripKnownSpreadsheetSuffix(name) {
    return String(name || "").replace(/\.(xlsx|xls)$/i, "");
  }

  function normalizeMarinaKey(value) {
    return String(value || "")
      .toLowerCase()
      .replace(/\([^)]*\)/g, "")
      .replace(/\bsafe harbor\b/g, "")
      .replace(/ship'?s/g, "ships")
      .replace(/[^a-z0-9]+/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  }

  function isPseudoMarinaKey(value) {
    return /^kml-\d+$/i.test(String(value || "").trim());
  }

  function projectSettingsMarinaCode(item) {
    const code = String(item?.portfolio?.code || "").trim();
    const id = String(item?.id || "").trim();
    const name = String(item?.name || "").trim();
    if (code && !isPseudoMarinaKey(code)) return code;
    if (id && !isPseudoMarinaKey(id)) return id;
    if (name && !isPseudoMarinaKey(name)) return name;
    return code || id || name;
  }

  function projectSettingsMarinaItems() {
    return [...(Array.isArray(state.items) ? state.items : [])]
      .filter((item) => !!projectSettingsMarinaCode(item))
      .sort((a, b) => {
        const codeA = projectSettingsMarinaCode(a);
        const codeB = projectSettingsMarinaCode(b);
        return codeA.localeCompare(codeB, undefined, { numeric: true, sensitivity: "base" });
      });
  }

  function canonicalizeProjectSettingsSelectedMarinaId() {
    const selectedId = String(state.projectSettings.selectedProjectId || "").trim();
    if (!selectedId) return;
    const match = (Array.isArray(state.items) ? state.items : []).find((item) => {
      const code = String(item?.portfolio?.code || "").trim();
      const id = String(item?.id || "").trim();
      return selectedId === code || selectedId === id || selectedId === projectSettingsMarinaCode(item);
    });
    if (!match) {
      if (isPseudoMarinaKey(selectedId)) state.projectSettings.selectedProjectId = "";
      return;
    }
    const canonical = projectSettingsMarinaCode(match);
    if (canonical && canonical !== selectedId) {
      state.projectSettings.selectedProjectId = canonical;
    }
  }

  function normalizeFeatureCollection(data) {
    if (data?.type === "FeatureCollection" && Array.isArray(data.features)) return JSON.parse(JSON.stringify(data));
    if (data?.type === "Feature" && data.geometry) {
      return { type: "FeatureCollection", features: [JSON.parse(JSON.stringify(data))] };
    }
    return { type: "FeatureCollection", features: [] };
  }

  function childElementsByLocalName(node, localName) {
    return Array.from(node?.children || []).filter((child) => String(child?.localName || "").toLowerCase() === String(localName || "").toLowerCase());
  }

  function firstChildText(node, localName) {
    const match = childElementsByLocalName(node, localName)[0];
    return String(match?.textContent || "").trim();
  }

  function parsePlacemarkExtendedData(placemarkEl) {
    const out = {};
    childElementsByLocalName(placemarkEl, "ExtendedData").forEach((extEl) => {
      childElementsByLocalName(extEl, "Data").forEach((dataEl) => {
        const key = String(dataEl.getAttribute("name") || "").trim();
        const value = firstChildText(dataEl, "value");
        if (key) out[key] = value;
      });
    });
    return out;
  }

  function parseCoordinateText(raw) {
    const text = String(raw || "").trim();
    if (!text) return null;
    const token = text.split(/\s+/).find(Boolean);
    if (!token) return null;
    const parts = token.split(",");
    const lng = Number(parts[0]);
    const lat = Number(parts[1]);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    return { lat, lng };
  }

  function extractItemsFromKmlDom(dom) {
    const placemarkEls = Array.from(dom?.getElementsByTagName?.("*") || []).filter((el) => String(el?.localName || "").toLowerCase() === "placemark");
    const items = [];
    placemarkEls.forEach((placemarkEl, placemarkIndex) => {
      const pointEl = Array.from(placemarkEl.getElementsByTagName("*")).find((el) => String(el?.localName || "").toLowerCase() === "point");
      if (!pointEl) return;
      const coordEl = Array.from(pointEl.getElementsByTagName("*")).find((el) => String(el?.localName || "").toLowerCase() === "coordinates");
      const coord = parseCoordinateText(coordEl?.textContent || "");
      if (!coord) return;
      const properties = {
        name: firstChildText(placemarkEl, "name"),
        description: firstChildText(placemarkEl, "description"),
        ...parsePlacemarkExtendedData(placemarkEl),
      };
      items.push({
        id: `kml-${placemarkIndex + 1}`,
        name: properties.name || `Marina ${placemarkIndex + 1}`,
        lat: coord.lat,
        lng: coord.lng,
        properties,
        selected: true,
        view: null,
        coveredSlips: false,
        excludeFromReport: false,
        noFlyZone: false,
        prioritizeAirTravel: false,
      });
    });
    return items;
  }

  function pointName(feature, fallbackIndex) {
    const props = feature?.properties || {};
    const keys = ["name", "Name", "title", "Title", "marina", "Marina", "facility", "Facility"];
    for (const key of keys) {
      const value = String(props?.[key] || "").trim();
      if (value) return value;
    }
    return `Marina ${fallbackIndex + 1}`;
  }

  function formatBounds(bounds) {
    if (!bounds) return "Default";
    return `W ${bounds.west.toFixed(4)} S ${bounds.south.toFixed(4)} E ${bounds.east.toFixed(4)} N ${bounds.north.toFixed(4)}`;
  }

  function isAirMissionPriority(value) {
    if (value === true) return true;
    if (typeof value === "number") return value === 1;
    const text = String(value ?? "").trim().toLowerCase();
    return ["true", "1", "yes", "y", "on"].includes(text);
  }

  function normalizeItem(item = {}) {
    return {
      ...item,
      selected: item.selected !== false,
      view: item.view || null,
      coveredSlips: item.coveredSlips === true,
      excludeFromReport: item.excludeFromReport === true,
      noFlyZone: item.noFlyZone === true,
      prioritizeAirTravel: isAirMissionPriority(item.prioritizeAirTravel),
    };
  }

  function buildItemFromCatalogEntry(catalogMarina, existingItem = null) {
    return {
      id: String(catalogMarina.code || catalogMarina.projectId || catalogMarina.name || ""),
      name: String(catalogMarina.name || catalogMarina.code || ""),
      lat: Number(catalogMarina.lat),
      lng: Number(catalogMarina.lng),
      properties: {},
      selected: existingItem ? existingItem.selected !== false : true,
      view: existingItem?.view ?? null,
      coveredSlips: existingItem?.coveredSlips === true,
      excludeFromReport: existingItem?.excludeFromReport === true,
      noFlyZone: existingItem?.noFlyZone === true,
      prioritizeAirTravel: isAirMissionPriority(existingItem?.prioritizeAirTravel),
      surveyHours: existingItem?.surveyHours,
      portfolio: existingItem?.portfolio ?? null,
    };
  }

  function currentItem() {
    if (state.activeIndex < 0 || state.activeIndex >= state.items.length) return null;
    return state.items[state.activeIndex] || null;
  }

  function normalizedTripPlaybackDayIndex(plan) {
    const days = Array.isArray(plan?.days) ? plan.days : [];
    if (!days.length) return -1;
    if (Number.isInteger(state.tripPlaybackDayIndex) && state.tripPlaybackDayIndex >= 0) {
      return Math.max(0, Math.min(days.length - 1, state.tripPlaybackDayIndex));
    }
    return 0;
  }

  function currentTripPlaybackDay(plan) {
    const index = normalizedTripPlaybackDayIndex(plan);
    return index >= 0 ? plan?.days?.[index] || null : null;
  }

  function milesBetween(aLat, aLng, bLat, bLng) {
    const meters = map.distance([Number(aLat), Number(aLng)], [Number(bLat), Number(bLng)]);
    return Number.isFinite(meters) ? (meters / 1609.344) : 0;
  }

  function estimateDriveMinutes(miles) {
    const speed = Math.max(5, Number(state.planning.averageSpeedMph) || 45);
    const factor = Math.max(1, Number(state.planning.roadFactor) || 1.2);
    return (Number(miles) || 0) * factor / speed * 60;
  }

  function haversineMiles(lat1, lon1, lat2, lon2) {
    const toRad = (d) => (Number(d) * Math.PI) / 180;
    const p1 = toRad(lat1);
    const p2 = toRad(lat2);
    const dp = toRad(lat2 - lat1);
    const dl = toRad(lon2 - lon1);
    const aa = Math.sin(dp / 2) ** 2 + Math.cos(p1) * Math.cos(p2) * Math.sin(dl / 2) ** 2;
    const km = 6371 * (2 * Math.atan2(Math.sqrt(aa), Math.sqrt(1 - aa)));
    return km * 0.621371;
  }

  function airportTypeAdj(type) {
    const value = String(type || "").toLowerCase();
    if (value === "large" || value === "large_airport") return 24;
    if (value === "medium" || value === "medium_airport") return 14;
    return 8;
  }

  function estimateFare(oneWayMiles, originAdj, destAdj) {
    const d = Math.max(0, Number(oneWayMiles) || 0);
    const seg1 = Math.min(d, 500) * 0.16;
    const seg2 = Math.min(Math.max(d - 500, 0), 1000) * 0.12;
    const seg3 = Math.max(d - 1500, 0) * 0.09;
    const oneWay = Math.max(69, 58 + seg1 + seg2 + seg3 + originAdj + destAdj);
    const roundTrip = oneWay * 1.87;
    return {
      oneWay,
      roundTrip,
      low: roundTrip * 0.72,
      high: roundTrip * 1.28,
    };
  }

  function airportPoint(airport) {
    if (!airport) return null;
    const lat = Number(airport.lat);
    const lng = Number(airport.lon);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    return { lat, lng };
  }

  function samePoint(a, b, tolerance = 1e-6) {
    if (!a || !b) return false;
    return Math.abs(Number(a.lat) - Number(b.lat)) <= tolerance
      && Math.abs(Number(a.lng) - Number(b.lng)) <= tolerance;
  }

  function ensureGlobalAirportsLoaded() {
    if (Array.isArray(globalAirports)) return Promise.resolve(globalAirports);
    if (globalAirportsPromise) return globalAirportsPromise;
    globalAirportsPromise = fetch(GLOBAL_AIRPORTS_URL, { headers: { Accept: "application/json" } })
      .then((response) => {
        if (!response.ok) throw new Error(`Airport data HTTP ${response.status}`);
        return response.json();
      })
      .then((rows) => {
        globalAirports = Array.isArray(rows) ? rows.filter((row) => row && row.iata && Number.isFinite(Number(row.lat)) && Number.isFinite(Number(row.lon))) : [];
        return globalAirports;
      })
      .catch(() => {
        globalAirports = [];
        return globalAirports;
      })
      .finally(() => {
        globalAirportsPromise = null;
        clearEnrichedTripPlan();
        render();
      });
    return globalAirportsPromise;
  }

  function ensureGsaConusRatesLoaded() {
    if (Array.isArray(gsaConusRates)) return Promise.resolve(gsaConusRates);
    if (gsaConusRatesPromise) return gsaConusRatesPromise;
    gsaConusRatesPromise = fetch(GSA_CONUS_RATES_URL, { headers: { Accept: "application/json" } })
      .then((response) => {
        if (!response.ok) throw new Error(`GSA CONUS data HTTP ${response.status}`);
        return response.json();
      })
      .then((payload) => {
        gsaConusRates = Array.isArray(payload?.entries) ? payload.entries : [];
        return gsaConusRates;
      })
      .catch(() => {
        gsaConusRates = [];
        return gsaConusRates;
      })
      .finally(() => {
        gsaConusRatesPromise = null;
      });
    return gsaConusRatesPromise;
  }

  function ensureDodOconusRatesLoaded() {
    if (Array.isArray(dodOconusRates)) return Promise.resolve(dodOconusRates);
    if (dodOconusRatesPromise) return dodOconusRatesPromise;
    dodOconusRatesPromise = fetch(DOD_OCONUS_NONFOREIGN_URL, { headers: { Accept: "application/json" } })
      .then((response) => {
        if (!response.ok) throw new Error(`DoD OCONUS data HTTP ${response.status}`);
        return response.json();
      })
      .then((payload) => {
        dodOconusRates = Array.isArray(payload?.entries) ? payload.entries : [];
        return dodOconusRates;
      })
      .catch(() => {
        dodOconusRates = [];
        return dodOconusRates;
      })
      .finally(() => {
        dodOconusRatesPromise = null;
      });
    return dodOconusRatesPromise;
  }

  function ensureStateForeignRatesLoaded() {
    if (Array.isArray(stateForeignRates)) return Promise.resolve(stateForeignRates);
    if (stateForeignRatesPromise) return stateForeignRatesPromise;
    stateForeignRatesPromise = fetch(STATE_FOREIGN_RATES_URL, { headers: { Accept: "application/json" } })
      .then((response) => {
        if (!response.ok) throw new Error(`State foreign data HTTP ${response.status}`);
        return response.json();
      })
      .then((payload) => {
        stateForeignRates = Array.isArray(payload?.entries) ? payload.entries : [];
        return stateForeignRates;
      })
      .catch(() => {
        stateForeignRates = [];
        return stateForeignRates;
      })
      .finally(() => {
        stateForeignRatesPromise = null;
      });
    return stateForeignRatesPromise;
  }

  function nearestGlobalAirport(lat, lon) {
    if (!Array.isArray(globalAirports) || !globalAirports.length) return null;
    let best = null;
    let bestMiles = Number.POSITIVE_INFINITY;
    globalAirports.forEach((airport) => {
      const miles = haversineMiles(lat, lon, airport.lat, airport.lon);
      if (miles < bestMiles) {
        bestMiles = miles;
        best = { ...airport, miles };
      }
    });
    return best;
  }

  function airportSizeRank(airport) {
    const size = String(airport?.size || "").toLowerCase();
    if (size === "large" || size === "large_airport") return 3;
    if (size === "medium" || size === "medium_airport") return 2;
    if (size === "small" || size === "small_airport") return 1;
    return 0;
  }

  function findGlobalAirportByCode(code) {
    const needle = String(code || "").trim().toUpperCase();
    if (!needle || !Array.isArray(globalAirports) || !globalAirports.length) return null;
    const airport = globalAirports.find((entry) => String(entry?.iata || "").trim().toUpperCase() === needle);
    return airport ? { ...airport, miles: 0 } : null;
  }

  function normalizeRegionText(...values) {
    return values
      .map((value) => String(value || "").trim().toUpperCase())
      .filter(Boolean)
      .join(" | ");
  }

  function marinaRegionText(item) {
    return normalizeRegionText(
      item?.portfolio?.stateCountry,
      item?.portfolio?.country,
      item?.properties?.stateCountry,
      item?.properties?.country,
      item?.properties?.Country,
    );
  }

  function isAirTriggerRegion(item) {
    const regionText = marinaRegionText(item);
    return AIR_TRIGGER_REGIONS.some((region) => regionText.includes(region));
  }

  function resolveTripTravelMode() {
    const requested = String(state.planning.travelMode || "auto").toLowerCase();
    if (requested === "drive" || requested === "air") return requested;
    return "auto";
  }

  function resolveOfficeAirport(office) {
    if (!office) return null;
    const override = String(state.planning.homeAirportOverride || "").trim().toUpperCase();
    if (override) return findGlobalAirportByCode(override) || nearestGlobalAirport(office.lat, office.lng);
    return findGlobalAirportByCode(office.airportCode) || nearestGlobalAirport(office.lat, office.lng);
  }

  function estimateAirLeg(from, to, { originAirport = null, destAirport = null } = {}) {
    const resolvedOriginAirport = originAirport || nearestGlobalAirport(from.lat, from.lng);
    const resolvedDestAirport = destAirport || nearestGlobalAirport(to.lat, to.lng);
    const airMiles = resolvedOriginAirport && resolvedDestAirport
      ? haversineMiles(resolvedOriginAirport.lat, resolvedOriginAirport.lon, resolvedDestAirport.lat, resolvedDestAirport.lon)
      : milesBetween(from.lat, from.lng, to.lat, to.lng);
    const overheadHours = Math.max(0, Number(state.planning.airOverheadHours) || 4);
    const cruiseMph = Math.max(100, Number(state.planning.airCruiseMph) || 450);
    const originGroundMiles = resolvedOriginAirport
      ? milesBetween(from.lat, from.lng, resolvedOriginAirport.lat, resolvedOriginAirport.lon)
      : 0;
    const destGroundMiles = resolvedDestAirport
      ? milesBetween(to.lat, to.lng, resolvedDestAirport.lat, resolvedDestAirport.lon)
      : 0;
    const groundMinutes = estimateDriveMinutes(originGroundMiles + destGroundMiles);
    const fares = estimateFare(
      airMiles,
      airportTypeAdj(resolvedOriginAirport?.size),
      airportTypeAdj(resolvedDestAirport?.size),
    );
    return {
      miles: originGroundMiles + destGroundMiles,
      driveMinutes: groundMinutes + (overheadHours * 60) + ((airMiles / cruiseMph) * 60),
      source: "air",
      airMiles,
      airfare: fares.roundTrip * (Math.max(1, Math.round(state.planning.crewSize) || DEFAULT_CREW_SIZE)),
      originAirport: resolvedOriginAirport,
      destAirport: resolvedDestAirport,
    };
  }

  function marinaMissionRegionKey(item) {
    return normalizeRegionText(
      item?.portfolio?.country,
      item?.portfolio?.stateCountry,
      item?.properties?.country,
      item?.properties?.Country,
      item?.properties?.stateCountry,
    );
  }

  function missionGroupCentroid(items) {
    if (!Array.isArray(items) || !items.length) return null;
    const sum = items.reduce((acc, item) => {
      acc.lat += Number(item?.lat) || 0;
      acc.lng += Number(item?.lng) || 0;
      return acc;
    }, { lat: 0, lng: 0 });
    return {
      lat: sum.lat / items.length,
      lng: sum.lng / items.length,
    };
  }

  function preferredMissionAirportCode(items) {
    const regionKey = marinaMissionRegionKey(items?.[0]);
    if (!regionKey) return "";
    if (regionKey.includes("PUERTO RICO")) return "SJU";
    if (/ST\.?\s*KITTS|SAINT KITTS|ST KITTS/.test(regionKey)) return "NEV";
    return "";
  }

  function resolveMissionAirport(items) {
    if (!Array.isArray(globalAirports) || !globalAirports.length || !Array.isArray(items) || !items.length) return null;
    const preferredCode = preferredMissionAirportCode(items);
    if (preferredCode) {
      const preferred = findGlobalAirportByCode(preferredCode);
      if (preferred) return preferred;
    }
    const centroid = missionGroupCentroid(items);
    if (!centroid) return null;
    const regionKey = marinaMissionRegionKey(items[0]);
    const countryToken = String(items[0]?.portfolio?.country || items[0]?.properties?.country || items[0]?.properties?.Country || "").trim().toUpperCase();
    const regionCandidates = globalAirports.filter((airport) => {
      const airportCountry = String(airport?.country || "").trim().toUpperCase();
      if (countryToken && airportCountry === countryToken) return true;
      if (regionKey && regionKey.includes("PUERTO RICO") && airportCountry === "PR") return true;
      if (regionKey && /ST\.?\s*KITTS|SAINT KITTS|ST KITTS/.test(regionKey) && airportCountry === "KN") return true;
      return false;
    });
    const candidates = regionCandidates.length ? regionCandidates : globalAirports;
    const preferred = candidates.filter((airport) => airportSizeRank(airport) >= 2);
    const pool = preferred.length ? preferred : candidates;
    let best = null;
    let bestScore = Number.POSITIVE_INFINITY;
    pool.forEach((airport) => {
      const distance = haversineMiles(centroid.lat, centroid.lng, airport.lat, airport.lon);
      const sizeBias = airportSizeRank(airport) >= 2 ? 0 : 80;
      const score = distance + sizeBias;
      if (score < bestScore) {
        bestScore = score;
        best = { ...airport, miles: distance };
      }
    });
    return best;
  }

  function areMissionMarinasConnected(a, b, nodeIndexById, scopeKey) {
    const aIndex = nodeIndexById.get(a?.id) || 0;
    const bIndex = nodeIndexById.get(b?.id) || 0;
    if (aIndex > 0 && bIndex > 0 && routeMatrixStatus === "ready" && routeMatrixKey === scopeKey) {
      return hasRoutedConnectivity(scopeKey, aIndex, bIndex) || hasRoutedConnectivity(scopeKey, bIndex, aIndex);
    }
    const regionA = marinaMissionRegionKey(a);
    const regionB = marinaMissionRegionKey(b);
    if (regionA && regionA === regionB) return true;
    return milesBetween(a?.lat, a?.lng, b?.lat, b?.lng) <= 150;
  }

  function buildAirMissionGroups(items, nodeIndexById, scopeKey) {
    const remaining = new Set(items.map((item) => item.id));
    const byId = new Map(items.map((item) => [item.id, item]));
    const groups = [];
    while (remaining.size) {
      const seedId = remaining.values().next().value;
      const seed = byId.get(seedId);
      const queue = seed ? [seed] : [];
      const group = [];
      remaining.delete(seedId);
      while (queue.length) {
        const current = queue.shift();
        group.push(current);
        Array.from(remaining).forEach((candidateId) => {
          const candidate = byId.get(candidateId);
          if (!candidate) return;
          if (!areMissionMarinasConnected(current, candidate, nodeIndexById, scopeKey)) return;
          remaining.delete(candidateId);
          queue.push(candidate);
        });
      }
      if (group.length) groups.push(group);
    }
    return groups;
  }

  function hasRoutedConnectivity(scopeKey, nodeIndexA, nodeIndexB) {
    if (routeMatrixStatus !== "ready" || routeMatrixKey !== scopeKey) return false;
    const value = routeMatrixDurations?.[nodeIndexA]?.[nodeIndexB];
    return Number.isFinite(Number(value));
  }

  function formatDriveMinutes(minutes) {
    const total = Math.max(0, Math.round(Number(minutes) || 0));
    if (total < 60) return `${total} min`;
    const hours = Math.floor(total / 60);
    const mins = total % 60;
    return mins ? `${hours}h ${mins}m` : `${hours}h`;
  }

  function formatMiles(miles) {
    return `${(Number(miles) || 0).toFixed(1)} mi`;
  }

  function useMapCenterForOfficeDraft() {
    const center = map.getCenter?.();
    if (!center) return;
    state.officeDraft = {
      ...state.officeDraft,
      lat: Number(center.lat).toFixed(6),
      lng: Number(center.lng).toFixed(6),
    };
    render();
  }

  function addOffice() {
    const name = String(state.officeDraft.name || "").trim();
    const lat = Number(state.officeDraft.lat);
    const lng = Number(state.officeDraft.lng);
    if (!name || !Number.isFinite(lat) || !Number.isFinite(lng)) {
      app.ui.toast("Office name, latitude, and longitude are required.");
      return;
    }
    state.offices.push({
      id: `office-${Date.now().toString(36)}-${Math.random().toString(16).slice(2, 6)}`,
      name,
      lat,
      lng,
      airportCode: "",
    });
    if (!state.planning.tripOfficeId) state.planning.tripOfficeId = state.offices[state.offices.length - 1]?.id || "";
    state.officeDraft = { name: "", lat: "", lng: "" };
    recomputePlanning();
    drawPlanningOverlay(currentItem());
    render();
  }

  function removeOffice(index) {
    if (index < 0 || index >= state.offices.length) return;
    const removed = state.offices[index];
    state.offices.splice(index, 1);
    state.selectedOfficeIds = state.selectedOfficeIds.filter((id) => id !== removed?.id);
    state.visibleOfficeMarinaIds = state.visibleOfficeMarinaIds.filter((id) => id !== removed?.id);
    if (state.planning.tripOfficeId === removed?.id) {
      state.planning.tripOfficeId = state.offices[0]?.id || "";
    }
    recomputePlanning();
    persistLocalState();
    drawPlanningOverlay(currentItem());
    render();
  }

  function toggleOfficeSelection(index) {
    const office = state.offices[index];
    if (!office) return;
    if (state.selectedOfficeIds.includes(office.id)) {
      state.selectedOfficeIds = state.selectedOfficeIds.filter((id) => id !== office.id);
    } else {
      state.selectedOfficeIds = [...state.selectedOfficeIds, office.id];
    }
    drawPlanningOverlay(currentItem());
    render();
  }

  function toggleOfficeMarinaVisibility(index) {
    const office = state.offices[index];
    if (!office) return;
    const isOn = state.visibleOfficeMarinaIds.includes(office.id);
    if (isOn) {
      state.visibleOfficeMarinaIds = state.visibleOfficeMarinaIds.filter((id) => id !== office.id);
      state.selectedOfficeIds = state.selectedOfficeIds.filter((id) => id !== office.id);
    } else {
      state.visibleOfficeMarinaIds = [...state.visibleOfficeMarinaIds, office.id];
      if (!state.selectedOfficeIds.includes(office.id)) {
        state.selectedOfficeIds = [...state.selectedOfficeIds, office.id];
      }
    }
    persistLocalState();
    drawPlanningOverlay(currentItem());
    render();
  }

  function selectedOfficeColorMap() {
    const mapById = new Map();
    state.selectedOfficeIds.forEach((id, index) => {
      mapById.set(id, OFFICE_SELECTION_COLORS[index % OFFICE_SELECTION_COLORS.length]);
    });
    return mapById;
  }

  function buildNeighborhoods(items) {
    const spacingMiles = Math.max(0.25, Number(state.planning.clusterSpacingMiles) || 70);
    const visited = new Set();
    const neighborhoods = [];
    items.forEach((seed) => {
      if (visited.has(seed.id)) return;
      const queue = [seed];
      const group = [];
      visited.add(seed.id);
      while (queue.length) {
        const current = queue.shift();
        group.push(current);
        items.forEach((candidate) => {
          if (visited.has(candidate.id)) return;
          const spacing = milesBetween(current.lat, current.lng, candidate.lat, candidate.lng);
          if (spacing > spacingMiles) return;
          visited.add(candidate.id);
          queue.push(candidate);
        });
      }
      neighborhoods.push(group);
    });
    return neighborhoods;
  }

  function neighborhoodCentroid(items) {
    if (!items.length) return null;
    const sum = items.reduce((acc, item) => {
      acc.lat += Number(item.lat) || 0;
      acc.lng += Number(item.lng) || 0;
      return acc;
    }, { lat: 0, lng: 0 });
    return {
      lat: sum.lat / items.length,
      lng: sum.lng / items.length,
    };
  }

  function neighborhoodSpreadMiles(items, centroid) {
    if (!items.length || !centroid) return 0;
    return items.reduce((max, item) => Math.max(max, milesBetween(item.lat, item.lng, centroid.lat, centroid.lng)), 0);
  }

  function scoreOfficeForNeighborhood(office, items) {
    const centroid = neighborhoodCentroid(items);
    if (!office || !centroid) return Number.POSITIVE_INFINITY;
    const officeToCentroid = milesBetween(office.lat, office.lng, centroid.lat, centroid.lng);
    const spread = neighborhoodSpreadMiles(items, centroid);
    return officeToCentroid + (spread * 0.35);
  }

  function assignOfficeToNeighborhood(items) {
    if (!state.offices.length) return null;
    let bestOffice = null;
    let bestScore = Number.POSITIVE_INFINITY;
    state.offices.forEach((office) => {
      const score = scoreOfficeForNeighborhood(office, items);
      if (score < bestScore) {
        bestScore = score;
        bestOffice = office;
      }
    });
    return bestOffice;
  }

  function splitNeighborhoodIntoClusters(items, office) {
    const maxMarinas = Math.max(1, Math.round(Number(state.planning.maxMarinasPerCluster) || 5));
    const sorted = items
      .slice()
      .sort((a, b) => (a.officeDriveMinutes || 0) - (b.officeDriveMinutes || 0));
    const clusters = [];
    for (let i = 0; i < sorted.length; i += maxMarinas) {
      clusters.push({
        officeId: office?.id || "",
        officeName: office?.name || "",
        items: sorted.slice(i, i + maxMarinas),
      });
    }
    return clusters;
  }

  function recomputePlanning() {
    clearEnrichedTripPlan();
    state.items.forEach((item) => {
      item.officeId = "";
      item.officeName = "";
      item.officeDistanceMiles = null;
      item.officeDriveMinutes = null;
      item.clusterId = "";
      item.clusterLabel = "";
      item.neighborhoodId = "";
    });

    if (!state.offices.length) return;

    const neighborhoods = buildNeighborhoods(state.items);
    neighborhoods.forEach((group, neighborhoodIndex) => {
      const office = assignOfficeToNeighborhood(group);
      group.forEach((item) => {
        item.neighborhoodId = `neighborhood-${neighborhoodIndex + 1}`;
        item.officeId = office?.id || "";
        item.officeName = office?.name || "";
        if (office) {
          const miles = milesBetween(item.lat, item.lng, office.lat, office.lng);
          item.officeDistanceMiles = miles;
          item.officeDriveMinutes = estimateDriveMinutes(miles);
        }
      });
    });

    let clusterSeq = 1;
    const byNeighborhood = new Map();
    state.items.forEach((item) => {
      const key = item.neighborhoodId || "none";
      const bucket = byNeighborhood.get(key) || [];
      bucket.push(item);
      byNeighborhood.set(key, bucket);
    });
    [...byNeighborhood.values()].forEach((group) => {
      const office = state.offices.find((entry) => entry.id === group[0]?.officeId) || null;
      const clusters = splitNeighborhoodIntoClusters(group, office);
      clusters.forEach((cluster) => {
        const label = `${cluster.officeName || "Unassigned"}-${clusterSeq}`;
        cluster.items.forEach((item) => {
          item.clusterId = `cluster-${clusterSeq}`;
          item.clusterLabel = label;
        });
        clusterSeq += 1;
      });
    });
  }

  function clusterSummaries() {
    const mapByLabel = new Map();
    state.items.forEach((item) => {
      if (!item.clusterLabel) return;
      const current = mapByLabel.get(item.clusterLabel) || {
        label: item.clusterLabel,
        officeName: item.officeName || "Unassigned",
        count: 0,
        selectedCount: 0,
        totalDriveMinutes: 0,
      };
      current.count += 1;
      current.selectedCount += item.selected ? 1 : 0;
      current.totalDriveMinutes += Number(item.officeDriveMinutes) || 0;
      mapByLabel.set(item.clusterLabel, current);
    });
    return [...mapByLabel.values()].sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: "base" }));
  }

  function zoomToCluster(label) {
    const clusterItems = state.items.filter((item) => item.clusterLabel === label);
    if (!clusterItems.length) return;
    const officeName = clusterItems[0]?.officeName || "";
    const office = state.offices.find((entry) => entry.name === officeName) || null;
    const points = clusterItems.map((item) => [Number(item.lat), Number(item.lng)]);
    if (office) points.push([Number(office.lat), Number(office.lng)]);
    if (!points.length) return;
    const bounds = L.latLngBounds(points);
    if (!bounds.isValid()) return;
    suppressAutoSaveFor();
    map.fitBounds(bounds.pad(0.18), { animate: false });
    map.invalidateSize(false);
    drawPlanningOverlay(currentItem());
  }


  function zoomToOfficeCoverage(officeId) {
    const bounds = officeCoverageBounds(officeId, { selectedOnly: false });
    if (!bounds) return;
    suppressAutoSaveFor();
    map.fitBounds(bounds, { animate: false });
    map.invalidateSize(false);
    drawPlanningOverlay(currentItem());
  }

  function fitMapToTripPlaybackDay(plan, tripDayIndex) {
    const day = Array.isArray(plan?.days) && tripDayIndex >= 0 ? plan.days[tripDayIndex] || null : null;
    if (!day) return;
    const coords = [];
    if (day.startMode !== "hotel" && plan?.office) coords.push([Number(plan.office.lat), Number(plan.office.lng)]);
    if (Array.isArray(day.stops)) {
      day.stops.forEach((stop) => {
        if (Number.isFinite(Number(stop?.item?.lat)) && Number.isFinite(Number(stop?.item?.lng))) {
          coords.push([Number(stop.item.lat), Number(stop.item.lng)]);
        }
      });
    }
    routeGeometryFeatures
      .filter((feature) => Number(feature.day) === Number(day.day))
      .forEach((feature) => {
        (feature.latlngs || []).forEach((latlng) => {
          if (Array.isArray(latlng) && latlng.length >= 2) coords.push([Number(latlng[0]), Number(latlng[1])]);
        });
      });
    if (!coords.length) return;
    const bounds = L.latLngBounds(coords);
    if (!bounds.isValid()) return;
    suppressAutoSaveFor();
    map.fitBounds(bounds.pad(coords.length === 1 ? 0.2 : 0.12), { animate: false, padding: [24, 24] });
    map.invalidateSize(false);
  }

  function syncTripPlaybackOverlay(plan, { fit = false } = {}) {
    const tripDayIndex = normalizedTripPlaybackDayIndex(plan);
    state.tripPlaybackDayIndex = tripDayIndex;
    drawPlanningOverlay(tripDayIndex >= 0 ? null : currentItem(), {
      tripPlan: plan,
      tripDayIndex,
      routeScopeKey: String(plan?.routeScopeKey || plan?.routing?.scopeKey || ""),
    });
    if (fit) fitMapToTripPlaybackDay(plan, tripDayIndex);
  }

  function focusTripPlaybackRelative(step) {
    const plan = buildTripPlan();
    const dayCount = Array.isArray(plan?.days) ? plan.days.length : 0;
    if (!dayCount) return;
    const currentIndex = normalizedTripPlaybackDayIndex(plan);
    const nextIndex = Math.max(0, Math.min(dayCount - 1, currentIndex + step));
    state.tripPlaybackDayIndex = nextIndex;
    syncTripPlaybackOverlay(plan, { fit: state.tripPlaybackZoomToDay === true });
    render();
    const day = plan.days[nextIndex];
    setStatus(`Trip playback Day ${day?.day || (nextIndex + 1)}${day?.travelOnly ? `: ${day.travelLabel || "travel only"}` : day?.stops?.length ? `: ${day.stops.map((stop) => stop.item?.portfolio?.code || stop.item?.name).filter(Boolean).join(", ")}` : ""}`);
  }

  function officeCoverageBounds(officeId, { selectedOnly = true, excludeReportExcluded = false, missionScope = "all" } = {}) {
    const office = state.offices.find((entry) => entry.id === officeId);
    if (!office) return null;
    const points = [[Number(office.lat), Number(office.lng)]];
    scopeForOffice(officeId, { excludeReportExcluded, missionScope }).marinas
      .filter((item) => (!selectedOnly || item.selected))
      .forEach((item) => {
        points.push([Number(item.lat), Number(item.lng)]);
      });
    const bounds = L.latLngBounds(points);
    return bounds.isValid() ? bounds.pad(0.18) : null;
  }

  async function prepareOfficeCoverageCapture(officeId, { excludeReportExcluded = false, missionScope = "all", routeScopeKey: routeScopeKeyOverride = "" } = {}) {
    const bounds = officeCoverageBounds(officeId, { selectedOnly: false, excludeReportExcluded, missionScope });
    if (bounds) {
      map.fitBounds(bounds, { animate: false });
      map.invalidateSize(false);
    }
    await delay(120);
    map.invalidateSize({ pan: false, debounceMoveend: false });
    await delay(120);
    const toggledOfficeIds = Array.isArray(state.visibleOfficeMarinaIds) ? state.visibleOfficeMarinaIds.filter(Boolean) : [];
    const overlayItems = toggledOfficeIds.length
      ? state.items.filter((entry) => (
          toggledOfficeIds.includes(entry.officeId)
          && (!excludeReportExcluded || entry.excludeFromReport !== true)
        ))
      : scopeForOffice(officeId, { excludeReportExcluded, missionScope }).marinas;
    drawPlanningOverlay(currentItem(), {
      items: overlayItems,
      routeScopeKey: routeScopeKeyOverride,
    });
    if (state.showTripRoute && routeScopeKeyOverride && routeGeometryKey === routeScopeKeyOverride && routeGeometryFeatures.length) {
      const waitStart = Date.now();
      while ((Date.now() - waitStart) < 6000) {
        const drawnRouteCount = ensureCaptureLayer().getLayers().filter((entry) => entry instanceof L.Polyline).length;
        if (drawnRouteCount >= routeGeometryFeatures.length) break;
        drawPlanningOverlay(currentItem(), {
          items: overlayItems,
          routeScopeKey: routeScopeKeyOverride,
        });
        await delay(120);
      }
    }
    await waitForMapToSettle({ timeoutMs: 12000, stableMs: 700, pollMs: 100 });
    if (state.showTripRoute && routeScopeKeyOverride && routeGeometryKey === routeScopeKeyOverride && routeGeometryFeatures.length) {
      await delay(250);
    }
  }

  function clearRouteMatrix() {
    routeMatrixKey = "";
    routeMatrixDurations = null;
    routeMatrixDistances = null;
    routeMatrixStatus = "idle";
    routeMatrixError = "";
    routeMatrixRequestId += 1;
    clearRouteGeometry();
    clearEnrichedTripPlan();
  }

  function clearRouteGeometry() {
    routeGeometryKey = "";
    routeGeometryStatus = "idle";
    routeGeometryError = "";
    routeGeometryFeatures = [];
  }

  function scopeForOffice(officeId = state.planning.tripOfficeId, { excludeReportExcluded = false, missionScope = "all" } = {}) {
    const office = state.offices.find((entry) => entry.id === officeId) || state.offices[0] || null;
    const groupFilter = state.planning.tripGroupFilter;
    let marinas;
    if (groupFilter) {
      const groupCodes = new Set(
        (state.catalog.marinas || [])
          .filter((m) => m.group === groupFilter)
          .map((m) => String(m.code || ""))
          .filter(Boolean)
      );
      marinas = state.items.filter((item) => (
        groupCodes.has(String(item.id || ""))
        && (!excludeReportExcluded || item.excludeFromReport !== true)
        && (
          missionScope === "all"
          || (missionScope === "road" && !isAirMissionPriority(item.prioritizeAirTravel))
          || (missionScope === "air" && isAirMissionPriority(item.prioritizeAirTravel))
        )
      ));
    } else {
      marinas = state.items.filter((item) => (
        item.selected
        && item.officeId === office?.id
        && (!excludeReportExcluded || item.excludeFromReport !== true)
        && (
          missionScope === "all"
          || (missionScope === "road" && !isAirMissionPriority(item.prioritizeAirTravel))
          || (missionScope === "air" && isAirMissionPriority(item.prioritizeAirTravel))
        )
      ));
    }
    return { office, marinas };
  }

  function activeTripScope() {
    return scopeForOffice(state.planning.tripOfficeId);
  }

  function routeScopeKey(office, marinas) {
    return `${office?.id || "none"}|${marinas.map((item) => item.id).join(",")}`;
  }

  function tripRouteGeometryKey(scopeKey, route = [], days = []) {
    const routeSig = Array.isArray(route)
      ? route.map((stop) => `${stop?.item?.id || "none"}:${stop?.driveSource || "estimated"}`).join(",")
      : "";
    const daySig = Array.isArray(days)
      ? days.map((day) => {
        const stopSig = Array.isArray(day?.stops)
          ? day.stops.map((stop) => `${stop?.item?.id || "none"}:${stop?.driveSource || "estimated"}`).join(",")
          : "";
        return [
          day?.day || 0,
          day?.startMode || "office",
          day?.endMode || "field",
          day?.travelOnly ? 1 : 0,
          day?.travelSource || "survey",
          stopSig,
          day?.overnightAnchorItem?.id || "none",
        ].join(":");
      }).join("|")
      : "";
    return `${scopeKey || "none"}|route=${routeSig}|days=${daySig}`;
  }

  function currentTripRouteScopeKey() {
    const plan = buildTripPlan(state.planning.tripOfficeId, { excludeReportExcluded: false });
    return String(plan?.routeScopeKey || plan?.routing?.scopeKey || "");
  }

  async function refreshRouteMatrix({ force = false, officeId = state.planning.tripOfficeId, excludeReportExcluded = false, missionScope = "all" } = {}) {
    const { office, marinas } = scopeForOffice(officeId, { excludeReportExcluded, missionScope });
    if (!office || !marinas.length) {
      clearRouteMatrix();
      render();
      return;
    }
    const key = routeScopeKey(office, marinas);
    if (!force && routeMatrixStatus === "ready" && routeMatrixKey === key && routeMatrixDurations) return;
    if (!force && routeMatrixStatus === "loading" && routeMatrixKey === key) return;
    if (!force && routeMatrixStatus === "fallback" && routeMatrixKey === key) return;
    if (marinas.length + 1 > 90) {
      routeMatrixKey = key;
      routeMatrixDurations = null;
      routeMatrixDistances = null;
      routeMatrixStatus = "fallback";
      routeMatrixError = "Too many stops for live routed matrix; using estimated travel.";
      render();
      return;
    }
    routeMatrixKey = key;
    routeMatrixStatus = "loading";
    routeMatrixError = "";
    routeMatrixDurations = null;
    routeMatrixDistances = null;
    const requestId = routeMatrixRequestId + 1;
    routeMatrixRequestId = requestId;
    render();
    try {
      const coords = [[office.lng, office.lat], ...marinas.map((item) => [item.lng, item.lat])]
        .map(([lng, lat]) => `${Number(lng)},${Number(lat)}`)
        .join(";");
      const url = `https://router.project-osrm.org/table/v1/driving/${coords}?annotations=duration,distance`;
      const response = await fetch(url, { headers: { Accept: "application/json" } });
      if (!response.ok) throw new Error(`Routing HTTP ${response.status}`);
      const payload = await response.json();
      if (requestId !== routeMatrixRequestId) return;
      if (payload?.code !== "Ok" || !Array.isArray(payload?.durations)) {
        throw new Error(payload?.message || "Routing matrix unavailable");
      }
      routeMatrixDurations = payload.durations;
      routeMatrixDistances = Array.isArray(payload.distances) ? payload.distances : null;
      routeMatrixStatus = "ready";
      routeMatrixError = "";
      render();
    } catch (error) {
      if (requestId !== routeMatrixRequestId) return;
      console.warn("[ImageGenerator] routed matrix failed", error);
      routeMatrixDurations = null;
      routeMatrixDistances = null;
      routeMatrixStatus = "fallback";
      routeMatrixError = error?.message || "Routing unavailable; using estimated travel.";
      render();
    }
  }

  function estimateLeg(a, b) {
    const miles = milesBetween(a.lat, a.lng, b.lat, b.lng);
    return {
      miles,
      driveMinutes: estimateDriveMinutes(miles),
    };
  }

  function formatCurrency(amount) {
    const value = Number(amount) || 0;
    return value.toLocaleString(undefined, { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  function formatMilesPrecise(miles) {
    return `${(Number(miles) || 0).toFixed(1)} mi`;
  }

  function readTravelPlanningGsaApiKey() {
    const localKey = String(state?.planning?.gsaApiKey || "").trim();
    if (localKey) return localKey;
    try {
      const raw = localStorage.getItem(TRAVEL_PLANNING_STATE_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      return String(parsed?.gsaApiKey || "").trim();
    } catch {
      return "";
    }
  }

  function writeTravelPlanningGsaApiKey(value) {
    const nextValue = String(value || "").trim();
    try {
      const raw = localStorage.getItem(TRAVEL_PLANNING_STATE_KEY);
      const parsed = raw ? JSON.parse(raw) : {};
      localStorage.setItem(TRAVEL_PLANNING_STATE_KEY, JSON.stringify({
        ...(parsed && typeof parsed === "object" ? parsed : {}),
        gsaApiKey: nextValue,
      }));
    } catch {}
  }

  function normalizeUsStateCode(stateCodeRaw, stateNameRaw = "") {
    const code = String(stateCodeRaw || "").trim().toUpperCase();
    if (/^[A-Z]{2}$/.test(code)) return code;
    if (/^US-[A-Z]{2}$/.test(code)) return code.slice(3);
    const codeMatches = String(stateCodeRaw || "").toUpperCase().match(/\b([A-Z]{2})\b/g) || [];
    const extractedCode = codeMatches.find((entry) => entry !== "US");
    if (extractedCode && /^[A-Z]{2}$/.test(extractedCode)) return extractedCode;
    const name = String(stateNameRaw || "").trim().toUpperCase();
    const byName = {
      ALABAMA: "AL", ALASKA: "AK", ARIZONA: "AZ", ARKANSAS: "AR", CALIFORNIA: "CA", COLORADO: "CO",
      CONNECTICUT: "CT", DELAWARE: "DE", "DISTRICT OF COLUMBIA": "DC", FLORIDA: "FL", GEORGIA: "GA",
      HAWAII: "HI", IDAHO: "ID", ILLINOIS: "IL", INDIANA: "IN", IOWA: "IA", KANSAS: "KS", KENTUCKY: "KY",
      LOUISIANA: "LA", MAINE: "ME", MARYLAND: "MD", MASSACHUSETTS: "MA", MICHIGAN: "MI", MINNESOTA: "MN",
      MISSISSIPPI: "MS", MISSOURI: "MO", MONTANA: "MT", NEBRASKA: "NE", NEVADA: "NV", "NEW HAMPSHIRE": "NH",
      "NEW JERSEY": "NJ", "NEW MEXICO": "NM", "NEW YORK": "NY", "NORTH CAROLINA": "NC", "NORTH DAKOTA": "ND",
      OHIO: "OH", OKLAHOMA: "OK", OREGON: "OR", PENNSYLVANIA: "PA", "RHODE ISLAND": "RI", "SOUTH CAROLINA": "SC",
      "SOUTH DAKOTA": "SD", TENNESSEE: "TN", TEXAS: "TX", UTAH: "UT", VERMONT: "VT", VIRGINIA: "VA",
      WASHINGTON: "WA", "WEST VIRGINIA": "WV", WISCONSIN: "WI", WYOMING: "WY",
      "PUERTO RICO": "PR", "U.S. VIRGIN ISLANDS": "VI", "US VIRGIN ISLANDS": "VI", "VIRGIN ISLANDS": "VI",
      GUAM: "GU", "NORTHERN MARIANA ISLANDS": "MP", "AMERICAN SAMOA": "AS",
    };
    return byName[name] || "";
  }

  function firstNumber(obj, keys) {
    for (const key of keys) {
      const val = obj?.[key];
      const n = Number(val);
      if (Number.isFinite(n) && n > 0) return n;
    }
    return null;
  }

  function normalizeRateCity(value) {
    return String(value || "")
      .toUpperCase()
      .replace(/\([^)]*\)/g, " ")
      .replace(/[^A-Z0-9]+/g, " ")
      .replace(/\bSAINT\b/g, "ST")
      .replace(/\bFT\b/g, "FORT")
      .replace(/\s+/g, " ")
      .trim();
  }

  function isConusState(stateCode) {
    const code = String(stateCode || "").trim().toUpperCase();
    return /^[A-Z]{2}$/.test(code) && !["AK", "HI", "PR", "VI", "GU", "AS", "MP"].includes(code);
  }

  function isDodNonForeignState(stateCode) {
    return ["PR", "VI", "AK", "HI", "GU", "AS", "MP"].includes(String(stateCode || "").trim().toUpperCase());
  }

  function isForeignPerDiemCandidate(item) {
    const country = String(item?.portfolio?.stateCountry || item?.properties?.stateCountry || item?.properties?.country || item?.properties?.Country || "").trim().toUpperCase();
    if (!country) return false;
    if (country === "PUERTO RICO" || country === "U.S. VIRGIN ISLANDS" || country === "US VIRGIN ISLANDS" || country === "VIRGIN ISLANDS") return false;
    return !/^[A-Z]{2}$/.test(country) || !["AL","AK","AZ","AR","CA","CO","CT","DE","DC","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","PR","VI","GU","AS","MP"].includes(country);
  }

  function monthInWrappedRange(month, startMonth, endMonth) {
    const m = Number(month);
    const start = Number(startMonth);
    const end = Number(endMonth);
    if (!Number.isFinite(m) || !Number.isFinite(start) || !Number.isFinite(end)) return false;
    if (start <= end) return m >= start && m <= end;
    return m >= start || m <= end;
  }

  function lookupLocalConusPerDiem(city, stateCode, month) {
    if (!isConusState(stateCode) || !Array.isArray(gsaConusRates) || !gsaConusRates.length) return null;
    const cityNorm = normalizeRateCity(city);
    const stateNorm = String(stateCode || "").trim().toUpperCase();
    const candidates = gsaConusRates.filter((entry) => String(entry?.state || "").toUpperCase() === stateNorm);
    if (!candidates.length) return null;
    let best = null;
    let bestScore = -1;
    candidates.forEach((entry) => {
      const entryCityNorm = normalizeRateCity(entry?.city || "");
      let score = 0;
      if (entryCityNorm === cityNorm) score += 10;
      else if (entryCityNorm.includes(cityNorm) || cityNorm.includes(entryCityNorm)) score += 5;
      if (score > bestScore) {
        bestScore = score;
        best = entry;
      }
    });
    if (!best || bestScore < 0) return null;
    const lodging = Number(best?.months?.[String(month)] ?? best?.months?.[String(((month - 1 + 12) % 12) + 1)]);
    const mie = Number(best?.mie);
    if (!Number.isFinite(lodging) || !Number.isFinite(mie)) return null;
    return {
      ok: true,
      fallback: false,
      source: "gsa-local",
      reason: "",
      lodging,
      mie,
    };
  }

  function lookupLocalDodOconusPerDiem(city, stateCode, month) {
    const stateNorm = String(stateCode || "").trim().toUpperCase();
    if (!isDodNonForeignState(stateNorm) || !Array.isArray(dodOconusRates) || !dodOconusRates.length) return null;
    const cityNorm = normalizeRateCity(city);
    const candidates = dodOconusRates.filter((entry) => String(entry?.state || "").toUpperCase() === stateNorm);
    if (!candidates.length) return null;
    let best = null;
    let bestScore = -1;
    candidates.forEach((entry) => {
      const aliases = [entry?.city, ...(Array.isArray(entry?.aliases) ? entry.aliases : [])].map((value) => normalizeRateCity(value));
      let score = 0;
      if (aliases.includes(cityNorm)) score += 10;
      else if (aliases.some((alias) => alias && (alias.includes(cityNorm) || cityNorm.includes(alias)))) score += 5;
      else if (aliases.includes("OTHER")) score += 1;
      if (score > bestScore) {
        bestScore = score;
        best = entry;
      }
    });
    const period = Array.isArray(best?.periods) ? best.periods.find((entry) => monthInWrappedRange(month, entry.startMonth, entry.endMonth)) : null;
    if (!best || !period) return null;
    const lodging = Number(period?.lodging);
    const mie = Number(period?.mie);
    if (!Number.isFinite(lodging) || !Number.isFinite(mie)) return null;
    return {
      ok: true,
      fallback: false,
      source: "dod-oconus",
      reason: "",
      lodging,
      mie,
    };
  }

  function normalizeForeignCountry(value) {
    return String(value || "")
      .toUpperCase()
      .replace(/&/g, "AND")
      .replace(/\bST\b/g, "SAINT")
      .replace(/[^A-Z0-9]+/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  }

  function lookupLocalStateForeignPerDiem(item, month) {
    if (!isForeignPerDiemCandidate(item) || !Array.isArray(stateForeignRates) || !stateForeignRates.length) return null;
    const countryRaw = String(item?.portfolio?.stateCountry || item?.properties?.stateCountry || item?.properties?.country || item?.properties?.Country || "").trim();
    const cityRaw = String(item?.portfolio?.city || item?.properties?.city || item?.properties?.City || "").trim();
    const countryNorm = normalizeForeignCountry(countryRaw);
    const cityNorm = normalizeRateCity(cityRaw);
    if (!countryNorm) return null;
    const candidates = stateForeignRates.filter((entry) => normalizeForeignCountry(entry?.country || "") === countryNorm);
    if (!candidates.length) return null;
    let best = null;
    let bestScore = -1;
    candidates.forEach((entry) => {
      const locationNorm = normalizeRateCity(entry?.location || "");
      const seasonCode = String(entry?.seasonCode || "").trim().toUpperCase();
      let score = 0;
      if (cityNorm && locationNorm === cityNorm) score += 10;
      else if (cityNorm && (locationNorm.includes(cityNorm) || cityNorm.includes(locationNorm))) score += 5;
      else if (locationNorm === "OTHER") score += 1;
      if (seasonCode === "S1") score += 0.25;
      if (score > bestScore) {
        bestScore = score;
        best = entry;
      }
    });
    if (!best) return null;
    const startMonth = Number(String(best?.start || "").slice(5, 7));
    const endMonth = Number(String(best?.end || "").slice(5, 7));
    const inSeason = monthInWrappedRange(month, startMonth || month, endMonth || month);
    if (!inSeason && bestScore <= 1) return null;
    const lodging = Number(best?.lodging);
    const mie = Number(best?.mie);
    if (!Number.isFinite(lodging) || !Number.isFinite(mie)) return null;
    return {
      ok: true,
      fallback: false,
      source: "state-foreign",
      reason: "",
      lodging,
      mie,
    };
  }

  function parseGsaRatePayload(json, month, cityNorm = "", stateNorm = "") {
    const rates = Array.isArray(json?.rates) ? json.rates : [];
    let best = null;
    let bestScore = -1;
    for (const rateBlock of rates) {
      if (stateNorm && String(rateBlock?.state || "").toUpperCase() !== stateNorm) continue;
      const entries = Array.isArray(rateBlock?.rate) ? rateBlock.rate : (rateBlock?.rate ? [rateBlock.rate] : []);
      for (const entry of entries) {
        const city = String(entry?.city || "").trim().toUpperCase();
        const meals = firstNumber(entry, ["meals", "mie", "meals_incidental", "mealsIncidental", "me"]);
        const monthRows = Array.isArray(entry?.months?.month) ? entry.months.month : (entry?.months?.month ? [entry.months.month] : []);
        const exactMonth = monthRows.find((m) => Number(m?.number) === Number(month));
        const fallbackMonth = monthRows[0] || null;
        const lodging = Number(exactMonth?.value ?? fallbackMonth?.value);
        if (!Number.isFinite(lodging) || !Number.isFinite(meals)) continue;
        let score = 0;
        if (cityNorm && city && city === cityNorm) score += 10;
        if (String(entry?.standardRate || "").toLowerCase() === "false") score += 3;
        if (!city) score -= 2;
        if (score > bestScore) {
          bestScore = score;
          best = { lodging, mie: meals };
        }
      }
    }
    return best;
  }

  async function fetchGsaPerDiem(city, stateCode, apiKey, item = null) {
    const now = new Date();
    const year = now.getUTCFullYear();
    const month = now.getUTCMonth() + 1;
    const cityNorm = String(city || "").trim().toUpperCase();
    const stateNorm = String(stateCode || "").trim().toUpperCase();
    const isForeign = !!(item && isForeignPerDiemCandidate(item));
    if (isForeign) {
      await ensureStateForeignRatesLoaded();
      const foreignRate = lookupLocalStateForeignPerDiem(item, month);
      if (foreignRate) return foreignRate;
      if (!cityNorm) {
        return {
          ok: false,
          fallback: true,
          source: "fallback",
          reason: "missing foreign city/country",
          lodging: DEFAULT_CONUS_LODGING,
          mie: DEFAULT_CONUS_MIE,
        };
      }
    } else if (!cityNorm || !stateNorm) {
      return {
        ok: false,
        fallback: true,
        source: "fallback",
        reason: "missing city/state",
        lodging: DEFAULT_CONUS_LODGING,
        mie: DEFAULT_CONUS_MIE,
      };
    }
    if (isConusState(stateNorm)) {
      await ensureGsaConusRatesLoaded();
      const localRate = lookupLocalConusPerDiem(cityNorm, stateNorm, month);
      if (localRate) return localRate;
    }
    if (isDodNonForeignState(stateNorm)) {
      await ensureDodOconusRatesLoaded();
      const dodRate = lookupLocalDodOconusPerDiem(cityNorm, stateNorm, month);
      if (dodRate) return dodRate;
    }
    if (!apiKey) {
      return {
        ok: false,
        fallback: true,
        source: "fallback",
        reason: isConusState(stateNorm)
          ? "missing GSA API key and no local CONUS match"
          : isDodNonForeignState(stateNorm)
            ? "no local DoD OCONUS match"
            : isForeign
              ? "no local State foreign match"
              : "missing GSA API key",
        lodging: DEFAULT_CONUS_LODGING,
        mie: DEFAULT_CONUS_MIE,
      };
    }
    const urls = [
      `https://api.gsa.gov/travel/perdiem/v2/rates/city/${encodeURIComponent(cityNorm)}/state/${encodeURIComponent(stateNorm)}/year/${year}?api_key=${encodeURIComponent(apiKey)}`,
      `https://api.gsa.gov/travel/perdiem/v2/rates/city/${encodeURIComponent(cityNorm)}/year/${year}?api_key=${encodeURIComponent(apiKey)}`,
      `https://api.gsa.gov/travel/perdiem/v2/rates/state/${encodeURIComponent(stateNorm)}/year/${year}?api_key=${encodeURIComponent(apiKey)}`,
    ];
    const failures = [];
    for (const url of urls) {
      try {
        const response = await fetch(url, { headers: { Accept: "application/json" } });
        if (!response.ok) {
          failures.push(`HTTP ${response.status}`);
          continue;
        }
        const payload = await response.json();
        const parsed = parseGsaRatePayload(payload, month, cityNorm, stateNorm);
        if (parsed) return { ok: true, fallback: false, source: "gsa", reason: "", ...parsed };
        failures.push(payload?.message || "no matching GSA rate");
      } catch {}
    }
    return {
      ok: false,
      fallback: true,
      source: "fallback",
      reason: failures.length ? `GSA lookup failed (${uniqueNonEmpty(failures).join("; ")}); using CONUS fallback` : "GSA lookup failed; using CONUS fallback",
      lodging: DEFAULT_CONUS_LODGING,
      mie: DEFAULT_CONUS_MIE,
    };
  }

  function uniqueNonEmpty(list) {
    return Array.from(new Set((list || []).map((value) => String(value || "").trim()).filter(Boolean)));
  }

  function perDiemSourceSummary(plan) {
    const days = Array.isArray(plan?.days) ? plan.days.filter((day) => (Number(day?.perDiemCost) || 0) > 0) : [];
    if (!days.length) {
      const planDays = Array.isArray(plan?.days) ? plan.days : [];
      const overnightDays = planDays.filter((day) => day?.startMode === "hotel" || day?.endMode === "hotel");
      return {
        label: "Not used",
        reason: overnightDays.length
          ? "No per diem-eligible overnight days were priced."
          : "Trip has no hotel/overnight days.",
      };
    }
    const hasGsa = days.some((day) => String(day.perDiemSource || "").startsWith("gsa"));
    const hasDod = days.some((day) => String(day.perDiemSource || "") === "dod-oconus");
    const hasState = days.some((day) => String(day.perDiemSource || "") === "state-foreign");
    const hasFallback = days.some((day) => day.perDiemSource === "fallback");
    const reasons = uniqueNonEmpty(days.filter((day) => day.perDiemSource === "fallback").map((day) => day.perDiemReason));
    const positiveSources = [hasGsa, hasDod, hasState].filter(Boolean).length;
    if ((positiveSources > 1) || ((hasGsa || hasDod) && hasFallback)) {
      return {
        label: "Mixed",
        reason: reasons.join("; "),
      };
    }
    if (hasGsa) {
      return {
        label: "GSA location-based",
        reason: "",
      };
    }
    if (hasDod) {
      return {
        label: "DoD OCONUS",
        reason: "",
      };
    }
    if (hasState) {
      return {
        label: "State Foreign",
        reason: "",
      };
    }
    return {
      label: "CONUS fallback",
      reason: reasons.join("; ") || "fallback used",
    };
  }

  function officeCityState(office) {
    const raw = String(office?.name || "").trim();
    const parts = raw.split(",").map((part) => part.trim()).filter(Boolean);
    return {
      city: parts[0] || raw,
      stateCode: normalizeUsStateCode(parts[1] || "", parts[1] || ""),
    };
  }

  function officePerDiemLocation(office) {
    const raw = String(office?.name || "").trim();
    const parts = raw.split(",").map((part) => part.trim()).filter(Boolean);
    const city = parts[0] || raw;
    const regionRaw = parts.slice(1).join(", ");
    const stateCode = normalizeUsStateCode(regionRaw, regionRaw);
    return {
      city,
      stateCode,
      country: stateCode ? "" : regionRaw,
      label: [city, stateCode || regionRaw].filter(Boolean).join(", "),
      cacheKey: stateCode
        ? `us|${String(city || "").toUpperCase()}|${stateCode}`
        : `foreign|${String(city || "").toUpperCase()}|${String(regionRaw || "").toUpperCase()}`,
    };
  }

  function itemPerDiemLocation(item, office = null) {
    const city = String(item?.portfolio?.city || item?.properties?.city || item?.properties?.City || "").split(",")[0].trim();
    const stateRaw = String(item?.portfolio?.stateCountry || item?.properties?.state || item?.properties?.State || item?.properties?.country || item?.properties?.Country || "").trim();
    const stateCode = normalizeUsStateCode(stateRaw, stateRaw);
    if (city && stateCode) {
      return {
        city,
        stateCode,
        country: "",
        label: [city, stateCode].filter(Boolean).join(", "),
        cacheKey: `us|${String(city || "").toUpperCase()}|${stateCode}`,
      };
    }
    if (item && isForeignPerDiemCandidate(item)) {
      const country = stateRaw;
      if (city || country) {
        return {
          city: city || country,
          stateCode: "",
          country,
          label: [city, country].filter(Boolean).join(", "),
          cacheKey: `foreign|${String(city || "").toUpperCase()}|${String(country || "").toUpperCase()}`,
        };
      }
    }
    return officePerDiemLocation(office);
  }

  function itemCityState(item, office = null) {
    const city = String(item?.portfolio?.city || item?.properties?.city || item?.properties?.City || "").split(",")[0].trim();
    const stateRaw = String(item?.portfolio?.stateCountry || item?.properties?.state || item?.properties?.State || "").trim();
    const stateCode = normalizeUsStateCode(stateRaw, stateRaw);
    if (city && stateCode) return { city, stateCode };
    return officeCityState(office);
  }

  function computeWeatherContingencyDays(baseTripDays) {
    const pct = Math.max(0, Number(state.planning.weatherContingencyPct) || 0);
    const baseDays = Math.max(0, Math.round(Number(baseTripDays) || 0));
    if (!baseDays || !pct) return 0;
    return Math.ceil(baseDays * (pct / 100));
  }

  function routingStatusLabel(routing) {
    const mode = String(routing?.mode || "estimated").toLowerCase();
    const status = String(routing?.status || "idle").toLowerCase();
    const error = String(routing?.error || "").trim();
    if (mode === "routed" && status === "ready") return "Routed";
    if (status === "fallback") {
      if (/too many stops/i.test(error)) return "Estimated: too many stops";
      if (/routing unavailable/i.test(error)) return "Estimated: routing unavailable";
      return error ? `Estimated: ${error}` : "Estimated: fallback";
    }
    if (status === "loading") return "Estimated: routing pending";
    if (status === "idle") return mode === "routed" ? "Routed" : "Estimated: not requested";
    return error ? `${mode === "routed" ? "Routed" : "Estimated"}: ${error}` : (mode === "routed" ? "Routed" : "Estimated");
  }

  function statusTone(kind, value) {
    const text = String(value || "").toLowerCase();
    if (kind === "routing") {
      if (text.includes("routed")) return "good";
      if (text.includes("pending")) return "warn";
      return "bad";
    }
    if (kind === "perDiem") {
      if (text.includes("gsa") || text.includes("dod") || text.includes("state")) return "good";
      if (text.includes("mixed")) return "warn";
      if (text.includes("not used")) return "neutral";
      return "bad";
    }
    if (kind === "routeOverlay") {
      if (text.includes("shown")) return "good";
      if (text.includes("loading") || text.includes("pending")) return "warn";
      return "neutral";
    }
    return "neutral";
  }

  function statusBadgeHtml(label, value, kind) {
    const tone = statusTone(kind, value);
    const styles = {
      good: { bg: "rgba(34,197,94,0.14)", border: "rgba(34,197,94,0.35)", color: "#86efac" },
      warn: { bg: "rgba(245,158,11,0.14)", border: "rgba(245,158,11,0.35)", color: "#fcd34d" },
      bad: { bg: "rgba(239,68,68,0.14)", border: "rgba(239,68,68,0.35)", color: "#fca5a5" },
      neutral: { bg: "rgba(148,163,184,0.12)", border: "rgba(148,163,184,0.28)", color: "#cbd5e1" },
    }[tone];
    return `<div class="small" style="margin-top:4px;">${esc(label)}: <span style="display:inline-flex; align-items:center; gap:6px; padding:2px 8px; border-radius:999px; border:1px solid ${styles.border}; background:${styles.bg}; color:${styles.color}; font-weight:700;">${esc(value)}</span></div>`;
  }

  function routingBadge(officeId) {
    if (officeId !== state.planning.tripOfficeId) {
      return {
        text: "Not trip office",
        bg: "rgba(148,163,184,0.12)",
        border: "rgba(148,163,184,0.28)",
        color: "#94a3b8",
      };
    }
    const label = routingStatusLabel({
      mode: routeMatrixStatus === "ready" ? "routed" : "estimated",
      status: routeMatrixStatus,
      error: routeMatrixError,
    });
    if (routeMatrixStatus === "ready") {
      return {
        text: label,
        bg: "rgba(34,197,94,0.14)",
        border: "rgba(34,197,94,0.35)",
        color: "#86efac",
      };
    }
    if (routeMatrixStatus === "loading") {
      return {
        text: label,
        bg: "rgba(245,158,11,0.14)",
        border: "rgba(245,158,11,0.35)",
        color: "#fcd34d",
      };
    }
    if (routeMatrixStatus === "fallback") {
      return {
        text: label,
        bg: "rgba(239,68,68,0.14)",
        border: "rgba(239,68,68,0.35)",
        color: "#fca5a5",
      };
    }
    return {
      text: label,
      bg: "rgba(148,163,184,0.12)",
      border: "rgba(148,163,184,0.28)",
      color: "#cbd5e1",
    };
  }

  function officeReportScopes(officeId, { excludeReportExcluded = true } = {}) {
    const road = scopeForOffice(officeId, { excludeReportExcluded, missionScope: "road" });
    const air = scopeForOffice(officeId, { excludeReportExcluded, missionScope: "air" });
    const scopes = [];
    if (road.office && road.marinas.length) scopes.push({ key: "road", office: road.office, marinas: road.marinas });
    if (air.office && air.marinas.length) scopes.push({ key: "air", office: air.office, marinas: air.marinas });
    if (!scopes.length) {
      const all = scopeForOffice(officeId, { excludeReportExcluded, missionScope: "all" });
      if (all.office && all.marinas.length) scopes.push({ key: "all", office: all.office, marinas: all.marinas });
    }
    return scopes;
  }

  function airRegionLabel(items) {
    const regionText = normalizeRegionText(...(items || []).map((item) => marinaMissionRegionKey(item)));
    if (/PUERTO RICO|ST\.?\s*KITTS|SAINT KITTS|NEVIS|VIRGIN ISLANDS|CARIBBEAN/.test(regionText)) return "Caribbean";
    return "Regional";
  }

  function reportTitleForScope(office, missionScope, marinas = []) {
    if (missionScope === "road") return `Trip Report: ${office?.name || "Office"} Office Proximity`;
    if (missionScope === "air") return `Trip Report: ${office?.name || "Office"} ${airRegionLabel(marinas)} Air Mission`;
    return `Trip Report: ${office?.name || "Office"} Office Proximity`;
  }

  function reportLauncherLabel(scope) {
    if (scope?.key === "air") return `${scope?.office?.name || "Office"} Air Report`;
    if (scope?.key === "road") return `${scope?.office?.name || "Office"} Road Report`;
    return `${scope?.office?.name || "Office"} Report`;
  }

  async function enrichPlanCosts(plan, office) {
    if (!plan || !Array.isArray(plan.days)) return plan;
    const apiKey = readTravelPlanningGsaApiKey();
    const mileageRate = Math.max(0.001, Number(state.planning.mileageRate) || FEDERAL_MILEAGE_RATE_2026);
    const crewSize = Math.max(1, Math.round(Number(state.planning.crewSize) || DEFAULT_CREW_SIZE));
    const overnightRateCache = new Map();
    for (const day of plan.days) {
      day.driveMiles = Number(day.driveMiles) || 0;
      day.mileageCost = day.driveMiles * mileageRate;
      day.perDiemCost = 0;
      day.perDiemLocation = "";
      day.perDiemSource = "";
      day.perDiemReason = "";
      const usesHotel = day.startMode === "hotel" || day.endMode === "hotel";
      if (!usesHotel) continue;
      const anchorItem = day.stops?.[day.stops.length - 1]?.item || day.stops?.[0]?.item || day.overnightAnchorItem || null;
      const loc = itemPerDiemLocation(anchorItem, office);
      const cacheKey = loc.cacheKey;
      let rate = overnightRateCache.get(cacheKey);
      if (!rate) {
        rate = await fetchGsaPerDiem(loc.city, loc.stateCode, apiKey, anchorItem);
        overnightRateCache.set(cacheKey, rate);
      }
      day.perDiemLocation = loc.label;
      day.perDiemCost = crewSize * ((Number(rate.lodging) || 0) + (Number(rate.mie) || 0));
      day.perDiemSource = String(rate?.source || (rate?.fallback ? "fallback" : "gsa")).toLowerCase();
      day.perDiemReason = String(rate?.reason || "").trim();
    }
    plan.totals.driveMiles = plan.days.reduce((sum, day) => sum + (Number(day.driveMiles) || 0), 0);
    plan.totals.mileageCost = plan.days.reduce((sum, day) => sum + (Number(day.mileageCost) || 0), 0);
    plan.totals.perDiemCost = plan.days.reduce((sum, day) => sum + (Number(day.perDiemCost) || 0), 0);
    plan.totals.perDiemDays = plan.days.reduce((sum, day) => sum + ((Number(day.perDiemCost) || 0) > 0 ? 1 : 0), 0);
    plan.totals.airfareCost = plan.days.reduce((sum, day) => sum + (Number(day.airfareCost) || 0), 0);
    plan.totals.travelCost = plan.totals.mileageCost + plan.totals.airfareCost + plan.totals.perDiemCost;
    plan.totals.pricePerMarina = Math.max(0, Number(state.planning.pricePerMarina) || 0);
    plan.totals.weatherContingencyPct = Math.max(0, Number(state.planning.weatherContingencyPct) || 0);
    plan.totals.baseTripDays = Math.max(0, Number(plan.days?.length) || 0);
    plan.totals.travelOnlyDays = plan.days.reduce((sum, day) => sum + (day?.travelOnly ? 1 : 0), 0);
    plan.totals.weatherContingencyDays = computeWeatherContingencyDays(plan.totals.baseTripDays);
    plan.totals.adjustedTripDays = plan.totals.baseTripDays + plan.totals.weatherContingencyDays;
    plan.totals.marinaRevenue = plan.totals.marinaCount * plan.totals.pricePerMarina;
    plan.totals.supportDayRate = (state.planning.weatherDayRate > 0) ? state.planning.weatherDayRate : plan.totals.pricePerMarina * 0.5;
    plan.totals.supportDayQty = plan.totals.travelOnlyDays + plan.totals.weatherContingencyDays;
    plan.totals.laborEquipmentCost = plan.totals.supportDayRate * plan.totals.supportDayQty;
    plan.totals.estimateTotal = plan.totals.marinaRevenue + plan.totals.travelCost + plan.totals.laborEquipmentCost;
    plan.totals.perDiemRate = plan.totals.perDiemDays > 0
      ? (plan.totals.perDiemCost / plan.totals.perDiemDays)
      : 0;
    const perDiemSummary = perDiemSourceSummary(plan);
    plan.totals.perDiemSource = perDiemSummary.label;
    plan.totals.perDiemReason = perDiemSummary.reason;
    plan.costs = { mileageRate, crewSize };
    return plan;
  }

  function clearEnrichedTripPlan() {
    enrichedTripPlanKey = "";
    enrichedTripPlan = null;
    enrichedTripPlanRequestId += 1;
    rawTripPlanCacheKey = "";
    rawTripPlanCache = null;
  }

  function clonePlan(plan) {
    if (!plan) return plan;
    if (typeof structuredClone === "function") {
      try { return structuredClone(plan); } catch {}
    }
    try {
      return JSON.parse(JSON.stringify(plan));
    } catch {
      return plan;
    }
  }

  function enrichedTripPlanCacheKey(plan) {
    return [
      plan?.routing?.scopeKey || "",
      routeMatrixStatus,
      routeMatrixKey,
      state.planning.tripOfficeId,
      state.planning.defaultSurveyHours,
      state.planning.maxWorkHoursPerDay,
      state.planning.maxDriveHoursPerDay,
      state.planning.maxMarinasPerDay,
      state.planning.pricePerMarina,
      state.planning.weatherContingencyPct,
      state.planning.weatherDayRate,
      state.planning.mileageRate,
      state.planning.crewSize,
      state.planning.returnToOffice ? 1 : 0,
      readTravelPlanningGsaApiKey(),
      state.planning.travelMode,
      state.planning.airOverheadHours,
      state.planning.airCruiseMph,
      state.items.filter((item) => item.selected && !item.excludeFromReport).map((item) => `${item.id}:${Number(item.surveyHours) || 0}:${item.prioritizeAirTravel ? 1 : 0}`).join("|"),
    ].join("::");
  }

  function ensureEnrichedTripPlan(plan, precomputedKey) {
    const key = precomputedKey ?? enrichedTripPlanCacheKey(plan);
    if (enrichedTripPlanKey === key && enrichedTripPlan) return;
    const requestId = enrichedTripPlanRequestId + 1;
    enrichedTripPlanRequestId = requestId;
    enrichPlanCosts(clonePlan(plan), plan?.office)
      .then((result) => {
        if (requestId !== enrichedTripPlanRequestId) return;
        enrichedTripPlanKey = key;
        enrichedTripPlan = result;
        render();
      })
      .catch(() => {});
  }

  function getMatrixLeg(nodeIndexA, nodeIndexB, from, to) {
    if (routeMatrixStatus === "ready" && Array.isArray(routeMatrixDurations) && routeMatrixDurations[nodeIndexA]?.[nodeIndexB] != null) {
      const durationSeconds = Number(routeMatrixDurations[nodeIndexA][nodeIndexB]);
      const distanceMeters = Number(routeMatrixDistances?.[nodeIndexA]?.[nodeIndexB]);
      return {
        miles: Number.isFinite(distanceMeters) ? distanceMeters / 1609.344 : milesBetween(from.lat, from.lng, to.lat, to.lng),
        driveMinutes: Number.isFinite(durationSeconds) ? durationSeconds / 60 : estimateDriveMinutes(milesBetween(from.lat, from.lng, to.lat, to.lng)),
        source: "routed",
      };
    }
    const estimated = estimateLeg(from, to);
    return { ...estimated, source: "estimated" };
  }

  function buildTripRouteLegRequests(plan) {
    const office = plan?.office;
    if (!office || !Array.isArray(plan?.days)) return [];
    const legs = [];
    let previousFieldPoint = null;
    function pushLeg(base) {
      if (!base?.from || !base?.to) return;
      legs.push(base);
    }
      function airLegEndpoints(leg, fallbackFrom, fallbackTo) {
        const fromAirportPoint = airportPoint(leg?.originAirport);
        const toAirportPoint = airportPoint(leg?.destAirport);
        return {
          from: fromAirportPoint || fallbackFrom || null,
          to: toAirportPoint || fallbackTo || null,
        };
      }
      plan.days.forEach((day) => {
        if (day?.travelOnly) {
          const anchorItem = day?.overnightAnchorItem || null;
          const anchorPoint = anchorItem
            ? { lat: Number(anchorItem.lat), lng: Number(anchorItem.lng) }
            : null;
          const officePoint = { lat: office.lat, lng: office.lng };
          const startPoint = day.startMode === "hotel" && previousFieldPoint
            ? previousFieldPoint
            : officePoint;
          const travelFrom = normalizeTravelPoint(day?.travelFrom) || startPoint;
          const travelTo = normalizeTravelPoint(day?.travelTo) || (day.endMode === "office" ? officePoint : anchorPoint);
          if (day?.travelSource === "air") {
            const groundFrom = normalizeTravelPoint(day?.travelFrom) || startPoint;
            const groundTo = normalizeTravelPoint(day?.travelTo) || (day.endMode === "office" ? officePoint : anchorPoint);
            const from = airportPoint(day.originAirport);
            const to = airportPoint(day.destAirport);
            if (groundFrom && from && (
              Math.abs(Number(groundFrom.lat) - Number(from.lat)) > 1e-6
              || Math.abs(Number(groundFrom.lng) - Number(from.lng)) > 1e-6
            )) {
              pushLeg({
                day: day.day,
                segment: "travel-only-ground-start",
                source: "estimated",
                from: groundFrom,
                to: from,
              });
            }
            if (from && to) {
              pushLeg({
              day: day.day,
              segment: "travel-only",
              source: "air",
              from,
                to,
              });
            }
            if (to && groundTo && (
              Math.abs(Number(groundTo.lat) - Number(to.lat)) > 1e-6
              || Math.abs(Number(groundTo.lng) - Number(to.lng)) > 1e-6
            )) {
              pushLeg({
                day: day.day,
                segment: "travel-only-ground-end",
                source: "estimated",
                from: to,
                to: groundTo,
              });
            }
          } else if ((day?.travelSource || "estimated") !== "air") {
            if (travelFrom && travelTo) {
              pushLeg({
                day: day.day,
                segment: "travel-only",
                source: day?.travelSource || "estimated",
                from: travelFrom,
                to: travelTo,
                startRatio: day?.travelStartRatio,
                endRatio: day?.travelEndRatio,
              });
            }
          }
          previousFieldPoint = day.endMode === "hotel"
            ? (normalizeTravelPoint(day?.travelTo) || anchorPoint || previousFieldPoint)
            : null;
          return;
        }
      if (!Array.isArray(day?.stops) || !day.stops.length) return;
      const firstStop = day.stops[0];
      const firstStopTravelFrom = normalizeTravelPoint(firstStop?.travelFrom);
      const firstStopTravelTo = normalizeTravelPoint(firstStop?.travelTo) || { lat: Number(firstStop?.item?.lat), lng: Number(firstStop?.item?.lng) };
      const firstStopHasSplitApproach = !!firstStopTravelFrom
        && !!firstStopTravelTo
        && ((Number(firstStop?.travelStartRatio) || 0) > 1e-6 || (Number(firstStop?.travelEndRatio) || 1) < (1 - 1e-6));
      if (day.startMode === "office") {
        if (firstStop?.driveSource === "air-arrival") {
          previousFieldPoint = { lat: Number(firstStop.item.lat), lng: Number(firstStop.item.lng) };
        } else if (firstStopHasSplitApproach) {
          pushLeg({
            day: day.day,
            segment: "start",
            source: firstStop?.driveSource || "estimated",
            from: firstStopTravelFrom,
            to: firstStopTravelTo,
            startRatio: firstStop?.travelStartRatio,
            endRatio: firstStop?.travelEndRatio,
          });
        } else {
        const airEndpoints = firstStop?.driveSource === "air"
          ? airLegEndpoints(firstStop, { lat: office.lat, lng: office.lng }, { lat: firstStop.item.lat, lng: firstStop.item.lng })
          : null;
        if (firstStop?.driveSource === "air") {
          if (airEndpoints?.from && airEndpoints?.to) {
            pushLeg({
              day: day.day,
              segment: "start",
              source: "air",
              from: airEndpoints.from,
              to: airEndpoints.to,
            });
          }
        } else {
          pushLeg({
            day: day.day,
            segment: "start",
            source: firstStop?.driveSource || "estimated",
            from: { lat: office.lat, lng: office.lng },
            to: { lat: firstStop.item.lat, lng: firstStop.item.lng },
          });
        }
        }
      } else if (day.startMode === "hotel" && previousFieldPoint) {
        if (firstStop?.driveSource === "air-arrival") {
          previousFieldPoint = { lat: Number(firstStop.item.lat), lng: Number(firstStop.item.lng) };
        } else if (firstStopHasSplitApproach) {
          pushLeg({
            day: day.day,
            segment: "field-start",
            source: firstStop?.driveSource || "estimated",
            from: firstStopTravelFrom,
            to: firstStopTravelTo,
            startRatio: firstStop?.travelStartRatio,
            endRatio: firstStop?.travelEndRatio,
          });
        } else {
        const airEndpoints = firstStop?.driveSource === "air"
          ? airLegEndpoints(firstStop, previousFieldPoint, { lat: firstStop.item.lat, lng: firstStop.item.lng })
          : null;
        if (firstStop?.driveSource === "air") {
          if (airEndpoints?.from && airEndpoints?.to) {
            pushLeg({
              day: day.day,
              segment: "field-start",
              source: "air",
              from: airEndpoints.from,
              to: airEndpoints.to,
            });
          }
        } else {
          pushLeg({
            day: day.day,
            segment: "field-start",
            source: firstStop?.driveSource || "estimated",
            from: { lat: previousFieldPoint.lat, lng: previousFieldPoint.lng },
            to: { lat: firstStop.item.lat, lng: firstStop.item.lng },
          });
        }
        }
      } else if (day.startMode === "hotel") {
        if (firstStop?.driveSource === "air-arrival") {
          previousFieldPoint = { lat: Number(firstStop.item.lat), lng: Number(firstStop.item.lng) };
        } else if (firstStopHasSplitApproach) {
          pushLeg({
            day: day.day,
            segment: "field-start",
            source: firstStop?.driveSource || "estimated",
            from: firstStopTravelFrom,
            to: firstStopTravelTo,
            startRatio: firstStop?.travelStartRatio,
            endRatio: firstStop?.travelEndRatio,
          });
        } else {
        const airEndpoints = firstStop?.driveSource === "air"
          ? airLegEndpoints(firstStop, { lat: office.lat, lng: office.lng }, { lat: firstStop.item.lat, lng: firstStop.item.lng })
          : null;
        if (firstStop?.driveSource === "air") {
          if (airEndpoints?.from && airEndpoints?.to) {
            pushLeg({
              day: day.day,
              segment: "start",
              source: "air",
              from: airEndpoints.from,
              to: airEndpoints.to,
            });
          }
        } else {
          pushLeg({
            day: day.day,
            segment: "start",
            source: firstStop?.driveSource || "estimated",
            from: { lat: office.lat, lng: office.lng },
            to: { lat: firstStop.item.lat, lng: firstStop.item.lng },
          });
        }
        }
      }
      for (let i = 1; i < day.stops.length; i += 1) {
        const stop = day.stops[i];
        const prevStop = day.stops[i - 1];
        const airEndpoints = stop?.driveSource === "air"
          ? airLegEndpoints(stop, { lat: prevStop.item.lat, lng: prevStop.item.lng }, { lat: stop.item.lat, lng: stop.item.lng })
          : null;
        if (stop?.driveSource === "air") {
          if (airEndpoints?.from && airEndpoints?.to) {
            pushLeg({
              day: day.day,
              segment: "stop",
              source: "air",
              from: airEndpoints.from,
              to: airEndpoints.to,
            });
          }
        } else {
          pushLeg({
            day: day.day,
            segment: "stop",
            source: stop?.driveSource || "estimated",
            from: { lat: prevStop.item.lat, lng: prevStop.item.lng },
            to: { lat: stop.item.lat, lng: stop.item.lng },
          });
        }
      }
      if (day.endMode === "office" && day.returnLeg) {
        const lastStop = day.stops[day.stops.length - 1]?.item;
        if (lastStop) {
          const airEndpoints = day.returnLeg?.source === "air"
            ? airLegEndpoints(day.returnLeg, { lat: lastStop.lat, lng: lastStop.lng }, { lat: office.lat, lng: office.lng })
            : null;
          if (day.returnLeg?.source === "air") {
            if (airEndpoints?.from && airEndpoints?.to) {
              pushLeg({
                day: day.day,
                segment: "return",
                source: "air",
                from: airEndpoints.from,
                to: airEndpoints.to,
              });
            }
          } else {
            pushLeg({
              day: day.day,
              segment: "return",
              source: day.returnLeg?.source || "estimated",
              from: { lat: lastStop.lat, lng: lastStop.lng },
              to: { lat: office.lat, lng: office.lng },
            });
          }
        }
        previousFieldPoint = null;
      } else {
        const lastStop = day.stops[day.stops.length - 1]?.item;
        previousFieldPoint = lastStop
          ? { lat: Number(lastStop.lat), lng: Number(lastStop.lng) }
          : previousFieldPoint;
      }
    });
    return legs;
  }

  function routeColorForDay(day) {
    const colors = ["#f59e0b", "#a855f7", "#14b8a6", "#eab308", "#ec4899", "#06b6d4", "#84cc16", "#f97316"];
    return colors[(Math.max(1, Number(day) || 1) - 1) % colors.length];
  }

  async function fetchRouteGeometry(from, to) {
    const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
    const timeoutId = controller ? window.setTimeout(() => controller.abort(), 8000) : 0;
    const coords = `${Number(from.lng)},${Number(from.lat)};${Number(to.lng)},${Number(to.lat)}`;
    const url = `https://router.project-osrm.org/route/v1/driving/${coords}?overview=full&geometries=geojson`;
    try {
      const response = await fetch(url, { headers: { Accept: "application/json" }, signal: controller?.signal });
      if (!response.ok) throw new Error(`Route HTTP ${response.status}`);
      const payload = await response.json();
      const geometry = payload?.routes?.[0]?.geometry;
      if (payload?.code !== "Ok" || !geometry?.coordinates?.length) {
        throw new Error(payload?.message || "Route geometry unavailable");
      }
      return geometry.coordinates.map(([lng, lat]) => [Number(lat), Number(lng)]);
    } finally {
      if (timeoutId) window.clearTimeout(timeoutId);
    }
  }

  async function fetchRouteGeometryWithDeadline(from, to, timeoutMs = 9000) {
    return await Promise.race([
      fetchRouteGeometry(from, to),
      new Promise((_, reject) => {
        window.setTimeout(() => reject(new Error("Route geometry timeout")), Math.max(1000, Number(timeoutMs) || 9000));
      }),
    ]);
  }

  function normalizeRouteEndpoints(latlngs, from, to) {
    const points = Array.isArray(latlngs) ? latlngs.map((pair) => [Number(pair[0]), Number(pair[1])]) : [];
    const fromPoint = [Number(from?.lat), Number(from?.lng)];
    const toPoint = [Number(to?.lat), Number(to?.lng)];
    const normalized = [];
    if (Number.isFinite(fromPoint[0]) && Number.isFinite(fromPoint[1])) normalized.push(fromPoint);
    points.forEach((pair) => {
      if (Array.isArray(pair) && pair.length >= 2 && Number.isFinite(pair[0]) && Number.isFinite(pair[1])) {
        const previous = normalized[normalized.length - 1];
        if (!previous || Math.abs(previous[0] - pair[0]) > 1e-6 || Math.abs(previous[1] - pair[1]) > 1e-6) {
          normalized.push(pair);
        }
      }
    });
    if (Number.isFinite(toPoint[0]) && Number.isFinite(toPoint[1])) {
      const previous = normalized[normalized.length - 1];
      if (!previous || Math.abs(previous[0] - toPoint[0]) > 1e-6 || Math.abs(previous[1] - toPoint[1]) > 1e-6) {
        normalized.push(toPoint);
      }
    }
    return normalized;
  }

  function normalizeTravelPoint(point) {
    if (!point) return null;
    const lat = Number(point.lat);
    const lng = Number(point.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    return { lat, lng };
  }

  function interpolateRoutePoint(a, b, ratio) {
    const t = Math.max(0, Math.min(1, Number(ratio) || 0));
    return [
      Number(a[0]) + ((Number(b[0]) - Number(a[0])) * t),
      Number(a[1]) + ((Number(b[1]) - Number(a[1])) * t),
    ];
  }

  function pointAtRouteDistance(points, targetDistance) {
    if (!Array.isArray(points) || !points.length) return null;
    if (points.length === 1 || targetDistance <= 0) return [Number(points[0][0]), Number(points[0][1])];
    let traveled = 0;
    for (let index = 1; index < points.length; index += 1) {
      const prev = points[index - 1];
      const next = points[index];
      const segment = haversineMiles(prev[0], prev[1], next[0], next[1]) * 1609.344;
      if (segment <= 1e-6) continue;
      if ((traveled + segment) >= targetDistance) {
        return interpolateRoutePoint(prev, next, (targetDistance - traveled) / segment);
      }
      traveled += segment;
    }
    const last = points[points.length - 1];
    return [Number(last[0]), Number(last[1])];
  }

  function sliceRouteGeometryByRatio(points, startRatio = 0, endRatio = 1) {
    if (!Array.isArray(points) || points.length < 2) return Array.isArray(points) ? points : [];
    const clampedStart = Math.max(0, Math.min(1, Number(startRatio) || 0));
    const clampedEnd = Math.max(clampedStart, Math.min(1, Number(endRatio) || 1));
    if (clampedStart <= 1e-6 && clampedEnd >= (1 - 1e-6)) return points;
    let totalDistance = 0;
    const cumulative = [0];
    for (let index = 1; index < points.length; index += 1) {
      const prev = points[index - 1];
      const next = points[index];
      totalDistance += haversineMiles(prev[0], prev[1], next[0], next[1]) * 1609.344;
      cumulative.push(totalDistance);
    }
    if (totalDistance <= 1e-6) return [points[0], points[points.length - 1]];
    const startDistance = totalDistance * clampedStart;
    const endDistance = totalDistance * clampedEnd;
    const sliced = [];
    const startPoint = pointAtRouteDistance(points, startDistance);
    const endPoint = pointAtRouteDistance(points, endDistance);
    if (startPoint) sliced.push(startPoint);
    for (let index = 1; index < points.length - 1; index += 1) {
      if (cumulative[index] > startDistance && cumulative[index] < endDistance) {
        sliced.push([Number(points[index][0]), Number(points[index][1])]);
      }
    }
    if (endPoint) {
      const previous = sliced[sliced.length - 1];
      if (!previous || Math.abs(previous[0] - endPoint[0]) > 1e-6 || Math.abs(previous[1] - endPoint[1]) > 1e-6) {
        sliced.push(endPoint);
      }
    }
    return sliced.length >= 2 ? sliced : [startPoint || points[0], endPoint || points[points.length - 1]];
  }

  async function refreshRouteGeometry(plan, { force = false, preferEstimated = false } = {}) {
    const office = plan?.office;
    const marinas = Array.isArray(plan?.route)
      ? Array.from(new Map(plan.route.map((stop) => [stop?.item?.id, stop?.item]).filter((entry) => entry[0] && entry[1]))).map(([, item]) => item)
      : scopeForOffice(office?.id, { excludeReportExcluded: false }).marinas;
    const key = String(plan?.routeScopeKey || plan?.routing?.scopeKey || routeScopeKey(office, marinas));
    const wantsPlaybackGeometry = normalizedTripPlaybackDayIndex(plan) >= 0;
    if (!state.showTripRoute && !wantsPlaybackGeometry) {
      clearRouteGeometry();
      return { key, status: "hidden", features: [] };
    }
    if (!office || !marinas.length) {
      clearRouteGeometry();
      return { key, status: "empty", features: [] };
    }
    if (!force && routeGeometryStatus === "ready" && routeGeometryKey === key && routeGeometryFeatures.length) {
      return { key, status: "ready", features: routeGeometryFeatures };
    }
    if (!force && routeGeometryStatus === "loading" && routeGeometryKey === key) {
      return { key, status: "loading", features: routeGeometryFeatures };
    }
    if (!force && routeGeometryStatus === "fallback" && routeGeometryKey === key) {
      return { key, status: "fallback", features: routeGeometryFeatures };
    }
    const legs = buildTripRouteLegRequests(plan);
    if (!legs.length) {
      routeGeometryKey = key;
      routeGeometryStatus = "fallback";
      routeGeometryError = "No mappable route legs for the current trip plan.";
      routeGeometryFeatures = [];
      render();
      return { key, status: "fallback", features: [], error: routeGeometryError };
    }
    routeGeometryKey = key;
    routeGeometryStatus = "loading";
    routeGeometryError = "";
    routeGeometryFeatures = [];
    const requestId = routeGeometryRequestId + 1;
    routeGeometryRequestId = requestId;
    render();
    try {
      const features = [];
      for (let index = 0; index < legs.length; index += 1) {
        const leg = legs[index];
        if (requestId !== routeGeometryRequestId) return { key, status: "cancelled", features: [] };
        try {
          if (leg.source === "air") {
            features.push({
              ...leg,
              index,
              latlngs: [
                [Number(leg.from.lat), Number(leg.from.lng)],
                [Number(leg.to.lat), Number(leg.to.lng)],
              ],
              geometrySource: "air",
            });
            continue;
          }
          if (preferEstimated) {
            features.push({
              ...leg,
              index,
              latlngs: [
                [Number(leg.from.lat), Number(leg.from.lng)],
                [Number(leg.to.lat), Number(leg.to.lng)],
              ],
              geometrySource: "estimated",
            });
            continue;
          }
          const latlngs = await fetchRouteGeometryWithDeadline(leg.from, leg.to);
          const normalized = normalizeRouteEndpoints(latlngs, leg.from, leg.to);
          features.push({
            ...leg,
            index,
            latlngs: sliceRouteGeometryByRatio(normalized, leg.startRatio, leg.endRatio),
            geometrySource: "routed",
          });
        } catch {
          features.push({
            ...leg,
            index,
            latlngs: [
              [Number(leg.from.lat), Number(leg.from.lng)],
              [Number(leg.to.lat), Number(leg.to.lng)],
            ],
            geometrySource: "estimated",
          });
        }
      }
      if (requestId !== routeGeometryRequestId) return { key, status: "cancelled", features: [] };
      routeGeometryFeatures = features;
      routeGeometryStatus = "ready";
      routeGeometryError = "";
      syncTripPlaybackOverlay(plan);
      render();
      return { key, status: "ready", features };
    } catch (error) {
      if (requestId !== routeGeometryRequestId) return { key, status: "cancelled", features: [] };
      routeGeometryFeatures = [];
      routeGeometryStatus = "fallback";
      routeGeometryError = error?.message || "Route geometry unavailable";
      syncTripPlaybackOverlay(plan);
      render();
      return { key, status: "fallback", features: [], error: routeGeometryError };
    }
  }

  function ensureRouteMatrixForOffice(officeId = state.planning.tripOfficeId, { excludeReportExcluded = false, force = false, missionScope = "all" } = {}) {
    const { office, marinas } = scopeForOffice(officeId, { excludeReportExcluded, missionScope });
    if (!office || !marinas.length) {
      if (routeMatrixStatus !== "idle" || routeMatrixKey) {
        clearRouteMatrix();
        render();
      }
      return;
    }
    refreshRouteMatrix({ force, officeId, excludeReportExcluded, missionScope }).catch(() => {});
  }

  function ensureRouteGeometryForPlan(plan, { force = false } = {}) {
    const wantsPlaybackGeometry = normalizedTripPlaybackDayIndex(plan) >= 0;
    if (!state.showTripRoute && !wantsPlaybackGeometry) {
      if (routeGeometryStatus !== "idle" || routeGeometryKey || routeGeometryFeatures.length) {
        clearRouteGeometry();
      }
      return;
    }
    if (!plan?.office || !plan?.totals?.marinaCount) {
      if (routeGeometryStatus !== "idle" || routeGeometryKey || routeGeometryFeatures.length) {
        clearRouteGeometry();
      }
      return;
    }
    refreshRouteGeometry(plan, { force }).catch(() => {});
  }

  function buildTripPlan(officeId = state.planning.tripOfficeId, { excludeReportExcluded = false, missionScope = "all" } = {}) {
    const { office, marinas } = scopeForOffice(officeId, { excludeReportExcluded, missionScope });
    const scopeKey = routeScopeKey(office, marinas);
    const tripTravelMode = resolveTripTravelMode();
    // Memoize for the common default-args call to avoid O(n²) re-computation on every render.
    // Key covers everything that affects route order and day assignments.
    if (officeId === state.planning.tripOfficeId && !excludeReportExcluded && missionScope === "all") {
      const planKey = [
        scopeKey,
        tripTravelMode,
        routeMatrixStatus,
        routeMatrixKey,
        state.planning.defaultSurveyHours,
        state.planning.maxWorkHoursPerDay,
        state.planning.maxDriveHoursPerDay,
        state.planning.maxMarinasPerDay,
        state.planning.returnToOffice ? 1 : 0,
        state.planning.airOverheadHours,
        state.planning.airCruiseMph,
        state.planning.averageSpeedMph,
        state.planning.roadFactor,
        state.planning.pricePerMarina,
        state.planning.weatherContingencyPct,
        marinas.map((m) => `${m.id}:${Number(m.surveyHours) || 0}:${m.prioritizeAirTravel ? 1 : 0}`).join("|"),
      ].join("::");
      if (planKey === rawTripPlanCacheKey && rawTripPlanCache) return rawTripPlanCache;
      // Fall through to compute; cache will be set at the end.
      const result = _buildTripPlanCore(office, marinas, scopeKey, tripTravelMode);
      rawTripPlanCacheKey = planKey;
      rawTripPlanCache = result;
      return result;
    }
    return _buildTripPlanCore(office, marinas, scopeKey, tripTravelMode);
  }
  function _buildTripPlanCore(office, marinas, scopeKey, tripTravelMode) {
    if (!office || !marinas.length) {
      return {
        office,
        route: [],
        days: [],
        totals: {
          marinaCount: marinas.length,
          availableSelectedCount: state.items.filter((item) => item.selected).length,
          driveMiles: 0,
          mileageCost: 0,
          perDiemCost: 0,
          perDiemDays: 0,
          perDiemRate: 0,
          airfareCost: 0,
          travelCost: 0,
          driveMinutes: 0,
          surveyMinutes: 0,
          totalMinutes: 0,
          hotelNights: 0,
          pricePerMarina: Math.max(0, Number(state.planning.pricePerMarina) || 0),
          marinaRevenue: 0,
          weatherContingencyPct: Math.max(0, Number(state.planning.weatherContingencyPct) || 0),
          baseTripDays: 0,
          travelOnlyDays: 0,
          weatherContingencyDays: 0,
          adjustedTripDays: 0,
          supportDayRate: Math.max(0, Number(state.planning.pricePerMarina) || 0) * 0.5,
          supportDayQty: 0,
          laborEquipmentCost: 0,
          estimateTotal: 0,
        },
      routing: {
        mode: "estimated",
        status: routeMatrixStatus,
        error: routeMatrixError,
        scopeKey,
      },
      routeScopeKey: scopeKey,
      travelMode: tripTravelMode,
    };
  }

    const byId = new Map(marinas.map((item) => [item.id, item]));
    const nodeIndexById = new Map(marinas.map((item, idx) => [item.id, idx + 1]));
    const route = [];
    let current = { lat: office.lat, lng: office.lng };
    let currentNodeIndex = 0;
    const homeAirport = resolveOfficeAirport(office);
    const airMissionGroupById = new Map();
    const airMissionAirportByGroup = new Map();
    function planLeg(nodeIndexA, nodeIndexB, from, to, { forceAir = false, forceDrive = false, originAirport = null, destAirport = null } = {}) {
      const prioritizeAir = isAirMissionPriority(to?.prioritizeAirTravel);
      const fromPrioritizesAir = isAirMissionPriority(from?.prioritizeAirTravel);
      const toMissionGroup = to?.id ? String(airMissionGroupById.get(to.id) || "") : "";
      const fromMissionGroup = from?.id ? String(airMissionGroupById.get(from.id) || "") : "";
      const sameMissionGroup = !!toMissionGroup && !!fromMissionGroup && toMissionGroup === fromMissionGroup;
      const connectivityKnown = routeMatrixStatus === "ready" && routeMatrixKey === scopeKey;
      const disconnected = connectivityKnown && !hasRoutedConnectivity(scopeKey, nodeIndexA, nodeIndexB);
      if (forceAir) return estimateAirLeg(from, to, { originAirport, destAirport });
      if (forceDrive) return getMatrixLeg(nodeIndexA, nodeIndexB, from, to);
      if (sameMissionGroup && nodeIndexA > 0 && nodeIndexB > 0) {
        return getMatrixLeg(nodeIndexA, nodeIndexB, from, to);
      }
      if (tripTravelMode === "air" && nodeIndexA === 0 && nodeIndexB > 0) return estimateAirLeg(from, to, { originAirport: homeAirport });
      if (tripTravelMode === "air" && nodeIndexA > 0 && nodeIndexB === 0) return estimateAirLeg(from, to, { destAirport: homeAirport });
      if (tripTravelMode === "auto" && nodeIndexA > 0 && nodeIndexB === 0 && fromPrioritizesAir) {
        return estimateAirLeg(from, to, {
          originAirport: airMissionAirportByGroup.get(fromMissionGroup) || null,
          destAirport: homeAirport,
        });
      }
      if (tripTravelMode === "auto" && prioritizeAir && toMissionGroup && fromMissionGroup !== toMissionGroup) {
        return estimateAirLeg(from, to, {
          originAirport: nodeIndexA === 0
            ? homeAirport
            : (airMissionAirportByGroup.get(fromMissionGroup) || null),
          destAirport: airMissionAirportByGroup.get(toMissionGroup) || null,
        });
      }
      if (tripTravelMode === "auto" && prioritizeAir && nodeIndexB > 0 && (nodeIndexA === 0 || disconnected)) {
        return estimateAirLeg(from, to, {
          originAirport: nodeIndexA === 0
            ? homeAirport
            : (airMissionAirportByGroup.get(fromMissionGroup) || null),
          destAirport: airMissionAirportByGroup.get(toMissionGroup) || null,
        });
      }
      return getMatrixLeg(nodeIndexA, nodeIndexB, from, to);
    }
    function pushRouteStop(item, fromPoint, leg) {
      route.push({
        item,
        from: fromPoint,
        milesFromPrevious: leg?.miles ?? 0,
        driveMinutesFromPrevious: leg?.driveMinutes ?? 0,
        driveSource: leg?.source || "estimated",
        airfareFromPrevious: leg?.airfare ?? 0,
        originAirport: leg?.originAirport || null,
        destAirport: leg?.destAirport || null,
        missionGroup: item?.id ? String(airMissionGroupById.get(item.id) || "") : "",
        surveyMinutes: Math.max(0, Number(item.surveyHours) || Number(state.planning.defaultSurveyHours) || 0) * 60,
      });
    }
    function appendGreedyRoute(items, startPoint, startNodeIndex, options = {}) {
      const remainingIds = new Set(items.map((item) => item.id));
      let cursorPoint = startPoint;
      let cursorNodeIndex = startNodeIndex;
      let forceAirFirst = options.forceAirFirst === true;
      while (remainingIds.size) {
        let best = null;
        let bestLeg = null;
        let bestCost = Number.POSITIVE_INFINITY;
        remainingIds.forEach((id) => {
          const candidate = byId.get(id);
          const candidateNodeIndex = nodeIndexById.get(id) || 0;
          const leg = planLeg(cursorNodeIndex, candidateNodeIndex, cursorPoint, candidate, {
            forceDrive: options.forceDrive === true,
            forceAir: forceAirFirst,
            originAirport: forceAirFirst ? options.originAirport || null : null,
          });
          const cost = Number(leg?.driveMinutes) || Number.POSITIVE_INFINITY;
          if (cost < bestCost) {
            bestCost = cost;
            best = candidate;
            bestLeg = leg;
          }
        });
        if (!best) break;
        pushRouteStop(best, cursorPoint, bestLeg);
        remainingIds.delete(best.id);
        cursorPoint = best;
        cursorNodeIndex = nodeIndexById.get(best.id) || 0;
        forceAirFirst = false;
      }
      return { point: cursorPoint, nodeIndex: cursorNodeIndex };
    }

    if (tripTravelMode !== "auto") {
      appendGreedyRoute(marinas, current, currentNodeIndex);
    } else {
      const surfaceMarinas = marinas.filter((item) => !isAirMissionPriority(item?.prioritizeAirTravel));
      const airMissionMarinas = marinas.filter((item) => isAirMissionPriority(item?.prioritizeAirTravel));
      if (surfaceMarinas.length) {
        const surfaceResult = appendGreedyRoute(surfaceMarinas, current, currentNodeIndex);
        current = surfaceResult.point;
        currentNodeIndex = surfaceResult.nodeIndex;
      }
      if (airMissionMarinas.length) {
        const missions = buildAirMissionGroups(airMissionMarinas, nodeIndexById, scopeKey);
        missions.forEach((mission, missionIndex) => {
          const missionGroupKey = `air-mission-${missionIndex + 1}`;
          const missionAirport = resolveMissionAirport(mission);
          if (missionAirport) airMissionAirportByGroup.set(missionGroupKey, missionAirport);
          mission.forEach((item) => {
            airMissionGroupById.set(item.id, missionGroupKey);
          });
        });
        current = office;
        currentNodeIndex = 0;
        while (missions.length) {
          let bestMissionIndex = -1;
          let bestMissionStart = null;
          let bestMissionLeg = null;
          let bestMissionCost = Number.POSITIVE_INFINITY;
          missions.forEach((mission, missionIndex) => {
            mission.forEach((candidate) => {
              const candidateNodeIndex = nodeIndexById.get(candidate.id) || 0;
              const leg = planLeg(currentNodeIndex, candidateNodeIndex, current, candidate, {
                forceAir: true,
                originAirport: currentNodeIndex === 0 ? homeAirport : null,
              });
              const cost = Number(leg?.driveMinutes) || Number.POSITIVE_INFINITY;
              if (cost < bestMissionCost) {
                bestMissionCost = cost;
                bestMissionIndex = missionIndex;
                bestMissionStart = candidate;
                bestMissionLeg = leg;
              }
            });
          });
          if (bestMissionIndex < 0 || !bestMissionStart) break;
          const mission = missions.splice(bestMissionIndex, 1)[0] || [];
          pushRouteStop(bestMissionStart, current, bestMissionLeg);
          const missionRemaining = mission.filter((item) => item.id !== bestMissionStart.id);
          const missionResult = appendGreedyRoute(
            missionRemaining,
            bestMissionStart,
            nodeIndexById.get(bestMissionStart.id) || 0,
            { forceDrive: true },
          );
          current = missionResult.point;
          currentNodeIndex = missionResult.nodeIndex;
        }
      }
    }

    normalizeDailyPlanning();
    const maxTotalMinutes = DAILY_TOTAL_HOURS * 60;
    const maxWorkMinutes = Math.max(60, Number(state.planning.maxWorkHoursPerDay) || 7) * 60;
    const maxDriveMinutes = Math.max(0, Number(state.planning.maxDriveHoursPerDay) || 3) * 60;
    const maxMarinas = Math.max(1, Math.round(Number(state.planning.maxMarinasPerDay) || 2));
    const days = [];
    let routeIndex = 0;
    let dayStartPoint = office;
    let dayStartNodeIndex = 0;
    let dayStartMode = "office";
    let hotelNights = 0;
    let finalReturnLeg = null;

    function pushTravelOnlyDays(minutes, { startMode = "office", finalMode = "hotel", note = "Travel only day", miles = 0, airfare = 0, anchorItem = null, source = "estimated", fromPoint = null, toPoint = null, routeStartRatio = 0, routeEndRatio = 1 } = {}) {
      let remaining = Math.max(0, Number(minutes) || 0);
      let remainingMiles = Math.max(0, Number(miles) || 0);
      let remainingAirfare = Math.max(0, Number(airfare) || 0);
      const totalMinutes = remaining || 1;
      let progressedMinutes = 0;
      const baseStartRatio = Math.max(0, Math.min(1, Number(routeStartRatio) || 0));
      const baseEndRatio = Math.max(baseStartRatio, Math.min(1, Number(routeEndRatio) || 1));
      const ratioSpan = Math.max(0, baseEndRatio - baseStartRatio);
      let currentStartMode = startMode;
      let created = 0;
      while (remaining > 0.01) {
        const chunk = Math.min(maxTotalMinutes, remaining);
        const isFinalChunk = (remaining - chunk) <= 0.01;
        const endMode = isFinalChunk ? finalMode : "hotel";
        const chunkMiles = isFinalChunk
          ? remainingMiles
          : Math.max(0, remainingMiles * (chunk / totalMinutes));
        const chunkAirfare = isFinalChunk
          ? remainingAirfare
          : Math.max(0, remainingAirfare * (chunk / totalMinutes));
        days.push({
          day: days.length + 1,
          startMode: currentStartMode,
          stops: [],
          driveMiles: chunkMiles,
          driveMinutes: chunk,
          workMinutes: 0,
          totalMinutes: chunk,
          airfareCost: chunkAirfare,
          marinaCount: 0,
          overnightAfter: endMode === "hotel",
          canReturnHome: endMode === "office",
          returnLeg: null,
          endMode,
          travelOnly: true,
          travelLabel: note,
          travelSource: source || "estimated",
          travelFrom: fromPoint || null,
          travelTo: toPoint || null,
          travelStartRatio: totalMinutes > 0 ? baseStartRatio + (((progressedMinutes / totalMinutes) || 0) * ratioSpan) : baseStartRatio,
          travelEndRatio: totalMinutes > 0 ? baseStartRatio + ((((progressedMinutes + chunk) / totalMinutes) || 1) * ratioSpan) : baseEndRatio,
          overnightAnchorItem: anchorItem || null,
        });
        if (endMode === "hotel") hotelNights += 1;
        currentStartMode = "hotel";
        progressedMinutes += chunk;
        remaining -= chunk;
        remainingMiles = Math.max(0, remainingMiles - chunkMiles);
        remainingAirfare = Math.max(0, remainingAirfare - chunkAirfare);
        created += 1;
      }
      return created;
    }

    function absorbTravelIntoPreviousDay(minutes, { miles = 0, airfare = 0 } = {}) {
      const previousDay = days[days.length - 1];
      if (!previousDay || previousDay.endMode !== "hotel") return { minutes, miles, airfare };
      const spareTotal = Math.max(0, maxTotalMinutes - (Number(previousDay.totalMinutes) || 0));
      const absorbMinutes = Math.min(Math.max(0, Number(minutes) || 0), spareTotal);
      if (absorbMinutes <= 0.01) return { minutes, miles, airfare };
      const totalMinutes = Math.max(0.01, Number(minutes) || 0.01);
      const absorbRatio = absorbMinutes / totalMinutes;
      const absorbMiles = Math.max(0, Number(miles) || 0) * absorbRatio;
      const absorbAirfare = Math.max(0, Number(airfare) || 0) * absorbRatio;
      previousDay.driveMinutes += absorbMinutes;
      previousDay.totalMinutes += absorbMinutes;
      previousDay.driveMiles += absorbMiles;
      previousDay.airfareCost += absorbAirfare;
      return {
        minutes: Math.max(0, (Number(minutes) || 0) - absorbMinutes),
        miles: Math.max(0, (Number(miles) || 0) - absorbMiles),
        airfare: Math.max(0, (Number(airfare) || 0) - absorbAirfare),
      };
    }

    function airTravelLabel(leg) {
      const origin = String(leg?.originAirport?.iata || "AIR").trim().toUpperCase();
      const dest = String(leg?.destAirport?.iata || "AIR").trim().toUpperCase();
      return `Air travel ${origin} -> ${dest}`;
    }

    function shouldStayInFieldInsteadOfReturning(lastStopItem, nextStop) {
      if (!lastStopItem || !nextStop?.item || nextStop?.driveSource === "air" || !state.planning.returnToOffice) return false;
      const lastNodeIndex = nodeIndexById.get(lastStopItem.id) || 0;
      const nextNodeIndex = nodeIndexById.get(nextStop.item.id) || 0;
      const officeReturn = planLeg(lastNodeIndex, 0, lastStopItem, office);
      const officeApproach = planLeg(0, nextNodeIndex, office, nextStop.item);
      const fieldApproach = planLeg(lastNodeIndex, nextNodeIndex, lastStopItem, nextStop.item);
      const bounceMinutes = (officeReturn?.driveMinutes || 0) + (officeApproach?.driveMinutes || 0);
      const fieldMinutes = fieldApproach?.driveMinutes || 0;
      const bounceMiles = (officeReturn?.miles || 0) + (officeApproach?.miles || 0);
      const fieldMiles = fieldApproach?.miles || 0;
      return (bounceMinutes - fieldMinutes) >= FIELD_STAY_LOOKAHEAD_MINUTES
        || (bounceMiles - fieldMiles) >= FIELD_STAY_LOOKAHEAD_MILES;
    }

    function pushAirTravelDay(leg, { startMode = "office", finalMode = "hotel", anchorItem = null, fromPoint = null, toPoint = null } = {}) {
      const endMode = finalMode === "office" ? "office" : "hotel";
      days.push({
        day: days.length + 1,
        startMode,
        stops: [],
        driveMiles: Math.max(0, Number(leg?.miles) || 0),
        driveMinutes: maxTotalMinutes,
        workMinutes: 0,
        totalMinutes: maxTotalMinutes,
        airfareCost: Math.max(0, Number(leg?.airfare) || 0),
        marinaCount: 0,
        overnightAfter: endMode === "hotel",
        canReturnHome: endMode === "office",
        returnLeg: null,
        endMode,
        travelOnly: true,
        travelLabel: airTravelLabel(leg),
        travelSource: "air",
        travelFrom: fromPoint || null,
        travelTo: toPoint || null,
        originAirport: leg?.originAirport || null,
        destAirport: leg?.destAirport || null,
        overnightAnchorItem: anchorItem || null,
      });
      if (endMode === "hotel") hotelNights += 1;
    }

    while (routeIndex < route.length) {
      const pendingStop = route[routeIndex];
      const pendingStopNodeIndex = nodeIndexById.get(pendingStop?.item?.id) || 0;
      if (pendingStop?.driveSource === "air" && !pendingStop?.airTravelCompleted) {
        const pendingMissionAirport = airMissionAirportByGroup.get(String(pendingStop.missionGroup || "")) || pendingStop.destAirport || null;
        const startMissionGroup = dayStartPoint?.id ? String(airMissionGroupById.get(dayStartPoint.id) || "") : "";
        const startMissionAirport = dayStartNodeIndex === 0
          ? homeAirport
          : (airMissionAirportByGroup.get(startMissionGroup) || pendingStop.originAirport || null);
        const startAirportPoint = airportPoint(startMissionAirport);
        const destAirportPoint = airportPoint(pendingMissionAirport);
        let airDayEndPoint = destAirportPoint || null;
        let arrivedAtMarinaFromAir = false;
        let airDayStartPoint = dayStartPoint || startAirportPoint || null;
        if (dayStartPoint && startAirportPoint && !samePoint(dayStartPoint, startAirportPoint)) {
          const airportApproach = estimateLeg(dayStartPoint, startAirportPoint);
          if (airportApproach.driveMinutes > AIRPORT_TRANSFER_SPLIT_MINUTES) {
            pushTravelOnlyDays(airportApproach.driveMinutes, {
              startMode: dayStartMode,
              finalMode: "hotel",
              note: `Travel to airport for "${marinaCodeOrName(pendingStop.item)}"`,
              miles: airportApproach.miles,
              anchorItem: null,
              source: airportApproach.source || "estimated",
              fromPoint: dayStartPoint,
              toPoint: startAirportPoint,
            });
            dayStartPoint = startAirportPoint;
            dayStartNodeIndex = -1;
            dayStartMode = "hotel";
            airDayStartPoint = startAirportPoint;
          }
        }
        if (destAirportPoint && pendingStop.item && !samePoint(destAirportPoint, pendingStop.item)) {
          const airportArrival = estimateLeg(destAirportPoint, pendingStop.item);
          if (airportArrival.driveMinutes <= AIRPORT_TRANSFER_SPLIT_MINUTES) {
            airDayEndPoint = pendingStop.item;
            arrivedAtMarinaFromAir = true;
          }
        }
        pendingStop.originAirport = startMissionAirport;
        pendingStop.destAirport = pendingMissionAirport;
        pushAirTravelDay({
          miles: pendingStop.milesFromPrevious,
          airfare: pendingStop.airfareFromPrevious,
          originAirport: startMissionAirport,
          destAirport: pendingMissionAirport,
        }, {
          startMode: dayStartMode,
          finalMode: "hotel",
          anchorItem: pendingStop.item || null,
          fromPoint: airDayStartPoint || startAirportPoint || null,
          toPoint: airDayEndPoint,
        });
        pendingStop.airTravelCompleted = true;
        pendingStop.arrivedFromAir = arrivedAtMarinaFromAir;
        dayStartPoint = arrivedAtMarinaFromAir ? pendingStop.item : (destAirportPoint || pendingStop.item);
        dayStartNodeIndex = -1;
        dayStartMode = "hotel";
        continue;
      }
      const day = {
        day: days.length + 1,
        startMode: dayStartMode,
        stops: [],
        driveMinutes: 0,
        driveMiles: 0,
        workMinutes: 0,
        totalMinutes: 0,
        airfareCost: 0,
        marinaCount: 0,
        overnightAfter: false,
        canReturnHome: false,
        returnLeg: null,
        endMode: "field",
        travelOnly: false,
        travelLabel: "",
      };
      let cursorPoint = dayStartPoint;
      let cursorNodeIndex = dayStartNodeIndex;

      while (routeIndex < route.length) {
        const nextStop = route[routeIndex];
        const nextNodeIndex = nodeIndexById.get(nextStop.item.id) || 0;
        const approachLeg = nextStop.arrivedFromAir
          ? { miles: 0, driveMinutes: 0, source: "air-arrival", airfare: 0, originAirport: nextStop.originAirport || null, destAirport: nextStop.destAirport || null }
          : planLeg(cursorNodeIndex, nextNodeIndex, cursorPoint, nextStop.item);
        const nextDriveMinutes = day.driveMinutes + (approachLeg?.driveMinutes || 0);
        const nextWorkMinutes = day.workMinutes + nextStop.surveyMinutes;
        const nextTotalMinutes = nextDriveMinutes + nextWorkMinutes;
        const driveOverflow = Math.max(0, nextDriveMinutes - maxDriveMinutes);
        const workOverflow = Math.max(0, nextWorkMinutes - maxWorkMinutes);
        const driveBorrowCapacity = Math.max(0, maxWorkMinutes - nextWorkMinutes);
        const workBorrowCapacity = Math.max(0, maxDriveMinutes - nextDriveMinutes);
        const driveCapViolated = driveOverflow > driveBorrowCapacity;
        const workCapViolated = workOverflow > workBorrowCapacity;
        const returnCandidate = planLeg(nextNodeIndex, 0, nextStop.item, office);
        const nextCanReturnHome = state.planning.returnToOffice
          && ((nextDriveMinutes + (returnCandidate?.driveMinutes || 0)) + nextWorkMinutes) <= maxTotalMinutes
          && (nextTotalMinutes + (returnCandidate?.driveMinutes || 0)) <= maxTotalMinutes;
        const exceedsCaps = day.marinaCount >= maxMarinas
          || workCapViolated
          || driveCapViolated
          || nextTotalMinutes > maxTotalMinutes;
        if (exceedsCaps && !day.stops.length) break;
        if (exceedsCaps && day.stops.length) break;
        if (
          day.stops.length
          && nextStop.driveSource === "air"
          && String(day.stops[day.stops.length - 1]?.missionGroup || "") !== String(nextStop.missionGroup || "")
        ) break;
        if (
          day.stops.length
          && nextStop.driveSource === "air"
          && day.stops.every((stop) => !isAirMissionPriority(stop?.item?.prioritizeAirTravel))
          && day.canReturnHome
        ) break;
        if (day.stops.length && day.canReturnHome && !nextCanReturnHome) break;

        day.stops.push({
          ...nextStop,
          milesFromPrevious: approachLeg?.miles ?? 0,
          driveMinutesFromPrevious: approachLeg?.driveMinutes ?? 0,
          driveSource: approachLeg?.source || "estimated",
          airfareFromPrevious: approachLeg?.airfare ?? 0,
          originAirport: approachLeg?.originAirport || null,
          destAirport: approachLeg?.destAirport || null,
        });
        day.driveMiles += approachLeg?.miles || 0;
        day.driveMinutes = nextDriveMinutes;
        day.workMinutes = nextWorkMinutes;
        day.totalMinutes = nextTotalMinutes;
        day.airfareCost += approachLeg?.airfare || 0;
        day.marinaCount += 1;
        day.canReturnHome = nextCanReturnHome;
        day.returnLeg = nextCanReturnHome ? returnCandidate : null;
        nextStop.arrivedFromAir = false;
        cursorPoint = nextStop.item;
        cursorNodeIndex = nextNodeIndex;
        routeIndex += 1;
        if (exceedsCaps) break;
      }

      if (!day.stops.length && routeIndex < route.length) {
        const forcedStop = route[routeIndex];
        const forcedNodeIndex = nodeIndexById.get(forcedStop.item.id) || 0;
        const forcedLeg = planLeg(dayStartNodeIndex, forcedNodeIndex, dayStartPoint, forcedStop.item);
        const forcedReturn = planLeg(forcedNodeIndex, 0, forcedStop.item, office);
        const forcedLegMinutes = Math.max(0, Number(forcedLeg?.driveMinutes || 0));
        const forcedDayTotal = forcedLegMinutes + forcedStop.surveyMinutes;
        if (forcedDayTotal > maxTotalMinutes && forcedLegMinutes > 0.01) {
          pushTravelOnlyDays(forcedLegMinutes, {
            startMode: dayStartMode,
            finalMode: "hotel",
            note: `Travel to "${marinaCodeOrName(forcedStop.item)}"`,
            miles: Number(forcedLeg?.miles || 0),
            airfare: forcedLeg?.source === "air" ? (forcedLeg?.airfare || 0) : 0,
            anchorItem: forcedStop.item || null,
            source: forcedLeg?.source || "estimated",
            fromPoint: dayStartPoint || null,
            toPoint: forcedStop.item || null,
            routeStartRatio: 0,
            routeEndRatio: 1,
          });
          dayStartPoint = forcedStop.item;
          dayStartNodeIndex = forcedNodeIndex;
          dayStartMode = "hotel";
          continue;
        }
        const surveyDayDriveMinutes = forcedLegMinutes;
        const surveyDayDriveMiles = Math.max(0, Number(forcedLeg?.miles || 0));
        const forcedCanReturnHome = state.planning.returnToOffice
          && (forcedDayTotal + (forcedReturn?.driveMinutes || 0)) <= maxTotalMinutes;
        day.stops.push({
          ...forcedStop,
          milesFromPrevious: surveyDayDriveMiles,
          driveMinutesFromPrevious: surveyDayDriveMinutes,
          driveSource: forcedLeg?.source || "estimated",
          airfareFromPrevious: forcedLeg?.airfare ?? 0,
          originAirport: forcedLeg?.originAirport || null,
          destAirport: forcedLeg?.destAirport || null,
          travelFrom: dayStartPoint || null,
          travelTo: forcedStop.item || null,
          travelStartRatio: 0,
          travelEndRatio: 1,
        });
        day.startMode = dayStartMode;
        day.driveMiles = surveyDayDriveMiles;
        day.driveMinutes = surveyDayDriveMinutes;
        day.workMinutes = forcedStop.surveyMinutes;
        day.totalMinutes = day.driveMinutes + day.workMinutes;
        day.airfareCost = forcedLeg?.airfare || 0;
        day.marinaCount = 1;
        day.canReturnHome = forcedCanReturnHome;
        day.returnLeg = forcedCanReturnHome ? forcedReturn : null;
        routeIndex += 1;
        cursorPoint = forcedStop.item;
        cursorNodeIndex = forcedNodeIndex;
      }

      const hasMoreStops = routeIndex < route.length;
      const nextPendingStop = hasMoreStops ? route[routeIndex] : null;
      const preferFieldStay = hasMoreStops
        && day.stops.length
        && shouldStayInFieldInsteadOfReturning(day.stops[day.stops.length - 1]?.item, nextPendingStop);
      if (day.canReturnHome && day.returnLeg && day.returnLeg.source !== "air" && !preferFieldStay) {
        day.driveMiles += day.returnLeg.miles || 0;
        day.driveMinutes += day.returnLeg.driveMinutes || 0;
        day.totalMinutes += day.returnLeg.driveMinutes || 0;
        day.airfareCost += day.returnLeg.airfare || 0;
        day.endMode = "office";
        dayStartPoint = office;
        dayStartNodeIndex = 0;
        dayStartMode = "office";
        finalReturnLeg = day.returnLeg;
      } else if (
        hasMoreStops
        && route[routeIndex]?.driveSource === "air"
        && day.stops.length
        && day.stops.every((stop) => !isAirMissionPriority(stop?.item?.prioritizeAirTravel))
        && state.planning.returnToOffice
      ) {
        const lastStop = day.stops[day.stops.length - 1];
        const forcedOfficeReturn = planLeg(nodeIndexById.get(lastStop?.item?.id) || 0, 0, lastStop?.item || office, office);
        if (forcedOfficeReturn?.source === "air") {
          const originAirportPoint = airportPoint(forcedOfficeReturn.originAirport);
          const destAirportPoint = airportPoint(forcedOfficeReturn.destAirport);
          let airReturnStartPoint = lastStop?.item || originAirportPoint || null;
          let airReturnEndPoint = office;
          let airReturnFinalMode = "office";
          if (lastStop?.item && originAirportPoint && !samePoint(lastStop.item, originAirportPoint)) {
            const airportApproach = estimateLeg(lastStop.item, originAirportPoint);
            if (airportApproach.driveMinutes > AIRPORT_TRANSFER_SPLIT_MINUTES) {
              pushTravelOnlyDays(airportApproach.driveMinutes, {
                startMode: "hotel",
                finalMode: "hotel",
                note: "Travel to departure airport",
                miles: airportApproach.miles,
                anchorItem: null,
                source: airportApproach.source || "estimated",
                fromPoint: lastStop.item,
                toPoint: originAirportPoint,
              });
              airReturnStartPoint = originAirportPoint;
            }
          }
          if (destAirportPoint && !samePoint(destAirportPoint, office)) {
            const finalGround = estimateLeg(destAirportPoint, office);
            if (finalGround.driveMinutes > AIRPORT_TRANSFER_SPLIT_MINUTES) {
              airReturnEndPoint = destAirportPoint;
              airReturnFinalMode = "hotel";
            }
          }
          pushAirTravelDay(forcedOfficeReturn, {
            startMode: "hotel",
            finalMode: airReturnFinalMode,
            anchorItem: lastStop?.item || null,
            fromPoint: airReturnStartPoint || originAirportPoint || null,
            toPoint: airReturnEndPoint,
          });
          if (airReturnFinalMode === "hotel" && destAirportPoint && !samePoint(destAirportPoint, office)) {
            const finalGround = estimateLeg(destAirportPoint, office);
            pushTravelOnlyDays(finalGround.driveMinutes, {
              startMode: "hotel",
              finalMode: "office",
              note: "Return from arrival airport",
              miles: finalGround.miles,
              anchorItem: null,
              source: finalGround.source || "estimated",
              fromPoint: destAirportPoint,
              toPoint: office,
            });
          }
        } else {
          pushTravelOnlyDays(forcedOfficeReturn?.driveMinutes || 0, {
            startMode: "hotel",
            finalMode: "office",
            note: "Return to office",
            miles: forcedOfficeReturn?.miles || 0,
            airfare: forcedOfficeReturn?.airfare || 0,
            anchorItem: lastStop?.item || null,
            source: forcedOfficeReturn?.source || "estimated",
            fromPoint: lastStop?.item || null,
            toPoint: office,
          });
        }
        day.endMode = "hotel";
        day.overnightAfter = true;
        hotelNights += 1;
        dayStartPoint = office;
        dayStartNodeIndex = 0;
        dayStartMode = "office";
        finalReturnLeg = forcedOfficeReturn;
      } else if (hasMoreStops) {
        day.overnightAfter = true;
        day.endMode = "hotel";
        hotelNights += 1;
        const lastStop = day.stops[day.stops.length - 1];
        dayStartPoint = lastStop?.item || office;
        dayStartNodeIndex = nodeIndexById.get(lastStop?.item?.id) || 0;
        dayStartMode = "hotel";
        finalReturnLeg = null;
      } else if (state.planning.returnToOffice && day.stops.length) {
        const lastStop = day.stops[day.stops.length - 1];
        const fallbackReturn = planLeg(nodeIndexById.get(lastStop?.item?.id) || 0, 0, lastStop?.item || office, office);
        const lastMissionGroup = lastStop?.item?.id ? String(airMissionGroupById.get(lastStop.item.id) || "") : "";
        if (fallbackReturn?.source === "air") {
          fallbackReturn.originAirport = airMissionAirportByGroup.get(lastMissionGroup) || fallbackReturn.originAirport || null;
          fallbackReturn.destAirport = homeAirport || fallbackReturn.destAirport || null;
        }
        day.endMode = "hotel";
        day.overnightAfter = true;
        hotelNights += 1;
        finalReturnLeg = fallbackReturn;
        day.day = days.length + 1;
        days.push(day);
        if (fallbackReturn?.source === "air") {
          const originAirportPoint = airportPoint(fallbackReturn.originAirport);
          const destAirportPoint = airportPoint(fallbackReturn.destAirport);
          let airReturnStartPoint = lastStop?.item || originAirportPoint || null;
          let airReturnEndPoint = office;
          let airReturnFinalMode = "office";
          if (lastStop?.item && originAirportPoint && !samePoint(lastStop.item, originAirportPoint)) {
            const airportApproach = estimateLeg(lastStop.item, originAirportPoint);
            if (airportApproach.driveMinutes > AIRPORT_TRANSFER_SPLIT_MINUTES) {
              pushTravelOnlyDays(airportApproach.driveMinutes, {
                startMode: "hotel",
                finalMode: "hotel",
                note: "Travel to departure airport",
                miles: airportApproach.miles,
                anchorItem: null,
                source: airportApproach.source || "estimated",
                fromPoint: lastStop.item,
                toPoint: originAirportPoint,
              });
              airReturnStartPoint = originAirportPoint;
            }
          }
          if (destAirportPoint && !samePoint(destAirportPoint, office)) {
            const finalGround = estimateLeg(destAirportPoint, office);
            if (finalGround.driveMinutes > AIRPORT_TRANSFER_SPLIT_MINUTES) {
              airReturnEndPoint = destAirportPoint;
              airReturnFinalMode = "hotel";
            }
          }
          pushAirTravelDay(fallbackReturn, {
            startMode: "hotel",
            finalMode: airReturnFinalMode,
            anchorItem: lastStop?.item || null,
            fromPoint: airReturnStartPoint || originAirportPoint || null,
            toPoint: airReturnEndPoint,
          });
          if (airReturnFinalMode === "hotel" && destAirportPoint && !samePoint(destAirportPoint, office)) {
            const finalGround = estimateLeg(destAirportPoint, office);
            pushTravelOnlyDays(finalGround.driveMinutes, {
              startMode: "hotel",
              finalMode: "office",
              note: "Return from arrival airport",
              miles: finalGround.miles,
              anchorItem: null,
              source: finalGround.source || "estimated",
              fromPoint: destAirportPoint,
              toPoint: office,
            });
          }
        } else {
          pushTravelOnlyDays(fallbackReturn?.driveMinutes || 0, {
            startMode: "hotel",
            finalMode: "office",
            note: "Return to office",
            miles: fallbackReturn?.miles || 0,
            airfare: fallbackReturn?.airfare || 0,
            anchorItem: lastStop?.item || null,
            source: fallbackReturn?.source || "estimated",
            fromPoint: lastStop?.item || null,
            toPoint: office,
          });
        }
        dayStartPoint = office;
        dayStartNodeIndex = 0;
        dayStartMode = "office";
        continue;
      } else {
        day.endMode = "field";
        finalReturnLeg = null;
      }

      day.day = days.length + 1;
      days.push(day);
    }

    const totalDriveMinutes = days.reduce((sum, day) => sum + (day.driveMinutes || 0), 0);
    const totalDriveMiles = days.reduce((sum, day) => sum + (day.driveMiles || 0), 0);
    const totalSurveyMinutes = days.reduce((sum, day) => sum + (day.workMinutes || 0), 0);
    const geometryKey = tripRouteGeometryKey(scopeKey, route, days);
    return {
      office,
      route,
      days,
      returnLeg: finalReturnLeg,
      totals: {
        marinaCount: marinas.length,
        availableSelectedCount: state.items.filter((item) => item.selected).length,
        driveMiles: totalDriveMiles,
        mileageCost: totalDriveMiles * FEDERAL_MILEAGE_RATE_2026,
        perDiemCost: 0,
        airfareCost: days.reduce((sum, day) => sum + (Number(day.airfareCost) || 0), 0),
        travelCost: totalDriveMiles * FEDERAL_MILEAGE_RATE_2026,
        driveMinutes: totalDriveMinutes,
        surveyMinutes: totalSurveyMinutes,
        totalMinutes: totalDriveMinutes + totalSurveyMinutes,
        hotelNights,
        weatherContingencyPct: Math.max(0, Number(state.planning.weatherContingencyPct) || 0),
        baseTripDays: days.length,
        weatherContingencyDays: computeWeatherContingencyDays(days.length),
        adjustedTripDays: days.length + computeWeatherContingencyDays(days.length),
      },
      routing: {
        mode: routeMatrixStatus === "ready" && routeMatrixKey === scopeKey ? "routed" : "estimated",
        status: routeMatrixStatus,
        error: routeMatrixError,
        scopeKey,
      },
      routeScopeKey: geometryKey,
      travelMode: tripTravelMode,
    };
  }

  function snapshotMapView() {
    const center = map.getCenter?.();
    const bounds = map.getBounds?.();
    return {
      center: center ? { lat: Number(center.lat), lng: Number(center.lng) } : null,
      zoom: Number(map.getZoom?.() ?? state.defaultZoom),
      bearing: typeof map.getBearing === "function" ? Number(map.getBearing() || 0) : 0,
      bounds: bounds?.isValid?.()
        ? {
            west: Number(bounds.getWest()),
            south: Number(bounds.getSouth()),
            east: Number(bounds.getEast()),
            north: Number(bounds.getNorth()),
          }
        : null,
    };
  }

  function approximateMilesToLat(miles) {
    return miles / 69;
  }

  function approximateMilesToLng(miles, lat) {
    const cosLat = Math.max(0.2, Math.cos((Number(lat || 0) * Math.PI) / 180));
    return miles / (69 * cosLat);
  }

  function smartFrameBounds(item) {
    if (!item) return null;
    const baseMiles = 0.18;
    const searchMiles = 0.4;
    const latRadius = approximateMilesToLat(baseMiles);
    const lngRadius = approximateMilesToLng(baseMiles, item.lat);
    const nearby = state.items.filter((other) => {
      if (!other || other.id === item.id) return false;
      return milesBetween(item.lat, item.lng, other.lat, other.lng) <= searchMiles;
    });
    const points = [[Number(item.lat), Number(item.lng)], ...nearby.map((other) => [Number(other.lat), Number(other.lng)])];
    let bounds = L.latLngBounds(points);
    if (!bounds.isValid()) {
      bounds = L.latLngBounds(
        [Number(item.lat) - latRadius, Number(item.lng) - lngRadius],
        [Number(item.lat) + latRadius, Number(item.lng) + lngRadius],
      );
    }
    const south = Math.min(bounds.getSouth(), Number(item.lat) - latRadius);
    const north = Math.max(bounds.getNorth(), Number(item.lat) + latRadius);
    const west = Math.min(bounds.getWest(), Number(item.lng) - lngRadius);
    const east = Math.max(bounds.getEast(), Number(item.lng) + lngRadius);
    return L.latLngBounds([south, west], [north, east]).pad(0.16);
  }

  function applySmartAutoView(item) {
    const bounds = smartFrameBounds(item);
    if (!bounds?.isValid?.()) {
      suppressAutoSaveFor();
      map.setView([Number(item.lat), Number(item.lng)], Math.max(3, (Number(state.defaultZoom) || 18) - 1), { animate: false });
      return;
    }
    suppressAutoSaveFor();
    map.fitBounds(bounds, { animate: false, padding: [24, 24] });
    const fittedBounds = map.getBounds?.();
    const center = map.getCenter?.();
    const zoom = Number(map.getZoom?.() ?? state.defaultZoom);
    if (fittedBounds?.isValid?.() && center) {
      const latSpan = Math.max(0.0005, Number(fittedBounds.getNorth()) - Number(fittedBounds.getSouth()));
      const shiftedCenter = [Number(center.lat) + (latSpan * 0.12), Number(center.lng)];
      suppressAutoSaveFor();
      map.setView(shiftedCenter, zoom, { animate: false });
    }
  }

  function applySavedView(item) {
    if (!item) return;
    const view = item.view;
    if (view?.bounds) {
      const bounds = L.latLngBounds(
        [Number(view.bounds.south), Number(view.bounds.west)],
        [Number(view.bounds.north), Number(view.bounds.east)],
      );
      if (bounds.isValid()) {
        suppressAutoSaveFor();
        map.fitBounds(bounds, { animate: false });
      } else {
        suppressAutoSaveFor();
        map.setView([Number(item.lat), Number(item.lng)], Number(view.zoom) || state.defaultZoom, { animate: false });
      }
    } else if (view?.center && Number.isFinite(Number(view.center.lat)) && Number.isFinite(Number(view.center.lng))) {
      suppressAutoSaveFor();
      map.setView([Number(view.center.lat), Number(view.center.lng)], Number(view.zoom) || state.defaultZoom, { animate: false });
    } else {
      applySmartAutoView(item);
    }
    if (typeof map.setBearing === "function") {
      map.setBearing(Number(view?.bearing) || 0);
    }
  }

  function saveCurrentViewForActive() {
    if (!state.editMarinaViews) return;
    const item = currentItem();
    if (!item) return;
    item.view = snapshotMapView();
    persistLocalState();
    render();
  }

  function saveCurrentViewForActiveSilently() {
    if (!state.editMarinaViews) return;
    const item = currentItem();
    if (!item) return;
    item.view = snapshotMapView();
    persistLocalState();
  }

  function queueAutoSaveCurrentView() {
    if (exporting || suppressAutoSaveView || !state.editMarinaViews) return;
    if (Date.now() < suppressAutoSaveUntil) return;
    const item = currentItem();
    if (!item) return;
    window.clearTimeout(autoSaveViewTimer);
    autoSaveViewTimer = window.setTimeout(() => {
      if (suppressAutoSaveView || !state.editMarinaViews) {
        autoSaveViewTimer = 0;
        return;
      }
      saveCurrentViewForActiveSilently();
      autoSaveViewTimer = 0;
    }, 250);
  }

  function flattenItems(featureCollection) {
    const features = Array.isArray(featureCollection?.features) ? featureCollection.features : [];
    const items = [];
    features.forEach((feature, featureIndex) => {
      const geometry = feature?.geometry;
      const type = String(geometry?.type || "");
      const pushItem = (lng, lat, suffix = "") => {
        if (!Number.isFinite(Number(lat)) || !Number.isFinite(Number(lng))) return;
        const name = pointName(feature, featureIndex);
        items.push({
          id: `pt-${featureIndex + 1}${suffix}`,
          name: suffix ? `${name} ${suffix.replace(/^-/, "")}` : name,
          lat: Number(lat),
          lng: Number(lng),
          properties: { ...(feature?.properties || {}) },
          selected: true,
          view: null,
          coveredSlips: false,
          excludeFromReport: false,
          noFlyZone: false,
          prioritizeAirTravel: false,
        });
      };
      if (type === "Point") {
        const [lng, lat] = Array.isArray(geometry.coordinates) ? geometry.coordinates : [];
        pushItem(lng, lat);
      }
      if (type === "MultiPoint") {
        const coords = Array.isArray(geometry.coordinates) ? geometry.coordinates : [];
        coords.forEach((coord, pointIndex) => {
          const [lng, lat] = Array.isArray(coord) ? coord : [];
          pushItem(lng, lat, `-${pointIndex + 1}`);
        });
      }
    });
    return items;
  }

  function dedupeItems(items) {
    const seen = new Set();
    return items.filter((item) => {
      const key = `${Number(item.lat).toFixed(7)}|${Number(item.lng).toFixed(7)}|${String(item.name || "").trim().toLowerCase()}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  async function parseDatasetFile(file) {
    const name = String(file?.name || "dataset");
    const lower = name.toLowerCase();
    if (lower.endsWith(".geojson") || lower.endsWith(".json")) {
      const text = await file.text();
      return {
        datasetName: stripKnownSuffix(name),
        featureCollection: normalizeFeatureCollection(JSON.parse(text)),
      };
    }
    if (lower.endsWith(".kml")) {
      const text = await file.text();
      const dom = new DOMParser().parseFromString(text, "text/xml");
      return {
        datasetName: stripKnownSuffix(name),
        featureCollection: normalizeFeatureCollection(toGeoJSON.kml(dom)),
        kmlItems: extractItemsFromKmlDom(dom),
      };
    }
    if (lower.endsWith(".kmz")) {
      const buff = await file.arrayBuffer();
      const zip = await JSZip.loadAsync(buff);
      const kmlEntry = Object.values(zip.files).find((entry) => /\.kml$/i.test(entry.name));
      if (!kmlEntry) throw new Error("KMZ has no KML payload.");
      const kmlText = await kmlEntry.async("string");
      const dom = new DOMParser().parseFromString(kmlText, "text/xml");
      return {
        datasetName: stripKnownSuffix(name),
        featureCollection: normalizeFeatureCollection(toGeoJSON.kml(dom)),
        kmlItems: extractItemsFromKmlDom(dom),
      };
    }
    throw new Error(`Unsupported file: ${name}`);
  }

  async function parseDatasetUrl(url, name = DEFAULT_DATASET_NAME) {
    const response = await fetch(url, { headers: { Accept: "application/vnd.google-earth.kml+xml,text/xml,application/json" } });
    if (!response.ok) throw new Error(`Dataset HTTP ${response.status}`);
    const lower = String(name || url || "dataset").toLowerCase();
    if (lower.endsWith(".geojson") || lower.endsWith(".json")) {
      const text = await response.text();
      return {
        datasetName: stripKnownSuffix(name),
        featureCollection: normalizeFeatureCollection(JSON.parse(text)),
      };
    }
    if (lower.endsWith(".kml")) {
      const text = await response.text();
      const dom = new DOMParser().parseFromString(text, "text/xml");
      return {
        datasetName: stripKnownSuffix(name),
        featureCollection: normalizeFeatureCollection(toGeoJSON.kml(dom)),
        kmlItems: extractItemsFromKmlDom(dom),
      };
    }
    if (lower.endsWith(".kmz")) {
      const buff = await response.arrayBuffer();
      const zip = await JSZip.loadAsync(buff);
      const kmlEntry = Object.values(zip.files).find((entry) => /\.kml$/i.test(entry.name));
      if (!kmlEntry) throw new Error("KMZ has no KML payload.");
      const kmlText = await kmlEntry.async("string");
      const dom = new DOMParser().parseFromString(kmlText, "text/xml");
      return {
        datasetName: stripKnownSuffix(name),
        featureCollection: normalizeFeatureCollection(toGeoJSON.kml(dom)),
        kmlItems: extractItemsFromKmlDom(dom),
      };
    }
    throw new Error(`Unsupported dataset URL: ${name}`);
  }

  function columnLabelToIndex(label) {
    let value = 0;
    for (let i = 0; i < label.length; i += 1) value = (value * 26) + (label.charCodeAt(i) - 64);
    return value - 1;
  }

  function parseWorksheetRows(xmlText, sharedStrings) {
    const ns = "http://schemas.openxmlformats.org/spreadsheetml/2006/main";
    const dom = new DOMParser().parseFromString(xmlText, "text/xml");
    const rows = Array.from(dom.getElementsByTagNameNS(ns, "row"));
    return rows.map((rowEl) => {
      const row = [];
      Array.from(rowEl.getElementsByTagNameNS(ns, "c")).forEach((cellEl) => {
        const ref = String(cellEl.getAttribute("r") || "");
        const match = ref.match(/[A-Z]+/);
        const colIndex = match ? columnLabelToIndex(match[0]) : row.length;
        const type = String(cellEl.getAttribute("t") || "");
        let value = "";
        if (type === "inlineStr") {
          value = Array.from(cellEl.getElementsByTagNameNS(ns, "t")).map((node) => node.textContent || "").join("");
        } else {
          const vEl = cellEl.getElementsByTagNameNS(ns, "v")[0];
          value = vEl?.textContent ?? "";
          if (type === "s" && value !== "") value = sharedStrings[Number(value)] ?? value;
        }
        row[colIndex] = String(value ?? "");
      });
      return row.map((value) => String(value ?? ""));
    });
  }

  function rowsToObjects(rows, headerRowIndex) {
    const headerRow = Array.isArray(rows[headerRowIndex]) ? rows[headerRowIndex] : [];
    const headers = headerRow.map((value, index) => {
      const clean = String(value || "").replace(/\s+/g, " ").trim();
      return clean || `Column ${index + 1}`;
    });
    const out = [];
    rows.slice(headerRowIndex + 1).forEach((row) => {
      const values = Array.isArray(row) ? row : [];
      if (!values.some((value) => String(value || "").trim())) return;
      const obj = {};
      headers.forEach((header, index) => {
        obj[header] = String(values[index] ?? "").trim();
      });
      out.push(obj);
    });
    return out;
  }

  async function parsePortfolioWorkbookUrl(url, name = DEFAULT_PORTFOLIO_NAME) {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Workbook HTTP ${response.status}`);
    const archive = await JSZip.loadAsync(await response.arrayBuffer());
    const workbookXml = await archive.file("xl/workbook.xml")?.async("string");
    const relsXml = await archive.file("xl/_rels/workbook.xml.rels")?.async("string");
    if (!workbookXml || !relsXml) throw new Error("Workbook structure is invalid.");
    const ns = {
      a: "http://schemas.openxmlformats.org/spreadsheetml/2006/main",
      r: "http://schemas.openxmlformats.org/officeDocument/2006/relationships",
      rel: "http://schemas.openxmlformats.org/package/2006/relationships",
    };
    const workbookDom = new DOMParser().parseFromString(workbookXml, "text/xml");
    const relsDom = new DOMParser().parseFromString(relsXml, "text/xml");
    const relMap = new Map(
      Array.from(relsDom.getElementsByTagNameNS(ns.rel, "Relationship")).map((relEl) => [
        String(relEl.getAttribute("Id") || ""),
        String(relEl.getAttribute("Target") || ""),
      ]),
    );
    const sharedStringsXml = await archive.file("xl/sharedStrings.xml")?.async("string");
    const sharedStrings = sharedStringsXml
      ? Array.from(new DOMParser().parseFromString(sharedStringsXml, "text/xml").getElementsByTagNameNS(ns.a, "si")).map((siEl) => (
          Array.from(siEl.getElementsByTagNameNS(ns.a, "t")).map((tEl) => tEl.textContent || "").join("")
        ))
      : [];
    const sheetNodes = Array.from(workbookDom.getElementsByTagNameNS(ns.a, "sheet"));
    const sheets = {};
    for (const sheetEl of sheetNodes) {
      const sheetName = String(sheetEl.getAttribute("name") || "");
      const rid = String(sheetEl.getAttributeNS(ns.r, "id") || sheetEl.getAttribute("r:id") || "");
      const target = relMap.get(rid);
      if (!sheetName || !target) continue;
      const normalizedTarget = target.startsWith("xl/") ? target : `xl/${target.replace(/^\//, "")}`;
      const sheetXml = await archive.file(normalizedTarget)?.async("string");
      if (!sheetXml) continue;
      sheets[sheetName] = parseWorksheetRows(sheetXml, sharedStrings);
    }
    return {
      workbookName: stripKnownSpreadsheetSuffix(name),
      detailRows: rowsToObjects(sheets["Property Detail"] || [], 3).filter((row) => row["Property (1)"] && !/^subtotal|total$/i.test(row["Property (1)"])),
      profileRows: rowsToObjects(sheets["Property Profile"] || [], 1).filter((row) => row["Marina"]),
      countRows: rowsToObjects(sheets["Marina Count"] || [], 0).filter((row) => row["Property Name"] && !/^\d+$/.test(row["Property Name"])),
    };
  }

  function syncPortfolioToItems(portfolio) {
    if (!portfolio || !state.items.length) return { matched: 0, total: state.items.length };
    const detailMap = new Map();
    const profileMap = new Map();
    const countMap = new Map();
    portfolio.detailRows.forEach((row) => detailMap.set(normalizeMarinaKey(row["Property (1)"]), row));
    portfolio.profileRows.forEach((row) => profileMap.set(normalizeMarinaKey(row["Marina"]), row));
    portfolio.countRows.forEach((row) => countMap.set(normalizeMarinaKey(row["Property Name"]), row));
    let matched = 0;
    state.items.forEach((item) => {
      const key = normalizeMarinaKey(item.name);
      const detail = detailMap.get(key) || null;
      const profile = profileMap.get(key) || null;
      const counts = countMap.get(key) || null;
      const propertyCount = pickNumericField(counts, [
        "Property Count",
        "Property Counts",
        "Count",
        "Counts",
        "Properties",
        "Property",
      ]);
      const primaryName = detail?.["Property (1)"] || profile?.["Marina"] || counts?.["Property Name"] || "";
      const propCity = String(item.properties?.city || item.properties?.City || "").trim();
      const propState = String(item.properties?.state || item.properties?.State || "").trim();
      item.portfolio = (detail || profile || counts)
        ? {
            matchedName: primaryName,
            code: detail?.Code || "",
            city: detail?.["City "] || propCity || profile?.["City, State"] || "",
            stateCountry: detail?.["State/Country"] || propState || "",
            archetype: detail?.Archetype || "",
            wetSlips: detail?.["Wet Slips"] || counts?.Slips || profile?.["Wet Slips"] || "",
            dryStorage: detail?.["Dry Rack Storage(10)"] || counts?.Storage || profile?.["Dry Storage"] || "",
            landStorage: detail?.["Approximate Land Storage"] || "",
            coreAttributes: profile?.["Core Attributes"] || "",
            amenities: profile?.Amenities || "",
            fuelDock: profile?.["Fuel Dock"] || "",
            watersideRestaurant: profile?.["Waterside Restaurant"] || "",
            propertyCount,
          }
        : null;
      if (item.portfolio) matched += 1;
    });
    return { matched, total: state.items.length };
  }

  function pickNumericField(row, candidateKeys = []) {
    if (!row || typeof row !== "object") return null;
    for (const key of candidateKeys) {
      if (!(key in row)) continue;
      const raw = String(row[key] ?? "").trim();
      if (!raw) continue;
      const value = Number.parseFloat(raw.replace(/[^0-9.\-]+/g, ""));
      if (Number.isFinite(value)) return value;
    }
    return null;
  }

  function filterZeroPropertyCountItems() {
    if (!Array.isArray(state.items) || !state.items.length) return 0;
    const activeId = currentItem()?.id || "";
    const kept = state.items.filter((item) => {
      const count = item?.portfolio?.propertyCount;
      return !(Number.isFinite(count) && count <= 0);
    });
    const removed = state.items.length - kept.length;
    if (!removed) return 0;
    state.items = kept;
    const nextActiveIndex = kept.findIndex((item) => item.id === activeId);
    state.activeIndex = nextActiveIndex >= 0
      ? nextActiveIndex
      : (kept.length ? Math.max(0, Math.min(state.activeIndex, kept.length - 1)) : -1);
    return removed;
  }

  async function applyParsedDataset(parsed, sourceName) {
    const normalized = app.services.io.normalizeImportFeatureCollection(parsed.featureCollection, {
      sourceCRS: null,
      targetCRS: "EPSG:4326",
    });
    const fromGeoJson = flattenItems(normalized.featureCollection);
    const fromKmlDom = Array.isArray(parsed.kmlItems) ? parsed.kmlItems : [];
    const items = dedupeItems(fromKmlDom.length ? [...fromKmlDom, ...fromGeoJson] : fromGeoJson).map((item) => normalizeItem(item));
    if (!items.length) throw new Error("No point features were found in the dataset.");
    state = {
      ...state,
      datasetName: parsed.datasetName || stripKnownSuffix(sourceName),
      prefix: sanitizeFilePart(parsed.datasetName || stripKnownSuffix(sourceName) || "marina", "marina"),
      items,
      activeIndex: items.length ? 0 : -1,
    };
    recomputePlanning();
    if (portfolioWorkbook) {
      syncPortfolioToItems(portfolioWorkbook);
      filterZeroPropertyCountItems();
      recomputePlanning();
    }
    if (!String(state.projectSettings.selectedProjectId || "").trim() && items.length) {
      const first = items[0];
      state.projectSettings.selectedProjectId = String(first?.id || first?.portfolio?.code || first?.name || "").trim();
    }
    render();
    setStatus(`Loaded ${items.length} point${items.length === 1 ? "" : "s"} from ${sourceName}.`);
    app.ui.toast(`Safe Harbor Marinas Toolset loaded ${items.length} point${items.length === 1 ? "" : "s"}.`);
    await focusItem(0, { saveCurrent: false });
  }

  function delay(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  function hasPendingTileWork() {
    try {
      const tileLayers = [];
      map.eachLayer((layer) => {
        if (layer?._loading) tileLayers.push(layer);
      });
      if (tileLayers.length) return true;
      const imgs = map.getContainer()?.querySelectorAll?.(".leaflet-tile-container img.leaflet-tile, .leaflet-overlay-pane img");
      for (let i = 0; i < (imgs?.length || 0); i += 1) {
        const img = imgs[i];
        if (!img.complete) return true;
      }
    } catch {}
    return false;
  }

  async function waitForMapToSettle({ timeoutMs = 9000, stableMs = 450, pollMs = 90 } = {}) {
    const started = Date.now();
    let stableSince = 0;
    while ((Date.now() - started) < timeoutMs) {
      const pending = hasPendingTileWork();
      if (!pending) {
        if (!stableSince) stableSince = Date.now();
        if ((Date.now() - stableSince) >= stableMs) return true;
      } else {
        stableSince = 0;
      }
      await delay(pollMs);
    }
    return false;
  }

  function ensureCaptureLayer() {
    if (!captureLayer) captureLayer = L.layerGroup().addTo(map);
    return captureLayer;
  }

  function updateLegend(items = []) {
    // Remove any existing legend element
    const old = [
      legendControlEl,
      map.getContainer?.()?.parentElement?.querySelector?.(".imggen-map-legend"),
      map.getContainer?.()?.querySelector?.(".imggen-map-legend"),
    ].filter(Boolean);
    old.forEach((el) => el.remove?.());
    legendControlEl = null;

    if (!state.portfolioShowMapLegend || !items.length) return;

    const el = document.createElement("div");
    el.className = "imggen-map-legend";
    el.style.cssText = [
      "position:absolute","bottom:10px","left:10px","z-index:1000",
      "background:rgba(15,23,42,0.88)","padding:8px 10px","border-radius:6px",
      "border:1px solid rgba(148,163,184,0.2)","pointer-events:none",
      "max-height:220px","overflow-y:auto",
    ].join(";");
    el.innerHTML = items.map(({ label, color }) =>
      `<div style="display:flex;align-items:center;gap:5px;font:500 10px/1.4 system-ui,sans-serif;color:#e2e8f0;margin:2px 0;white-space:nowrap;">` +
      `<span style="width:9px;height:9px;border-radius:50%;background:${color};flex-shrink:0;"></span>` +
      `${esc(label)}</div>`
    ).join("");
    map.getContainer?.()?.appendChild(el);
    legendControlEl = el;
  }

  function drawPlanningOverlay(item, { items = null, routeScopeKey: routeScopeKeyOverride = "", tripPlan = null, tripDayIndex = -1 } = {}) {
    const layer = ensureCaptureLayer();
    layer.clearLayers();
    const officeColors = selectedOfficeColorMap();
    const labelOffsetMap = new Map();
    const marinaGroups = [];
    const screenThreshold = 28;
    const effectiveTripDay = Array.isArray(tripPlan?.days) && tripDayIndex >= 0 ? tripPlan.days[tripDayIndex] || null : null;
    const explicitItems = Array.isArray(items) ? items : null;
    const visibleOfficeFilter = explicitItems ? [] : state.visibleOfficeMarinaIds;
    const overlayItems = explicitItems || (
      visibleOfficeFilter.length
        ? state.items.filter((marina) => visibleOfficeFilter.includes(marina.officeId) || (!!item && marina.id === item.id))
        : state.items
    );
    const effectiveRouteScopeKey = String(routeScopeKeyOverride || currentTripRouteScopeKey());
    if ((state.showTripRoute || effectiveTripDay) && routeGeometryKey === effectiveRouteScopeKey && routeGeometryFeatures.length) {
      routeGeometryFeatures.forEach((feature) => {
        if (effectiveTripDay && !state.showTripRoute && Number(feature.day) !== Number(effectiveTripDay.day)) return;
        L.polyline(feature.latlngs, {
          color: routeColorForDay(feature.day),
          weight: feature.segment === "return" ? 1.25 : 1.75,
          opacity: state.showTripRoute ? (feature.source === "air" ? 0.8 : 0.72) : 0.18,
          dashArray: feature.source === "air"
            ? "10 6"
            : feature.geometrySource === "estimated"
              ? "6 6"
              : null,
          pane: "overlayPane",
        }).addTo(layer);
      });
    }
    overlayItems.forEach((marina) => {
      const point = map.latLngToContainerPoint([Number(marina.lat), Number(marina.lng)]);
      let group = marinaGroups.find((entry) => entry.points.some((other) => other.point.distanceTo(point) <= screenThreshold));
      if (!group) {
        group = { points: [] };
        marinaGroups.push(group);
      }
      group.points.push({ marina, point });
    });
    marinaGroups.forEach((group) => {
      const count = group.points.length;
      group.points.forEach(({ marina }, index) => {
        if (count <= 1) {
          labelOffsetMap.set(marina.id, { x: 10, y: -14 });
          return;
        }
        const angle = (-Math.PI / 2) + ((Math.PI * 2 * index) / count);
        const radius = count <= 4 ? 18 : 24;
        labelOffsetMap.set(marina.id, {
          x: Math.round(Math.cos(angle) * radius),
          y: Math.round(Math.sin(angle) * radius),
        });
      });
    });
    overlayItems.forEach((marina) => {
      const isActive = !!item && marina.id === item.id && state.marker;
      const officeColor = marina.officeId ? officeColors.get(marina.officeId) : null;
      const fillColor = isActive ? "#ef4444" : (officeColor?.fill || "#60a5fa");
      const strokeColor = isActive ? "#ffffff" : (officeColor?.stroke || "#dbeafe");
      const codeLabel = String(marina?.portfolio?.code || "").trim();
      const labelOffset = labelOffsetMap.get(marina.id) || { x: 10, y: -14 };
      if (state.showMarinaPoints) {
        L.circleMarker([Number(marina.lat), Number(marina.lng)], {
          radius: isActive ? 7 : 4,
          color: strokeColor,
          weight: isActive ? 2 : 1.5,
          fillColor,
          fillOpacity: isActive ? 0.95 : 0.78,
          pane: "overlayPane",
        }).addTo(layer);
      }
      if (codeLabel && state.showMarinaCodes) {
        L.marker([Number(marina.lat), Number(marina.lng)], {
          interactive: false,
          keyboard: false,
          icon: L.divIcon({
            className: "imggen-code-label",
            html: `<div style="background:${isActive ? "rgba(127,29,29,0.92)" : "rgba(15,23,42,0.82)"}; color:#fff; padding:2px 6px; border-radius:999px; font:700 10px/1.1 system-ui,sans-serif; white-space:nowrap; border:1px solid ${isActive ? "#fca5a5" : "rgba(255,255,255,0.18)"}; box-shadow:0 3px 10px rgba(15,23,42,0.25);">${esc(codeLabel)}</div>`,
            iconAnchor: [-labelOffset.x, -labelOffset.y],
          }),
        }).addTo(layer);
      }
      if (state.showMarinaPoints && isActive && state.marker) {
        L.circleMarker([Number(marina.lat), Number(marina.lng)], {
          radius: 16,
          color: "#ef4444",
          weight: 2,
          fill: false,
          opacity: 0.9,
          pane: "overlayPane",
        }).addTo(layer);
      }
    });
    // Portfolio map dots — all catalog entries with lat/lng, colored by selected mode
    // Build a set of codes already drawn by the planning overlay so we never render two dots for the same marina.
    const overlayItemCodes = state.showMarinaPoints
      ? new Set(overlayItems.map((m) => String(m?.portfolio?.code || m?.id || "").toUpperCase()).filter(Boolean))
      : new Set();
    const pmp = state.portfolioMapPoints || {};
    if (pmp.active || pmp.potential || pmp.archived) {
      // Use catalog.marinas as the authoritative source — it is already scoped to the
      // selected client and contains new projects immediately after creation, unlike
      // the legacy portfolioPoints endpoint which has no clientId field.
      const selectedClientId = state.catalog?.selectedClientId || "";
      const mapDots = (state.catalog.marinas || []).filter(m => {
        if (!Number.isFinite(m.lat) || !Number.isFinite(m.lng)) return false;
        if (selectedClientId && String(m.clientId || m.clientSlug || "safeharbor") !== selectedClientId) return false;
        return true;
      });

      if (mapDots.length) {
        const STATUS_STYLE = {
          active:   { fill: "#22c55e", stroke: "#166534", radius: 3 },
          potential: { fill: "#94a3b8", stroke: "#334155", radius: 2 },
          archived:  { fill: "#38bdf8", stroke: "#0c4a6e", radius: 2 },
        };
        // Shared palette for color-by-state and color-by-group (hue-spaced, legible on dark map)
        const MULTI_PALETTE = [
          "#f87171","#fb923c","#fbbf24","#a3e635","#34d399","#22d3ee",
          "#60a5fa","#a78bfa","#f472b6","#f43f5e","#84cc16","#10b981",
          "#06b6d4","#3b82f6","#8b5cf6","#ec4899","#14b8a6","#6366f1",
          "#d946ef","#f59e0b","#ef4444","#22c55e","#0ea5e9","#7c3aed",
        ];
        const stateKeys = [...new Set(mapDots.map(p => p.state).filter(Boolean))].sort();
        const stateColorMap = new Map(stateKeys.map((s, i) => [s, MULTI_PALETTE[i % MULTI_PALETTE.length]]));
        const groupKeys = [...new Set(mapDots.map(p => p.group).filter(Boolean))].sort();
        const groupColorMap = new Map(groupKeys.map((g, i) => {
          const meta = getGroupMeta(selectedClientId, g);
          return [g, meta.color || MULTI_PALETTE[(i + 4) % MULTI_PALETTE.length]];
        }));
        const groupVisibleMap = new Map(groupKeys.map(g => [g, getGroupMeta(selectedClientId, g).visible !== false]));

        const colorBy    = state.portfolioColorBy  || "status";
        const colorStep  = state.portfolioColorStep || "";
        const MILESTONE_COLORS = {
          "Not Started": "#475569", "In Progress": "#f59e0b", "Complete": "#22c55e",
          "On Hold": "#f97316",    "N/A": "#64748b",
        };

        mapDots.forEach((proj) => {
          if (overlayItemCodes.has(String(proj.code || "").toUpperCase())) return;
          const grp = proj.archivedAt ? "archived" : (proj.active ? "active" : "potential");
          if (!pmp[grp]) return;
          if (proj.group && groupVisibleMap.get(proj.group) === false) return;

          let fill, stroke, radius;
          if (colorBy === "state") {
            fill   = stateColorMap.get(proj.state) || "#94a3b8";
            stroke = "#0f172a";
            radius = 3;
          } else if (colorBy === "group") {
            fill   = proj.group ? (groupColorMap.get(proj.group) || "#94a3b8") : "#475569";
            stroke = "#0f172a";
            radius = 3;
          } else if (colorBy === "milestone" && colorStep) {
            const val  = (proj.statusColumns || {})[colorStep] || "Not Started";
            fill   = MILESTONE_COLORS[val] || "#64748b";
            stroke = "#0f172a";
            radius = 3;
          } else {
            const s = STATUS_STYLE[grp];
            fill = s.fill; stroke = s.stroke; radius = s.radius;
          }

          const tooltip = `<strong>${proj.code}</strong> — ${proj.name}${proj.group ? `<br><span style="opacity:0.75">${proj.group}</span>` : ""}${proj.city ? `<br>${proj.city}, ${proj.state}` : ""}`;
          L.circleMarker([proj.lat, proj.lng], {
            radius, color: stroke, weight: 1, fillColor: fill, fillOpacity: 0.88, pane: "overlayPane",
          }).bindTooltip(tooltip, { direction: "top", offset: [0, -6] }).addTo(layer);
        });

        // Build map legend items for the floating overlay
        let legendItems = [];
        if (colorBy === "state") {
          legendItems = stateKeys.map(s => ({ label: s, color: stateColorMap.get(s) }));
        } else if (colorBy === "group") {
          legendItems = groupKeys.map(g => ({ label: g, color: groupColorMap.get(g) }));
          if (mapDots.some(p => !p.group && pmp[p.archivedAt ? "archived" : (p.active ? "active" : "potential")])) {
            legendItems.push({ label: "Unassigned", color: "#475569" });
          }
        } else if (colorBy === "milestone" && colorStep) {
          legendItems = Object.entries(MILESTONE_COLORS).map(([val, col]) => ({ label: val, color: col }));
        } else {
          if (pmp.active)    legendItems.push({ label: "Active",    color: "#22c55e" });
          if (pmp.potential) legendItems.push({ label: "Potential", color: "#94a3b8" });
          if (pmp.archived)  legendItems.push({ label: "Archived",  color: "#38bdf8" });
        }
        updateLegend(legendItems);
      } else {
        updateLegend([]);
      }
    } else {
      updateLegend([]);
    }
    if (state.showOfficeMarkers) state.offices.forEach((office) => {
      const officeColor = officeColors.get(office.id);
      L.marker([Number(office.lat), Number(office.lng)], {
        interactive: false,
        keyboard: false,
        icon: L.divIcon({
          className: "imggen-office-marker",
          html: `
            <div style="display:flex; align-items:center; gap:6px;">
              <span style="width:12px; height:12px; border-radius:2px; background:${officeColor?.fill || "#22c55e"}; border:2px solid ${officeColor?.stroke || "#dcfce7"}; display:inline-block; box-shadow:0 2px 8px rgba(0,0,0,0.35);"></span>
              <span style="background:rgba(15,23,42,0.88); color:${officeColor?.text || "#dcfce7"}; padding:3px 7px; border-radius:999px; font:600 11px/1.2 system-ui,sans-serif; white-space:nowrap; border:1px solid ${officeColor?.fill || "rgba(34,197,94,0.4)"};">${esc(office.name)}</span>
            </div>
          `,
          iconAnchor: [6, 6],
        }),
      }).addTo(layer);
    });
  }

  async function captureCurrentMap({ includeMetadata = false } = {}) {
    const html2canvas = (await import("html2canvas")).default;
    const mapContainer = map.getContainer();

    // html2canvas doesn't reliably composite CSS opacity on <canvas> tile elements
    // (e.g. GeoRasterLayer). Pre-bake the opacity into each tile canvas's pixel alpha
    // and set the container opacity to 1 so html2canvas sees the correct result
    // without double-applying the opacity.
    const restores = [];
    mapContainer.querySelectorAll(".leaflet-layer, .leaflet-tile-container").forEach((container) => {
      const opacity = parseFloat(window.getComputedStyle(container).opacity);
      if (!Number.isFinite(opacity) || opacity >= 0.999) return;
      const canvases = Array.from(container.querySelectorAll("canvas"));
      if (!canvases.length) return;
      const originalOpacityStyle = container.style.opacity;
      canvases.forEach((cvs) => {
        try {
          const ctx2d = cvs.getContext("2d");
          if (!ctx2d) return;
          const original = ctx2d.getImageData(0, 0, cvs.width, cvs.height);
          const baked = new ImageData(new Uint8ClampedArray(original.data), original.width, original.height);
          for (let i = 3; i < baked.data.length; i += 4) {
            baked.data[i] = Math.round(baked.data[i] * opacity);
          }
          ctx2d.putImageData(baked, 0, 0);
          restores.push(() => ctx2d.putImageData(original, 0, 0));
        } catch {}
      });
      container.style.opacity = "1";
      restores.push(() => { container.style.opacity = originalOpacityStyle; });
    });

    try {
      const canvas = await html2canvas(mapContainer, {
        backgroundColor: "#ffffff",
        useCORS: true,
        logging: false,
        scale: Math.max(1, Math.min(4, Number(state.scale) || 2)),
        ignoreElements(element) {
          const classes = String(element?.className || "");
          if (classes.includes("leaflet-control")) return true;
          if (classes.includes("map-measure-control")) return true;
          if (classes.includes("map-search-control")) return true;
          if (classes.includes("map-rotate-control")) return true;
          if (classes.includes("map-mapillary-control")) return true;
          if (classes.includes("map-mapillary-panel")) return true;
          // map-control-stack (theme/capture/measure/search/rotate/mapillary buttons),
          // map-north-arrow and map-status-tray (bottom zoom/bearing readout) are custom
          // floating controls appended directly to the Leaflet map container -- not part of
          // Leaflet's own control system, so "leaflet-control" above never matches them. Left
          // unfiltered, they showed up baked into every captured report map image.
          if (classes.includes("map-control-stack")) return true;
          if (classes.includes("map-north-arrow")) return true;
          if (classes.includes("map-status-tray")) return true;
          return false;
        },
      });
      const dataUrl = state.format === "png"
        ? canvas.toDataURL("image/png")
        : canvas.toDataURL("image/jpeg", 0.92);
      if (!includeMetadata) return dataUrl;
      return {
        dataUrl,
        width: Number(canvas.width) || 0,
        height: Number(canvas.height) || 0,
      };
    } finally {
      restores.forEach((fn) => fn());
    }
  }

  async function withStableReportViewport(task) {
    const docEl = document.documentElement;
    const previousInlineRightPanelWidth = docEl.style.getPropertyValue("--rightPanelW");
    const computedRightPanelWidth = getComputedStyle(docEl).getPropertyValue("--rightPanelW").trim();
    const needsResize = computedRightPanelWidth !== REPORT_CAPTURE_RIGHT_PANEL_WIDTH;
    try {
      if (needsResize) {
        docEl.style.setProperty("--rightPanelW", REPORT_CAPTURE_RIGHT_PANEL_WIDTH);
        map.invalidateSize(false);
        await delay(120);
        await waitForMapToSettle({ timeoutMs: 12000, stableMs: 700, pollMs: 100 });
      }
      return await task();
    } finally {
      if (needsResize) {
        if (previousInlineRightPanelWidth) docEl.style.setProperty("--rightPanelW", previousInlineRightPanelWidth);
        else docEl.style.removeProperty("--rightPanelW");
        map.invalidateSize(false);
        await delay(120);
        await waitForMapToSettle({ timeoutMs: 12000, stableMs: 700, pollMs: 100 });
      }
    }
  }

  async function captureReportMarinaImages(items, { onProgress = null } = {}) {
    const captures = [];
    const total = items.length;
    setCaptureProgress(0, total);
    for (let index = 0; index < total; index += 1) {
      const marina = items[index];
      const stateIndex = state.items.findIndex((entry) => entry.id === marina.id);
      const preparingMessage = `Screenshots ${captures.length}/${total} | Preparing ${index + 1}/${total}: ${marina.name}`;
      setStatus(preparingMessage);
      if (typeof onProgress === "function") onProgress(preparingMessage);
      if (stateIndex >= 0) {
        await focusItem(stateIndex, { saveCurrent: false, overlayItems: items });
        await warmVisibleLayersForCapture({ extraDelayMs: 350 });
      } else {
        applySavedView(marina);
        map.invalidateSize(false);
        await delay(100);
        drawPlanningOverlay(marina, { items });
        await waitForMapToSettle();
        await warmVisibleLayersForCapture({ extraDelayMs: 350 });
      }
      const capturingMessage = `Screenshots ${captures.length}/${total} | Capturing ${index + 1}/${total}: ${marina.name}`;
      setStatus(capturingMessage);
      if (typeof onProgress === "function") onProgress(capturingMessage);
      const dataUrl = await captureCurrentMap();
      captures.push({
        id: marina.id,
        name: marina.name,
        code: String(marina?.portfolio?.code || marina.name || "").trim(),
        dataUrl,
      });
      setCaptureProgress(captures.length, total);
      const capturedMessage = `Screenshots ${captures.length}/${total} captured`;
      setStatus(capturedMessage);
      if (typeof onProgress === "function") onProgress(capturedMessage);
    }
    return captures;
  }

  function findEsriImageryLayerIds() {
    return app.services.layers.listLayers()
      .filter((layer) => layer?.toolId === "overlay-manager" && layer?.kind === "basemap" && String(layer?.id || "").includes("esri-imagery"))
      .map((layer) => String(layer.id));
  }

  function setLayerVisibility(ids, visible) {
    ids.forEach((id) => {
      try { app.services.layers.setVisible(id, !!visible); } catch {}
    });
  }

  function captureLayerVisibility(ids) {
    const current = new Map();
    const layers = app.services.layers.listLayers();
    ids.forEach((id) => {
      const layer = layers.find((entry) => entry.id === id);
      current.set(id, !!layer?.visible);
    });
    return current;
  }

  function restoreLayerVisibility(snapshot) {
    snapshot.forEach((visible, id) => {
      try { app.services.layers.setVisible(id, !!visible); } catch {}
    });
  }

  async function warmVisibleLayersForCapture({ extraDelayMs = 600 } = {}) {
    try { map.invalidateSize({ pan: false, debounceMoveend: false }); } catch {}
    await delay(180);
    await waitForMapToSettle({ timeoutMs: 14000, stableMs: 900, pollMs: 120 });
    if (extraDelayMs > 0) await delay(extraDelayMs);
  }

  function reportEsc(value) {
    return String(value ?? "").replace(/[&<>"']/g, (ch) => ({
      "&": "&amp;",
      "<": "&lt;",
      ">": "&gt;",
      '"': "&quot;",
      "'": "&#39;",
    }[ch]));
  }

  function reclaimHydrotoolsFocus() {
    const focusMain = () => {
      try { window.focus?.(); } catch (_) {}
    };
    focusMain();
    try { setTimeout(focusMain, 0); } catch (_) {}
    try { setTimeout(focusMain, 60); } catch (_) {}
    try { setTimeout(focusMain, 180); } catch (_) {}
    try { requestAnimationFrame(focusMain); } catch (_) {}
  }

  function excelCell(value) {
    const text = String(value ?? "");
    return text
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;");
  }

  function downloadBlob(blob, fileName) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      URL.revokeObjectURL(url);
      a.remove();
    }, 300);
  }

  async function waitForReportDocumentReady(doc) {
    if (!doc) return;
    if (doc.fonts?.ready) {
      try { await doc.fonts.ready; } catch (_) {}
    }
    const images = Array.from(doc.images || []);
    await Promise.all(images.map((img) => {
      if (img.complete) return Promise.resolve();
      return new Promise((resolve) => {
        const done = () => resolve();
        img.addEventListener("load", done, { once: true });
        img.addEventListener("error", done, { once: true });
      });
    }));
    await delay(120);
  }

  const REPORT_FIRST_PAGE_LAYOUT = Object.freeze({
    summaryColumns: "minmax(320px, 0.95fr) minmax(0, 2.05fr)",
    summaryGap: "18px",
    metaMinHeight: "96px",
    metaMarginBottom: "16px",
    // Purely cosmetic alignment offset for the map column -- was eating into the vertical
    // budget of a page whose left column (meta + two estimate blocks) already runs close to
    // a full page on its own. See tripReportHtml's clampedMapRatio comment for the other half
    // of the first-page-overflow fix.
    mapPanePaddingTop: "6px",
    mapTitleFontSize: "24px",
    mapTitleMarginBottom: "12px",
    printSummaryColumns: "minmax(82mm, 1fr) minmax(0, 2fr)",
    printSummaryGap: "12px",
    printMetaMarginBottom: "12px",
    printMapTitleFontSize: "18px",
    printMapTitleMarginBottom: "8px",
  });

  function reportFirstPageLayoutCss({ important = false } = {}) {
    const bang = important ? " !important" : "";
    return `
      .summary-layout { display:grid${bang}; grid-template-columns:${REPORT_FIRST_PAGE_LAYOUT.summaryColumns}${bang}; gap:${REPORT_FIRST_PAGE_LAYOUT.summaryGap}${bang}; align-items:start${bang}; }
      .summary-pane, .map-pane { min-width:0${bang}; }
      .meta { min-height:${REPORT_FIRST_PAGE_LAYOUT.metaMinHeight}${bang}; margin-bottom:${REPORT_FIRST_PAGE_LAYOUT.metaMarginBottom}${bang}; }
      .map-pane { padding-top:${REPORT_FIRST_PAGE_LAYOUT.mapPanePaddingTop}${bang}; }
      .map-wrap { display:flex${bang}; flex-direction:column${bang}; }
      .map-pane-title { text-align:center${bang}; font-size:${REPORT_FIRST_PAGE_LAYOUT.mapTitleFontSize}${bang}; line-height:1.15${bang}; margin:0 0 ${REPORT_FIRST_PAGE_LAYOUT.mapTitleMarginBottom}${bang}; color:var(--accent-strong)${bang}; letter-spacing:0.02em${bang}; }
      .map-frame { position:relative${bang}; display:flex${bang}; align-items:center${bang}; justify-content:center${bang}; overflow:hidden${bang}; aspect-ratio:var(--mapAspectRatio)${bang}; min-height:0${bang}; }
      .map-wrap img { max-width:100%${bang}; max-height:100%${bang}; margin:0 auto${bang}; display:block${bang}; }
    `;
  }

  function reportFirstPagePrintLayoutCss({ important = false } = {}) {
    const bang = important ? " !important" : "";
    return `
      .summary-layout { grid-template-columns:${REPORT_FIRST_PAGE_LAYOUT.printSummaryColumns}${bang}; gap:${REPORT_FIRST_PAGE_LAYOUT.printSummaryGap}${bang}; align-items:start${bang}; }
      .meta { min-height:${REPORT_FIRST_PAGE_LAYOUT.metaMinHeight}${bang}; margin-bottom:${REPORT_FIRST_PAGE_LAYOUT.printMetaMarginBottom}${bang}; }
      .map-pane { padding-top:${REPORT_FIRST_PAGE_LAYOUT.mapPanePaddingTop}${bang}; }
      .map-pane, .map-wrap, .map-frame { height:auto${bang}; }
      .map-frame { min-height:0${bang}; aspect-ratio:var(--mapAspectRatio)${bang}; }
      .map-pane-title { font-size:${REPORT_FIRST_PAGE_LAYOUT.printMapTitleFontSize}${bang}; margin:0 0 ${REPORT_FIRST_PAGE_LAYOUT.printMapTitleMarginBottom}${bang}; }
      .map-wrap img { max-width:100%${bang}; max-height:100%${bang}; margin:0 auto${bang}; display:block${bang}; }
    `;
  }

  async function renderReportHtmlToCanvases(report, { statusLabel = "", onProgress = null } = {}) {
    const html2canvas = (await import("html2canvas")).default;
    const iframe = document.createElement("iframe");
    iframe.setAttribute("aria-hidden", "true");
    iframe.style.position = "fixed";
    iframe.style.left = "-20000px";
    iframe.style.top = "0";
    iframe.style.width = "1400px";
    iframe.style.height = "2200px";
    iframe.style.opacity = "0";
    iframe.style.pointerEvents = "none";
    iframe.style.border = "0";
    document.body.appendChild(iframe);
    try {
      const doc = iframe.contentDocument;
      if (!doc) throw new Error("Unable to create report document.");
      doc.open();
      doc.write(report.html);
      doc.close();
      const exportStyle = doc.createElement("style");
      exportStyle.textContent = `
        .no-print { display: none !important; }
        .page { min-height: auto !important; padding: 0 !important; }
        ${reportFirstPageLayoutCss({ important: true }).replaceAll("var(--accent-strong)", "#0f2d52")}
      `;
      doc.head.appendChild(exportStyle);
      await new Promise((resolve) => {
        const win = iframe.contentWindow;
        if (!win) return resolve();
        if (doc.readyState === "complete") return resolve();
        win.addEventListener("load", () => resolve(), { once: true });
        setTimeout(resolve, 400);
      });
      await waitForReportDocumentReady(doc);
      const pageNodes = Array.from(doc.querySelectorAll(".page"));
      if (!pageNodes.length) throw new Error("Report pages were not rendered.");
      const canvases = [];
      for (let index = 0; index < pageNodes.length; index += 1) {
        if (statusLabel) {
          const renderMessage = `${statusLabel} Rendering page ${index + 1}/${pageNodes.length}...`;
          setStatus(renderMessage);
          if (typeof onProgress === "function") onProgress(renderMessage);
        }
        const node = pageNodes[index];
        const canvas = await html2canvas(node, {
          backgroundColor: "#ffffff",
          useCORS: true,
          logging: false,
          scale: 2,
          windowWidth: Math.max(node.scrollWidth, 1400),
          windowHeight: Math.max(node.scrollHeight, 1800),
        });
        canvases.push(canvas);
      }
      return canvases;
    } finally {
      iframe.remove();
    }
  }

  async function createLandscapeLetterPdf() {
    const { jsPDF } = await import("jspdf");
    return new jsPDF({
      orientation: "landscape",
      unit: "pt",
      format: "letter",
      compress: true,
    });
  }

  function appendCanvasesToPdf(pdf, canvases = [], { pageIndex = 0 } = {}) {
    if (!pdf) throw new Error("PDF document is required.");
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    const margin = 28;
    const contentWidth = Math.max(1, pdfWidth - (margin * 2));
    const contentHeight = Math.max(1, pdfHeight - (margin * 2));
    let nextPageIndex = Math.max(0, Number(pageIndex) || 0);
    canvases.forEach((canvas) => {
      const oneFullPageHeightPx = canvas.width * (contentHeight / contentWidth);
      // Each canvas is already one logical report page (title/map page, table page, or an
      // images-page), captured at its own natural content height -- not an arbitrarily long
      // strip meant to be diced into fixed-size chunks. Slicing at a fixed sliceHeightPx
      // regardless of actual content height left a near-empty "leftover sliver" continuation
      // page whenever natural content was just barely taller than one page (e.g. the map page
      // with a stacked estimate table below it), and left every shorter page (partial image
      // rows, etc.) sitting unscaled with unused space below -- only slice when content
      // genuinely can't fit on one page, and when it can't, split it evenly across the pages
      // it needs instead of maxing out each page and stranding a sliver on the last one.
      const pagesNeeded = Math.max(1, Math.ceil(canvas.height / oneFullPageHeightPx));
      const sliceHeightPx = Math.max(1, Math.ceil(canvas.height / pagesNeeded));
      let offsetY = 0;
      while (offsetY < canvas.height) {
        const chunkHeight = Math.min(sliceHeightPx, canvas.height - offsetY);
        const sliceCanvas = document.createElement("canvas");
        sliceCanvas.width = canvas.width;
        sliceCanvas.height = chunkHeight;
        const sliceCtx = sliceCanvas.getContext("2d");
        if (!sliceCtx) throw new Error("Unable to render PDF page.");
        sliceCtx.drawImage(
          canvas,
          0,
          offsetY,
          canvas.width,
          chunkHeight,
          0,
          0,
          canvas.width,
          chunkHeight,
        );
        if (nextPageIndex > 0) pdf.addPage("letter", "landscape");
        pdf.setPage(nextPageIndex + 1);
        const drawWidth = contentWidth;
        const drawHeight = Math.min(contentHeight, drawWidth * (sliceCanvas.height / sliceCanvas.width));
        const drawX = margin;
        const drawY = margin;
        pdf.setFillColor(255, 255, 255);
        pdf.rect(0, 0, pdfWidth, pdfHeight, "F");
        pdf.addImage(sliceCanvas.toDataURL("image/jpeg", 0.92), "JPEG", drawX, drawY, drawWidth, drawHeight, undefined, "FAST");
        offsetY += chunkHeight;
        nextPageIndex += 1;
      }
    });
    return nextPageIndex;
  }

  async function reportHtmlToPdfBlob(report, { statusLabel = "", onProgress = null } = {}) {
    const canvases = await renderReportHtmlToCanvases(report, { statusLabel, onProgress });
    const pdf = await createLandscapeLetterPdf();
    appendCanvasesToPdf(pdf, canvases);
    return pdf.output("blob");
  }

  async function generatePrepReport(code) {
    if (!code) return;
    setStatus("Generating prep report…");
    try {
      // ── Gather project data ──────────────────────────────────────
      const marina = (state.catalog.marinas || []).find(m => m.code === code);
      const ctxAllProjects = app.services?.context?.getAllProjects?.() || [];
      const ctx = ctxAllProjects.find(p =>
        [p.id, p.marinaId, p.code].some(v => String(v || "").toUpperCase() === code.toUpperCase())
      ) || null;
      const clientId = String(marina?.clientId || marina?.clientSlug || state.catalog.selectedClientId || "");
      const milestoneSchema = getClientMilestoneSchema(clientId);

      // ── Fetch boundary from vector-files DAM ─────────────────────
      let boundaryGeojson = null;
      try {
        const assetsResp = await fetch(`/api/project/assets?code=${encodeURIComponent(code)}&category=vector-files`);
        const assetsData = await assetsResp.json();
        const boundaryFile = (assetsData.files || []).find(f => f.name === "project-boundary.geojson");
        if (boundaryFile) {
          const presignResp = await fetch("/api/s3/presign/get", {
            method: "POST", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ key: boundaryFile.key }),
          });
          const presignData = await presignResp.json();
          if (presignData.url) {
            const geoResp = await fetch(presignData.url);
            boundaryGeojson = await geoResp.json();
          }
        }
      } catch (_) { /* no boundary */ }

      // ── Add boundary to map, fly to it, capture ──────────────────
      let tempLayer = null;
      let mapImageDataUrl = null;
      let mapW = 0, mapH = 0;
      try {
        if (boundaryGeojson && typeof L !== "undefined" && map) {
          tempLayer = L.geoJSON(boundaryGeojson, {
            style: { color: "#38bdf8", weight: 2.5, fillColor: "#38bdf8", fillOpacity: 0.12, dashArray: "6 4" },
          }).addTo(map);
          const bounds = tempLayer.getBounds();
          if (bounds.isValid()) map.fitBounds(bounds, { padding: [60, 60], maxZoom: 17 });
          await delay(900);
        } else if (marina?.lat && marina?.lng && map) {
          map.flyTo([marina.lat, marina.lng], 15, { duration: 0.5 });
          await delay(700);
        }
        const mapResult = await captureCurrentMap();
        if (mapResult) {
          mapImageDataUrl = mapResult;
          const mapEl = map?.getContainer?.();
          mapW = mapEl?.clientWidth || 800;
          mapH = mapEl?.clientHeight || 500;
        }
      } finally {
        if (tempLayer) { try { tempLayer.remove(); } catch (_) {} }
      }

      // ── Compute boundary area/perimeter ──────────────────────────
      let boundaryStats = "";
      if (boundaryGeojson) {
        try {
          const coords = boundaryGeojson?.geometry?.coordinates?.[0]
            || boundaryGeojson?.features?.[0]?.geometry?.coordinates?.[0] || [];
          if (coords.length >= 3) {
            const toRad = d => d * Math.PI / 180;
            const R = 6371000;
            let area = 0, perim = 0;
            for (let i = 0; i < coords.length; i++) {
              const [x1, y1] = coords[i];
              const [x2, y2] = coords[(i + 1) % coords.length];
              area += toRad(x2 - x1) * (2 + Math.sin(toRad(y1)) + Math.sin(toRad(y2)));
              const dLat = toRad(y2 - y1), dLng = toRad(x2 - x1);
              const a = Math.sin(dLat/2)**2 + Math.cos(toRad(y1))*Math.cos(toRad(y2))*Math.sin(dLng/2)**2;
              perim += R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
            }
            const areaSqFt  = Math.abs(area) * R * R * 10.7639;
            const perimFt   = perim * 3.28084;
            const acreage   = areaSqFt / 43560;
            boundaryStats = `${acreage.toFixed(2)} ac · ${(perimFt/5280).toFixed(2)} mi perimeter`;
          }
        } catch (_) {}
      }

      // ── Build report HTML ─────────────────────────────────────────
      const name    = ctx?.name    || marina?.name    || code;
      const addr    = ctx?.address || marina?.address || "";
      const city    = ctx?.city    || marina?.city    || "";
      const st      = ctx?.state   || marina?.state   || "";
      const zip     = ctx?.zip     || marina?.zip     || "";
      const phone   = ctx?.phone   || marina?.phone   || "";
      const gmName  = ctx?.gmName  || marina?.gmName  || "";
      const gmEmail = ctx?.gmEmail || marina?.gmEmail || "";
      const hCRS    = ctx?.horizontalCRS   || "";
      const _hURaw  = ctx?.horizontalUnits || "";
      const hUnits  = (!_hURaw || _hURaw === "m" || _hURaw === "meters") ? "ftUS" : _hURaw;
      const vDatum  = ctx?.verticalDatum   || "MSL";
      const _vURaw  = ctx?.verticalUnits   || "";
      const vUnits  = (!_vURaw || _vURaw === "m" || _vURaw === "meters") ? "ft" : _vURaw;
      const geoid   = ctx?.geoidModel      || "None";
      const created = marina?.createdAt ? new Date(marina.createdAt).toLocaleDateString() : "";
      const updated = (marina?.updatedAt || marina?.activatedAt) ? new Date(marina.updatedAt || marina.activatedAt).toLocaleDateString() : "";
      const latLng  = (marina?.lat && marina?.lng) ? `${Number(marina.lat).toFixed(5)}, ${Number(marina.lng).toFixed(5)}` : "";
      const location = [addr, city, st && zip ? `${st} ${zip}` : st || zip].filter(Boolean).join(", ");

      const msRows = milestoneSchema.length
        ? milestoneSchema.map(s => {
            const val = (marina?.statusColumns || {})[s.key] || "Not Started";
            const dot = val === "Complete" ? "#22c55e" : val === "In Progress" ? "#38bdf8" : val === "On Hold" ? "#f97316" : val === "N/A" ? "#64748b" : "#94a3b8";
            return `<tr><td class="pp-l">${reportEsc(s.label)}</td><td class="pp-v"><span class="pp-dot" style="background:${dot};"></span>${reportEsc(val)}</td></tr>`;
          }).join("")
        : "";

      const row = (label, val) => val
        ? `<tr><td class="pp-l">${reportEsc(label)}</td><td class="pp-v">${reportEsc(val)}</td></tr>`
        : "";
      const sec = (title, rows) => rows
        ? `<div class="pp-sec"><div class="pp-sec-t">${reportEsc(title)}</div><table><tbody>${rows}</tbody></table></div>`
        : "";

      const dateStr = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
      const statusLabel = marina?.active ? "Active" : marina?.archivedAt ? "Archived" : "Potential";

      const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"/>
        <style>
          *{box-sizing:border-box;margin:0;padding:0;}
          body{background:#fff;font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;}
          .page{width:1380px;height:940px;overflow:hidden;color:#0f172a;}
          .pp-inner{width:100%;height:100%;display:flex;flex-direction:column;padding:28px 36px;}
          .pp-hdr{display:flex;align-items:baseline;justify-content:space-between;border-bottom:2px solid #0f172a;padding-bottom:10px;margin-bottom:16px;flex-shrink:0;}
          .pp-hdr h1{font-size:18px;font-weight:700;}
          .pp-hdr .pp-sub{font-size:11px;color:#64748b;margin-top:3px;}
          .pp-hdr-right{font-size:10px;color:#94a3b8;text-align:right;line-height:1.5;}
          .pp-body{flex:1;display:flex;flex-direction:row;gap:20px;min-height:0;}
          .pp-info{width:300px;flex-shrink:0;flex-grow:0;display:flex;flex-direction:column;overflow:hidden;}
          .pp-map{flex:1;min-width:0;display:flex;flex-direction:column;}
          .pp-map-img{flex:1;min-height:0;position:relative;overflow:hidden;border-radius:6px;border:1px solid #e2e8f0;}
          .pp-map-img img{position:absolute;top:0;left:0;width:100%;height:100%;object-fit:contain;}
          .pp-map-none{flex:1;background:#f1f5f9;border-radius:6px;border:1px dashed #cbd5e1;display:flex;align-items:center;justify-content:center;}
          .pp-stats{font-size:9px;color:#94a3b8;text-align:right;margin-top:5px;flex-shrink:0;}
          .pp-sec{margin-bottom:10px;}
          .pp-sec-t{font-size:8px;font-weight:700;text-transform:uppercase;letter-spacing:0.09em;color:#94a3b8;border-bottom:1px solid #e2e8f0;padding-bottom:2px;margin-bottom:3px;}
          table{border-collapse:collapse;width:100%;}
          td{vertical-align:top;}
          .pp-l{font-size:9px;color:#64748b;padding:2px 7px 2px 0;white-space:nowrap;width:112px;}
          .pp-v{font-size:10px;padding:2px 0;}
          .pp-dot{display:inline-block;width:7px;height:7px;border-radius:50%;margin-right:5px;vertical-align:middle;}
        </style>
      </head><body><div class="page"><div class="pp-inner">
        <div class="pp-hdr">
          <div><h1>Project Status Report</h1><div class="pp-sub">${reportEsc(code)} — ${reportEsc(name)}</div></div>
          <div class="pp-hdr-right">AOS HydroTools<br>${reportEsc(dateStr)}</div>
        </div>
        <div class="pp-body">
          <div class="pp-info">
            ${sec("Identity", [row("Code", code), row("Name", name), row("Status", statusLabel), row("Group", marina?.group || "")].join(""))}
            ${sec("Location", [row("Address", location), row("Coordinates", latLng)].join(""))}
            ${sec("Contact",  [row("GM", gmName), row("Phone", phone), row("Email", gmEmail)].join(""))}
            ${sec("Geodetic", [row("Horiz CRS", hCRS), row("Horiz Units", hUnits), row("Vert Datum", vDatum), row("Vert Units", vUnits), row("Geoid", geoid)].join(""))}
            ${sec("Record",   [row("Created", created), row("Updated", updated)].join(""))}
            ${msRows ? `<div class="pp-sec"><div class="pp-sec-t">Milestones</div><table><tbody>${msRows}</tbody></table></div>` : ""}
          </div>
          <div class="pp-map">
            ${mapImageDataUrl
              ? `<div class="pp-map-img"><img src="${mapImageDataUrl}" /></div>`
              : `<div class="pp-map-none"><span style="font-size:12px;color:#94a3b8;">No map captured</span></div>`}
            ${boundaryStats ? `<div class="pp-stats">${reportEsc(boundaryStats)}</div>` : ""}
          </div>
        </div>
      </div></div></body></html>`;

      setStatus("Rendering prep report PDF…");
      const blob = await reportHtmlToPdfBlob({ html }, { statusLabel: "Status report" });
      downloadBlob(blob, `status-report-${code.toLowerCase()}-${new Date().toISOString().slice(0,10)}.pdf`);
      setStatus("Status report downloaded.");
    } catch (err) {
      setStatus("Status report failed.");
      app.ui.toast(`Status report failed: ${err?.message || err}`, { level: "error" });
    }
  }

  function downloadExcelWorkbook(fileName, sheets = []) {
    const blob = createExcelWorkbookBlob(sheets);
    downloadBlob(blob, fileName);
  }

  function createExcelWorkbookBlob(sheets = []) {
    const worksheetHtml = sheets.map((sheet) => `
      <table data-sheet-name="${excelCell(sheet.name || "Sheet1")}">
        <thead>
          <tr>${(sheet.columns || []).map((column) => `<th>${excelCell(column)}</th>`).join("")}</tr>
        </thead>
        <tbody>
          ${(sheet.rows || []).map((row) => {
            const rowObject = Array.isArray(row) ? { cells: row } : (row || {});
            const className = String(rowObject.className || "").trim();
            const cells = Array.isArray(rowObject.cells) ? rowObject.cells : [];
            return `<tr${className ? ` class="${excelCell(className)}"` : ""}>${cells.map((cell) => `<td>${excelCell(cell)}</td>`).join("")}</tr>`;
          }).join("")}
        </tbody>
      </table>
    `).join("");
    const html = `<!doctype html>
<html xmlns:o="urn:schemas-microsoft-com:office:office"
      xmlns:x="urn:schemas-microsoft-com:office:excel"
      xmlns="http://www.w3.org/TR/REC-html40">
  <head>
    <meta charset="utf-8" />
    <!--[if gte mso 9]><xml><x:ExcelWorkbook><x:ExcelWorksheets>${sheets.map((sheet) => `<x:ExcelWorksheet><x:Name>${excelCell(sheet.name || "Sheet1")}</x:Name><x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions></x:ExcelWorksheet>`).join("")}</x:ExcelWorksheets></x:ExcelWorkbook></xml><![endif]-->
    <style>
      td,th{border:1px solid #cbd5e1;padding:4px 6px;}
      table{border-collapse:collapse;}
      tr.work-row td{background:#f9fbfe;}
      tr.travel-row td{background:#fef2f2;color:#7f1d1d;}
    </style>
  </head>
  <body>${worksheetHtml}</body>
</html>`;
    return new Blob([html], { type: "application/vnd.ms-excel;charset=utf-8" });
  }

  function marinaLocationSummary(item) {
    const p = item?.portfolio || {};
    const cityText = String(p.city || "").trim();
    const stateText = String(p.stateCountry || "").trim();
    const cityLooksLikeStateOnly = /^[A-Z]{2,3}$/.test(cityText);
    const hasRealCity = !!cityText && !cityLooksLikeStateOnly;
    if (!hasRealCity) return "";
    return stateText && cityText.toLowerCase().includes(stateText.toLowerCase())
      ? cityText
      : [cityText, stateText].filter(Boolean).join(", ");
  }

  function marinaCodeOrName(item) {
    const code = String(item?.portfolio?.code || "").trim();
    return code || String(item?.name || "marina").trim() || "marina";
  }

  function buildOfficeTripTableRows(plan) {
    return (plan?.days || []).map((day) => ({
      className: day.travelOnly ? "travel-row" : "work-row",
      cells: [
        day.day,
        day.travelOnly || !Array.isArray(day.stops) || !day.stops.length
          ? (day.travelLabel || "-")
          : Array.from(new Set(day.stops.map((stop) => String(stop?.item?.portfolio?.code || "").trim()).filter(Boolean))).join(", ") || "-",
        day.travelOnly || !Array.isArray(day.stops) || !day.stops.length
          ? "-"
          : Array.from(new Set(day.stops.map((stop) => String(stop?.item?.portfolio?.archetype || "").trim()).filter(Boolean))).join(", ") || "-",
        day.travelOnly || !Array.isArray(day.stops) || !day.stops.length
          ? "-"
          : (() => {
              let total = 0;
              let found = false;
              day.stops.forEach((stop) => {
                const raw = String(stop?.item?.portfolio?.wetSlips || "").trim();
                const value = Number.parseFloat(raw.replace(/[^0-9.\-]+/g, ""));
                if (Number.isFinite(value)) {
                  total += value;
                  found = true;
                }
              });
              return found ? String(Math.round(total)) : "-";
            })(),
        day.travelOnly || !Array.isArray(day.stops) || !day.stops.length
          ? "-"
          : (day.stops.some((stop) => stop?.item?.coveredSlips === true) ? "Yes" : "No"),
        day.travelOnly || !Array.isArray(day.stops) || !day.stops.length
          ? "-"
          : (day.stops.some((stop) => stop?.item?.noFlyZone === true) ? "Yes" : "No"),
        day.startMode === "hotel" ? "Hotel / field" : "Office / home",
        day.travelOnly
          ? (day.travelLabel || "Travel only day")
          : (day.stops.map((stop) => stop.item.name).join(", ") || "-"),
        day.travelOnly
          ? ""
          : Array.from(new Set(day.stops.map((stop) => marinaLocationSummary(stop.item)).filter(Boolean))).slice(0, 3).join(" | "),
        formatMilesPrecise(day.driveMiles),
        formatDriveMinutes(day.driveMinutes),
        day.perDiemCost ? formatCurrency(day.perDiemCost) : "-",
        formatDriveMinutes(day.workMinutes),
        formatDriveMinutes(day.totalMinutes || (day.driveMinutes + day.workMinutes)),
        day.travelOnly
          ? (day.endMode === "office" ? "Travel only / office arrival" : "Travel only / overnight")
          : (day.endMode === "office" ? "Return home" : day.endMode === "hotel" ? "Hotel / overnight" : "Field end"),
      ],
    }));
  }

  function exportFullDatabaseExcel() {
    const items = state.items.filter((item) => item.excludeFromReport !== true);
    const columns = [
      "Name",
      "Code",
      "Office",
      "Latitude",
      "Longitude",
      "Location",
      "Type",
      "Wet Slips",
      "Dry Storage",
      "Covered Slips",
      "No Fly Zone",
      "Prioritize Air Travel",
      "Excluded",
      "Survey Hours",
      "Property Count",
      "Neighborhood",
      "Cluster",
    ];
    const rows = items.map((item) => [
      item.name,
      item?.portfolio?.code || "",
      item.officeName || "",
      item.lat,
      item.lng,
      marinaLocationSummary(item),
      item?.portfolio?.archetype || "",
      item?.portfolio?.wetSlips || "",
      item?.portfolio?.dryStorage || "",
      item.coveredSlips ? "Yes" : "No",
      item.noFlyZone ? "Yes" : "No",
      item.prioritizeAirTravel ? "Yes" : "No",
      item.excludeFromReport ? "Yes" : "No",
      item.surveyHours ?? state.planning.defaultSurveyHours,
      item?.portfolio?.propertyCount ?? "",
      item.neighborhoodId || "",
      item.clusterLabel || "",
    ]);
    downloadExcelWorkbook(`${sanitizeFilePart(state.datasetName || "marina-database", "marina-database")}.xls`, [
      { name: "Marinas", columns, rows },
    ]);
    setStatus(`Exported ${items.length} marinas to Excel.`);
    app.ui.toast(`Exported ${items.length} marinas to Excel.`);
  }

  async function exportOfficeTripTableExcel(officeId) {
    const { office, marinas } = scopeForOffice(officeId, { excludeReportExcluded: true });
    if (!office || !marinas.length) {
      app.ui.toast("No reportable sites are assigned to that office.");
      return;
    }
    const plan = await enrichPlanCosts(buildTripPlan(officeId, { excludeReportExcluded: true }), office);
    const columns = ["Day", "Code", "Type", "Wet Slips", "Covered Slips", "No Fly Zone", "Start", "Sites", "Cities", "Mileage", "Drive", "Per Diem", "Survey", "Total", "End"];
    const summaryRows = [
      ["Office", office.name],
      ["Sites", plan.totals.marinaCount],
      ["Trip Days", plan.totals.baseTripDays ?? plan.days.length],
      ["Travel Mode", String(plan.travelMode || "drive").toUpperCase()],
      ["Weather Contingency", `${Number(plan.totals.weatherContingencyPct || 0).toFixed(0)}%`],
      ["Weather Days", plan.totals.weatherContingencyDays || 0],
      ["Trip Days w/ Weather", plan.totals.adjustedTripDays ?? plan.days.length],
      ["Travel Only Days", plan.totals.travelOnlyDays || 0],
      ["Hotel Nights", plan.totals.hotelNights],
      ["Price per Site", formatCurrency(plan.totals.pricePerMarina)],
      ["Site Cost", formatCurrency(plan.totals.marinaRevenue)],
      ["Weather/Travel Contingency Day Rate", formatCurrency(plan.totals.supportDayRate || 0)],
      ["Weather/Travel Contingency Days", plan.totals.supportDayQty || 0],
      ["Weather/Travel Contingency Cost", formatCurrency(plan.totals.laborEquipmentCost || 0)],
      ["Mileage", formatMilesPrecise(plan.totals.driveMiles)],
      ["Mileage Rate", `${formatCurrency(plan.costs?.mileageRate || FEDERAL_MILEAGE_RATE_2026)} / mi`],
      ["Mileage Cost", formatCurrency(plan.totals.mileageCost)],
      ["Airfare", formatCurrency(plan.totals.airfareCost || 0)],
      ["Per Diem", formatCurrency(plan.totals.perDiemCost)],
      ["Per Diem Source", plan.totals.perDiemSource || "Not used"],
      ["Per Diem Fallback Reason", plan.totals.perDiemReason || "-"],
      ["Travel Cost", formatCurrency(plan.totals.travelCost)],
      ["Estimate Total", formatCurrency(plan.totals.estimateTotal)],
      ["Drive Time", formatDriveMinutes(plan.totals.driveMinutes)],
      ["Survey Time", formatDriveMinutes(plan.totals.surveyMinutes)],
      ["Total Time", formatDriveMinutes(plan.totals.totalMinutes)],
      ["Routing", routingStatusLabel(plan.routing)],
    ];
    downloadExcelWorkbook(`${sanitizeFilePart(`${office.name}-trip-table`, "office-trip-table")}.xls`, [
      { name: "Summary", columns: ["Metric", "Value"], rows: summaryRows },
      { name: "Daily Plan", columns, rows: buildOfficeTripTableRows(plan) },
    ]);
    setStatus(`Exported ${office.name} trip table to Excel.`);
    app.ui.toast(`Exported ${office.name} trip table.`);
  }

  // Portfolio-wide status grid for the selected client — one row per project, one column
  // per milestone step, cells tinted by status so the whole portfolio scans at a glance.
  // A real .xlsx (not the HTML-as-.xls trick downloadExcelWorkbook uses elsewhere in this
  // file) — Excel flags that trick's format/extension mismatch with a scary security
  // warning, which isn't acceptable for a workbook staff hand straight to clients. SheetJS's
  // community build can't write cell styles directly, so — same technique already used for
  // slip-inventory's XLSX export (see exportXls in slip-delivery) — the base workbook is
  // generated normally, then xl/styles.xml and the worksheet XML are patched by hand to add
  // the fills/bold and reference them via each cell's s="" attribute, before re-zipping.
  const PORTFOLIO_STATUS_ORDER = ["Complete", "In Progress", "On Hold", "N/A", "Not Started"];
  const PORTFOLIO_STATUS_FILL_HEX = {
    "Complete": "FFDCFCE7",
    "In Progress": "FFDBEAFE",
    "On Hold": "FFFFEDD5",
    "N/A": "FFE2E8F0",
    "Not Started": "FFF1F5F9",
  };

  async function exportPortfolioStatusExcel() {
    const clients = Array.isArray(state.catalog.clients) ? state.catalog.clients : [];
    const selectedClientId = String(state.catalog.selectedClientId || clients[0]?.id || "");
    if (!selectedClientId) {
      app.ui.toast("Select a client first.");
      return;
    }
    const client = clients.find((c) => c.id === selectedClientId) || null;
    const schema = getClientMilestoneSchema(selectedClientId);
    const marinas = (Array.isArray(state.catalog.marinas) ? state.catalog.marinas : [])
      .filter((m) => String(m.clientId || m.clientSlug || "safeharbor") === selectedClientId)
      .slice()
      .sort((a, b) => {
        const aArchived = a.archivedAt ? 1 : 0, bArchived = b.archivedAt ? 1 : 0;
        if (aArchived !== bArchived) return aArchived - bArchived; // active projects first
        return String(a.code || "").localeCompare(String(b.code || ""), undefined, { sensitivity: "base" });
      });
    if (!marinas.length) {
      app.ui.toast("No projects in this client's portfolio yet.");
      return;
    }

    const hasGroupCol = marinas.some((m) => String(m.group || "").trim());
    const milestoneColStart = 2; // after Code, Name
    const milestoneColEnd = milestoneColStart + schema.length;
    const headers = ["Code", "Name", ...schema.map((c) => c.label), ...(hasGroupCol ? ["Group"] : []), "Project Status"];
    const dataRows = marinas.map((m) => [
      String(m.code || ""),
      String(m.name || ""),
      ...schema.map((c) => String((m.statusColumns || {})[c.key] || "Not Started")),
      ...(hasGroupCol ? [String(m.group || "")] : []),
      m.archivedAt ? "Archived" : "Active",
    ]);

    const [{ utils, write }, { default: JSZip }] = await Promise.all([
      import("xlsx"),
      import("jszip"),
    ]);

    const ws1 = utils.aoa_to_sheet([headers, ...dataRows]);
    const ws2 = utils.aoa_to_sheet([["Milestone Status"], ...PORTFOLIO_STATUS_ORDER.map((s) => [s])]);
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws1, "Portfolio Status");
    utils.book_append_sheet(wb, ws2, "Legend");
    const zip = await JSZip.loadAsync(write(wb, { type: "array", bookType: "xlsx" }));

    // Patch xl/styles.xml: one solid fill per status, plus a bold font for header rows.
    let stylesXml = await zip.file("xl/styles.xml").async("string");
    const baseFillCount = parseInt(stylesXml.match(/<fills count="(\d+)">/)[1], 10);
    const newFillsXml = PORTFOLIO_STATUS_ORDER.map((s) =>
      `<fill><patternFill patternType="solid"><fgColor rgb="${PORTFOLIO_STATUS_FILL_HEX[s]}"/><bgColor indexed="64"/></patternFill></fill>`
    ).join("");
    stylesXml = stylesXml
      .replace(/<fills count="\d+">/, `<fills count="${baseFillCount + PORTFOLIO_STATUS_ORDER.length}">`)
      .replace("</fills>", newFillsXml + "</fills>");
    const baseFontCount = parseInt(stylesXml.match(/<fonts count="(\d+)">/)[1], 10);
    const boldFontId = baseFontCount;
    stylesXml = stylesXml
      .replace(/<fonts count="\d+">/, `<fonts count="${baseFontCount + 1}">`)
      .replace("</fonts>", `<font><b/><sz val="11"/><color theme="1"/><name val="Calibri"/><family val="2"/><scheme val="minor"/></font></fonts>`);
    const baseXfCount = parseInt(stylesXml.match(/<cellXfs count="(\d+)">/)[1], 10);
    const statusXfIndex = {};
    PORTFOLIO_STATUS_ORDER.forEach((s, i) => { statusXfIndex[s] = baseXfCount + i; });
    const headerXfIndex = baseXfCount + PORTFOLIO_STATUS_ORDER.length;
    const newXfsXml = PORTFOLIO_STATUS_ORDER.map((s, i) =>
      `<xf numFmtId="0" fontId="0" fillId="${baseFillCount + i}" borderId="0" xfId="0" applyFill="1"/>`
    ).join("") + `<xf numFmtId="0" fontId="${boldFontId}" fillId="0" borderId="0" xfId="0" applyFont="1"/>`;
    stylesXml = stylesXml
      .replace(/<cellXfs count="\d+">/, `<cellXfs count="${baseXfCount + PORTFOLIO_STATUS_ORDER.length + 1}">`)
      .replace("</cellXfs>", newXfsXml + "</cellXfs>");
    zip.file("xl/styles.xml", stylesXml);

    // sheet1 = Portfolio Status: bold header row, milestone data cells colored by status.
    const styleByCellSheet1 = new Map();
    headers.forEach((_, colIdx) => styleByCellSheet1.set(`${utils.encode_col(colIdx)}1`, headerXfIndex));
    dataRows.forEach((row, rIdx) => {
      const rowNum = rIdx + 2;
      for (let c = milestoneColStart; c < milestoneColEnd; c++) {
        const xfIdx = statusXfIndex[row[c]] ?? statusXfIndex["Not Started"];
        styleByCellSheet1.set(`${utils.encode_col(c)}${rowNum}`, xfIdx);
      }
    });
    const sheet1Xml = (await zip.file("xl/worksheets/sheet1.xml").async("string"))
      .replace(/<c r="([A-Z]+\d+)"/g, (m, ref) => {
        const idx = styleByCellSheet1.get(ref);
        return idx != null ? `<c r="${ref}" s="${idx}"` : m;
      });
    zip.file("xl/worksheets/sheet1.xml", sheet1Xml);

    // sheet2 = Legend: bold header, each status name colored with its own fill.
    const styleByCellSheet2 = new Map([["A1", headerXfIndex]]);
    PORTFOLIO_STATUS_ORDER.forEach((s, i) => styleByCellSheet2.set(`A${i + 2}`, statusXfIndex[s]));
    const sheet2Xml = (await zip.file("xl/worksheets/sheet2.xml").async("string"))
      .replace(/<c r="([A-Z]+\d+)"/g, (m, ref) => {
        const idx = styleByCellSheet2.get(ref);
        return idx != null ? `<c r="${ref}" s="${idx}"` : m;
      });
    zip.file("xl/worksheets/sheet2.xml", sheet2Xml);

    const outBuf = await zip.generateAsync({ type: "uint8array", compression: "DEFLATE" });
    const blob = new Blob([outBuf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    const clientLabel = client?.name || selectedClientId;
    const dateStamp = new Date().toISOString().slice(0, 10);
    downloadBlob(blob, `${sanitizeFilePart(clientLabel, "portfolio")}_portfolio_status_${dateStamp}.xlsx`);
    setStatus(`Exported portfolio status for ${clientLabel} (${marinas.length} project${marinas.length === 1 ? "" : "s"}).`);
    app.ui.toast(`Exported ${clientLabel} portfolio status.`);
  }

  function tripReportHtml({ office, plan, mapImageDataUrl, mapImageWidth = 0, mapImageHeight = 0, marinaImages = [], reportTitle = "" }) {
    const generatedAt = new Date().toLocaleString();
    const safeMapWidth = Math.max(1, Number(mapImageWidth) || 1);
    const safeMapHeight = Math.max(1, Number(mapImageHeight) || 1);
    // The captured map image's raw aspect ratio depends entirely on the live map panel's
    // on-screen shape when the report was generated -- a narrower/taller browser window
    // produces a narrower/taller capture. Left unclamped, a tall capture forces a tall
    // .map-frame box (aspect-ratio-locked) at a fixed pane width, which was ballooning the
    // whole first page well past one page's height. Floor the ratio to a wide, page-friendly
    // minimum so the frame's own height is always bounded regardless of capture shape; the
    // The html2canvas build this export goes through doesn't support object-fit at all --
    // verified directly (a pixel test against the exact same build) that it silently
    // stretches an img to fill its box in both dimensions regardless of the declared value,
    // which is what was distorting/"scrunching" the map. Sizing the <img> itself with plain
    // width/height (one set to "auto") sidesteps object-fit entirely and lets ordinary CSS
    // aspect-ratio-preserving layout do the work, which html2canvas renders correctly.
    const rawMapRatio = safeMapWidth / safeMapHeight;
    const clampedMapRatio = Math.max(rawMapRatio, 1.5);
    const mapAspectRatio = String(clampedMapRatio);
    // Image is at least as wide (relative to its height) as the clamped frame -> width is the
    // binding dimension (fills full frame width, letterboxed top/bottom if shorter than the
    // frame). Otherwise height is binding (fills full frame height, letterboxed left/right).
    const mapImgSizeStyle = rawMapRatio >= clampedMapRatio
      ? "width:100%;height:auto;"
      : "height:100%;width:auto;";
    function dayCodeSummary(day) {
      if (day.travelOnly || !Array.isArray(day.stops) || !day.stops.length) return day.travelLabel || "-";
      const codes = day.stops
        .map((stop) => String(stop?.item?.portfolio?.code || stop?.item?.id || "").trim())
        .filter(Boolean);
      return codes.length ? Array.from(new Set(codes)).join(", ") : "-";
    }
    function dayTypeSummary(day) {
      if (day.travelOnly || !Array.isArray(day.stops) || !day.stops.length) return "-";
      const types = day.stops
        .map((stop) => String(stop?.item?.portfolio?.archetype || "").trim())
        .filter(Boolean);
      return types.length ? Array.from(new Set(types)).join(", ") : "-";
    }
    function dayWetSlipSummary(day) {
      if (day.travelOnly || !Array.isArray(day.stops) || !day.stops.length) return "-";
      let total = 0;
      let found = false;
      day.stops.forEach((stop) => {
        const raw = String(stop?.item?.portfolio?.wetSlips || "").trim();
        const value = Number.parseFloat(raw.replace(/[^0-9.\-]+/g, ""));
        if (Number.isFinite(value)) {
          total += value;
          found = true;
        }
      });
      return found ? String(Math.round(total)) : "-";
    }
    function dayCoveredSlipSummary(day) {
      if (day.travelOnly || !Array.isArray(day.stops) || !day.stops.length) return "-";
      return day.stops.some((stop) => stop?.item?.coveredSlips === true) ? "Yes" : "No";
    }
    function dayMileageSummary(day) {
      return formatMilesPrecise(day.driveMiles);
    }
    function dayPerDiemSummary(day) {
      return day.perDiemCost ? formatCurrency(day.perDiemCost) : "-";
    }
    function dayPortfolioSummary(day) {
      if (day.travelOnly || !Array.isArray(day.stops) || !day.stops.length) return "";
      const cities = new Set();
      day.stops.forEach((stop) => {
        const p = stop?.item?.portfolio;
        if (!p) return;
        const cityText = String(p.city || "").trim();
        const stateText = String(p.stateCountry || "").trim();
        const cityLooksLikeStateOnly = /^[A-Z]{2,3}$/.test(cityText);
        const hasRealCity = !!cityText && !cityLooksLikeStateOnly;
        const cityLabel = !hasRealCity
          ? ""
          : stateText && cityText.toLowerCase().includes(stateText.toLowerCase())
            ? cityText
            : [cityText, stateText].filter(Boolean).join(", ");
        if (cityLabel) cities.add(cityLabel);
      });
      return cities.size ? Array.from(cities).slice(0, 3).join(" | ") : "";
    }
    const rows = plan.days.map((day) => `
      <tr class="${day.travelOnly ? "travel-row" : "work-row"}">
        <td>${day.day}</td>
        <td>${reportEsc(dayCodeSummary(day))}</td>
        <td>${reportEsc(day.startMode === "hotel" ? "Hotel / field" : "Office / home")}</td>
        <td>
          <div>${reportEsc(day.travelOnly ? (day.travelLabel || "Travel only day") : (day.stops.map((stop) => stop.item.name).join(", ") || "-"))}</div>
          ${dayPortfolioSummary(day) ? `<div class="subline">${reportEsc(dayPortfolioSummary(day))}</div>` : ""}
        </td>
        <td>${reportEsc(dayMileageSummary(day))}</td>
        <td>${reportEsc(formatDriveMinutes(day.driveMinutes))}</td>
        <td>${reportEsc(dayPerDiemSummary(day))}</td>
        <td>${reportEsc(formatDriveMinutes(day.workMinutes))}</td>
        <td>${reportEsc(formatDriveMinutes(day.totalMinutes || (day.driveMinutes + day.workMinutes)))}</td>
        <td>${reportEsc(day.travelOnly ? (day.endMode === "office" ? "Travel only / office arrival" : "Travel only / overnight") : (day.endMode === "office" ? "Return home" : day.endMode === "hotel" ? "Hotel / overnight" : "Field end"))}</td>
      </tr>
    `).join("");
    const imagePages = [];
    const marinaImagesPerPage = 6;
    for (let index = 0; index < marinaImages.length; index += marinaImagesPerPage) {
      imagePages.push(marinaImages.slice(index, index + marinaImagesPerPage));
    }
    const imagePagesHtml = imagePages.map((pageItems, pageIndex) => `
      <div class="page images-page">
        <h2 class="page-title">${pageIndex === 0 ? "Site Images" : `Site Images ${pageIndex + 1}`}</h2>
        <div class="image-grid">
          ${pageItems.map((entry) => `
            <figure class="image-card">
              <div class="image-card-label">${reportEsc(entry.code || entry.name)}</div>
              <img src="${entry.dataUrl}" alt="${reportEsc(entry.name)}" />
            </figure>
          `).join("")}
        </div>
      </div>
    `).join("");
    const routingLabel = routingStatusLabel(plan.routing);
    const perDiemSourceLabel = plan.totals.perDiemSource || "Not used";
    const perDiemReasonLabel = plan.totals.perDiemReason || "";
    const resolvedReportTitle = reportTitle || `Trip Report: ${office?.name || "Office"} Office Proximity`;
    return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>${reportEsc(resolvedReportTitle)}</title>
    <style>
      :root {
        color-scheme: light;
        --ink:#10233f;
        --ink-soft:#415a77;
        --ink-faint:#6b7d94;
        --panel:#f6f9fc;
        --panel-strong:#e6eef7;
        --panel-accent:#d8e5f3;
        --border:#bfd0e2;
        --accent:#173b67;
        --accent-strong:#0f2d52;
        --accent-soft:#2f5f95;
      }
      @page { size: landscape; margin: 10mm; }
      body { font: 13px/1.45 "Segoe UI", Arial, sans-serif; color:var(--ink); margin:0; background:#fff; }
      h1,h2 { margin:0 0 8px; color:var(--accent-strong); letter-spacing:0.01em; }
      .page { min-height:100vh; box-sizing:border-box; padding:18px; }
      .page + .page { page-break-before: always; }
      .report-title { text-align:center; font-size:32px; line-height:1.1; margin:0 0 18px; color:var(--accent-strong); letter-spacing:0.02em; }
      .page-title { text-align:center; font-size:24px; line-height:1.15; margin:0 0 14px; color:var(--accent-strong); letter-spacing:0.02em; }
      .meta { color:var(--ink-soft); }
      ${reportFirstPageLayoutCss()}
      .estimate-block { border:1px solid var(--border); border-radius:12px; background:linear-gradient(180deg, #fbfdff 0%, var(--panel) 100%); overflow:hidden; margin-top:14px; box-shadow:0 10px 22px rgba(16,35,63,0.05); }
      .estimate-head { padding:10px 12px; background:linear-gradient(180deg, var(--panel-accent) 0%, var(--panel-strong) 100%); font:700 12px/1.2 "Segoe UI", Arial, sans-serif; color:var(--accent-strong); text-transform:uppercase; letter-spacing:0.06em; }
      .estimate-body { padding:8px 12px; }
      .estimate-grid { display:grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap:8px 14px; }
      .estimate-line { display:flex; justify-content:space-between; gap:12px; padding:4px 0; border-bottom:1px solid var(--panel-accent); }
      .estimate-line:last-child { border-bottom:none; }
      .estimate-label { color:var(--ink-soft); }
      .estimate-value { font-weight:700; text-align:right; color:var(--accent-strong); }
      .estimate-table { width:100%; border-collapse:collapse; font-size:12px; }
      .estimate-table th, .estimate-table td { padding:6px 4px; border-bottom:1px solid var(--panel-accent); text-align:left; }
      .estimate-table th { color:var(--ink-faint); font-size:11px; background:transparent; text-transform:uppercase; letter-spacing:0.04em; }
      .estimate-table td.num, .estimate-table th.num { text-align:right; }
      .estimate-table tr.total-row td { border-top:2px solid var(--border); border-bottom:none; font-weight:700; color:var(--accent-strong); }
      .map-wrap img { border:1px solid var(--border); border-radius:12px; display:block; background:var(--panel); box-shadow:0 12px 28px rgba(16,35,63,0.08); }
      table { width:100%; border-collapse:collapse; margin-top:10px; }
      th, td { border:1px solid var(--border); padding:5px 6px; vertical-align:top; text-align:center; }
      th { background:linear-gradient(180deg, var(--panel-accent) 0%, var(--panel-strong) 100%); color:var(--accent-strong); font-size:11px; line-height:1.15; text-transform:uppercase; letter-spacing:0.04em; }
      td { font-size:10.5px; line-height:1.2; color:var(--ink); }
      .subline { margin-top:3px; color:var(--ink-soft); font-size:9.5px; line-height:1.15; text-align:center; }
      .table-page tbody tr.work-row td { background:#fbfdff; }
      .table-page tbody tr.work-row:nth-child(even) td { background:#f6faff; }
      .table-page tbody tr.travel-row td { background:#fef2f2; color:#7f1d1d; }
      .table-page table { table-layout:fixed; }
      .table-page th:nth-child(1), .table-page td:nth-child(1) { width:5%; }
      .table-page th:nth-child(2), .table-page td:nth-child(2) { width:8%; }
      .table-page th:nth-child(3), .table-page td:nth-child(3) { width:10%; }
      .table-page th:nth-child(4), .table-page td:nth-child(4) { width:27%; }
      .table-page th:nth-child(5), .table-page td:nth-child(5) { width:9%; }
      .table-page th:nth-child(6), .table-page td:nth-child(6) { width:9%; }
      .table-page th:nth-child(7), .table-page td:nth-child(7) { width:8%; }
      .table-page th:nth-child(8), .table-page td:nth-child(8) { width:8%; }
      .table-page th:nth-child(9), .table-page td:nth-child(9) { width:8%; }
      .table-page th:nth-child(10), .table-page td:nth-child(10) { width:8%; }
      .images-page {
        page-break-before: always;
        break-before: page;
        page-break-inside: avoid;
        break-inside: avoid-page;
      }
      .image-grid { display:grid; grid-template-columns:repeat(3, minmax(0, 1fr)); gap:12px; margin-top:12px; }
      .image-card {
        margin:0;
        border:1px solid var(--border);
        border-radius:10px;
        overflow:hidden;
        background:var(--panel);
        page-break-inside:avoid;
        break-inside: avoid-page;
        box-shadow:0 10px 22px rgba(16,35,63,0.05);
      }
      .image-card-label { padding:6px 8px; font:700 11px/1.2 "Segoe UI", Arial, sans-serif; background:linear-gradient(180deg, var(--panel-accent) 0%, var(--panel-strong) 100%); color:var(--accent-strong); }
      .image-card img { display:block; width:100%; height:auto; }
      @media print {
        .no-print { display:none; }
        .page { min-height:auto; padding:0; }
        .summary-pane { max-width:none; }
        .estimate-block { margin-top:10px; }
        .estimate-head { padding:8px 10px; font-size:11px; }
        .estimate-body { padding:6px 10px; }
        .estimate-grid { grid-template-columns: 1fr; gap:4px 0; }
        .estimate-line { padding:3px 0; }
        ${reportFirstPagePrintLayoutCss()}
        .images-page {
          page-break-before:always;
          break-before:page;
          page-break-inside: avoid;
          break-inside: avoid-page;
        }
        .report-title { font-size:24px; margin:0 0 10px; }
        .page-title { font-size:18px; margin:0 0 8px; }
        .image-grid { gap:10px; }
      }
    </style>
  </head>
  <body>
    <div class="no-print" style="margin:12px 18px;">
      <button onclick="window.print()">Print</button>
    </div>
    <div class="page">
      <h1 class="report-title">${reportEsc(resolvedReportTitle)}</h1>
      <h2 class="page-title">Summary</h2>
      <div class="summary-layout">
        <div class="summary-pane">
          <div class="meta">
            Office: <strong>${reportEsc(office?.name || "Unknown")}</strong><br />
            Generated: ${reportEsc(generatedAt)}<br />
            Routing: ${reportEsc(routingLabel)}<br />
            Travel Mode: ${reportEsc(String(plan.travelMode || "drive").toUpperCase())}<br />
            Per Diem: ${reportEsc(perDiemSourceLabel)}${perDiemReasonLabel ? `<br />Per Diem Fallback: ${reportEsc(perDiemReasonLabel)}` : ""}
          </div>
          <div class="estimate-block">
            <div class="estimate-head">Summary</div>
            <div class="estimate-body estimate-grid">
              <div class="estimate-line"><span class="estimate-label">Sites</span><span class="estimate-value">${plan.totals.marinaCount}</span></div>
              <div class="estimate-line"><span class="estimate-label">Trip Days</span><span class="estimate-value">${plan.totals.baseTripDays ?? plan.days.length}</span></div>
              <div class="estimate-line"><span class="estimate-label">Weather Contingency</span><span class="estimate-value">${reportEsc(`${Number(plan.totals.weatherContingencyPct || 0).toFixed(0)}%`)}</span></div>
              <div class="estimate-line"><span class="estimate-label">Weather Days</span><span class="estimate-value">${plan.totals.weatherContingencyDays || 0}</span></div>
              <div class="estimate-line"><span class="estimate-label">Trip Days w/ Weather</span><span class="estimate-value">${plan.totals.adjustedTripDays ?? plan.days.length}</span></div>
              <div class="estimate-line"><span class="estimate-label">Travel Only Days</span><span class="estimate-value">${plan.totals.travelOnlyDays || 0}</span></div>
              <div class="estimate-line"><span class="estimate-label">Travel Mode</span><span class="estimate-value">${reportEsc(String(plan.travelMode || "drive").toUpperCase())}</span></div>
              <div class="estimate-line"><span class="estimate-label">Hotel Nights</span><span class="estimate-value">${plan.totals.hotelNights}</span></div>
              <div class="estimate-line"><span class="estimate-label">Mileage</span><span class="estimate-value">${reportEsc(formatMilesPrecise(plan.totals.driveMiles))}</span></div>
              <div class="estimate-line"><span class="estimate-label">Airfare</span><span class="estimate-value">${reportEsc(formatCurrency(plan.totals.airfareCost || 0))}</span></div>
              <div class="estimate-line"><span class="estimate-label">Drive Time</span><span class="estimate-value">${reportEsc(formatDriveMinutes(plan.totals.driveMinutes))}</span></div>
              <div class="estimate-line"><span class="estimate-label">Survey Time</span><span class="estimate-value">${reportEsc(formatDriveMinutes(plan.totals.surveyMinutes))}</span></div>
              <div class="estimate-line"><span class="estimate-label">Total Time</span><span class="estimate-value">${reportEsc(formatDriveMinutes(plan.totals.totalMinutes))}</span></div>
              <div class="estimate-line"><span class="estimate-label">Routing</span><span class="estimate-value">${reportEsc(plan.routing?.mode || "estimated")}</span></div>
              <div class="estimate-line"><span class="estimate-label">Per Diem Source</span><span class="estimate-value">${reportEsc(perDiemSourceLabel)}</span></div>
              <div class="estimate-line"><span class="estimate-label">Per Diem Fallback</span><span class="estimate-value">${reportEsc(perDiemReasonLabel || "-")}</span></div>
            </div>
          </div>
          <div class="estimate-block">
            <div class="estimate-head">Cost Estimate</div>
            <div class="estimate-body">
              <table class="estimate-table">
                <thead>
                  <tr>
                    <th>Line Item</th>
                    <th class="num">Rate</th>
                    <th class="num">Qty.</th>
                    <th class="num">Total</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Site Survey</td>
                    <td class="num">${reportEsc(formatCurrency(plan.totals.pricePerMarina))}</td>
                    <td class="num">${plan.totals.marinaCount}</td>
                    <td class="num">${reportEsc(formatCurrency(plan.totals.marinaRevenue))}</td>
                  </tr>
                  <tr>
                    <td>Mileage</td>
                    <td class="num">${reportEsc(formatCurrency(plan.costs?.mileageRate || FEDERAL_MILEAGE_RATE_2026))}/mi</td>
                    <td class="num">${reportEsc(formatMilesPrecise(plan.totals.driveMiles))}</td>
                    <td class="num">${reportEsc(formatCurrency(plan.totals.mileageCost))}</td>
                  </tr>
                  <tr>
                    <td>Airfare</td>
                    <td class="num">Estimated</td>
                    <td class="num">${plan.totals.airfareCost > 0 ? "1" : "0"}</td>
                    <td class="num">${reportEsc(formatCurrency(plan.totals.airfareCost || 0))}</td>
                  </tr>
                  <tr>
                    <td>Per Diem</td>
                    <td class="num">${reportEsc(formatCurrency(plan.totals.perDiemRate || 0))}/day</td>
                    <td class="num">${plan.totals.perDiemDays || 0}</td>
                    <td class="num">${reportEsc(formatCurrency(plan.totals.perDiemCost))}</td>
                  </tr>
                  <tr>
                    <td>Weather Contingency</td>
                    <td class="num">${reportEsc(formatCurrency(plan.totals.supportDayRate || 0))}/day</td>
                    <td class="num">${plan.totals.weatherContingencyDays || 0}</td>
                    <td class="num">${reportEsc(formatCurrency((plan.totals.supportDayRate || 0) * (plan.totals.weatherContingencyDays || 0)))}</td>
                  </tr>
                  <tr>
                    <td>Travel Days</td>
                    <td class="num">${reportEsc(formatCurrency(plan.totals.supportDayRate || 0))}/day</td>
                    <td class="num">${plan.totals.travelOnlyDays || 0}</td>
                    <td class="num">${reportEsc(formatCurrency((plan.totals.supportDayRate || 0) * (plan.totals.travelOnlyDays || 0)))}</td>
                  </tr>
                  <tr class="total-row">
                    <td>Estimate Total</td>
                    <td class="num"></td>
                    <td class="num"></td>
                    <td class="num">${reportEsc(formatCurrency(plan.totals.estimateTotal))}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div class="map-pane">
          <div class="map-wrap">
            <h2 class="map-pane-title">Coverage Map</h2>
            <div class="map-frame" style="--mapAspectRatio:${mapAspectRatio};">
              <img src="${mapImageDataUrl}" alt="Trip coverage map" style="${mapImgSizeStyle}" />
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="page table-page">
      <h2 class="page-title">Daily Detail</h2>
      <table>
        <thead>
          <tr>
            <th>Day</th>
            <th>Code</th>
            <th>Start</th>
            <th>Sites</th>
            <th>Mileage</th>
            <th>Drive</th>
            <th>Per Diem</th>
            <th>Survey</th>
            <th>Total</th>
            <th>End</th>
          </tr>
        </thead>
        <tbody>${rows || `<tr><td colspan="10">No trip days available.</td></tr>`}</tbody>
      </table>
    </div>
    ${imagePagesHtml || `<div class="page images-page"><h2 class="page-title">Site Images</h2><div>No site images captured.</div></div>`}
  </body>
</html>`;
  }

  async function printOfficeTripReport(officeId, reportWindow, { missionScope = "all" } = {}) {
    const report = await generateOfficeTripReportHtml(officeId, { missionScope });
    if (!ENABLE_REPORT_POPUPS) {
      throw new Error("Report popups are temporarily disabled.");
    }
    const win = reportWindow || window.open("", "_blank");
    if (!win) throw new Error("Popup blocked");
    win.document.open();
    win.document.write(report.html);
    win.document.close();
    setStatus(`Trip report ready for ${report.office.name}.`);
  }

  async function generateOfficeTripReportPdf(officeId, { missionScope = "all", onProgress = null } = {}) {
    const report = await generateOfficeTripReportHtml(officeId, { missionScope, onProgress });
    const statusLabel = `Preparing PDF for ${report.office.name}${missionScope !== "all" ? ` (${missionScope})` : ""}...`;
    setStatus(statusLabel);
    if (typeof onProgress === "function") onProgress(statusLabel);
    const blob = await reportHtmlToPdfBlob(report, { statusLabel, onProgress });
    const fileName = `${sanitizeFilePart(report.title.replace(/:/g, ""), "trip-report")}.pdf`;
    return { ...report, blob, fileName };
  }

  async function prepareReportPlan(officeId, { missionScope = "all", onProgress = null } = {}) {
    const waitForReady = async (checkFn, { timeoutMs = 45000, pollMs = 400 } = {}) => {
      const start = Date.now();
      while ((Date.now() - start) < timeoutMs) {
        const result = checkFn();
        if (result === true) return true;
        if (typeof result === "string" && result) throw new Error(result);
        await delay(pollMs);
      }
      return false;
    };
    const officeName = state.offices.find((entry) => entry.id === officeId)?.name || "office";
    const scopeSuffix = missionScope !== "all" ? ` (${missionScope})` : "";
    const progress = (message) => {
      setStatus(message);
      if (typeof onProgress === "function") onProgress(message);
    };
    // Fail fast before spending time on routing if there are no marinas to report on.
    const preflightScope = scopeForOffice(officeId, { excludeReportExcluded: true, missionScope });
    if (!preflightScope.marinas.length) {
      const groupFilter = state.planning.tripGroupFilter;
      if (groupFilter && !state.catalog.marinas?.length) {
        throw new Error(`Catalog not loaded yet — open the image generator panel first, then retry.`);
      }
      throw new Error(groupFilter
        ? `No marinas in group "${groupFilter}" are in the image generator queue. Add them via the image generator first.`
        : `No marinas are queued for ${officeName}. Add marinas to the image generator queue first.`);
    }
    progress(`Preparing route data for ${officeName}${scopeSuffix}...`);
    await refreshRouteMatrix({ force: true, officeId, excludeReportExcluded: true, missionScope });
    await waitForReady(() => {
      if (routeMatrixStatus === "ready" || routeMatrixStatus === "fallback") return true;
      return false;
    });
    progress(`Building trip plan for ${officeName}${scopeSuffix}...`);
    const scope = scopeForOffice(officeId, { excludeReportExcluded: true, missionScope });
    const rawPlan = buildTripPlan(officeId, { excludeReportExcluded: true, missionScope });
    progress(`Pricing trip plan for ${officeName}${scopeSuffix}...`);
    const plan = await enrichPlanCosts(rawPlan, scope.office);
    if (routeMatrixStatus !== "ready" && routeMatrixStatus !== "fallback") {
      throw new Error(`Routing not ready: ${routingStatusLabel(plan.routing)}`);
    }
    if (state.showTripRoute) {
      progress(`Preparing route overlay for ${officeName}${scopeSuffix}...`);
      const geometryResult = await refreshRouteGeometry(rawPlan, { force: true });
      const reportGeometryKey = String(rawPlan?.routeScopeKey || rawPlan?.routing?.scopeKey || routeScopeKey(scope.office, scope.marinas));
      const hasDirectGeometry = geometryResult?.key === reportGeometryKey
        && geometryResult?.status === "ready"
        && Array.isArray(geometryResult?.features)
        && geometryResult.features.length > 0;
      const hasGlobalGeometry = routeGeometryKey === reportGeometryKey
        && Array.isArray(routeGeometryFeatures)
        && routeGeometryFeatures.length > 0;
      if (!hasDirectGeometry && !hasGlobalGeometry) {
        throw new Error(`Route overlay not ready: ${geometryResult?.error || routeGeometryError || geometryResult?.status || routeGeometryStatus}`);
      }
    }
    const perDiemTone = statusTone("perDiem", plan.totals.perDiemSource || "Not used");
    if (perDiemTone === "bad") {
      throw new Error(`Per diem not ready: ${plan.totals.perDiemSource || "unknown"}${plan.totals.perDiemReason ? ` (${plan.totals.perDiemReason})` : ""}`);
    }
    return { scope, rawPlan, plan, reportRouteScopeKey: String(rawPlan?.routeScopeKey || rawPlan?.routing?.scopeKey || routeScopeKey(scope.office, scope.marinas)) };
  }

  async function generateGroupTripReport(clientId, groupName, gm) {
    const officeId = gm.officeId;
    const prevGroupFilter = state.planning.tripGroupFilter;
    const prevTravelMode = state.planning.travelMode;
    const prevHomeAirportOverride = state.planning.homeAirportOverride;
    state.planning.tripGroupFilter = groupName;
    if (gm.flyIn) {
      state.planning.travelMode = "air";
      state.planning.homeAirportOverride = gm.flyInAirport || "";
    }
    try {
      await generateScopedOfficeReportPdfs(officeId);
    } finally {
      state.planning.tripGroupFilter = prevGroupFilter;
      state.planning.travelMode = prevTravelMode;
      state.planning.homeAirportOverride = prevHomeAirportOverride;
    }
  }

  async function generateScopedOfficeReportPdfs(officeId) {
    const scopes = officeReportScopes(officeId, { excludeReportExcluded: true });
    const total = Math.max(1, scopes.length);
    const downloads = [];
    const autoDownload = total === 1;
    const launcherTitle = `Trip Reports: ${scopes[0]?.office?.name || "Office"}`;
    const reportWindow = total > 1
      ? openPreparedReportWindow("Preparing multiple trip reports. Links will appear here as each PDF is ready.", "imggen-trip-report-launcher")
      : null;
    const launcherReports = scopes.map((scope) => ({
      key: scope?.key || "all",
      label: reportLauncherLabel(scope),
      status: "Queued",
      ready: false,
      openUrl: "",
      downloadUrl: "",
      fileName: "",
    }));
    if (reportWindow) {
      updateReportLauncherWindow(reportWindow, launcherTitle, launcherReports, {
        status: autoDownload
          ? `Preparing 0/${total} reports...`
          : `Preparing 0/${total} reports. Download each file from this page when it is ready.`,
        complete: false,
      });
    }
    for (let index = 0; index < total; index += 1) {
      const scope = scopes[index] || null;
      setStatus(`Generating PDF report ${index + 1}/${total} for ${scope?.office?.name || "office"}${scope?.key ? ` (${scope.key})` : ""}...`);
      if (reportWindow && launcherReports[index]) {
        launcherReports[index].status = "Generating...";
        updateReportLauncherWindow(reportWindow, launcherTitle, launcherReports, {
          status: `Preparing ${index + 1}/${total} reports...`,
          complete: false,
        });
      }
      const built = await generateOfficeTripReportPdf(officeId, {
        missionScope: scope?.key || "all",
        onProgress: reportWindow && launcherReports[index]
          ? (message) => {
              launcherReports[index].status = String(message || "Generating...");
              updateReportLauncherWindow(reportWindow, launcherTitle, launcherReports, {
                status: autoDownload
                  ? `Preparing ${index + 1}/${total} reports...`
                  : `Preparing ${index + 1}/${total} reports. Download each file from this page when it is ready.`,
                complete: false,
              });
            }
          : null,
      });
      if (autoDownload) downloadBlob(built.blob, built.fileName);
      downloads.push(built.fileName);
      if (reportWindow && launcherReports[index]) {
        const reportUrls = createPdfLauncherUrls(built.blob, built.fileName);
        launcherReports[index].status = built.fileName;
        launcherReports[index].ready = true;
        launcherReports[index].openUrl = reportUrls.openUrl;
        launcherReports[index].downloadUrl = reportUrls.downloadUrl;
        launcherReports[index].fileName = built.fileName;
        updateReportLauncherWindow(reportWindow, launcherTitle, launcherReports, {
          status: autoDownload
            ? `Prepared ${index + 1}/${total} reports.`
            : `Prepared ${index + 1}/${total} reports. Use the links below to download them.`,
          complete: index === total - 1,
        });
      }
      app.ui.toast(autoDownload ? `Downloaded ${built.fileName}` : `Prepared ${built.fileName}`);
    }
    return downloads;
  }

  async function generateAllOfficeReports() {
    const offices = state.offices.filter((office) => officeReportScopes(office.id, { excludeReportExcluded: true }).length);
    if (!offices.length) {
      app.ui.toast("No office reports are available.");
      return;
    }
    const previousTripOfficeId = state.planning.tripOfficeId;
    const previousTripGroupFilter = state.planning.tripGroupFilter;
    const previousSelectedOfficeIds = [...state.selectedOfficeIds];
    const previousVisibleOfficeMarinaIds = [...state.visibleOfficeMarinaIds];
    const previousView = snapshotMapView();
    const previousActiveIndex = state.activeIndex;
    let totalDownloads = 0;
    const officeScopeQueue = offices.flatMap((office) => {
      state.planning.tripGroupFilter = office.groupName || "";
      const scopes = officeReportScopes(office.id, { excludeReportExcluded: true });
      state.planning.tripGroupFilter = previousTripGroupFilter;
      return scopes.map((scope) => ({ officeId: office.id, officeName: office.name, scope }));
    });
    const launcherReports = officeScopeQueue.map((entry, index) => ({
      id: `${entry.officeId}:${entry.scope?.key || "all"}:${index}`,
      label: reportLauncherLabel(entry.scope),
      status: "Queued",
      ready: false,
      openUrl: "",
      downloadUrl: "",
      fileName: "",
    }));
    const summaryRows = [];
    let currentSummaryActions = [];
    const reportWindow = openPreparedReportWindow(
      "Preparing all trip reports. Links will appear here as each PDF is ready.",
      "imggen-all-trip-reports-launcher",
    );
    try {
      updateReportLauncherWindow(reportWindow, "All Trip Reports", launcherReports, {
        status: `Preparing 0/${officeScopeQueue.length} reports. Download each file from this page when it is ready.`,
        complete: false,
        summaryActions: currentSummaryActions,
      });
      for (let officeIndex = 0; officeIndex < offices.length; officeIndex += 1) {
        const office = offices[officeIndex];
        state.planning.tripOfficeId = office.id;
        state.planning.tripGroupFilter = office.groupName || "";
        state.selectedOfficeIds = [office.id];
        state.visibleOfficeMarinaIds = [office.id];
        persistLocalState();
        render();
        zoomToOfficeCoverage(office.id);
        await delay(120);
        await waitForMapToSettle({ timeoutMs: 12000, stableMs: 700, pollMs: 100 });
        setStatus(`Generating office ${officeIndex + 1}/${offices.length}: ${office.name}`);
        const officeQueueEntries = officeScopeQueue
          .map((entry, queueIndex) => ({ ...entry, queueIndex }))
          .filter((entry) => entry.officeId === office.id);
        for (let scopeIndex = 0; scopeIndex < officeQueueEntries.length; scopeIndex += 1) {
          const entry = officeQueueEntries[scopeIndex];
          const globalIndex = entry.queueIndex;
          const progressStatus = `Preparing ${globalIndex + 1}/${officeScopeQueue.length} reports. Download each file from this page when it is ready.`;
          launcherReports[globalIndex].status = "Generating...";
          updateReportLauncherWindow(reportWindow, "All Trip Reports", launcherReports, {
            status: progressStatus,
            complete: false,
            summaryActions: currentSummaryActions,
          });
          try {
            const built = await generateOfficeTripReportPdf(office.id, {
              missionScope: entry.scope?.key || "all",
              onProgress: (message) => {
                launcherReports[globalIndex].status = String(message || "Generating...");
                updateReportLauncherWindow(reportWindow, "All Trip Reports", launcherReports, {
                  status: progressStatus,
                  complete: false,
                  summaryActions: currentSummaryActions,
                });
              },
            });
            if (reportWindow) {
              const reportUrls = createPdfLauncherUrls(built.blob, built.fileName);
              launcherReports[globalIndex].status = built.fileName;
              launcherReports[globalIndex].ready = true;
              launcherReports[globalIndex].openUrl = reportUrls.openUrl;
              launcherReports[globalIndex].downloadUrl = reportUrls.downloadUrl;
              launcherReports[globalIndex].fileName = built.fileName;
            } else {
              downloadBlob(built.blob, built.fileName);
            }
            {
              const _mileageCost = Number(built.plan?.totals?.mileageCost) || 0;
              const _airfare    = Number(built.plan?.totals?.airfareCost)  || 0;
              const _perDiem    = Number(built.plan?.totals?.perDiemCost)  || 0;
              const _travelCost = _mileageCost + _airfare + _perDiem;
              const _marina     = Number(built.plan?.totals?.marinaRevenue) || 0;
              const _support    = Number(built.plan?.totals?.laborEquipmentCost) || 0;
              summaryRows.push({
                title: built.title,
                office: built.office?.name || entry.officeName,
                scope: String(entry.scope?.key || "all").toUpperCase(),
                marinas: built.plan?.totals?.marinaCount || 0,
                pricePerMarina: built.plan?.totals?.pricePerMarina || 0,
                marinaRevenue: _marina,
                tripDays: built.plan?.totals?.baseTripDays || 0,
                driveMiles: built.plan?.totals?.driveMiles || 0,
                mileageCost: _mileageCost,
                airfare: _airfare,
                perDiem: _perDiem,
                travelCost: _travelCost,
                supportDays: (built.plan?.totals?.weatherContingencyDays || 0) + (built.plan?.totals?.travelOnlyDays || 0),
                supportDayRate: built.plan?.totals?.supportDayRate || 0,
                supportCost: _support,
                estimateTotal: _marina + _travelCost + _support,
              });
            }
            currentSummaryActions = await buildTripCostSummaryActions(summaryRows);
            totalDownloads += 1;
            app.ui.toast(`Prepared ${built.fileName}`);
          } catch (reportErr) {
            launcherReports[globalIndex].status = `Failed: ${reportErr?.message || "unknown error"}`;
            console.error(`[ImageGenerator] report failed for ${office.name}:`, reportErr);
          }
          updateReportLauncherWindow(reportWindow, "All Trip Reports", launcherReports, {
            status: `Processed ${globalIndex + 1}/${officeScopeQueue.length} reports.`,
            complete: globalIndex === officeScopeQueue.length - 1,
            summaryActions: currentSummaryActions,
          });
        }
      }
      setStatus(`Prepared ${totalDownloads} trip PDF${totalDownloads === 1 ? "" : "s"}.`);
      app.ui.toast(`Prepared ${totalDownloads} trip PDF${totalDownloads === 1 ? "" : "s"}.`);
    } finally {
      state.planning.tripOfficeId = previousTripOfficeId;
      state.planning.tripGroupFilter = previousTripGroupFilter;
      state.selectedOfficeIds = previousSelectedOfficeIds;
      state.visibleOfficeMarinaIds = previousVisibleOfficeMarinaIds;
      persistLocalState();
      if (previousView?.bounds) {
        const bounds = L.latLngBounds(
          [Number(previousView.bounds.south), Number(previousView.bounds.west)],
          [Number(previousView.bounds.north), Number(previousView.bounds.east)],
        );
        if (bounds.isValid()) map.fitBounds(bounds, { animate: false });
      } else if (previousView?.center) {
        map.setView([Number(previousView.center.lat), Number(previousView.center.lng)], Number(previousView.zoom) || state.defaultZoom, { animate: false });
      }
      if (typeof map.setBearing === "function") map.setBearing(Number(previousView?.bearing) || 0);
      state.activeIndex = previousActiveIndex;
      drawPlanningOverlay(currentItem());
      map.invalidateSize(false);
      render();
    }
  }

  async function generateCombinedReportBookPdf() {
    const previousTripOfficeId = state.planning.tripOfficeId;
    const previousTripGroupFilter = state.planning.tripGroupFilter;
    const offices = state.offices.filter((office) => {
      state.planning.tripGroupFilter = office.groupName || "";
      const has = officeReportScopes(office.id, { excludeReportExcluded: true }).length > 0;
      state.planning.tripGroupFilter = previousTripGroupFilter;
      return has;
    });
    if (!offices.length) {
      app.ui.toast("No office reports are available.");
      return;
    }
    const officeScopeQueue = offices.flatMap((office) => {
      state.planning.tripGroupFilter = office.groupName || "";
      const scopes = officeReportScopes(office.id, { excludeReportExcluded: true });
      state.planning.tripGroupFilter = previousTripGroupFilter;
      return scopes.map((scope) => ({ officeId: office.id, officeName: office.name, scope }));
    });
    const launcherReports = officeScopeQueue.map((entry, index) => ({
      id: `${entry.officeId}:${entry.scope?.key || "all"}:${index}`,
      label: reportLauncherLabel(entry.scope),
      status: "Queued",
      ready: false,
      openUrl: "",
      downloadUrl: "",
      fileName: "",
    }));
    const reportWindow = openPreparedReportWindow(
      "Preparing a combined trip report book. Leave this page open while the summary and report pages are assembled.",
      "imggen-combined-trip-report-book",
    );
    const builtReports = [];
    const summaryRows = [];
    let currentSummaryActions = [];
    let launcherActions = [];

    updateReportLauncherWindow(reportWindow, "Combined Trip Report Book", launcherReports, {
      status: `Preparing content for 0/${officeScopeQueue.length} reports...`,
      complete: false,
      summaryActions: currentSummaryActions,
    });

    try {
    for (let index = 0; index < officeScopeQueue.length; index += 1) {
      const entry = officeScopeQueue[index];
      const entryOffice = state.offices.find((o) => o.id === entry.officeId);
      state.planning.tripOfficeId = entry.officeId;
      state.planning.tripGroupFilter = entryOffice?.groupName || "";
      launcherReports[index].status = "Generating...";
      updateReportLauncherWindow(reportWindow, "Combined Trip Report Book", launcherReports, {
        status: `Preparing content for ${index + 1}/${officeScopeQueue.length} reports...`,
        complete: false,
        summaryActions: currentSummaryActions,
      });
      const report = await generateOfficeTripReportHtml(entry.officeId, {
        missionScope: entry.scope?.key || "all",
        onProgress: (message) => {
          launcherReports[index].status = String(message || "Generating...");
          updateReportLauncherWindow(reportWindow, "Combined Trip Report Book", launcherReports, {
            status: `Preparing content for ${index + 1}/${officeScopeQueue.length} reports...`,
            complete: false,
            summaryActions: currentSummaryActions,
          });
        },
      });
      builtReports.push(report);
      {
        const _mileageCost = Number(report.plan?.totals?.mileageCost) || 0;
        const _airfare    = Number(report.plan?.totals?.airfareCost)  || 0;
        const _perDiem    = Number(report.plan?.totals?.perDiemCost)  || 0;
        const _travelCost = _mileageCost + _airfare + _perDiem;
        const _marina     = Number(report.plan?.totals?.marinaRevenue) || 0;
        const _support    = Number(report.plan?.totals?.laborEquipmentCost) || 0;
        summaryRows.push({
          title: report.title,
          office: report.office?.name || entry.officeName,
          scope: String(entry.scope?.key || "all").toUpperCase(),
          marinas: report.plan?.totals?.marinaCount || 0,
          pricePerMarina: report.plan?.totals?.pricePerMarina || 0,
          marinaRevenue: _marina,
          tripDays: report.plan?.totals?.baseTripDays || 0,
          driveMiles: report.plan?.totals?.driveMiles || 0,
          mileageCost: _mileageCost,
          airfare: _airfare,
          perDiem: _perDiem,
          travelCost: _travelCost,
          supportDays: (report.plan?.totals?.weatherContingencyDays || 0) + (report.plan?.totals?.travelOnlyDays || 0),
          supportDayRate: report.plan?.totals?.supportDayRate || 0,
          supportCost: _support,
          estimateTotal: _marina + _travelCost + _support,
        });
      }
      currentSummaryActions = await buildTripCostSummaryActions(summaryRows);
      launcherReports[index].status = "Preparing standalone PDF...";
      updateReportLauncherWindow(reportWindow, "Combined Trip Report Book", launcherReports, {
        status: `Preparing standalone report ${index + 1}/${officeScopeQueue.length}...`,
        complete: false,
        summaryActions: currentSummaryActions,
      });
      const standaloneFileName = `${sanitizeFilePart(report.title.replace(/:/g, ""), "trip-report")}.pdf`;
      const standaloneBlob = await reportHtmlToPdfBlob(report, {
        statusLabel: `Preparing PDF for ${report.title}...`,
        onProgress: (message) => {
          launcherReports[index].status = String(message || "Preparing standalone PDF...");
          updateReportLauncherWindow(reportWindow, "Combined Trip Report Book", launcherReports, {
            status: `Preparing standalone report ${index + 1}/${officeScopeQueue.length}...`,
            complete: false,
            summaryActions: currentSummaryActions,
          });
        },
      });
      const standaloneUrls = createPdfLauncherUrls(standaloneBlob, standaloneFileName);
      launcherReports[index].status = "Prepared for book assembly";
      launcherReports[index].ready = true;
      launcherReports[index].openUrl = standaloneUrls.openUrl;
      launcherReports[index].downloadUrl = standaloneUrls.downloadUrl;
      launcherReports[index].fileName = standaloneFileName;
      updateReportLauncherWindow(reportWindow, "Combined Trip Report Book", launcherReports, {
        status: `Prepared content for ${index + 1}/${officeScopeQueue.length} reports...`,
        complete: false,
        summaryActions: currentSummaryActions,
      });
    }

    const summaryTitle = "Trip Cost Summary";
    const pdf = await createLandscapeLetterPdf();
    let pdfPageIndex = 0;

    updateReportLauncherWindow(reportWindow, "Combined Trip Report Book", launcherReports, {
      status: "Rendering trip cost summary into the combined PDF...",
      complete: false,
      summaryActions: currentSummaryActions,
    });
    const summaryCanvases = await renderReportHtmlToCanvases(
      { html: buildTripCostSummaryHtml(summaryTitle, summaryRows) },
      {
        statusLabel: `Preparing ${summaryTitle} for combined PDF...`,
        onProgress: (message) => {
          setStatus(message);
          updateReportLauncherWindow(reportWindow, "Combined Trip Report Book", launcherReports, {
            status: String(message || "Rendering trip cost summary into the combined PDF..."),
            complete: false,
            summaryActions: currentSummaryActions,
          });
        },
      },
    );
    pdfPageIndex = appendCanvasesToPdf(pdf, summaryCanvases, { pageIndex: pdfPageIndex });

    for (let index = 0; index < builtReports.length; index += 1) {
      const report = builtReports[index];
      launcherReports[index].status = "Adding to combined PDF...";
      updateReportLauncherWindow(reportWindow, "Combined Trip Report Book", launcherReports, {
        status: `Assembling combined PDF ${index + 1}/${builtReports.length}...`,
        complete: false,
        summaryActions: currentSummaryActions,
      });
      const reportCanvases = await renderReportHtmlToCanvases(report, {
        statusLabel: `Preparing ${report.title} for combined PDF...`,
        onProgress: (message) => {
          launcherReports[index].status = String(message || "Adding to combined PDF...");
          updateReportLauncherWindow(reportWindow, "Combined Trip Report Book", launcherReports, {
            status: `Assembling combined PDF ${index + 1}/${builtReports.length}...`,
            complete: false,
            summaryActions: currentSummaryActions,
          });
        },
      });
      pdfPageIndex = appendCanvasesToPdf(pdf, reportCanvases, { pageIndex: pdfPageIndex });
      launcherReports[index].status = "Included in combined PDF";
      updateReportLauncherWindow(reportWindow, "Combined Trip Report Book", launcherReports, {
        status: `Assembled ${index + 1}/${builtReports.length} reports into the combined PDF...`,
        complete: false,
        summaryActions: currentSummaryActions,
      });
    }

    const combinedFileName = `${sanitizeFilePart(`Trip Report Book ${new Date().toLocaleDateString("en-US")}`, "trip-report-book")}.pdf`;
    const combinedBlob = pdf.output("blob");
    const summaryArtifacts = await createTripCostSummaryArtifacts(summaryTitle, summaryRows);
    if (reportWindow) {
      const combinedUrls = createPdfLauncherUrls(combinedBlob, combinedFileName);
      launcherActions = [
        { label: "Open Combined PDF", href: combinedUrls.openUrl },
        { label: "Download Combined PDF", href: combinedUrls.downloadUrl, download: combinedFileName },
        { label: "Open Cost Summary", href: summaryArtifacts.openUrl },
        { label: "Download Cost Summary PDF", href: summaryArtifacts.pdfDownloadUrl, download: summaryArtifacts.pdfFileName },
        { label: "Download Cost Summary Excel", href: summaryArtifacts.excelDownloadUrl, download: summaryArtifacts.excelFileName },
      ];
      updateReportLauncherWindow(reportWindow, "Combined Trip Report Book", launcherReports, {
        status: `Combined PDF ready with ${builtReports.length} reports plus the summary.`,
        complete: true,
        summaryActions: launcherActions,
      });
    } else {
      downloadBlob(combinedBlob, combinedFileName);
      downloadBlob(summaryArtifacts.pdfBlob, summaryArtifacts.pdfFileName);
      downloadBlob(summaryArtifacts.excelBlob, summaryArtifacts.excelFileName);
    }
    setStatus(`Combined trip report book ready: ${combinedFileName}`);
    app.ui.toast("Combined trip report book ready.");
    } finally {
      state.planning.tripOfficeId = previousTripOfficeId;
      state.planning.tripGroupFilter = previousTripGroupFilter;
    }
  }

  async function generateCombinedGroupReportPdf() {
    const activeContextClient = app.services?.context?.getActiveClient?.();
    const clients = Array.isArray(state.catalog.clients) ? state.catalog.clients : [];
    const selectedClientId = activeContextClient?.id || String(state.catalog.selectedClientId || clients[0]?.id || "");
    const explicitGroups = Array.isArray(state.portfolioCustomGroups?.[selectedClientId]) ? state.portfolioCustomGroups[selectedClientId] : [];
    let marinas = Array.isArray(state.catalog.marinas) ? state.catalog.marinas : [];
    if (selectedClientId) marinas = marinas.filter(m => String(m.clientId || m.clientSlug || "safeharbor") === selectedClientId);
    const catalogGroupNames = [...new Set(marinas.map(m => String(m.group || "")).filter(Boolean))].filter(g => !explicitGroups.includes(g)).sort();
    const allGroups = [...explicitGroups, ...catalogGroupNames];
    const includedGroups = allGroups.filter(g => getGroupMeta(selectedClientId, g).includeInCombined);

    if (!includedGroups.length) {
      app.ui.toast("No groups are selected for the combined PDF. Toggle the PDF button on at least one group.");
      return;
    }
    const missingOffice = includedGroups.filter(g => !getGroupMeta(selectedClientId, g).officeId);
    if (missingOffice.length) {
      app.ui.alertModal(
        `${missingOffice.length === 1 ? `"${missingOffice[0]}" does` : `These groups don't`} have an office assigned yet:\n\n${missingOffice.join("\n")}\n\nOpen the group row and pick an office from the dropdown, then try Combined PDF again.`,
        { title: "Can't Build Combined PDF" },
      );
      return;
    }

    const previousTripGroupFilter = state.planning.tripGroupFilter;
    const previousTravelMode = state.planning.travelMode;
    const previousHomeAirportOverride = state.planning.homeAirportOverride;

    const launcherReports = includedGroups.map((groupName, index) => ({
      id: `group:${groupName}:${index}`,
      label: groupName,
      status: "Queued",
      ready: false,
      openUrl: "",
      downloadUrl: "",
      fileName: "",
    }));

    const reportWindow = openPreparedReportWindow(
      "Preparing a combined group trip report. Leave this page open while reports are assembled.",
      "imggen-combined-group-report",
    );
    const builtReports = [];
    const summaryRows = [];
    let currentSummaryActions = [];
    let launcherActions = [];

    updateReportLauncherWindow(reportWindow, "Combined Group Trip Report", launcherReports, {
      status: `Preparing content for 0/${includedGroups.length} group${includedGroups.length === 1 ? "" : "s"}...`,
      complete: false,
      summaryActions: currentSummaryActions,
    });

    try {
      for (let index = 0; index < includedGroups.length; index += 1) {
        const groupName = includedGroups[index];
        const gm = getGroupMeta(selectedClientId, groupName);

        state.planning.tripGroupFilter = groupName;
        if (gm.flyIn) {
          state.planning.travelMode = "air";
          state.planning.homeAirportOverride = gm.flyInAirport || "";
        } else {
          state.planning.travelMode = previousTravelMode;
          state.planning.homeAirportOverride = previousHomeAirportOverride;
        }

        launcherReports[index].status = "Generating...";
        updateReportLauncherWindow(reportWindow, "Combined Group Trip Report", launcherReports, {
          status: `Preparing content for ${index + 1}/${includedGroups.length} group${includedGroups.length === 1 ? "" : "s"}...`,
          complete: false,
          summaryActions: currentSummaryActions,
        });

        const report = await generateOfficeTripReportHtml(gm.officeId, {
          missionScope: "all",
          onProgress: (message) => {
            launcherReports[index].status = String(message || "Generating...");
            updateReportLauncherWindow(reportWindow, "Combined Group Trip Report", launcherReports, {
              status: `Preparing content for ${index + 1}/${includedGroups.length} group${includedGroups.length === 1 ? "" : "s"}...`,
              complete: false,
              summaryActions: currentSummaryActions,
            });
          },
        });

        builtReports.push(report);
        {
          const _mileageCost = Number(report.plan?.totals?.mileageCost) || 0;
          const _airfare    = Number(report.plan?.totals?.airfareCost)  || 0;
          const _perDiem    = Number(report.plan?.totals?.perDiemCost)  || 0;
          const _travelCost = _mileageCost + _airfare + _perDiem;
          const _marina     = Number(report.plan?.totals?.marinaRevenue) || 0;
          const _support    = Number(report.plan?.totals?.laborEquipmentCost) || 0;
          summaryRows.push({
            title: groupName,
            office: report.office?.name || "",
            scope: "ALL",
            marinas: report.plan?.totals?.marinaCount || 0,
            pricePerMarina: report.plan?.totals?.pricePerMarina || 0,
            marinaRevenue: _marina,
            tripDays: report.plan?.totals?.baseTripDays || 0,
            driveMiles: report.plan?.totals?.driveMiles || 0,
            mileageCost: _mileageCost,
            airfare: _airfare,
            perDiem: _perDiem,
            travelCost: _travelCost,
            supportDays: (report.plan?.totals?.weatherContingencyDays || 0) + (report.plan?.totals?.travelOnlyDays || 0),
            supportDayRate: report.plan?.totals?.supportDayRate || 0,
            supportCost: _support,
            estimateTotal: _marina + _travelCost + _support,
          });
        }
        currentSummaryActions = await buildTripCostSummaryActions(summaryRows);

        launcherReports[index].status = "Preparing standalone PDF...";
        updateReportLauncherWindow(reportWindow, "Combined Group Trip Report", launcherReports, {
          status: `Preparing standalone PDF for ${index + 1}/${includedGroups.length} group${includedGroups.length === 1 ? "" : "s"}...`,
          complete: false,
          summaryActions: currentSummaryActions,
        });

        const standaloneFileName = `${sanitizeFilePart(groupName, "group-trip-report")}.pdf`;
        const standaloneBlob = await reportHtmlToPdfBlob(report, {
          statusLabel: `Preparing PDF for ${groupName}...`,
          onProgress: (message) => {
            launcherReports[index].status = String(message || "Preparing standalone PDF...");
            updateReportLauncherWindow(reportWindow, "Combined Group Trip Report", launcherReports, {
              status: `Preparing standalone PDF for ${index + 1}/${includedGroups.length} group${includedGroups.length === 1 ? "" : "s"}...`,
              complete: false,
              summaryActions: currentSummaryActions,
            });
          },
        });
        const standaloneUrls = createPdfLauncherUrls(standaloneBlob, standaloneFileName);
        launcherReports[index].status = "Prepared for book assembly";
        launcherReports[index].ready = true;
        launcherReports[index].openUrl = standaloneUrls.openUrl;
        launcherReports[index].downloadUrl = standaloneUrls.downloadUrl;
        launcherReports[index].fileName = standaloneFileName;
        updateReportLauncherWindow(reportWindow, "Combined Group Trip Report", launcherReports, {
          status: `Prepared content for ${index + 1}/${includedGroups.length} group${includedGroups.length === 1 ? "" : "s"}...`,
          complete: false,
          summaryActions: currentSummaryActions,
        });
      }

      const summaryTitle = "Trip Cost Summary";
      const pdf = await createLandscapeLetterPdf();
      let pdfPageIndex = 0;

      updateReportLauncherWindow(reportWindow, "Combined Group Trip Report", launcherReports, {
        status: "Rendering trip cost summary into the combined PDF...",
        complete: false,
        summaryActions: currentSummaryActions,
      });
      const summaryCanvases = await renderReportHtmlToCanvases(
        { html: buildTripCostSummaryHtml(summaryTitle, summaryRows) },
        {
          statusLabel: `Preparing ${summaryTitle} for combined PDF...`,
          onProgress: (message) => {
            setStatus(message);
            updateReportLauncherWindow(reportWindow, "Combined Group Trip Report", launcherReports, {
              status: String(message || "Rendering trip cost summary into the combined PDF..."),
              complete: false,
              summaryActions: currentSummaryActions,
            });
          },
        },
      );
      pdfPageIndex = appendCanvasesToPdf(pdf, summaryCanvases, { pageIndex: pdfPageIndex });

      for (let index = 0; index < builtReports.length; index += 1) {
        const report = builtReports[index];
        launcherReports[index].status = "Adding to combined PDF...";
        updateReportLauncherWindow(reportWindow, "Combined Group Trip Report", launcherReports, {
          status: `Assembling combined PDF ${index + 1}/${builtReports.length}...`,
          complete: false,
          summaryActions: currentSummaryActions,
        });
        const reportCanvases = await renderReportHtmlToCanvases(report, {
          statusLabel: `Preparing ${includedGroups[index]} for combined PDF...`,
          onProgress: (message) => {
            launcherReports[index].status = String(message || "Adding to combined PDF...");
            updateReportLauncherWindow(reportWindow, "Combined Group Trip Report", launcherReports, {
              status: `Assembling combined PDF ${index + 1}/${builtReports.length}...`,
              complete: false,
              summaryActions: currentSummaryActions,
            });
          },
        });
        pdfPageIndex = appendCanvasesToPdf(pdf, reportCanvases, { pageIndex: pdfPageIndex });
        launcherReports[index].status = "Included in combined PDF";
        updateReportLauncherWindow(reportWindow, "Combined Group Trip Report", launcherReports, {
          status: `Assembled ${index + 1}/${builtReports.length} report${builtReports.length === 1 ? "" : "s"} into the combined PDF...`,
          complete: false,
          summaryActions: currentSummaryActions,
        });
      }

      const combinedFileName = `${sanitizeFilePart(`Group Report Book ${new Date().toLocaleDateString("en-US")}`, "group-report-book")}.pdf`;
      const combinedBlob = pdf.output("blob");
      const summaryArtifacts = await createTripCostSummaryArtifacts(summaryTitle, summaryRows);
      if (reportWindow) {
        const combinedUrls = createPdfLauncherUrls(combinedBlob, combinedFileName);
        launcherActions = [
          { label: "Open Combined PDF", href: combinedUrls.openUrl },
          { label: "Download Combined PDF", href: combinedUrls.downloadUrl, download: combinedFileName },
          { label: "Open Cost Summary", href: summaryArtifacts.openUrl },
          { label: "Download Cost Summary PDF", href: summaryArtifacts.pdfDownloadUrl, download: summaryArtifacts.pdfFileName },
          { label: "Download Cost Summary Excel", href: summaryArtifacts.excelDownloadUrl, download: summaryArtifacts.excelFileName },
        ];
        updateReportLauncherWindow(reportWindow, "Combined Group Trip Report", launcherReports, {
          status: `Combined PDF ready with ${builtReports.length} group report${builtReports.length === 1 ? "" : "s"} plus the summary.`,
          complete: true,
          summaryActions: launcherActions,
        });
      } else {
        downloadBlob(combinedBlob, combinedFileName);
        downloadBlob(summaryArtifacts.pdfBlob, summaryArtifacts.pdfFileName);
        downloadBlob(summaryArtifacts.excelBlob, summaryArtifacts.excelFileName);
      }
      setStatus(`Combined group trip report ready: ${combinedFileName}`);
      app.ui.toast("Combined group trip report ready.");
    } finally {
      state.planning.tripGroupFilter = previousTripGroupFilter;
      state.planning.travelMode = previousTravelMode;
      state.planning.homeAirportOverride = previousHomeAirportOverride;
    }
  }

  async function generateOfficeTripReportHtml(officeId, { missionScope = "all", onProgress = null } = {}) {
    const reportWindow = null;
    return withStableReportViewport(async () => {
      const previousSuppressUiRouteSync = suppressUiRouteSync;
      suppressUiRouteSync = true;
      const { scope, rawPlan, plan, reportRouteScopeKey } = await prepareReportPlan(officeId, { missionScope, onProgress });
      const { office, marinas } = scope;
      if (!office || !marinas.length) {
        throw new Error("No reportable marinas are assigned to that office.");
      }
      const preparingMessage = `Preparing trip report for ${office.name}${missionScope !== "all" ? ` (${missionScope})` : ""}...`;
      setStatus(preparingMessage);
      if (typeof onProgress === "function") onProgress(preparingMessage);
      const previousView = snapshotMapView();
      const previousActiveIndex = state.activeIndex;
      const previousActiveItem = currentItem();
      const imageryLayerIds = findEsriImageryLayerIds();
      const imageryVisibility = captureLayerVisibility(imageryLayerIds);
      suppressAutoSaveView = true;
      try {
        setLayerVisibility(imageryLayerIds, true);
        await warmVisibleLayersForCapture({ extraDelayMs: 900 });
        const reportItems = Array.from(new Map(rawPlan.route.map((stop) => [stop.item.id, stop.item])).values());
        setLayerVisibility(imageryLayerIds, false);
        await warmVisibleLayersForCapture({ extraDelayMs: 250 });
        if (typeof onProgress === "function") onProgress(`Capturing coverage map for ${office.name}${missionScope !== "all" ? ` (${missionScope})` : ""}...`);
        await prepareOfficeCoverageCapture(officeId, { excludeReportExcluded: true, missionScope, routeScopeKey: reportRouteScopeKey });
        const mapImage = await captureCurrentMap({ includeMetadata: true });
        setLayerVisibility(imageryLayerIds, true);
        await warmVisibleLayersForCapture({ extraDelayMs: 350 });
        if (typeof onProgress === "function") onProgress(`Capturing marina images for ${office.name}${missionScope !== "all" ? ` (${missionScope})` : ""}...`);
        const marinaImages = await captureReportMarinaImages(reportItems, { onProgress });
        setLayerVisibility(imageryLayerIds, false);
        const title = reportTitleForScope(office, missionScope, marinas);
        const html = tripReportHtml({
          office,
          plan,
          mapImageDataUrl: mapImage.dataUrl,
          mapImageWidth: mapImage.width,
          mapImageHeight: mapImage.height,
          marinaImages,
          reportTitle: title,
        });
        return { office, marinas, plan, title, html, reportWindow };
      } finally {
        suppressUiRouteSync = previousSuppressUiRouteSync;
        clearCaptureProgress();
        window.clearTimeout(autoSaveViewTimer);
        autoSaveViewTimer = 0;
        suppressAutoSaveView = false;
        state.activeIndex = previousActiveIndex;
        const active = (previousActiveIndex >= 0 && state.items[previousActiveIndex]) ? state.items[previousActiveIndex] : previousActiveItem;
        restoreLayerVisibility(imageryVisibility);
        if (previousView?.bounds) {
          const bounds = L.latLngBounds(
            [Number(previousView.bounds.south), Number(previousView.bounds.west)],
            [Number(previousView.bounds.north), Number(previousView.bounds.east)],
          );
          if (bounds.isValid()) map.fitBounds(bounds, { animate: false });
        } else if (previousView?.center) {
          map.setView([Number(previousView.center.lat), Number(previousView.center.lng)], Number(previousView.zoom) || state.defaultZoom, { animate: false });
        }
        if (typeof map.setBearing === "function") map.setBearing(Number(previousView?.bearing) || 0);
        drawPlanningOverlay(active);
        map.invalidateSize(false);
        render();
      }
    });
  }

  async function focusItem(index, { saveCurrent = true, overlayItems = null } = {}) {
    if (index < 0 || index >= state.items.length) return;
    if (saveCurrent && state.editMarinaViews) saveCurrentViewForActive();
    state.activeIndex = index;
    const item = currentItem();
    applySavedView(item);
    map.invalidateSize(false);
    await delay(100);
    drawPlanningOverlay(item, { items: overlayItems });
    await waitForMapToSettle();
    render();
    setStatus(`Previewing ${index + 1}/${state.items.length}: ${item?.name || "item"}`);
  }

  async function focusRelative(step) {
    if (!state.items.length) return;
    const nextIndex = state.activeIndex < 0
      ? 0
      : Math.max(0, Math.min(state.items.length - 1, state.activeIndex + step));
    await focusItem(nextIndex, { saveCurrent: true });
  }

  function onGlobalKeyDown(event) {
    if (!containerEl || exporting || !state.items.length) return;
    if (event.defaultPrevented) return;
    if (event.ctrlKey || event.metaKey || event.altKey) return;
    if (isEditableTarget(event.target)) return;
    if (event.key === "ArrowLeft" || event.key === "[") {
      event.preventDefault();
      focusRelative(-1);
      return;
    }
    if (event.key === "ArrowRight" || event.key === "]") {
      event.preventDefault();
      focusRelative(1);
    }
  }

  function suppressDragDefault(e) {
    if (e.dataTransfer?.types?.includes("Files")) e.preventDefault();
  }

  function onTilesetReady(e) {
    const marina = e.detail?.marina;
    if (marina && marina === state.projectSettings.selectedProjectId) {
      loadDigitalAssets(marina, "tilesets").catch(() => {});
    }
  }

  // The Slip Inventory tool's Generate Deliverables flow registers files here as it goes
  // (one deliverable at a time, not all-or-nothing at the end) -- refresh right away so the
  // checklist checkmarks and category file lists flip live instead of needing a full reload.
  function onDeliverableRegistered(e) {
    const code = String(e.detail?.code || "");
    if (!code) return;
    loadDeliveryData(code).catch(() => {});
    const category = String(e.detail?.category || "");
    if (category) loadDigitalAssets(code, category).catch(() => {});
  }

  async function loadDataset(file) {
    setStatus(`Parsing ${file.name}...`);
    const parsed = await parseDatasetFile(file);
    await applyParsedDataset(parsed, file.name);
  }

  function selectedItems() {
    return state.batchScope === "all"
      ? [...state.items]
      : state.items.filter((item) => item.selected);
  }

  async function exportItems(items, { zip = true } = {}) {
    if (exporting) return;
    if (!items.length) {
      app.ui.toast("No preview rows are selected for export.");
      return;
    }
    exporting = true;
    cancelRequested = false;
    saveCurrentViewForActive();
    render();
    const originalCenter = map.getCenter?.();
    const originalZoom = map.getZoom?.();
    const originalBearing = typeof map.getBearing === "function" ? map.getBearing() : 0;
    const ext = state.format === "png" ? "png" : "jpg";
    try {
      if (!zip && items.length === 1) {
        const only = items[0];
        setCaptureProgress(0, 1);
        setStatus(`Capturing ${only.name}`);
        applySavedView(only);
        map.invalidateSize(false);
        drawPlanningOverlay(only);
        await waitForMapToSettle();
        if (cancelRequested) throw new Error("Export cancelled.");
        const dataUrl = await captureCurrentMap();
        const blob = await dataUrlToBlob(dataUrl);
        setCaptureProgress(1, 1);
        triggerDownload(blob, `${sanitizeFilePart(`${state.prefix}_${String((state.items.indexOf(only) + 1)).padStart(3, "0")}_${only.name}`, "capture")}.${ext}`);
        setStatus(`Exported ${only.name}.`);
        app.ui.toast(`Exported ${only.name}.`);
      } else {
        const archive = new JSZip();
        setCaptureProgress(0, items.length);
        for (let index = 0; index < items.length; index += 1) {
          if (cancelRequested) throw new Error("Export cancelled.");
          const item = items[index];
          setStatus(`Capturing ${index + 1}/${items.length}: ${item.name}`);
          applySavedView(item);
          map.invalidateSize(false);
          drawPlanningOverlay(item);
          await waitForMapToSettle();
          if (cancelRequested) throw new Error("Export cancelled.");
          const dataUrl = await captureCurrentMap();
          const base64 = String(dataUrl).split(",")[1] || "";
          const order = state.items.indexOf(item) + 1;
          archive.file(`${sanitizeFilePart(`${state.prefix}_${String(order).padStart(3, "0")}_${item.name}`, "capture")}.${ext}`, base64, { base64: true });
          setCaptureProgress(index + 1, items.length);
        }
        if (cancelRequested) throw new Error("Export cancelled.");
        setStatus("Packaging ZIP...");
        const blob = await archive.generateAsync({ type: "blob" });
        triggerDownload(blob, `${sanitizeFilePart(state.prefix || state.datasetName || "marina-captures", "marina-captures")}.zip`);
        setStatus(`Exported ${items.length} image${items.length === 1 ? "" : "s"} to ZIP.`);
        app.ui.toast(`Exported ${items.length} image${items.length === 1 ? "" : "s"}.`);
      }
    } catch (error) {
      console.error("[ImageGenerator] export failed", error);
      if (/cancelled/i.test(String(error?.message || ""))) {
        setStatus("Export cancelled.");
        app.ui.toast("Image export cancelled.");
      } else {
        setStatus(`Export failed: ${error?.message || "unknown error"}`);
        app.ui.toast("Image export failed.");
      }
    } finally {
      clearCaptureProgress();
      const active = currentItem();
      try { ensureCaptureLayer().clearLayers(); } catch {}
      try {
        if (active) applySavedView(active);
        else map.setView(originalCenter, originalZoom, { animate: false });
      } catch {}
      try {
        if (!active && typeof map.setBearing === "function") map.setBearing(originalBearing || 0);
      } catch {}
      try { if (active) drawPlanningOverlay(active); } catch {}
      try { map.invalidateSize(false); } catch {}
      exporting = false;
      cancelRequested = false;
      render();
    }
  }

  function triggerDownload(blob, fileName) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      URL.revokeObjectURL(url);
      a.remove();
    }, 300);
  }

  async function dataUrlToBlob(dataUrl) {
    const response = await fetch(dataUrl);
    return response.blob();
  }

  function toggleAllSelected(next) {
    state.items.forEach((item) => { item.selected = !!next; });
    render();
  }

  function openPreparedReportWindow(message = "The report window will update automatically when generation is complete.", targetName = "_blank") {
    if (!ENABLE_REPORT_POPUPS) return null;
    let reportWindow = window.open("about:blank", targetName);
    if (!reportWindow || reportWindow.closed) {
      try {
        const anchor = document.createElement("a");
        anchor.href = "about:blank";
        anchor.target = targetName;
        anchor.rel = "noopener";
        anchor.style.display = "none";
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        reportWindow = window.open("", targetName);
      } catch (_) {}
    }
    if (reportWindow && !reportWindow.closed) {
      reportWindow.document.open();
      reportWindow.document.write(`<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>Preparing Trip Report</title>
    <style>
      body {
        margin: 0;
        font: 16px/1.5 "Segoe UI", Arial, sans-serif;
        color: #10233f;
        background: linear-gradient(180deg, #f8fbff 0%, #eef4fb 100%);
        display: grid;
        place-items: center;
        min-height: 100vh;
      }
      .card {
        width: min(520px, calc(100vw - 48px));
        padding: 28px 30px;
        border-radius: 16px;
        background: rgba(255,255,255,0.92);
        border: 1px solid rgba(23,59,103,0.14);
        box-shadow: 0 20px 48px rgba(16,35,63,0.12);
      }
      h1 {
        margin: 0 0 8px;
        font-size: 28px;
        line-height: 1.1;
        color: #173b67;
      }
      p {
        margin: 0;
        color: #415a77;
      }
    </style>
  </head>
  <body>
    <div class="card">
      <h1>Preparing Trip Report</h1>
      <p>${reportEsc(message)}</p>
    </div>
  </body>
</html>`);
      reportWindow.document.close();
      try { reportWindow.blur?.(); } catch (_) {}
      reclaimHydrotoolsFocus();
    }
    return reportWindow;
  }

  function buildPdfViewerHtml(fileName, pdfUrl) {
    return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>${reportEsc(fileName || "Trip Report")}</title>
    <style>
      body {
        margin: 0;
        min-height: 100vh;
        font: 14px/1.5 "Segoe UI", Arial, sans-serif;
        color: #10233f;
        background: linear-gradient(180deg, #eef5fb 0%, #e6eff8 100%);
      }
      .shell {
        display: grid;
        grid-template-rows: auto 1fr;
        min-height: 100vh;
      }
      .toolbar {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        padding: 16px 20px;
        background: rgba(255,255,255,0.94);
        border-bottom: 1px solid rgba(23,59,103,0.12);
        box-shadow: 0 10px 28px rgba(16,35,63,0.08);
      }
      .title {
        font-weight: 700;
        color: #173b67;
      }
      .actions {
        display: flex;
        gap: 10px;
      }
      .btn {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        padding: 8px 14px;
        border-radius: 999px;
        border: 1px solid rgba(23,59,103,0.16);
        background: #ffffff;
        color: #0f4aa3;
        font-weight: 700;
        text-decoration: none;
      }
      .btn:hover {
        background: #f8fbff;
      }
      iframe {
        width: 100%;
        height: calc(100vh - 74px);
        border: 0;
        background: #dbe7f3;
      }
    </style>
  </head>
  <body>
    <div class="shell">
      <div class="toolbar">
        <div class="title">${reportEsc(fileName || "Trip Report")}</div>
        <div class="actions">
          <a class="btn" href="${reportEsc(pdfUrl)}" download="${reportEsc(fileName || "trip-report.pdf")}">Download PDF</a>
        </div>
      </div>
      <iframe src="${reportEsc(pdfUrl)}" title="${reportEsc(fileName || "Trip Report")}"></iframe>
    </div>
  </body>
</html>`;
  }

  function createPdfLauncherUrls(blob, fileName) {
    const downloadUrl = URL.createObjectURL(blob);
    const viewerBlob = new Blob([buildPdfViewerHtml(fileName, downloadUrl)], { type: "text/html;charset=utf-8" });
    const openUrl = URL.createObjectURL(viewerBlob);
    return { openUrl, downloadUrl };
  }

  function buildTripCostSummaryHtml(title, rows = []) {
    const generatedAt = new Date().toLocaleString();
    const officeCount = new Set(rows.map((row) => String(row.office || "").trim()).filter(Boolean)).size;
    const reportCount = rows.length;
    const totals = rows.reduce((acc, row) => ({
      marinas: acc.marinas + (Number(row.marinas) || 0),
      marinaRevenue: acc.marinaRevenue + (Number(row.marinaRevenue) || 0),
      tripDays: acc.tripDays + (Number(row.tripDays) || 0),
      driveMiles: acc.driveMiles + (Number(row.driveMiles) || 0),
      mileageCost: acc.mileageCost + (Number(row.mileageCost) || 0),
      airfare: acc.airfare + (Number(row.airfare) || 0),
      perDiem: acc.perDiem + (Number(row.perDiem) || 0),
      travelCost: acc.travelCost + (Number(row.travelCost) || 0),
      supportDays: acc.supportDays + (Number(row.supportDays) || 0),
      supportCost: acc.supportCost + (Number(row.supportCost) || 0),
      estimateTotal: acc.estimateTotal + (Number(row.estimateTotal) || 0),
    }), {
      marinas: 0,
      marinaRevenue: 0,
      tripDays: 0,
      driveMiles: 0,
      mileageCost: 0,
      airfare: 0,
      perDiem: 0,
      travelCost: 0,
      supportDays: 0,
      supportCost: 0,
      estimateTotal: 0,
    });
    const bodyRows = rows.map((row) => `
      <tr>
        <td>
          <div class="report-name">${reportEsc(row.title)}</div>
          <div class="report-subline">${reportEsc(row.office)}</div>
        </td>
        <td><span class="scope-badge scope-${reportEsc(String(row.scope || "").toLowerCase())}">${reportEsc(row.scope)}</span></td>
        <td class="num">${reportEsc(String(row.tripDays))}</td>
        <td class="num">${reportEsc(String(row.marinas))}</td>
        <td class="num rate">${reportEsc(formatCurrency(row.pricePerMarina))}</td>
        <td class="num">${reportEsc(formatCurrency(row.marinaRevenue))}</td>
        <td class="num"><div>${reportEsc(formatCurrency(row.mileageCost))}</div><div class="report-subline">${reportEsc(formatMilesPrecise(row.driveMiles))}</div></td>
        <td class="num">${reportEsc(formatCurrency(row.airfare))}</td>
        <td class="num">${reportEsc(formatCurrency(row.perDiem))}</td>
        <td class="num group-subtotal">${reportEsc(formatCurrency(row.travelCost))}</td>
        <td class="num">${reportEsc(String(row.supportDays))}</td>
        <td class="num rate">${reportEsc(formatCurrency(row.supportDayRate))}</td>
        <td class="num group-subtotal">${reportEsc(formatCurrency(row.supportCost))}</td>
        <td class="num total">${reportEsc(formatCurrency(row.estimateTotal))}</td>
      </tr>
    `).join("");
    const totalsRow = `
      <tr class="totals-row">
        <td><strong>Grand Total</strong></td>
        <td></td>
        <td class="num total">${reportEsc(String(totals.tripDays))}</td>
        <td class="num total">${reportEsc(String(totals.marinas))}</td>
        <td></td>
        <td class="num total">${reportEsc(formatCurrency(totals.marinaRevenue))}</td>
        <td class="num total"><div>${reportEsc(formatCurrency(totals.mileageCost))}</div><div style="font-size:9px;color:var(--ink-soft);font-weight:400;">${reportEsc(formatMilesPrecise(totals.driveMiles))}</div></td>
        <td class="num total">${reportEsc(formatCurrency(totals.airfare))}</td>
        <td class="num total">${reportEsc(formatCurrency(totals.perDiem))}</td>
        <td class="num total group-subtotal">${reportEsc(formatCurrency(totals.travelCost))}</td>
        <td class="num total">${reportEsc(String(totals.supportDays))}</td>
        <td></td>
        <td class="num total group-subtotal">${reportEsc(formatCurrency(totals.supportCost))}</td>
        <td class="num total">${reportEsc(formatCurrency(totals.estimateTotal))}</td>
      </tr>
    `;
    return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>${reportEsc(title)}</title>
    <style>
      @page { size: landscape; margin: 12mm; }
      :root {
        --ink:#10233f;
        --ink-soft:#50667f;
        --accent:#173b67;
        --panel:#ffffff;
        --panel-soft:#f4f8fc;
        --line:#d9e5f1;
        --good-bg:#e4f3e8;
        --good-line:#9bc8a8;
        --marina-bg:#eef6ff;
        --marina-line:#c0d8f0;
        --travel-bg:#f5f0fb;
        --travel-line:#cfc0e8;
        --support-bg:#fff8ec;
        --support-line:#e8d5a0;
      }
      body { margin: 0; font: 14px/1.45 "Segoe UI", Arial, sans-serif; color: var(--ink); background: linear-gradient(180deg, #f7fbff 0%, #eef5fb 100%); }
      .page { min-height: 100vh; box-sizing: border-box; padding: 18px; }
      .hero {
        display: grid;
        grid-template-columns: minmax(0, 1.1fr) minmax(400px, 0.9fr);
        gap: 12px;
        align-items: start;
        margin-bottom: 12px;
      }
      .hero-card, .stats-card, .table-shell {
        background: rgba(255,255,255,0.96);
        border: 1px solid rgba(23,59,103,0.12);
        border-radius: 18px;
        box-shadow: 0 18px 36px rgba(16,35,63,0.08);
      }
      .hero-card { padding: 16px 18px; }
      h1 { margin: 0 0 8px; color: var(--accent); font-size: 28px; line-height: 1.06; letter-spacing: 0.01em; }
      .hero-meta { color: var(--ink-soft); font-size: 11px; line-height: 1.35; }
      .hero-meta strong { color: var(--ink); }
      .stats-card {
        padding: 10px;
        display: grid;
        grid-template-columns: repeat(3, minmax(0, 1fr));
        grid-template-rows: repeat(2, auto);
        gap: 8px;
      }
      .stat {
        padding: 10px 12px 9px;
        border-radius: 12px;
        background: linear-gradient(180deg, #fbfdff 0%, var(--panel-soft) 100%);
        border: 1px solid var(--line);
      }
      .stat.stat-total {
        grid-column: 1 / -1;
        background: linear-gradient(135deg, #eaf3ff 0%, #dceeff 100%);
        border-color: var(--marina-line);
        display: flex;
        align-items: center;
        gap: 12px;
      }
      .stat.stat-total .stat-label { flex: none; }
      .stat.stat-total .stat-value { font-size: 22px; }
      .stat.stat-marina { background: linear-gradient(180deg, #f5faff 0%, var(--marina-bg) 100%); border-color: var(--marina-line); }
      .stat.stat-travel { background: linear-gradient(180deg, #faf8ff 0%, var(--travel-bg) 100%); border-color: var(--travel-line); }
      .stat.stat-support { background: linear-gradient(180deg, #fffdf5 0%, var(--support-bg) 100%); border-color: var(--support-line); }
      .stat-label { font-size: 9px; text-transform: uppercase; letter-spacing: 0.07em; color: var(--ink-soft); }
      .stat-value { margin-top: 4px; font-size: 16px; line-height: 1.1; font-weight: 700; color: var(--accent); }
      .stat-sub { margin-top: 3px; font-size: 9px; color: var(--ink-soft); }
      .table-shell { overflow: hidden; }
      table { width: 100%; border-collapse: collapse; table-layout: fixed; background: var(--panel); }
      th, td { box-sizing: border-box; padding: 5px 5px; border-bottom: 1px solid var(--line); text-align: left; vertical-align: middle; }
      th { color: var(--accent); font-size: 9px; text-transform: uppercase; letter-spacing: 0.04em; }
      th.num-head { text-align: right; }
      th.group-head { text-align: center; font-size: 8px; letter-spacing: 0.06em; border-bottom: 2px solid; padding-bottom: 3px; }
      th.group-marina { background: var(--marina-bg); border-color: var(--marina-line); color: #174d8f; }
      th.group-travel { background: var(--travel-bg); border-color: var(--travel-line); color: #4a2b8a; }
      th.group-support { background: var(--support-bg); border-color: var(--support-line); color: #7a5000; }
      th.col-head-marina { background: var(--marina-bg); }
      th.col-head-travel { background: var(--travel-bg); }
      th.col-head-support { background: var(--support-bg); }
      tr.group-row th { border-top: 1px solid var(--line); }
      td { font-size: 10px; line-height: 1.28; }
      td.num { text-align: right; font-variant-numeric: tabular-nums; }
      td.total { font-weight: 700; color: #0f2d52; }
      td.rate { color: var(--ink-soft); }
      td.group-subtotal { font-weight: 600; }
      tr:nth-child(even) td { background: #fbfdff; }
      .report-name { font-weight: 700; color: var(--ink); font-size: 11px; }
      .report-subline { margin-top: 1px; color: var(--ink-soft); font-size: 8px; }
      .col-report { width: 22%; }
      .col-scope { width: 5%; }
      .col-days { width: 5%; }
      .col-count { width: 4%; }
      .col-rate { width: 6.5%; }
      .col-rev { width: 7%; }
      .col-miles { width: 6%; }
      .col-money { width: 6.5%; }
      .col-sub { width: 7%; }
      .col-sdays { width: 4.5%; }
      .col-total { width: 7.5%; }
      .scope-badge {
        display: inline-flex; align-items: center; padding: 2px 6px;
        border-radius: 999px; font-size: 8px; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase;
        border: 1px solid #c7d8ea; background: #eef4fb; color: var(--accent);
      }
      .scope-road { background: #edf6ef; border-color: #bdd8c4; color: #245a34; }
      .scope-air { background: #eef2fb; border-color: #c7d1ee; color: #324f93; }
      .totals-row td { background: var(--good-bg); border-top: 2px solid var(--good-line); font-weight: 700; }
      .totals-row td:first-child { color: #184d2a; }
      @media print {
        .page { padding: 0; min-height: auto; }
        .hero { grid-template-columns: minmax(0, 1fr) minmax(360px, 0.85fr); gap: 10px; }
        .hero-card, .stats-card, .table-shell { box-shadow: none; }
        h1 { font-size: 22px; }
        .stat-value { font-size: 14px; }
        .stat.stat-total .stat-value { font-size: 18px; }
      }
    </style>
  </head>
  <body>
    <div class="page">
    <div class="hero">
      <div class="hero-card">
        <h1>${reportEsc(title)}</h1>
        <div class="hero-meta">
          Generated <strong>${reportEsc(generatedAt)}</strong><br />
          Offices: <strong>${reportEsc(String(officeCount))}</strong> &nbsp;|&nbsp; Reports: <strong>${reportEsc(String(reportCount))}</strong> &nbsp;|&nbsp; Sites: <strong>${reportEsc(String(totals.marinas))}</strong> &nbsp;|&nbsp; Trip Days: <strong>${reportEsc(String(totals.tripDays))}</strong>
        </div>
      </div>
      <div class="stats-card">
        <div class="stat stat-total">
          <div>
            <div class="stat-label">Estimate Total</div>
            <div class="stat-value">${reportEsc(formatCurrency(totals.estimateTotal))}</div>
          </div>
          <div class="stat-sub">= Marina Cost + Travel Cost + Support Cost</div>
        </div>
        <div class="stat stat-marina">
          <div class="stat-label">Marina Cost</div>
          <div class="stat-value">${reportEsc(formatCurrency(totals.marinaRevenue))}</div>
          <div class="stat-sub">${reportEsc(String(totals.marinas))} sites across all reports</div>
        </div>
        <div class="stat stat-travel">
          <div class="stat-label">Travel Cost</div>
          <div class="stat-value">${reportEsc(formatCurrency(totals.travelCost))}</div>
          <div class="stat-sub">Mileage + Airfare + Per Diem</div>
        </div>
        <div class="stat stat-support">
          <div class="stat-label">Support Cost</div>
          <div class="stat-value">${reportEsc(formatCurrency(totals.supportCost))}</div>
          <div class="stat-sub">${reportEsc(String(totals.supportDays))} support days (weather + travel) × standby rate</div>
        </div>
      </div>
    </div>
    <div class="table-shell">
    <table>
      <colgroup>
        <col class="col-report" />
        <col class="col-scope" />
        <col class="col-days" />
        <col class="col-count" />
        <col class="col-rate" />
        <col class="col-rev" />
        <col class="col-miles" />
        <col class="col-money" />
        <col class="col-money" />
        <col class="col-sub" />
        <col class="col-sdays" />
        <col class="col-rate" />
        <col class="col-sub" />
        <col class="col-total" />
      </colgroup>
      <thead>
        <tr class="group-row">
          <th rowspan="2">Report</th>
          <th rowspan="2">Scope</th>
          <th rowspan="2" class="num-head">Trip<br/>Days</th>
          <th colspan="3" class="group-head group-marina">Per Site Cost</th>
          <th colspan="4" class="group-head group-travel">Travel Cost</th>
          <th colspan="3" class="group-head group-support">Support Cost</th>
          <th rowspan="2" class="num-head">Estimate<br/>Total</th>
        </tr>
        <tr>
          <th class="num-head col-head-marina">#</th>
          <th class="num-head col-head-marina">Rate</th>
          <th class="num-head col-head-marina">Subtotal</th>
          <th class="num-head col-head-travel">Mileage<br/>Cost</th>
          <th class="num-head col-head-travel">Airfare</th>
          <th class="num-head col-head-travel">Per Diem</th>
          <th class="num-head col-head-travel">Subtotal</th>
          <th class="num-head col-head-support">Days</th>
          <th class="num-head col-head-support">Day Rate</th>
          <th class="num-head col-head-support">Cost</th>
        </tr>
      </thead>
      <tbody>${bodyRows}${totalsRow}</tbody>
    </table>
    </div>
    </div>
  </body>
</html>`;
  }

  async function createTripCostSummaryArtifacts(title, rows = []) {
    const totals = rows.reduce((acc, row) => ({
      marinas: acc.marinas + (Number(row.marinas) || 0),
      marinaRevenue: acc.marinaRevenue + (Number(row.marinaRevenue) || 0),
      tripDays: acc.tripDays + (Number(row.tripDays) || 0),
      driveMiles: acc.driveMiles + (Number(row.driveMiles) || 0),
      mileageCost: acc.mileageCost + (Number(row.mileageCost) || 0),
      airfare: acc.airfare + (Number(row.airfare) || 0),
      perDiem: acc.perDiem + (Number(row.perDiem) || 0),
      travelCost: acc.travelCost + (Number(row.travelCost) || 0),
      supportDays: acc.supportDays + (Number(row.supportDays) || 0),
      supportCost: acc.supportCost + (Number(row.supportCost) || 0),
      estimateTotal: acc.estimateTotal + (Number(row.estimateTotal) || 0),
    }), {
      marinas: 0,
      marinaRevenue: 0,
      tripDays: 0,
      driveMiles: 0,
      mileageCost: 0,
      airfare: 0,
      perDiem: 0,
      travelCost: 0,
      supportDays: 0,
      supportCost: 0,
      estimateTotal: 0,
    });
    const html = buildTripCostSummaryHtml(title, rows);
    const openUrl = URL.createObjectURL(new Blob([html], { type: "text/html;charset=utf-8" }));
    const pdfBlob = await reportHtmlToPdfBlob({ html }, { statusLabel: `Preparing ${title} PDF...` });
    const pdfFileName = `${sanitizeFilePart(title, "trip-cost-summary")}.pdf`;
    const pdfDownloadUrl = URL.createObjectURL(pdfBlob);
    const excelFileName = `${sanitizeFilePart(title, "trip-cost-summary")}.xls`;
    const excelBlob = createExcelWorkbookBlob([
      {
        name: "Trip Cost Summary",
        columns: ["Report", "Office", "Scope", "Trip Days", "# Sites", "Rate/Site", "Marina Cost", "Miles", "Mileage Cost", "Airfare", "Per Diem", "Travel Cost", "Support Days", "Support Day Rate", "Support Cost", "Estimate Total"],
        rows: [
          ...rows.map((row) => [
            row.title,
            row.office,
            row.scope,
            row.tripDays,
            row.marinas,
            Number(row.pricePerMarina || 0).toFixed(2),
            Number(row.marinaRevenue || 0).toFixed(2),
            Number(row.driveMiles || 0).toFixed(1),
            Number(row.mileageCost || 0).toFixed(2),
            Number(row.airfare || 0).toFixed(2),
            Number(row.perDiem || 0).toFixed(2),
            Number(row.travelCost || 0).toFixed(2),
            row.supportDays,
            Number(row.supportDayRate || 0).toFixed(2),
            Number(row.supportCost || 0).toFixed(2),
            Number(row.estimateTotal || 0).toFixed(2),
          ]),
          ["Grand Total", "", "", totals.tripDays, totals.marinas, "", totals.marinaRevenue.toFixed(2), totals.driveMiles.toFixed(1), totals.mileageCost.toFixed(2), totals.airfare.toFixed(2), totals.perDiem.toFixed(2), totals.travelCost.toFixed(2), totals.supportDays, "", totals.supportCost.toFixed(2), totals.estimateTotal.toFixed(2)],
        ],
      },
    ]);
    const excelDownloadUrl = URL.createObjectURL(excelBlob);
    return {
      openUrl,
      pdfBlob,
      pdfDownloadUrl,
      pdfFileName,
      excelBlob,
      excelDownloadUrl,
      excelFileName,
    };
  }

  async function buildTripCostSummaryActions(rows = []) {
    if (!Array.isArray(rows) || !rows.length) return [];
    const summaryUrls = await createTripCostSummaryArtifacts("Trip Cost Summary", rows);
    return [
      { label: "Open Cost Summary", href: summaryUrls.openUrl },
      { label: "Download Cost Summary PDF", href: summaryUrls.pdfDownloadUrl, download: summaryUrls.pdfFileName },
      { label: "Download Cost Summary Excel", href: summaryUrls.excelDownloadUrl, download: summaryUrls.excelFileName },
    ];
  }

  function buildReportLauncherHtml(title, reports = [], { status = "", complete = false, summaryActions = [] } = {}) {
    const reportCards = reports.map((report) => `
      <div class="report-card ${report.ready ? "ready" : ""}">
        <div class="report-card-title">${reportEsc(report.label || "Report")}</div>
        <div class="report-card-status">${reportEsc(report.status || (report.ready ? "Ready" : "Queued"))}</div>
        ${(report.openUrl || report.downloadUrl) ? `
          <div class="report-card-actions">
            ${report.openUrl ? `<a href="${reportEsc(report.openUrl)}" target="_blank" rel="noopener">Open</a>` : ""}
            ${report.downloadUrl ? `<a href="${reportEsc(report.downloadUrl)}" download="${reportEsc(report.fileName || "report.pdf")}">Download</a>` : ""}
          </div>
        ` : ""}
      </div>
    `).join("");
    const summaryActionsHtml = summaryActions.length ? `
      <div class="summary-actions">
        ${summaryActions.map((action) => `
          <a href="${reportEsc(action.href || "#")}" ${action.download ? `download="${reportEsc(action.download)}"` : `target="_blank" rel="noopener"`}>${reportEsc(action.label || "Open")}</a>
        `).join("")}
      </div>
    ` : "";
    return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>${reportEsc(title)}</title>
    <style>
      body {
        margin: 0;
        min-height: 100vh;
        font: 15px/1.5 "Segoe UI", Arial, sans-serif;
        color: #10233f;
        background: linear-gradient(180deg, #f8fbff 0%, #eef4fb 100%);
      }
      .shell {
        max-width: 860px;
        margin: 0 auto;
        padding: 28px 24px 40px;
      }
      h1 {
        margin: 0 0 10px;
        font-size: 30px;
        line-height: 1.1;
        color: #173b67;
      }
      .status {
        margin: 0 0 18px;
        color: #415a77;
      }
      .reports {
        display: grid;
        gap: 14px;
      }
      .report-card {
        padding: 16px 18px;
        border-radius: 14px;
        background: rgba(255,255,255,0.92);
        border: 1px solid rgba(23,59,103,0.14);
        box-shadow: 0 14px 32px rgba(16,35,63,0.08);
      }
      .report-card.ready {
        background: rgba(214, 241, 224, 0.82);
        border-color: rgba(70,160,106,0.34);
      }
      .report-card-title {
        font-weight: 700;
        color: #173b67;
      }
      .report-card-status {
        margin-top: 4px;
        color: #415a77;
      }
      .report-card-actions {
        margin-top: 10px;
        display: flex;
        gap: 14px;
        flex-wrap: wrap;
      }
      a {
        color: #0f4aa3;
        font-weight: 600;
        text-decoration: none;
      }
      a:hover {
        text-decoration: underline;
      }
      .summary-actions {
        display: flex;
        gap: 14px;
        flex-wrap: wrap;
        margin: 0 0 18px;
      }
      .summary-actions a {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        padding: 8px 14px;
        border-radius: 999px;
        border: 1px solid rgba(23,59,103,0.14);
        background: rgba(255,255,255,0.96);
        text-decoration: none;
      }
      .done {
        margin-top: 18px;
        color: ${complete ? "#166534" : "#415a77"};
        font-weight: ${complete ? "700" : "500"};
      }
    </style>
  </head>
  <body>
    <div class="shell">
      <h1>${reportEsc(title)}</h1>
      <p class="status">${reportEsc(status || "Preparing reports...")}</p>
      ${summaryActionsHtml}
      <div class="reports">${reportCards || "<div>No reports queued.</div>"}</div>
      <div class="done">${complete ? "Reports are ready." : "Leave this tab open while reports are generated."}</div>
    </div>
  </body>
</html>`;
  }

  function updateReportLauncherWindow(reportWindow, title, reports = [], options = {}) {
    if (!reportWindow || reportWindow.closed) return;
    reportWindow.document.open();
    reportWindow.document.write(buildReportLauncherHtml(title, reports, options));
    reportWindow.document.close();
    try { reportWindow.blur?.(); } catch (_) {}
    reclaimHydrotoolsFocus();
  }

  async function loadSharedProject360Photos({ code = "", append = false } = {}) {
    const marinaCode = String(code || state.projectSettings.selectedProjectId || "").trim();
    if (!marinaCode) return 0;
    const loadSeq = ++sharedDockPhotosLoadSeq;

    const assets = state.digitalAsset.assetsByCategory[marinaCode]?.["360-dock-photos"] || [];

    if (!assets.length) {
      if (marinaCode !== String(state.projectSettings.selectedProjectId || "").trim() || loadSeq !== sharedDockPhotosLoadSeq) return 0;
      if (!streetViewRuntime) streetViewRuntime = createStreetView360Runtime(app);
      streetViewRuntime?.destroy?.();
      streetViewRuntime = createStreetView360Runtime(app);
      const streetViewHost = containerEl?.querySelector?.("#imggen-streetview-360-host");
      if (streetViewHost) streetViewRuntime.mount(streetViewHost, { initialProjectId: String(state.projectSettings.selectedProjectId || "") });
      state.projectSettings.dockPhotosLinked = false;
      state.projectSettings.dockPhotosImport = { active: false, current: 0, total: 0, label: "No 360 photos in DAM for this marina." };
      persistLocalState();
      render();
      return 0;
    }

    state.projectSettings.dockPhotosImport = { active: true, current: 0, total: assets.length, label: `Fetching ${assets.length} photo URLs…` };
    render();

    let urlItems = [];
    try {
      const resp = await fetch("/api/s3/presign/batch-download", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ keys: assets.map((a) => a.key) }),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      const rawUrls = Array.isArray(data.urls) ? data.urls : [];
      urlItems = assets.map((a, i) => ({ url: rawUrls[i] || "", name: a.name || `photo-${i + 1}.jpg` })).filter((x) => x.url);
    } catch (err) {
      if (marinaCode !== String(state.projectSettings.selectedProjectId || "").trim() || loadSeq !== sharedDockPhotosLoadSeq) return 0;
      state.projectSettings.dockPhotosImport = { active: false, current: 0, total: 0, label: `Failed to load 360 photos: ${err.message}` };
      render();
      return 0;
    }
    if (marinaCode !== String(state.projectSettings.selectedProjectId || "").trim() || loadSeq !== sharedDockPhotosLoadSeq) return 0;

    if (!streetViewRuntime) streetViewRuntime = createStreetView360Runtime(app);
    if (!append) {
      streetViewRuntime?.destroy?.();
      streetViewRuntime = createStreetView360Runtime(app);
      const streetViewHost = containerEl?.querySelector?.("#imggen-streetview-360-host");
      if (streetViewHost) streetViewRuntime.mount(streetViewHost, { initialProjectId: String(state.projectSettings.selectedProjectId || "") });
    }
    const update360ProgressDom = (current, total, label) => {
      if (!containerEl) return;
      const pct = total > 0 ? Math.round((current / total) * 100) : 0;
      const bar = containerEl.querySelector("#imggen-360-progress-bar");
      const lbl = containerEl.querySelector("#imggen-360-progress-label");
      if (bar) bar.style.width = `${pct}%`;
      if (lbl) lbl.textContent = String(label || "");
    };
    let lastProgressPaint = 0;
    await streetViewRuntime.importHostedImageUrls(urlItems, {
      concurrency: 10,
      onProgress: (progress = {}) => {
        if (marinaCode !== String(state.projectSettings.selectedProjectId || "").trim() || loadSeq !== sharedDockPhotosLoadSeq) return;
        const total = Math.max(1, Number(progress.total) || urlItems.length || 1);
        const current = Math.max(0, Math.min(total, Number(progress.current) || 0));
        const phase = String(progress.phase || "");
        const name = String(progress.name || "");
        const phaseLabel = phase === "fetching" ? "Fetching" : "Processing";
        const label = `${phaseLabel} ${name} (${current}/${total})`;
        state.projectSettings.dockPhotosImport = { active: true, current, total, label };
        const now = Date.now();
        if ((now - lastProgressPaint) > 80 || current === total) {
          lastProgressPaint = now;
          update360ProgressDom(current, total, label);
        }
      },
    });
    if (marinaCode !== String(state.projectSettings.selectedProjectId || "").trim() || loadSeq !== sharedDockPhotosLoadSeq) return 0;
    state.projectSettings.dockPhotosLinked = true;
    state.projectSettings.dockPhotosImport = { active: false, current: urlItems.length, total: urlItems.length, label: `Loaded ${urlItems.length} photos from DAM.` };
    persistLocalState();
    render();
    return urlItems.length;
  }

  function firstMarinaIdFromState() {
    const items = projectSettingsMarinaItems();
    for (let i = 0; i < items.length; i += 1) {
      const item = items[i];
      const key = String(projectSettingsMarinaCode(item) || "").trim();
      if (key) return key;
    }
    return "";
  }

  function queueSharedProject360PhotosLoad(projectId, { force = false } = {}) {
    const marina = String(projectId || "").trim();
    if (!marina) return;
    if (sharedDockPhotosLoadInFlight) {
      sharedDockPhotosPendingMarina = marina;
      return;
    }
    if (!force && sharedDockPhotosLoadedForMarina === marina) return;
    sharedDockPhotosLoadInFlight = true;
    sharedDockPhotosPendingMarina = "";
    loadSharedProject360Photos({ code: marina, append: false })
      .then(() => {
        sharedDockPhotosLoadedForMarina = marina;
      })
      .catch((error) => {
        sharedDockPhotosLoadedForMarina = "";
        console.warn("[ProjectSettings] shared 360 load failed", error);
        const message = String(error?.message || error || "unknown error");
        state.projectSettings.dockPhotosImport = { active: false, current: 0, total: 0, label: `Load failed: ${message}` };
        state.projectSettings.dockPhotosLinked = false;
        render();
      })
      .finally(() => {
        sharedDockPhotosLoadInFlight = false;
        if (sharedDockPhotosPendingMarina && sharedDockPhotosPendingMarina !== marina) {
          const pending = sharedDockPhotosPendingMarina;
          sharedDockPhotosPendingMarina = "";
          queueSharedProject360PhotosLoad(pending, { force: true });
        }
      });
  }

  function remountStreetViewRuntime() {
    sharedDockPhotosLoadSeq += 1;
    sharedDockPhotosLoadedForMarina = "";
    sharedDockPhotosPendingMarina = "";
    resetProject360UiState();
    try { streetViewRuntime?.destroy?.(); } catch {}
    streetViewRuntime = createStreetView360Runtime(app);
    const streetViewHost = containerEl?.querySelector?.("#imggen-streetview-360-host");
    if (streetViewHost) streetViewRuntime.mount(streetViewHost, { initialProjectId: String(state.projectSettings.selectedProjectId || "") });
  }

  function remountPointCloudRuntime() {
    try { pointCloudRuntime?.destroy?.(); } catch {}
    pointCloudRuntime = createPointCloudWorkspaceRuntime(app);
    const pointCloudHost = containerEl?.querySelector?.("#imggen-pointcloud-host");
    if (pointCloudHost) pointCloudRuntime.mount(pointCloudHost);
    pointCloudRuntime.setEpsgCode?.(state.projectSettings.epsgCode);
    if (state.projectSettings.selectedProjectId) {
      pointCloudRuntime.setActiveMarina?.(state.projectSettings.selectedProjectId);
      syncPointCloudRuntimeFromDam(state.projectSettings.selectedProjectId);
    }
  }

  function syncPointCloudRuntimeFromDam(code = state.projectSettings.selectedProjectId, { allowEmpty = false } = {}) {
    const marina = String(code || "").trim();
    if (!marina || !pointCloudRuntime?.setProjectFilesFromDam) return false;
    const files = state.digitalAsset.assetsByCategory[marina]?.["las-files"];
    if (!Array.isArray(files)) return false;
    const lasKeys = files
      .filter((a) => /\.(las|laz)$/i.test(String(a?.name || a?.key || "")))
      .map((a) => String(a?.key || "").trim())
      .filter(Boolean);
    if (!lasKeys.length && !allowEmpty) return false;
    pointCloudRuntime.setProjectFilesFromDam(lasKeys);
    return true;
  }

  function readMarinaAssetEntry(mapValue, marinaId) {
    const marina = String(marinaId || "").trim();
    if (!marina || !mapValue || typeof mapValue !== "object") return null;
    const entry = mapValue[marina];
    return entry && typeof entry === "object" ? entry : null;
  }

  function saveProjectSettingsAssetStateForMarina(marinaId) {
    const marina = String(marinaId || "").trim();
    if (!marina) return;
    const nextGeoTiffById = {
      ...(state.projectSettings.marinaGeoTiffById && typeof state.projectSettings.marinaGeoTiffById === "object"
        ? state.projectSettings.marinaGeoTiffById
        : {}),
      [marina]: {
        ...(readMarinaAssetEntry(state.projectSettings.marinaGeoTiffById, marina) || {}),
        hostPath: String(state.projectSettings.geoTiffHostPath || ""),
        fileName: String(state.projectSettings.geoTiffFileName || ""),
      },
    };
    const nextGeoJsonById = {
      ...(state.projectSettings.marinaGeoJsonById && typeof state.projectSettings.marinaGeoJsonById === "object"
        ? state.projectSettings.marinaGeoJsonById
        : {}),
      [marina]: {
        hostPath: String(state.projectSettings.geoJsonHostPath || ""),
        fileName: String(state.projectSettings.geoJsonFileName || ""),
      },
    };
    const nextLasById = {
      ...(state.projectSettings.marinaLasById && typeof state.projectSettings.marinaLasById === "object"
        ? state.projectSettings.marinaLasById
        : {}),
      [marina]: {
        hostPath: String(state.projectSettings.lasHostPath || ""),
        fileName: String(state.projectSettings.lasFileName || ""),
      },
    };
    state.projectSettings.marinaGeoTiffById = nextGeoTiffById;
    state.projectSettings.marinaGeoJsonById = nextGeoJsonById;
    state.projectSettings.marinaLasById = nextLasById;
  }

  function hydrateProjectSettingsAssetStateForMarina(marinaId) {
    const marina = String(marinaId || "").trim();
    const geoTiffEntry = readMarinaAssetEntry(state.projectSettings.marinaGeoTiffById, marina);
    const geoJsonEntry = readMarinaAssetEntry(state.projectSettings.marinaGeoJsonById, marina);
    const lasEntry = readMarinaAssetEntry(state.projectSettings.marinaLasById, marina);
    state.projectSettings.geoTiffHostPath = String(geoTiffEntry?.hostPath || "");
    state.projectSettings.geoTiffFileName = String(geoTiffEntry?.fileName || "");
    state.projectSettings.geoJsonHostPath = String(geoJsonEntry?.hostPath || "");
    state.projectSettings.geoJsonFileName = String(geoJsonEntry?.fileName || "");
    state.projectSettings.lasHostPath = String(lasEntry?.hostPath || "");
    state.projectSettings.lasFileName = String(lasEntry?.fileName || "");
  }

  async function restoreGeoJsonForMarinaFromHostPath(marinaId) {
    const marina = String(marinaId || "").trim();
    if (!marina) return false;
    const entry = readMarinaAssetEntry(state.projectSettings.marinaGeoJsonById, marina);
    const hostPath = String(entry?.hostPath || "").trim();
    const fileName = String(entry?.fileName || hostPath.split(/[\\/]/).pop() || "");
    state.projectSettings.geoJsonHostPath = hostPath;
    state.projectSettings.geoJsonFileName = fileName;
    if (!hostPath) {
      render();
      return false;
    }
    try {
      const probe = await fetch(`/api/qimera/local-file?path=${encodeURIComponent(hostPath)}&v=${Date.now()}`, { method: "HEAD" }).catch(() => null);
      if (!probe || !probe.ok) {
        state.projectSettings.geoJsonHostPath = "";
        state.projectSettings.geoJsonFileName = "";
        render();
        return false;
      }
    } catch {
      return false;
    }
    render();
    await importProjectGeoJson();
    return true;
  }

  async function restoreLasForMarinaFromHostPath(marinaId) {
    const marina = String(marinaId || "").trim();
    if (!marina) return false;
    const entry = readMarinaAssetEntry(state.projectSettings.marinaLasById, marina);
    const hostPath = String(entry?.hostPath || "").trim();
    const fileName = String(entry?.fileName || hostPath.split(/[\\/]/).pop() || "");
    state.projectSettings.lasHostPath = hostPath;
    state.projectSettings.lasFileName = fileName;
    if (!hostPath) {
      render();
      return false;
    }
    render();
    await importProjectLas();
    return true;
  }

  function resetProject360UiState() {
    state.projectSettings.dockPhotosLinked = false;
    state.projectSettings.dockPhotosImport = { active: false, current: 0, total: 0, label: "" };
  }

  function clearProjectSettingsMapLayers() {
    const layerSvc = app.services?.layers;
    if (!layerSvc || typeof layerSvc.listLayers !== "function" || typeof layerSvc.removeLayer !== "function") return 0;
    const layers = layerSvc.listLayers();
    let removed = 0;
    for (let i = 0; i < layers.length; i += 1) {
      const layer = layers[i] || {};
      const source = String(layer?.meta?.source || "");
      const isPclExport = !!layer?.meta?.pclExport;
      if (source !== "project-settings-geotiff" && source !== "project-settings-geojson" && !isPclExport && source !== "dam-spatial-layer") continue;
      try {
        layerSvc.removeLayer(String(layer.id || ""));
        removed += 1;
      } catch {}
    }
    if (state.digitalAsset?.spatialLayersOnMap) state.digitalAsset.spatialLayersOnMap = {};
    return removed;
  }

  function removeProjectGeoTiffLayersForMarina(marinaId) {
    const marina = String(marinaId || "").trim();
    const layerSvc = app.services?.layers;
    if (!marina || !layerSvc || typeof layerSvc.listLayers !== "function" || typeof layerSvc.removeLayer !== "function") return 0;
    const layers = layerSvc.listLayers();
    let removed = 0;
    for (let i = 0; i < layers.length; i += 1) {
      const layer = layers[i] || {};
      const source = String(layer?.meta?.source || "");
      const layerMarina = String(layer?.meta?.marina || "").trim();
      if (source !== "project-settings-geotiff" || layerMarina !== marina) continue;
      try {
        layerSvc.removeLayer(String(layer.id || ""));
        removed += 1;
      } catch {}
    }
    return removed;
  }

  function removeAllProjectGeoTiffLayers() {
    const layerSvc = app.services?.layers;
    if (!layerSvc || typeof layerSvc.listLayers !== "function" || typeof layerSvc.removeLayer !== "function") return 0;
    const layers = layerSvc.listLayers();
    let removed = 0;
    for (let i = 0; i < layers.length; i += 1) {
      const layer = layers[i] || {};
      const source = String(layer?.meta?.source || "");
      if (source !== "project-settings-geotiff") continue;
      try {
        layerSvc.removeLayer(String(layer.id || ""));
        removed += 1;
      } catch {}
    }
    return removed;
  }

  function hasProjectGeoTiffLayerForMarina(marinaId) {
    const marina = String(marinaId || "").trim();
    const layerSvc = app.services?.layers;
    if (!marina || !layerSvc || typeof layerSvc.listLayers !== "function") return false;
    const layers = layerSvc.listLayers();
    return layers.some((layer) => {
      const source = String(layer?.meta?.source || "");
      const layerMarina = String(layer?.meta?.marina || "").trim();
      return source === "project-settings-geotiff" && layerMarina === marina;
    });
  }

  function restoreCachedRasterLayerForMarina(marinaId) {
    const marina = String(marinaId || "").trim();
    if (!marina) return false;
    const cached = marinaRasterLayerCache.get(marina);
    if (!cached || !cached.url) return false;
    const layerSvc = app.services?.layers;
    if (!layerSvc || typeof layerSvc.addLayer !== "function") return false;
    removeAllProjectGeoTiffLayers();
    const layerId = `project-geotiff-${marina}-${Date.now()}`;
    layerSvc.addLayer("overlay-manager", {
      id: layerId,
      name: String(cached.name || "GeoTIFF (tiled)"),
      type: "xyz",
      url: String(cached.url),
      opacity: 0.92,
      visible: true,
      options: {
        crossOrigin: true,
        maxNativeZoom: 22,
        maxZoom: 24,
      },
      meta: {
        source: "project-settings-geotiff",
        sourceType: "host-path-cog-tiles",
        marina,
        hostPath: String(cached.hostPath || ""),
        cogPath: String(cached.cogPath || ""),
        tileJsonUrl: String(cached.tileJsonUrl || ""),
        bounds: Array.isArray(cached.bounds) ? cached.bounds : null,
      },
    });
    return true;
  }

  async function discoverLatestMarinaGeoTiffAsset(marinaId) {
    const marina = String(marinaId || "").trim();
    if (!marina) return null;
    const rsp = await fetch(`/api/qimera/assets?kind=${encodeURIComponent("geotiff")}&marina=${encodeURIComponent(marina)}`, {
      method: "GET",
      cache: "no-store",
    });
    const payload = await rsp.json().catch(() => ({}));
    if (!rsp.ok || payload?.ok === false) {
      throw new Error(String(payload?.error || `GeoTIFF asset lookup failed (${rsp.status})`));
    }
    const files = Array.isArray(payload?.files) ? payload.files : [];
    if (!files.length) return null;
    const latest = files[files.length - 1] || files[0];
    const hostPath = String(latest?.path || "").trim();
    if (!hostPath) return null;
    return {
      hostPath,
      fileName: String(latest?.name || hostPath.split(/[\\/]/).pop() || ""),
    };
  }

  async function restoreGeoTiffForMarinaFromHostPath(marinaId) {
    const marina = String(marinaId || "").trim();
    if (!marina) return false;
    const byId = state.projectSettings.marinaGeoTiffById && typeof state.projectSettings.marinaGeoTiffById === "object"
      ? state.projectSettings.marinaGeoTiffById
      : {};
    const entry = byId[marina] && typeof byId[marina] === "object" ? byId[marina] : null;
    let hostPath = "";
    let fileName = "";
    try {
      const discovered = await discoverLatestMarinaGeoTiffAsset(marina);
      hostPath = String(discovered?.hostPath || "").trim();
      fileName = String(discovered?.fileName || "");
    } catch {}
    if (!hostPath) {
      const nextById = { ...byId };
      if (nextById[marina]) delete nextById[marina];
      state.projectSettings.marinaGeoTiffById = nextById;
      state.projectSettings.geoTiffHostPath = "";
      state.projectSettings.geoTiffFileName = "";
      marinaRasterLayerCache.delete(marina);
      render();
      return false;
    }
    if (hostPath !== String(entry?.hostPath || "").trim() || fileName !== String(entry?.fileName || "")) {
      state.projectSettings.marinaGeoTiffById = {
        ...byId,
        [marina]: {
          ...(entry && typeof entry === "object" ? entry : {}),
          hostPath,
          fileName: fileName || hostPath.split(/[\\/]/).pop() || "",
        },
      };
    }

    const cachedEntryHostPath = String(entry?.hostPath || "").trim();
    const canReuseCachedTiles = cachedEntryHostPath && cachedEntryHostPath === hostPath;
    const cachedTileUrl = canReuseCachedTiles ? String(entry?.tileUrl || "").trim() : "";
    const cachedTileJsonUrl = canReuseCachedTiles ? String(entry?.tileJsonUrl || "").trim() : "";
    const cachedCogPath = canReuseCachedTiles ? String(entry?.cogPath || "").trim() : "";
    const cachedBounds = canReuseCachedTiles && Array.isArray(entry?.bounds) ? entry.bounds : null;
    if (cachedTileUrl) {
      marinaRasterLayerCache.set(marina, {
        name: `${String(fileName || hostPath.split(/[\\/]/).pop() || "GeoTIFF")} (tiled)`,
        url: cachedTileUrl,
        hostPath,
        cogPath: cachedCogPath,
        tileJsonUrl: cachedTileJsonUrl,
        bounds: cachedBounds,
      });
    }

    state.projectSettings.geoTiffHostPath = hostPath;
    state.projectSettings.geoTiffFileName = String(fileName || hostPath.split(/[\\/]/).pop() || "");
    render();

    if (hasProjectGeoTiffLayerForMarina(marina)) return true;
    if (restoreCachedRasterLayerForMarina(marina)) return true;
    try {
      await importProjectGeoTiff();
      return true;
    } catch (error) {
      console.warn("[ProjectSettings] GeoTIFF auto-restore failed", error);
      return false;
    }
  }

  async function importProjectGeoTiff() {
    let hostPath = String(state.projectSettings.geoTiffHostPath || "").trim();
    let usingHostPath = !projectGeoTiffFile && !!hostPath;
    if (!projectGeoTiffFile && !usingHostPath) {
      throw new Error("Select a GeoTIFF file with Browse or Browse Host Path before importing.");
    }
    const baseName = usingHostPath
      ? String(hostPath.split(/[\\/]/).pop() || "GeoTIFF")
      : String(projectGeoTiffFile.name || "GeoTIFF");
    state.projectSettings.geoTiffImport = {
      active: true,
      current: 1,
      total: 2,
      label: "Reading GeoTIFF from local browser file...",
    };
    render();
    try {
      if (!usingHostPath && projectGeoTiffFile) {
        state.projectSettings.geoTiffImport = {
          active: true,
          current: 1,
          total: 3,
          label: "Uploading GeoTIFF to shared host storage...",
        };
        render();
        const uploaded = await uploadProjectFileToHost(projectGeoTiffFile, {
          kind: "geotiff",
          projectId: state.projectSettings.selectedProjectId,
        });
        hostPath = String(uploaded?.path || "").trim();
        if (!hostPath) throw new Error("Upload succeeded but no host path was returned.");
        usingHostPath = true;
        state.projectSettings.geoTiffHostPath = hostPath;
        state.projectSettings.geoTiffFileName = String(uploaded?.storedAs || uploaded?.filename || baseName);
      }
      const localUrl = usingHostPath
        ? `/api/qimera/local-file?path=${encodeURIComponent(hostPath)}&v=${Date.now()}`
        : URL.createObjectURL(projectGeoTiffFile);
      if (usingHostPath) {
        const probe = await fetch(localUrl, { method: "HEAD" }).catch(() => null);
        if (!probe || !probe.ok) {
          throw new Error(`Host path is not readable by server (${probe?.status || "network error"}): ${hostPath}`);
        }
      }
      state.projectSettings.geoTiffImport = {
        active: true,
        current: usingHostPath && projectGeoTiffFile ? 3 : 2,
        total: usingHostPath && projectGeoTiffFile ? 4 : 3,
        label: "Converting GeoTIFF to web tiles (COG)...",
      };
      render();
      const marinaForImport = String(state.projectSettings.selectedProjectId || "shared");
      const existingByMarina = state.projectSettings.marinaGeoTiffById && typeof state.projectSettings.marinaGeoTiffById === "object"
        ? state.projectSettings.marinaGeoTiffById
        : {};
      const existingForMarina = existingByMarina[marinaForImport] && typeof existingByMarina[marinaForImport] === "object"
        ? existingByMarina[marinaForImport]
        : null;
      const existingHostPath = String(existingForMarina?.hostPath || "").trim();
      const currentEpsgCode = String(state.projectSettings.epsgCode || "").trim();
      const canReuseExistingTiles = usingHostPath
        && !!hostPath
        && !!existingForMarina
        && existingHostPath === hostPath
        && existingForMarina?.epsgCode === currentEpsgCode
        && String(existingForMarina?.tileUrl || "").trim();

      let cogifyPayload = {};
      let tileUrl = "";
      let tileJsonUrl = "";
      let reusedExistingCog = false;

      if (canReuseExistingTiles) {
        tileUrl = String(existingForMarina?.tileUrl || "").trim();
        tileJsonUrl = String(existingForMarina?.tileJsonUrl || "").trim();
        reusedExistingCog = true;
      } else {
        const cogifyResponse = await fetch("/api/qimera/cogify", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            path: hostPath,
            marina: marinaForImport,
            sourceEpsg: currentEpsgCode || undefined,
          }),
        });
        cogifyPayload = await cogifyResponse.json().catch(() => ({}));
        if (!cogifyResponse.ok || cogifyPayload?.ok === false) {
          throw new Error(String(cogifyPayload?.error || `COG conversion failed (${cogifyResponse.status})`));
        }
        tileUrl = String(cogifyPayload?.tileUrl || "").trim();
        tileJsonUrl = String(cogifyPayload?.tileJsonUrl || "").trim();
        reusedExistingCog = !!cogifyPayload?.reusedExistingCog;
      }
      if (!tileUrl) throw new Error("COG conversion completed but no tile URL was returned.");
      let tileBounds = null;
      if (tileJsonUrl) {
        try {
          const tjRes = await fetch(tileJsonUrl, { method: "GET" });
          const tj = await tjRes.json().catch(() => ({}));
          const b = Array.isArray(tj?.bounds) ? tj.bounds : null;
          if (b && b.length === 4 && b.every((v) => Number.isFinite(Number(v)))) {
            tileBounds = [Number(b[0]), Number(b[1]), Number(b[2]), Number(b[3])];
          }
        } catch {}
      }
      state.projectSettings.geoTiffImport = {
        active: true,
        current: usingHostPath && projectGeoTiffFile ? 4 : 3,
        total: usingHostPath && projectGeoTiffFile ? 4 : 3,
        label: "Registering tiled raster layer...",
      };
      render();
      const layerSvc = app.services?.layers;
      if (!layerSvc || typeof layerSvc.addLayer !== "function" || typeof layerSvc.listLayers !== "function") {
        throw new Error("Layer service unavailable. Could not register GeoTIFF layer.");
      }
      const marina = String(state.projectSettings.selectedProjectId || "").trim();
      if (marina) removeProjectGeoTiffLayersForMarina(marina);
      const layerId = `project-geotiff-${marina || "shared"}-${Date.now()}`;
      layerSvc.addLayer("overlay-manager", {
        id: layerId,
        name: `${baseName} (tiled)`,
        type: "xyz",
        url: tileUrl,
        opacity: 0.92,
        visible: true,
        options: {
          crossOrigin: true,
          maxNativeZoom: 22,
          maxZoom: 24,
        },
        meta: {
          source: "project-settings-geotiff",
          sourceType: "host-path-cog-tiles",
          epsg: String(state.projectSettings.epsgCode || ""),
          marina: String(state.projectSettings.selectedProjectId || ""),
          hostPath: usingHostPath ? hostPath : "",
          cogPath: String(cogifyPayload?.cogPath || existingForMarina?.cogPath || ""),
          tileJsonUrl,
          bounds: tileBounds,
          reusedExistingCog,
        },
      });
      const layerRegistered = layerSvc.listLayers().some((layer) => String(layer?.id || "") === layerId);
      if (!layerRegistered) {
        throw new Error("GeoTIFF layer registration did not complete.");
      }
      const marinaForCache = String(state.projectSettings.selectedProjectId || "").trim();
      if (marinaForCache) {
        marinaRasterLayerCache.set(marinaForCache, {
          name: `${baseName} (tiled)`,
          url: tileUrl,
          hostPath: usingHostPath ? hostPath : "",
          cogPath: String(cogifyPayload?.cogPath || existingForMarina?.cogPath || ""),
          tileJsonUrl,
          bounds: tileBounds,
        });
        state.projectSettings.marinaGeoTiffById = {
          ...(state.projectSettings.marinaGeoTiffById && typeof state.projectSettings.marinaGeoTiffById === "object"
            ? state.projectSettings.marinaGeoTiffById
            : {}),
          [marinaForCache]: {
            hostPath: usingHostPath ? hostPath : String(state.projectSettings.geoTiffHostPath || hostPath || ""),
            fileName: String(state.projectSettings.geoTiffFileName || baseName || ""),
            epsgCode: currentEpsgCode,
            tileUrl,
            tileJsonUrl,
            cogPath: String(cogifyPayload?.cogPath || existingForMarina?.cogPath || ""),
            bounds: tileBounds,
          },
        };
      }
      let zoomAttempts = 0;
      const maxZoomAttempts = 8;
      const tryZoomToLayer = () => {
        zoomAttempts += 1;
        const ok = layerSvc.zoomTo?.(layerId) === true;
        if (!ok && zoomAttempts < maxZoomAttempts) window.setTimeout(tryZoomToLayer, 450);
      };
      window.setTimeout(tryZoomToLayer, 150);
      setStatus(`GeoTIFF imported: ${baseName}`);
      state.projectSettings.geoTiffImport = {
        active: false,
        current: 2,
        total: 2,
        label: `Imported: ${baseName}`,
      };
      render();
      app.ui.toast("GeoTIFF imported.");
    } catch (error) {
      state.projectSettings.geoTiffImport = {
        active: false,
        current: 0,
        total: 1,
        label: `GeoTIFF import failed: ${String(error?.message || error)}`,
      };
      render();
      throw error;
    }
  }

  async function importProjectLas() {
    const hostPath = String(state.projectSettings.lasHostPath || "").trim();
    const usingHostPath = !projectLasFile && !!hostPath;
    if (!projectLasFile && !usingHostPath) {
      throw new Error("Select a LAS/LAZ file with Browse or Browse Host Path before importing.");
    }
    if (!pointCloudRuntime) pointCloudRuntime = createPointCloudWorkspaceRuntime(app);
    state.projectSettings.lasImport = {
      active: true,
      current: 1,
      total: 2,
      label: usingHostPath ? "Linking host path..." : "Uploading LAS to S3...",
    };
    render();
    try {
      if (usingHostPath) {
        pointCloudRuntime.importExternalLasPaths([hostPath]);
        pointCloudRuntime.setDatasetPath(hostPath);
      } else {
        // Upload directly to S3 (browser → S3, bypasses VM disk).
        const safeName = String(projectLasFile.name || "point-cloud.las").replace(/[^a-zA-Z0-9._-]/g, "_");
        const s3Key = `point-clouds/${Date.now()}-${safeName}`;
        const presignRes = await fetch("/api/s3/presign/upload", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ key: s3Key, contentType: "application/octet-stream" }),
        });
        const presignPayload = await presignRes.json().catch(() => ({}));
        if (!presignRes.ok || !presignPayload.url) {
          throw new Error(String(presignPayload?.error || `Failed to get S3 upload URL (${presignRes.status})`));
        }
        state.projectSettings.lasImport = { active: true, current: 1, total: 2, label: `Uploading ${safeName} to S3...` };
        render();
        const uploadRes = await fetch(presignPayload.url, {
          method: "PUT",
          headers: { "Content-Type": "application/octet-stream" },
          body: projectLasFile,
        });
        if (!uploadRes.ok) throw new Error(`S3 upload failed (${uploadRes.status})`);
        pointCloudRuntime.importExternalLasPaths([presignPayload.key]);
        pointCloudRuntime.setDatasetPath(presignPayload.key);
      }
      state.projectSettings.lasImport = {
        active: false,
        current: 2,
        total: 2,
        label: `Imported: ${String(usingHostPath ? (hostPath.split(/[\\/]/).pop() || hostPath) : (projectLasFile?.name || "LAS file"))}`,
      };
      const marinaForCache = String(state.projectSettings.selectedProjectId || "").trim();
      if (marinaForCache) {
        state.projectSettings.marinaLasById = {
          ...(state.projectSettings.marinaLasById && typeof state.projectSettings.marinaLasById === "object"
            ? state.projectSettings.marinaLasById
            : {}),
          [marinaForCache]: {
            hostPath: usingHostPath ? hostPath : String(state.projectSettings.lasHostPath || ""),
            fileName: String(state.projectSettings.lasFileName || (usingHostPath ? (hostPath.split(/[\\/]/).pop() || hostPath) : (projectLasFile?.name || ""))),
          },
        };
      }
      render();
      app.ui.toast(`LAS imported into Point Cloud Workspace (${usingHostPath ? hostPath : projectLasFile.name}).`);
    } catch (error) {
      state.projectSettings.lasImport = {
        active: false,
        current: 0,
        total: 1,
        label: `LAS import failed: ${String(error?.message || error)}`,
      };
      render();
      throw error;
    }
  }

  async function importProjectGeoJson() {
    let hostPath = String(state.projectSettings.geoJsonHostPath || "").trim();
    let usingHostPath = !projectGeoJsonFile && !!hostPath;
    if (!projectGeoJsonFile && !usingHostPath) {
      throw new Error("Select a GeoJSON file with Browse or Browse Host Path before importing.");
    }
    const baseName = usingHostPath
      ? String(hostPath.split(/[\\/]/).pop() || "GeoJSON")
      : String(projectGeoJsonFile.name || "GeoJSON");
    state.projectSettings.geoJsonImport = {
      active: true,
      current: 1,
      total: 2,
      label: "Reading GeoJSON from local browser file...",
    };
    render();
    try {
      if (!usingHostPath && projectGeoJsonFile) {
        state.projectSettings.geoJsonImport = {
          active: true,
          current: 1,
          total: 3,
          label: "Uploading GeoJSON to shared host storage...",
        };
        render();
        const uploaded = await uploadProjectFileToHost(projectGeoJsonFile, {
          kind: "geojson",
          projectId: state.projectSettings.selectedProjectId,
        });
        hostPath = String(uploaded?.path || "").trim();
        if (!hostPath) throw new Error("Upload succeeded but no host path was returned.");
        usingHostPath = true;
        state.projectSettings.geoJsonHostPath = hostPath;
        state.projectSettings.geoJsonFileName = String(uploaded?.storedAs || uploaded?.filename || baseName);
      }
      const text = usingHostPath
        ? await fetch(`/api/qimera/local-file?path=${encodeURIComponent(hostPath)}&v=${Date.now()}`).then(async (r) => {
            if (!r.ok) throw new Error(`Host GeoJSON read failed (${r.status})`);
            return r.text();
          })
        : await projectGeoJsonFile.text();
      const parsed = JSON.parse(text);
      let featureCollection = parsed?.type === "FeatureCollection"
        ? parsed
        : parsed?.type === "Feature"
          ? { type: "FeatureCollection", features: [parsed] }
          : null;
      if (!featureCollection || !Array.isArray(featureCollection.features)) {
        throw new Error("GeoJSON must be a Feature or FeatureCollection.");
      }
      const findFirstCoord = (value) => {
        if (!Array.isArray(value)) return null;
        if (value.length >= 2 && Number.isFinite(Number(value[0])) && Number.isFinite(Number(value[1]))) {
          return [Number(value[0]), Number(value[1])];
        }
        for (let i = 0; i < value.length; i += 1) {
          const found = findFirstCoord(value[i]);
          if (found) return found;
        }
        return null;
      };
      const firstFeature = featureCollection.features.find((f) => f?.geometry?.coordinates);
      const firstCoord = findFirstCoord(firstFeature?.geometry?.coordinates || null);
      const looksProjected = !!firstCoord && (Math.abs(firstCoord[0]) > 180 || Math.abs(firstCoord[1]) > 90);
      if (looksProjected) {
        const projectionSvc = app.services?.projections;
        const epsgRaw = String(state.projectSettings.epsgCode || "2227").trim();
        const fromCode = /^EPSG:/i.test(epsgRaw) ? epsgRaw.toUpperCase() : `EPSG:${epsgRaw}`;
        if (!projectionSvc || typeof projectionSvc.transformFeatureCollection !== "function") {
          throw new Error(`GeoJSON appears projected (${fromCode}), but projection service is unavailable.`);
        }
        state.projectSettings.geoJsonImport = {
          active: true,
          current: 2,
          total: 3,
          label: `Reprojecting GeoJSON from ${fromCode} to EPSG:4326...`,
        };
        render();
        featureCollection = projectionSvc.transformFeatureCollection(featureCollection, fromCode, "EPSG:4326");
      }
      state.projectSettings.geoJsonImport = {
        active: true,
        current: looksProjected ? (usingHostPath && projectGeoJsonFile ? 4 : 3) : (usingHostPath && projectGeoJsonFile ? 3 : 2),
        total: looksProjected ? (usingHostPath && projectGeoJsonFile ? 4 : 3) : (usingHostPath && projectGeoJsonFile ? 3 : 2),
        label: "Registering GeoJSON layer...",
      };
      render();
      const managedLayer = L.geoJSON(featureCollection, {
        style: {
          color: "#22d3ee",
          weight: 2,
          opacity: 0.95,
          fillColor: "#22d3ee",
          fillOpacity: 0.18,
        },
        pointToLayer: (feature, latlng) => L.circleMarker(latlng, {
          radius: 5,
          color: "#22d3ee",
          weight: 2,
          opacity: 1,
          fillColor: "#0ea5e9",
          fillOpacity: 0.85,
        }),
      });
      const updateOpacity = (opacityValue) => {
        const o = Math.max(0, Math.min(1, Number(opacityValue) || 0));
        managedLayer.eachLayer((layer) => {
          if (layer?.setStyle) {
            layer.setStyle({
              opacity: o,
              fillOpacity: Math.max(0, Math.min(0.5, o * 0.5)),
            });
          }
          if (typeof layer?.setOpacity === "function") {
            layer.setOpacity(o);
          }
        });
      };
      const managed = {
        _visible: true,
        setVisible(visible) {
          this._visible = !!visible;
          if (this._visible) {
            managedLayer.addTo(map);
          } else {
            try { map.removeLayer(managedLayer); } catch {}
          }
        },
        setOpacity(opacityValue) {
          updateOpacity(opacityValue);
        },
        remove() {
          try { map.removeLayer(managedLayer); } catch {}
        },
      };
      managed.setVisible(true);
      updateOpacity(1);
      const layerSvc = app.services?.layers;
      if (!layerSvc || typeof layerSvc.addLayer !== "function" || typeof layerSvc.listLayers !== "function") {
        throw new Error("Layer service unavailable. Could not register GeoJSON layer.");
      }
      const layerId = `project-geojson-${Date.now()}`;
      layerSvc.addLayer("overlay-manager", {
        id: layerId,
        name: baseName,
        type: "managed",
        kind: "overlay",
        opacity: 1,
        visible: true,
        managed,
        meta: {
          source: "project-settings-geojson",
          sourceType: usingHostPath ? "host-path" : "local-file-browser",
          marina: String(state.projectSettings.selectedProjectId || ""),
          hostPath: usingHostPath ? hostPath : "",
        },
      });
      const layerRegistered = layerSvc.listLayers().some((layer) => String(layer?.id || "") === layerId);
      if (!layerRegistered) {
        throw new Error("GeoJSON layer registration did not complete.");
      }
      try {
        const bounds = managedLayer.getBounds?.();
        if (bounds?.isValid?.()) map.fitBounds(bounds, { padding: [24, 24], maxZoom: 20 });
      } catch {}
      setStatus(`GeoJSON imported: ${baseName}`);
      state.projectSettings.geoJsonImport = {
        active: false,
        current: looksProjected ? 3 : 2,
        total: looksProjected ? 3 : 2,
        label: `Imported: ${baseName}`,
      };
      const marinaForCache = String(state.projectSettings.selectedProjectId || "").trim();
      if (marinaForCache) {
        state.projectSettings.marinaGeoJsonById = {
          ...(state.projectSettings.marinaGeoJsonById && typeof state.projectSettings.marinaGeoJsonById === "object"
            ? state.projectSettings.marinaGeoJsonById
            : {}),
          [marinaForCache]: {
            hostPath: usingHostPath ? hostPath : String(state.projectSettings.geoJsonHostPath || ""),
            fileName: String(state.projectSettings.geoJsonFileName || baseName || ""),
          },
        };
      }
      render();
      app.ui.toast("GeoJSON imported.");
    } catch (error) {
      state.projectSettings.geoJsonImport = {
        active: false,
        current: 0,
        total: 1,
        label: `GeoJSON import failed: ${String(error?.message || error)}`,
      };
      render();
      throw error;
    }
  }

  function formatBytes(value) {
    const n = Number(value);
    if (!Number.isFinite(n) || n < 0) return "n/a";
    const units = ["B", "KB", "MB", "GB", "TB"];
    let size = n;
    let idx = 0;
    while (size >= 1024 && idx < units.length - 1) {
      size /= 1024;
      idx += 1;
    }
    return `${size.toFixed(idx === 0 ? 0 : 1)} ${units[idx]}`;
  }

  function resolveProjectSettingsMarina() {
    canonicalizeProjectSettingsSelectedMarinaId();
    const selectedId = String(state.projectSettings.selectedProjectId || "");
    const items = Array.isArray(state.items) ? state.items : [];
    const index = items.findIndex((item) => {
      const key = String(projectSettingsMarinaCode(item) || "");
      return key === selectedId;
    });
    if (index < 0) return { item: null, index: -1 };
    return { item: items[index], index };
  }

  // Canonical horizontalCRS lookup for a project code — same source the Project Settings
  // panel itself reads (app.services.context), not the separate/lagging
  // state.projectSettings.marinaEpsgCodes cache, which can default to EPSG:4326 when a
  // project hasn't been "visited" yet this session.
  function lookupProjectHorizontalCRS(projectId) {
    if (!projectId) return "";
    const all = app.services?.context?.getAllProjects?.() || [];
    const match = all.find((p) =>
      [p.id, p.marinaId, p.code].some((v) => String(v || "").toUpperCase() === String(projectId).toUpperCase())
    );
    return match?.horizontalCRS || "";
  }
  function dispatchProjectChanged(projectId) {
    document.dispatchEvent(new CustomEvent("hydrotools:project-changed", {
      detail: { projectId, horizontalCRS: lookupProjectHorizontalCRS(projectId) },
    }));
  }

  function epsgOptionsForMarina(item) {
    const options = [];
    const push = (code, label) => {
      if (!options.some((x) => x.code === code)) options.push({ code, label });
    };
    push("4326", "EPSG:4326 - WGS 84 (Lat/Lon)");
    push("3857", "EPSG:3857 - Web Mercator");
    const lat = Number(item?.lat);
    const lon = Number(item?.lng);
    if (Number.isFinite(lat) && Number.isFinite(lon) && lat >= -80 && lat <= 84) {
      const zone = Math.max(1, Math.min(60, Math.floor((lon + 180) / 6) + 1));
      if (lat >= 0) {
        push(`326${String(zone).padStart(2, "0")}`, `EPSG:326${String(zone).padStart(2, "0")} - WGS84 / UTM zone ${zone}N`);
        if (lon >= -170 && lon <= -50 && lat >= 10) {
          push(`269${String(zone).padStart(2, "0")}`, `EPSG:269${String(zone).padStart(2, "0")} - NAD83 / UTM zone ${zone}N`);
        }
      } else {
        push(`327${String(zone).padStart(2, "0")}`, `EPSG:327${String(zone).padStart(2, "0")} - WGS84 / UTM zone ${zone}S`);
      }
      for (const z of STATE_PLANE_ZONES) {
        if (lat >= z.latMin && lat <= z.latMax && lon >= z.lonMin && lon <= z.lonMax) {
          push(z.code, `EPSG:${z.code} - ${z.label}`);
        }
      }
    }
    return options;
  }

  // Milestone clicks cycle the UI immediately, but the PUT is debounced so a
  // click burst commits (and audit-logs) only the final value. A round trip
  // back to the base value skips the request.
  const pmMilestonePending = new Map();

  // The default row order is most-complete-first (see the sort in the portfolio
  // render below), so re-sorting on every click would jerk a row to a new spot
  // mid click-through. Pin a row to its pre-edit position for a few seconds
  // after a milestone click -- long enough to outlast the PUT debounce above --
  // then let it settle into its true position once the user stops clicking.
  const pmSortPinned = new Map(); // code -> { pct, timer }
  function pmPinSortOrder(code, marina, statusCols) {
    let pin = pmSortPinned.get(code);
    if (!pin) {
      pin = { pct: marinaSortScore(marina, statusCols), timer: null };
      pmSortPinned.set(code, pin);
    }
    clearTimeout(pin.timer);
    pin.timer = setTimeout(() => { pmSortPinned.delete(code); render(); }, 2500);
  }
  function flushMilestonePut(id, unloading = false) {
    const p = pmMilestonePending.get(id);
    if (!p) return;
    pmMilestonePending.delete(id);
    clearTimeout(p.timer);
    if (p.val === p.base) return;
    fetch(`/api/project/${encodeURIComponent(p.code)}/milestone`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ key: p.col, value: p.val, name: p.name, clientSlug: p.clientSlug }),
      keepalive: unloading,
    }).then(async (r) => {
      const d = await r.json().catch(() => ({}));
      if (!r.ok || !d.ok) throw new Error(d.error || `HTTP ${r.status}`);
      if (d.pendingAlert && !unloading) {
        state.pendingAlertConfirm = d.pendingAlert;
        render();
      }
    }).catch((err) => {
      app.ui.toast(`Milestone save failed: ${err?.message || err}`);
      loadCatalog().catch(() => {});
    });
  }

  function sendPendingAlert() {
    const alert = state.pendingAlertConfirm;
    if (!alert) return;
    state.pendingAlertConfirm = null;
    render();
    fetch("/api/alerts/send", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ruleId: alert.ruleId, code: alert.code, marinaName: alert.marinaName, timestamp: alert.timestamp }),
    }).then(async (r) => {
      const d = await r.json().catch(() => ({}));
      if (!r.ok || !d.ok) throw new Error(d.error || `HTTP ${r.status}`);
      app.ui.toast(`Alert sent to ${alert.recipients.join(", ")}`);
    }).catch((err) => app.ui.toast(`Alert send failed: ${err?.message || err}`));
  }

  // Regenerating an existing client portal link immediately breaks the old URL for anyone
  // who has it (client staff, bookmarks, embedded elsewhere) — no undo. Actually performs the
  // rotation; callers gate this behind the confirm popup when a token already exists (see
  // data-client-deliv-gen-token / data-client-deliv-regen-confirm below). First-time generation
  // (no prior token) calls this directly since there's no live link to break yet.
  function regenerateClientDeliveryToken(clientSlug) {
    if (!clientSlug) return;
    fetch("/api/clients/delivery-token", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ clientSlug }),
    }).then(r => r.json()).then(d => {
      if (!d.ok) { app.ui.toast(`Failed: ${d.error}`); return; }
      if (!state.digitalAsset.clientDeliveryTokens) state.digitalAsset.clientDeliveryTokens = {};
      state.digitalAsset.clientDeliveryTokens[clientSlug] = { token: d.token, createdAt: d.createdAt };
      app.ui.toast("Client portal link regenerated.");
      render();
    }).catch(err => app.ui.toast(`Error: ${String(err?.message || err)}`));
  }

  // Mirrors regenerateClientDeliveryToken above, against the separate field-tracking token
  // namespace -- regenerating breaks the old field-tracking link the same way.
  function regenerateFieldTrackingToken(clientSlug) {
    if (!clientSlug) return;
    fetch("/api/field-tracking-token", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ clientSlug }),
    }).then(r => r.json()).then(d => {
      if (!d.ok) { app.ui.toast(`Failed: ${d.error}`); return; }
      if (!state.digitalAsset.fieldTrackingTokens) state.digitalAsset.fieldTrackingTokens = {};
      state.digitalAsset.fieldTrackingTokens[clientSlug] = { token: d.token, createdAt: d.createdAt };
      app.ui.toast("Field tracking link regenerated.");
      render();
    }).catch(err => app.ui.toast(`Error: ${String(err?.message || err)}`));
  }

  // Mirrors regenerateFieldTrackingToken above, against the site-wide Invoice Wizard token.
  function regenerateInvoiceWizardToken() {
    fetch("/api/invoice-wizard-token", { method: "POST" }).then(r => r.json()).then(d => {
      if (!d.ok) { app.ui.toast(`Failed: ${d.error}`); return; }
      state.digitalAsset.invoiceWizardToken = { token: d.token, createdAt: d.createdAt };
      app.ui.toast("Invoice wizard link regenerated.");
      render();
    }).catch(err => app.ui.toast(`Error: ${String(err?.message || err)}`));
  }

  // ── Alert rules editor ────────────────────────────────────────────────
  // Text fields (recipients/subject/body/id) aren't bound to state per
  // keystroke -- like the milestone-step editor above, they're read straight
  // out of the DOM at save/test time, so a full render() mid-edit can't
  // clobber the cursor or an in-progress edit in another row.
  function renderAlertPreviewTemplate(template, vars) {
    return String(template || "").replace(/\{(\w+)\}/g, (m, name) => (name in vars ? String(vars[name]) : m));
  }
  function alertRuleToDraft(r) {
    return {
      id: String(r?.id || ""),
      triggerKey: String(r?.trigger?.key || ""),
      toValue: String(r?.trigger?.toValue || "In Progress"),
      recipients: Array.isArray(r?.recipients) ? r.recipients.join(", ") : "",
      subject: String(r?.subject || ""),
      body: String(r?.body || ""),
    };
  }
  async function loadAlertRules() {
    state.alertRulesLoading = true;
    state.alertRulesLoadError = null;
    render();
    try {
      const resp = await fetch("/api/alerts/rules");
      const d = await resp.json().catch(() => ({}));
      if (!resp.ok || !d.ok) throw new Error(d.error || `HTTP ${resp.status}`);
      state.alertRulesDraft = (d.rules || []).map(alertRuleToDraft);
    } catch (err) {
      state.alertRulesLoadError = err?.message || String(err);
      state.alertRulesDraft = state.alertRulesDraft || [];
    } finally {
      state.alertRulesLoading = false;
      render();
    }
  }
  function readAlertRuleRowFromDom(idx) {
    const row = containerEl?.querySelector(`[data-ar-row="${idx}"]`);
    if (!row) return null;
    const val = (sel) => row.querySelector(sel)?.value ?? "";
    return {
      id: val("[data-ar-field='id']").trim(),
      triggerKey: val("[data-ar-field='triggerKey']"),
      toValue: val("[data-ar-field='toValue']"),
      recipients: val("[data-ar-field='recipients']"),
      subject: val("[data-ar-field='subject']"),
      body: val("[data-ar-field='body']"),
    };
  }
  function syncAlertRulesDraftFromDom() {
    if (!Array.isArray(state.alertRulesDraft)) return;
    state.alertRulesDraft = state.alertRulesDraft.map((r, i) => readAlertRuleRowFromDom(i) || r);
  }
  function addAlertRuleDraft() {
    syncAlertRulesDraftFromDom();
    if (!Array.isArray(state.alertRulesDraft)) state.alertRulesDraft = [];
    state.alertRulesDraft.push({ id: "", triggerKey: "", toValue: "In Progress", recipients: "", subject: "", body: "" });
    render();
  }
  function removeAlertRuleDraft(idx) {
    syncAlertRulesDraftFromDom();
    if (!Array.isArray(state.alertRulesDraft)) return;
    state.alertRulesDraft.splice(idx, 1);
    state.alertTestStatus = {};
    render();
  }
  async function saveAlertRules() {
    syncAlertRulesDraftFromDom();
    const rules = (state.alertRulesDraft || []).map((r) => ({
      id: r.id,
      trigger: { type: "milestone", key: r.triggerKey, toValue: r.toValue },
      recipients: String(r.recipients || "").split(",").map((s) => s.trim()).filter(Boolean),
      subject: r.subject,
      body: r.body,
    }));
    state.alertRulesSaveStatus = null;
    render();
    try {
      const resp = await fetch("/api/alerts/rules", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ rules }),
      });
      const d = await resp.json().catch(() => ({}));
      if (!resp.ok || !d.ok) throw new Error(d.error || `HTTP ${resp.status}`);
      state.alertRulesSaveStatus = { ok: true };
      app.ui.toast("Alert rules saved");
    } catch (err) {
      state.alertRulesSaveStatus = { ok: false, error: err?.message || String(err) };
    } finally {
      render();
    }
  }
  async function sendAlertTest(idx) {
    syncAlertRulesDraftFromDom();
    const row = state.alertRulesDraft?.[idx];
    if (!row) return;
    const recipientEl = containerEl?.querySelector(`[data-ar-row="${idx}"] [data-ar-test-recipient]`);
    const recipient = String(recipientEl?.value || "").trim();
    if (!recipient) { app.ui.toast("Enter a test recipient address first"); return; }
    const vars = { marinaName: "Test Marina", code: "TEST", key: row.triggerKey, from: "Not Started", to: row.toValue, timestamp: new Date().toISOString(), source: "internal" };
    const subject = renderAlertPreviewTemplate(row.subject, vars);
    const body = renderAlertPreviewTemplate(row.body, vars);
    state.alertTestStatus[idx] = "sending";
    render();
    try {
      const resp = await fetch("/api/alerts/test-send", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ recipient, subject, body, ruleId: row.id || null }),
      });
      const d = await resp.json().catch(() => ({}));
      if (!resp.ok || !d.ok) throw new Error(d.error || `HTTP ${resp.status}`);
      state.alertTestStatus[idx] = { ok: true };
      app.ui.toast(`Test email sent to ${recipient}`);
    } catch (err) {
      state.alertTestStatus[idx] = { ok: false, error: err?.message || String(err) };
    } finally {
      render();
    }
  }

  window.addEventListener("pagehide", () => {
    for (const id of [...pmMilestonePending.keys()]) flushMilestonePut(id, true);
  });

  function onClick(event) {
    // DEBUG: log every click on portfolio buttons so we can trace where it breaks
    const tgt = event.target;
    if (tgt && (tgt.hasAttribute("data-portfolio-new-client") || tgt.hasAttribute("data-portfolio-new-client-confirm") || tgt.hasAttribute("data-portfolio-new-client-cancel") || tgt.hasAttribute("data-portfolio-new-project-confirm") || tgt.closest?.("[data-portfolio-new-client-confirm]") || tgt.closest?.("[data-portfolio-new-project-confirm]"))) {
      console.log("[portfolio:click] target:", tgt.outerHTML?.slice(0, 120), "| currentTarget.id:", event.currentTarget?.id, "| currentTarget:", event.currentTarget?.tagName, event.currentTarget?.className?.slice?.(0, 40));
    }
    if (event.target?.closest?.("#imggen-project-marina-zoom")) {
      const { index } = resolveProjectSettingsMarina();
      if (index >= 0) focusItem(index, { saveCurrent: true });
      else if (state.projectSettings.selectedProjectId) zoomToCatalogProject(state.projectSettings.selectedProjectId);
      return;
    }
    if (event.target?.closest?.("#imggen-pm-pick-location")) {
      startProjectLocationPick();
      return;
    }
    const portfolioZoomBtn = event.target?.closest?.("[data-portfolio-zoom]");
    if (portfolioZoomBtn) {
      const code = String(portfolioZoomBtn.getAttribute("data-portfolio-zoom") || "");
      if (code) {
        if (!zoomToCatalogProject(code)) app.ui.toast(`No map location found for ${code}`);
      }
      return;
    }
    if (event.target?.id === "imggen-toggle-map-legend") {
      state.portfolioShowMapLegend = !state.portfolioShowMapLegend;
      drawPlanningOverlay(currentItem());
      render();
      return;
    }
    if (event.target?.closest?.("#imggen-loc-use-center")) {
      const center = map.getCenter?.();
      if (center) {
        const latEl = containerEl?.querySelector?.("#imggen-loc-lat");
        const lngEl = containerEl?.querySelector?.("#imggen-loc-lng");
        if (latEl) latEl.value = Number(center.lat).toFixed(5);
        if (lngEl) lngEl.value = Number(center.lng).toFixed(5);
      }
      return;
    }
    if (event.target?.closest?.("#imggen-loc-save")) {
      const code = String(state.projectSettings.selectedProjectId || "");
      if (!code) return;
      const latEl = containerEl?.querySelector?.("#imggen-loc-lat");
      const lngEl = containerEl?.querySelector?.("#imggen-loc-lng");
      const lat = Number(latEl?.value);
      const lng = Number(lngEl?.value);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
        app.ui.toast("Enter valid lat and lng first.");
        return;
      }
      // Optimistically update the local catalog so every subsequent render (including
      // the one that fires from loadPortfolioPoints) shows the new coords immediately.
      const _prevLat = [], _prevLng = [];
      state.catalog.marinas = (state.catalog.marinas || []).map((m, i) => {
        if (String(m.code || "").toUpperCase() !== code.toUpperCase()) return m;
        _prevLat[i] = m.lat; _prevLng[i] = m.lng;
        return { ...m, lat, lng };
      });
      const btn = event.target.closest("#imggen-loc-save");
      btn.disabled = true;
      btn.textContent = "…";
      fetch("/api/project/location", {
        method: "PATCH", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code, lat, lng }),
      }).then(r => r.json()).then(d => {
        if (d.ok) {
          app.ui.toast(`${code} location saved: ${d.lat.toFixed(5)}, ${d.lng.toFixed(5)}`);
          loadPortfolioPoints().catch(() => {});
          loadCatalog().catch(() => {});
        } else {
          // Revert optimistic update on failure
          state.catalog.marinas = (state.catalog.marinas || []).map((m, i) => {
            if (_prevLat[i] === undefined) return m;
            return { ...m, lat: _prevLat[i], lng: _prevLng[i] };
          });
          app.ui.toast(`Save failed: ${d.error}`);
          btn.disabled = false;
          btn.textContent = "Save";
        }
      }).catch(err => {
        // Revert optimistic update on error
        state.catalog.marinas = (state.catalog.marinas || []).map((m, i) => {
          if (_prevLat[i] === undefined) return m;
          return { ...m, lat: _prevLat[i], lng: _prevLng[i] };
        });
        app.ui.toast(`Save error: ${err.message}`);
        btn.disabled = false;
        btn.textContent = "Save";
      });
      return;
    }
    if (event.target?.closest?.("#imggen-rotation-use-current")) {
      const bearing = typeof map.getBearing === "function" ? Number(map.getBearing()) : 0;
      const degEl = containerEl?.querySelector?.("#imggen-rotation-deg");
      if (degEl && Number.isFinite(bearing)) degEl.value = String(Math.round(((bearing % 360) + 360) % 360));
      return;
    }
    if (event.target?.closest?.("#imggen-rotation-save")) {
      const code = String(state.projectSettings.selectedProjectId || "");
      if (!code) return;
      const degEl = containerEl?.querySelector?.("#imggen-rotation-deg");
      const rawDegrees = Number(degEl?.value);
      if (!Number.isFinite(rawDegrees)) {
        app.ui.toast("Enter a valid rotation in degrees first.");
        return;
      }
      const normalized = ((rawDegrees % 360) + 360) % 360;
      // Optimistically update the local catalog so every subsequent render shows the new
      // rotation immediately -- tracked by index/prev-value (not a sparse array keyed by
      // presence, like the coordinates save above) since mapRotationDeg being genuinely unset
      // is a real, common prior state here, not just an unmatched-row placeholder.
      let matchIdx = -1, prevRotation;
      state.catalog.marinas = (state.catalog.marinas || []).map((m, i) => {
        if (String(m.code || "").toUpperCase() !== code.toUpperCase()) return m;
        matchIdx = i; prevRotation = m.mapRotationDeg;
        return { ...m, mapRotationDeg: normalized };
      });
      const revert = () => {
        if (matchIdx < 0) return;
        state.catalog.marinas = state.catalog.marinas.map((m, i) => i === matchIdx ? { ...m, mapRotationDeg: prevRotation } : m);
      };
      const btn = event.target.closest("#imggen-rotation-save");
      btn.disabled = true;
      btn.textContent = "…";
      fetch("/api/project/map-rotation", {
        method: "PATCH", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code, degrees: normalized }),
      }).then(r => r.json()).then(d => {
        if (d.ok) {
          app.ui.toast(`${code} map rotation saved: ${Math.round(d.degrees)}°`);
          if (typeof map.setBearing === "function") map.setBearing(d.degrees);
          loadCatalog().catch(() => {});
        } else {
          revert();
          app.ui.toast(`Save failed: ${d.error}`);
          btn.disabled = false;
          btn.textContent = "Save";
        }
      }).catch(err => {
        revert();
        app.ui.toast(`Save error: ${err.message}`);
        btn.disabled = false;
        btn.textContent = "Save";
      });
      return;
    }
    const prepReportBtn = event.target?.closest?.("[data-prep-report]");
    if (prepReportBtn) {
      generatePrepReport(prepReportBtn.getAttribute("data-prep-report"));
      return;
    }
    // Project Settings card — Save / Save & Apply CRS / Set Active
    const giscardSaveBtn  = event.target?.closest?.("#giscard-save");
    const giscardActivate = event.target?.closest?.("#giscard-activate");
    if (giscardSaveBtn) {
      const projectId = giscardSaveBtn.getAttribute("data-ctx-project-id");
      if (!projectId) return;
      const ctx = app.services?.context;
      if (!ctx) return;
      const collect = (id) => containerEl.querySelector(`#${id}`)?.value?.trim() || "";
      const locLat = Number(containerEl.querySelector("#imggen-loc-lat")?.value);
      const locLng = Number(containerEl.querySelector("#imggen-loc-lng")?.value);
      const updates = {
        name:            collect("giscard-name"),
        projectNumber:   collect("giscard-projectnumber"),
        address:         collect("giscard-address"),
        city:            collect("giscard-city"),
        state:           collect("giscard-state"),
        zip:             collect("giscard-zip"),
        phone:           collect("giscard-phone"),
        gmName:          collect("giscard-gm-name"),
        gmEmail:         collect("giscard-gm-email"),
        horizontalUnits: containerEl.querySelector("#giscard-hunits")?.value || "m",
        verticalDatum:   collect("giscard-vdatum") || "MSL",
        verticalUnits:   containerEl.querySelector("#giscard-zunits")?.value || "m",
        geoidModel:      collect("giscard-geoid")  || "None",
      };
      // Only send the CRS when the user picked a different one (the picker keeps the rendered
      // value in data-initial). Re-sending an unchanged value would let a stale
      // render overwrite the project's CRS; see registerProjectWithServer's sendCrs.
      const crsSel = containerEl.querySelector("#giscard-crs");
      if (crsSel?.value && crsSel.value !== crsSel.dataset.initial) updates.horizontalCRS = crsSel.value;
      if (Number.isFinite(locLat) && Number.isFinite(locLng)) {
        updates.location = { lat: locLat, lng: locLng, zoom: 16 };
        // Immediately update local catalog so the very next render shows the new coords.
        const selectedCode = String(state.projectSettings.selectedProjectId || "");
        if (selectedCode) {
          state.catalog.marinas = (state.catalog.marinas || []).map(m =>
            String(m.code || "").toUpperCase() === selectedCode.toUpperCase()
              ? { ...m, lat: locLat, lng: locLng }
              : m
          );
        }
      }
      if (!updates.name) { app.ui?.toast?.("Name is required."); return; }
      ctx.updateProject(projectId, updates);
      ctx.applyActiveCRS?.();
      app.ui?.toast?.("Project settings saved.");
      return;
    }
    if (giscardActivate) {
      const projectId = giscardActivate.getAttribute("data-ctx-project-id");
      if (projectId) app.services?.context?.setActiveProject?.(projectId);
      return;
    }

    const geocodeBtn = event.target?.closest?.("[data-portfolio-geocode]");
    if (geocodeBtn) {
      const code = String(geocodeBtn.getAttribute("data-portfolio-geocode") || "");
      if (!code) return;
      geocodeBtn.disabled = true;
      geocodeBtn.textContent = "…";
      fetch("/api/project/geocode", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code }),
      }).then(r => r.json()).then(d => {
        if (d.ok) {
          app.ui.toast(`${code} geocoded: ${d.lat.toFixed(5)}, ${d.lng.toFixed(5)}`);
          loadPortfolioPoints().catch(() => {});
          loadCatalog().catch(() => {});
        } else {
          app.ui.toast(`Geocode failed for ${code}: ${d.error}`);
        }
      }).catch(err => app.ui.toast(`Geocode error: ${err.message}`));
      return;
    }
    const trayToggleKey = event.target?.closest?.("[data-portfolio-tray-toggle]")?.getAttribute("data-portfolio-tray-toggle");
    if (trayToggleKey && PORTFOLIO_MODES.includes(trayToggleKey)) {
      if (!state.portfolioActiveTrays) state.portfolioActiveTrays = { production: false, routing: false, "project-management": false };
      const nextOn = !state.portfolioActiveTrays[trayToggleKey];
      state.portfolioActiveTrays[trayToggleKey] = nextOn;
      if (trayToggleKey === "routing") {
        if (!state.panels) state.panels = {};
        state.panels.planningGroup = nextOn;
      }
      persistLocalState();
      render();
      return;
    }
    const mapPointsGroup = event.target?.closest?.("[data-portfolio-map-points]")?.getAttribute("data-portfolio-map-points");
    if (mapPointsGroup) {
      if (!state.portfolioMapPoints) state.portfolioMapPoints = { active: false, potential: false, archived: false };
      state.portfolioMapPoints[mapPointsGroup] = !state.portfolioMapPoints[mapPointsGroup];
      if (state.portfolioMapPoints[mapPointsGroup] && !state.portfolioPoints.length) {
        loadPortfolioPoints().catch(() => {});
      }
      drawPlanningOverlay(currentItem());
      render();
      return;
    }
    // Handle "+ New Project" regardless of whether it's inside a group row or in the empty-state UI
    if (event.target?.closest?.("[data-portfolio-new-client]")) {
      state.portfolioNewClientShow = true;
      state.portfolioNewClientName = "";
      const focusPanel = event.currentTarget;
      render();
      setTimeout(() => focusPanel?.querySelector?.("#portfolio-new-client-name")?.focus(), 80);
      return;
    }
    if (event.target?.closest?.("[data-portfolio-new-client-cancel]")) {
      state.portfolioNewClientShow = false;
      state.portfolioNewClientName = "";
      render();
      return;
    }
    if (event.target?.closest?.("[data-portfolio-new-client-confirm]")) {
      const root = event.currentTarget;
      const nameEl = root?.querySelector?.("#portfolio-new-client-name");
      const clientName = (nameEl ? String(nameEl.value || "") : state.portfolioNewClientName).trim();
      if (nameEl) state.portfolioNewClientName = clientName;
      console.log("[portfolio:new-client-confirm] clientName:", JSON.stringify(clientName), "| nameEl found:", !!nameEl, "| hasContext.createClient:", typeof app.services?.context?.createClient);
      if (!clientName) { app.ui.toast("Enter a client name."); return; }
      const confirmBtn = event.target?.closest?.("[data-portfolio-new-client-confirm]");
      if (confirmBtn) { confirmBtn.textContent = "⏳"; confirmBtn.disabled = true; }
      (async () => {
        try {
          console.log("[portfolio:new-client-confirm] fetching /api/project/client-create with name:", clientName);
          app.ui.toast(`Creating client "${clientName}"…`);
          const resp = await fetch("/api/project/client-create", {
            method: "POST",
            headers: { "Content-Type": "application/json", Accept: "application/json" },
            body: JSON.stringify({ name: clientName }),
          });
          const payload = await resp.json().catch((e) => ({ _parseErr: String(e) }));
          console.log("[portfolio:new-client-confirm] response:", resp.status, payload);
          if (!resp.ok || !payload?.ok) throw new Error(payload?.error || `HTTP ${resp.status}`);
          const saved = payload.client;
          if (!saved?.id) throw new Error("No client returned from server.");
          state.portfolioNewClientShow = false;
          state.portfolioNewClientName = "";
          // Pin the new client so the dropdown shows it immediately regardless of
          // what loadCatalog or onContextCatalogChanged does to state.catalog.clients
          state.portfolioJustCreatedClient = saved;
          state.catalog.selectedClientId = saved.id;
          render();
          app.ui.toast(`Client "${saved.name}" created.`);
          // Background refresh; clear the pin once the server catalog includes the client
          loadCatalog().then(() => {
            if (state.catalog.clients.some(c => c.id === saved.id)) {
              state.portfolioJustCreatedClient = null;
            }
            if (state.catalog.selectedClientId !== saved.id) {
              state.catalog.selectedClientId = saved.id;
              render();
            }
          }).catch(() => {});
        } catch (err) {
          console.error("[portfolio:new-client-confirm] error:", err);
          if (confirmBtn) { confirmBtn.textContent = "✓ Create"; confirmBtn.disabled = false; }
          app.ui.toast(`Create client failed: ${String(err?.message || err)}`);
        }
      })();
      return;
    }
    if (event.target?.closest?.("[data-portfolio-archive-client]")) {
      const btn = event.target.closest("[data-portfolio-archive-client]");
      const slug = String(btn.getAttribute("data-portfolio-archive-client") || "");
      const clientName = String(btn.getAttribute("data-portfolio-archive-client-name") || slug);
      if (!slug) return;
      const ok = window.confirm(
        `Archive client "${clientName}"?\n\nThis moves ALL of its projects and files out of the active catalog into clients/archive/${slug}/. ` +
        `It stops showing up anywhere in the app, but nothing is deleted — an admin can still permanently purge it later from the archive folder.`
      );
      if (!ok) return;
      btn.textContent = "⏳";
      btn.disabled = true;
      (async () => {
        try {
          const resp = await fetch(`/api/clients/${encodeURIComponent(slug)}/archive`, { method: "POST" });
          const payload = await resp.json().catch((e) => ({ _parseErr: String(e) }));
          if (!resp.ok || !payload?.ok) throw new Error(payload?.error || `HTTP ${resp.status}`);
          app.ui.toast(`Client "${clientName}" archived.`);
          state.catalog.selectedClientId = "";
          await loadCatalog().catch(() => {});
          render();
        } catch (err) {
          console.error("[portfolio:archive-client] error:", err);
          app.ui.toast(`Archive client failed: ${String(err?.message || err)}`);
          btn.textContent = "Archive Client";
          btn.disabled = false;
        }
      })();
      return;
    }
    if (event.target?.closest?.("[data-portfolio-new-project]")) {
      state.portfolioNewProjectShow = true;
      state.portfolioNewProjectCode = "";
      state.portfolioNewProjectName = "";
      state.portfolioAddGroupShow = false;
      render();
      setTimeout(() => containerEl.querySelector("#portfolio-new-project-name")?.focus(), 50);
      return;
    }
    const portfolioGroupBtn = event.target?.closest?.("[data-portfolio-group]");
    if (portfolioGroupBtn) {
      if (event.target?.closest?.("[data-portfolio-new-group]")) {
        state.portfolioAddGroupShow = true;
        state.portfolioAddGroupName = "";
        state.portfolioNewProjectShow = false;
        render();
        setTimeout(() => containerEl.querySelector("#portfolio-add-group-input")?.focus(), 50);
        return;
      }
      if (event.target?.closest?.("[data-portfolio-new-project]")) {
        state.portfolioNewProjectShow = true;
        state.portfolioNewProjectCode = "";
        state.portfolioNewProjectName = "";
        state.portfolioAddGroupShow = false;
        render();
        setTimeout(() => containerEl.querySelector("#portfolio-new-project-name")?.focus(), 50);
        return;
      }
      const purgeBtn = event.target?.closest?.("[data-portfolio-purge-archived]");
      if (purgeBtn) {
        const clientId = String(purgeBtn.getAttribute("data-portfolio-purge-archived") || "");
        const archived = (state.catalog.marinas || []).filter(m => !!m.archivedAt && String(m.clientId || m.clientSlug || "safeharbor") === clientId);
        if (!archived.length) return;
        if (!window.confirm(`Permanently delete all ${archived.length} archived projects for this client? This cannot be undone.`)) return;
        purgeBtn.textContent = "Deleting…";
        purgeBtn.disabled = true;
        (async () => {
          try {
            const resp = await fetch("/api/project/purge-archived", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ clientId }),
            });
            const result = await resp.json().catch(() => ({}));
            if (!resp.ok) app.ui?.toast?.(`Delete failed: ${result.error || `HTTP ${resp.status}`}`);
          } catch (err) {
            app.ui?.toast?.(`Delete failed: ${err?.message || err}`);
          }
          await loadCatalog();
        })();
        return;
      }
      const key = String(portfolioGroupBtn.getAttribute("data-portfolio-group") || "");
      if (key && state.portfolioGroups) {
        state.portfolioGroups[key] = !state.portfolioGroups[key];
        if (!state.portfolioGroups[key]) state.portfolioAddGroupShow = false;
        persistLocalState();
        render();
      }
      return;
    }
    const groupVisibleBtn = event.target?.closest?.("[data-portfolio-group-visible]");
    if (groupVisibleBtn) {
      const collapseKey = String(groupVisibleBtn.getAttribute("data-portfolio-group-visible") || "");
      const [clientId, ...nameParts] = collapseKey.split("::");
      const groupName = nameParts.join("::");
      const cur = getGroupMeta(clientId, groupName);
      const nowVisible = !cur.visible;
      setGroupMetaField(clientId, groupName, { visible: nowVisible });
      if (nowVisible) {
        if (!state.portfolioMapPoints) state.portfolioMapPoints = { active: false, potential: false, archived: false };
        state.portfolioMapPoints.active = true;
        if (!state.portfolioPoints.length) loadPortfolioPoints().catch(() => {});
      }
      saveClientPortfolioGroups(clientId).catch(() => {});
      persistLocalState();
      render();
      drawPlanningOverlay(currentItem());
      return;
    }
    const groupFlyInCb = event.target?.closest?.("[data-portfolio-group-flyin]");
    if (groupFlyInCb && groupFlyInCb.tagName === "INPUT") {
      const collapseKey = String(groupFlyInCb.getAttribute("data-portfolio-group-flyin") || "");
      const [clientId, ...nameParts] = collapseKey.split("::");
      const groupName = nameParts.join("::");
      setGroupMetaField(clientId, groupName, { flyIn: groupFlyInCb.checked });
      saveClientPortfolioGroups(clientId).catch(() => {});
      persistLocalState();
      render();
      return;
    }
    const groupIncludeBtn = event.target?.closest?.("[data-portfolio-group-include]");
    if (groupIncludeBtn) {
      const collapseKey = String(groupIncludeBtn.getAttribute("data-portfolio-group-include") || "");
      const [clientId, ...nameParts] = collapseKey.split("::");
      const groupName = nameParts.join("::");
      const cur = getGroupMeta(clientId, groupName);
      setGroupMetaField(clientId, groupName, { includeInCombined: !cur.includeInCombined });
      saveClientPortfolioGroups(clientId).catch(() => {});
      persistLocalState();
      render();
      return;
    }
    const groupReportBtn = event.target?.closest?.("[data-portfolio-group-report]");
    if (groupReportBtn) {
      const collapseKey = String(groupReportBtn.getAttribute("data-portfolio-group-report") || "");
      const [clientId, ...nameParts] = collapseKey.split("::");
      const groupName = nameParts.join("::");
      const gm = getGroupMeta(clientId, groupName);
      if (!gm.officeId) {
        app.ui.alertModal(`"${groupName}" doesn't have an office assigned yet.\n\nOpen the group row and pick an office from the dropdown, then try again.`, { title: "Can't Generate Trip Report" });
        return;
      }
      (async () => {
        try {
          await generateGroupTripReport(clientId, groupName, gm);
        } catch (error) {
          console.error("[ImageGenerator] group trip report failed", error);
          app.ui.toast(`Trip report failed: ${error?.message || "unknown error"}`);
        }
      })();
      return;
    }
    const customGroupBtn = event.target?.closest?.("[data-portfolio-custom-group]");
    if (customGroupBtn) {
      if (event.target?.closest?.("[data-portfolio-custom-group-toggle]")) {
        const collapseKey = String(event.target.closest("[data-portfolio-custom-group-toggle]").getAttribute("data-portfolio-custom-group-toggle") || "");
        if (!state.portfolioCustomGroupCollapsed) state.portfolioCustomGroupCollapsed = {};
        state.portfolioCustomGroupCollapsed[collapseKey] = !state.portfolioCustomGroupCollapsed[collapseKey];
        persistLocalState();
        render();
        return;
      }
      if (event.target?.closest?.("[data-portfolio-rename-group]")) {
        const collapseKey = String(customGroupBtn.getAttribute("data-portfolio-custom-group") || "");
        const [, ...nameParts] = collapseKey.split("::");
        const oldName = nameParts.join("::");
        const newName = (window.prompt("Rename group:", oldName) || "").trim();
        if (!newName || newName === oldName) return;
        const clientId = collapseKey.split("::")[0];
        const groups = state.portfolioCustomGroups?.[clientId] || [];
        state.portfolioCustomGroups[clientId] = groups.map(g => g === oldName ? newName : g);
        // migrate meta key to new name
        if (state.portfolioGroupMeta?.[clientId]?.[oldName]) {
          if (!state.portfolioGroupMeta[clientId]) state.portfolioGroupMeta[clientId] = {};
          state.portfolioGroupMeta[clientId][newName] = state.portfolioGroupMeta[clientId][oldName];
          delete state.portfolioGroupMeta[clientId][oldName];
        }
        (state.catalog.marinas || []).forEach((m, i) => {
          if (m.group === oldName) state.catalog.marinas[i] = { ...m, group: newName };
        });
        saveCatalog().catch(err => app.ui.toast(`Group rename failed: ${err?.message || err}`));
        saveClientPortfolioGroups(clientId).catch(() => {});
        persistLocalState();
        delete _htmlCache.portfolio;
        renderSection("marinaPortfolio");
        return;
      }
      if (event.target?.closest?.("[data-portfolio-delete-group]")) {
        const collapseKey = String(customGroupBtn.getAttribute("data-portfolio-custom-group") || "");
        const [clientId, ...nameParts] = collapseKey.split("::");
        const groupName = nameParts.join("::");
        if (groupName === "__unassigned__") return;
        if (!window.confirm(`Delete group "${groupName}"? Projects in this group will become unassigned.`)) return;
        state.portfolioCustomGroups[clientId] = (state.portfolioCustomGroups?.[clientId] || []).filter(g => g !== groupName);
        delete (state.portfolioCustomGroupCollapsed || {})[collapseKey];
        if (state.portfolioGroupMeta?.[clientId]) delete state.portfolioGroupMeta[clientId][groupName];
        (state.catalog.marinas || []).forEach((m, i) => {
          if (m.group === groupName) state.catalog.marinas[i] = { ...m, group: "" };
        });
        saveCatalog().catch(err => app.ui.toast(`Group delete failed: ${err?.message || err}`));
        saveClientPortfolioGroups(clientId).catch(() => {});
        persistLocalState();
        delete _htmlCache.portfolio;
        renderSection("marinaPortfolio");
        return;
      }
      return;
    }
    const addGroupConfirm = event.target?.closest?.("[data-portfolio-add-group-confirm]");
    if (addGroupConfirm) {
      const name = (containerEl.querySelector("#portfolio-add-group-input")?.value || "").trim();
      if (!name) { app.ui?.toast?.("Group name cannot be empty."); return; }
      const clientId = String(state.catalog?.selectedClientId || "");
      if (!state.portfolioCustomGroups) state.portfolioCustomGroups = {};
      if (!state.portfolioCustomGroups[clientId]) state.portfolioCustomGroups[clientId] = [];
      if (!state.portfolioCustomGroups[clientId].includes(name)) {
        state.portfolioCustomGroups[clientId].push(name);
      }
      state.portfolioAddGroupShow = false;
      state.portfolioAddGroupName = "";
      saveClientPortfolioGroups(clientId).catch(() => {});
      persistLocalState();
      render();
      return;
    }
    const addGroupCancel = event.target?.closest?.("[data-portfolio-add-group-cancel]");
    if (addGroupCancel) {
      state.portfolioAddGroupShow = false;
      state.portfolioAddGroupName = "";
      render();
      return;
    }
    const portfolioSortBtn = event.target?.closest?.("[data-portfolio-sort]");
    if (portfolioSortBtn) {
      const col = String(portfolioSortBtn.getAttribute("data-portfolio-sort") || "");
      if (col) {
        if (state.portfolioSort.col === col) {
          state.portfolioSort.dir = state.portfolioSort.dir === "asc" ? "desc" : "asc";
        } else {
          state.portfolioSort = { col, dir: "asc" };
        }
        render();
      }
      return;
    }
    const portfolioArchiveBtn = event.target?.closest?.("[data-portfolio-archive]");
    if (portfolioArchiveBtn) {
      const code = String(portfolioArchiveBtn.getAttribute("data-portfolio-archive") || "");
      if (code) archiveMarina(code);
      return;
    }
    const portfolioRestoreBtn = event.target?.closest?.("[data-portfolio-restore]");
    if (portfolioRestoreBtn) {
      const code = String(portfolioRestoreBtn.getAttribute("data-portfolio-restore") || "");
      if (code) activateMarina(code);
      return;
    }
    const portfolioDeleteBtn = event.target?.closest?.("[data-portfolio-delete]");
    if (portfolioDeleteBtn) {
      const code = String(portfolioDeleteBtn.getAttribute("data-portfolio-delete") || "");
      if (code) permanentlyDeleteMarina(code);
      return;
    }
    const portfolioCtxActivateBtn = event.target?.closest?.("[data-portfolio-ctx-activate]");
    if (portfolioCtxActivateBtn) {
      const code = String(portfolioCtxActivateBtn.getAttribute("data-portfolio-ctx-activate") || "");
      if (!code) return;
      const ctxProjects = app.services?.context?.getAllProjects?.() || [];
      const ctxProject = ctxProjects.find(p =>
        [p.marinaId, p.code, p.id].some(v => String(v || "").toUpperCase() === code.toUpperCase())
      );
      if (ctxProject) {
        app.services.context.setActiveProject(ctxProject.id);
        // setActiveProject alone left state.projectSettings.selectedProjectId (and every
        // section that reads it -- DAM, Notes, Tickets) on the previous project until the
        // next renderSection() sync; dispatch it directly too so other tools' own
        // "hydrotools:project-changed" listeners fire immediately, same as the
        // imggen-project-marina dropdown already does.
        state.projectSettings.selectedProjectId = code;
        dispatchProjectChanged(code);
      } else {
        app.ui?.toast?.(`Project ${code} not found in catalog. Try reloading the page.`);
      }
      return;
    }
    const portfolioNewProjectConfirm = event.target?.closest?.("[data-portfolio-new-project-confirm]");
    if (portfolioNewProjectConfirm) {
      const nameEl = event.currentTarget?.querySelector?.("#portfolio-new-project-name");
      const codeEl = event.currentTarget?.querySelector?.("#portfolio-new-project-code");
      if (nameEl) state.portfolioNewProjectName = String(nameEl.value || "");
      if (codeEl) state.portfolioNewProjectCode = String(codeEl.value || "").toUpperCase().replace(/[^A-Z0-9_-]/g, "");
      console.log("[portfolio:new-project-confirm] name:", JSON.stringify(state.portfolioNewProjectName), "code:", JSON.stringify(state.portfolioNewProjectCode), "| currentTarget:", event.currentTarget?.id || event.currentTarget?.tagName);
      portfolioNewProjectConfirm.textContent = "⏳";
      portfolioNewProjectConfirm.disabled = true;
      createProjectFromPortfolioForm().finally(() => {
        if (portfolioNewProjectConfirm.isConnected) {
          portfolioNewProjectConfirm.textContent = "✓ Create";
          portfolioNewProjectConfirm.disabled = false;
        }
      });
      return;
    }
    const portfolioNewProjectCancel = event.target?.closest?.("[data-portfolio-new-project-cancel]");
    if (portfolioNewProjectCancel) {
      state.portfolioNewProjectShow = false;
      state.portfolioNewProjectCode = "";
      state.portfolioNewProjectName = "";
      render();
      return;
    }
    const assetCollapseBtn = event.target?.closest?.("[data-asset-collapse]");
    if (assetCollapseBtn) {
      const catKey = String(assetCollapseBtn.getAttribute("data-asset-collapse") || "");
      if (catKey) state.digitalAsset.collapsedCategories[catKey] = state.digitalAsset.collapsedCategories[catKey] !== false ? false : true;
      render();
      return;
    }
    const assetFolderCollapseBtn = event.target?.closest?.("[data-asset-folder-collapse]");
    if (assetFolderCollapseBtn) {
      const key = String(assetFolderCollapseBtn.getAttribute("data-asset-folder-collapse") || "");
      if (key) {
        if (!state.digitalAsset.collapsedFolders) state.digitalAsset.collapsedFolders = {};
        state.digitalAsset.collapsedFolders[key] = state.digitalAsset.collapsedFolders[key] !== false ? false : true;
        render();
      }
      return;
    }
    const assetMkdirBtn = event.target?.closest?.("[data-asset-mkdir]");
    if (assetMkdirBtn) {
      const catKey = String(assetMkdirBtn.getAttribute("data-asset-mkdir") || "");
      const [code, cat] = catKey.split("::");
      if (!code || !cat) return;
      const folderName = window.prompt("Folder name:");
      if (!folderName?.trim()) return;
      const safeName = folderName.trim().replace(/[^a-zA-Z0-9._-]/g, "_").slice(0, 64);
      fetch("/api/project/asset/mkdir", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code, category: cat, folder: safeName }),
      }).then((r) => r.ok ? loadDigitalAssets(code, cat) : r.json().then((j) => app.ui.toast(`Error: ${j.error || "unknown"}`)))
        .catch((e) => app.ui.toast(`Failed: ${e.message}`));
      return;
    }
    const assetDeleteFolderBtn = event.target?.closest?.("[data-asset-delete-folder]");
    if (assetDeleteFolderBtn) {
      if (!isAdminUser()) { app.ui.toast("Deleting a folder is admin-only."); return; }
      const catKey = String(assetDeleteFolderBtn.getAttribute("data-asset-delete-folder") || "");
      const folder = String(assetDeleteFolderBtn.getAttribute("data-asset-delete-folder-name") || "");
      const count = Number(assetDeleteFolderBtn.getAttribute("data-asset-delete-folder-count") || 0);
      const [code, cat] = catKey.split("::");
      if (!code || !cat || !folder) return;
      if (!window.confirm(`Delete folder "${folder}" and all ${count} file${count !== 1 ? "s" : ""} in it, including every subfolder? This cannot be undone from HydroTools.`)) return;
      assetDeleteFolderBtn.disabled = true;
      (async () => {
        const r = await fetch("/api/project/asset/delete-folder", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ code, category: cat, folder }),
        });
        const j = await r.json().catch(() => ({}));
        if (!r.ok || !j.ok) throw new Error(j.error || `HTTP ${r.status}`);
        const sel = state.digitalAsset.selectedFiles[catKey] || {};
        for (const k of Object.keys(sel)) if (k.includes(`/${cat}/${folder}/`)) delete sel[k];
        app.ui.toast(`Deleted folder "${folder}" (${j.deleted} file${j.deleted !== 1 ? "s" : ""}).`);
        await loadDigitalAssets(code, cat);
        render();
      })().catch((err) => {
        assetDeleteFolderBtn.disabled = false;
        app.ui.toast(`Folder delete failed: ${err?.message || err}`);
      });
      return;
    }
    const assetCheckAllBtn = event.target?.closest?.("[data-asset-check-all]");
    if (assetCheckAllBtn) {
      const catKey = String(assetCheckAllBtn.getAttribute("data-asset-check-all") || "");
      if (catKey) {
        const [code, cat] = catKey.split("::");
        const files = (state.digitalAsset.assetsByCategory[code] || {})[cat] || [];
        const sel = state.digitalAsset.selectedFiles[catKey] || {};
        const allSel = files.length > 0 && files.every((f) => sel[f.key]);
        state.digitalAsset.selectedFiles[catKey] = allSel ? {} : Object.fromEntries(files.map((f) => [f.key, true]));
        render();
      }
      return;
    }
    const assetRemoveSelBtn = event.target?.closest?.("[data-asset-remove-selected]");
    if (assetRemoveSelBtn) {
      const catKey = String(assetRemoveSelBtn.getAttribute("data-asset-remove-selected") || "");
      if (!catKey) return;
      const sel = state.digitalAsset.selectedFiles[catKey] || {};
      const keys = Object.keys(sel).filter((k) => sel[k]);
      if (!keys.length) return;
      if (!window.confirm(`Delete ${keys.length} file${keys.length !== 1 ? "s" : ""}?`)) return;
      (async () => {
        await Promise.all(keys.map((k) => fetch(`/api/project/asset?key=${encodeURIComponent(k)}`, { method: "DELETE" })));
        state.digitalAsset.selectedFiles[catKey] = {};
        const [code, cat] = catKey.split("::");
        await loadDigitalAssets(code, cat);
        render();
      })().catch((err) => app.ui.toast(`Delete failed: ${err?.message || err}`));
      return;
    }
    const assetOpenSpaceBtn = event.target?.closest?.("[data-asset-open-space]");
    if (assetOpenSpaceBtn) {
      const code = String(assetOpenSpaceBtn.getAttribute("data-asset-open-space") || "");
      if (!code) return;
      // Open the tab now, inside the click, so popup blockers allow it; point it at the space once
      // the server answers (it creates the project's space on first use).
      const win = window.open("about:blank", "_blank");
      (async () => {
        const r = await fetch("/api/project/pointcloud-space", {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ marina: code }),
        });
        const d = await r.json();
        if (!r.ok || !d.ok) throw new Error(d.error || `HTTP ${r.status}`);
        if (win) win.location.href = d.url; else window.open(d.url, "_blank");
      })().catch((err) => { try { win?.close(); } catch {} app.ui.toast(`Opening the 3D space failed: ${err?.message || err}`); });
      return;
    }
    const assetCombineLasBtn = event.target?.closest?.("[data-asset-combine-las]");
    if (assetCombineLasBtn) {
      const catKey = String(assetCombineLasBtn.getAttribute("data-asset-combine-las") || "");
      if (!catKey) return;
      const sel = state.digitalAsset.selectedFiles[catKey] || {};
      const keys = Object.keys(sel).filter((k) => sel[k]);
      if (keys.length < 2) return;
      const [code, cat] = catKey.split("::");
      if (!window.confirm(`Combine ${keys.length} LAS files into one new file? The originals are kept — this creates a new merged file alongside them.`)) return;
      state.digitalAsset.selectedFiles[catKey] = {};
      render();
      (async () => {
        const r = await fetch("/api/project/las/combine", {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ code, sourceKeys: keys }),
        });
        const d = await r.json();
        if (!r.ok || !d.ok) throw new Error(d.error || `HTTP ${r.status}`);
        app.ui.toast(`Combining ${keys.length} files…`);
        startCombineLasPoll(d.jobId, code, cat);
      })().catch((err) => app.ui.toast(`Combine failed: ${err?.message || err}`));
      return;
    }
    const assetBuildMosaicBtn = event.target?.closest?.("[data-asset-build-mosaic]");
    if (assetBuildMosaicBtn) {
      const catKey = String(assetBuildMosaicBtn.getAttribute("data-asset-build-mosaic") || "");
      const folder = String(assetBuildMosaicBtn.getAttribute("data-asset-build-mosaic-folder") || "");
      if (!catKey || !folder) return;
      const [code] = catKey.split("::");
      if (!window.confirm(`Mosaic the per-ping GeoTIFFs in "${folder}" into one file? The originals are kept -- the result is sent to GeoTIFF Files.`)) return;
      (async () => {
        const r = await fetch("/api/project/minsas/mosaic", {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ code, folder }),
        });
        const d = await r.json();
        if (!r.ok || !d.ok) throw new Error(d.error || `HTTP ${r.status}`);
        app.ui.toast(`Building mosaic from ${d.sourceCount} tiles…`);
        startMinsasMosaicPoll(d.jobId, code, "geotiff-files");
      })().catch((err) => app.ui.toast(`Mosaic failed: ${err?.message || err}`));
      return;
    }
    const tilesetLoadBtn = event.target?.closest?.("[data-tileset-load]");
    if (tilesetLoadBtn) {
      const jobId = String(tilesetLoadBtn.getAttribute("data-tileset-load") || "");
      const code = String(tilesetLoadBtn.getAttribute("data-tileset-code") || "");
      const entry = (state.digitalAsset.assetsByCategory[code]?.["tilesets"] || []).find((e) => e.jobId === jobId);
      if (entry) {
        if (!pointCloudRuntime) remountPointCloudRuntime();
        pointCloudRuntime?.loadTilesetOnMap?.(entry);
      }
      return;
    }
    const tilesetDeleteBtn = event.target?.closest?.("[data-tileset-delete]");
    if (tilesetDeleteBtn) {
      const jobId = String(tilesetDeleteBtn.getAttribute("data-tileset-delete") || "");
      const code = String(tilesetDeleteBtn.getAttribute("data-tileset-code") || "");
      if (jobId && code) {
        pointCloudRuntime?.removeTilesetFromMap?.(jobId);
        fetch(`/api/project/tilesets/${encodeURIComponent(code)}/${encodeURIComponent(jobId)}`, { method: "DELETE" })
          .then(() => loadDigitalAssets(code, "tilesets"))
          .catch((err) => app.ui.toast(`Delete failed: ${String(err?.message || err)}`));
      }
      return;
    }
    const tilesetExpBtn = event.target?.closest?.("[data-tileset-exp]");
    if (tilesetExpBtn) {
      const jobId = String(tilesetExpBtn.getAttribute("data-tileset-exp") || "");
      const cogPath = String(tilesetExpBtn.getAttribute("data-tileset-exp-path") || "").trim();
      if (!cogPath) return;
      const fmtSel = tilesetExpBtn.parentElement?.querySelector(`select[data-tileset-exp-fmt="${CSS.escape(jobId)}"]`);
      const fmt = fmtSel?.value || "geotiff";

      if (fmt === "geotiff") {
        const a = document.createElement("a");
        a.href = `/api/qimera/cog-export?path=${encodeURIComponent(cogPath)}&format=geotiff`;
        a.download = "";
        document.body.appendChild(a);
        a.click();
        setTimeout(() => a.remove(), 250);
        return;
      }

      // asc / xyz — async with tray progress
      const TASK_SOURCE = "cog-export";
      const fmtLabel = fmt === "asc" ? "ASCII Grid" : "XYZ";
      const emitTask = (message, progress, running = true) => {
        reportTaskStatus(app, TASK_SOURCE, {
          label: `Exporting ${fmtLabel}`, message, progress,
          status: running ? "running" : "idle",
        });
      };

      emitTask("Starting conversion…", 5);
      fetch("/api/qimera/cog-export-async", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ path: cogPath, format: fmt }),
      })
        .then((r) => r.json())
        .then((data) => {
          if (!data.ok) {
            emitTask("", null, false);
            app?.ui?.toast?.(`Export failed: ${data.error}`);
            return;
          }
          const exportJobId = data.jobId;
          let dots = 0;
          const poll = setInterval(async () => {
            try {
              const sr = await fetch(`/api/qimera/cog-export-status/${exportJobId}`);
              const sd = await sr.json();
              if (sd.status === "done") {
                clearInterval(poll);
                emitTask("Downloading…", 100);
                const a = document.createElement("a");
                a.href = `/api/qimera/cog-export-download/${exportJobId}`;
                a.download = "";
                document.body.appendChild(a);
                a.click();
                setTimeout(() => {
                  a.remove();
                  emitTask("", null, false);
                }, 2000);
              } else if (sd.status === "error") {
                clearInterval(poll);
                emitTask("", null, false);
                app?.ui?.toast?.(`Export failed: ${sd.error}`);
              } else {
                dots = (dots + 1) % 4;
                emitTask(`Converting${".".repeat(dots + 1)}`, sd.progress ?? 10);
              }
            } catch {
              clearInterval(poll);
              emitTask("", null, false);
            }
          }, 1500);
        })
        .catch(() => {
          emitTask("", null, false);
          app?.ui?.toast?.("Export request failed");
        });
      return;
    }
    const setDepthBtn = event.target?.closest?.("[data-asset-set-depth]");
    if (setDepthBtn) {
      const s3Key = String(setDepthBtn.getAttribute("data-asset-set-depth") || "");
      const code = String(setDepthBtn.getAttribute("data-asset-set-depth-code") || "");
      if (!s3Key || !code) return;
      const epsgRaw = String(state.projectSettings.epsgCode || "2227").trim();
      const epsg = epsgRaw.startsWith("EPSG:") ? epsgRaw : `EPSG:${epsgRaw}`;
      const unit = state.projectSettings.nativeZUnit === "m" ? "m" : "ft";
      setDepthLayer(code, s3Key, epsg, unit);
      return;
    }
    const depthBuildBtn = event.target?.closest?.("[data-depth-build]");
    if (depthBuildBtn) {
      const code = String(depthBuildBtn.getAttribute("data-depth-build") || "");
      if (!code) return;
      fetch("/api/depth/build-raster", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ marina: code }),
      }).catch(() => {});
      startDepthBuildPoll(code);
      return;
    }
    // ── Delivery portal handlers ──────────────────────────────────────────────
    const sendToDelivBtn = event.target?.closest?.("[data-send-to-deliv]");
    if (sendToDelivBtn) {
      const sourceKey = String(sendToDelivBtn.getAttribute("data-send-to-deliv") || "");
      const filename = String(sendToDelivBtn.getAttribute("data-send-to-deliv-name") || "");
      const size = Number(sendToDelivBtn.getAttribute("data-send-to-deliv-size") || 0);
      const subfolder = String(sendToDelivBtn.getAttribute("data-send-to-deliv-cat") || "");
      const todayStr = new Date().toISOString().slice(0, 10);
      // sourceName is the DAM file's own name, kept around only for the "from ..." caption
      // in the modal -- filename is what actually gets sent, and changes as soon as a
      // deliverable type is picked (or the user edits it directly).
      state.digitalAsset.sendToDeliverables = { sourceKey, filename, sourceName: filename, size, subfolder, targetFolder: todayStr, kind: null, epsgCode: "" };
      render();
      return;
    }
    if (event.target?.closest?.("[data-deliv-send-cancel]")) {
      state.digitalAsset.sendToDeliverables = null;
      render();
      return;
    }
    const delivSendConfirm = event.target?.closest?.("[data-deliv-send-confirm]");
    if (delivSendConfirm) {
      const code = String(delivSendConfirm.getAttribute("data-deliv-send-confirm") || "");
      const s = state.digitalAsset.sendToDeliverables;
      if (!code || !s?.sourceKey) return;
      const folderInput = containerEl?.querySelector?.("[data-deliv-folder-input]");
      const targetFolder = (folderInput?.value || s.targetFolder || "").trim();
      if (!targetFolder) { app.ui.toast("Enter a folder name"); return; }
      if (s.kind === "slam_las" && !String(s.epsgCode || "").replace(/[^0-9]/g, "")) {
        app.ui.toast("SLAM LAS deliverables need an EPSG code");
        return;
      }
      // For a standard kind, always re-derive the canonical filename here rather than trust
      // whatever's in the (normally disabled) input -- the naming convention is a fixed
      // contract clients ingest against (see deliverableFilename), so a standard-kind
      // delivery can never end up under a stale/hand-edited name, e.g. a raw DAM export's
      // own filename carrying a LAS-only EPSG suffix that doesn't belong on a TIFF.
      let filename;
      if (s.kind) {
        const ctxProject = (app.services?.context?.getAllProjects?.() || []).find((p) =>
          [p.id, p.marinaId, p.code].some((v) => String(v || "").toUpperCase() === code.toUpperCase())
        ) || null;
        try {
          filename = deliverableFilename({
            projectNumber: ctxProject?.projectNumber, code, kind: s.kind,
            epsgCode: s.kind === "slam_las" ? s.epsgCode : undefined,
          });
        } catch (err) {
          app.ui.toast(`Failed: ${err?.message || err}`);
          return;
        }
      } else {
        const filenameInput = containerEl?.querySelector?.("[data-deliv-filename-input]");
        filename = (filenameInput?.value || s.filename || "").trim();
      }
      if (!filename) { app.ui.toast("Enter a filename"); return; }
      s.targetFolder = targetFolder;
      s.filename = filename;
      fetch("/api/project/deliverables/link", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code, folder: s.targetFolder, subfolder: s.subfolder || "", filename: s.filename, sourceKey: s.sourceKey, size: s.size, kind: s.kind || undefined }),
      }).then(r => r.json()).then(d => {
        if (!d.ok) { app.ui.toast(`Failed: ${d.error}`); return; }
        if (!state.digitalAsset.deliveryManifest) state.digitalAsset.deliveryManifest = {};
        state.digitalAsset.deliveryManifest[code] = d.entries;
        state.digitalAsset.sendToDeliverables = null;
        render();
        app.ui.toast(`Sent to Deliverables › ${s.targetFolder}`);
      }).catch(err => app.ui.toast(`Error: ${String(err?.message || err)}`));
      return;
    }
    const delivFolderPick = event.target?.closest?.("[data-deliv-folder-pick]");
    if (delivFolderPick) {
      const f = String(delivFolderPick.getAttribute("data-deliv-folder-pick") || "");
      if (state.digitalAsset.sendToDeliverables) {
        state.digitalAsset.sendToDeliverables.targetFolder = f;
        render();
      }
      return;
    }
    const generateDeliverablesBtn = event.target?.closest?.("[data-generate-deliverables]");
    if (generateDeliverablesBtn) {
      const code = String(generateDeliverablesBtn.getAttribute("data-generate-deliverables") || "");
      if (!code) return;
      // The Slip Inventory tool owns the actual slip records/geometry this needs, so it does
      // the generating -- this just asks it to open its picker, same cross-tool pattern as
      // hydrotools:project-changed. If that tool was never enabled for this user there's
      // nothing listening and this is a silent no-op, since there'd be no slip data anyway.
      document.dispatchEvent(new CustomEvent("hydrotools:generate-deliverables", { detail: { code } }));
      return;
    }
    const delivGenToken = event.target?.closest?.("[data-deliv-gen-token]");
    if (delivGenToken) {
      const code = String(delivGenToken.getAttribute("data-deliv-gen-token") || "");
      if (!code) return;
      const action = (state.digitalAsset.deliveryTokens || {})[code] ? "regenerate" : "generate";
      if (action === "regenerate" && !window.confirm("Regenerate the share link? The old URL will stop working.")) return;
      fetch("/api/project/delivery-token", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code }),
      }).then(r => r.json()).then(d => {
        if (!d.ok) { app.ui.toast(`Failed: ${d.error}`); return; }
        if (!state.digitalAsset.deliveryTokens) state.digitalAsset.deliveryTokens = {};
        state.digitalAsset.deliveryTokens[code] = { token: d.token, createdAt: d.createdAt };
        render();
      }).catch(err => app.ui.toast(`Error: ${String(err?.message || err)}`));
      return;
    }
    const delivRevokeToken = event.target?.closest?.("[data-deliv-revoke-token]");
    if (delivRevokeToken) {
      const code = String(delivRevokeToken.getAttribute("data-deliv-revoke-token") || "");
      if (!code || !window.confirm("Revoke the share link? Clients will lose access.")) return;
      fetch(`/api/project/delivery-token?code=${encodeURIComponent(code)}`, { method: "DELETE" })
        .then(r => r.json()).then(d => {
          if (!d.ok) { app.ui.toast(`Failed: ${d.error}`); return; }
          if (!state.digitalAsset.deliveryTokens) state.digitalAsset.deliveryTokens = {};
          state.digitalAsset.deliveryTokens[code] = null;
          render();
        }).catch(err => app.ui.toast(`Error: ${String(err?.message || err)}`));
      return;
    }
    const delivCopyLink = event.target?.closest?.("[data-deliv-copy-link]");
    if (delivCopyLink) {
      const url = String(delivCopyLink.getAttribute("data-deliv-copy-link") || "");
      if (url) navigator.clipboard?.writeText(url).then(() => app.ui.toast("Link copied!")).catch(() => app.ui.toast("Copy failed"));
      return;
    }
    const clientDelivGenToken = event.target?.closest?.("[data-client-deliv-gen-token]");
    if (clientDelivGenToken) {
      const clientSlug = String(clientDelivGenToken.getAttribute("data-client-deliv-gen-token") || "");
      if (!clientSlug) return;
      const action = (state.digitalAsset.clientDeliveryTokens || {})[clientSlug]?.token ? "regenerate" : "generate";
      if (action === "regenerate") {
        // No live link to break yet on first-time generation -- only an existing, possibly
        // already-shared link needs the extra confirm step.
        state.pendingClientDelivRegenConfirm = { clientSlug };
        render();
        return;
      }
      regenerateClientDeliveryToken(clientSlug);
      return;
    }
    if (event.target?.closest?.("[data-client-deliv-regen-confirm]")) {
      const pending = state.pendingClientDelivRegenConfirm;
      state.pendingClientDelivRegenConfirm = null;
      render();
      if (pending?.clientSlug) regenerateClientDeliveryToken(pending.clientSlug);
      return;
    }
    if (event.target?.closest?.("[data-client-deliv-regen-dismiss]")) {
      state.pendingClientDelivRegenConfirm = null;
      render();
      return;
    }
    const clientDelivRevokeToken = event.target?.closest?.("[data-client-deliv-revoke-token]");
    if (clientDelivRevokeToken) {
      const clientSlug = String(clientDelivRevokeToken.getAttribute("data-client-deliv-revoke-token") || "");
      if (!clientSlug || !window.confirm("Revoke the client portal link? The client will lose access to all project deliverables.")) return;
      fetch(`/api/clients/delivery-token?clientSlug=${encodeURIComponent(clientSlug)}`, { method: "DELETE" })
        .then(r => r.json()).then(d => {
          if (!d.ok) { app.ui.toast(`Failed: ${d.error}`); return; }
          if (!state.digitalAsset.clientDeliveryTokens) state.digitalAsset.clientDeliveryTokens = {};
          state.digitalAsset.clientDeliveryTokens[clientSlug] = null;
          render();
        }).catch(err => app.ui.toast(`Error: ${String(err?.message || err)}`));
      return;
    }
    const clientDelivCopyLink = event.target?.closest?.("[data-client-deliv-copy-link]");
    if (clientDelivCopyLink) {
      const url = String(clientDelivCopyLink.getAttribute("data-client-deliv-copy-link") || "");
      if (url) navigator.clipboard?.writeText(url).then(() => app.ui.toast("Link copied!")).catch(() => app.ui.toast("Copy failed"));
      return;
    }
    // Field tracking link controls -- mirrors the client-deliv-* block above, against the
    // separate field-tracking token namespace (never handed to a client).
    const fieldTrackGenToken = event.target?.closest?.("[data-field-track-gen-token]");
    if (fieldTrackGenToken) {
      const clientSlug = String(fieldTrackGenToken.getAttribute("data-field-track-gen-token") || "");
      if (!clientSlug) return;
      const action = (state.digitalAsset.fieldTrackingTokens || {})[clientSlug]?.token ? "regenerate" : "generate";
      if (action === "regenerate") {
        state.pendingFieldTrackRegenConfirm = { clientSlug };
        render();
        return;
      }
      regenerateFieldTrackingToken(clientSlug);
      return;
    }
    if (event.target?.closest?.("[data-field-track-regen-confirm]")) {
      const pending = state.pendingFieldTrackRegenConfirm;
      state.pendingFieldTrackRegenConfirm = null;
      render();
      if (pending?.clientSlug) regenerateFieldTrackingToken(pending.clientSlug);
      return;
    }
    if (event.target?.closest?.("[data-field-track-regen-dismiss]")) {
      state.pendingFieldTrackRegenConfirm = null;
      render();
      return;
    }
    const fieldTrackRevokeToken = event.target?.closest?.("[data-field-track-revoke-token]");
    if (fieldTrackRevokeToken) {
      const clientSlug = String(fieldTrackRevokeToken.getAttribute("data-field-track-revoke-token") || "");
      if (!clientSlug || !window.confirm("Revoke the field tracking link? Field crews using it will lose access.")) return;
      fetch(`/api/field-tracking-token?clientSlug=${encodeURIComponent(clientSlug)}`, { method: "DELETE" })
        .then(r => r.json()).then(d => {
          if (!d.ok) { app.ui.toast(`Failed: ${d.error}`); return; }
          if (!state.digitalAsset.fieldTrackingTokens) state.digitalAsset.fieldTrackingTokens = {};
          state.digitalAsset.fieldTrackingTokens[clientSlug] = null;
          render();
        }).catch(err => app.ui.toast(`Error: ${String(err?.message || err)}`));
      return;
    }
    const fieldTrackCopyLink = event.target?.closest?.("[data-field-track-copy-link]");
    if (fieldTrackCopyLink) {
      const url = String(fieldTrackCopyLink.getAttribute("data-field-track-copy-link") || "");
      if (url) navigator.clipboard?.writeText(url).then(() => app.ui.toast("Link copied!")).catch(() => app.ui.toast("Copy failed"));
      return;
    }
    // Invoice wizard link controls -- a single site-wide link (not per-client) for contract
    // staff to log hours and export their own PDF/Excel invoice; nothing about it is ever
    // saved server-side beyond the token, so there's no confirm dialog beyond the plain
    // window.confirm below (regenerating just breaks the old link, same as revoking).
    if (event.target?.closest?.("[data-invoice-wizard-gen-token]")) {
      const hasToken = !!state.digitalAsset.invoiceWizardToken?.token;
      if (hasToken && !window.confirm("Regenerate the invoice wizard link? The old link will stop working immediately.")) return;
      regenerateInvoiceWizardToken();
      return;
    }
    if (event.target?.closest?.("[data-invoice-wizard-revoke-token]")) {
      if (!window.confirm("Revoke the invoice wizard link? Contract staff using it will lose access.")) return;
      fetch("/api/invoice-wizard-token", { method: "DELETE" }).then(r => r.json()).then(d => {
        if (!d.ok) { app.ui.toast(`Failed: ${d.error}`); return; }
        state.digitalAsset.invoiceWizardToken = null;
        render();
      }).catch(err => app.ui.toast(`Error: ${String(err?.message || err)}`));
      return;
    }
    const invoiceWizardCopyLink = event.target?.closest?.("[data-invoice-wizard-copy-link]");
    if (invoiceWizardCopyLink) {
      const url = String(invoiceWizardCopyLink.getAttribute("data-invoice-wizard-copy-link") || "");
      if (url) navigator.clipboard?.writeText(url).then(() => app.ui.toast("Link copied!")).catch(() => app.ui.toast("Copy failed"));
      return;
    }
    const pmStatusBtn = event.target?.closest?.("[data-pm-status-code]");
    if (pmStatusBtn) {
      const code = String(pmStatusBtn.getAttribute("data-pm-status-code") || "");
      const col  = String(pmStatusBtn.getAttribute("data-pm-status-col") || "");
      const cur  = String(pmStatusBtn.getAttribute("data-pm-status-val") || "Not Started");
      const cycle = ["Not Started", "In Progress", "Complete", "On Hold", "N/A"];
      const next = cycle[(cycle.indexOf(cur) + 1) % cycle.length];
      if (code && col) {
        const idx = (state.catalog.marinas || []).findIndex((m) => m.code === code);
        const row = idx >= 0 ? state.catalog.marinas[idx] : null;
        if (idx >= 0) {
          pmPinSortOrder(code, row, getClientMilestoneSchema(row.clientSlug || row.clientId || ""));
          state.catalog.marinas[idx] = {
            ...row,
            statusColumns: { ...(row.statusColumns || {}), [col]: next },
          };
          render();
        }
        // Milestones are saved through their dedicated endpoint — never via the
        // bulk catalog PUT, which the server now bars from changing them.
        const id = `${code}|${col}`;
        let p = pmMilestonePending.get(id);
        if (!p) {
          p = { base: cur, code, col, name: row?.name || code, clientSlug: row?.clientSlug || row?.clientId || "" };
          pmMilestonePending.set(id, p);
        }
        p.val = next;
        clearTimeout(p.timer);
        p.timer = setTimeout(() => flushMilestonePut(id), 2000);
      }
      return;
    }
    if (event.target?.closest?.("[data-alert-confirm-send]")) {
      sendPendingAlert();
      return;
    }
    if (event.target?.closest?.("[data-alert-confirm-dismiss]")) {
      state.pendingAlertConfirm = null;
      render();
      return;
    }
    if (event.target?.closest?.("[data-ms-toggle]")) {
      state.milestoneSchemaEditorOpen = !state.milestoneSchemaEditorOpen;
      render();
      return;
    }
    if (event.target?.closest?.("[data-ar-toggle]")) {
      state.alertRulesEditorOpen = !state.alertRulesEditorOpen;
      if (state.alertRulesEditorOpen && state.alertRulesDraft === null) loadAlertRules();
      else render();
      return;
    }
    if (event.target?.closest?.("[data-ar-add-rule]")) {
      addAlertRuleDraft();
      return;
    }
    const arRemoveBtn = event.target?.closest?.("[data-ar-remove]");
    if (arRemoveBtn) {
      removeAlertRuleDraft(parseInt(arRemoveBtn.getAttribute("data-ar-remove"), 10));
      return;
    }
    if (event.target?.closest?.("[data-ar-save]")) {
      saveAlertRules();
      return;
    }
    const arTestBtn = event.target?.closest?.("[data-ar-test]");
    if (arTestBtn) {
      sendAlertTest(parseInt(arTestBtn.getAttribute("data-ar-test"), 10));
      return;
    }
    if (event.target?.closest?.("[data-msch-toggle]")) {
      state.milestoneChangesOpen = !state.milestoneChangesOpen;
      if (state.milestoneChangesOpen && !Array.isArray(state.milestoneChanges?.rows)) {
        loadMilestoneChanges("7");
      } else {
        render();
      }
      return;
    }
    const mschRangeBtn = event.target?.closest?.("[data-msch-range]");
    if (mschRangeBtn) {
      loadMilestoneChanges(String(mschRangeBtn.getAttribute("data-msch-range") || "7"));
      return;
    }
    if (event.target?.closest?.("[data-msch-print]")) {
      printMilestoneChanges();
      return;
    }
    const msUpBtn = event.target?.closest?.("[data-ms-up]");
    if (msUpBtn) {
      const idx = parseInt(msUpBtn.getAttribute("data-ms-up"), 10);
      const clientSlug = msUpBtn.closest("[data-ms-client]")?.getAttribute("data-ms-client") || "";
      if (!clientSlug || idx <= 0) return;
      const schema = [...getClientMilestoneSchema(clientSlug)];
      document.querySelectorAll("#ms-step-rows [data-ms-edit-label]").forEach((inp) => {
        const i = parseInt(inp.getAttribute("data-ms-edit-label"), 10);
        if (schema[i]) schema[i] = { ...schema[i], label: inp.value || schema[i].label };
      });
      [schema[idx - 1], schema[idx]] = [schema[idx], schema[idx - 1]];
      if (!state.digitalAsset.milestoneSchemas) state.digitalAsset.milestoneSchemas = {};
      state.digitalAsset.milestoneSchemas[clientSlug] = schema;
      render();
      saveClientMilestoneSchema(clientSlug, schema).catch(() => {});
      return;
    }
    const msDnBtn = event.target?.closest?.("[data-ms-dn]");
    if (msDnBtn) {
      const idx = parseInt(msDnBtn.getAttribute("data-ms-dn"), 10);
      const clientSlug = msDnBtn.closest("[data-ms-client]")?.getAttribute("data-ms-client") || "";
      const schema = [...getClientMilestoneSchema(clientSlug)];
      if (!clientSlug || idx >= schema.length - 1) return;
      document.querySelectorAll("#ms-step-rows [data-ms-edit-label]").forEach((inp) => {
        const i = parseInt(inp.getAttribute("data-ms-edit-label"), 10);
        if (schema[i]) schema[i] = { ...schema[i], label: inp.value || schema[i].label };
      });
      [schema[idx], schema[idx + 1]] = [schema[idx + 1], schema[idx]];
      if (!state.digitalAsset.milestoneSchemas) state.digitalAsset.milestoneSchemas = {};
      state.digitalAsset.milestoneSchemas[clientSlug] = schema;
      render();
      saveClientMilestoneSchema(clientSlug, schema).catch(() => {});
      return;
    }
    const msRemoveBtn = event.target?.closest?.("[data-ms-remove]");
    if (msRemoveBtn) {
      const idx = parseInt(msRemoveBtn.getAttribute("data-ms-remove"), 10);
      const clientSlug = msRemoveBtn.closest("[data-ms-client]")?.getAttribute("data-ms-client") || "";
      if (!clientSlug) return;
      const schema = [...getClientMilestoneSchema(clientSlug)];
      schema.splice(idx, 1);
      if (!state.digitalAsset.milestoneSchemas) state.digitalAsset.milestoneSchemas = {};
      state.digitalAsset.milestoneSchemas[clientSlug] = schema;
      render();
      saveClientMilestoneSchema(clientSlug, schema).catch(() => {});
      return;
    }
    const msAddBtn = event.target?.closest?.("[data-ms-add-step]");
    if (msAddBtn) {
      const clientSlug = msAddBtn.closest("[data-ms-client]")?.getAttribute("data-ms-client") || "";
      if (!clientSlug) return;
      const schema = [...getClientMilestoneSchema(clientSlug), { key: `step${Date.now()}`, label: "New Step" }];
      if (!state.digitalAsset.milestoneSchemas) state.digitalAsset.milestoneSchemas = {};
      state.digitalAsset.milestoneSchemas[clientSlug] = schema;
      render();
      saveClientMilestoneSchema(clientSlug, schema).catch(() => {});
      return;
    }
    const msSaveBtn = event.target?.closest?.("[data-ms-save-schema]");
    if (msSaveBtn) {
      const clientSlug = msSaveBtn.closest("[data-ms-client]")?.getAttribute("data-ms-client") || "";
      if (!clientSlug) return;
      // Rebuild from the label inputs, but carry each step's existing properties over
      // (hideOnPortal has no UI here -- dropping it silently changed the portal's "Fully
      // Complete" count). Keep established keys too: re-keying from a renamed label orphans
      // every project's stored value for that step. Only placeholder `step<ts>` keys from
      // "Add Step" get re-derived from their label.
      const current = getClientMilestoneSchema(clientSlug);
      const inputs = document.querySelectorAll("#ms-step-rows [data-ms-edit-label]");
      const schema = [];
      for (const labelInput of inputs) {
        const label = (labelInput.value || "").trim();
        if (!label) continue;
        const prev = current[parseInt(labelInput.getAttribute("data-ms-edit-label"), 10)];
        const key = prev?.key && !/^step\d+$/.test(prev.key) ? prev.key : labelToKey(label);
        schema.push({ ...(prev || {}), key, label });
      }
      if (!schema.length) { app.ui.toast("Schema must have at least one step"); return; }
      saveClientMilestoneSchema(clientSlug, schema)
        .then(() => app.ui.toast("Milestone schema saved"))
        .catch((err) => app.ui.toast(`Save failed: ${String(err?.message || err)}`));
      return;
    }
    const msResetBtn = event.target?.closest?.("[data-ms-reset-schema]");
    if (msResetBtn) {
      const clientSlug = msResetBtn.closest("[data-ms-client]")?.getAttribute("data-ms-client") || "";
      if (!clientSlug) return;
      if (!window.confirm("Reset milestone schema to default? This will overwrite the current schema.")) return;
      saveClientMilestoneSchema(clientSlug, DEFAULT_MILESTONE_SCHEMA)
        .then(() => app.ui.toast("Schema reset to default"))
        .catch((err) => app.ui.toast(`Reset failed: ${String(err?.message || err)}`));
      return;
    }
    const delivRemoveLink = event.target?.closest?.("[data-deliv-remove-link]");
    if (delivRemoveLink) {
      const code = String(delivRemoveLink.getAttribute("data-deliv-remove-code") || "");
      const folder = String(delivRemoveLink.getAttribute("data-deliv-remove-folder") || "");
      const subfolder = String(delivRemoveLink.getAttribute("data-deliv-remove-subfolder") || "");
      const filename = String(delivRemoveLink.getAttribute("data-deliv-remove-file") || "");
      if (!code || !folder || !filename) return;
      if (!window.confirm(`Remove "${filename}" from Deliverables?`)) return;
      fetch("/api/project/deliverables/link", {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code, folder, subfolder, filename }),
      }).then(r => r.json()).then(d => {
        if (!d.ok) { app.ui.toast(`Failed: ${d.error}`); return; }
        if (!state.digitalAsset.deliveryManifest) state.digitalAsset.deliveryManifest = {};
        state.digitalAsset.deliveryManifest[code] = d.entries;
        render();
      }).catch(err => app.ui.toast(`Error: ${String(err?.message || err)}`));
      return;
    }
    const delivDownloadAll = event.target?.closest?.("[data-deliv-download-all]");
    if (delivDownloadAll) {
      const code = String(delivDownloadAll.getAttribute("data-deliv-download-all-code") || "");
      const folder = String(delivDownloadAll.getAttribute("data-deliv-download-all-folder") || "");
      if (!code || !folder) return;
      const manifest = (state.digitalAsset.deliveryManifest || {})[code] || [];
      const files = manifest.filter((e) => e.folder === folder);
      if (!files.length) { app.ui.toast("No files in this folder."); return; }
      app.ui.toast(`Downloading ${files.length} file${files.length === 1 ? "" : "s"}…`);
      // Individual downloads, not the ZIP -- staggered so the browser doesn't treat a burst of
      // simultaneous downloads as a multi-download attack and block them outright. 350ms used
      // to still land inside that burst-detection window often enough that one file of a
      // 5-file folder would come up silently missing (nothing fires a JS event for a blocked
      // download, so there's no way to detect/retry it -- the browser just drops it and shows
      // its one-time "allow multiple downloads?" prompt). 900ms spaces clicks out enough to
      // reliably read as separate user-triggered downloads rather than a burst, at the cost of
      // a few extra seconds for a large folder.
      files.forEach((e, i) => {
        setTimeout(() => {
          const a = document.createElement("a");
          a.href = `/api/project/deliverables/download?${new URLSearchParams({ code, folder, subfolder: e.subfolder || "", file: e.filename }).toString()}`;
          a.download = e.filename;
          document.body.appendChild(a);
          a.click();
          a.remove();
        }, i * 900);
      });
      return;
    }
    const delivRenameFolder = event.target?.closest?.("[data-deliv-rename-folder]");
    if (delivRenameFolder) {
      const oldName = String(delivRenameFolder.getAttribute("data-deliv-rename-folder") || "");
      const code = String(delivRenameFolder.getAttribute("data-deliv-rename-code") || "");
      if (!oldName || !code) return;
      const newName = window.prompt("Rename folder:", oldName);
      if (!newName || newName === oldName) return;
      fetch("/api/project/deliverables/folder", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code, oldName, newName }),
      }).then(r => r.json()).then(d => {
        if (!d.ok) { app.ui.toast(`Failed: ${d.error}`); return; }
        if (!state.digitalAsset.deliveryManifest) state.digitalAsset.deliveryManifest = {};
        state.digitalAsset.deliveryManifest[code] = d.entries;
        render();
      }).catch(err => app.ui.toast(`Error: ${String(err?.message || err)}`));
      return;
    }
    const assetDeleteBtn = event.target?.closest?.("[data-asset-delete]");
    if (assetDeleteBtn) {
      const key = String(assetDeleteBtn.getAttribute("data-asset-delete") || "");
      if (key) deleteAsset(key);
      return;
    }
    const assetDownloadBtn = event.target?.closest?.("[data-asset-download]");
    if (assetDownloadBtn) {
      const key = String(assetDownloadBtn.getAttribute("data-asset-download") || "");
      if (key) downloadAsset(key);
      return;
    }
    const damSpatialBtn = event.target?.closest?.("[data-dam-spatial-toggle]");
    if (damSpatialBtn) {
      const key = String(damSpatialBtn.getAttribute("data-dam-spatial-toggle") || "");
      if (key) addDamSpatialToMap(key);
      return;
    }
    const damGeotiffBtn = event.target?.closest?.("[data-dam-geotiff-toggle]");
    if (damGeotiffBtn) {
      const key = String(damGeotiffBtn.getAttribute("data-dam-geotiff-toggle") || "");
      const code = String(damGeotiffBtn.getAttribute("data-dam-geotiff-code") || "");
      if (key && !damGeotiffBtn.disabled) toggleDamGeotiffOnMap(key, code);
      return;
    }
    const lasCrsFixBtn = event.target?.closest?.("[data-las-crs-fix]");
    if (lasCrsFixBtn) {
      const key = String(lasCrsFixBtn.getAttribute("data-las-crs-fix") || "");
      const code = String(lasCrsFixBtn.getAttribute("data-las-crs-fix-code") || "");
      if (key && code) startLasCrsFix(code, key);
      return;
    }
    const damLasFootprintBtn = event.target?.closest?.("[data-dam-las-footprint-toggle]");
    if (damLasFootprintBtn) {
      const key = String(damLasFootprintBtn.getAttribute("data-dam-las-footprint-toggle") || "");
      const code = String(damLasFootprintBtn.getAttribute("data-dam-las-footprint-code") || "");
      if (key && !damLasFootprintBtn.disabled) toggleDamLasFootprintOnMap(key, code);
      return;
    }
    const assetUploadFolderBtn = event.target?.closest?.("[data-asset-upload-folder]");
    if (assetUploadFolderBtn) {
      const code = String(assetUploadFolderBtn.getAttribute("data-asset-upload-code") || "");
      const category = String(assetUploadFolderBtn.getAttribute("data-asset-upload-cat") || "");
      if (code && category) {
        // Deliberately NOT a hidden input in the panel HTML: any render() while the OS
        // folder picker is open replaces the panel's DOM, detaching such an input so its
        // change event fires on an orphaned node and the selection is silently lost. A
        // detached input held by this closure survives re-renders.
        const input = document.createElement("input");
        input.type = "file";
        input.multiple = true;
        input.webkitdirectory = true;
        input.addEventListener("change", () => {
          const files = input.files ? Array.from(input.files) : [];
          for (const f of files) {
            if (f.webkitRelativePath) f._damRelPath = f.webkitRelativePath;
          }
          if (files.length) uploadAssetFiles(code, category, files);
        });
        input.click();
      }
      return;
    }
    const assetUploadBtn = event.target?.closest?.("[data-asset-upload]");
    if (assetUploadBtn) {
      const code = String(assetUploadBtn.getAttribute("data-asset-upload-code") || "");
      const category = String(assetUploadBtn.getAttribute("data-asset-upload-cat") || "");
      if (code && category) {
        const hiddenInput = containerEl?.querySelector?.(`[data-asset-file-input="${code}::${category}"]`);
        hiddenInput?.click?.();
      }
      return;
    }
    if (event.target?.closest?.("#imggen-load")) {
      fileInputEl?.click?.();
      return;
    }

    if (event.target?.closest?.("#imggen-pm-reload")) {
      loadCatalog().catch(() => {});
      return;
    }
    if (event.target?.closest?.("#imggen-import-kml-coords")) {
      importKmlToCatalog().catch((err) => app.ui.toast(`Import failed: ${err?.message || err}`));
      return;
    }
    if (event.target?.closest?.("#imggen-pm-add")) {
      createPortfolioProjectFromDraft();
      return;
    }
    if (event.target?.closest?.("#imggen-pm-import-dataset")) {
      const existing = new Set((state.catalog.marinas || []).map((m) => String(m.code || "").toUpperCase()));
      const toAdd = (state.items || []).filter((item) => {
        const code = String(projectSettingsMarinaCode(item) || "").trim().toUpperCase();
        return code && !existing.has(code);
      }).map((item) => {
        const code = String(projectSettingsMarinaCode(item) || "").trim().toUpperCase();
        const name = String(item.name || "").trim();
        return {
          code,
          name,
          active: false,
          projectType: "marina",
          statusColumns: defaultProjectStatusColumns(),
        };
      });
      if (!toAdd.length) { app.ui.toast("All dataset marinas already in catalog."); return; }
      state.catalog.marinas = [...(state.catalog.marinas || []), ...toAdd];
      saveCatalog().catch((err) => app.ui.toast(`Catalog save failed: ${err?.message || err}`));
      render();
      app.ui.toast(`Imported ${toAdd.length} marina${toAdd.length === 1 ? "" : "s"} into catalog.`);
      return;
    }
    if (event.target?.closest?.("#imggen-prev")) {
      focusRelative(-1);
      return;
    }
    if (event.target?.closest?.("#imggen-next")) {
      focusRelative(1);
      return;
    }
    if (event.target?.closest?.("#imggen-save-view")) {
      if (!state.editMarinaViews) {
        setStatus("Enable Edit marina views to save framing changes.");
        return;
      }
      saveCurrentViewForActive();
      setStatus("Saved current map view for this preview.");
      return;
    }
    if (event.target?.closest?.("#imggen-las-browse")) {
      browseLasSource();
      return;
    }
    if (event.target?.closest?.("#imggen-las-check")) {
      checkLasToolchain();
      return;
    }
    if (event.target?.closest?.("#imggen-las-inspect")) {
      inspectLasSource();
      return;
    }
    if (event.target?.closest?.("#imggen-las-products")) {
      generateLasProducts();
      return;
    }
    if (event.target?.closest?.("#imggen-las-load-products")) {
      loadLasProductsIntoMap();
      return;
    }
    if (event.target?.closest?.("#imggen-las-training-draw")) {
      startDockTrainingDraw();
      return;
    }
    if (event.target?.closest?.("#imggen-las-training-remove")) {
      removeSelectedDockTrainingLabel();
      return;
    }
    if (event.target?.closest?.("#imggen-las-training-export")) {
      downloadDockTrainingLabels();
      return;
    }
    const labelRowBtn = event.target?.closest?.("[data-las-training-label-id]");
    if (labelRowBtn) {
      const labelId = String(labelRowBtn.getAttribute("data-las-training-label-id") || "");
      if (labelId) {
        state.lasDock.training.selectedLabelId = labelId;
        persistLocalState();
        renderDockTrainingLayers();
        render();
      }
      return;
    }
    const officeZoomBtn = event.target?.closest?.("[data-office-zoom]");
    if (officeZoomBtn) {
      const officeId = String(officeZoomBtn.getAttribute("data-office-zoom") || "");
      if (officeId) zoomToOfficeCoverage(officeId);
      return;
    }
    const officeReportBtn = event.target?.closest?.("[data-office-report]");
    if (officeReportBtn) {
      const officeId = String(officeReportBtn.getAttribute("data-office-report") || "");
      (async () => {
        try {
          await generateScopedOfficeReportPdfs(officeId);
        } catch (error) {
          console.error("[ImageGenerator] trip report failed", error);
          setStatus(`Trip report failed: ${error?.message || "unknown error"}`);
          app.ui.toast("Trip report failed.");
        }
      })();
      return;
    }
    if (event.target?.closest?.("#imggen-generate-all-reports")) {
      generateAllOfficeReports().catch((error) => {
        console.error("[ImageGenerator] generate all reports failed", error);
        setStatus(`Generate all reports failed: ${error?.message || "unknown error"}`);
        app.ui.toast("Generate all reports failed.");
      });
      return;
    }
    if (event.target?.closest?.("#imggen-generate-combined-report-book")) {
      generateCombinedReportBookPdf().catch((error) => {
        console.error("[ImageGenerator] generate combined report book failed", error);
        setStatus(`Generate combined report book failed: ${error?.message || "unknown error"}`);
        app.ui.toast("Generate combined report book failed.");
      });
      return;
    }
    if (event.target?.closest?.("[data-portfolio-combined-report]")) {
      generateCombinedGroupReportPdf().catch((error) => {
        console.error("[ImageGenerator] generate combined group report failed", error);
        setStatus(`Generate combined group report failed: ${error?.message || "unknown error"}`);
        app.ui.toast(`Combined report failed: ${error?.message || "unknown error"}`);
      });
      return;
    }
    const scheduleHistoryToggleBtn = event.target?.closest?.("[data-schedule-history-toggle]");
    if (scheduleHistoryToggleBtn) {
      const clientSlug = scheduleHistoryToggleBtn.getAttribute("data-schedule-history-toggle") || "";
      if (state.scheduleHistoryPanelOpen === clientSlug) {
        state.scheduleHistoryPanelOpen = null;
      } else {
        state.scheduleHistoryPanelOpen = clientSlug;
        if (!state.scheduleHistory?.[clientSlug]) loadScheduleHistory(clientSlug).catch(() => {});
      }
      render();
      return;
    }
    if (event.target?.closest?.("[data-schedule-history-close]")) {
      state.scheduleHistoryPanelOpen = null;
      render();
      return;
    }
    const scheduleHistoryRestoreBtn = event.target?.closest?.("[data-schedule-history-restore]");
    if (scheduleHistoryRestoreBtn) {
      const raw = scheduleHistoryRestoreBtn.getAttribute("data-schedule-history-restore") || "";
      const [clientSlug, ...rest] = raw.split("::");
      const versionId = rest.join("::");
      if (clientSlug && versionId && window.confirm(
        "Restore the planned Acquisition/Processing dates shown to this checkpoint?\n\nThis only changes schedule dates -- group colors, visibility, and milestone status are untouched."
      )) {
        restoreScheduleCheckpoint(clientSlug, versionId).catch(() => {});
      }
      return;
    }
    if (event.target?.closest?.("[data-portfolio-export-xls]")) {
      exportPortfolioStatusExcel().catch((error) => {
        console.error("[ImageGenerator] portfolio Excel export failed", error);
        app.ui.toast(`Portfolio export failed: ${error?.message || "unknown error"}`);
      });
      return;
    }
    const officeExcelBtn = event.target?.closest?.("[data-office-excel]");
    if (officeExcelBtn) {
      const officeId = String(officeExcelBtn.getAttribute("data-office-excel") || "");
      if (officeId) {
        exportOfficeTripTableExcel(officeId).catch((error) => {
          console.error("[ImageGenerator] office Excel export failed", error);
          setStatus(`Office Excel export failed: ${error?.message || "unknown error"}`);
          app.ui.toast("Office Excel export failed.");
        });
      }
      return;
    }
    if (event.target?.closest?.("#imggen-refresh-route")) {
      refreshRouteMatrix({ force: true }).then(() => {
        if (state.showTripRoute) {
          const plan = buildTripPlan();
          ensureRouteGeometryForPlan(plan, { force: true });
        }
      }).catch(() => {});
      return;
    }
    if (event.target?.closest?.("#imggen-trip-day-prev")) {
      focusTripPlaybackRelative(-1);
      return;
    }
    if (event.target?.closest?.("#imggen-trip-day-next")) {
      focusTripPlaybackRelative(1);
      return;
    }
    const tripDayButton = event.target?.closest?.("[data-trip-day-index]");
    if (tripDayButton) {
      const idx = Number(tripDayButton.getAttribute("data-trip-day-index"));
      const plan = buildTripPlan();
      if (Number.isFinite(idx) && Array.isArray(plan?.days) && plan.days[idx]) {
        state.tripPlaybackDayIndex = idx;
        syncTripPlaybackOverlay(plan, { fit: state.tripPlaybackZoomToDay === true });
        render();
      }
      return;
    }
    const officeMarinaToggleBtn = event.target?.closest?.("[data-office-marinas-toggle]");
    if (officeMarinaToggleBtn) {
      const idx = Number(officeMarinaToggleBtn.getAttribute("data-office-marinas-toggle"));
      if (Number.isFinite(idx)) toggleOfficeMarinaVisibility(idx);
      return;
    }
    if (event.target?.closest?.("#imggen-export-database")) {
      exportFullDatabaseExcel();
      return;
    }
    if (event.target?.closest?.("#imggen-export")) {
      exportItems(selectedItems(), { zip: true });
      return;
    }
    const clusterZoomBtn = event.target?.closest?.("[data-cluster-zoom]");
    if (clusterZoomBtn) {
      const label = String(clusterZoomBtn.getAttribute("data-cluster-zoom") || "");
      if (label) zoomToCluster(label);
      return;
    }
    if (event.target?.closest?.("#imggen-cancel")) {
      cancelRequested = true;
      setStatus("Cancelling export...");
      render();
      return;
    }
    if (event.target?.closest?.("#imggen-select-all")) {
      toggleAllSelected(true);
      return;
    }
    if (event.target?.closest?.("#imggen-select-none")) {
      toggleAllSelected(false);
      return;
    }
    const rowBtn = event.target?.closest?.("[data-item-index]");
    if (rowBtn) {
      const idx = Number(rowBtn.getAttribute("data-item-index"));
      if (Number.isFinite(idx)) focusItem(idx, { saveCurrent: true });
      return;
    }
    const exportOneBtn = event.target?.closest?.("[data-export-index]");
    if (exportOneBtn) {
      const idx = Number(exportOneBtn.getAttribute("data-export-index"));
      if (Number.isFinite(idx) && state.items[idx]) exportItems([state.items[idx]], { zip: false });
      return;
    }
    // ── Notes ──────────────────────────────────────────────────────────────
    if (event.target?.closest?.("[data-note-add]")) {
      postProjectNote(String(state.projectSettings.selectedProjectId || ""));
      return;
    }
    // ── Tickets ────────────────────────────────────────────────────────────
    if (event.target?.closest?.("[data-ticket-new]")) { openTicketComposer(); return; }
    if (event.target?.closest?.("[data-ticket-cancel]")) { closeTicketComposer(); return; }
    if (event.target?.closest?.("[data-ticket-submit]")) { submitTicketComposer(); return; }
    const ticketNoteAddBtn = event.target?.closest?.("[data-ticket-note-add]");
    if (ticketNoteAddBtn) {
      postTicketNote(String(state.projectSettings.selectedProjectId || ""), ticketNoteAddBtn.getAttribute("data-ticket-note-add"));
      return;
    }
    if (event.target?.closest?.("[data-ticket-attach-pick]")) {
      containerEl?.querySelector?.("[data-ticket-file-input]")?.click();
      return;
    }
    const removeBtn = event.target?.closest?.("[data-ticket-attach-remove]");
    if (removeBtn) {
      removeTicketDraftAttachment(Number(removeBtn.getAttribute("data-ticket-attach-remove")));
      return;
    }
    if (event.target?.closest?.("[data-ticket-screenshot]")) {
      takeTicketScreenshot(String(state.projectSettings.selectedProjectId || ""));
      return;
    }
    if (event.target?.closest?.("[data-ticket-record-start]")) { startTicketScreenRecording(); return; }
    if (event.target?.closest?.("[data-ticket-record-stop]")) { stopTicketScreenRecording(); return; }
    if (event.target?.closest?.("[data-ticket-record-attach]")) {
      attachTicketRecording(String(state.projectSettings.selectedProjectId || ""));
      return;
    }
    if (event.target?.closest?.("[data-ticket-record-discard]")) { discardTicketRecording(); return; }
  }

  function onChange(event) {
    const target = event.target;
    if (target?.matches?.("[data-portfolio-color-by-select]")) {
      state.portfolioColorBy = ["status", "state", "milestone", "group"].includes(target.value) ? target.value : "status";
      if (state.portfolioColorBy === "milestone" && !state.portfolioColorStep) {
        const schema = getClientMilestoneSchema(state.catalog?.selectedClientId || "");
        state.portfolioColorStep = schema[0]?.key || "";
      }
      drawPlanningOverlay(currentItem());
      render();
      return;
    }
    const groupColorInput = target?.getAttribute?.("data-portfolio-group-color");
    if (groupColorInput) {
      const collapseKey = groupColorInput;
      const [clientId, ...nameParts] = collapseKey.split("::");
      const groupName = nameParts.join("::");
      setGroupMetaField(clientId, groupName, { color: target.value || null });
      saveClientPortfolioGroups(clientId).catch(() => {});
      persistLocalState();
      delete _htmlCache.portfolio;
      renderSection("marinaPortfolio");
      drawPlanningOverlay(currentItem());
      return;
    }
    const groupAirportInput = target?.getAttribute?.("data-portfolio-group-flyin-airport");
    if (groupAirportInput) {
      const collapseKey = groupAirportInput;
      const [clientId, ...nameParts] = collapseKey.split("::");
      const groupName = nameParts.join("::");
      setGroupMetaField(clientId, groupName, { flyInAirport: String(target.value || "").toUpperCase() });
      saveClientPortfolioGroups(clientId).catch(() => {});
      persistLocalState();
      return;
    }
    const groupOfficeSelect = target?.getAttribute?.("data-portfolio-group-office");
    if (groupOfficeSelect) {
      const collapseKey = groupOfficeSelect;
      const [clientId, ...nameParts] = collapseKey.split("::");
      const groupName = nameParts.join("::");
      setGroupMetaField(clientId, groupName, { officeId: target.value || null });
      saveClientPortfolioGroups(clientId).catch(() => {});
      persistLocalState();
      return;
    }
    if (target?.id === "imggen-portfolio-color-step") {
      state.portfolioColorStep = String(target.value || "");
      drawPlanningOverlay(currentItem());
      render();
      return;
    }
    if (target?.getAttribute?.("data-deliv-folder-input")) {
      if (state.digitalAsset.sendToDeliverables) {
        state.digitalAsset.sendToDeliverables.targetFolder = target.value;
      }
      return;
    }
    if (target?.getAttribute?.("data-deliv-filename-input")) {
      if (state.digitalAsset.sendToDeliverables) {
        state.digitalAsset.sendToDeliverables.filename = target.value;
      }
      return;
    }
    if (target?.getAttribute?.("data-deliv-epsg-input")) {
      const s = state.digitalAsset.sendToDeliverables;
      if (!s) return;
      s.epsgCode = target.value;
      if (s.kind === "slam_las") {
        const code = String(state.projectSettings.selectedProjectId || "");
        const ctxProject = (app.services?.context?.getAllProjects?.() || []).find((p) =>
          [p.id, p.marinaId, p.code].some((v) => String(v || "").toUpperCase() === code.toUpperCase())
        ) || null;
        try {
          s.filename = deliverableFilename({ projectNumber: ctxProject?.projectNumber, code, kind: s.kind, epsgCode: s.epsgCode });
          const filenameInput = containerEl?.querySelector?.("[data-deliv-filename-input]");
          if (filenameInput) filenameInput.value = s.filename;
        } catch {}
      }
      return;
    }
    if (target?.getAttribute?.("data-deliv-kind-select")) {
      const s = state.digitalAsset.sendToDeliverables;
      if (!s) return;
      const kind = String(target.value || "") || null;
      s.kind = kind;
      if (kind) {
        const code = String(state.projectSettings.selectedProjectId || "");
        const ctxProject = (app.services?.context?.getAllProjects?.() || []).find((p) =>
          [p.id, p.marinaId, p.code].some((v) => String(v || "").toUpperCase() === code.toUpperCase())
        ) || null;
        try {
          s.filename = deliverableFilename({
            projectNumber: ctxProject?.projectNumber, code, kind,
            epsgCode: kind === "slam_las" ? s.epsgCode : undefined,
          });
        } catch {
          // slam_las with no EPSG entered yet -- leave the filename as-is until one is.
        }
      } else {
        s.filename = s.sourceName;
      }
      // Unlike the plain text inputs above, this needs a re-render: it can toggle the
      // EPSG field's visibility and always changes the filename field's displayed value.
      render();
      return;
    }
    if (target?.getAttribute?.("data-asset-show-all-categories")) {
      state.digitalAsset.showAllCategories = !!target.checked;
      render();
      return;
    }
    if (target?.getAttribute?.("data-asset-cb")) {
      const catKey = target.getAttribute("data-asset-cb");
      const s3key = target.getAttribute("data-asset-cb-key") || "";
      if (!state.digitalAsset.selectedFiles[catKey]) state.digitalAsset.selectedFiles[catKey] = {};
      if (target.checked) state.digitalAsset.selectedFiles[catKey][s3key] = true;
      else delete state.digitalAsset.selectedFiles[catKey][s3key];
      render();
      return;
    }
    const assetFileInput = target?.getAttribute?.("data-asset-file-input");
    if (assetFileInput) {
      const [code, category] = String(assetFileInput || "").split("::");
      const files = target.files ? Array.from(target.files) : [];
      target.value = "";
      if (code && category && files.length) {
        uploadAssetFiles(code, category, files);
      }
      return;
    }
    if (target?.id === "imggen-file") {
      const file = target.files?.[0];
      if (file) {
        loadDataset(file).catch((error) => {
          console.error("[ImageGenerator] load failed", error);
          setStatus(`Load failed: ${error?.message || "unknown error"}`);
          app.ui.toast(`Safe Harbor Marinas Toolset could not read ${file.name}.`);
        });
      }
      target.value = "";
      return;
    }
    if (target?.closest?.("[data-portfolio-client-select]")) {
      const newClientId = String(target.value || "");
      if (newClientId) {
        state.catalog.selectedClientId = newClientId;
        app.services?.context?.setActiveClient?.(newClientId);
        // syncItemsFromCatalog rebuilds the trip-planning "queue" (state.items) for the
        // newly-selected client -- without it, Trip Planning / Generate Trip Report cross-
        // references group membership against whichever client's items were last synced
        // (e.g. still Safe Harbor after switching to Eco Power), finds zero overlap, and
        // fails with a silent toast instead of doing anything visible.
        syncItemsFromCatalog();
        render();
      }
      return;
    }
    if (target?.id === "portfolio-new-client-name") {
      state.portfolioNewClientName = String(target.value || "");
      return;
    }
    if (target?.id === "portfolio-new-project-name") {
      state.portfolioNewProjectName = String(target.value || "");
      return;
    }
    if (target?.id === "portfolio-new-project-code") {
      state.portfolioNewProjectCode = String(target.value || "").toUpperCase().replace(/[^A-Z0-9_-]/g, "");
      return;
    }
    const assignGroupSelect = target?.closest?.("[data-portfolio-assign-group]");
    if (assignGroupSelect) {
      const code = String(assignGroupSelect.getAttribute("data-portfolio-assign-group") || "");
      const groupVal = String(target.value || "");
      const idx = (state.catalog.marinas || []).findIndex(m => m.code === code);
      if (idx >= 0) {
        state.catalog.marinas[idx] = { ...state.catalog.marinas[idx], group: groupVal || "" };
        saveCatalog().catch(err => app.ui.toast(`Group save failed: ${err?.message || err}`));
        delete _htmlCache.portfolio;
        renderSection("marinaPortfolio");
      }
      return;
    }
    handleFormInput(target, { commitFiles: false, source: "change" });
  }

  function handleFormInput(target, { commitFiles = false } = {}) {
    if (!target) return;
    if (commitFiles && target?.id === "imggen-file") return;
    if (target?.id === "imggen-pm-add-code") {
      ensureProjectCreateState().code = normalizeProjectCode(target.value || "", "");
      persistLocalState();
      return;
    }
    if (target?.id === "imggen-pm-add-name") {
      const draft = ensureProjectCreateState();
      draft.name = String(target.value || "");
      if (!draft.code) draft.code = normalizeProjectCode(draft.name, "");
      persistLocalState();
      return;
    }
    if (target?.id === "imggen-pm-add-type") {
      ensureProjectCreateState().type = String(target.value || "marina");
      persistLocalState();
      renderAllSections();
      return;
    }
    if (target?.id === "imggen-pm-add-lat") {
      ensureProjectCreateState().lat = String(target.value || "").trim();
      persistLocalState();
      return;
    }
    if (target?.id === "imggen-pm-add-lng") {
      ensureProjectCreateState().lng = String(target.value || "").trim();
      persistLocalState();
      return;
    }
    if (target?.id === "imggen-project-marina") {
      const previousMarinaId = String(state.projectSettings.selectedProjectId || "").trim();
      state.projectSettings.selectedProjectId = String(target.value || "");
      const nextMarinaId = String(state.projectSettings.selectedProjectId || "").trim();
      // Load per-marina EPSG if saved, otherwise pick from geographic options list
      if (nextMarinaId && state.projectSettings.marinaEpsgCodes?.[nextMarinaId]) {
        state.projectSettings.epsgCode = state.projectSettings.marinaEpsgCodes[nextMarinaId];
      } else {
        const { item } = resolveProjectSettingsMarina();
        const epsgOptions = epsgOptionsForMarina(item);
        if (!epsgOptions.some((x) => x.code === String(state.projectSettings.epsgCode || ""))) {
          state.projectSettings.epsgCode = String(epsgOptions[0]?.code || "2227");
        }
      }
      if (nextMarinaId && nextMarinaId !== previousMarinaId) {
        app.log("marina.switched", { from: previousMarinaId || null, to: nextMarinaId });
        const removedLayerCount = clearProjectSettingsMapLayers();
        sharedDockPhotosLoadInFlight = false;
        slipInventoryRuntime?.setProjectMarina?.(nextMarinaId);
        remountStreetViewRuntime();
        remountPointCloudRuntime();
        dispatchProjectChanged(nextMarinaId);
        if (removedLayerCount > 0) {
          app.ui.toast(`Cleared ${removedLayerCount} map layer${removedLayerCount === 1 ? "" : "s"} from previous marina.`);
        }
        loadDamAssetsForProject(nextMarinaId);
        const { index: zoomIndex } = resolveProjectSettingsMarina();
        if (zoomIndex >= 0) focusItem(zoomIndex, { saveCurrent: false });
        // Keep context.js (the landing page's source of truth for "is a project active")
        // in sync -- this dropdown switches state.projectSettings.selectedProjectId directly
        // without ever touching context.js otherwise.
        const ctxProjects = app.services?.context?.getAllProjects?.() || [];
        const ctxProject = ctxProjects.find((p) =>
          [p.marinaId, p.code, p.id].some((v) => String(v || "").toUpperCase() === nextMarinaId.toUpperCase())
        );
        if (ctxProject) app.services.context.setActiveProject(ctxProject.id);
      }
      persistLocalState();
      render();
      return;
    }
    if (target?.id === "imggen-project-epsg") {
      const epsg = String(target.value || "2227");
      state.projectSettings.epsgCode = epsg;
      const projectId = String(state.projectSettings.selectedProjectId || "").trim();
      if (projectId) {
        if (!state.projectSettings.marinaEpsgCodes) state.projectSettings.marinaEpsgCodes = {};
        state.projectSettings.marinaEpsgCodes[projectId] = epsg;
      }
      pointCloudRuntime?.setEpsgCode?.(epsg);
      persistLocalState();
      return;
    }
    if (target?.id === "imggen-project-z-unit") {
      state.projectSettings.nativeZUnit = target.value === "m" ? "m" : "ft";
      persistLocalState();
      return;
    }
    if (target?.id === "imggen-dam-marina") {
      return;
    }
    if (target?.id === "imggen-zoom") {
      state.defaultZoom = Math.max(3, Math.min(24, Number(target.value) || 18));
      return;
    }
    if (target?.id === "imggen-scale") {
      state.scale = Math.max(1, Math.min(4, Number(target.value) || 2));
      return;
    }
    if (target?.id === "imggen-format") {
      state.format = String(target.value || "jpg").toLowerCase() === "png" ? "png" : "jpg";
      return;
    }
    if (target?.id === "imggen-marker") {
      state.marker = !!target.checked;
      drawPlanningOverlay(currentItem());
      return;
    }
    if (target?.id === "imggen-show-legend") {
      state.showLegend = false;
      updateLegend();
      return;
    }
    if (target?.id === "imggen-edit-views") {
      state.editMarinaViews = !!target.checked;
      window.clearTimeout(autoSaveViewTimer);
      autoSaveViewTimer = 0;
      persistLocalState();
      render();
      setStatus(state.editMarinaViews ? "Edit marina views enabled." : "Edit marina views locked.");
      return;
    }
    if (target?.id === "imggen-show-marina-codes") {
      state.showMarinaCodes = !!target.checked;
      drawPlanningOverlay(currentItem());
      persistLocalState();
      return;
    }
    if (target?.id === "imggen-show-marina-points") {
      state.showMarinaPoints = !!target.checked;
      drawPlanningOverlay(currentItem());
      persistLocalState();
      return;
    }
    if (target?.id === "imggen-show-office-markers") {
      state.showOfficeMarkers = !!target.checked;
      drawPlanningOverlay(currentItem());
      persistLocalState();
      return;
    }
    if (target?.id === "imggen-active-covered-slips") {
      const active = currentItem();
      if (active) {
        active.coveredSlips = !!target.checked;
        persistLocalState();
        render();
      }
      return;
    }
    if (target?.id === "imggen-active-exclude-report") {
      const active = currentItem();
      if (active) {
        active.excludeFromReport = !!target.checked;
        persistLocalState();
        render();
      }
      return;
    }
    if (target?.id === "imggen-active-no-fly-zone") {
      const active = currentItem();
      if (active) {
        active.noFlyZone = !!target.checked;
        persistLocalState();
        render();
      }
      return;
    }
    if (target?.id === "imggen-active-prioritize-air") {
      const active = currentItem();
      if (active) {
        active.prioritizeAirTravel = !!target.checked;
        clearRouteMatrix();
        persistLocalState();
        render();
      }
      return;
    }
    if (target?.id === "imggen-prefix") {
      state.prefix = String(target.value || "").trim() || "marina";
      return;
    }
    if (target?.id === "imggen-las-path") {
      state.lasDock.filePath = String(target.value || "");
      return;
    }
    if (target?.id === "imggen-las-resolution") {
      state.lasDock.resolution = Math.max(0.1, Number(target.value) || 1);
      return;
    }
    if (target?.id === "imggen-las-min-above-water") {
      state.lasDock.minAboveWater = Math.max(0, Number(target.value) || 0.5);
      return;
    }
    if (target?.id === "imggen-las-max-above-water") {
      state.lasDock.maxAboveWater = Math.max(state.lasDock.minAboveWater || 0, Number(target.value) || 6);
      return;
    }
    if (target?.id === "imggen-las-training-class") {
      state.lasDock.training.activeClass = String(target.value || "dock");
      persistLocalState();
      render();
      return;
    }
    if (target?.id === "imggen-batch-scope") {
      state.batchScope = String(target.value || "selected") === "all" ? "all" : "selected";
      render();
      return;
    }
    if (target?.id === "imggen-preview-code-filter") {
      const nextValue = String(target.value || "").toUpperCase();
      const selectionStart = typeof target.selectionStart === "number" ? target.selectionStart : nextValue.length;
      const selectionEnd = typeof target.selectionEnd === "number" ? target.selectionEnd : selectionStart;
      state.previewCodeFilter = nextValue.trim();
      persistLocalState();
      render();
      restoreInputFocusById("imggen-preview-code-filter", selectionStart, selectionEnd);
      return;
    }
    if (target?.id === "imggen-speed") {
      state.planning.averageSpeedMph = Math.max(5, Number(target.value) || 45);
      recomputePlanning();
      clearRouteMatrix();
      drawPlanningOverlay(currentItem());
      render();
      return;
    }
    if (target?.id === "imggen-road-factor") {
      state.planning.roadFactor = Math.max(1, Number(target.value) || 1.2);
      recomputePlanning();
      clearRouteMatrix();
      drawPlanningOverlay(currentItem());
      render();
      return;
    }
    if (target?.id === "imggen-cluster-spacing") {
      state.planning.clusterSpacingMiles = Math.max(0.25, Number(target.value) || 70);
      recomputePlanning();
      clearRouteMatrix();
      drawPlanningOverlay(currentItem());
      render();
      return;
    }
    if (target?.id === "imggen-cluster-size") {
      state.planning.maxMarinasPerCluster = Math.max(1, Math.round(Number(target.value) || 5));
      recomputePlanning();
      clearRouteMatrix();
      drawPlanningOverlay(currentItem());
      render();
      return;
    }
    if (target?.id === "imggen-default-survey-hours") {
      state.planning.defaultSurveyHours = Math.max(0, Number(target.value) || 6);
      persistLocalState();
      render();
      return;
    }
    if (target?.id === "imggen-max-work-hours") {
      state.planning.maxWorkHoursPerDay = Math.max(1, Number(target.value) || 7);
      normalizeDailyPlanning("work");
      persistLocalState();
      render();
      return;
    }
    if (target?.id === "imggen-max-drive-hours") {
      state.planning.maxDriveHoursPerDay = Math.max(0, Number(target.value) || 3);
      normalizeDailyPlanning("drive");
      persistLocalState();
      render();
      return;
    }
    if (target?.id === "imggen-max-marinas-day") {
      state.planning.maxMarinasPerDay = Math.max(1, Math.round(Number(target.value) || 2));
      persistLocalState();
      render();
      return;
    }
    if (target?.id === "imggen-price-per-marina") {
      state.planning.pricePerMarina = Math.max(0, Number(target.value) || 0);
      persistLocalState();
      render();
      return;
    }
    if (target?.id === "imggen-weather-contingency") {
      state.planning.weatherContingencyPct = Math.max(0, Number(target.value) || 0);
      persistLocalState();
      render();
      return;
    }
    if (target?.id === "imggen-weather-day-rate") {
      state.planning.weatherDayRate = Math.max(0, Number(target.value) || 0);
      clearEnrichedTripPlan();
      persistLocalState();
      render();
      return;
    }
    if (target?.id === "imggen-mileage-rate") {
      state.planning.mileageRate = Math.max(0, Number(target.value) || FEDERAL_MILEAGE_RATE_2026);
      clearEnrichedTripPlan();
      persistLocalState();
      render();
      return;
    }
    if (target?.id === "imggen-crew-size") {
      state.planning.crewSize = Math.max(1, Math.round(Number(target.value) || DEFAULT_CREW_SIZE));
      clearEnrichedTripPlan();
      persistLocalState();
      render();
      return;
    }
    if (target?.id === "imggen-travel-mode") {
      const nextMode = String(target.value || "auto").toLowerCase();
      state.planning.travelMode = ["auto", "drive", "air"].includes(nextMode) ? nextMode : "auto";
      clearRouteMatrix();
      persistLocalState();
      render();
      return;
    }
    if (target?.id === "imggen-air-overhead-hours") {
      state.planning.airOverheadHours = Math.max(0, Number(target.value) || 0);
      clearRouteMatrix();
      persistLocalState();
      render();
      return;
    }
    if (target?.id === "imggen-air-cruise-mph") {
      state.planning.airCruiseMph = Math.max(100, Number(target.value) || 450);
      clearRouteMatrix();
      persistLocalState();
      render();
      return;
    }
    if (target?.id === "imggen-gsa-api-key") {
      state.planning.gsaApiKey = String(target.value || "").trim();
      writeTravelPlanningGsaApiKey(state.planning.gsaApiKey);
      clearEnrichedTripPlan();
      persistLocalState();
      render();
      return;
    }
    if (target?.id === "imggen-trip-group") {
      state.planning.tripGroupFilter = String(target.value || "");
      const matchedOffice = DEFAULT_OFFICES.find((o) => o.groupName === state.planning.tripGroupFilter) || null;
      if (matchedOffice) state.planning.tripOfficeId = matchedOffice.id;
      clearRouteMatrix();
      clearRouteGeometry();
      persistLocalState();
      render();
      return;
    }
    if (target?.id === "imggen-return-office") {
      state.planning.returnToOffice = !!target.checked;
      clearRouteGeometry();
      persistLocalState();
      render();
      return;
    }
    if (target?.id === "imggen-show-trip-route") {
      state.showTripRoute = !!target.checked;
      if (!state.showTripRoute) clearRouteGeometry();
      persistLocalState();
      drawPlanningOverlay(currentItem());
      render();
      return;
    }
    if (target?.id === "imggen-trip-day-zoom") {
      state.tripPlaybackZoomToDay = !!target.checked;
      persistLocalState();
      return;
    }
    const pmStatusInput = target?.closest?.("[data-pm-status-index]");
    if (pmStatusInput) {
      const idx = Number(pmStatusInput.getAttribute("data-pm-status-index"));
      if (Number.isFinite(idx) && state.projectTasks?.[idx]) {
        const next = String(target.value || "Not Started");
        state.projectTasks[idx].status = PROJECT_TASK_STATUS_OPTIONS.includes(next) ? next : "Not Started";
        persistLocalState();
        queueSharedProjectTasksSave();
        render();
      }
      return;
    }
    const pmNotesInput = target?.closest?.("[data-pm-notes-index]");
    if (pmNotesInput) {
      const idx = Number(pmNotesInput.getAttribute("data-pm-notes-index"));
      if (Number.isFinite(idx) && state.projectTasks?.[idx]) {
        state.projectTasks[idx].notes = String(target.value || "");
        persistLocalState();
        queueSharedProjectTasksSave();
      }
      return;
    }
    const rowCheck = target?.closest?.("[data-select-index]");
    if (rowCheck) {
      const idx = Number(rowCheck.getAttribute("data-select-index"));
      if (Number.isFinite(idx) && state.items[idx]) {
        state.items[idx].selected = !!target.checked;
        clearRouteMatrix();
        render();
      }
      return;
    }
    const surveyInput = target?.closest?.("[data-survey-index]");
    if (surveyInput) {
      const idx = Number(surveyInput.getAttribute("data-survey-index"));
      if (Number.isFinite(idx) && state.items[idx]) {
        state.items[idx].surveyHours = Math.max(0, Number(target.value) || 0);
        render();
      }
      return;
    }
    const coveredInput = target?.closest?.("[data-covered-index]");
    if (coveredInput) {
      const idx = Number(coveredInput.getAttribute("data-covered-index"));
      if (Number.isFinite(idx) && state.items[idx]) {
        state.items[idx].coveredSlips = !!target.checked;
        persistLocalState();
        render();
      }
      return;
    }
    const excludeInput = target?.closest?.("[data-exclude-report-index]");
    if (excludeInput) {
      const idx = Number(excludeInput.getAttribute("data-exclude-report-index"));
      if (Number.isFinite(idx) && state.items[idx]) {
        state.items[idx].excludeFromReport = !!target.checked;
        persistLocalState();
        render();
      }
      return;
    }
    const noFlyInput = target?.closest?.("[data-no-fly-zone-index]");
    if (noFlyInput) {
      const idx = Number(noFlyInput.getAttribute("data-no-fly-zone-index"));
      if (Number.isFinite(idx) && state.items[idx]) {
        state.items[idx].noFlyZone = !!target.checked;
        clearRouteMatrix();
        persistLocalState();
        render();
      }
      return;
    }
    const prioritizeAirInput = target?.closest?.("[data-prioritize-air-index]");
    if (prioritizeAirInput) {
      const idx = Number(prioritizeAirInput.getAttribute("data-prioritize-air-index"));
      if (Number.isFinite(idx) && state.items[idx]) {
        state.items[idx].prioritizeAirTravel = !!target.checked;
        clearRouteMatrix();
        persistLocalState();
        render();
      }
      return;
    }
    // ── Tickets / Notes ────────────────────────────────────────────────────
    const ticketFilterSel = target?.closest?.("[data-ticket-filter]");
    if (ticketFilterSel) {
      const field = ticketFilterSel.getAttribute("data-ticket-filter");
      if (field === "status") state.tickets.filterStatus = ticketFilterSel.value;
      else if (field === "milestoneKey") state.tickets.filterMilestoneKey = ticketFilterSel.value;
      render();
      return;
    }
    const ticketStatusSel = target?.closest?.("[data-ticket-status]");
    if (ticketStatusSel) {
      const id = ticketStatusSel.getAttribute("data-ticket-status");
      patchProjectTicket(String(state.projectSettings.selectedProjectId || ""), id, { status: ticketStatusSel.value });
      return;
    }
    const ticketFieldEl = target?.closest?.("[data-ticket-field]");
    if (ticketFieldEl) {
      const field = ticketFieldEl.getAttribute("data-ticket-field");
      // title/description: sync state without render() -- the input already shows what was
      // typed, and re-rendering the whole panel on every keystroke would reset the cursor.
      // milestoneKey/blocksMilestone (select/checkbox) have visible knock-on effects (the
      // "blocks milestone" checkbox's enabled state, the milestone label shown elsewhere)
      // so those do need a render().
      if (field === "title") state.tickets.composerDraft.title = ticketFieldEl.value;
      else if (field === "description") state.tickets.composerDraft.description = ticketFieldEl.value;
      else if (field === "milestoneKey") { state.tickets.composerDraft.milestoneKey = ticketFieldEl.value; render(); }
      else if (field === "blocksMilestone") { state.tickets.composerDraft.blocksMilestone = !!ticketFieldEl.checked; render(); }
      return;
    }
    if (target?.hasAttribute?.("data-ticket-file-input")) {
      const code = String(state.projectSettings.selectedProjectId || "");
      const files = Array.from(target.files || []);
      target.value = "";
      files.forEach((f) => attachFileToTicketDraft(code, f));
      return;
    }
    if (target?.hasAttribute?.("data-note-draft")) {
      state.notes.draftText = target.value;
      return;
    }
    if (target?.hasAttribute?.("data-ticket-note-draft")) {
      state.tickets.noteDrafts[target.getAttribute("data-ticket-note-draft")] = target.value;
      return;
    }
  }

  function onInput(event) {
    const target = event.target;
    handleFormInput(target, { source: "input" });
  }

  function restoreInputFocusById(inputId, selectionStart = null, selectionEnd = null) {
    window.requestAnimationFrame(() => {
      const input = containerEl?.querySelector?.(`#${inputId}`);
      if (!input || typeof input.focus !== "function") return;
      input.focus();
      if (
        typeof input.setSelectionRange === "function"
        && Number.isFinite(selectionStart)
        && Number.isFinite(selectionEnd)
      ) {
        input.setSelectionRange(selectionStart, selectionEnd);
      }
    });
  }

  let _renderScheduled = false;
  const _htmlCache = {};
  function render() {
    if (_renderScheduled) return;
    _renderScheduled = true;
    requestAnimationFrame(() => {
      _renderScheduled = false;
      _doRender();
    });
  }
  function _doRender() {
    // Must run before anything below reads state.projectSettings.selectedProjectId --
    // Notes/Tickets/Project Settings/Portfolio's nested copy are all built inline further
    // down in this same function, not through renderSection() (which does its own sync but
    // only runs for separately-mounted standalone sections, and only if any are mounted).
    syncSelectedProjectFromContext();
    for (const k of Object.keys(_htmlCache)) delete _htmlCache[k];
    renderAllSections();
    if (!containerEl) return;
    const active = currentItem();
    const selectedCount = state.items.filter((item) => item.selected).length;
    if (!suppressUiRouteSync) ensureRouteMatrixForOffice(state.planning.tripOfficeId, { excludeReportExcluded: false });
    const rawTripPlan = buildTripPlan();
    const enrichedPlanKey = enrichedTripPlanCacheKey(rawTripPlan);
    ensureEnrichedTripPlan(rawTripPlan, enrichedPlanKey);
    const tripPlan = enrichedTripPlanKey === enrichedPlanKey && enrichedTripPlan
      ? enrichedTripPlan
      : rawTripPlan;
    const tripPlaybackDayIndex = normalizedTripPlaybackDayIndex(tripPlan);
    const tripPlaybackDay = currentTripPlaybackDay(tripPlan);
    state.tripPlaybackDayIndex = tripPlaybackDayIndex;
    if (!suppressUiRouteSync) ensureRouteGeometryForPlan(rawTripPlan);
    syncTripPlaybackOverlay(tripPlan);
    const tripClientId = state.catalog.selectedClientId || "";
    const tripExplicitGroups = Array.isArray(state.portfolioCustomGroups?.[tripClientId]) ? state.portfolioCustomGroups[tripClientId] : [];
    const tripCatalogGroups = [...new Set((state.catalog.marinas || []).map(m => String(m.group || "")).filter(Boolean))].filter(g => !tripExplicitGroups.includes(g)).sort();
    const tripCustomGroups = [...tripExplicitGroups, ...tripCatalogGroups];
    const tripGroupOptions = tripCustomGroups.map((g) => `<option value="${esc(g)}" ${state.planning.tripGroupFilter === g ? "selected" : ""}>${esc(g)}</option>`).join("");
    const tripGroupFilter = state.planning.tripGroupFilter;
    const lasToolStatus = state.lasDock.toolStatus || null;
    const lasInspection = state.lasDock.inspection || null;
    const lasQuicklook = lasInspection?.quicklook || null;
    const lasProducts = state.lasDock.products || null;
    const dockTraining = state.lasDock.training || { activeClass: "dock", labels: [], selectedLabelId: "" };
    const dayCards = tripPlan.days.map((day, index) => `
      <button type="button" data-trip-day-index="${index}" style="padding:8px 10px; border:1px solid ${index === tripPlaybackDayIndex ? "rgba(251,191,36,0.65)" : "rgba(148,163,184,0.18)"}; border-radius:10px; background:${index === tripPlaybackDayIndex ? "rgba(245,158,11,0.16)" : "rgba(15,23,42,0.45)"}; min-width:240px; text-align:left; color:inherit; cursor:pointer; box-shadow:${index === tripPlaybackDayIndex ? "0 0 0 1px rgba(251,191,36,0.22)" : "none"};">
        <div style="font-weight:700;">Day ${day.day}</div>
        <div class="small">Start: ${day.startMode === "hotel" ? "Hotel / field" : "Office / home"}</div>
        <div class="small">Stops: ${day.marinaCount}</div>
        <div class="small">Drive: ${esc(formatDriveMinutes(day.driveMinutes))}</div>
        <div class="small">Survey: ${esc(formatDriveMinutes(day.workMinutes))}</div>
        <div class="small">Day total: ${esc(formatDriveMinutes(day.totalMinutes || (day.driveMinutes + day.workMinutes)))}</div>
        <div class="small">End: ${day.travelOnly ? (day.endMode === "office" ? "Travel only / office arrival" : "Travel only / overnight in field") : (day.endMode === "office" ? "Return home to office" : day.endMode === "hotel" ? "Hotel / overnight in field" : "Field end / return exceeds daily cap")}</div>
        <div style="margin-top:6px; display:grid; gap:4px;">
          ${day.travelOnly ? `<div class="small">${esc(day.travelLabel || "Travel only day")}</div>` : ""}
          ${day.stops.map((stop) => `<div class="small">${esc(stop.item.name)} (${formatMiles(stop.milesFromPrevious)}, ${formatDriveMinutes(stop.driveMinutesFromPrevious)}, ${esc(stop.driveSource || "estimated")}${stop.driveSource === "air" && stop.originAirport?.iata && stop.destAirport?.iata ? ` ${esc(stop.originAirport.iata)}→${esc(stop.destAirport.iata)}` : ""}${stop.airfareFromPrevious ? `, airfare ${esc(formatCurrency(stop.airfareFromPrevious))}` : ""}, survey ${Number(stop.surveyMinutes / 60).toFixed(1)}h)</div>`).join("")}
          ${day.returnLeg ? `<div class="small">Return to office (${formatMiles(day.returnLeg.miles)}, ${formatDriveMinutes(day.returnLeg.driveMinutes)}, ${esc(day.returnLeg.source || "estimated")})</div>` : ""}
        </div>
      </button>
    `).join("");
    const projectTasks = Array.isArray(state.projectTasks) ? state.projectTasks : [];
    const pmByMarina = projectTasks.reduce((acc, row) => {
      const key = String(row.marina || "Unassigned");
      const bucket = acc.get(key) || { total: 0, done: 0, inProgress: 0, blocked: 0, nextDate: "" };
      bucket.total += 1;
      if (row.status === "Done") bucket.done += 1;
      if (row.status === "In Progress") bucket.inProgress += 1;
      if (row.status === "Blocked") bucket.blocked += 1;
      if (row.endDate && (!bucket.nextDate || row.endDate < bucket.nextDate) && row.status !== "Done") bucket.nextDate = row.endDate;
      acc.set(key, bucket);
      return acc;
    }, new Map());
    const pmSummaryCards = Array.from(pmByMarina.entries()).map(([marina, summary]) => {
      const pct = summary.total ? Math.round((summary.done / summary.total) * 100) : 0;
      return `
        <div style="padding:8px 10px; border:1px solid rgba(148,163,184,0.18); border-radius:10px; background:rgba(15,23,42,0.45); min-width:220px;">
          <div style="font-weight:700;">${esc(marina)}</div>
          <div class="small">Progress: ${summary.done}/${summary.total} (${pct}%)</div>
          <div class="small">In progress: ${summary.inProgress} | Blocked: ${summary.blocked}</div>
          <div class="small">Next target: ${esc(summary.nextDate || "-")}</div>
        </div>
      `;
    }).join("");
    const projectTaskRows = projectTasks.map((row, index) => `
      <tr>
        <td>${esc(row.marina)}</td>
        <td>${esc(row.category)}</td>
        <td>${esc(row.task)}</td>
        <td>${esc(row.owner)}</td>
        <td>${esc(fmtProjectDate(row.startDate))}</td>
        <td>${esc(fmtProjectDate(row.endDate))}</td>
        <td>${esc(String(row.durationDays ?? ""))}</td>
        <td>
          <select data-pm-status-index="${index}">
            ${PROJECT_TASK_STATUS_OPTIONS.map((opt) => `<option value="${esc(opt)}"${row.status === opt ? " selected" : ""}>${esc(opt)}</option>`).join("")}
          </select>
        </td>
        <td><input type="text" value="${esc(row.notes || "")}" data-pm-notes-index="${index}" placeholder="Notes" style="min-width:140px;" /></td>
      </tr>
    `).join("");
    const officeRows = state.offices.map((office, index) => `
      <div class="row" style="justify-content:space-between; gap:8px; padding:6px 8px; border:1px solid ${state.selectedOfficeIds.includes(office.id) ? "rgba(245,158,11,0.55)" : "rgba(148,163,184,0.16)"}; border-radius:10px; background:${state.selectedOfficeIds.includes(office.id) ? "rgba(245,158,11,0.08)" : "transparent"};">
        <div style="min-width:0; display:flex; align-items:flex-start; gap:8px;">
          <label style="padding-top:2px;" title="Toggle this office and its marinas on the map">
            <input type="checkbox" data-office-marinas-toggle="${index}" ${state.visibleOfficeMarinaIds.includes(office.id) ? "checked" : ""} ${exporting ? "disabled" : ""} />
          </label>
          <div>
            <div style="font-weight:600;">${esc(office.name)}</div>
          <div class="small">${Number(office.lat).toFixed(5)}, ${Number(office.lng).toFixed(5)}</div>
          <div class="small" style="margin-top:4px;">
            ${(() => {
              const badge = routingBadge(office.id);
              return `<span style="display:inline-flex; align-items:center; padding:2px 8px; border-radius:999px; background:${badge.bg}; border:1px solid ${badge.border}; color:${badge.color}; font-weight:600;">${esc(badge.text)}</span>`;
            })()}
          </div>
          </div>
        </div>
        <div class="row" style="gap:6px;">
          <button class="iconbtn" data-office-zoom="${esc(office.id)}" title="Zoom to office coverage" aria-label="Zoom to office coverage" ${exporting ? "disabled" : ""}>⌕</button>
          <button class="iconbtn" data-office-excel="${esc(office.id)}" title="Export office trip table to Excel" aria-label="Export office trip table to Excel" ${exporting ? "disabled" : ""}>⇩</button>
        </div>
      </div>
    `).join("");

    const panelSection = (key, title, body, { open = false, group = "" } = {}) => `
      <details class="toolset-group" data-imggen-panel="${key}" ${group ? `data-imggen-group="${group}"` : ""} ${open ? "open" : ""}>
        <summary>${title}</summary>
        <div class="toolset-content">
          ${body}
        </div>
      </details>
    `;

    const selectedProjectCode = String(state.projectSettings.selectedProjectId || "");
    const selectedProject = selectedProjectCode
      ? state.catalog.marinas.find((m) => String(m.code || "") === selectedProjectCode)
      : null;
    const activeContextProject = app.services?.context?.getActiveProject?.() || null;
    const projectionProfile = app.services?.projections?.getProjectProfile?.() || null;
    const activeContextMatches = selectedProjectCode && (
      String(activeContextProject?.marinaId || "").toUpperCase() === selectedProjectCode.toUpperCase() ||
      String(activeContextProject?.code || "").toUpperCase() === selectedProjectCode.toUpperCase()
    );
    const selectedProjectName = selectedProject?.name || resolveProjectSettingsMarina().item?.name || "";
    const activeProjectCount = Array.isArray(state.catalog.marinas)
      ? state.catalog.marinas.filter((m) => !!m.active).length
      : 0;
    const selectedAssets = selectedProjectCode ? (state.digitalAsset.assetsByCategory[selectedProjectCode] || {}) : {};
    const assetCount = Object.values(selectedAssets).reduce((sum, files) => sum + (Array.isArray(files) ? files.length : 0), 0);
    const loadedCategories = Object.values(selectedAssets).filter((files) => Array.isArray(files) && files.length > 0).length;
    const projectLabel = selectedProjectCode ? selectedProjectCode : "No active project";
    const activeName = selectedProjectName || (activeContextMatches ? activeContextProject?.name : "") || "";
    const crsRaw = String(
      (activeContextMatches ? activeContextProject?.horizontalCRS : "") ||
      projectionProfile?.horizontalCRS ||
      state.projectSettings.epsgCode ||
      "2227"
    );
    const crsLabel = /^EPSG:/i.test(crsRaw) ? crsRaw.toUpperCase() : `EPSG:${crsRaw}`;
    const zUnitRaw = String((activeContextMatches ? activeContextProject?.verticalUnits : "") || state.projectSettings.nativeZUnit || "").toLowerCase();
    const zUnitLabel = zUnitRaw === "m" || zUnitRaw === "meter" || zUnitRaw === "meters" ? "meters" : zUnitRaw ? "feet" : "not set";
    const formatMetaDate = (value) => {
      const d = value ? new Date(value) : null;
      return d && Number.isFinite(d.getTime()) ? d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "not set";
    };
    const createdDate = formatMetaDate(selectedProject?.createdAt || (activeContextMatches ? activeContextProject?.createdAt : ""));
    const activeDate = formatMetaDate(selectedProject?.activatedAt || selectedProject?.updatedAt || selectedProject?.createdAt || (activeContextMatches ? activeContextProject?.createdAt : ""));
    const updatedDate = formatMetaDate(selectedProject?.updatedAt || selectedProject?.activatedAt || selectedProject?.archivedAt || "");
    const lat = Number(selectedProject?.lat ?? (activeContextMatches ? activeContextProject?.location?.lat : null));
    const lng = Number(selectedProject?.lng ?? (activeContextMatches ? activeContextProject?.location?.lng : null));
    const locationLabel = Number.isFinite(lat) && Number.isFinite(lng) ? `${lat.toFixed(5)}, ${lng.toFixed(5)}` : "not set";
    const assetsLabel = selectedProjectCode ? `${assetCount} file${assetCount === 1 ? "" : "s"} / ${loadedCategories} categor${loadedCategories === 1 ? "y" : "ies"}` : `${activeProjectCount} active projects`;
    const projectType = String(selectedProject?.projectType || selectedProject?.type || (activeContextMatches ? activeContextProject?.type : "") || "project").replaceAll("-", " ");

    // GIS card data
    const addr      = String(selectedProject?.address || "").trim();
    const addrCity  = String(selectedProject?.city    || "").trim();
    const addrState = String(selectedProject?.state   || "").trim();
    const addrZip   = String(selectedProject?.zip     || "").trim();
    const rawPhone  = String(selectedProject?.phone   || "").replace(/\D/g, "");
    const phone     = rawPhone.length === 10
      ? `(${rawPhone.slice(0,3)}) ${rawPhone.slice(3,6)}-${rawPhone.slice(6)}`
      : rawPhone.length === 11 && rawPhone[0] === "1"
        ? `+1 (${rawPhone.slice(1,4)}) ${rawPhone.slice(4,7)}-${rawPhone.slice(7)}`
        : selectedProject?.phone || "";
    const gmName    = String(selectedProject?.gmName  || "").trim();
    const gmEmail   = String(selectedProject?.gmEmail || "").trim();
    const cityStateZip = [addrCity, addrState ? (addrZip ? `${addrState} ${addrZip}` : addrState) : addrZip].filter(Boolean).join(", ");
    const hasCoords = Number.isFinite(lat) && Number.isFinite(lng);
    const rotationDeg = Number(selectedProject?.mapRotationDeg);
    const hasRotation = Number.isFinite(rotationDeg);

    const gRow = (label, value) => value
      ? `<tr><td style="padding:2px 6px 2px 0; font-size:10px; color:var(--muted); white-space:nowrap; vertical-align:top; width:82px;">${esc(label)}</td><td style="padding:2px 0 2px 4px; font-size:11px; vertical-align:top; word-break:break-word;">${value}</td></tr>`
      : "";
    const gSection = (label) =>
      `<tr><td colspan="2" style="padding:6px 0 2px; font-size:9px; font-weight:700; color:var(--muted); text-transform:uppercase; letter-spacing:0.07em; border-top:1px solid rgba(148,163,184,0.12);">${esc(label)}</td></tr>`;
    const statusLabel = selectedProject?.status === "archived" ? "Archived"
      : selectedProject?.active ? "Active"
      : selectedProjectCode ? "Potential"
      : "No project selected";
    const statusColor = selectedProject?.active ? "#22c55e" : selectedProject?.status === "archived" ? "#38bdf8" : "#94a3b8";

    // Project Settings card — look up context project for the selected portfolio entry
    const ctxAllProjects = app.services?.context?.getAllProjects?.() || [];
    const ctxProject = selectedProjectCode
      ? ctxAllProjects.find(p =>
          [p.id, p.marinaId, p.code].some(v => String(v || "").toUpperCase() === selectedProjectCode.toUpperCase())
        ) || null
      : null;

    // Geodetic: prefer context project, fall back to active context / profile
    const geodCRS    = ctxProject?.horizontalCRS  || (activeContextMatches ? activeContextProject?.horizontalCRS  : "") || projectionProfile?.horizontalCRS || "EPSG:4326";
    const _geodHRaw  = ctxProject?.horizontalUnits || (activeContextMatches ? activeContextProject?.horizontalUnits : "") || "";
    const geodHUnits = _geodHRaw === "meters" ? "m" : (_geodHRaw || "ftUS");
    const geodVDatum = ctxProject?.verticalDatum   || (activeContextMatches ? activeContextProject?.verticalDatum   : "") || "MSL";
    const _geodVRaw  = ctxProject?.verticalUnits   || (activeContextMatches ? activeContextProject?.verticalUnits   : "") || "";
    const geodVUnits = _geodVRaw === "meters" ? "m" : (_geodVRaw || "ft");
    const geodGeoid  = ctxProject?.geoidModel      || (activeContextMatches ? activeContextProject?.geoidModel      : "") || "None";

    // Identity/contact: prefer context project (editable), fall back to portfolio data
    const editName    = ctxProject?.name    || activeName || selectedProjectName || "";
    const editAddr    = ctxProject?.address || addr;
    const editCity    = ctxProject?.city    || addrCity;
    const editState   = ctxProject?.state   || addrState;
    const editZip     = ctxProject?.zip     || addrZip;
    const editPhone   = ctxProject?.phone   || selectedProject?.phone || "";
    const editGmName  = ctxProject?.gmName  || gmName;
    const editGmEmail = ctxProject?.gmEmail || gmEmail;
    const editProjectNumber = ctxProject?.projectNumber || selectedProject?.projectNumber || "";

    const hzUnitDropdownHtml = [
      { v: "ftUS", l: "US Feet" }, { v: "ftInt", l: "International Feet" }, { v: "m", l: "Meters" },
    ].map(o => `<option value="${o.v}"${o.v === geodHUnits ? " selected" : ""}>${o.l}</option>`).join("");
    const vUnitDropdownHtml = [
      { v: "ft", l: "Feet" }, { v: "m", l: "Meters" },
    ].map(o => `<option value="${o.v}"${o.v === geodVUnits ? " selected" : ""}>${o.l}</option>`).join("");

    const psIn  = "background:transparent;border:none;border-bottom:1px solid rgba(148,163,184,0.18);color:inherit;width:100%;font-size:11px;padding:1px 2px;outline:none;";
    const psSel = "background:rgba(15,23,42,0.7);border:1px solid rgba(148,163,184,0.2);color:inherit;width:100%;font-size:11px;padding:1px 3px;border-radius:3px;";
    const psTd1 = `padding:2px 6px 2px 0; font-size:10px; color:var(--muted); white-space:nowrap; vertical-align:middle; width:82px;`;
    const psTd2 = `padding:2px 0 2px 4px; font-size:11px; vertical-align:middle;`;
    const gInRow  = (label, id, val, type = "text", listId = "") =>
      `<tr><td style="${psTd1}">${esc(label)}</td><td style="${psTd2}"><input id="${id}" type="${type}" value="${esc(String(val || ""))}"${listId ? ` list="${listId}"` : ""} style="${psIn}" /></td></tr>`;
    const gSelRow = (label, id, opts) =>
      `<tr><td style="${psTd1}">${esc(label)}</td><td style="${psTd2}"><select id="${id}" style="${psSel}">${opts}</select></td></tr>`;

    const psCtxId = esc(ctxProject?.id || "");
    const psCanSave = !!ctxProject;
    const psIsActive = activeContextMatches;

    const projectMetadataHtml = `
      <details class="toolset-group" data-imggen-panel="projectMetadata" ${state.panels.projectMetadata !== false ? "open" : ""}>
        <summary>Project Settings${selectedProjectCode ? ` — ${esc(projectLabel)}` : ""}</summary>
        <div class="toolset-content">
          <datalist id="giscard-vdatum-list">
            <option value="MLLW"><option value="MSL"><option value="NAVD88"><option value="MHHW"><option value="MLW"><option value="MHW"><option value="AHD">
          </datalist>
          <datalist id="giscard-geoid-list">
            <option value="GEOID18"><option value="GEOID12B"><option value="GEOID09"><option value="GEOID03"><option value="AUSGeoid2020"><option value="AUSGeoid09"><option value="None">
          </datalist>
          <table style="width:100%; border-collapse:collapse;">
            <tbody>
              ${gSection("Identity")}
              ${gRow("Code", selectedProjectCode || "—")}
              ${gInRow("Project #", "giscard-projectnumber", editProjectNumber)}
              ${gInRow("Name", "giscard-name", editName)}
              ${gRow("Type",   projectType)}
              ${gRow("Status", `<span style="color:${statusColor};">${statusLabel}</span>`)}
              ${gSection("Location")}
              ${gInRow("Address", "giscard-address", editAddr)}
              ${gInRow("City",    "giscard-city",    editCity)}
              <tr>
                <td style="${psTd1}">ST / ZIP</td>
                <td style="${psTd2}">
                  <div class="row" style="gap:4px; align-items:center;">
                    <input id="giscard-state" type="text" value="${esc(editState)}" placeholder="ST" style="${psIn} width:36px; flex-shrink:0;" />
                    <input id="giscard-zip"   type="text" value="${esc(editZip)}"   placeholder="ZIP" style="${psIn} flex:1;" />
                  </div>
                </td>
              </tr>
              ${selectedProjectCode ? `<tr><td style="${psTd1}">Coordinates</td><td style="${psTd2}">
                <div class="row" style="gap:4px; flex-wrap:wrap; align-items:center;">
                  <input id="imggen-loc-lat" type="number" step="0.00001" value="${hasCoords ? lat.toFixed(5) : ""}" placeholder="Lat" style="width:90px; font-size:10px; padding:1px 4px;" />
                  <input id="imggen-loc-lng" type="number" step="0.00001" value="${hasCoords ? lng.toFixed(5) : ""}" placeholder="Lng" style="width:95px; font-size:10px; padding:1px 4px;" />
                  <button id="imggen-loc-use-center" class="btn" style="font-size:9px; padding:1px 5px;" title="Set to current map center">⊕ Map</button>
                  <button id="imggen-loc-save" class="btn" style="font-size:9px; padding:1px 5px;" title="Save coordinates">Save</button>
                  <button data-portfolio-geocode="${esc(selectedProjectCode)}" class="btn" style="font-size:9px; padding:1px 5px; opacity:0.7;" title="Auto-geocode from address">&#x2316;</button>
                </div>
              </td></tr>
              <tr><td style="${psTd1}">Map Rotation</td><td style="${psTd2}">
                <div class="row" style="gap:4px; flex-wrap:wrap; align-items:center;">
                  <input id="imggen-rotation-deg" type="number" step="1" min="0" max="359" value="${hasRotation ? rotationDeg.toFixed(0) : "0"}" placeholder="0" style="width:55px; font-size:10px; padding:1px 4px;" />
                  <span style="font-size:9px; color:var(--muted);">&deg;</span>
                  <button id="imggen-rotation-use-current" class="btn" style="font-size:9px; padding:1px 5px;" title="Set to current map rotation">⊕ Map</button>
                  <button id="imggen-rotation-save" class="btn" style="font-size:9px; padding:1px 5px;" title="Save map rotation for this project">Save</button>
                </div>
              </td></tr>` : ""}
              ${gSection("Contact")}
              ${gInRow("GM Name", "giscard-gm-name",  editGmName)}
              ${gInRow("Phone",   "giscard-phone",     editPhone, "tel")}
              ${gInRow("Email",   "giscard-gm-email",  editGmEmail, "email")}
              ${gSection("Geodetic")}
              <tr><td style="${psTd1}">Horiz CRS</td><td style="${psTd2}">${crsPickerField("giscard-crs", geodCRS, { style: "width:100%", point: Number.isFinite(lat) && Number.isFinite(lng) ? [lng, lat] : null })}</td></tr>
              ${gSelRow("Horiz Units", "giscard-hunits", hzUnitDropdownHtml)}
              ${gInRow("Vert Datum",   "giscard-vdatum", geodVDatum, "text", "giscard-vdatum-list")}
              ${gSelRow("Vert Units",  "giscard-zunits", vUnitDropdownHtml)}
              ${gInRow("Geoid Model",  "giscard-geoid",  geodGeoid,  "text", "giscard-geoid-list")}
              ${gSection("Assets")}
              ${gRow("Files",   assetsLabel)}
              ${gRow("Created", createdDate)}
              ${gRow("Updated", updatedDate || activeDate)}
            </tbody>
          </table>
          ${selectedProjectCode ? `
            <div style="margin-top:10px; display:flex; gap:6px; flex-wrap:wrap; align-items:center;">
              ${psIsActive
                ? `<span style="font-size:10px; color:var(--accent,#38bdf8);">● Active</span>`
                : `<button id="giscard-activate" data-ctx-project-id="${psCtxId}" class="btn primary" style="font-size:10px; padding:2px 8px;"${!psCanSave ? ' disabled title="Project not in context catalog"' : ""}>Set Active</button>`}
              <button id="giscard-save" data-ctx-project-id="${psCtxId}" class="btn" style="font-size:10px; padding:2px 8px;"${!psCanSave ? ' disabled title="Project not in context catalog"' : ""}>Save</button>
              <button data-prep-report="${esc(selectedProjectCode)}" class="btn" style="font-size:10px; padding:2px 8px;" title="Generate project status report PDF">Status Report</button>
            </div>` : ""}
        </div>
      </details>
    `;
    const workspacePanel = (panelKey, title, body, open = false) => `
      <details class="toolset-group" data-imggen-panel="${panelKey}" ${open ? "open" : ""}>
        <summary>${esc(title)}</summary>
        <div class="toolset-content">${body}</div>
      </details>
    `;

    containerEl.innerHTML = `
      <div id="project-workspace-root" class="subtoolset-list">
        ${workspacePanel("projectPortfolio", "Project Portfolio", state.panels.projectPortfolio !== false ? buildMarinaPortfolioHtml() : "", state.panels.projectPortfolio !== false)}
        ${projectMetadataHtml}
        ${workspacePanel("digitalAssetManager", "Digital Asset Manager", state.panels.digitalAssetManager !== false ? buildDigitalAssetManagerHtml() : "", state.panels.digitalAssetManager !== false)}
        ${workspacePanel("notes", "Notes", state.panels.notes !== false ? buildNotesHtml() : "", state.panels.notes !== false)}
        ${workspacePanel("tickets", "Tickets", state.panels.tickets !== false ? buildTicketsHtml() : "", state.panels.tickets !== false)}
        ${panelSection("officeAccess", "Office Reports", `
          <div class="row" style="margin-bottom:10px; gap:8px; flex-wrap:wrap;">
            <button id="imggen-generate-all-reports" class="btn" ${exporting ? "disabled" : ""}>Generate All Reports</button>
            <button id="imggen-generate-combined-report-book" class="btn" ${exporting ? "disabled" : ""}>Combined Book PDF</button>
          </div>
          <div style="display:grid; gap:6px;">
            ${officeRows || `<div class="small">No offices configured.</div>`}
          </div>
        `, { open: state.panels.officeAccess, group: "planningGroup" })}
        ${panelSection("tripPlanning", "Trip Planning", `
          <div class="small">Build a first-pass route for the selected office group. The planner attempts routed travel time first and falls back to estimated travel if routing is unavailable.</div>
          <div class="small" style="margin-top:4px;">Daily schedule is capped at ${DAILY_TOTAL_HOURS} total hours, combining drive and survey time.</div>
          <div class="row" style="margin-top:10px; gap:10px; flex-wrap:wrap;">
            <label class="small">Office / Group
              <select id="imggen-trip-group" style="margin-left:6px; min-width:180px;">
                <option value="">— None selected —</option>
                ${tripGroupOptions}
              </select>
            </label>
            <label class="small">Default survey hrs
              <input id="imggen-default-survey-hours" type="number" min="0" step="0.5" value="${esc(state.planning.defaultSurveyHours)}" style="width:80px; margin-left:6px;" />
            </label>
            <label class="small">Max work hrs/day
              <input id="imggen-max-work-hours" type="number" min="1" step="0.5" value="${esc(state.planning.maxWorkHoursPerDay)}" style="width:80px; margin-left:6px;" />
            </label>
            <label class="small">Max drive hrs/day
              <input id="imggen-max-drive-hours" type="number" min="0" step="0.5" value="${esc(state.planning.maxDriveHoursPerDay)}" style="width:80px; margin-left:6px;" />
            </label>
            <label class="small">Max sites/day
              <input id="imggen-max-marinas-day" type="number" min="1" step="1" value="${esc(state.planning.maxMarinasPerDay)}" style="width:70px; margin-left:6px;" />
            </label>
            <label class="small">Price / site
              <input id="imggen-price-per-marina" type="number" min="0" step="50" value="${esc(state.planning.pricePerMarina)}" style="width:100px; margin-left:6px;" />
            </label>
            <label class="small">Weather contingency %
              <input id="imggen-weather-contingency" type="number" min="0" step="1" value="${esc(state.planning.weatherContingencyPct)}" style="width:80px; margin-left:6px;" />
            </label>
            <label class="small">Weather/travel day rate $
              <input id="imggen-weather-day-rate" type="number" min="0" step="50" value="${esc(state.planning.weatherDayRate || "")}" placeholder="${esc(String(Math.round((state.planning.pricePerMarina || 6150) * 0.5)))}" style="width:100px; margin-left:6px;" />
            </label>
            <label class="small">Mileage rate $/mi
              <input id="imggen-mileage-rate" type="number" min="0" step="0.005" value="${esc(state.planning.mileageRate || "")}" placeholder="${esc(String(FEDERAL_MILEAGE_RATE_2026))}" style="width:80px; margin-left:6px;" />
            </label>
            <label class="small">Crew size
              <input id="imggen-crew-size" type="number" min="1" step="1" value="${esc(state.planning.crewSize || "")}" placeholder="2" style="width:60px; margin-left:6px;" />
            </label>
            <label class="small">Travel mode
              <select id="imggen-travel-mode" style="margin-left:6px; width:100px;">
                <option value="auto" ${state.planning.travelMode === "auto" ? "selected" : ""}>Auto</option>
                <option value="drive" ${state.planning.travelMode === "drive" ? "selected" : ""}>Drive</option>
                <option value="air" ${state.planning.travelMode === "air" ? "selected" : ""}>Air</option>
              </select>
            </label>
            <label class="small">Air overhead hrs
              <input id="imggen-air-overhead-hours" type="number" min="0" step="0.5" value="${esc(state.planning.airOverheadHours)}" style="width:80px; margin-left:6px;" />
            </label>
            <label class="small">Air cruise mph
              <input id="imggen-air-cruise-mph" type="number" min="100" step="10" value="${esc(state.planning.airCruiseMph)}" style="width:80px; margin-left:6px;" />
            </label>
            <label class="small">GSA API key
              <input id="imggen-gsa-api-key" type="password" value="${esc(state.planning.gsaApiKey || "")}" placeholder="optional" style="width:180px; margin-left:6px;" />
            </label>
            <label class="small" style="display:flex; align-items:center; gap:6px;">
              <input id="imggen-return-office" type="checkbox" ${state.planning.returnToOffice ? "checked" : ""} />
              Return to office
            </label>
            <label class="small" style="display:flex; align-items:center; gap:6px;">
              <input id="imggen-show-trip-route" type="checkbox" ${state.showTripRoute ? "checked" : ""} />
              Show planned route
            </label>
            <button id="imggen-refresh-route" class="btn" ${tripPlan.totals.marinaCount ? "" : "disabled"}>Refresh Routed Times</button>
          </div>
          <div class="row" style="margin-top:10px; gap:10px; flex-wrap:wrap;">
            <label class="small">Avg mph
              <input id="imggen-speed" type="number" min="5" step="1" value="${esc(state.planning.averageSpeedMph)}" style="width:80px; margin-left:6px;" />
            </label>
            <label class="small">Road factor
              <input id="imggen-road-factor" type="number" min="1" step="0.05" value="${esc(state.planning.roadFactor)}" style="width:80px; margin-left:6px;" />
            </label>
            <label class="small">Cluster spacing mi
              <input id="imggen-cluster-spacing" type="number" min="0.25" step="0.25" value="${esc(state.planning.clusterSpacingMiles)}" style="width:90px; margin-left:6px;" />
            </label>
            <label class="small">Max sites/cluster
              <input id="imggen-cluster-size" type="number" min="1" step="1" value="${esc(state.planning.maxMarinasPerCluster)}" style="width:70px; margin-left:6px;" />
            </label>
          </div>
          <div class="row" style="margin-top:10px; gap:8px; flex-wrap:wrap; align-items:center;">
            <button id="imggen-trip-day-prev" class="btn" ${tripPlaybackDayIndex <= 0 || !tripPlan.days.length ? "disabled" : ""}>&lt;</button>
            <button id="imggen-trip-day-next" class="btn" ${tripPlaybackDayIndex < 0 || tripPlaybackDayIndex >= tripPlan.days.length - 1 || !tripPlan.days.length ? "disabled" : ""}>&gt;</button>
            <div class="small">Playback day: <strong>${tripPlaybackDay ? `Day ${tripPlaybackDay.day}` : "none"}</strong></div>
            <label class="small" style="display:flex; align-items:center; gap:6px;">
              <input id="imggen-trip-day-zoom" type="checkbox" ${state.tripPlaybackZoomToDay ? "checked" : ""} />
              Zoom to Day
            </label>
            <div class="small">${tripPlaybackDay ? (tripPlaybackDay.travelOnly ? esc(tripPlaybackDay.travelLabel || "Travel only day") : `${tripPlaybackDay.marinaCount} site${tripPlaybackDay.marinaCount === 1 ? "" : "s"} highlighted`) : "No trip days available."}</div>
          </div>
          ${statusBadgeHtml("Routing", routingStatusLabel(tripPlan.routing), "routing")}
          ${statusBadgeHtml("Per diem", tripPlan.totals.perDiemSource || "Not used", "perDiem")}
          ${tripPlan.totals.perDiemReason ? `<div class="small" style="margin-top:4px; color:#94a3b8;">Fallback reason: ${esc(tripPlan.totals.perDiemReason)}</div>` : ""}
          ${statusBadgeHtml("Route overlay", state.showTripRoute ? (routeGeometryStatus === "ready" ? "shown" : routeGeometryStatus === "loading" ? "loading" : routeGeometryError || "pending") : "hidden", "routeOverlay")}
          <div style="margin-top:10px; display:flex; gap:8px; flex-wrap:wrap;">
            <div style="padding:8px 10px; border:1px solid rgba(148,163,184,0.18); border-radius:10px; background:rgba(15,23,42,0.45); min-width:180px;">
              <div style="font-weight:700;">Trip Totals</div>
              <div class="small">Sites: ${tripPlan.totals.marinaCount}</div>
              <div class="small">Selected overall: ${tripPlan.totals.availableSelectedCount ?? tripPlan.totals.marinaCount}</div>
              <div class="small">Office scope: ${esc(tripPlan.office?.name || "None")}</div>
              <div class="small">Drive: ${esc(formatDriveMinutes(tripPlan.totals.driveMinutes))}</div>
              <div class="small">Mileage: ${esc(formatMilesPrecise(tripPlan.totals.driveMiles || 0))}</div>
              <div class="small">Survey: ${esc(formatDriveMinutes(tripPlan.totals.surveyMinutes))}</div>
              <div class="small">Total: ${esc(formatDriveMinutes(tripPlan.totals.totalMinutes))}</div>
              <div class="small">Weather contingency: ${esc(`${Number(tripPlan.totals.weatherContingencyPct || 0).toFixed(0)}%`)}</div>
              <div class="small">Weather days: ${tripPlan.totals.weatherContingencyDays || 0}</div>
              <div class="small">Trip days w/ weather: ${tripPlan.totals.adjustedTripDays ?? tripPlan.days.length}</div>
              <div class="small">Travel only days: ${tripPlan.totals.travelOnlyDays || 0}</div>
              <div class="small">Travel mode: ${esc(String(tripPlan.travelMode || state.planning.travelMode || "drive").toUpperCase())}</div>
              <div class="small">Per diem source: ${esc(tripPlan.totals.perDiemSource || "Not used")}</div>
              ${tripPlan.totals.perDiemReason ? `<div class="small">Per diem fallback: ${esc(tripPlan.totals.perDiemReason)}</div>` : ""}
              <div class="small">Price / site: ${esc(formatCurrency(state.planning.pricePerMarina || 0))}</div>
              <div class="small">Site cost: ${esc(formatCurrency((tripPlan.totals.marinaCount || 0) * (state.planning.pricePerMarina || 0)))}</div>
              <div class="small">Weather/travel contingency: ${esc(formatCurrency(tripPlan.totals.laborEquipmentCost || 0))}</div>
              <div class="small">Travel cost: ${esc(formatCurrency(tripPlan.totals.travelCost || 0))}</div>
              <div class="small">Per diem: ${esc(formatCurrency(tripPlan.totals.perDiemCost || 0))}${tripPlan.totals.perDiemDays ? ` (${tripPlan.totals.perDiemDays} night${tripPlan.totals.perDiemDays === 1 ? "" : "s"} × ${esc(formatCurrency(tripPlan.totals.perDiemRate || 0))}/night)` : ""}</div>
              <div class="small">Airfare: ${esc(formatCurrency(tripPlan.totals.airfareCost || 0))}</div>
              <div class="small">Hotel nights: ${tripPlan.totals.hotelNights}</div>
              ${tripPlan.returnLeg ? `<div class="small">Return leg: ${formatMiles(tripPlan.returnLeg.miles)}, ${formatDriveMinutes(tripPlan.returnLeg.driveMinutes)}, ${esc(tripPlan.returnLeg.source || "estimated")}</div>` : ""}
            </div>
          </div>
          <div style="margin-top:10px; display:flex; gap:8px; flex-wrap:wrap;">
            ${dayCards || `<div class="small">Select sites assigned to ${esc(tripPlan.office?.name || "the chosen office")} to generate a trip itinerary.</div>`}
          </div>
        `, { open: state.panels.tripPlanning, group: "planningGroup" })}
      </div>
      ${state.pendingAlertConfirm ? `
        <div id="alert-confirm-backdrop" style="position:fixed; inset:0; background:rgba(0,0,0,0.55); z-index:9000; display:flex; align-items:center; justify-content:center;" data-alert-confirm-dismiss="1">
          <div style="background:var(--bg1); border:1px solid var(--border); border-radius:8px; padding:18px 20px; max-width:440px; width:90%; box-shadow:0 12px 40px rgba(0,0,0,0.4);" onclick="event.stopPropagation()">
            <div style="font-weight:600; font-size:14px; margin-bottom:8px;">Send alert email?</div>
            <div class="small" style="margin-bottom:10px; color:var(--muted);">${esc(state.pendingAlertConfirm.marinaName)} (${esc(state.pendingAlertConfirm.code)})</div>
            <div class="small" style="margin-bottom:4px;"><strong>To:</strong> ${esc(state.pendingAlertConfirm.recipients.join(", "))}</div>
            <div class="small" style="margin-bottom:8px;"><strong>Subject:</strong> ${esc(state.pendingAlertConfirm.subject)}</div>
            <div class="small" style="white-space:pre-wrap; background:var(--bg2); border:1px solid var(--border); border-radius:5px; padding:8px; max-height:140px; overflow:auto; margin-bottom:14px;">${esc(state.pendingAlertConfirm.preview)}</div>
            <div style="display:flex; gap:8px; justify-content:flex-end;">
              <button data-alert-confirm-dismiss="1" class="btn" style="font-size:11px; padding:5px 12px;">Don't Send</button>
              <button data-alert-confirm-send="1" class="btn" style="font-size:11px; padding:5px 12px; background:#38bdf822; color:#38bdf8; border-color:#38bdf844;">Send Alert</button>
            </div>
          </div>
        </div>
      ` : ""}
      ${state.pendingClientDelivRegenConfirm ? `
        <div id="client-deliv-regen-confirm-backdrop" style="position:fixed; inset:0; background:rgba(0,0,0,0.55); z-index:9000; display:flex; align-items:center; justify-content:center;" data-client-deliv-regen-dismiss="1">
          <div style="background:var(--bg1); border:1px solid var(--border); border-radius:8px; padding:18px 20px; max-width:440px; width:90%; box-shadow:0 12px 40px rgba(0,0,0,0.4);" onclick="event.stopPropagation()">
            <div style="font-weight:600; font-size:14px; margin-bottom:8px;">Regenerate client portal link?</div>
            <div class="small" style="margin-bottom:14px; color:var(--muted);">
              ${(() => {
                const slug = state.pendingClientDelivRegenConfirm.clientSlug;
                const name = (state.catalog.clients || []).find((c) => c.id === slug)?.name || slug;
                return esc(name);
              })()} — the current link will stop working immediately for anyone who has it (client staff, bookmarks, anything it's embedded in). This can't be undone.
            </div>
            <div style="display:flex; gap:8px; justify-content:flex-end;">
              <button data-client-deliv-regen-dismiss="1" class="btn" style="font-size:11px; padding:5px 12px;">Cancel</button>
              <button data-client-deliv-regen-confirm="1" class="btn" style="font-size:11px; padding:5px 12px; background:#f8717122; color:#f87171; border-color:#f8717144;">Regenerate Link</button>
            </div>
          </div>
        </div>
      ` : ""}
      ${state.pendingFieldTrackRegenConfirm ? `
        <div id="field-track-regen-confirm-backdrop" style="position:fixed; inset:0; background:rgba(0,0,0,0.55); z-index:9000; display:flex; align-items:center; justify-content:center;" data-field-track-regen-dismiss="1">
          <div style="background:var(--bg1); border:1px solid var(--border); border-radius:8px; padding:18px 20px; max-width:440px; width:90%; box-shadow:0 12px 40px rgba(0,0,0,0.4);" onclick="event.stopPropagation()">
            <div style="font-weight:600; font-size:14px; margin-bottom:8px;">Regenerate field tracking link?</div>
            <div class="small" style="margin-bottom:14px; color:var(--muted);">
              ${(() => {
                const slug = state.pendingFieldTrackRegenConfirm.clientSlug;
                const name = (state.catalog.clients || []).find((c) => c.id === slug)?.name || slug;
                return esc(name);
              })()} — the current link will stop working immediately for anyone who has it (field crews, bookmarks, anything it's embedded in). This can't be undone.
            </div>
            <div style="display:flex; gap:8px; justify-content:flex-end;">
              <button data-field-track-regen-dismiss="1" class="btn" style="font-size:11px; padding:5px 12px;">Cancel</button>
              <button data-field-track-regen-confirm="1" class="btn" style="font-size:11px; padding:5px 12px; background:#f8717122; color:#f87171; border-color:#f8717144;">Regenerate Link</button>
            </div>
          </div>
        </div>
      ` : ""}
    `;
    renderProjectManagementBottomPanel();
    fileInputEl = containerEl.querySelector("#imggen-file");
    containerEl.querySelectorAll("details[data-imggen-panel]").forEach((details) => {
      details.ontoggle = () => {
        const key = String(details.dataset.imggenPanel || "");
        if (!key || !state.panels || !(key in state.panels)) return;
        const wasOpen = state.panels[key] !== false;
        state.panels[key] = !!details.open;
        syncPanelGroupVisibility();
        if (details.open && !wasOpen) render();
      };
    });
    syncPanelGroupVisibility();
    syncScheduleHistoryPortal();
  }

  function syncPanelGroupVisibility() {
    if (!containerEl) return;
    containerEl.querySelectorAll("details[data-imggen-group]").forEach((details) => {
      const groupKey = String(details.dataset.imggenGroup || "").trim();
      if (!groupKey) return;
      const visible = state?.panels?.[groupKey] !== false;
      details.style.display = visible ? "" : "none";
    });
  }

  // ── Section mounting (for standalone sidebar tool panels) ────────────────────
  const sectionEls = {};

  function buildMarinaPortfolioHtml() {
    if ("portfolio" in _htmlCache) return _htmlCache.portfolio;
    return (_htmlCache.portfolio = _buildMarinaPortfolioHtml());
  }
  function _buildMarinaPortfolioHtml() {
    if (state.tickets.blockingSummary === null) { state.tickets.blockingSummary = {}; loadTicketBlockingSummary().catch(() => {}); }
    const STATUS_OPTS = ["Not Started", "In Progress", "Complete", "On Hold", "N/A"];
    const statusIcon = (val) => {
      if (val === "Complete") return "✓";
      if (val === "In Progress") return "◑";
      if (val === "N/A") return "—";
      return "○";
    };
    const statusColor = (val) => {
      if (val === "Complete") return "#22c55e";
      if (val === "In Progress") return "#38bdf8";
      if (val === "N/A") return "#64748b";
      return "#94a3b8";
    };
    const sortIcon = (col) => {
      if (state.portfolioSort.col !== col) return `<span style="opacity:0.3; font-size:9px;">⇅</span>`;
      return `<span style="font-size:9px;">${state.portfolioSort.dir === "asc" ? "↑" : "↓"}</span>`;
    };
    const thBottom = "padding:3px 5px 4px; font-size:10px; font-weight:600; white-space:nowrap; color:var(--muted); border-bottom:1px solid var(--border); cursor:pointer; user-select:none; vertical-align:bottom;";
    const thVertical = (col, label) => `<th data-portfolio-sort="${esc(col)}" style="width:26px; min-width:26px; padding:4px 2px 2px; border-bottom:1px solid var(--border); cursor:pointer; user-select:none; text-align:center; vertical-align:bottom;">
      <div style="display:flex; flex-direction:column; align-items:center; gap:2px;">
        <span style="writing-mode:vertical-rl; transform:rotate(180deg); white-space:nowrap; font-size:10px; font-weight:600; color:var(--muted); line-height:1;">${label}</span>
        <span style="font-size:8px; opacity:0.6; line-height:1;">${state.portfolioSort.col === col ? (state.portfolioSort.dir === "asc" ? "↑" : "↓") : "⇅"}</span>
      </div>
    </th>`;
    const clients = Array.isArray(state.catalog.clients) ? state.catalog.clients : [];
    // state.catalog.selectedClientId is the authoritative source for the portfolio client dropdown.
    // Don't let context service's active client override the dropdown — they are separate concepts.
    const selectedClientId = String(state.catalog.selectedClientId || clients[0]?.id || "");
    const activeContextClient = clients.find(c => c.id === selectedClientId) || null;
    const STATUS_COLS = getClientMilestoneSchema(selectedClientId);
    let marinas = Array.isArray(state.catalog.marinas) ? [...state.catalog.marinas] : [];
    if (selectedClientId) {
      marinas = marinas.filter((m) => String(m.clientId || m.clientSlug || "safeharbor") === selectedClientId);
    }
    if (state.portfolioSort.col) {
      const col = state.portfolioSort.col;
      const dir = state.portfolioSort.dir === "desc" ? -1 : 1;
      marinas.sort((a, b) => {
        let av = "", bv = "";
        if (col === "code") { av = String(a.code || ""); bv = String(b.code || ""); }
        else if (col === "name") { av = String(a.name || ""); bv = String(b.name || ""); }
        else { av = String((a.statusColumns || {})[col] || ""); bv = String((b.statusColumns || {})[col] || ""); }
        return dir * av.localeCompare(bv, undefined, { sensitivity: "base" });
      });
    } else {
      // Default order: most-complete-first within each group (matches the client portal).
      // A row just edited keeps its pre-edit position for a few seconds (see
      // pmPinSortOrder) so cycling through milestone states doesn't reorder the
      // row out from under the next click.
      marinas.sort((a, b) => {
        const pa = pmSortPinned.has(a.code) ? pmSortPinned.get(a.code).pct : marinaSortScore(a, STATUS_COLS);
        const pb = pmSortPinned.has(b.code) ? pmSortPinned.get(b.code).pct : marinaSortScore(b, STATUS_COLS);
        return pb - pa || String(a.name || "").localeCompare(String(b.name || ""), undefined, { sensitivity: "base" });
      });
    }
    const groupActive   = marinas.filter(m => !m.archivedAt);
    const groupArchived = marinas.filter(m =>  !!m.archivedAt);
    const explicitGroups = Array.isArray(state.portfolioCustomGroups?.[selectedClientId]) ? state.portfolioCustomGroups[selectedClientId] : [];
    const catalogGroupNames = [...new Set(marinas.map(m => String(m.group || "")).filter(Boolean))].filter(g => !explicitGroups.includes(g)).sort();
    const customGroups = [...explicitGroups, ...catalogGroupNames];
    const activeTrays = state.portfolioActiveTrays && typeof state.portfolioActiveTrays === "object"
      ? state.portfolioActiveTrays : { production: true, routing: false, "project-management": false };
    const milestonesExpanded = !!activeTrays.production;
    const hasGroupCol = customGroups.length > 0;
    const TOTAL_COLS = 2 + (milestonesExpanded ? STATUS_COLS.length : 0) + (hasGroupCol ? 1 : 0) + 1;
    const activeClientName = clients.find((c) => c.id === selectedClientId)?.name || selectedClientId || "—";
    const clientPortalTokenLoaded = selectedClientId in (state.digitalAsset.clientDeliveryTokens || {});
    if (selectedClientId && !clientPortalTokenLoaded) setTimeout(() => loadClientDeliveryToken(selectedClientId).catch(() => {}), 0);
    const milestoneSchemaLoaded = selectedClientId in (state.digitalAsset.milestoneSchemas || {});
    if (selectedClientId && !milestoneSchemaLoaded) setTimeout(() => loadClientMilestoneSchema(selectedClientId).catch(() => {}), 0);
    const portfolioGroupsLoaded = selectedClientId in (state.portfolioGroupMeta || {});
    if (selectedClientId && !portfolioGroupsLoaded) setTimeout(() => loadClientPortfolioGroups(selectedClientId).catch(() => {}), 0);
    const clientPortalToken = (state.digitalAsset.clientDeliveryTokens || {})[selectedClientId];
    const clientPortalControls = selectedClientId ? (() => {
      if (!clientPortalTokenLoaded) {
        return `<span class="small" style="color:var(--muted);">Loading client portal link…</span>`;
      }
      if (!clientPortalToken?.token) {
        return `<button data-client-deliv-gen-token="${esc(selectedClientId)}" class="btn" style="font-size:10px; padding:2px 7px;">Generate Client Portal Link</button>`;
      }
      const url = `${window.location.origin}/client-delivery/${clientPortalToken.token}`;
      return `
        <a href="${esc(url)}" target="_blank" rel="noopener noreferrer" class="btn" style="font-size:10px; padding:2px 7px;">Client Portal ↗</a>
        <button data-client-deliv-copy-link="${esc(url)}" class="btn" style="font-size:10px; padding:2px 7px;">Copy Link</button>
        <button data-client-deliv-gen-token="${esc(selectedClientId)}" class="btn" style="font-size:10px; padding:2px 7px;">Regenerate</button>
        <button data-client-deliv-revoke-token="${esc(selectedClientId)}" class="btn" style="font-size:10px; padding:2px 7px; color:#f87171; border-color:#f8717144;">Revoke</button>
      `;
    })() : "";
    // Mirrors clientPortalControls above, against the separate field-tracking token
    // namespace -- an internal, no-login link for field crews, never handed to a client.
    const fieldTrackingTokenLoaded = selectedClientId in (state.digitalAsset.fieldTrackingTokens || {});
    if (selectedClientId && !fieldTrackingTokenLoaded) setTimeout(() => loadFieldTrackingToken(selectedClientId).catch(() => {}), 0);
    const fieldTrackingToken = (state.digitalAsset.fieldTrackingTokens || {})[selectedClientId];
    const fieldTrackingControls = selectedClientId ? (() => {
      if (!fieldTrackingTokenLoaded) {
        return `<span class="small" style="color:var(--muted);">Loading field tracking link…</span>`;
      }
      if (!fieldTrackingToken?.token) {
        return `<button data-field-track-gen-token="${esc(selectedClientId)}" class="btn" style="font-size:10px; padding:2px 7px;">Generate Field Tracking Link</button>`;
      }
      const url = `${window.location.origin}/field-tracking/${fieldTrackingToken.token}`;
      return `
        <a href="${esc(url)}" target="_blank" rel="noopener noreferrer" class="btn" style="font-size:10px; padding:2px 7px;">Field Tracking ↗</a>
        <button data-field-track-copy-link="${esc(url)}" class="btn" style="font-size:10px; padding:2px 7px;">Copy Link</button>
        <button data-field-track-gen-token="${esc(selectedClientId)}" class="btn" style="font-size:10px; padding:2px 7px;">Regenerate</button>
        <button data-field-track-revoke-token="${esc(selectedClientId)}" class="btn" style="font-size:10px; padding:2px 7px; color:#f87171; border-color:#f8717144;">Revoke</button>
      `;
    })() : "";
    // Invoice Wizard link -- unlike the two controls above, this isn't scoped to a client at
    // all (contract staff invoice AOS directly, not through any one marina client), so it's
    // loaded and rendered once, independent of selectedClientId.
    if (!state.digitalAsset.invoiceWizardTokenLoaded) setTimeout(() => loadInvoiceWizardToken().catch(() => {}), 0);
    const invoiceWizardToken = state.digitalAsset.invoiceWizardToken;
    const invoiceWizardControls = !state.digitalAsset.invoiceWizardTokenLoaded
      ? `<span class="small" style="color:var(--muted);">Loading invoice wizard link…</span>`
      : !invoiceWizardToken?.token
        ? `<button data-invoice-wizard-gen-token="1" class="btn" style="font-size:10px; padding:2px 7px;">Generate Invoice Wizard Link</button>`
        : (() => {
            const url = `${window.location.origin}/invoice-wizard/${invoiceWizardToken.token}`;
            return `
              <a href="${esc(url)}" target="_blank" rel="noopener noreferrer" class="btn" style="font-size:10px; padding:2px 7px;">Invoice Wizard ↗</a>
              <button data-invoice-wizard-copy-link="${esc(url)}" class="btn" style="font-size:10px; padding:2px 7px;">Copy Link</button>
              <button data-invoice-wizard-gen-token="1" class="btn" style="font-size:10px; padding:2px 7px;">Regenerate</button>
              <button data-invoice-wizard-revoke-token="1" class="btn" style="font-size:10px; padding:2px 7px; color:#f87171; border-color:#f8717144;">Revoke</button>
            `;
          })();
    const schemaOpen = !!(state.milestoneSchemaEditorOpen);
    const currentSchema = getClientMilestoneSchema(selectedClientId);
    const milestoneSchemaEditor = selectedClientId ? (() => {
      const rows = currentSchema.map((step, i) => `
        <div class="row" data-ms-drag-idx="${i}" style="gap:4px; align-items:center; margin-bottom:3px; cursor:default; border-radius:3px; padding:1px 0;">
          <button data-ms-up="${i}" class="btn" style="font-size:9px; padding:1px 4px; line-height:1; ${i === 0 ? "opacity:0.3; cursor:default;" : "cursor:pointer;"}" title="Move up" ${i === 0 ? "disabled" : ""}>▲</button>
          <button data-ms-dn="${i}" class="btn" style="font-size:9px; padding:1px 4px; line-height:1; margin-left:-2px; ${i === currentSchema.length - 1 ? "opacity:0.3; cursor:default;" : "cursor:pointer;"}" title="Move down" ${i === currentSchema.length - 1 ? "disabled" : ""}>▼</button>
          <input type="text" value="${esc(step.label)}" data-ms-edit-label="${i}" placeholder="Step name" style="font-size:10px; padding:2px 5px; width:200px; background:var(--bg2); color:var(--fg); border:1px solid var(--border); border-radius:3px;" />
          <button data-ms-remove="${i}" class="btn" style="font-size:10px; padding:1px 6px; color:#f87171; border-color:#f8717144;" title="Remove step">✕</button>
        </div>`).join("");
      return `
        <div style="margin-top:4px; padding:6px 8px; border:1px solid rgba(56,189,248,0.2); border-radius:5px; background:var(--bg2);" data-ms-client="${esc(selectedClientId)}">
          <div class="row" style="gap:6px; align-items:center; margin-bottom:${schemaOpen ? "8px" : "0"}; cursor:pointer;" data-ms-toggle="1">
            <span class="small" style="font-weight:600; color:var(--muted);">${schemaOpen ? "▾" : "▸"} Milestone Steps</span>
            <span class="small" style="color:var(--muted); font-weight:400;">(${currentSchema.length} steps)</span>
          </div>
          ${schemaOpen ? `
            <div id="ms-step-rows">${rows}</div>
            <div class="row" style="gap:6px; margin-top:4px;">
              <button data-ms-add-step="1" class="btn" style="font-size:10px; padding:2px 8px;">+ Add Step</button>
              <button data-ms-save-schema="1" class="btn" style="font-size:10px; padding:2px 8px; background:#38bdf822; color:#38bdf8; border-color:#38bdf844;">Save Schema</button>
              <button data-ms-reset-schema="1" class="btn" style="font-size:10px; padding:2px 8px; opacity:0.6;">Reset to Default</button>
            </div>` : ""}
        </div>`;
    })() : "";
    const mc = state.milestoneChanges || {};
    const mcOpen = !!state.milestoneChangesOpen;
    const MCC = { "Not Started": "#9ca3af", "In Progress": "#3b82f6", "Complete": "#22c55e", "On Hold": "#ef4444", "N/A": "#a855f7" };
    const rangeBtn = (kind, lbl) => `<button data-msch-range="${kind}" class="btn" style="font-size:10px; padding:2px 8px;${mc.kind === kind ? " background:#38bdf822; color:#38bdf8; border-color:#38bdf844;" : ""}">${lbl}</button>`;
    const milestoneChangesSection = selectedClientId ? `
      <div style="margin-top:4px; padding:6px 8px; border:1px solid rgba(167,139,250,0.25); border-radius:5px; background:var(--bg2);">
        <div class="row" style="gap:6px; align-items:center; margin-bottom:${mcOpen ? "8px" : "0"}; cursor:pointer;" data-msch-toggle="1">
          <span class="small" style="font-weight:600; color:var(--muted);">${mcOpen ? "▾" : "▸"} Milestone Changes</span>
          ${mcOpen && Array.isArray(mc.rows) ? `<span class="small" style="color:var(--muted); font-weight:400;">(${mc.rows.length} in ${mc.kind === "today" ? "today" : `past ${mc.kind}d`})</span>` : ""}
        </div>
        ${mcOpen ? `
          <div class="row" style="gap:6px; align-items:center; flex-wrap:wrap; margin-bottom:6px;">
            ${rangeBtn("today", "Today")}${rangeBtn("7", "7 Days")}${rangeBtn("30", "30 Days")}
            ${Array.isArray(mc.rows) && mc.since ? `
              <a class="btn" style="font-size:10px; padding:2px 8px; text-decoration:none;" href="/api/project/milestone-changes?format=csv&since=${encodeURIComponent(mc.since)}${selectedClientId ? `&clientSlug=${encodeURIComponent(selectedClientId)}` : ""}">↓ CSV</a>
              <button data-msch-print="1" class="btn" style="font-size:10px; padding:2px 8px;">🖨 Print</button>` : ""}
          </div>
          ${mc.loading ? `<div class="small" style="color:var(--muted); padding:6px 0;">Loading…</div>`
            : !Array.isArray(mc.rows) ? `<div class="small" style="color:var(--muted); padding:6px 0;">Pick a range.</div>`
            : !mc.rows.length ? `<div class="small" style="color:var(--muted); padding:6px 0;">No milestone changes in this period.</div>`
            : `<div style="max-height:260px; overflow:auto;"><table style="width:100%; border-collapse:collapse; font-size:10px;">
                <thead><tr>
                  <th style="text-align:left; padding:2px 6px; color:var(--muted); border-bottom:1px solid var(--border); white-space:nowrap;">When</th>
                  <th style="text-align:left; padding:2px 6px; color:var(--muted); border-bottom:1px solid var(--border);">Project</th>
                  <th style="text-align:left; padding:2px 6px; color:var(--muted); border-bottom:1px solid var(--border);">Milestone</th>
                  <th style="text-align:left; padding:2px 6px; color:var(--muted); border-bottom:1px solid var(--border); white-space:nowrap;">Change</th>
                  <th style="text-align:left; padding:2px 6px; color:var(--muted); border-bottom:1px solid var(--border);">By</th>
                </tr></thead><tbody>
                ${mc.rows.map((r) => `<tr>
                  <td style="padding:2px 6px; white-space:nowrap; border-bottom:1px solid rgba(148,163,184,0.08);">${esc(new Date(r.ts).toLocaleString())}</td>
                  <td style="padding:2px 6px; border-bottom:1px solid rgba(148,163,184,0.08);">${esc(r.projectName)} <span style="color:var(--muted)">${esc(r.code)}</span></td>
                  <td style="padding:2px 6px; border-bottom:1px solid rgba(148,163,184,0.08);">${esc(r.milestoneLabel)}</td>
                  <td style="padding:2px 6px; white-space:nowrap; border-bottom:1px solid rgba(148,163,184,0.08);"><span style="color:${MCC[r.from] || "var(--muted)"}">${esc(r.from)}</span> <span style="color:var(--muted)">→</span> <span style="color:${MCC[r.to] || "var(--fg)"}">${esc(r.to)}</span></td>
                  <td style="padding:2px 6px; border-bottom:1px solid rgba(148,163,184,0.08);">${r.source === "portal" ? `<span style="color:#38bdf8;">Client Portal</span>` : `<span style="color:#a78bfa;">AOS</span>`}</td>
                </tr>`).join("")}
                </tbody></table></div>`}` : ""}
      </div>` : "";
    // Use state.catalog.clients (S3-sourced via loadCatalog) as the dropdown list.
    // Also inject justCreatedClient if it hasn't been indexed by the server yet.
    const jcc = state.portfolioJustCreatedClient;
    const displayClients = (jcc && !clients.some(c => c.id === jcc.id))
      ? [...clients, jcc].sort((a, b) => a.name.localeCompare(b.name))
      : clients;
    const clientOptions = displayClients.map(c =>
      `<option value="${esc(c.id)}"${c.id === selectedClientId ? " selected" : ""}>${esc(c.name)}</option>`
    ).join("");
    const newClientInline = state.portfolioNewClientShow ? `
      <div class="row" style="gap:4px; align-items:center; margin-top:6px; flex-wrap:wrap;">
        <span class="small" style="color:var(--muted);">New client name:</span>
        <input id="portfolio-new-client-name" type="text" placeholder="Client name (required)" value="${esc(state.portfolioNewClientName || "")}" style="font-size:11px; padding:2px 6px; width:180px; background:var(--bg2); color:var(--fg); border:1px solid rgba(99,102,241,0.4); border-radius:3px;" />
        <button data-portfolio-new-client-confirm="1" class="btn" style="font-size:10px; padding:1px 6px; background:#6366f122; color:#818cf8; border-color:#6366f144;">✓ Create</button>
        <button data-portfolio-new-client-cancel="1" class="btn" style="font-size:10px; padding:1px 5px; opacity:0.5;">✕</button>
      </div>` : "";
    const selectedClientObj = displayClients.find(c => c.id === selectedClientId);
    const canArchiveSelectedClient = !!(selectedClientId && selectedClientId !== "safeharbor");
    // Alert rules are safeharbor-specific server-side (ALERT_RULES_KEY isn't
    // parameterized by client yet), so this panel is shown regardless of
    // which client is picked in the dropdown above, using safeharbor's own
    // milestone schema for the trigger-key options.
    const arOpen = !!state.alertRulesEditorOpen;
    const AR_STATUS_VALUES = ["Not Started", "In Progress", "Complete", "On Hold", "N/A"];
    const arSchema = getClientMilestoneSchema("safeharbor");
    const arTriggerOptions = (val) => arSchema.map((s) => `<option value="${esc(s.key)}"${s.key === val ? " selected" : ""}>${esc(s.label)}</option>`).join("");
    const arStatusOptions = (val) => AR_STATUS_VALUES.map((v) => `<option value="${esc(v)}"${v === val ? " selected" : ""}>${esc(v)}</option>`).join("");
    const arDraft = Array.isArray(state.alertRulesDraft) ? state.alertRulesDraft : [];
    const arRows = arDraft.map((r, i) => {
      const previewVars = { marinaName: "Test Marina", code: "TEST", key: r.triggerKey, from: "Not Started", to: r.toValue, timestamp: new Date().toISOString(), source: "internal" };
      const previewSubject = renderAlertPreviewTemplate(r.subject, previewVars);
      const previewBody = renderAlertPreviewTemplate(r.body, previewVars);
      const testStatus = state.alertTestStatus[i];
      return `
        <div data-ar-row="${i}" style="border:1px solid rgba(148,163,184,0.18); border-radius:6px; padding:8px; margin-bottom:8px; background:var(--bg1);">
          <div class="row" style="gap:6px; align-items:center; flex-wrap:wrap; margin-bottom:6px;">
            <input type="text" data-ar-field="id" value="${esc(r.id)}" placeholder="rule-id" style="font-size:10px; padding:2px 5px; width:140px; background:var(--bg2); color:var(--fg); border:1px solid var(--border); border-radius:3px;" />
            <span class="small" style="color:var(--muted);">when</span>
            <select data-ar-field="triggerKey" style="font-size:10px; padding:2px 4px; background:var(--bg2); color:var(--fg); border:1px solid var(--border); border-radius:3px;">${arTriggerOptions(r.triggerKey)}</select>
            <span class="small" style="color:var(--muted);">→</span>
            <select data-ar-field="toValue" style="font-size:10px; padding:2px 4px; background:var(--bg2); color:var(--fg); border:1px solid var(--border); border-radius:3px;">${arStatusOptions(r.toValue)}</select>
            <button data-ar-remove="${i}" class="btn" style="font-size:10px; padding:1px 6px; color:#f87171; border-color:#f8717144; margin-left:auto;" title="Remove rule">✕</button>
          </div>
          <div class="row" style="gap:6px; align-items:center; margin-bottom:6px;">
            <span class="small" style="color:var(--muted); white-space:nowrap;">To:</span>
            <input type="text" data-ar-field="recipients" value="${esc(r.recipients)}" placeholder="name@shmarinas.com, name2@shmarinas.com" style="font-size:10px; padding:2px 5px; flex:1; min-width:200px; background:var(--bg2); color:var(--fg); border:1px solid var(--border); border-radius:3px;" />
          </div>
          <input type="text" data-ar-field="subject" value="${esc(r.subject)}" placeholder="Subject — {marinaName} {code} {from} {to} {timestamp} {source}" style="font-size:10px; padding:2px 5px; width:100%; box-sizing:border-box; margin-bottom:6px; background:var(--bg2); color:var(--fg); border:1px solid var(--border); border-radius:3px;" />
          <textarea data-ar-field="body" placeholder="Body — same {vars} available" rows="3" style="font-size:10px; padding:4px 5px; width:100%; box-sizing:border-box; margin-bottom:6px; background:var(--bg2); color:var(--fg); border:1px solid var(--border); border-radius:3px; resize:vertical;">${esc(r.body)}</textarea>
          <div style="font-size:10px; color:var(--muted); background:var(--bg2); border:1px dashed var(--border); border-radius:4px; padding:5px 6px; margin-bottom:6px; white-space:pre-wrap;"><strong>Preview:</strong> ${esc(previewSubject)}
${esc(previewBody)}</div>
          <div class="row" style="gap:6px; align-items:center; flex-wrap:wrap;">
            <input type="email" data-ar-test-recipient placeholder="test recipient address" style="font-size:10px; padding:2px 5px; width:200px; background:var(--bg2); color:var(--fg); border:1px solid var(--border); border-radius:3px;" />
            <button data-ar-test="${i}" class="btn" style="font-size:10px; padding:2px 8px;" ${testStatus === "sending" ? "disabled" : ""}>${testStatus === "sending" ? "Sending…" : "Send Test"}</button>
            ${testStatus && testStatus !== "sending" ? (testStatus.ok
              ? `<span class="small" style="color:#22c55e;">✓ Sent</span>`
              : `<span class="small" style="color:#f87171;">✕ ${esc(testStatus.error)}</span>`) : ""}
          </div>
        </div>`;
    }).join("");
    const alertRulesSection = `
      <div style="margin-top:4px; padding:6px 8px; border:1px solid rgba(56,189,248,0.2); border-radius:5px; background:var(--bg2);">
        <div class="row" style="gap:6px; align-items:center; margin-bottom:${arOpen ? "8px" : "0"}; cursor:pointer;" data-ar-toggle="1">
          <span class="small" style="font-weight:600; color:var(--muted);">${arOpen ? "▾" : "▸"} Alert Rules</span>
          <span class="small" style="color:var(--muted); font-weight:400;">(${arDraft.length} rule${arDraft.length === 1 ? "" : "s"})</span>
        </div>
        ${arOpen ? `
          ${state.alertRulesLoading ? `<div class="small" style="color:var(--muted);">Loading…</div>` : ""}
          ${state.alertRulesLoadError ? `<div class="small" style="color:#f87171; margin-bottom:6px;">Load failed: ${esc(state.alertRulesLoadError)}</div>` : ""}
          ${!state.alertRulesLoading ? (arRows || `<div class="small" style="color:var(--muted); margin-bottom:6px;">No alert rules configured yet.</div>`) : ""}
          <div class="row" style="gap:6px; margin-top:4px;">
            <button data-ar-add-rule="1" class="btn" style="font-size:10px; padding:2px 8px;">+ Add Rule</button>
            <button data-ar-save="1" class="btn" style="font-size:10px; padding:2px 8px; background:#38bdf822; color:#38bdf8; border-color:#38bdf844;">Save Rules</button>
            ${state.alertRulesSaveStatus ? (state.alertRulesSaveStatus.ok
              ? `<span class="small" style="color:#22c55e;">✓ Saved</span>`
              : `<span class="small" style="color:#f87171;">✕ ${esc(state.alertRulesSaveStatus.error)}</span>`) : ""}
          </div>
        ` : ""}
      </div>`;
    const clientSelector = `
      <div class="row" style="gap:8px; align-items:center; flex-wrap:wrap;">
        <label class="small" style="color:var(--muted); white-space:nowrap;">Client:
          <select data-portfolio-client-select="1" style="margin-left:4px; font-size:11px; padding:1px 4px; background:var(--bg2); color:var(--fg); border:1px solid var(--border); border-radius:4px; max-width:200px;">
            ${clientOptions}
          </select>
        </label>
        ${!state.portfolioNewClientShow ? `<button data-portfolio-new-client="1" class="btn" style="font-size:10px; padding:1px 6px; background:#6366f122; color:#818cf8; border:1px solid #6366f144;">+ New Client</button>` : ""}
        <button id="imggen-pm-reload" class="btn" style="font-size:10px; padding:2px 7px;" title="Reload the client/project catalog from the server">Refresh</button>
        ${selectedClientId ? `<button data-portfolio-export-xls="1" class="btn" style="font-size:10px; padding:2px 7px;" title="Export every project's milestone status as a color-coded Excel workbook">⬇ Export XLS</button>` : ""}
        ${canArchiveSelectedClient ? `<button data-portfolio-archive-client="${esc(selectedClientId)}" data-portfolio-archive-client-name="${esc(selectedClientObj?.name || selectedClientId)}" class="btn" style="font-size:10px; padding:1px 6px; color:#f87171; border-color:#f8717144;" title="Move this client and all its projects out of the active catalog into an archive folder. Nothing is deleted.">Archive Client</button>` : ""}
      </div>
      ${clientPortalControls ? `<div class="row" style="gap:8px; align-items:center; flex-wrap:wrap; margin-top:6px;">${clientPortalControls}</div>` : ""}
      ${fieldTrackingControls ? `<div class="row" style="gap:8px; align-items:center; flex-wrap:wrap; margin-top:6px;">${fieldTrackingControls}</div>` : ""}
      <div class="row" style="gap:8px; align-items:center; flex-wrap:wrap; margin-top:6px;">${invoiceWizardControls}</div>
      ${newClientInline}
      ${milestoneSchemaEditor}
      ${milestoneChangesSection}
      ${alertRulesSection}
    `;
    if (state.catalog.error) {
      return `<div class="small" style="margin-top:8px; color:#f87171;">Catalog error: ${esc(state.catalog.error)}</div>`;
    }
    // No early return for empty clients — always render the table with Active/Archived groups
    const buildRows = (group, showGroupAssign = false) => {
      const activeCtxCode = String(app.services?.context?.getActiveProject?.()?.marinaId || app.services?.context?.getActiveProject?.()?.code || "").toUpperCase();
      return group.map((m) => {
        const code = String(m.code || "");
        const name = String(m.name || "");
        const archived = !!m.archivedAt;
        const lat = Number(m.lat);
        const lng = Number(m.lng);
        const hasLocation = Number.isFinite(lat) && Number.isFinite(lng);
        const isCtxActive = !!code && code.toUpperCase() === activeCtxCode;
        // Archive/restore/delete all move (or move+forget) every file in the marina's folder
        // synchronously with the request (see /api/project/activate, /api/project/deactivate,
        // /api/project/remove) -- a large marina can take real time with the button otherwise
        // looking completely inert. Swap it to a disabled, spinner-labeled state for the
        // duration so it reads as "working," not "broken."
        const isBusy = state.portfolioBusyCodes.has(code);
        const busyKind = state.portfolioBusyKind[code];
        const BUSY_LABEL = { restore: "Restoring…", archive: "Archiving…", delete: "Deleting…" };
        const BUSY_COLOR = { restore: "#22c55e", archive: "#38bdf8", delete: "#f87171" };
        const spinnerHtml = `<span class="spin" style="display:inline-block; width:9px; height:9px; border:1.5px solid currentColor; border-right-color:transparent; border-radius:50%; margin-right:4px; vertical-align:-1px;"></span>`;
        const archiveRestoreBtn = isBusy
          ? `<button class="btn" disabled style="font-size:10px; padding:1px 6px; background:${BUSY_COLOR[busyKind]}22; color:${BUSY_COLOR[busyKind]}; border:1px solid ${BUSY_COLOR[busyKind]}44; white-space:nowrap; opacity:0.85; cursor:default;">${spinnerHtml}${BUSY_LABEL[busyKind] || "Working…"}</button>`
          : archived
            ? `<button data-portfolio-restore="${esc(code)}" class="btn" style="font-size:10px; padding:1px 6px; background:#22c55e22; color:#22c55e; border:1px solid #22c55e44; white-space:nowrap;">Restore</button>`
            : isAdminUser()
              ? `<button data-portfolio-archive="${esc(code)}" class="btn" style="font-size:10px; padding:1px 6px; background:#38bdf822; color:#38bdf8; border:1px solid #38bdf844; white-space:nowrap;">Archive</button>`
              : "";
        const statusColsHtml = milestonesExpanded ? STATUS_COLS.map((c) => {
          const val = String((m.statusColumns || {})[c.key] || "Not Started");
          // Manual-flip design: an open ticket with blocksMilestone never writes statusColumns
          // itself (see server.js createTicketForProject/patchProjectTicket) — it only earns
          // this badge, leaving the actual status change to a person.
          const blockCount = state.tickets.blockingSummary?.[code]?.[c.key] || 0;
          const blockDot = blockCount
            ? `<span title="Blocked by ${blockCount} open ticket${blockCount === 1 ? "" : "s"}" style="position:absolute; top:-1px; right:-1px; width:7px; height:7px; border-radius:50%; background:#ef4444; box-shadow:0 0 0 1px var(--surface);"></span>`
            : "";
          return `<td style="padding:2px 0; text-align:center; width:26px;">
            <button data-pm-status-code="${esc(code)}" data-pm-status-col="${esc(c.key)}" data-pm-status-val="${esc(val)}"
              title="${esc(c.label)}: ${esc(val)}${blockCount ? ` — blocked by ${blockCount} open ticket${blockCount === 1 ? "" : "s"}` : ""}"
              style="position:relative; background:none; border:none; cursor:pointer; font-size:13px; color:${statusColor(val)}; padding:0; line-height:20px; width:20px; height:20px; text-align:center; display:inline-block;">
              ${statusIcon(val)}${blockDot}
            </button>
          </td>`;
        }).join("") : "";
        let groupTdHtml = "";
        if (hasGroupCol) {
          const currentGroup = String(m.group || "");
          groupTdHtml = showGroupAssign && activeTrays["project-management"]
            ? `<td style="padding:2px 4px; white-space:nowrap;">
                <select data-portfolio-assign-group="${esc(code)}" style="font-size:10px; padding:1px 4px; background:var(--bg2); color:var(--fg); border:1px solid var(--border); border-radius:3px; max-width:90px;">
                  <option value="">— None —</option>
                  ${customGroups.map(g => `<option value="${esc(g)}"${currentGroup === g ? " selected" : ""}>${esc(g)}</option>`).join("")}
                </select>
              </td>`
            : `<td></td>`;
        }
        const pmActionsHtml = activeTrays["project-management"] ? `
            <button data-portfolio-zoom="${esc(code)}" class="btn" style="font-size:10px; padding:1px 5px;" title="Zoom to ${esc(name)} on map">⌖</button>
            ${!archived ? `<button data-portfolio-ctx-activate="${esc(code)}" class="btn" style="font-size:10px; padding:1px 6px;${isCtxActive ? " opacity:0.4; cursor:default;" : " background:#6366f122; color:#a5b4fc; border:1px solid #6366f144;"}" ${isCtxActive ? "disabled" : ""}>Activate</button>` : ""}
            ${archiveRestoreBtn}
            ${archived && !isBusy ? `<button data-portfolio-delete="${esc(code)}" class="btn" style="font-size:10px; padding:1px 5px; color:#f87171; border-color:#f8717144; margin-left:3px;" title="Permanently delete from catalog (irreversible)">&#x1F5D1;</button>` : ""}
          ` : "";
        return `<tr style="border-bottom:1px solid rgba(148,163,184,0.06); background:${isCtxActive ? "rgba(99,102,241,0.12)" : "transparent"};">
          <td style="padding:3px 5px; font-size:11px; font-weight:700; white-space:nowrap;">${isCtxActive ? `<span style="color:var(--accent,#38bdf8); margin-right:3px;">●</span>` : ""}${esc(code)}</td>
          <td style="padding:3px 5px; font-size:11px; white-space:nowrap; width:100%; overflow:hidden; text-overflow:ellipsis;" title="${esc(name)}">${esc(name)}</td>
          ${statusColsHtml}
          ${groupTdHtml}
          <td style="padding:3px 4px; white-space:nowrap; text-align:right;">${pmActionsHtml}</td>
        </tr>`;
      }).join("");
    };
    const groupHeader = (key, label, count, rgb) => {
      const open = !!(state.portfolioGroups?.[key]);
      const dotsOn = !!(state.portfolioMapPoints?.[key]);
      const addGroupBtn = key === "active" && open
        ? `<button data-portfolio-new-project="1" class="btn" style="font-size:10px; padding:1px 6px; background:#22c55e22; color:#22c55e; border:1px solid #22c55e44;" title="Create a new project for this client">+ New Project</button>
           <button data-portfolio-new-group="1" class="btn" style="font-size:10px; padding:1px 6px; opacity:0.7;" title="Add a new group to organize active projects">+ Group</button>`
        : key === "archived" && open && count > 0
        ? `<button data-portfolio-purge-archived="${esc(selectedClientId)}" class="btn" style="font-size:10px; padding:1px 6px; color:#f87171; border-color:#f8717144;" title="Permanently delete all ${count} archived projects">&#x1F5D1; Delete All (${count})</button>`
        : "";
      return `<tr data-portfolio-group="${key}" style="cursor:pointer; background:rgba(${rgb},0.07); border-bottom:1px solid rgba(${rgb},0.18);">
        <td colspan="${TOTAL_COLS}" style="padding:5px 8px; font-size:11px; font-weight:600; color:rgba(${rgb},1); user-select:none;">
          <div style="display:flex; align-items:center; gap:4px;">
            <span style="display:inline-block; width:12px; font-size:10px; flex-shrink:0;">${open ? "▾" : "▸"}</span>
            <span style="flex-shrink:0;">${label}</span>
            <span style="font-size:10px; font-weight:400; opacity:0.6; flex-shrink:0;">(${count})</span>
            <span style="margin-left:auto; display:inline-flex; align-items:center; gap:4px; flex-shrink:0;">
              <button data-portfolio-map-points="${key}" class="btn" style="font-size:10px; padding:1px 6px;${dotsOn ? ` background:rgba(${rgb},0.18); color:rgba(${rgb},1); border-color:rgba(${rgb},0.5);` : " opacity:0.5;"}" title="${dotsOn ? "Hide" : "Show"} on map">&#x25cf; Map</button>
              ${addGroupBtn}
            </span>
          </div>
        </td>
      </tr>`;
    };
    const customGroupHeader = (groupName, count) => {
      const collapseKey = `${selectedClientId}::${groupName}`;
      const collapsed = !!(state.portfolioCustomGroupCollapsed?.[collapseKey]);
      const gm = getGroupMeta(selectedClientId, groupName);
      const autoColor = CB_PALETTE[(explicitGroups.indexOf(groupName) + 4) % CB_PALETTE.length] || "#a5b4fc";
      const dotColor = gm.color || autoColor;
      const officeOpts = (state.offices || []).map(o =>
        `<option value="${esc(o.id)}"${gm.officeId === o.id ? " selected" : ""}>${esc(o.name)}</option>`
      ).join("");
      return `<tr data-portfolio-custom-group="${esc(collapseKey)}" style="background:rgba(99,102,241,0.08); border-bottom:1px solid rgba(99,102,241,0.2);">
        <td colspan="${TOTAL_COLS}" style="padding:4px 8px; font-size:11px; font-weight:600; color:#a5b4fc; user-select:none;">
          <div style="display:flex; align-items:center; gap:4px;">
            <span data-portfolio-custom-group-toggle="${esc(collapseKey)}" style="cursor:pointer; display:inline-flex; align-items:center; gap:4px; flex-shrink:0;">
              <span style="display:inline-block; width:12px; font-size:10px;">${collapsed ? "▸" : "▾"}</span>
              ${esc(groupName)}
              <span style="font-size:10px; font-weight:400; opacity:0.6;">(${count})</span>
            </span>
            <span style="margin-left:auto; display:inline-flex; align-items:center; gap:3px; flex-shrink:0;">
              <button data-portfolio-group-visible="${esc(collapseKey)}" class="btn" style="font-size:10px; padding:1px 6px;${gm.visible ? " background:rgba(99,102,241,0.18); color:#a5b4fc; border-color:rgba(99,102,241,0.5);" : " opacity:0.4;"}" title="${gm.visible ? "Hide group on map" : "Show group on map"}">&#x25cf; Map</button>
              <input type="color" data-portfolio-group-color="${esc(collapseKey)}" value="${dotColor}" title="Map dot color for this group" style="width:22px; height:18px; padding:0; border:1px solid rgba(148,163,184,0.3); border-radius:3px; background:none; cursor:pointer; vertical-align:middle;">
              ${activeTrays.routing ? `
              <select data-portfolio-group-office="${esc(collapseKey)}" title="Assigned office for trip reports" style="font-size:10px; padding:1px 3px; background:var(--bg2); color:var(--fg); border:1px solid var(--border); border-radius:3px; max-width:120px;">
                <option value="">— Office —</option>
                ${officeOpts}
              </select>
              <label title="Fly-in required" style="display:inline-flex; align-items:center; gap:2px; font-size:10px; font-weight:400; cursor:pointer;">
                <input type="checkbox" data-portfolio-group-flyin="${esc(collapseKey)}" ${gm.flyIn ? "checked" : ""} style="margin:0;"> ✈
              </label>
              ${gm.flyIn ? `<input type="text" data-portfolio-group-flyin-airport="${esc(collapseKey)}" value="${esc(gm.flyInAirport)}" placeholder="IATA" maxlength="4" title="Fly-in departure airport code" style="width:44px; font-size:10px; padding:1px 4px; background:var(--bg2); color:var(--fg); border:1px solid var(--border); border-radius:3px; text-transform:uppercase;">` : ""}
              <button data-portfolio-group-include="${esc(collapseKey)}" class="btn" style="font-size:10px; padding:1px 6px;${gm.includeInCombined ? " background:rgba(99,102,241,0.18); color:#a5b4fc; border-color:rgba(99,102,241,0.5);" : " opacity:0.4;"}" title="${gm.includeInCombined ? "Remove from combined PDF" : "Include in combined PDF"}">&#128196; PDF</button>
              <button data-portfolio-group-report="${esc(collapseKey)}" class="btn" style="font-size:11px; padding:0px 5px; opacity:0.7;" title="Generate trip report for this group">⎙</button>
              ` : ""}
              <button data-portfolio-rename-group="${esc(collapseKey)}" class="btn" style="font-size:10px; padding:0px 5px; opacity:0.5;" title="Rename group">✎</button>
              <button data-portfolio-delete-group="${esc(collapseKey)}" class="btn" style="font-size:10px; padding:0px 5px; opacity:0.45; color:#f87171; border-color:#f8717144;" title="Delete group (projects become unassigned)">✕</button>
            </span>
          </div>
        </td>
      </tr>`;
    };
    // Color By selector — swatch legends live on the map itself via the Map Legend toggle,
    // no need to duplicate them here. The milestone step select is a functional control, not
    // just a legend, so it stays inline.
    const colorBy   = state.portfolioColorBy  || "status";
    const colorStep = state.portfolioColorStep || "";
    const CB_PALETTE = [
      "#f87171","#fb923c","#fbbf24","#a3e635","#34d399","#22d3ee",
      "#60a5fa","#a78bfa","#f472b6","#f43f5e","#84cc16","#10b981",
      "#06b6d4","#3b82f6","#8b5cf6","#ec4899","#14b8a6","#6366f1",
      "#d946ef","#f59e0b","#ef4444","#22c55e","#0ea5e9","#7c3aed",
    ];

    let colorStepControl = "";
    if (colorBy === "milestone") {
      const stepOpts = STATUS_COLS.map(c => `<option value="${esc(c.key)}"${colorStep === c.key ? " selected" : ""}>${esc(c.label)}</option>`).join("");
      colorStepControl = `<select id="imggen-portfolio-color-step" style="font-size:10px; padding:2px 5px; background:var(--bg2); color:var(--fg); border:1px solid var(--border); border-radius:3px;">${stepOpts}</select>`;
    }

    const showLeg = state.portfolioShowMapLegend;
    const colorByStrip = `<div style="display:flex; align-items:center; gap:6px; margin-top:8px; padding-bottom:6px; border-bottom:1px solid rgba(148,163,184,0.1); flex-wrap:wrap;">
      <label class="small" style="display:flex; align-items:center; gap:4px; color:var(--muted);">
        Color by:
        <select data-portfolio-color-by-select="1" style="font-size:10px; padding:1px 4px; background:var(--bg2); color:var(--fg); border:1px solid var(--border); border-radius:3px;">
          <option value="status"${colorBy === "status" ? " selected" : ""}>Status</option>
          <option value="state"${colorBy === "state" ? " selected" : ""}>State</option>
          <option value="milestone"${colorBy === "milestone" ? " selected" : ""}>Milestone</option>
          <option value="group"${colorBy === "group" ? " selected" : ""}>Group</option>
        </select>
      </label>
      ${colorStepControl}
      <span style="margin-left:auto; display:flex; gap:4px;">
        <button id="imggen-toggle-map-legend" class="btn" style="font-size:10px; padding:1px 7px;${showLeg ? " background:rgba(96,165,250,0.18); color:#60a5fa; border-color:#60a5fa66;" : " opacity:0.75;"}" title="Toggle map legend overlay">Map Legend</button>
        <span class="small" style="display:flex; align-items:center; gap:4px; color:var(--muted);" title="Toggle which trays appear on each project row — any combination. Production: milestone columns. Routing: trip/PDF/office tools on group headers. Project Management: zoom/activate/archive/delete actions. All off shows just the project name.">
          Show:
          <button data-portfolio-tray-toggle="production" class="btn" style="font-size:10px; padding:1px 7px; white-space:nowrap;${activeTrays.production ? " background:rgba(34,197,94,0.18); color:#4ade80; border-color:#4ade8066;" : " opacity:0.5;"}">Production</button>
          <button data-portfolio-tray-toggle="routing" class="btn" style="font-size:10px; padding:1px 7px; white-space:nowrap;${activeTrays.routing ? " background:rgba(96,165,250,0.18); color:#60a5fa; border-color:#60a5fa66;" : " opacity:0.5;"}">Routing</button>
          <button data-portfolio-tray-toggle="project-management" class="btn" style="font-size:10px; padding:1px 7px; white-space:nowrap;${activeTrays["project-management"] ? " background:rgba(167,139,250,0.18); color:#a78bfa; border-color:#a78bfa66;" : " opacity:0.5;"}">Project Mgmt</button>
        </span>
      </span>
    </div>`;

    // Build the active sub-groups section (ctxByCode is only computed here, when needed)
    const activeSubGroupsHtml = (() => {
      if (!state.portfolioGroups?.active) return "";
      const addGroupRow = state.portfolioAddGroupShow
        ? `<tr><td colspan="${TOTAL_COLS}" style="padding:4px 14px; background:rgba(99,102,241,0.05);">
            <input id="portfolio-add-group-input" type="text"
              placeholder="Group name (e.g. Wave 1)"
              value="${esc(state.portfolioAddGroupName || "")}"
              style="font-size:11px; padding:2px 6px; width:160px; background:var(--bg2); color:var(--fg); border:1px solid rgba(99,102,241,0.4); border-radius:3px;" />
            <button data-portfolio-add-group-confirm="1" class="btn" style="font-size:10px; padding:1px 6px; background:#6366f122; color:#a5b4fc; border-color:#6366f144; margin-left:4px;">✓ Add</button>
            <button data-portfolio-add-group-cancel="1" class="btn" style="font-size:10px; padding:1px 5px; opacity:0.5; margin-left:3px;">✕</button>
          </td></tr>`
        : "";
      const newProjectRow = state.portfolioNewProjectShow
        ? `<tr><td colspan="${TOTAL_COLS}" style="padding:5px 14px; background:rgba(34,197,94,0.05); border-bottom:1px solid rgba(34,197,94,0.15);">
            <span style="font-size:10px; color:var(--muted); margin-right:6px;">New project:</span>
            <input id="portfolio-new-project-name" type="text"
              placeholder="Name (required)"
              value="${esc(state.portfolioNewProjectName || "")}"
              style="font-size:11px; padding:2px 6px; width:160px; background:var(--bg2); color:var(--fg); border:1px solid rgba(34,197,94,0.4); border-radius:3px;" />
            <input id="portfolio-new-project-code" type="text"
              placeholder="Code (auto if blank)"
              value="${esc(state.portfolioNewProjectCode || "")}"
              style="font-size:11px; padding:2px 6px; width:120px; background:var(--bg2); color:var(--fg); border:1px solid rgba(34,197,94,0.3); border-radius:3px; margin-left:4px;" />
            <button data-portfolio-new-project-confirm="1" class="btn" style="font-size:10px; padding:1px 6px; background:#22c55e22; color:#22c55e; border-color:#22c55e44; margin-left:4px;">✓ Create</button>
            <button data-portfolio-new-project-cancel="1" class="btn" style="font-size:10px; padding:1px 5px; opacity:0.5; margin-left:3px;">✕</button>
          </td></tr>`
        : "";
      // Partition active marinas by group (group stored on catalog marina m.group)
      const byGroup = new Map();
      const unassigned = [];
      for (const m of groupActive) {
        const g = String(m.group || "");
        if (g && customGroups.includes(g)) {
          if (!byGroup.has(g)) byGroup.set(g, []);
          byGroup.get(g).push(m);
        } else {
          unassigned.push(m);
        }
      }
      const customRows = customGroups.map(groupName => {
        const collapseKey = `${selectedClientId}::${groupName}`;
        const collapsed = !!(state.portfolioCustomGroupCollapsed?.[collapseKey]);
        const rows = byGroup.get(groupName) || [];
        return customGroupHeader(groupName, rows.length) + (collapsed ? "" : buildRows(rows, true));
      }).join("");
      const unassignedColKey = `${selectedClientId}::__unassigned__`;
      const unassignedCollapsed = !!(state.portfolioCustomGroupCollapsed?.[unassignedColKey]);
      const unassignedSection = customGroups.length > 0
        ? `<tr data-portfolio-custom-group="${esc(unassignedColKey)}" style="cursor:pointer; background:rgba(100,116,139,0.08); border-bottom:1px solid rgba(100,116,139,0.15);">
            <td colspan="${TOTAL_COLS}" style="padding:4px 14px; font-size:11px; font-weight:500; color:rgba(148,163,184,0.8); user-select:none;">
              <span style="display:inline-block; width:12px; font-size:10px;">${unassignedCollapsed ? "▸" : "▾"}</span>
              Unassigned
              <span style="font-size:10px; font-weight:400; opacity:0.6; margin-left:4px;">(${unassigned.length})</span>
            </td>
          </tr>
          ${unassignedCollapsed ? "" : buildRows(unassigned, true)}`
        : buildRows(unassigned, true);
      return newProjectRow + addGroupRow + customRows + unassignedSection;
    })();
    const combinedGroupCount = customGroups.filter(g => getGroupMeta(selectedClientId, g).includeInCombined).length;
    return `
      ${clientSelector}
      ${colorByStrip}
      <div style="margin-top:8px; overflow-x:auto;">
        <table style="width:100%; border-collapse:collapse; font-size:11px;">
          <thead>
            <tr>
              <th data-portfolio-sort="code" style="${thBottom} text-align:left;">Code ${sortIcon("code")}</th>
              <th data-portfolio-sort="name" style="${thBottom} text-align:left; width:100%;">Name ${sortIcon("name")}</th>
              ${milestonesExpanded ? STATUS_COLS.map((c) => thVertical(c.key, c.label)).join("") : ""}
              ${hasGroupCol ? `<th style="${thBottom} text-align:left; font-size:10px;">Group</th>` : ""}
              <th style="${thBottom}"></th>
            </tr>
          </thead>
          <tbody>
            ${groupHeader("active", "Active Projects", groupActive.length, "34,197,94")}
            ${activeSubGroupsHtml}
          </tbody>
          <tbody>
            ${groupHeader("archived", "Archived Projects", groupArchived.length, "56,189,248")}
            ${state.portfolioGroups?.archived ? buildRows(groupArchived, false) : ""}
          </tbody>
        </table>
      </div>
      ${customGroups.length > 0 || isAdminUser() ? `
      <div style="margin-top:6px; display:flex; align-items:center; justify-content:flex-end; gap:8px;">
        ${customGroups.length > 0 ? `<span style="font-size:10px; opacity:0.5;">${combinedGroupCount} of ${customGroups.length} group${customGroups.length === 1 ? "" : "s"} selected for PDF</span>
        <button data-portfolio-combined-report="1" class="btn" ${combinedGroupCount === 0 || exporting ? "disabled" : ""} style="font-size:11px; padding:2px 10px;">&#128196; Combined PDF</button>` : ""}
        ${isAdminUser() ? `<button data-schedule-history-toggle="${esc(selectedClientId)}" class="btn" style="font-size:11px; padding:2px 10px;" title="View and restore prior planned-schedule saves for this client">&#128337; Schedule History</button>` : ""}
      </div>` : ""}
    `;
  }

  function renderScheduleHistoryPanel(clientSlug) {
    const checkpoints = state.scheduleHistory?.[clientSlug];
    const fieldLabel = {
      acquisitionStart: "Acq. Start", acquisitionEnd: "Acq. End",
      processingStart: "Proc. Start", processingEnd: "Proc. End",
    };
    const rows = (checkpoints || []).map((cp) => {
      const dt = new Date(cp.ts);
      const when = Number.isFinite(dt.getTime())
        ? dt.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })
        : cp.ts;
      const summary = cp.changes.length
        ? cp.changes.map((c) => `${esc(c.group)} ${esc(fieldLabel[c.field] || c.field)}: ${esc(c.from || "—")} &rarr; ${esc(c.to || "—")}`).join("<br>")
        : "Earliest known state";
      return `
        <div style="padding:6px 8px; border-bottom:1px solid rgba(148,163,184,0.14); font-size:10px;">
          <div style="display:flex; align-items:center; justify-content:space-between; gap:6px;">
            <span style="font-weight:600;${cp.isLatest ? " color:#38bdf8;" : ""}">${esc(when)}${cp.isLatest ? " (current)" : ""}</span>
            ${cp.isLatest ? "" : `<button data-schedule-history-restore="${esc(clientSlug)}::${esc(cp.versionId)}" class="btn" style="font-size:9px; padding:1px 6px;">Restore</button>`}
          </div>
          <div style="opacity:0.75; margin-top:2px;">${summary}</div>
        </div>`;
    }).join("");
    return `
      <div data-schedule-history-panel="1" style="position:fixed; z-index:9999; width:320px; max-height:320px; overflow-y:auto; background:var(--bg2); border:1px solid var(--border); border-radius:6px; box-shadow:0 6px 20px rgba(0,0,0,0.45);">
        <div style="padding:5px 8px; display:flex; align-items:center; justify-content:space-between; font-size:10px; font-weight:600; border-bottom:1px solid rgba(148,163,184,0.18); opacity:0.8;">
          <span>Planned-schedule saves</span>
          <span data-schedule-history-close="1" style="cursor:pointer; opacity:0.7;" title="Close">&#x2715;</span>
        </div>
        ${checkpoints === undefined
          ? `<div style="padding:10px; font-size:10px; opacity:0.6;">Loading…</div>`
          : (rows || `<div style="padding:10px; font-size:10px; opacity:0.6;">No schedule changes recorded.</div>`)}
      </div>`;
  }

  function buildProjectSettingsHtml() {
    // Active project is driven by Project Portfolio via context service
    const activeCtxProject = app.services?.context?.getActiveProject?.();
    const activeCtxClient = app.services?.context?.getActiveClient?.();
    // Sync local selectedProjectId with context whenever context has an active project
    if (activeCtxProject?.marinaId && state.projectSettings.selectedProjectId !== activeCtxProject.marinaId) {
      state.projectSettings.selectedProjectId = activeCtxProject.marinaId;
    }
    const epsgItem = resolveProjectSettingsMarina().item;
    const epsgOptions = epsgOptionsForMarina(epsgItem);
    const selectedEpsg = epsgOptions.some((x) => x.code === String(state.projectSettings.epsgCode || ""))
      ? String(state.projectSettings.epsgCode || "")
      : String(epsgOptions[0]?.code || "2227");
    if (!activeCtxProject) {
      return `
        <div class="small" style="color:var(--muted); margin-top:4px;">No active project — select one in Project Portfolio.</div>
      `;
    }
    const projectLabel = `${esc(activeCtxProject.marinaId || activeCtxProject.code || activeCtxProject.id)}`;
    const clientLabel = esc(activeCtxClient?.name || activeCtxProject.clientName || "");
    const selectedCatalogMarina = resolveProjectSettingsMarina().item;
    return `
      <div class="row" style="gap:6px; align-items:center; margin-bottom:8px; flex-wrap:wrap;">
        <span class="small" style="color:var(--muted);">Client:</span>
        <span class="small" style="color:var(--fg); font-weight:600;">${clientLabel}</span>
        <span class="small" style="color:var(--muted); margin-left:8px;">Project:</span>
        <span class="small" style="color:var(--fg); font-weight:600;">${projectLabel}</span>
        <button id="imggen-project-marina-zoom" type="button" class="btn" style="font-size:10px; padding:1px 7px; margin-left:4px;"${selectedCatalogMarina ? "" : " disabled"}>Zoom To</button>
      </div>
      <div class="row" style="gap:8px; flex-wrap:wrap; align-items:center;">
        <label class="small">EPSG
          <select id="imggen-project-epsg" style="margin-left:6px; min-width:200px;">
            ${epsgOptions.map((opt) => `<option value="${esc(opt.code)}"${selectedEpsg === opt.code ? " selected" : ""}>${esc(opt.label)}</option>`).join("")}
          </select>
        </label>
        <label class="small">Z Unit
          <select id="imggen-project-z-unit" style="margin-left:6px;">
            <option value="ft" ${state.projectSettings.nativeZUnit === "ft" ? "selected" : ""}>feet</option>
            <option value="m" ${state.projectSettings.nativeZUnit === "m" ? "selected" : ""}>meters</option>
          </select>
        </label>
      </div>
    `;
  }

  function buildNotesHtml() {
    const code = String(state.projectSettings.selectedProjectId || "");
    if (!code) return `<div class="small" style="margin-top:8px; color:var(--muted);">Select an active project in Project Manager to view its notes.</div>`;
    const notes = state.notes.byProject[code];
    if (notes === undefined && !state.notes.loading[code]) setTimeout(() => loadProjectNotes(code), 0);
    const posting = !!state.notes.posting;
    const draft = String(state.notes.draftText || "");
    const listHtml = notes === undefined
      ? `<div class="small" style="padding:8px 0; color:var(--muted);">Loading…</div>`
      : notes.length === 0
        ? `<div class="small" style="padding:8px 0; color:var(--muted);">No notes yet for ${esc(code)}.</div>`
        : notes.map((n) => `
          <div style="padding:10px 0; border-bottom:1px solid rgba(148,163,184,0.15);">
            <div class="small" style="color:var(--muted); display:flex; gap:8px; margin-bottom:4px;">
              <span>${esc(new Date(n.createdAt).toLocaleString())}</span>
              <span>·</span>
              <span>${n.source === "portal" ? `Client Portal${n.authorName ? " — " + esc(n.authorName) : ""}` : esc(n.authorEmail || n.authorName || "AOS")}</span>
            </div>
            <div style="white-space:pre-wrap; font-size:13px;">${esc(n.text)}</div>
          </div>
        `).join("");
    return `
      <div style="margin-top:8px; max-width:640px;">
        <textarea data-note-draft rows="3" placeholder="Add a note about ${esc(code)}…"
          style="width:100%; resize:vertical; padding:8px; background:var(--bg2); border:1px solid var(--border); border-radius:6px; color:var(--fg); font:inherit; box-sizing:border-box;">${esc(draft)}</textarea>
        <button data-note-add class="btn primary" style="margin:8px 0 14px;" ${posting ? "disabled" : ""}>${posting ? "Adding…" : "Add Note"}</button>
        <div>${listHtml}</div>
      </div>
    `;
  }

  function buildTicketsHtml() {
    const code = String(state.projectSettings.selectedProjectId || "");
    if (!code) return `<div class="small" style="margin-top:8px; color:var(--muted);">Select an active project in Project Manager to view its tickets.</div>`;
    if (state.tickets.blockingSummary === null) { state.tickets.blockingSummary = {}; loadTicketBlockingSummary().catch(() => {}); }
    const tickets = state.tickets.byProject[code];
    if (tickets === undefined && !state.tickets.loading[code]) setTimeout(() => loadProjectTickets(code), 0);
    const clientSlug = (state.catalog.marinas || []).find((m) => m.code === code)?.clientSlug || "";
    const schema = getClientMilestoneSchema(clientSlug);

    const filterStatus = state.tickets.filterStatus;
    const filterMilestoneKey = state.tickets.filterMilestoneKey;
    const view = (tickets || []).filter((t) =>
      (!filterStatus || t.status === filterStatus) &&
      (!filterMilestoneKey || t.milestoneKey === filterMilestoneKey)
    );

    const statusLabel = { open: "Open", in_progress: "In Progress", resolved: "Resolved", closed: "Closed" };
    const statusColor = { open: "#38bdf8", in_progress: "#f59e0b", resolved: "#22c55e", closed: "#64748b" };

    const filterBar = `
      <div style="display:flex; gap:8px; align-items:center; margin-bottom:10px; flex-wrap:wrap;">
        <select data-ticket-filter="status" style="font-size:11px; padding:3px 6px; background:var(--bg2); color:var(--fg); border:1px solid var(--border); border-radius:4px;">
          <option value="">All Statuses</option>
          ${TICKET_STATUS_ORDER.map((s) => `<option value="${s}" ${filterStatus === s ? "selected" : ""}>${statusLabel[s]}</option>`).join("")}
        </select>
        <select data-ticket-filter="milestoneKey" style="font-size:11px; padding:3px 6px; background:var(--bg2); color:var(--fg); border:1px solid var(--border); border-radius:4px;">
          <option value="">All Milestones</option>
          ${schema.map((s) => `<option value="${esc(s.key)}" ${filterMilestoneKey === s.key ? "selected" : ""}>${esc(s.label)}</option>`).join("")}
        </select>
        <button data-ticket-new class="btn primary" style="margin-left:auto;">+ Log a Ticket</button>
      </div>
    `;

    const composerHtml = state.tickets.composerOpen ? buildTicketComposerHtml(code, schema) : "";

    const listHtml = tickets === undefined
      ? `<div class="small" style="padding:8px 0; color:var(--muted);">Loading…</div>`
      : view.length === 0
        ? `<div class="small" style="padding:8px 0; color:var(--muted);">${tickets.length ? "No tickets match the current filters." : `No tickets yet for ${esc(code)}.`}</div>`
        : `<div style="display:flex; flex-direction:column; gap:8px;">${view.map((t) => buildTicketRowHtml(t)).join("")}</div>`;

    return `<div style="margin-top:8px; max-width:760px;">${filterBar}${composerHtml}${listHtml}</div>`;
  }

  function buildTicketRowHtml(t) {
    const statusLabel = { open: "Open", in_progress: "In Progress", resolved: "Resolved", closed: "Closed" };
    const statusColor = { open: "#38bdf8", in_progress: "#f59e0b", resolved: "#22c55e", closed: "#64748b" };
    const attachBadge = t.attachments?.length ? `<span title="${t.attachments.length} attachment(s)" style="color:var(--muted); font-size:11px;">📎 ${t.attachments.length}</span>` : "";
    const blockBadge = t.blocksMilestone ? `<span style="background:#ef444422; color:#ef4444; font-size:10px; padding:1px 6px; border-radius:3px;">⛔ blocks milestone</span>` : "";
    const reporter = t.reportedBy?.source === "portal"
      ? `Client Portal${t.reportedBy?.name ? " — " + esc(t.reportedBy.name) : ""}`
      : esc(t.reportedBy?.email || t.reportedBy?.name || "AOS");
    return `
      <div style="border:1px solid var(--border); border-radius:8px; padding:10px 12px; background:var(--bg2);">
        <div style="display:flex; justify-content:space-between; gap:8px; align-items:flex-start;">
          <div style="font-weight:600; font-size:13px;">${esc(t.title)}</div>
          <span style="background:${statusColor[t.status]}22; color:${statusColor[t.status]}; font-size:10px; padding:2px 7px; border-radius:4px; white-space:nowrap;">${statusLabel[t.status] || t.status}</span>
        </div>
        <div class="small" style="color:var(--muted); margin:4px 0; display:flex; gap:8px; flex-wrap:wrap; align-items:center;">
          <span>${esc(new Date(t.createdAt).toLocaleString())}</span>
          <span>·</span><span>${reporter}</span>
          ${t.milestoneLabelSnapshot ? `<span>·</span><span>${esc(t.milestoneLabelSnapshot)}</span>` : ""}
          ${attachBadge} ${blockBadge}
        </div>
        ${t.description ? `<div style="white-space:pre-wrap; font-size:12px; margin:6px 0;">${esc(t.description)}</div>` : ""}
        ${t.attachments?.length ? `<div style="display:flex; gap:6px; flex-wrap:wrap; margin:6px 0;">${t.attachments.map((a) => ticketAttachmentThumbHtml(t.projectCode || "", a)).join("")}</div>` : ""}
        <div style="display:flex; gap:6px; margin-top:8px;">
          <select data-ticket-status="${esc(t.id)}" style="font-size:11px; padding:2px 5px; background:var(--surface); color:var(--fg); border:1px solid var(--border); border-radius:4px;">
            ${TICKET_STATUS_ORDER.map((s) => `<option value="${s}" ${t.status === s ? "selected" : ""}>${statusLabel[s]}</option>`).join("")}
          </select>
        </div>
        ${buildTicketNotesHtml(t)}
      </div>
    `;
  }

  // Unbounded, append-only note log on a ticket (mirrors buildNotesHtml's project-level
  // notes) -- shows who wrote it and when, oldest first so the thread reads top-to-bottom.
  function buildTicketNotesHtml(t) {
    const notes = Array.isArray(t.notes) ? t.notes : [];
    const posting = !!state.tickets.notePosting[t.id];
    const draft = String(state.tickets.noteDrafts[t.id] || "");
    const notesHtml = notes.map((n) => `
      <div style="padding:6px 0; border-bottom:1px solid rgba(148,163,184,0.15);">
        <div class="small" style="color:var(--muted); display:flex; gap:8px; margin-bottom:2px;">
          <span>${esc(new Date(n.createdAt).toLocaleString())}</span>
          <span>·</span>
          <span>${n.source === "portal" ? `Client Portal${n.authorName ? " — " + esc(n.authorName) : ""}` : esc(n.authorEmail || n.authorName || "AOS")}</span>
        </div>
        <div style="white-space:pre-wrap; font-size:12px;">${esc(n.text)}</div>
      </div>
    `).join("");
    return `
      <div style="margin-top:10px; padding-top:8px; border-top:1px solid var(--border);">
        <div class="small" style="color:var(--muted); font-weight:600; margin-bottom:4px;">Notes${notes.length ? ` (${notes.length})` : ""}</div>
        ${notesHtml || `<div class="small" style="color:var(--muted); padding:2px 0 6px;">No notes yet.</div>`}
        <div style="display:flex; gap:6px; margin-top:8px; align-items:flex-start;">
          <textarea data-ticket-note-draft="${esc(t.id)}" rows="2" placeholder="Add a note…"
            style="flex:1; resize:vertical; padding:6px 7px; background:var(--bg2); border:1px solid var(--border); border-radius:6px; color:var(--fg); font:inherit; font-size:12px; box-sizing:border-box;">${esc(draft)}</textarea>
          <button data-ticket-note-add="${esc(t.id)}" class="btn" style="font-size:11px; padding:5px 9px;" ${posting ? "disabled" : ""}>${posting ? "Adding…" : "Add"}</button>
        </div>
      </div>
    `;
  }

  function ticketAttachmentThumbHtml(code, a) {
    const url = `/api/project/tickets/attachment/view?code=${encodeURIComponent(code)}&key=${encodeURIComponent(a.key)}`;
    if (a.kind === "video") {
      return `<a href="${esc(url)}" target="_blank" rel="noopener" class="small" style="color:var(--accent, #38bdf8);">▶ ${esc(a.filename || "recording.webm")}</a>`;
    }
    return `<a href="${esc(url)}" target="_blank" rel="noopener"><img src="${esc(url)}" alt="${esc(a.filename || "attachment")}" style="width:64px; height:64px; object-fit:cover; border-radius:4px; border:1px solid var(--border);" /></a>`;
  }

  function buildTicketComposerHtml(code, schema) {
    const draft = state.tickets.composerDraft;
    const posting = !!state.tickets.posting;
    const uploading = !!state.tickets.attachmentUploading;
    const recording = !!ticketRecorder;
    const pending = ticketPendingRecording;
    const attachmentsHtml = (draft.attachments || []).map((a, i) => `
      <span style="display:inline-flex; align-items:center; gap:4px; background:var(--surface); border:1px solid var(--border); border-radius:4px; padding:2px 6px; font-size:11px;">
        ${a.kind === "video" ? "🎬" : "🖼"} ${esc(a.filename || a.key.split("/").pop())}
        <button type="button" data-ticket-attach-remove="${i}" class="btn" style="padding:0 4px; font-size:10px;">✕</button>
      </span>
    `).join(" ");
    const recordingControls = recording
      ? `<span class="small" style="color:#ef4444;">🔴 Recording… <span data-ticket-recording-elapsed>0:00</span></span>
         <button type="button" data-ticket-record-stop class="btn">Stop</button>`
      : pending
        ? `<video src="${esc(pending.blobUrl)}" controls style="max-width:220px; max-height:120px; display:block; margin-bottom:6px; border-radius:4px;"></video>
           <button type="button" data-ticket-record-attach class="btn primary">Attach Recording</button>
           <button type="button" data-ticket-record-discard class="btn">Discard</button>`
        : `<button type="button" data-ticket-screenshot class="btn" ${uploading ? "disabled" : ""}>📷 Take Screenshot</button>
           <button type="button" data-ticket-record-start class="btn" ${uploading ? "disabled" : ""}>🔴 Record Screen</button>`;
    return `
      <div style="border:1px solid var(--border); border-radius:8px; padding:14px; margin-bottom:14px; background:var(--bg2);">
        <input type="text" data-ticket-field="title" value="${esc(draft.title)}" placeholder="Ticket title"
          style="width:100%; box-sizing:border-box; padding:7px 8px; margin-bottom:8px; background:var(--surface); border:1px solid var(--border); border-radius:6px; color:var(--fg); font:inherit;" />
        <textarea data-ticket-field="description" rows="3" placeholder="Describe the issue…"
          style="width:100%; box-sizing:border-box; resize:vertical; padding:7px 8px; margin-bottom:8px; background:var(--surface); border:1px solid var(--border); border-radius:6px; color:var(--fg); font:inherit;">${esc(draft.description)}</textarea>
        <div style="display:flex; gap:12px; align-items:center; flex-wrap:wrap; margin-bottom:10px;">
          <select data-ticket-field="milestoneKey" style="font-size:12px; padding:5px 7px; background:var(--surface); color:var(--fg); border:1px solid var(--border); border-radius:6px;">
            <option value="">— No milestone —</option>
            ${schema.map((s) => `<option value="${esc(s.key)}" ${draft.milestoneKey === s.key ? "selected" : ""}>${esc(s.label)}</option>`).join("")}
          </select>
          <label class="small" style="display:flex; align-items:center; gap:5px; ${draft.milestoneKey ? "" : "opacity:0.5;"}">
            <input type="checkbox" data-ticket-field="blocksMilestone" ${draft.blocksMilestone ? "checked" : ""} ${draft.milestoneKey ? "" : "disabled"} />
            Blocks this milestone
          </label>
        </div>
        <div style="margin-bottom:10px;">
          <div class="small" style="color:var(--muted); margin-bottom:6px;">Attachments (screenshot, GIF, or video)</div>
          <div style="display:flex; gap:8px; align-items:center; flex-wrap:wrap; margin-bottom:8px;">
            <button type="button" data-ticket-attach-pick class="btn">📎 Add File</button>
            <input type="file" data-ticket-file-input accept="${TICKET_ATTACHMENT_ACCEPT}" style="display:none;" />
            <span class="small" style="color:var(--muted);">or paste a screenshot (Ctrl/Cmd+V) anywhere in this box</span>
          </div>
          <div data-ticket-paste-zone tabindex="0" style="border:1px dashed var(--border); border-radius:6px; padding:10px; min-height:44px;">
            ${recordingControls}
            ${uploading ? `<div class="small" style="color:var(--muted); margin-top:6px;">Uploading…</div>` : ""}
            ${attachmentsHtml ? `<div style="margin-top:8px; display:flex; gap:6px; flex-wrap:wrap;">${attachmentsHtml}</div>` : ""}
          </div>
        </div>
        <div style="display:flex; gap:8px;">
          <button type="button" data-ticket-submit class="btn primary" ${posting ? "disabled" : ""}>${posting ? "Logging…" : "Create Ticket"}</button>
          <button type="button" data-ticket-cancel class="btn">Cancel</button>
        </div>
      </div>
    `;
  }

  function buildDigitalAssetManagerHtml() {
    if ("dam" in _htmlCache) return _htmlCache.dam;
    return (_htmlCache.dam = _buildDigitalAssetManagerHtml());
  }
  function _buildDigitalAssetManagerHtml() {
    const ASSET_CATS = [
      { key: "360-dock-photos", label: "360 Dock Photos", accept: ".jpg,.jpeg,.36p,image/jpeg" },
      { key: "las-files", label: "Point Files", accept: ".las,.laz,.xyz,.txt,.csv,.pts,.bag,.e57,.asc" },
      { key: "geotiff-files", label: "GeoTIFF Files", accept: ".tif,.tiff,image/tiff" },
      { key: "vector-files", label: "Spatial Layers", accept: ".geojson,.json,.kml,.kmz,.gpx,.dxf,.zip,.shp,.dbf,.prj,.cpg" },
      { key: "surfaces", label: "Processed Surfaces", accept: ".tif,.tiff,.xyz,.asc" },
      { key: "source-data", label: "Source Data", accept: "" },
      { key: "gnss-raw", label: "GNSS Raw", accept: "" },
      { key: "project-files", label: "Project Documents", accept: ".pdf,.png,.jpg,.jpeg,.gif,.csv,.xlsx,.json,application/pdf" },
      { key: "tilesets", label: "Tile Pyramids", accept: "" },
      { key: "deliverables", label: "Deliverables", accept: ".pdf,.docx,.xlsx,.pptx,.png,.jpg,.jpeg,.zip,.kmz,.kml" },
      { key: "uploaded-from-client", label: "Upload", accept: "" },
    ];
    const selectedCode = String(state.projectSettings.selectedProjectId || "");
    if (!selectedCode) return `<div class="small" style="margin-top:8px; color:var(--muted);">Select an active project in Project Manager to manage its digital assets.</div>`;
    const _catData = state.digitalAsset.assetsByCategory[selectedCode];
    if (!_catData && !damLoadInFlight.has(selectedCode)) {
      setTimeout(() => loadDamAssetsForProject(selectedCode), 0);
    }
    const RAMP_CSS = {
      viridis:  "linear-gradient(to right,#440154,#414487,#2a788e,#22a884,#7ad151,#fde725)",
      plasma:   "linear-gradient(to right,#0d0887,#6a00a8,#b12a90,#e16462,#fca636,#f0f921)",
      inferno:  "linear-gradient(to right,#000004,#420a68,#932667,#dd513a,#fca50a,#fcffa4)",
      magma:    "linear-gradient(to right,#000004,#3b0f70,#8c2981,#de4968,#fe9f6d,#fcfdbf)",
      turbo:    "linear-gradient(to right,#30123b,#4485f4,#1de4b5,#a8f252,#fbb328,#cb4679,#7a0403)",
      jet:      "linear-gradient(to right,#000080,#0000ff,#00ffff,#00ff00,#ffff00,#ff0000,#800000)",
      gray:     "linear-gradient(to right,#000000,#ffffff)",
      rdylgn:   "linear-gradient(to right,#d73027,#fdae61,#ffffbf,#a6d96a,#1a9641)",
      spectral: "linear-gradient(to right,#9e0142,#f46d43,#ffffbf,#66c2a5,#5e4fa2)",
    };
    const fmtElev = (v) => v == null ? "" : Number(v).toFixed(2);
    const fmtPtsLocal = (n) => n >= 1e6 ? (n / 1e6).toFixed(1) + "M" : n >= 1e3 ? (n / 1e3).toFixed(0) + "K" : String(n);
    const showAllCategories = !!state.digitalAsset.showAllCategories;
    const categoryModels = ASSET_CATS.map((cat) => {
      const catKey = `${selectedCode}::${cat.key}`;
      const files = (state.digitalAsset.assetsByCategory[selectedCode] || {})[cat.key] || null;
      const upState = state.digitalAsset.uploadingByCategory[catKey];
      const uploading = !!upState;
      const numFiles = Array.isArray(files) ? files.length : 0;
      return { cat, catKey, files, upState, uploading, numFiles };
    });
    const visibleCategoryModels = categoryModels.filter((model) =>
      showAllCategories ||
      model.files === null ||
      model.uploading ||
      model.numFiles > 0
    );
    const hiddenEmptyCount = categoryModels.length - visibleCategoryModels.length;
    const categoryToggle = `
      <label class="small" style="display:inline-flex; align-items:center; gap:6px; color:var(--muted); user-select:none; margin-top:2px;">
        <input data-asset-show-all-categories="1" type="checkbox" ${showAllCategories ? "checked" : ""} style="accent-color:#38bdf8;" />
        Show all categories${hiddenEmptyCount > 0 && !showAllCategories ? ` (${hiddenEmptyCount} hidden)` : ""}
      </label>
    `;
    const treeRows = visibleCategoryModels.map((model, catIdx) => {
      const { cat, catKey, files, uploading, numFiles } = model;
      const collapsed = state.digitalAsset.collapsedCategories[catKey] !== false;
      const sel = state.digitalAsset.selectedFiles[catKey] || {};
      const numSel = Array.isArray(files) ? files.filter((f) => sel[f.key]).length : 0;
      const allSel = numFiles > 0 && numSel === numFiles;
      const topBorder = catIdx > 0 ? "border-top:1px solid rgba(148,163,184,0.1);" : "";
      // Each file picked for this upload gets its own placeholder row + progress bar the
      // instant the batch starts (not one combined bar for the whole batch), so a 50-file
      // folder upload shows which files are done, in flight, or still queued.
      const uploadingFiles = uploading ? (state.digitalAsset.uploadingFilesByCategory[catKey] || []) : [];
      const progressBar = uploadingFiles.length ? `
        <div style="margin:3px 8px 4px; display:flex; flex-direction:column; gap:3px;">
          ${uploadingFiles.map((uf) => {
            const ufKey = `${catKey}::${uf.key}`;
            const ufPct = uf.status === "done" ? 100 : uf.size > 0 ? Math.round((uf.uploadedBytes / uf.size) * 100) : 0;
            const statusLabel = uf.status === "done" ? "Done"
              : uf.status === "failed" ? "Failed"
              : uf.label || (uf.status === "queued" ? "Queued…" : `${damFormatBytes(uf.uploadedBytes)} / ${damFormatBytes(uf.size)}`);
            const barColor = uf.status === "failed" ? "#f87171" : uf.status === "done" ? "#22c55e" : "linear-gradient(90deg,#38bdf8,#22c55e)";
            return `<div style="display:flex; align-items:center; gap:6px;">
              <span class="small" style="flex:1; min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; font-size:10px; color:var(--muted);" title="${esc(uf.name)}">${esc(uf.name)}</span>
              <div style="width:72px; height:3px; border-radius:2px; background:rgba(148,163,184,0.15); overflow:hidden; flex-shrink:0;">
                <div data-progress-bar="${esc(ufKey)}" style="height:100%; border-radius:2px; background:${barColor}; width:${ufPct}%; transition:width 0.15s ease;"></div>
              </div>
              <span data-progress-label="${esc(ufKey)}" class="small" style="font-size:9px; color:var(--muted); min-width:70px; text-align:right; flex-shrink:0;">${esc(statusLabel)}</span>
            </div>`;
          }).join("")}
        </div>
      ` : "";
      const countBadge = numFiles ? `<span style="font-size:9px; color:var(--muted); margin-left:3px; font-weight:400;">(${numFiles})</span>` : "";
      if (cat.key === "tilesets") {
        const tilesetList = files === null
          ? `<div class="small" style="padding:4px 0; color:var(--muted);">Loading…</div>`
          : files.length === 0
            ? `<div class="small" style="padding:4px 0; color:var(--muted);">No tile pyramids yet. Generate tiles from the 3D Point Cloud workspace.</div>`
            : files.map((f) => {
                const rampCss = RAMP_CSS[f.colorRamp] || "";
                const rampMin = f.stretchMin ?? f.minElev;
                const rampMax = f.stretchMax ?? f.maxElev;
                const minStr = fmtElev(rampMin);
                const maxStr = fmtElev(rampMax);
                const dataRangeStr = (f.minElev != null && f.maxElev != null && (f.stretchMin != null || f.stretchMax != null))
                  ? `data ${fmtElev(f.minElev)}–${fmtElev(f.maxElev)}` : "";
                const ptStr = f.pointCount ? fmtPtsLocal(f.pointCount) + " pts" : "";
                const resStr = f.pointResolution != null ? Number(f.pointResolution).toFixed(2) + " spacing" : "";
                const dateStr = f.createdAt ? new Date(f.createdAt).toLocaleDateString() : "";
                const rampBar = rampCss && minStr && maxStr
                  ? `<div style="display:flex;align-items:center;gap:4px;flex-shrink:0;">
                      <span style="font-size:9px;color:var(--muted);min-width:32px;text-align:right;">${esc(minStr)}</span>
                      <div style="width:72px;height:7px;border-radius:2px;background:${rampCss};flex-shrink:0;"></div>
                      <span style="font-size:9px;color:var(--muted);min-width:32px;">${esc(maxStr)}</span>
                    </div>` : "";
                const hasCog = f.sourcePath && /\.tiff?$/i.test(f.sourcePath);
                return `<div style="padding:5px 0 6px;border-bottom:1px solid rgba(148,163,184,0.07);">
                  <div style="display:flex;align-items:center;gap:6px;margin-bottom:5px;">
                    <span class="small" style="flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-weight:500;" title="${esc(f.name)}">${esc(f.name)}</span>
                    <button data-tileset-load="${esc(f.jobId)}" data-tileset-code="${esc(selectedCode)}" class="btn" style="font-size:10px;padding:2px 7px;flex-shrink:0;">Load Map</button>
                    ${hasCog ? `
                      <select data-tileset-exp-fmt="${esc(f.jobId)}" style="font-size:9px;background:rgba(15,23,42,0.8);border:1px solid rgba(148,163,184,0.2);border-radius:3px;color:var(--muted);padding:1px 2px;cursor:pointer;flex-shrink:0;">
                        <option value="geotiff">GeoTIFF</option>
                        <option value="asc">ASCII Grid</option>
                        <option value="xyz">XYZ</option>
                      </select>
                      <button data-tileset-exp="${esc(f.jobId)}" data-tileset-exp-path="${esc(f.sourcePath)}" class="btn" style="font-size:10px;padding:2px 7px;flex-shrink:0;" title="Export raster">↓</button>
                    ` : ""}
                    <button data-tileset-delete="${esc(f.jobId)}" data-tileset-code="${esc(selectedCode)}" class="btn" style="font-size:10px;padding:2px 7px;color:#f87171;flex-shrink:0;">✕</button>
                  </div>
                  <div style="display:flex;align-items:center;gap:10px;flex-wrap:wrap;">
                    ${rampBar}
                    ${dataRangeStr ? `<span style="font-size:9px;color:var(--muted);opacity:.6;" title="Actual data range">${esc(dataRangeStr)}</span>` : ""}
                    ${ptStr ? `<span style="font-size:10px;color:var(--muted);">${esc(ptStr)}</span>` : ""}
                    ${resStr ? `<span style="font-size:10px;color:var(--muted);">${esc(resStr)}</span>` : ""}
                    ${dateStr ? `<span style="font-size:10px;color:var(--muted);margin-left:auto;">${esc(dateStr)}</span>` : ""}
                  </div>
                </div>`;
              }).join("");
        return `<div style="${topBorder}">
          <button data-asset-collapse="${esc(catKey)}" style="width:100%; padding:4px 10px; background:rgba(2,6,23,0.4); border:none; display:flex; align-items:center; gap:5px; cursor:pointer; text-align:left;">
            <span style="font-size:9px; color:var(--muted); opacity:0.7;">${collapsed ? "▸" : "▾"}</span>
            <span style="font-size:10px; font-weight:600; color:var(--muted); text-transform:uppercase; letter-spacing:0.06em; flex:1;">${esc(cat.label)}${countBadge}</span>
          </button>
          ${collapsed ? "" : `<div style="padding:4px 10px 6px;">${tilesetList}</div>`}
        </div>`;
      }
      if (cat.key === "deliverables") {
        const delivDownloadUrl = (code, folder, subfolder, filename) =>
          `/api/project/deliverables/download?${new URLSearchParams({ code, folder, subfolder, file: filename }).toString()}`;
        const delivZipUrl = (code, folder) =>
          `/api/project/deliverables/zip?${new URLSearchParams({ code, folder }).toString()}`;
        const manifest = (state.digitalAsset.deliveryManifest || {})[selectedCode];
        // Completeness checklist for the 5 standard required deliverable kinds -- the
        // manifest itself has no notion of "required", so this is purely a client-side
        // read: which kinds does at least one current entry carry?
        const checklistSection = collapsed || !Array.isArray(manifest) ? "" : (() => {
          const presentKinds = new Set(manifest.map((e) => e.kind).filter(Boolean));
          const items = deliverableKinds().map((kind) => {
            const present = presentKinds.has(kind);
            const color = present ? "#4ade80" : "#f87171";
            return `<span style="font-size:9px;padding:2px 6px;border-radius:3px;background:${present ? "rgba(34,197,94,0.12)" : "rgba(248,113,113,0.1)"};color:${color};border:1px solid ${present ? "rgba(34,197,94,0.3)" : "rgba(248,113,113,0.25)"};white-space:nowrap;">${present ? "✓" : "✗"} ${esc(DELIVERABLE_KIND_LABELS[kind] || kind)}</span>`;
          }).join("");
          const readyCount = deliverableKinds().filter((k) => presentKinds.has(k)).length;
          return `<div style="display:flex;align-items:center;gap:5px;flex-wrap:wrap;padding:4px 0 8px;border-bottom:1px solid rgba(148,163,184,0.1);margin-bottom:6px;">
            <span style="font-size:9px;color:var(--muted);margin-right:2px;">${readyCount}/${deliverableKinds().length} required:</span>
            ${items}
          </div>`;
        })();
        const tokenInfo = (state.digitalAsset.deliveryTokens || {})[selectedCode];
        const tokenLoaded = selectedCode in (state.digitalAsset.deliveryTokens || {});
        if (!tokenLoaded) setTimeout(() => loadDeliveryData(selectedCode).catch(() => {}), 0);
        const deliveryUrl = tokenInfo?.token ? `${window.location.origin}/delivery/${tokenInfo.token}` : "";
        const tokenSection = collapsed ? "" : (() => {
          if (!tokenLoaded) return `<div class="small" style="color:var(--muted);padding:4px 0;">Loading…</div>`;
          if (!tokenInfo) return `
            <div style="display:flex;align-items:center;gap:8px;padding:4px 0 6px;border-bottom:1px solid rgba(148,163,184,0.1);margin-bottom:6px;">
              <span class="small" style="color:var(--muted);flex:1;">No share link yet</span>
              <button data-deliv-gen-token="${esc(selectedCode)}" class="btn" style="font-size:9px;padding:2px 8px;">Generate Link</button>
            </div>`;
          return `
            <div style="display:flex;align-items:center;gap:6px;padding:4px 0 6px;border-bottom:1px solid rgba(148,163,184,0.1);margin-bottom:6px;flex-wrap:wrap;">
              <input readonly value="${esc(deliveryUrl)}" style="flex:1;min-width:0;font-size:9px;background:rgba(2,6,23,0.5);border:1px solid rgba(148,163,184,0.2);border-radius:3px;padding:2px 5px;color:#38bdf8;cursor:text;" />
              <button data-deliv-copy-link="${esc(deliveryUrl)}" class="btn" style="font-size:9px;padding:2px 7px;flex-shrink:0;" title="Copy link">Copy</button>
              <button data-deliv-gen-token="${esc(selectedCode)}" class="btn" style="font-size:9px;padding:2px 7px;flex-shrink:0;" title="Regenerate link">↺</button>
              <button data-deliv-revoke-token="${esc(selectedCode)}" class="btn" style="font-size:9px;padding:2px 7px;color:#f87171;border-color:#f8717155;flex-shrink:0;" title="Revoke link">Revoke</button>
            </div>`;
        })();
        const folderMap = {};
        if (Array.isArray(manifest)) {
          for (const e of manifest) {
            if (!folderMap[e.folder]) folderMap[e.folder] = [];
            folderMap[e.folder].push(e);
          }
        }
        const folderNames = Object.keys(folderMap).sort();
        const delivBody = collapsed ? "" : (() => {
          if (!manifest) return `<div class="small" style="color:var(--muted);padding:4px 0;">Loading…</div>`;
          if (!folderNames.length) return `<div class="small" style="color:var(--muted);padding:4px 0 2px;">No deliverables yet. Use the → button on files in other categories to send them here.</div>`;
          return folderNames.map(fname => {
            const fKey = `${catKey}::${fname}`;
            const fCollapsed = (state.digitalAsset.collapsedFolders || {})[fKey] !== false;
            const fFiles = folderMap[fname] || [];
            // Group by subfolder within this folder
            const subMap = {};
            for (const e of fFiles) {
              const sub = e.subfolder || "";
              if (!subMap[sub]) subMap[sub] = [];
              subMap[sub].push(e);
            }
            const fileRows = fCollapsed ? "" : Object.keys(subMap).sort().map(sub => {
              const subLabel = sub ? `<div style="font-size:9px;color:var(--muted);padding:5px 0 2px;font-weight:600;text-transform:uppercase;letter-spacing:0.05em;">${esc(sub)}</div>` : "";
              const rows = subMap[sub].map(e => {
                const sz = e.size ? `${(e.size / 1048576).toFixed(1)} MB` : "";
                const kindBadge = e.kind
                  ? `<span style="font-size:8px;padding:1px 5px;border-radius:3px;background:rgba(167,139,250,0.12);color:#a78bfa;border:1px solid rgba(167,139,250,0.3);flex-shrink:0;white-space:nowrap;" title="Standard deliverable kind">${esc(DELIVERABLE_KIND_LABELS[e.kind] || e.kind)}</span>`
                  : "";
                return `<div style="display:flex;align-items:center;gap:6px;padding:3px 0;border-bottom:1px solid rgba(148,163,184,0.06);">
                  <span class="small" style="flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;" title="${esc(e.sourceKey)}">${esc(e.filename)}</span>
                  ${kindBadge}
                  <span class="small" style="color:var(--muted);min-width:44px;text-align:right;flex-shrink:0;">${esc(sz)}</span>
                  <a href="${esc(delivDownloadUrl(selectedCode, fname, sub, e.filename))}" class="btn" style="font-size:9px;padding:1px 5px;flex-shrink:0;" title="Download ${esc(e.filename)}">⬇</a>
                  <button data-deliv-remove-link="1" data-deliv-remove-code="${esc(selectedCode)}" data-deliv-remove-folder="${esc(fname)}" data-deliv-remove-subfolder="${esc(sub)}" data-deliv-remove-file="${esc(e.filename)}" class="btn" style="font-size:9px;padding:1px 5px;color:#f87171;border-color:#f8717155;flex-shrink:0;" title="Remove from deliverables">✕</button>
                </div>`;
              }).join("");
              return subLabel + rows;
            }).join("");
            return `<div style="margin:2px 0;border:1px solid rgba(148,163,184,0.13);border-radius:5px;overflow:hidden;">
              <div style="display:flex;align-items:center;background:rgba(2,6,23,0.4);">
                <button data-asset-folder-collapse="${esc(fKey)}" style="display:flex;align-items:center;gap:5px;background:none;border:none;cursor:pointer;padding:4px 8px;flex:1;text-align:left;">
                  <span style="font-size:9px;color:var(--muted);">${fCollapsed ? "▸" : "▾"}</span>
                  <span style="font-size:10px;">📁</span>
                  <span class="small" style="font-weight:600;flex:1;">${esc(fname)}</span>
                  <span style="font-size:9px;color:var(--muted);">${fFiles.length} file${fFiles.length !== 1 ? "s" : ""}</span>
                </button>
                <a href="${esc(delivZipUrl(selectedCode, fname))}" class="btn" style="font-size:9px;padding:2px 6px;margin-right:6px;flex-shrink:0;" title="Download whole folder as .zip">⬇ ZIP</a>
                <button data-deliv-download-all="1" data-deliv-download-all-code="${esc(selectedCode)}" data-deliv-download-all-folder="${esc(fname)}" class="btn" style="font-size:9px;padding:2px 6px;margin-right:6px;flex-shrink:0;" title="Download every file in this folder individually">⬇ All</button>
                <button data-deliv-rename-folder="${esc(fname)}" data-deliv-rename-code="${esc(selectedCode)}" class="btn" style="font-size:9px;padding:2px 6px;margin-right:6px;flex-shrink:0;" title="Rename folder">✎</button>
              </div>
              ${fCollapsed ? "" : `<div style="padding:0 8px 2px;">${fileRows || '<div class="small" style="padding:4px 0;color:var(--muted);">Empty</div>'}</div>`}
            </div>`;
          }).join("");
        })();
        return `<div style="${topBorder}">
          <div style="padding:4px 10px;background:rgba(2,6,23,0.4);display:flex;align-items:center;gap:4px;">
            <button data-asset-collapse="${esc(catKey)}" style="display:flex;align-items:center;gap:5px;background:none;border:none;cursor:pointer;padding:0;flex:1;text-align:left;">
              <span style="font-size:9px;color:var(--muted);opacity:0.7;">${collapsed ? "▸" : "▾"}</span>
              <span style="font-size:10px;font-weight:600;color:var(--muted);text-transform:uppercase;letter-spacing:0.06em;">${esc(cat.label)}${Array.isArray(manifest) && manifest.length ? `<span style="font-size:9px;color:var(--muted);margin-left:3px;font-weight:400;">(${manifest.length})</span>` : ""}</span>
            </button>
            <button data-generate-deliverables="${esc(selectedCode)}" class="btn" style="font-size:9px;padding:2px 8px;white-space:nowrap;flex-shrink:0;color:#a78bfa;" title="Auto-generate and register all 5 standard Safe Harbor deliverables into a dated folder">Generate Deliverables</button>
          </div>
          ${collapsed ? "" : `<div style="padding:4px 10px 6px;">${checklistSection}${tokenSection}${delivBody}</div>`}
        </div>`;
      }
      const isLasCat = cat.key === "las-files";
      const depthInfo = isLasCat ? (state.digitalAsset.depthLayers?.[selectedCode] || null) : null;
      const currentDepthKey = depthInfo?.layer?.s3Key || null;
      const depthBuildRunning = depthInfo?.buildJob?.status === "running";
      const depthBuildError = depthInfo?.buildJob?.status === "error";
      const depthHeader = isLasCat && depthInfo?.layer ? (() => {
        const depthName = String(currentDepthKey || "").split("/").pop();
        const buildMsg = depthBuildRunning && depthInfo.buildJob?.message ? ` — ${esc(depthInfo.buildJob.message)}` : "";
        const buildPct = depthBuildRunning ? (depthInfo.buildJob?.progressPct || 0) : 0;
        const badge = depthInfo.rasterReady
          ? `<span style="font-size:9px;padding:1px 5px;border-radius:3px;background:rgba(34,197,94,0.15);color:#4ade80;border:1px solid rgba(34,197,94,0.3);">raster ready</span>`
          : depthBuildError
            ? `<span style="font-size:9px;padding:1px 5px;border-radius:3px;background:rgba(248,113,113,0.15);color:#f87171;border:1px solid rgba(248,113,113,0.3);" title="${esc(depthInfo.buildJob?.message || "")}">build error — hover for detail</span>`
            : depthBuildRunning
              ? `<span style="font-size:9px;padding:1px 5px;border-radius:3px;background:rgba(251,191,36,0.15);color:#fbbf24;border:1px solid rgba(251,191,36,0.3);">${buildPct}%${buildMsg}</span>`
              : `<span style="font-size:9px;padding:1px 5px;border-radius:3px;background:rgba(248,113,113,0.15);color:#f87171;border:1px solid rgba(248,113,113,0.3);">not built</span>`;
        const rebuildBtn = !depthBuildRunning
          ? `<button data-depth-build="${esc(selectedCode)}" class="btn" style="font-size:9px;padding:1px 6px;flex-shrink:0;">Rebuild</button>` : "";
        return `<div style="padding:4px 0 5px;border-bottom:1px solid rgba(148,163,184,0.1);display:flex;align-items:center;gap:6px;flex-wrap:wrap;margin-bottom:4px;">
          <span style="font-size:10px;color:var(--muted);">Depth layer:</span>
          <span class="small" style="font-weight:500;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;max-width:160px;" title="${esc(currentDepthKey)}">${esc(depthName)}</span>
          <span style="font-size:9px;color:var(--muted);">${esc(depthInfo.layer?.nativeUnit || "ft")}</span>
          ${badge}${rebuildBtn}
        </div>`;
      })() : "";
      const renderFileRow = (f, inFolder = false) => {
        const name = String(f.key || "").split("/").pop();
        const size = f.size != null ? `${(Number(f.size) / 1024 / 1024).toFixed(2)} MB` : "";
        const checked = !!sel[f.key];
        const isLasLaz = isLasCat && /\.(las|laz)$/i.test(name);
        const isCurrentDepth = isLasLaz && f.key === currentDepthKey;
        const depthBadge = isCurrentDepth
          ? `<span style="font-size:9px;padding:1px 4px;border-radius:3px;background:rgba(56,189,248,0.15);color:#38bdf8;border:1px solid rgba(56,189,248,0.3);flex-shrink:0;">depth</span>` : "";
        const setDepthBtn = isLasLaz
          ? `<button data-asset-set-depth="${esc(f.key)}" data-asset-set-depth-code="${esc(selectedCode)}" class="btn" style="font-size:9px;padding:1px 5px;flex-shrink:0;" title="Set as depth layer">depth</button>` : "";
        const isVectorCat = cat.key === "vector-files";
        const onMap = isVectorCat && !!((state.digitalAsset.spatialLayersOnMap || {})[f.key]);
        const addToMapBtn = isVectorCat
          ? `<button data-dam-spatial-toggle="${esc(f.key)}" class="btn" style="font-size:10px;padding:2px 7px;flex-shrink:0;${onMap ? "background:rgba(56,189,248,0.18);color:#38bdf8;border-color:rgba(56,189,248,0.45);" : ""}" title="${onMap ? "Remove from map" : "Add to map"}">${onMap ? "⊗" : "⊕"} Map</button>`
          : "";
        const isGeotiffCat = cat.key === "geotiff-files" && /\.tiff?$/i.test(name);
        const geotiffOnMap = isGeotiffCat && !!((state.digitalAsset.spatialLayersOnMap || {})[f.key]);
        const geotiffLoading = isGeotiffCat && !!((state.digitalAsset.geotiffLoadingKeys || {})[f.key]);
        const geotiffProgress = isGeotiffCat ? (state.digitalAsset.geotiffLoadingProgress || {})[f.key] : null;
        const GEOTIFF_STEP_LABEL = { warping: "Reprojecting", "building-cog": "Building tiles", uploading: "Uploading" };
        const geotiffLoadingLabel = geotiffProgress
          ? `${GEOTIFF_STEP_LABEL[geotiffProgress.step] || "Working"}${Number.isFinite(geotiffProgress.pct) ? ` ${geotiffProgress.pct}%` : "…"}`
          : "…";
        const geotiffMapBtn = isGeotiffCat
          ? `<button data-dam-geotiff-toggle="${esc(f.key)}" data-dam-geotiff-code="${esc(selectedCode)}" class="btn" style="font-size:10px;padding:2px 7px;flex-shrink:0;${geotiffOnMap ? "background:rgba(56,189,248,0.18);color:#38bdf8;border-color:rgba(56,189,248,0.45);" : ""}"${geotiffLoading ? " disabled" : ""} title="${geotiffOnMap ? "Remove from map" : "Load on map (converts to web tiles)"}">${geotiffLoading ? esc(geotiffLoadingLabel) : geotiffOnMap ? "⊗ Map" : "⊕ Map"}</button>`
          : "";
        const footprintOnMap = isLasLaz && !!((state.digitalAsset.spatialLayersOnMap || {})[f.key]);
        const footprintLoading = isLasLaz && !!((state.digitalAsset.lasFootprintLoadingKeys || {})[f.key]);
        const footprintMapBtn = isLasLaz
          ? `<button data-dam-las-footprint-toggle="${esc(f.key)}" data-dam-las-footprint-code="${esc(selectedCode)}" class="btn" style="font-size:10px;padding:2px 7px;flex-shrink:0;${footprintOnMap ? "background:rgba(56,189,248,0.18);color:#38bdf8;border-color:rgba(56,189,248,0.45);" : ""}"${footprintLoading ? " disabled" : ""} title="${footprintOnMap ? "Remove footprint from map" : "Show this file's bounding box on the map"}">${footprintLoading ? "…" : footprintOnMap ? "⊗ Footprint" : "⊕ Footprint"}</button>`
          : "";
        const dragAttrs = inFolder ? "" : ` draggable="true" data-dam-drag-key="${esc(f.key)}" data-dam-drag-cat="${esc(catKey)}"`;
        return `<div class="row"${dragAttrs} style="align-items:center; gap:6px; padding:4px 0; border-bottom:1px solid rgba(148,163,184,0.07); cursor:${inFolder ? "default" : "grab"};">
          ${inFolder ? "" : `<span style="font-size:9px;color:var(--muted);opacity:0.4;flex-shrink:0;line-height:1;" title="Drag into a folder">⠿</span>`}
          <input type="checkbox" data-asset-cb="${esc(catKey)}" data-asset-cb-key="${esc(f.key)}"${checked ? " checked" : ""} style="flex-shrink:0; accent-color:#38bdf8; cursor:pointer;" />
          <span class="small" style="flex:1; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;" title="${esc(f.key)}">${esc(name)}</span>
          ${isLasLaz ? lasCrsBadgeHtml(selectedCode, f.key) : ""}
          ${depthBadge}
          <span class="small" style="color:var(--muted); min-width:54px; text-align:right; flex-shrink:0;">${esc(size)}</span>
          ${setDepthBtn}
          ${addToMapBtn}
          ${geotiffMapBtn}
          ${footprintMapBtn}
          <button data-send-to-deliv="${esc(f.key)}" data-send-to-deliv-name="${esc(name)}" data-send-to-deliv-size="${f.size || 0}" data-send-to-deliv-cat="${esc(cat.key)}" class="btn" style="font-size:10px; padding:2px 6px; flex-shrink:0; color:#a78bfa;" title="Send to Deliverables">→</button>
          <button data-asset-download="${esc(f.key)}" class="btn" style="font-size:10px; padding:2px 7px; flex-shrink:0;">↓</button>
          <button data-asset-delete="${esc(f.key)}" class="btn" style="font-size:10px; padding:2px 7px; color:#f87171; flex-shrink:0;">✕</button>
        </div>`;
      };
      const catFolders = (state.digitalAsset.foldersByCategory || {})[catKey] || [];
      // Folders render as a real tree: every directory level (from .keep markers and from
      // the paths of files inside it) gets its own row, so each subfolder is its own drop
      // target for uploads and moves, not just the top-level folder.
      const allFolderPaths = (() => {
        const set = new Set();
        const addWithParents = (p) => {
          const segs = String(p || "").split("/").filter(Boolean);
          for (let i = 1; i <= segs.length; i++) set.add(segs.slice(0, i).join("/"));
        };
        catFolders.forEach(addWithParents);
        if (Array.isArray(files)) files.forEach((f) => { const n = String(f.name || ""); if (n.includes("/")) addWithParents(n.slice(0, n.lastIndexOf("/"))); });
        return [...set].sort();
      })();
      const buildFolderNode = (folderPath, depth) => {
        const folderStateKey = `${catKey}::${folderPath}`;
        const folderCollapsed = (state.digitalAsset.collapsedFolders || {})[folderStateKey] !== false;
        const folderLabel = folderPath.split("/").pop();
        const prefix = `${folderPath}/`;
        const folderFiles = Array.isArray(files) ? files.filter(f => String(f.name || "").startsWith(prefix)) : [];
        const directFiles = folderFiles.filter(f => !String(f.name || "").slice(prefix.length).includes("/"));
        const childFolders = allFolderPaths.filter(p => p.startsWith(prefix) && !p.slice(prefix.length).includes("/"));
        const body = folderCollapsed ? "" : childFolders.map(c => buildFolderNode(c, depth + 1)).join("") + directFiles.map(f => renderFileRow(f, true)).join("");
        // "Build Mosaic": per-ping MinSAS/sonar GeoTIFFs land directly in a leaf folder like this
        // one, already georeferenced (own rotation per tile) by the sonar processing software --
        // offered on any source-data folder with at least one qualifying tile, not just ones named
        // "Minsas", since the same shape of data can come from other sonar systems later.
        const mosaicSourceCount = cat.key === "source-data"
          ? directFiles.filter(f => /\.tif$/i.test(f.name || "") && !/\.Bathy\.tif$/i.test(f.name || "")).length
          : 0;
        return `<div data-dam-folder-drop="${esc(folderPath)}" data-dam-folder-drop-cat="${esc(catKey)}"
          style="margin:0; border:1px solid rgba(148,163,184,0.15); border-radius:3px; overflow:hidden;">
          <div style="display:flex; align-items:center; background:rgba(2,6,23,${depth ? "0.3" : "0.5"});">
            <button data-asset-folder-collapse="${esc(folderStateKey)}" style="display:flex; align-items:center; gap:5px; flex:1; min-width:0; background:none; border:none; cursor:pointer; padding:3px 8px; text-align:left;" title="${esc(folderPath)} — drop files or folders here to upload into it">
              <span style="font-size:9px; color:var(--muted);">${folderCollapsed ? "▸" : "▾"}</span>
              <span style="font-size:10px;">📁</span>
              <span class="small" style="font-weight:600; flex:1; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;">${esc(folderLabel)}</span>
              <span style="font-size:9px; color:var(--muted);">${folderFiles.length} file${folderFiles.length !== 1 ? "s" : ""}</span>
            </button>
            ${mosaicSourceCount > 0 ? `<button data-asset-build-mosaic="${esc(catKey)}" data-asset-build-mosaic-folder="${esc(folderPath)}" class="btn" style="font-size:9px; padding:1px 6px; margin-right:6px; flex-shrink:0;" title="Merge ${mosaicSourceCount} per-ping GeoTIFFs in this folder into one mosaic (sent to GeoTIFF Files)">Build Mosaic (${mosaicSourceCount})</button>` : ""}
            ${isAdminUser() ? `<button data-asset-delete-folder="${esc(catKey)}" data-asset-delete-folder-name="${esc(folderPath)}" data-asset-delete-folder-count="${folderFiles.length}" class="btn" style="font-size:9px; padding:1px 6px; margin-right:6px; color:#f87171; border-color:#f8717155; flex-shrink:0;" title="Delete this folder and everything in it, including subfolders (admin only)">Delete folder</button>` : ""}
          </div>
          ${folderCollapsed ? "" : `<div style="padding:0 8px 2px;">${body || `<div class="small" style="padding:4px 0; color:var(--muted); text-align:center;">Drop files here</div>`}</div>`}
        </div>`;
      };
      const buildFolderRows = () => allFolderPaths.filter(p => !p.includes("/")).map(p => buildFolderNode(p, 0)).join("");
      const rootFiles = Array.isArray(files) ? files.filter(f => !String(f.name || "").includes("/")) : [];
      const fileList = collapsed ? "" : files === null
        ? `<div class="small" style="padding:4px 0; color:var(--muted);">Loading…</div>`
        : (files.length === 0 && !catFolders.length)
          ? `<div class="small" style="padding:3px 0; color:var(--muted);">No files yet.</div>`
          : depthHeader + buildFolderRows() + rootFiles.map(f => renderFileRow(f)).join("");
      return `<div data-asset-drop-zone="${esc(catKey)}" data-asset-drop-code="${esc(selectedCode)}" data-asset-drop-cat="${esc(cat.key)}" style="${topBorder} position:relative; transition:outline 0.1s;">
        <div style="padding:4px 10px; background:rgba(2,6,23,0.4); display:flex; align-items:center; gap:4px; flex-wrap:wrap;">
          <button data-asset-collapse="${esc(catKey)}" style="display:flex; align-items:center; gap:5px; background:none; border:none; cursor:pointer; padding:0; flex:1; min-width:0; text-align:left;">
            <span style="font-size:9px; color:var(--muted); opacity:0.7;">${collapsed ? "▸" : "▾"}</span>
            <span style="font-size:10px; font-weight:600; color:var(--muted); text-transform:uppercase; letter-spacing:0.06em;">${esc(cat.label)}${countBadge}</span>
          </button>
          ${!collapsed && numFiles > 1 ? `<button data-asset-check-all="${esc(catKey)}" class="btn" style="font-size:9px; padding:1px 5px; white-space:nowrap; flex-shrink:0;">${allSel ? "Desel." : "All"}</button>` : ""}
          ${!collapsed && numSel ? `<button data-asset-remove-selected="${esc(catKey)}" class="btn" style="font-size:9px; padding:1px 5px; color:#f87171; border-color:#f8717155; white-space:nowrap; flex-shrink:0;">Delete ${numSel}</button>` : ""}
          ${!collapsed && cat.key === "las-files" && numFiles ? `<button data-asset-open-space="${esc(selectedCode)}" class="btn" style="font-size:9px; padding:1px 6px; white-space:nowrap; flex-shrink:0;" title="Open every LAS/LAZ in this project as layers in one 3D space">3D space ↗</button>` : ""}
          ${!collapsed && cat.key === "las-files" && numSel >= 2 ? `<button data-asset-combine-las="${esc(catKey)}" class="btn" style="font-size:9px; padding:1px 5px; white-space:nowrap; flex-shrink:0;" title="Merge the selected LAS files into one new file (e.g. folding a rework dock into the marina LAS)">Combine ${numSel}</button>` : ""}
          ${cat.key !== "tilesets" ? `<button data-asset-mkdir="${esc(catKey)}" class="btn" style="font-size:9px; padding:1px 6px; white-space:nowrap; flex-shrink:0;" title="Create folder">📁+</button>` : ""}
          <button data-asset-upload="1" data-asset-upload-code="${esc(selectedCode)}" data-asset-upload-cat="${esc(cat.key)}" class="btn" style="font-size:9px; padding:1px 6px; white-space:nowrap; flex-shrink:0;"${uploading ? " disabled" : ""}>${uploading ? "…" : "+ Upload"}</button>
          ${cat.key !== "tilesets" ? `<button data-asset-upload-folder="1" data-asset-upload-code="${esc(selectedCode)}" data-asset-upload-cat="${esc(cat.key)}" class="btn" style="font-size:9px; padding:1px 6px; white-space:nowrap; flex-shrink:0;" title="Upload an entire folder, preserving its structure"${uploading ? " disabled" : ""}>+ Folder</button>` : ""}
          <input type="file" data-asset-file-input="${esc(catKey)}" accept="${esc(cat.accept)}" multiple style="display:none;" />
        </div>
        ${progressBar}
        ${collapsed ? "" : `<div style="padding:0 10px 4px;">${fileList}</div>`}
      </div>`;
    }).join("");
    const emptyFilteredMessage = !treeRows
      ? `<div class="small" style="padding:8px 10px; color:var(--muted);">No files in visible categories. Turn on Show all categories to upload into an empty category.</div>`
      : "";
    const sendModal = (() => {
      const s = state.digitalAsset.sendToDeliverables;
      if (!s) return "";
      const existingFolders = [...new Set(((state.digitalAsset.deliveryManifest || {})[selectedCode] || []).map(e => e.folder))].sort();
      const todayStr = new Date().toISOString().slice(0, 10);
      const needsEpsg = s.kind === "slam_las";
      const kindOptions = [`<option value=""${!s.kind ? " selected" : ""}>— Not a standard deliverable —</option>`]
        .concat(deliverableKinds().map((k) => `<option value="${esc(k)}"${s.kind === k ? " selected" : ""}>${esc(DELIVERABLE_KIND_LABELS[k] || k)}</option>`))
        .join("");
      return `<div style="position:relative;margin-top:8px;background:rgba(15,23,42,0.97);border:1px solid rgba(148,163,184,0.25);border-radius:8px;padding:12px 14px;">
        <div style="font-size:11px;font-weight:600;color:var(--muted);text-transform:uppercase;letter-spacing:0.06em;margin-bottom:8px;">Send to Deliverables</div>
        ${s.subfolder ? `<div style="font-size:9px;color:var(--muted);margin-bottom:8px;">from <span style="color:#a78bfa;">${esc(s.subfolder)}/</span>${esc(s.sourceName || "")}</div>` : ""}
        <div style="margin-bottom:10px;">
          <div style="font-size:10px;color:var(--muted);margin-bottom:4px;">Deliverable type</div>
          <select data-deliv-kind-select="1" style="width:100%;font-size:11px;background:rgba(2,6,23,0.6);border:1px solid rgba(148,163,184,0.25);border-radius:4px;padding:4px 7px;color:#e2e8f0;">${kindOptions}</select>
        </div>
        ${needsEpsg ? `<div style="margin-bottom:10px;">
          <div style="font-size:10px;color:var(--muted);margin-bottom:4px;">EPSG code (required for SLAM LAS)</div>
          <input data-deliv-epsg-input="1" value="${esc(s.epsgCode || "")}" placeholder="e.g. 2227" style="width:100%;font-size:11px;background:rgba(2,6,23,0.6);border:1px solid rgba(148,163,184,0.25);border-radius:4px;padding:4px 7px;color:#e2e8f0;" />
        </div>` : ""}
        <div style="margin-bottom:10px;">
          <div style="font-size:10px;color:var(--muted);margin-bottom:4px;">Filename${s.kind ? " (standard name for this deliverable type -- not editable, to keep the client-facing naming convention exact)" : " (this file's current name; pick a type above to auto-fill the standard name)"}</div>
          <input data-deliv-filename-input="1" value="${esc(s.filename)}"${s.kind ? " disabled" : ""} style="width:100%;font-size:11px;background:rgba(2,6,23,0.6);border:1px solid rgba(148,163,184,0.25);border-radius:4px;padding:4px 7px;color:#e2e8f0;${s.kind ? " opacity:0.7;" : ""}" />
        </div>
        ${existingFolders.length ? `<div style="margin-bottom:8px;">
          <div style="font-size:10px;color:var(--muted);margin-bottom:4px;">Existing folders</div>
          <div style="display:flex;flex-wrap:wrap;gap:4px;">
            ${existingFolders.map(f => `<button data-deliv-folder-pick="${esc(f)}" class="btn" style="font-size:10px;padding:2px 8px;${s.targetFolder === f ? "background:rgba(167,139,250,0.2);border-color:#a78bfa;color:#a78bfa;" : ""}">${esc(f)}</button>`).join("")}
          </div>
        </div>` : ""}
        <div style="margin-bottom:10px;">
          <div style="font-size:10px;color:var(--muted);margin-bottom:4px;">Folder name</div>
          <input data-deliv-folder-input="1" value="${esc(s.targetFolder || todayStr)}" placeholder="YYYY-MM-DD" style="width:100%;font-size:11px;background:rgba(2,6,23,0.6);border:1px solid rgba(148,163,184,0.25);border-radius:4px;padding:4px 7px;color:#e2e8f0;" />
        </div>
        <div style="display:flex;gap:8px;justify-content:flex-end;">
          <button data-deliv-send-cancel="1" class="btn" style="font-size:10px;padding:3px 10px;">Cancel</button>
          <button data-deliv-send-confirm="${esc(selectedCode)}" class="btn" style="font-size:10px;padding:3px 10px;background:rgba(167,139,250,0.15);border-color:#a78bfa;color:#a78bfa;">Send</button>
        </div>
      </div>`;
    })();
    return `${categoryToggle}<div style="margin-top:8px; border:1px solid rgba(148,163,184,0.18); border-radius:8px; overflow:hidden;">${treeRows || emptyFilteredMessage}</div>${sendModal}`;
  }

  // `app.services.context` (set via Project Portfolio's "Activate" button, among other
  // paths) is the canonical active-project source; `state.projectSettings.selectedProjectId`
  // is this tool's own local copy, previously kept in sync only by the "imggen-project-marina"
  // dropdown and a since-orphaned sync block inside buildProjectSettingsHtml (that tool was
  // never registered, so it never ran) -- leaving Notes/Tickets/DAM stuck on a stale project
  // whenever the context changed some other way (e.g. Activate). Centralizing the sync here
  // means every renderSection() call sees a fresh value, regardless of how it changed.
  function syncSelectedProjectFromContext() {
    const activeCtxProject = app.services?.context?.getActiveProject?.();
    if (activeCtxProject?.marinaId && state.projectSettings.selectedProjectId !== activeCtxProject.marinaId) {
      state.projectSettings.selectedProjectId = activeCtxProject.marinaId;
    }
  }

  function renderSection(key) {
    const el = sectionEls[key];
    if (!el) return;
    syncSelectedProjectFromContext();
    if (key === "marinaPortfolio") { el.innerHTML = buildMarinaPortfolioHtml(); syncScheduleHistoryPortal(); }
    else if (key === "projectSettings") el.innerHTML = buildProjectSettingsHtml();
    else if (key === "digitalAssetManager") el.innerHTML = buildDigitalAssetManagerHtml();
    else if (key === "projectManagement") renderProjectManagementBottomPanelVariant("table", PROJECT_MGMT_BOTTOM_PANEL_ID, "Project Management");
    else if (key === "gantt") renderProjectManagementBottomPanelVariant("gantt", PROJECT_GANTT_BOTTOM_PANEL_ID, "Gantt");
  }

  // Shared by mount() (Tickets is now a nested panel inside Project Workspace's own
  // container) and attachSectionListeners() (in case anything ever mounts it as a separate
  // section container again) so clipboard-paste-to-attach works from either.
  function onTicketPasteEvent(e) {
    if (!e.target?.closest?.("[data-ticket-paste-zone]")) return;
    const items = Array.from(e.clipboardData?.items || []).filter((it) => it.kind === "file" && it.type.startsWith("image/"));
    if (!items.length) return;
    e.preventDefault();
    const code = String(state.projectSettings.selectedProjectId || "");
    for (const item of items) {
      const file = item.getAsFile();
      if (file) attachFileToTicketDraft(code, file, `pasted-${Date.now()}.${(item.type.split("/")[1] || "png")}`);
    }
  }

  function attachSectionListeners(el) {
    el.addEventListener("click", onClick);
    el.addEventListener("change", onChange);
    el.addEventListener("input", onInput);
    el.addEventListener("paste", onTicketPasteEvent);
    el.addEventListener("keydown", (e) => {
      if (e.target?.id === "portfolio-add-group-input") {
        if (e.key === "Enter") {
          e.preventDefault();
          const name = (e.target.value || "").trim();
          if (!name) return;
          const clientId = String(state.catalog?.selectedClientId || "");
          if (!state.portfolioCustomGroups) state.portfolioCustomGroups = {};
          if (!state.portfolioCustomGroups[clientId]) state.portfolioCustomGroups[clientId] = [];
          if (!state.portfolioCustomGroups[clientId].includes(name)) state.portfolioCustomGroups[clientId].push(name);
          state.portfolioAddGroupShow = false;
          state.portfolioAddGroupName = "";
          saveClientPortfolioGroups(clientId).catch(() => {});
          persistLocalState();
          render();
        } else if (e.key === "Escape") {
          state.portfolioAddGroupShow = false;
          state.portfolioAddGroupName = "";
          render();
        }
      }
      if (e.target?.id === "portfolio-new-client-name") {
        if (e.key === "Enter") {
          e.preventDefault();
          e.currentTarget?.querySelector?.("[data-portfolio-new-client-confirm]")?.click();
        } else if (e.key === "Escape") {
          state.portfolioNewClientShow = false;
          state.portfolioNewClientName = "";
          render();
        }
      }
      if (e.target?.id === "portfolio-new-project-name" || e.target?.id === "portfolio-new-project-code") {
        if (e.key === "Enter") {
          e.preventDefault();
          createProjectFromPortfolioForm();
        } else if (e.key === "Escape") {
          state.portfolioNewProjectShow = false;
          state.portfolioNewProjectCode = "";
          state.portfolioNewProjectName = "";
          render();
        }
      }
    });
    el.addEventListener("dragstart", (e) => {
      const dragRow = e.target?.closest?.("[data-dam-drag-key]");
      if (!dragRow) return;
      e.dataTransfer.setData("text/dam-key", dragRow.getAttribute("data-dam-drag-key") || "");
      e.dataTransfer.setData("text/dam-cat", dragRow.getAttribute("data-dam-drag-cat") || "");
      e.dataTransfer.effectAllowed = "move";
    });
    el.addEventListener("dragenter", (e) => {
      const folderZone = e.target?.closest?.("[data-dam-folder-drop]");
      // stopPropagation on all three drag phases, not just drop: shell.js's global
      // "Drop files to import" overlay (for KMZ/GeoJSON/etc.) listens on rootEl and
      // reacts to ANY file drag anywhere in the app, full-screen blurring the page --
      // which buries the purple/blue highlight below that's the only visual cue a DAM
      // drop target is actually armed. Stopping it here keeps that overlay off while
      // the drag is over a DAM zone, so what the cursor is over stays legible.
      if (folderZone) { e.preventDefault(); e.stopPropagation(); folderZone.style.outline = "2px dashed #a78bfa"; folderZone.style.outlineOffset = "-2px"; return; }
      const zone = e.target?.closest?.("[data-asset-drop-zone]");
      if (!zone) return;
      e.preventDefault();
      e.stopPropagation();
      zone.style.outline = "2px dashed #38bdf8";
      zone.style.outlineOffset = "-2px";
    });
    el.addEventListener("dragover", (e) => {
      const folderZone = e.target?.closest?.("[data-dam-folder-drop]");
      if (folderZone) {
        e.preventDefault();
        e.stopPropagation();
        e.dataTransfer.dropEffect = Array.from(e.dataTransfer.types || []).includes("Files") ? "copy" : "move";
        return;
      }
      const zone = e.target?.closest?.("[data-asset-drop-zone]");
      if (!zone) return;
      e.preventDefault();
      e.stopPropagation();
      e.dataTransfer.dropEffect = "copy";
    });
    el.addEventListener("dragleave", (e) => {
      const folderZone = e.target?.closest?.("[data-dam-folder-drop]");
      if (folderZone) { e.stopPropagation(); if (!folderZone.contains(e.relatedTarget)) { folderZone.style.outline = ""; folderZone.style.outlineOffset = ""; } return; }
      const zone = e.target?.closest?.("[data-asset-drop-zone]");
      if (!zone) return;
      e.stopPropagation();
      if (!zone.contains(e.relatedTarget)) { zone.style.outline = ""; zone.style.outlineOffset = ""; }
    });
    el.addEventListener("drop", (e) => {
      const folderZone = e.target?.closest?.("[data-dam-folder-drop]");
      if (folderZone) {
        e.preventDefault();
        folderZone.style.outline = "";
        folderZone.style.outlineOffset = "";
        // Stop here so the category-level drop zone (an ancestor, also listened on
        // containerEl) doesn't also upload the same files to the category root.
        e.stopPropagation();
        const fromKey = e.dataTransfer.getData("text/dam-key");
        const catKey = folderZone.getAttribute("data-dam-folder-drop-cat") || "";
        const folderName = folderZone.getAttribute("data-dam-folder-drop") || "";
        if (!catKey || !folderName) return;
        if (fromKey) { moveAssetToFolder(fromKey, folderName, catKey); return; }
        // Files/folders dragged in from the desktop: upload into this (sub)folder,
        // keeping any dropped directory tree beneath it.
        const [code, category] = catKey.split("::");
        if (!code || !category) return;
        collectDroppedFiles(e.dataTransfer).then((dropped) => {
          for (const f of dropped) f._damRelPath = `${folderName}/${f._damRelPath || f.name}`;
          if (dropped.length) uploadAssetFiles(code, category, dropped);
        });
        return;
      }
      const zone = e.target?.closest?.("[data-asset-drop-zone]");
      if (!zone) return;
      e.preventDefault();
      // Same reason as the folderZone branch above: containerEl has its own
      // category-root drop listener (re-registered on every re-render); without
      // this, a drop that lands directly on the category zone (not inside any
      // folder) would bubble there too and upload the same files twice.
      e.stopPropagation();
      zone.style.outline = "";
      zone.style.outlineOffset = "";
      const code = String(zone.getAttribute("data-asset-drop-code") || "");
      const category = String(zone.getAttribute("data-asset-drop-cat") || "");
      if (!code || !category) return;
      collectDroppedFiles(e.dataTransfer).then((files) => {
        if (files.length) uploadAssetFiles(code, category, files);
      });
    });
  }

  function mountSection(key, el) {
    if (sectionEls[key]) {
      sectionEls[key].removeEventListener("click", onClick);
      sectionEls[key].removeEventListener("change", onChange);
      sectionEls[key].removeEventListener("input", onInput);
    }
    sectionEls[key] = el || null;
    if (!el) return;
    if (!imageGeneratorTool._rt) return;
    ensureContextListeners();
    if (!state.catalog.loading && !state.catalog.marinas.length && !state.catalog.error) {
      loadCatalog().catch(() => {});
    }
    if (state.projectSettings.selectedProjectId) {
      dispatchProjectChanged(state.projectSettings.selectedProjectId);
    }
    renderSection(key);
    syncPointCloudRuntimeFromDam();
    attachSectionListeners(el);
  }

  function renderAllSections() {
    for (const key of Object.keys(sectionEls)) {
      if (sectionEls[key]) renderSection(key);
    }
    syncPointCloudRuntimeFromDam();
  }

  function onContextCatalogChanged(data) {
    if (Array.isArray(data?.clients) && data.clients.length > 0) {
      state.catalog.clients = data.clients;
    }
    if (Array.isArray(data?.projects) && data.projects.length > 0) {
      const byCode = new Map(
        data.projects.map(p => [String(p.code || p.marinaId || p.id || "").toUpperCase(), p])
      );
      state.catalog.marinas = state.catalog.marinas.map(m => {
        const updated = byCode.get(String(m.code || "").toUpperCase());
        if (!updated) return m;
        return { ...m, name: String(updated.name || m.name) };
      });
    }
    renderAllSections();
    render();
  }

  function onContextMarinaChanged(e) {
    const code = String(e.detail?.projectId || "").trim();
    if (!code) return;
    const prev = String(state.projectSettings.selectedProjectId || "").trim();
    if (code === prev) {
      syncPointCloudRuntimeFromDam(code);
      return;
    }
    clearProjectSettingsMapLayers();
    state.projectSettings.selectedProjectId = code;
    // Pan/zoom to the newly-activated project -- every activation path (Marina Portfolio
    // Manager, Project Settings, the landing page's Resume) funnels through here via
    // hydrotools:project-changed, so this is the one place that needs to do it.
    const { index: zoomIndex } = resolveProjectSettingsMarina();
    if (zoomIndex >= 0) focusItem(zoomIndex, { saveCurrent: false });
    else zoomToCatalogProject(code);
    // Restore this project's saved map orientation (Project Settings > Map Rotation), if any --
    // this is what makes the saved rotation actually persistent across visits, not just stored.
    // Deferred until the map's next moveend (falling back to a short timeout if nothing ever
    // pans/zooms it, e.g. re-activating a project already in view) rather than applied here
    // synchronously -- the pan/zoom above is already in flight, and
    // calling setBearing() while that's still animating raced leaflet-rotate's own view math,
    // occasionally landing the map in the wrong spot. Waiting for the position to settle first
    // and only then rotating fixes that; re-activating "fixed" it before because the second
    // activation's setBearing ran against an already-settled (no longer animating) view.
    const savedRotation = Number(state.catalog.marinas.find((m) => String(m.code || "").toUpperCase() === code.toUpperCase())?.mapRotationDeg);
    if (Number.isFinite(savedRotation) && typeof map?.setBearing === "function") {
      let rotationApplied = false;
      const applySavedRotation = () => {
        if (rotationApplied) return;
        rotationApplied = true;
        map.setBearing(savedRotation);
      };
      map.once("moveend", applySavedRotation);
      // Generous fallback in case nothing ever pans the map (e.g. re-activating a project
      // already in view, so no moveend fires) -- must comfortably outlast any realistic
      // flyTo/setView animation elsewhere, or the fallback itself could fire mid-animation
      // and recreate the exact race this is meant to avoid.
      setTimeout(applySavedRotation, 2000);
    }
    sharedDockPhotosLoadInFlight = false;
    remountStreetViewRuntime();
    loadDamAssetsForProject(code);
    slipInventoryRuntime?.setProjectMarina?.(code);
    remountPointCloudRuntime();
    persistLocalState();
    render();
  }

  function ensureContextListeners() {
    if (contextListenersAttached) return;
    contextListenersAttached = true;
    document.addEventListener("hydrotools:project-changed", onContextMarinaChanged);
    app.events.on("context:changed", renderAllSections);
    app.events.on("context:catalog:changed", onContextCatalogChanged);
  }

  function mount(nextContainer) {
    if (containerEl) {
      containerEl.removeEventListener("click", onClick);
      containerEl.removeEventListener("change", onChange);
      containerEl.removeEventListener("input", onInput);
      containerEl.removeEventListener("paste", onTicketPasteEvent);
    }
    document.removeEventListener("keydown", onGlobalKeyDown);
    ensureContextListeners();
    try { map.off("moveend zoomend resize", queueAutoSaveCurrentView); } catch {}
    try { if (typeof map.off === "function") map.off("rotate", queueAutoSaveCurrentView); } catch {}
    containerEl = nextContainer;
    let justHydrated = false;
    if (!hydratedFromLocalState && !state.items.length) {
      hydratedFromLocalState = true;
      justHydrated = true;
      const localState = readLocalState();
      // Strip the cached active project before restoring -- the app should never come up
      // with a project already "live" on a bare page load (see landing.js). Two independent
      // things needed stripping, not just one: projectSettings.selectedProjectId (which arms
      // both the justHydrated restore-and-rebroadcast right below AND mountSection()'s own
      // independent dispatch, whenever it's non-empty) AND activeIndex -- restore()'s own tail
      // unconditionally calls applySavedView(state.items[state.activeIndex]) whenever
      // activeIndex >= 0, panning the map to wherever that item's saved view was, completely
      // independent of projectSettings/context.js. -1 (not just clearing it) is what
      // currentItem()'s own bounds check already treats as "nothing selected".
      if (localState && typeof localState === "object") {
        restore({
          ...localState,
          activeIndex: -1,
          projectSettings: { ...(localState.projectSettings || {}), selectedProjectId: "" },
        });
      }
    }
    loadSharedProjectTasks()
      .then(() => {
        renderProjectManagementBottomPanel();
        persistLocalState();
      })
      .catch(() => {});
    startSharedProjectTasksPolling();
    render();
    renderDockTrainingLayers();
    // If we just restored state from localStorage and a marina was active, run the
    // same initialization that a live marina selection would have triggered.
    if (justHydrated) {
      const restoredMarina = String(state.projectSettings.selectedProjectId || "").trim();
      if (restoredMarina) {
        remountStreetViewRuntime();
        remountPointCloudRuntime();
        loadDamAssetsForProject(restoredMarina);
        slipInventoryRuntime?.setProjectMarina?.(restoredMarina);
        dispatchProjectChanged(restoredMarina);
        const { index: zoomIndex } = resolveProjectSettingsMarina();
        if (zoomIndex >= 0) focusItem(zoomIndex, { saveCurrent: false });
        else zoomToCatalogProject(restoredMarina);
      }
    }
    containerEl.addEventListener("click", onClick);
    containerEl.addEventListener("change", onChange);
    containerEl.addEventListener("input", onInput);
    containerEl.addEventListener("paste", onTicketPasteEvent);

    // Suppress browser's full-page drag overlay for file drags — prevents the default
    // "drop here to open file" UI from appearing over the whole window.
    document.addEventListener("dragover", suppressDragDefault);
    document.addEventListener("drop", suppressDragDefault);

    // DAM drag-and-drop — delegate on containerEl so re-renders don't lose handlers.
    // stopPropagation keeps shell.js's global "Drop files to import" overlay (full-
    // screen blur, for KMZ/GeoJSON/etc.) from reacting while the drag is over a DAM
    // zone -- see the matching comment in attachSectionListeners' dragenter above.
    containerEl.addEventListener("dragenter", (e) => {
      const zone = e.target?.closest?.("[data-asset-drop-zone]");
      if (!zone) return;
      e.preventDefault();
      e.stopPropagation();
      zone.style.outline = "2px dashed #38bdf8";
      zone.style.outlineOffset = "-2px";
    });
    containerEl.addEventListener("dragover", (e) => {
      const zone = e.target?.closest?.("[data-asset-drop-zone]");
      if (!zone) return;
      e.preventDefault();
      e.stopPropagation();
      e.dataTransfer.dropEffect = "copy";
    });
    containerEl.addEventListener("dragleave", (e) => {
      const zone = e.target?.closest?.("[data-asset-drop-zone]");
      if (!zone) return;
      e.stopPropagation();
      // Only clear highlight when leaving the zone itself (not just a child)
      if (!zone.contains(e.relatedTarget)) {
        zone.style.outline = "";
        zone.style.outlineOffset = "";
      }
    });
    containerEl.addEventListener("drop", (e) => {
      const zone = e.target?.closest?.("[data-asset-drop-zone]");
      if (!zone) return;
      e.preventDefault();
      e.stopPropagation();
      zone.style.outline = "";
      zone.style.outlineOffset = "";
      const code = String(zone.getAttribute("data-asset-drop-code") || "");
      const category = String(zone.getAttribute("data-asset-drop-cat") || "");
      if (!code || !category) return;
      collectDroppedFiles(e.dataTransfer).then((files) => {
        if (files.length) uploadAssetFiles(code, category, files);
      });
    });
    document.addEventListener("keydown", onGlobalKeyDown);
    document.addEventListener("hydrotools:tileset-ready", onTilesetReady);
    document.addEventListener("hydrotools:deliverable-registered", onDeliverableRegistered);
    map.on("moveend zoomend resize", queueAutoSaveCurrentView);
    if (typeof map.on === "function" && typeof map.getBearing === "function") {
      map.on("rotate", queueAutoSaveCurrentView);
    }
    if (!defaultPortfolioAttempted) {
      defaultPortfolioAttempted = true;
      parsePortfolioWorkbookUrl(DEFAULT_PORTFOLIO_URL, DEFAULT_PORTFOLIO_NAME)
        .then((portfolio) => {
          portfolioWorkbook = portfolio;
          if (state.items.length) {
            const result = syncPortfolioToItems(portfolioWorkbook);
            const removed = filterZeroPropertyCountItems();
            recomputePlanning();
            render();
            setStatus(`Synced portfolio workbook to ${result.matched}/${result.total} marinas.${removed ? ` Removed ${removed} with property count 0.` : ""}`);
          }
        })
        .catch((error) => {
          console.error("[ImageGenerator] default portfolio load failed", error);
        });
    }
    ensureGlobalAirportsLoaded().catch(() => {});
    // Deliberately no longer defaults selectedProjectId to firstMarinaIdFromState() when
    // empty -- this ran unconditionally on every mount() and was undoing the landing page's
    // whole premise (nothing populates until the user explicitly activates a project),
    // silently re-arming DAM/slip-inventory/etc. for whatever marina happened to be first in
    // state.items right after the cold-start sanitization cleared it. The DAM/Project
    // Settings panels already have their own real "no active project" empty state (see
    // _buildDigitalAssetManagerHtml's early return) that this was overriding.
    canonicalizeProjectSettingsSelectedMarinaId();
    loadCatalog().catch(() => {});
    loadPortfolioPoints().catch(() => {});
  }

  function snapshot() {
    return {
      datasetName: state.datasetName,
      items: state.items,
      activeIndex: state.activeIndex,
      defaultZoom: state.defaultZoom,
      format: state.format,
      scale: state.scale,
      marker: state.marker,
      showLegend: state.showLegend,
      showMarinaCodes: state.showMarinaCodes,
      showMarinaPoints: state.showMarinaPoints,
      portfolioMapPoints: state.portfolioMapPoints,
      portfolioGroups: state.portfolioGroups,
      portfolioCustomGroups: state.portfolioCustomGroups,
      portfolioGroupMeta: state.portfolioGroupMeta,
      portfolioCustomGroupCollapsed: state.portfolioCustomGroupCollapsed,
      portfolioActiveTrays: state.portfolioActiveTrays,
      portfolioColorBy: state.portfolioColorBy,
      portfolioColorStep: state.portfolioColorStep,
      portfolioShowMapLegend: state.portfolioShowMapLegend,
      showOfficeMarkers: state.showOfficeMarkers,
      showTripRoute: state.showTripRoute,
      tripPlaybackDayIndex: state.tripPlaybackDayIndex,
      tripPlaybackZoomToDay: state.tripPlaybackZoomToDay,
      editMarinaViews: state.editMarinaViews,
      prefix: state.prefix,
      batchScope: state.batchScope,
      offices: state.offices,
      selectedOfficeIds: state.selectedOfficeIds,
      visibleOfficeMarinaIds: state.visibleOfficeMarinaIds,
      previewCodeFilter: state.previewCodeFilter,
      officeDraft: state.officeDraft,
      projectCreate: state.projectCreate,
      planning: state.planning,
      panels: state.panels,
      projectTasks: state.projectTasks,
      projectUi: state.projectUi,
      projectSettings: state.projectSettings,
      catalog: {
        selectedClientId: state.catalog.selectedClientId,
      },
      lasDock: state.lasDock,
      status,
    };
  }

  function restore(data) {
    if (!data || typeof data !== "object") return;
    // Migrate the old single-select portfolioMode (pre-toggle-trays) into the new independent-
    // tray shape when no portfolioActiveTrays was ever saved. "simple" is an older rename of
    // "project-management" from before that.
    const initialTrays = data.portfolioActiveTrays && typeof data.portfolioActiveTrays === "object"
      ? {
          production: data.portfolioActiveTrays.production === true,
          routing: data.portfolioActiveTrays.routing === true,
          "project-management": data.portfolioActiveTrays["project-management"] === true,
        }
      : (() => {
          const legacyMode = data.portfolioMode === "simple" ? "project-management"
            : PORTFOLIO_MODES.includes(data.portfolioMode) ? data.portfolioMode : "production";
          return { production: legacyMode === "production", routing: legacyMode === "routing", "project-management": legacyMode === "project-management" };
        })();
    state = {
      ...state,
      datasetName: String(data.datasetName || ""),
      items: Array.isArray(data.items) ? data.items.map((item) => normalizeItem(item)) : [],
      activeIndex: Number.isInteger(data.activeIndex) ? data.activeIndex : -1,
      defaultZoom: Math.max(3, Math.min(24, Number(data.defaultZoom) || 18)),
      format: String(data.format || "jpg").toLowerCase() === "png" ? "png" : "jpg",
      scale: Math.max(1, Math.min(4, Number(data.scale) || 2)),
      marker: data.marker !== false,
      showLegend: false,
      showMarinaCodes: false,
      showMarinaPoints: false,
      portfolioMapPoints: data.portfolioMapPoints && typeof data.portfolioMapPoints === "object"
        ? { active: !!data.portfolioMapPoints.active, potential: !!data.portfolioMapPoints.potential, archived: !!data.portfolioMapPoints.archived }
        : { active: false, potential: false, archived: false },
      showOfficeMarkers: false,
      showTripRoute: data.showTripRoute === true,
      tripPlaybackDayIndex: Number.isInteger(data.tripPlaybackDayIndex) ? data.tripPlaybackDayIndex : -1,
      tripPlaybackZoomToDay: data.tripPlaybackZoomToDay === true,
      editMarinaViews: data.editMarinaViews === true,
      prefix: String(data.prefix || "marina"),
      batchScope: String(data.batchScope || "selected") === "all" ? "all" : "selected",
      offices: defaultOffices(),
      selectedOfficeIds: Array.isArray(data.selectedOfficeIds) ? data.selectedOfficeIds : [],
      visibleOfficeMarinaIds: Array.isArray(data.visibleOfficeMarinaIds) ? data.visibleOfficeMarinaIds : [],
      previewCodeFilter: String(data.previewCodeFilter || ""),
      officeDraft: data.officeDraft && typeof data.officeDraft === "object" ? data.officeDraft : { name: "", lat: "", lng: "" },
      projectCreate: data.projectCreate && typeof data.projectCreate === "object"
        ? {
            code: String(data.projectCreate.code || ""),
            name: String(data.projectCreate.name || ""),
            type: String(data.projectCreate.type || "marina"),
            lat: String(data.projectCreate.lat || ""),
            lng: String(data.projectCreate.lng || ""),
            picking: false,
          }
        : { code: "", name: "", type: "marina", lat: "", lng: "", picking: false },
      planning: data.planning && typeof data.planning === "object"
        ? {
            averageSpeedMph: Math.max(5, Number(data.planning.averageSpeedMph) || 45),
            roadFactor: Math.max(1, Number(data.planning.roadFactor) || 1.2),
            clusterSpacingMiles: Math.max(0.25, Number(data.planning.clusterSpacingMiles) || 70),
            maxMarinasPerCluster: Math.max(1, Math.round(Number(data.planning.maxMarinasPerCluster) || 5)),
            defaultSurveyHours: Math.max(0, Number(data.planning.defaultSurveyHours) || 6),
            maxWorkHoursPerDay: Math.max(1, Number(data.planning.maxWorkHoursPerDay) || 7),
            maxDriveHoursPerDay: Math.max(0, Number(data.planning.maxDriveHoursPerDay) || 3),
            maxMarinasPerDay: Math.max(1, Math.round(Number(data.planning.maxMarinasPerDay) || 2)),
            pricePerMarina: Math.max(0, Number(data.planning.pricePerMarina) || 6150),
            weatherContingencyPct: Math.max(0, Number(data.planning.weatherContingencyPct) || 10),
            weatherDayRate: Math.max(0, Number(data.planning.weatherDayRate) || 0),
            mileageRate: Math.max(0, Number(data.planning.mileageRate) || FEDERAL_MILEAGE_RATE_2026),
            crewSize: Math.max(1, Math.round(Number(data.planning.crewSize) || DEFAULT_CREW_SIZE)),
            gsaApiKey: String(data.planning.gsaApiKey || ""),
            travelMode: ["auto", "drive", "air"].includes(String(data.planning.travelMode || "").toLowerCase()) ? String(data.planning.travelMode || "").toLowerCase() : "auto",
            airOverheadHours: Math.max(0, Number(data.planning.airOverheadHours) || 4),
            airCruiseMph: Math.max(100, Number(data.planning.airCruiseMph) || 450),
            tripOfficeId: String(data.planning.tripOfficeId || DEFAULT_OFFICES[0].id),
            tripGroupFilter: String(data.planning.tripGroupFilter || ""),
            returnToOffice: data.planning.returnToOffice !== false,
          }
        : state.planning,
      projectSettings: data.projectSettings && typeof data.projectSettings === "object"
        ? {
            selectedProjectId: String(data.projectSettings.selectedProjectId || ""),
            epsgCode: String(data.projectSettings.epsgCode || "2227"),
            nativeZUnit: data.projectSettings.nativeZUnit === "m" ? "m" : "ft",
            marinaEpsgCodes: data.projectSettings.marinaEpsgCodes && typeof data.projectSettings.marinaEpsgCodes === "object"
              ? Object.fromEntries(Object.entries(data.projectSettings.marinaEpsgCodes).map(([k, v]) => [k, String(v)]))
              : {},
          }
        : state.projectSettings,
      catalog: data.catalog && typeof data.catalog === "object"
        ? {
            ...state.catalog,
            selectedClientId: String(data.catalog.selectedClientId || state.catalog.selectedClientId || "safeharbor"),
          }
        : state.catalog,
      portfolioSort: data.portfolioSort && typeof data.portfolioSort === "object"
        ? { col: String(data.portfolioSort.col || ""), dir: String(data.portfolioSort.dir || "asc") === "desc" ? "desc" : "asc" }
        : state.portfolioSort,
      portfolioGroups: data.portfolioGroups && typeof data.portfolioGroups === "object"
        ? { active: data.portfolioGroups.active !== false, potential: data.portfolioGroups.potential !== false, archived: !!data.portfolioGroups.archived }
        : state.portfolioGroups,
      portfolioCustomGroups: data.portfolioCustomGroups && typeof data.portfolioCustomGroups === "object"
        ? data.portfolioCustomGroups
        : state.portfolioCustomGroups,
      portfolioGroupMeta: data.portfolioGroupMeta && typeof data.portfolioGroupMeta === "object"
        ? data.portfolioGroupMeta
        : state.portfolioGroupMeta,
      portfolioCustomGroupCollapsed: data.portfolioCustomGroupCollapsed && typeof data.portfolioCustomGroupCollapsed === "object"
        ? data.portfolioCustomGroupCollapsed
        : state.portfolioCustomGroupCollapsed,
      portfolioActiveTrays: initialTrays,
      portfolioColorBy: ["status","state","milestone","group"].includes(data.portfolioColorBy) ? data.portfolioColorBy : state.portfolioColorBy,
      portfolioColorStep: String(data.portfolioColorStep || state.portfolioColorStep || ""),
      portfolioShowMapLegend: data.portfolioShowMapLegend === true,
      panels: data.panels && typeof data.panels === "object"
        ? {
            planningGroup: initialTrays.routing,
            productionGroup: data.panels.productionGroup === true,
            projectMetadata: data.panels.projectMetadata !== false,
            marinaPortfolio: (data.panels.marinaPortfolio || data.panels.previewQueue) === true,
            projectPortfolio: data.panels.projectPortfolio !== false,
            projectSettings: data.panels.projectSettings === true,
            digitalAssetManager: data.panels.digitalAssetManager === true,
            notes: data.panels.notes === true,
            tickets: data.panels.tickets === true,
            officeAccess: data.panels.officeAccess === true,
            tripPlanning: data.panels.tripPlanning === true,
            dockView360: data.panels.dockView360 === true,
            pointCloud3d: data.panels.pointCloud3d === true,
            lasSoundings: data.panels.lasSoundings === true,
            slipInventory: data.panels.slipInventory === true,
            dockExtraction: data.panels.dockExtraction === true,
            projectManagement: data.panels.projectManagement === true,
            previewItems: data.panels.previewItems === true,
          }
        : state.panels,
      projectTasks: Array.isArray(data.projectTasks)
        ? normalizeProjectTaskRows(data.projectTasks)
        : defaultProjectTasks(),
      projectUi: data.projectUi && typeof data.projectUi === "object"
        ? {
            expandedMarinas: data.projectUi.expandedMarinas && typeof data.projectUi.expandedMarinas === "object"
              ? { ...data.projectUi.expandedMarinas }
              : {},
            activeView: String(data.projectUi.activeView || "table") === "gantt" ? "gantt" : "table",
          }
        : { expandedMarinas: {}, activeView: "table" },
      lasDock: data.lasDock && typeof data.lasDock === "object"
        ? {
            filePath: String(data.lasDock.filePath || ""),
            resolution: Math.max(0.1, Number(data.lasDock.resolution) || 1),
            minAboveWater: Math.max(0, Number(data.lasDock.minAboveWater) || 0.5),
            maxAboveWater: Math.max(0, Number(data.lasDock.maxAboveWater) || 6),
            toolStatus: data.lasDock.toolStatus || null,
            inspection: data.lasDock.inspection || null,
            products: data.lasDock.products || null,
            statusText: String(data.lasDock.statusText || "No LAS inspected yet."),
            training: data.lasDock.training && typeof data.lasDock.training === "object"
              ? {
                  activeClass: String(data.lasDock.training.activeClass || "dock"),
                  labels: Array.isArray(data.lasDock.training.labels) ? data.lasDock.training.labels : [],
                  selectedLabelId: String(data.lasDock.training.selectedLabelId || ""),
                }
              : { activeClass: "dock", labels: [], selectedLabelId: "" },
          }
        : state.lasDock,
    };
    recomputePlanning();
    normalizeDailyPlanning();
    status = String(data.status || status);
    persistLocalState();
    renderDockTrainingLayers();
    render();
    if (state.activeIndex >= 0 && state.items[state.activeIndex]) {
      applySavedView(state.items[state.activeIndex]);
      syncTripPlaybackOverlay(buildTripPlan());
    }
  }

  function destroy() {
    stopProjectLocationPick();
    stopDockTrainingDraw("destroyed");
    closeScheduleHistoryPortal();
    try { slipInventoryRuntime?.destroy?.(); } catch {}
    slipInventoryRuntime = null;
    try { streetViewRuntime?.destroy?.(); } catch {}
    streetViewRuntime = null;
    try { pointCloudRuntime?.destroy?.(); } catch {}
    pointCloudRuntime = null;
    if (containerEl) {
      containerEl.removeEventListener("click", onClick);
      containerEl.removeEventListener("change", onChange);
      containerEl.removeEventListener("input", onInput);
      containerEl.removeEventListener("paste", onTicketPasteEvent);
    }
    document.removeEventListener("keydown", onGlobalKeyDown);
    document.removeEventListener("dragover", suppressDragDefault);
    document.removeEventListener("drop", suppressDragDefault);
    document.removeEventListener("hydrotools:tileset-ready", onTilesetReady);
    document.removeEventListener("hydrotools:deliverable-registered", onDeliverableRegistered);
    document.removeEventListener("hydrotools:project-changed", onContextMarinaChanged);
    app.events.off("context:changed", renderAllSections);
    app.events.off("context:catalog:changed", onContextCatalogChanged);
    contextListenersAttached = false;
    window.clearTimeout(autoSaveViewTimer);
    autoSaveViewTimer = 0;
    if (sharedProjectTasksSaveTimer) {
      clearTimeout(sharedProjectTasksSaveTimer);
      sharedProjectTasksSaveTimer = 0;
    }
    if (sharedProjectTasksPollTimer) {
      clearInterval(sharedProjectTasksPollTimer);
      sharedProjectTasksPollTimer = 0;
    }
    try { map.off("moveend zoomend resize", queueAutoSaveCurrentView); } catch {}
    try { if (typeof map.off === "function") map.off("rotate", queueAutoSaveCurrentView); } catch {}
    try { captureLayer?.clearLayers?.(); } catch {}
    try { if (captureLayer) map.removeLayer(captureLayer); } catch {}
    try { if (projectLocationPickMarker) map.removeLayer(projectLocationPickMarker); } catch {}
    try { dockTrainingSavedLayer.clearLayers(); } catch {}
    try { dockTrainingSketchLayer.clearLayers(); } catch {}
    try { if (dockTrainingRoot) map.removeLayer(dockTrainingRoot); } catch {}
    try { if (legendControlEl?.parentElement) legendControlEl.parentElement.removeChild(legendControlEl); } catch {}
    captureLayer = null;
    projectLocationPickMarker = null;
    legendControlEl = null;
    containerEl = null;
    fileInputEl = null;
    try { ticketRecorder?.state !== "inactive" && ticketRecorder?.stop(); } catch {}
    try { ticketRecorderStream?.getTracks?.().forEach((t) => t.stop()); } catch {}
    try { if (ticketPendingRecording?.blobUrl) URL.revokeObjectURL(ticketPendingRecording.blobUrl); } catch {}
    if (ticketRecordingTimer) clearInterval(ticketRecordingTimer);
    ticketRecorderStream = null;
    ticketRecorder = null;
    ticketRecordedChunks = [];
    ticketRecordingTimer = null;
    ticketPendingRecording = null;
  }

  return { mount, destroy, snapshot, restore, mountSection };
}

export const marinaPortfolioManagerTool = {
  id: "marina-portfolio-manager",
  name: "Project Workspace: Portfolio",
  init(app) { imageGeneratorTool.init(app); },
  destroy() {},
  renderPanel(app, el) {
    imageGeneratorTool.init(app);
    imageGeneratorTool._rt.mountSection("marinaPortfolio", el);
  },
};

export const projectSettingsTool = {
  id: "project-settings",
  name: "Project Settings",
  init(app) { imageGeneratorTool.init(app); },
  destroy() {},
  renderPanel(app, el) {
    imageGeneratorTool.init(app);
    imageGeneratorTool._rt.mountSection("projectSettings", el);
  },
};

export const digitalAssetManagerTool = {
  id: "digital-asset-manager",
  name: "Project Workspace: Digital Asset Manager",
  init(app) { imageGeneratorTool.init(app); },
  destroy() {},
  renderPanel(app, el) {
    imageGeneratorTool.init(app);
    imageGeneratorTool._rt.mountSection("digitalAssetManager", el);
  },
};

export const projectManagementTool = {
  id: "project-management",
  name: "Project Workspace: Project Management",
  init(app) { imageGeneratorTool.init(app); },
  destroy() {},
  renderPanel(app, el) {
    imageGeneratorTool.init(app);
    imageGeneratorTool._rt.mountSection("projectManagement", el);
  },
};

export const ganttTool = {
  id: "gantt",
  name: "Project Workspace: Gantt",
  init(app) { imageGeneratorTool.init(app); },
  destroy() {},
  renderPanel(app, el) {
    imageGeneratorTool.init(app);
    imageGeneratorTool._rt.mountSection("gantt", el);
  },
};
